// PWG V92: uniform multi-select pill filters for MLB environment tiers
// PWG V99: mobile clean header + full-screen Profile navigation
// PWG V100: mobile profile shell + single lineup results workspace
// PWG V99: mobile profile full-screen navigation
// PWG V92: stabilize nested grid component identity; preserve native grid events
// PWG V88: mobile Injury Status access
// PWG V87: safe lineup tags + MLB/WNBA basic eligibility
// PWG V85: lineup eligibility + sports-only advanced tags
// PWG V83 cumulative fix: injury envelope, safe arrays, single panel headers, active-sport lineups, slate taxonomy
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
// PWG PATCH: cumulative UI + injury envelope + safe arrays + slate taxonomy TDZ fix — 2026-07-20
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { getPublicModelLabel } from "./publicBaseballCopy";
import { chunks, favoriteAdditions, favoriteRemovals } from "./guruFavorites";
import { directionPresentation, directionRailColor } from "./directionPresentation";
import { directionPreset, recommendationTagsForDirection, toggleCompatibleRecommendation, toggleDirectionPreset } from "./directionFilterState";
import { destinationDatasetState, isActualSportTransition, normalizeUnsupportedDirectionUrl, sportOnlyUrl } from "./sportFilterIsolation";
import { shouldDismissSportsSplash } from "./sportsLoadPresentation";


// ---------------- PWG PROB helpers (direction-aligned, UI display only) ----------------
const _pwgToPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  // support both 0-1 and 0-100 formats
  return n <= 1 ? n * 100 : n;
};

const pwgProbLabelForRow = (row) => {
  if (!row) return { text: "", pct: null, side: "" };

  const tag = String(row.Tag ?? row.tag ?? "").toUpperCase();
  const over = _pwgToPct(row.PWG_Prob_Over ?? row["PWG_Prob_Over"]);
  const under = _pwgToPct(row.PWG_Prob_Under ?? row["PWG_Prob_Under"]);

  if (!Number.isFinite(over) && !Number.isFinite(under)) return { text: "", pct: null, side: "" };

  const isUnderTag =
    tag.includes("UNDER") || tag.includes("FADE"); // LEAN UNDER / FADE / MEGA FADE
  const isOverTag =
    tag.includes("OVER") || tag.includes("SMASH"); // LEAN OVER / SMASH / MEGA SMASH

  let pct = null;
  let side = "";

  if (tag.includes("COIN")) {
    // show stronger side transparently
    if (Number.isFinite(over) && Number.isFinite(under)) {
      if (over >= under) { pct = over; side = "OVER"; }
      else { pct = under; side = "UNDER"; }
    } else if (Number.isFinite(over)) { pct = over; side = "OVER"; }
    else { pct = under; side = "UNDER"; }
  } else if (isUnderTag && Number.isFinite(under)) {
    pct = under; side = "UNDER";
  } else if (isOverTag && Number.isFinite(over)) {
    pct = over; side = "OVER";
  } else {
    // fallback: pick whichever exists / stronger
    if (Number.isFinite(over) && Number.isFinite(under)) {
      if (over >= under) { pct = over; side = "OVER"; }
      else { pct = under; side = "UNDER"; }
    } else if (Number.isFinite(over)) { pct = over; side = "OVER"; }
    else { pct = under; side = "UNDER"; }
  }

  if (!Number.isFinite(pct)) return { text: "", pct: null, side: "" };
  const text = `${pct.toFixed(1)}% ${side}`;
  return { text, pct, side };
};
// --------------------------------------------------------------------------------------

import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { useAuth } from './firebase/AuthContext';

import InstallPWAButton from "./InstallPWAButton";
import { getDiscordDestinationAction, getDiscordStatus } from "./api/discord";
import { discordActionLabel } from "./discordPresentation";
import { API_BASE, DATA_BASE } from "./api/config";
import { clearAdminMlbShardCache, loadLocalNflCandidate, loadLocalSoccerCandidate, loadMlbBoard, loadMlbProjection, loadSecureSport, secureSportsDataEnabledFor } from "./api/sportsDataClient";
import { nhlAnalyticsSummary, nhlDisplayPropType, nhlEvidenceLabel, nhlGridMatchupCells, nhlMatchupPresentation, nhlPublishedConfidencePct, normalizeNhlCustomerPayload } from "./nhlCustomerPresentation";
import { candidateSupportsCanonicalMatchup, filterRowsByMatchupGrade, hasCanonicalMatchupContract, MATCHUP_FILTER_OPTIONS } from "./matchupGradePresentation";
import { productionStaticCustomerSportEnabled } from "./api/staticCustomerSportsClient";
import { governedSavedSportKeys, isLocalDashboardHost, isProductionDashboardHost, productionOffering } from "./api/productionOfferingRegistry";
import { createStaticCustomerReleaseMonitor } from "./api/staticCustomerReleaseMonitor";
import { authoritativeMlbGameRows, detailCacheKey, projectionIdFor, selectedMlbGameIds, selectedMlbLineType } from "./api/mlbFrontendState";
import { createMlbDetailLoader, detailWindowIndexes } from "./api/mlbDetailPrefetch";
import { mlbAbValue, mlbPaValue } from "./api/mlbHistoryContext";
import MlbHistoryCells from "./MlbHistoryCells";
import { useEntitlements } from "./entitlements/EntitlementContext";
import { resolvedJsonDataURL } from "./dataDelivery";
import { canViewPwgRow, pwgGuruModeView } from "./entitlements/pwgGuruMode";
import { getWnbaThreePointAttempts } from "./wnbaHistoricalStat";
import { resolveNbaHistoricalStat } from "./nbaHistoricalStat";
import TEGSimpleDashboard from "./components/TEGSimpleDashboard";
import SoccerMatchIntelligence from "./components/SoccerMatchIntelligence";
import { tegExperienceForAccess } from "./tegSimple";

import FiltersSheet from "./components/FiltersSheet";
import MobileActionsBar from "./components/MobileActionsBar";
import NavigationIcon from "./NavigationIcon";
import LineTypeControls from "./LineTypeControls";
import MlbDetailLoadingState from "./MlbDetailLoadingState";
import { mobileLineupsIsActive } from "./mobileNavigation";
import { isDraftKingsLineupMode, lineupModesForUser, normalizeLineupMode } from "./lineupModeAccess";
import { abortStaticSportLoad, fetchStaticJson, loadStaticSportOnce, stableDataUrl } from "./staticSportsDataLoader";
import CommandRailIconButton, { commandRailItemIsActive } from "./CommandRailIconButton";

import LineupGeneratorPanel from './LineupGeneratorPanel';
import AdvancedLineupPanel from './AdvancedLineupPanel';
import DraftKingsNflWorkspace from './draftkings/DraftKingsNflWorkspace';
import DraftKingsLineupResults from './draftkings/DraftKingsLineupResults';
import SportsMenu from './SportsMenu';
import TEGDashboard from "./TEGDashboard.jsx";

// other imports...
import UserBadge from "./UserBadge";  
import SettingsDrawer from "./SettingsDrawer";
import AboutModal from "./AboutModal";
import TagHelpModal from './TagHelpModal';
import HelpButton from './components/HelpButton';
import SplashScreen from './components/SplashScreen';
import CompleteProfileModal from "./components/CompleteProfileModal";
import PillButton from "./components/PillButton";

import logo from './assets/playswithguru-logo.png';
import nbaTeamLogos from './assets/nbaTeamLogos.js';
import mlbTeamLogos from './assets/mlbTeamLogos.js';

import { hasAccess } from './utils/authRoles';
import { formatGameWeekday, formatLocalEventTime, formatLocalTime } from './utils/time';
import { buildAwayAtHomeGameOptions, buildCFBFixtureGroups, buildGenericGameOptions, buildMLBGameGroups, buildNFLFixtureOptions, buildSoccerFixtureGroups, canonicalCFBFixtureKey, canonicalGameSelectionKey, canonicalMLBGameKey, canonicalNFLFixtureKey, canonicalTeamSportFixtureKey, groupNFLFixtureOptions, labeledCount, mlbPropCountLabel, projectionCountsByGame, soccerFixtureSelectionKey, singleSportGameKey, toggleAuthoritativeGameSelection, toggleCollapsedFilter } from './utils/fixture';
import { allowsMinutesUi, CFBEligibilityDisplay, footballOpportunitySignal, observedNumber } from './cfbUi';
import { canonicalLineType, customerLineTypeLabel, defaultLineTypesForSport, filterRowsByLineTypes, goblinAttainability, lineTypeTone, lineTypesAfterSportChange, projectionIdentity, propCardAccessibleLabel, supportedLineTypes } from './lineType';
import { compactCustomerMatchup, customerPrimaryLineContext, customerVisibleLineType, CustomerDetailsGuruBanner, CustomerGuruResultLabel, CustomerLineTypeTile, showCustomerGuruTierTile } from './cardPresentation';
import { GridOptionalCells, GridOptionalHeaders, selectedRegistryColumns } from './gridOptionalColumns';
import { compareNullableNumbers, defenseRankLabel, defenseRankValue, gridDeltaValue, isFootballSport, supportsDqs } from './footballGrid';
import { getTeamColor } from './teamColors';
import PatternIntelligencePanel from './PatternIntelligencePanel';
import { availablePatternComponents, canonicalExactPattern, matchesPatternFilters, patternFilterSummary } from './patternIntelligence';
import { DEFAULT_DETAILS_PERIOD, DETAILS_PERIODS, DetailsPeriodSelector, PeriodUnavailable, clearUnsupportedDetailsPeriodFromUrl, detailsPeriodOptions, normalizeDetailsPeriod } from './detailsPeriod';
import NhlCustomerDetails from './NhlCustomerDetails';
import WnbaComboDetails from './WnbaComboDetails';
import { analysisBlockLabel, isWnbaAnalysisBlocked, visibleForCustomerAnalysis } from './analysisReadiness';
import { lineupEmptyState, lineupEntryCapability } from './lineupCapability';
import { eligibleLineupPool, lineupPlayerKey, lineupStillEligible } from './lineupGameEligibility';
import { buildQuickFactItems, formatRate, formatStoredRate, recentRecord } from './quickFacts';
import ContextState from './ContextState';
import HistoricalPerformanceChart from './HistoricalPerformanceChart';
import HistoricalGamesDisclosure from './HistoricalGamesDisclosure';
import GameFilterRow from './GameFilterRow';
import { historicalResultStyle, mlbHistoricalDisplayLabel, mlbHistoricalValue } from './mlbHistoricalMetrics';
import { resolveDirectSport, sportAccess, visiblePwgSports } from './entitlements/pwgSports';
import { fetchSportAvailability } from './api/sportAvailability';
import { usesSharedCustomerDetails } from './detailsExperience';
import { minutesTrendKey } from './minutesIntelligence';
import NBAAdminResearchWorkbench, { canRenderNbaAdminResearch } from './NBAAdminResearchWorkbench';
import SoccerAdminResearchPanel from './SoccerAdminResearchPanel';
import { formatNbaDetailNumber, nbaRestB2bLabel } from './nbaDetailsPresentation';
import { bestAvailableCompletedHistory, formatHistoricalTableDate, formatHistoryDate, historyDatePresentation, selectCompletedHistory } from './historyPresentation';

import PWGDashboard from "./components/PWGDashboard";

import { Info } from 'lucide-react';

// ✅ use raw Radix only
import * as Tooltip from '@radix-ui/react-tooltip';
import * as Popover from '@radix-ui/react-popover';

import './darkMode.css';

import { applyGuru } from "./guru/guruSearch";
import { CACHE_VERSION, sportListCacheKey } from "./cacheVersion";

const STATIC_DATA_VERSION =
  process.env.REACT_APP_DATA_VERSION ||
  process.env.REACT_APP_BUILD_DATE ||
  CACHE_VERSION;

const LEFT_RAIL_STATE_KEY = `pwg:leftRailOpen:${CACHE_VERSION}`;
const TOP_FILTERS_STATE_KEY = `pwg:desktopTopFiltersOpen:${CACHE_VERSION}`;


const sectionDivider = (darkMode) => ({
  height: 1,
  width: "100%",
  background: darkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)",
  marginTop: 10,
  marginBottom: 8,
});

const sectionLabel = (darkMode) => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: darkMode ? "rgba(229,231,235,0.65)" : "rgba(100,116,139,0.90)",
  marginTop: 2,
  marginBottom: 6,
});


// ⚠️ DO NOT CHANGE
// Compare limit is intentionally 8 (Kobe number)
// Used across Grid, Cards, Modals
const db = getFirestore();

function StableFilterSection({ title, defaultOpen = true, children, rightSlot, isNarrow = false }) {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <div
      style={{
        border: "1px solid rgba(148,163,184,0.35)",
        borderRadius: 14,
        padding: 10,
        background: "rgba(255,255,255,0.65)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          padding: "6px 4px",
          touchAction: "manipulation",
        }}
      >
        <div style={{ fontWeight: 900, letterSpacing: 0.6, fontSize: 13, opacity: 0.85 }}>
          {title}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isNarrow ? 6 : 10 }}>
          {rightSlot}
          <span style={{ fontSize: 12, opacity: 0.7 }}>{open ? "▾" : "▸"}</span>
        </div>
      </button>

      {open && <div style={{ marginTop: 10, display: "grid", gap: 10 }}>{children}</div>}
    </div>
  );
}
function AccessPending({ title = "Access Pending", subtitle = "Your account is active, but access to PlaysWithGuru is not enabled yet." }) {
  return (
    <div style={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
      <div style={{ maxWidth: 720, textAlign: "center" }}>
        <h1 style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>{title}</h1>
        <p style={{ fontSize: "1.05rem", marginBottom: "0.75rem" }}>{subtitle}</p>
        <p style={{ opacity: 0.8 }}>
          PlaysWithGuru is currently in private beta. Access is granted manually to keep quality high.
        </p>
      </div>
    </div>
  );
}
export const COMPARE_MAX = 8;

const OptimizedImage = React.memo(({ src, alt, style }) => {
  return <img src={src} alt={alt} loading="lazy" decoding="async" style={style} />;
});

// --- safe env + host helpers ---
// --- env/host helpers (single copy) ---
const isProdHost = () =>
  window.location.hostname.endsWith("playswithguru.com") ||
  window.location.hostname.endsWith(".netlify.app");


// -- handle mobile bar at bottom -- //

// Map sport → paths
const jsonPathFor = (sport = "NBA") => {
  const s = String(sport).toUpperCase();
  switch (s) {
    case "MLBB": return "/data/mlb-b-props.json";
    case "WNBA": return "/data/wnba-props.json";
    case "WNBAB": return "/data/wnba-b-props.json";
    case "FPA": return "/data/fpa-props.json";
    case "EURO": return "/data/euro-props.json";
    case "MMA": return "/data/ufc-props.json";
    case "NFL": return "/data/nfl-props.json";
    case "SOCCER": return "/data/soccer-props.json";
    case "CFB" : return "/data/cfb-props.json";
    case "NHL" : return "/data/nhl-props.json";
    case "TEG" : return "/data/teg.json";
    case "CBB" : return "/data/cbb-props.json";
    case "GBL" : return "/data/gbl-props.json";
    case "LBSA" : return "/data/lbsa-props.json";
    case "SACB" : return "/data/sacb-props.json";
    case "BBL" : return "/data/bbl-props.json";
    case "NBB" : return "/data/nbb-props.json";
    case "AUSNBL" : return "/data/ausnbl-props.json";
    case "BCL" : return "/data/bcl-props.json";
    case "KBO" : return "/data/kbo-props.json";

    case "NBA": return "/data/nba-props.json";
    default: throw new Error(`Unknown sport: ${s || "UNKNOWN"}`);
  }
};

// Slate-level Daily Guru Brief JSON (optional companion file)
const dailyBriefJsonPathFor = (sport = "NBA") => {
  const s = String(sport).toUpperCase();
  switch (s) {
    case "MLB": return "/data/mlb_daily_brief.json";
    case "WNBA": return "/data/wnba_daily_brief.json";
    case "FPA": return "/data/fpa_daily_brief.json";
    case "EURO": return "/data/euro_daily_brief.json";
    case "MMA": return "/data/mma_daily_brief.json";
    case "NFL": return "/data/nfl_daily_brief.json";
    case "SOCCER": return "/data/soccer_daily_brief.json";
    case "CFB" : return "/data/cfb_daily_brief.json";
    case "NHL" : return "/data/nhl_daily_brief.json";
    case "CBB" : return "/data/cbb_daily_brief.json";
    case "GBL" : return "/data/gbl_daily_brief.json";
    case "SACB" : return "/data/sacb_daily_brief.json";
    case "BBL" : return "/data/bbl_daily_brief.json";
    case "LBSA" : return "/data/lbsa_daily_brief.json";
    case "NBB" : return "/data/nbb_daily_brief.json";
    case "AUSNBL" : return "/data/ausnbl_daily_brief.json";
    case "BCL" : return "/data/bcl_daily_brief.json";
    case "KBO" : return "/data/kbo-props.json";


    // TEG doesn't use this
    default: return "/data/nba_daily_brief.json"; // NBA
  }
};

const apiPathFor = (sport = "NBA") => {
  const s = String(sport).toUpperCase();
  switch (s) {
    case "WNBA": return "/wnba-props";
    case "FPA": return "/fpa-props";
    case "EURO": return "/euro-props";
    case "MMA": return "/ufc-props";
    case "NFL": return "/nfl-props";
    case "SOCCER": return "/soccer-props";
    case "CFB" : return "/cfb-props";
    case "NHL" : return "/nhl-props";
    case "CBB" : return "/cbb-props";
    case "GBL" : return "/gbl-props";
    case "SACB" : return "/sacb-props";
    case "BBL" : return "/bbl-props";
    case "LBSA" : return "/lbsa-props";
    case "NBB" : return "/nbb-props";
    case "AUSNBL" : return "/ausnbl-props";
    case "KBO" : return "/kbo-props";

    default: return "/nba-props";
  }
};

  // Shared chip styles for tags / trends / DvP
  const chipBase = {
    background: "#F3F4F6",
    color: "#374151",
    border: "1px solid #E5E7EB",
    padding: "2px 10px",
    borderRadius: 999,
    fontWeight: 600,
    fontSize: ".75rem",
    lineHeight: "1.4",
  };
  
  const goodChip = {
    ...chipBase,
    background: "#dcfce7",
    color: "#166534",
  };
  
  const warnChip = {
    ...chipBase,
    background: "#fef3c7",
    color: "#92400e",
  };
  
  const riskChip = {
    ...chipBase,
    background: "#fee2e2",
    color: "#b91c1c",
  };

// Primary/secondary selection (single copy)
function resolvePropsURLs(sport = "MLB") {
  const jsonURL = stableDataUrl(
    resolvedJsonDataURL({ sport, origin: window.location.origin, path: jsonPathFor(sport) }),
    STATIC_DATA_VERSION,
  );


  // default: prod hosts prefer JSON, dev prefers API
  /*return isProdHost()
    ? { primary: jsonURL, fallback: apiURL }
    : { primary: apiURL,  fallback: jsonURL };
  */

  // NEW default for ALL hosts (prod + dev): JSON first
  return { primary: jsonURL };
}

// One loader that handles primary → fallback and both JSON contracts:
// legacy: [ ...props ]
// current: { props: [...], injury_report: [...] }
async function loadProps(sport) {
  const staticCustomerSport = String(sport || "").toUpperCase();
  const localDashboard = isLocalDashboardHost(window.location);
  const normalizeLoadedPayload = (payload) => staticCustomerSport === "NHL"
    ? normalizeNhlCustomerPayload(payload)
    : payload;
  if (isProductionDashboardHost(window.location)) {
    const offering = productionOffering(staticCustomerSport);
    if (!offering) throw new Error(`${staticCustomerSport || "Unknown"} does not have a governed production release.`);
    return normalizeLoadedPayload(await loadSecureSport(staticCustomerSport));
  }
  if (!localDashboard && ["NBA", "CFB", "WNBA", "KBO"].includes(staticCustomerSport) && productionStaticCustomerSportEnabled(staticCustomerSport)) {
    return loadSecureSport(staticCustomerSport);
  }
  if (String(sport || "").toUpperCase() === "NFL") {
    if (!localDashboard && productionStaticCustomerSportEnabled("NFL")) return loadSecureSport("NFL");
    const localCandidate = await loadLocalNflCandidate();
    if (localCandidate) return localCandidate;
  }
  if (String(sport || "").toUpperCase() === "SOCCER") {
    if (!localDashboard && productionStaticCustomerSportEnabled("SOCCER")) return loadSecureSport("SOCCER");
    const localCandidate = await loadLocalSoccerCandidate();
    if (localCandidate) return localCandidate;
  }
  if (String(sport || "").toUpperCase() === "MLB") {
    if (!secureSportsDataEnabledFor("MLB")) {
      throw new Error("The dedicated MLB sports-data service is not configured.");
    }
    return loadSecureSport("MLB");
  }
  if (!localDashboard && secureSportsDataEnabledFor(sport)) {
    // Secure mode intentionally fails closed. Public JSON and legacy Render
    // routes remain rollback assets during migration, never runtime fallbacks.
    return loadSecureSport(sport);
  }
  const { primary } = resolvePropsURLs(sport);

  const normalizePayload = (data) => {
    if (Array.isArray(data)) {
      return { props: data, injuryReport: [] };
    }

    if (data && typeof data === "object") {
      return {
        props: Array.isArray(data.props) ? data.props : [],
        injuryReport: Array.isArray(data.injury_report)
          ? data.injury_report
          : Array.isArray(data.injuryReport)
            ? data.injuryReport
            : [],
      };
    }

    return { props: [], injuryReport: [] };
  };

  const fetchPayload = async (url, signal) => normalizePayload(await fetchStaticJson(url, { signal }));

  return loadStaticSportOnce(sport, async (signal) => {
    const payload = await fetchPayload(primary, signal);
    if (!payload.props.length && !payload.injuryReport.length) throw new Error(`${staticCustomerSport} local data is empty.`);
    return normalizeLoadedPayload(payload);
  });
}


// Optional: load slate-level Daily Guru Brief (JSON only; no API fallback)
async function loadDailyBrief(sport) {
  if (String(sport).toUpperCase() === "TEG") return null;
  if (isProductionDashboardHost(window.location)) return null;
  if (String(sport).toUpperCase() === "KBO" && productionStaticCustomerSportEnabled("KBO")) return null;
  if (secureSportsDataEnabledFor(sport)) return null;
  const url = stableDataUrl(`${window.location.origin}${dailyBriefJsonPathFor(sport)}`, STATIC_DATA_VERSION);
  const obj = await fetchStaticJson(url);
  // allow either object or single-row array
  if (Array.isArray(obj)) return obj[0] || null;
  return obj || null;
}


// Loose matching helper: ignore case, spaces, punctuation
const norm = s => String(s || "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "");

// new helpers
// ---- canonical helpers (drop in once) ----
// ---- canonical helpers (drop in once, no name clash) ----
const canon = (v) => String(v ?? "").trim();
const up    = (v) => canon(v).toUpperCase();

const isMMArow = (p) => up(p.Sport) === "MMA";

const teamOf = (p) => p.Team || p["Team Name"] || "";
const oppOf  = (p) => p.Opponent || p["Opponent Name"] || "";

// Build ONE canonical game key that the UI shows AND the generator filters against
// Examples: "NYY vs TOR (MLB)" or "Mason Jones vs Bolaji Oki (MMA)"
const matchupKey = (p, fallbackSportKey) => {
  const sportKey = up(p.Sport || fallbackSportKey || "");
  if (!sportKey) return "";

  if (isMMArow(p)) {
    const a = canon(p.Player);
    const b = canon(p.Opponent);
    if (!a && !b) return "";
    // sort so the same fight yields one key regardless of order
    return `${[a, b].filter(Boolean).sort().join(" vs ")} (MMA)`;
  }

  // prefer explicit team/opponent
  const t = canon(teamOf(p));
  const o = canon(oppOf(p));
  if (t && o) return `${[t, o].sort().join(" vs ")} (${sportKey})`;

  // fallback to provided Matchup if present
  const m = p.Matchup && !/nan/i.test(String(p.Matchup)) ? canon(p.Matchup) : "";
  return m ? `${m} (${sportKey})` : "";
};

// Canonical game key for UI + filtering, including MMA
const canonicalGameKey = (p, fallbackSportKey = "") => {
  const sport = String(p.Sport || fallbackSportKey).toUpperCase();
  if (!sport) return "";

  // MMA uses fighter names
  if (sport === "MMA") {
    const a = String(p.Player || "").trim();
    const b = String(p.Opponent || "").trim();
    if (!a || !b) return "";
    return `${[a, b].sort().join(" vs ")} (MMA)`;
  }

  // Team sports
  const team  = String(p.Team || p["Team Name"] || "").trim();
  const opp   = String(p.Opponent || p["Opponent Name"] || "").trim();
  if (!team || !opp) return "";
  return `${[team, opp].sort().join(" vs ")} (${sport})`;
};



const SPORTS = [ "NBA"];
const ALL_SPORTS = [ "NBA"];

//const SPORTS = ["TEG","MLB", "NHL", "SOCCER", "NFL", "NBA", "CFB", "MMA", "WNBA"];
//const ALL_SPORTS = ["TEG","MLB", "NHL", "SOCCER", "NFL", "NBA", "CFB", "MMA","WNBA"];


// --- Tag helpers (top of PropsDashboard.jsx, after imports) ---
const OVER_TAGS  = new Set(["MEGA SMASH", "SMASH", "LEAN OVER"]);
const UNDER_TAGS = new Set(["MEGA FADE", "FADE", "LEAN UNDER"]);


// WNBA prop-type display/filter normalization.
// Keeps unsupported WNBA props out of the UI without changing backend data.
const WNBA_UNSUPPORTED_PROP_TYPES = new Set([
  "Pts - 1st 3 Mins",
  "Qtrs w/3+ Pts",
  "Qtrs w/4+ Pts",
]);

const dashboardCanonicalPropType = (v) => {
  const s = String(v || "").trim();
  const key = s.toLowerCase().replace(/[^a-z0-9+]/g, "");

  const aliases = {
    fieldgoalsmade: "FG Made",
    fgmade: "FG Made",
    fgm: "FG Made",

    fieldgoalsattempted: "FG Attempted",
    fgattempted: "FG Attempted",
    fga: "FG Attempted",

    twopointersmade: "2-PT Made",
    twopointmade: "2-PT Made",
    twopointsmade: "2-PT Made",
    twopm: "2-PT Made",
    twoptmade: "2-PT Made",
    twoptm: "2-PT Made",
    twopointersmade: "2-PT Made",
    "2pointmade": "2-PT Made",
    "2pointsmade": "2-PT Made",
    "2ptmade": "2-PT Made",
    "2ptm": "2-PT Made",
    "2pm": "2-PT Made",

    twopointersattempted: "2-PT Att",
    twopointattempted: "2-PT Att",
    twopointsattempted: "2-PT Att",
    twopa: "2-PT Att",
    twoptatt: "2-PT Att",
    twoptattempted: "2-PT Att",
    twopta: "2-PT Att",
    "2pointattempted": "2-PT Att",
    "2pointsattempted": "2-PT Att",
    "2ptatt": "2-PT Att",
    "2ptattempted": "2-PT Att",
    "2pta": "2-PT Att",
    "2pa": "2-PT Att",

    threepointersmade: "3PTM",
    threepointsmade: "3PTM",
    threepointmade: "3PTM",
    "3ptmade": "3PTM",
    "3ptm": "3PTM",
    "3pm": "3PTM",

    threepointersattempted: "3PTA",
    threepointsattempted: "3PTA",
    threepointattempted: "3PTA",
    "3ptattempted": "3PTA",
    "3pta": "3PTA",
    "3pa": "3PTA",

    freethrowsmade: "FTM",
    ftmade: "FTM",
    ftm: "FTM",

    freethrowsattempted: "FTA",
    ftattempted: "FTA",
    fta: "FTA",

    pointsassists: "Pts+Asts",
    ptsasts: "Pts+Asts",
    pointsrebounds: "Pts+Rebs",
    ptsrebs: "Pts+Rebs",
    reboundsassists: "Rebs+Asts",
    rebsasts: "Rebs+Asts",
    pointsreboundsassists: "PRA",
    ptsrebsasts: "PRA",
    pra: "PRA",

    fantasyscore: "Fantasy Score",
    fantasyscorepp: "Fantasy Score",

    doubleouble: "Double-Double", // harmless fallback for imperfect source labels
    doubledouble: "Double-Double",
    tripleouble: "Triple-Double",
    tripledouble: "Triple-Double",

    pts1st3mins: "Pts - 1st 3 Mins",
    points1st3mins: "Pts - 1st 3 Mins",
    qtrswith3pts: "Qtrs w/3+ Pts",
    qtrsw3pts: "Qtrs w/3+ Pts",
    qtrswith4pts: "Qtrs w/4+ Pts",
    qtrsw4pts: "Qtrs w/4+ Pts",
  };

  return aliases[key] || s;
};

const isUnsupportedWNBAPropType = (pt) =>
  WNBA_UNSUPPORTED_PROP_TYPES.has(dashboardCanonicalPropType(pt));



const isUnderTag = (tag = "") => {
  const T = String(tag).toUpperCase();
  return UNDER_TAGS.has(T) || T.includes("UNDER");
};

const isOverTag = (tag = "") => {
  const T = String(tag).toUpperCase();
  return OVER_TAGS.has(T) || T.includes("OVER");
};

function getPitcherTier(era) {
  if (era >= 5.0) return "Weak (Target)";       // ERA 5.00+
  if (era >= 4.25) return "Poor";               // ERA 4.25–4.99
  if (era >= 3.5) return "Neutral";             // ERA 3.50–4.24
  if (era >= 2.75) return "Tough";              // ERA 2.75–3.49
  return "Elite";                               // ERA < 2.75
}

function getHitterTier(teamBA) {
  if (teamBA <= 0.225) return "Very Weak";
  if (teamBA <= 0.240) return "Weak";
  if (teamBA <= 0.255) return "Neutral";
  if (teamBA <= 0.270) return "Strong";
  return "Very Strong";
}


  function getConfidenceLabel(val) {
    const x = Number(val);
    if (!Number.isFinite(x)) return "Unknown";
  
    if (x > 10)  return "Volatile Elite";
    if (x >= 7.5) return "Elite";
    if (x >= 5.0) return "Strong";
    if (x >= 2.5) return "Moderate";
    if (x >= 0)   return "Low";
    return "Unknown";
  }

  // WNBA backend now sends public Confidence as a label from the Decision Engine
  // (Elite/Strong/Moderate/Low), while older sports can still send numeric confidence.
  // This keeps WNBA card/tooltips from showing "Unknown" when Confidence = "Strong".
  const getDisplayConfidenceLabel = (val, sport = "") => {
    const raw = String(val ?? "").trim();
    const sportKey = String(sport || "").toUpperCase();
    const n = Number(val);

    if (sportKey === "WNBA" && raw && !Number.isFinite(n)) {
      const key = raw.toUpperCase();
      const map = {
        ELITE: "Elite",
        HIGH: "Strong",
        STRONG: "Strong",
        MED: "Moderate",
        MEDIUM: "Moderate",
        MODERATE: "Moderate",
        LOW: "Low",
      };
      return map[key] || raw;
    }

    if (sportKey === "NHL" && Number.isFinite(n)) return `${n.toFixed(0)}%`;

    if (Number.isFinite(n)) return getConfidenceLabel(n);
    return raw || "Unknown";
  };

  // ---------------- WNBA Player DNA helpers ----------------
  // Phase 1: display-only context on WNBA cards.
  // It does not change Guru Pick, Tag, PWG Probability, or filters.
  const getPlayerDNAMeta = (row, activeSportValue = "") => {
    const sportKey = String(activeSportValue || row?.Sport || "").toUpperCase();
    if (sportKey !== "WNBA") return null;

    const scoreRaw =
      row?.Prop_DNA_Score ??
      row?.["Prop_DNA_Score"] ??
      row?.["Prop DNA Score"] ??
      row?.prop_dna_score;

    const score = Number(scoreRaw);
    const tier = String(
      row?.Prop_DNA_Tier ??
      row?.["Prop_DNA_Tier"] ??
      row?.["Prop DNA Tier"] ??
      row?.prop_dna_tier ??
      ""
    ).trim().toUpperCase();

    const primary = String(
      row?.Player_DNA_Primary ??
      row?.["Player_DNA_Primary"] ??
      row?.["Player DNA Primary"] ??
      row?.player_dna_primary ??
      ""
    ).trim();

    const secondary = String(
      row?.Player_DNA_Secondary ??
      row?.["Player_DNA_Secondary"] ??
      row?.["Player DNA Secondary"] ??
      row?.player_dna_secondary ??
      ""
    ).trim();

    const conflictRaw =
      row?.Identity_Conflict_Flag ??
      row?.["Identity_Conflict_Flag"] ??
      row?.["Identity Conflict Flag"] ??
      row?.identity_conflict_flag;

    const conflict =
      conflictRaw === true ||
      String(conflictRaw || "").toLowerCase() === "true" ||
      String(conflictRaw || "") === "1";

    const conflictReason = String(
      row?.Identity_Conflict_Reason ??
      row?.["Identity_Conflict_Reason"] ??
      row?.["Identity Conflict Reason"] ??
      row?.identity_conflict_reason ??
      ""
    ).trim();

    const ceilingScore = Number(
      row?.Prop_Ceiling_Score ??
      row?.["Prop_Ceiling_Score"] ??
      row?.["Prop Ceiling Score"] ??
      row?.prop_ceiling_score
    );

    const ceilingGap = Number(
      row?.Prop_Ceiling_Gap ??
      row?.["Prop_Ceiling_Gap"] ??
      row?.["Prop Ceiling Gap"] ??
      row?.prop_ceiling_gap
    );

    if (!primary && !tier && !Number.isFinite(score)) return null;

    const displayTier = tier
      ? tier.charAt(0) + tier.slice(1).toLowerCase()
      : Number.isFinite(score)
        ? score >= 85 ? "Elite" : score >= 72 ? "Strong" : score >= 58 ? "Solid" : "Weak"
        : "";

    return {
      primary,
      secondary,
      score: Number.isFinite(score) ? score : null,
      tier,
      displayTier,
      conflict,
      conflictReason,
      ceilingScore: Number.isFinite(ceilingScore) ? ceilingScore : null,
      ceilingGap: Number.isFinite(ceilingGap) ? ceilingGap : null,
    };
  };

  const playerDNATierStyle = (tier = "", darkMode = false) => {
    const t = String(tier || "").toUpperCase();
    const base = {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 900,
      lineHeight: 1,
      whiteSpace: "nowrap",
      border: "1px solid",
    };

    if (t === "ELITE") return { ...base, background: "#fef3c7", color: "#92400e", borderColor: "#fcd34d" };
    if (t === "STRONG") return { ...base, background: "#dcfce7", color: "#166534", borderColor: "#86efac" };
    if (t === "SOLID") return { ...base, background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" };
    return {
      ...base,
      background: darkMode ? "rgba(255,255,255,0.06)" : "#f8fafc",
      color: darkMode ? "#d1d5db" : "#475569",
      borderColor: darkMode ? "rgba(255,255,255,0.12)" : "#cbd5e1",
    };
  };


  // ---------------- MLB Environment Chips ----------------
  // Purpose: make matchup context visible on the card without forcing users to open filters/details.
  // Hitter props use opposing pitcher environment. Pitcher props use opponent hitter environment.
  const firstNonEmpty = (row, keys = []) => {
    for (const key of keys) {
      const value = row?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "" && String(value).trim().toLowerCase() !== "nan") {
        return String(value).trim();
      }
    }
    return "";
  };

  const cleanEnvLabel = (value) => String(value || "").replace(/\s+/g, " ").trim();

  const getMlbPlayerType = (row) => {
    const raw = firstNonEmpty(row, [
      "Player Type", "PlayerType", "player_type", "position", "Position", "POS", "pos"
    ]).toUpperCase();

    const prop = firstNonEmpty(row, ["Prop Type", "PropType", "prop_type", "Prop"])
      .toUpperCase();

    if (raw.includes("PITCH")) return "PITCHER";
    if (raw.includes("BAT") || raw.includes("HITTER")) return "HITTER";

    if (
      prop.includes("PITCHER") ||
      prop.includes("PITCHING") ||
      prop.includes("PITCHES THROWN") ||
      prop.includes("HITS ALLOWED") ||
      prop.includes("EARNED RUNS ALLOWED") ||
      prop.includes("WALKS ALLOWED")
    ) {
      return "PITCHER";
    }

    return "HITTER";
  };

  const mlbEnvTone = (label, kind = "") => {
    const txt = String(label || "").toUpperCase();
    const k = String(kind || "").toUpperCase();

    if (!txt) return "neutral";

    // For hitter props, Weak/Poor/Very High/High means target-friendly.
    // For pitcher props, Very Weak/Weak/contact-prone offense can be favorable depending on prop.
    if (
      txt.includes("VERY HIGH") ||
      txt.includes("HIGH (TARGET)") ||
      txt.includes("WEAK (TARGET)") ||
      txt === "POOR" ||
      txt.includes("POOR") ||
      txt.includes("CONTACT") ||
      txt.includes("VERY WEAK")
    ) return "target";

    if (
      txt.includes("LOW (AVOID)") ||
      txt.includes("ELITE") ||
      txt.includes("TOUGH") ||
      txt.includes("STRIKEOUT ACE") ||
      txt.includes("GREAT")
    ) return "avoid";

    if (txt.includes("MEDIUM") || txt.includes("AVERAGE") || txt.includes("NEUTRAL")) return "neutral";
    if (k.includes("HAND")) return "info";

    return "neutral";
  };

  const mlbEnvChipStyle = (tone) => {
    const base = {
      display: "inline-flex",
      alignItems: "center",
      padding: "5px 10px",
      borderRadius: 999,
      border: "1px solid #d1d5db",
      fontSize: 11,
      fontWeight: 900,
      lineHeight: 1,
      whiteSpace: "nowrap",
    };

    if (tone === "target") return { ...base, background: "#dcfce7", color: "#166534", borderColor: "#86efac" };
    if (tone === "avoid") return { ...base, background: "#fee2e2", color: "#991b1b", borderColor: "#fca5a5" };
    if (tone === "warn") return { ...base, background: "#fef3c7", color: "#92400e", borderColor: "#fcd34d" };
    if (tone === "info") return { ...base, background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" };
    return { ...base, background: "#f8fafc", color: "#475569", borderColor: "#cbd5e1" };
  };

  const shortMlbEnvLabel = (kind, value) => {
    const v = cleanEnvLabel(value);
    if (!v) return "";

    if (kind === "pitcherTier") {
      if (v.includes("Weak")) return "🎯 Weak Pitcher";
      if (v.includes("Poor")) return "🎯 Poor Pitcher";
      if (v.includes("Neutral")) return "⚖️ Neutral Pitcher";
      if (v.includes("Tough")) return "🛑 Tough Pitcher";
      if (v.includes("Elite")) return "🛑 Elite Pitcher";
      return `Pitcher: ${v}`;
    }

    if (kind === "attackability") {
      if (v.includes("Very High")) return "🔥 Very Attackable";
      if (v.includes("High")) return "🔥 Attackable";
      if (v.includes("Medium")) return "⚠️ Medium Risk";
      if (v.includes("Low")) return "🧊 Low Attack";
      return `Attack: ${v}`;
    }

    if (kind === "kpct") {
      if (v.includes("Strikeout Ace")) return "🥶 K Ace";
      if (v.includes("Contact")) return "🧤 Contact Pitcher";
      return `K%: ${v}`;
    }

    if (kind === "whip") {
      if (v.includes("Poor") || v.includes("Below")) return `💧 WHIP ${v}`;
      if (v.includes("Elite") || v.includes("Great")) return `🔒 WHIP ${v}`;
      return `WHIP: ${v}`;
    }

    if (kind === "hand") return `vs ${v.slice(0, 1).toUpperCase()}HP`;

    if (kind === "oppHitterTier") return `Opp bats: ${v}`;
    if (kind === "teamHitterTier") return `Team bats: ${v}`;
    if (kind === "oppKEnv") return `Opp K: ${v}`;

    return v;
  };

  const getMlbEnvironmentChips = (row, activeSportValue = "") => {
    const sportKey = String(activeSportValue || row?.Sport || "").toUpperCase();
    if (!["MLB", "KBO"].includes(sportKey)) return [];

    const playerType = getMlbPlayerType(row);
    const chips = [];
    const seen = new Set();

    const add = (kind, rawValue, titlePrefix = "") => {
      const value = cleanEnvLabel(rawValue);
      if (!value) return;
      const label = shortMlbEnvLabel(kind, value);
      const key = `${kind}:${label}`.toLowerCase();
      if (!label || seen.has(key)) return;
      seen.add(key);
      chips.push({
        kind,
        label,
        tone: mlbEnvTone(value, kind),
        title: titlePrefix ? `${titlePrefix}: ${value}` : value,
      });
    };

    if (playerType === "PITCHER") {
      // Pitcher-card environment: what kind of lineup is he facing?
      add("oppHitterTier", firstNonEmpty(row, [
        "opp_hitter_tier", "Opp_Hitter_Tier", "Opponent Hitter Tier", "Opp Hitter Tier", "oppHitterTier"
      ]), "Opponent hitter tier");

      add("teamHitterTier", firstNonEmpty(row, [
        "team_hitter_tier", "Team_Hitter_Tier", "Team Hitter Tier", "hitter_tier", "Hitter Tier", "teamHitterTier"
      ]), "Opponent/team hitter tier");

      add("oppKEnv", firstNonEmpty(row, [
        "opp_team_kpct_tier", "Opp_Team_Kpct_Tier", "Opponent K% Tier", "Opp K% Tier", "opp_kpct_tier", "Opp_Kpct_Tier"
      ]), "Opponent strikeout environment");

      return chips.slice(0, 3);
    }

    // Hitter-card environment: what kind of pitcher is he facing?
    add("pitcherTier", firstNonEmpty(row, [
      "opp_pitcher_tier", "Opp_Pitcher_Tier", "Opp Pitcher Tier", "Opponent Pitcher Tier", "oppPitcherTier", "OppPitcherTier"
    ]), "Opposing pitcher tier");

    add("attackability", firstNonEmpty(row, [
      "opp_pitcher_attackability", "Opp_Pitcher_Attackability", "Opp Pitcher Attackability", "Opponent Attackability", "Opp Attackability", "oppAttackability", "OppAttackability"
    ]), "Opponent attackability");

    add("kpct", firstNonEmpty(row, [
      "opp_kpct_tier", "Opp_Kpct_Tier", "Opp K% Tier", "Opponent K% Tier", "oppKpctTier", "OppKpctTier"
    ]), "Opponent K% tier");

    add("whip", firstNonEmpty(row, [
      "opp_whip_tier", "Opp_WHIP_Tier", "Opp WHIP Tier", "Opponent WHIP Tier", "oppWhipTier", "OppWhipTier"
    ]), "Opponent WHIP tier");

    add("hand", firstNonEmpty(row, [
      "opp_pitcher_hand", "opp_hand", "opp_pitch_hand", "OppPitcherThrows", "Opp Pitcher Hand", "Opp Hand"
    ]), "Opponent pitcher hand");

    return chips.slice(0, 4);
  };

  const getOpposingPitcherName = (row, activeSportValue = "") => {
    const sportKey = String(activeSportValue || row?.Sport || row?.sport || "").toUpperCase();
    if (!["MLB", "KBO"].includes(sportKey)) return "";
    if (getMlbPlayerType(row) === "PITCHER") return "";

    return firstNonEmpty(row, [
      "opp_pitcher", "Opp_Pitcher", "Opp Pitcher", "Opponent Pitcher",
      "oppPitcher", "OppPitcher", "opposing_pitcher", "Opposing Pitcher"
    ]);
  };

  const renderHitterPitcherContext = (row, activeSportValue = "", compact = false, darkModeValue = false) => {
    const sportKey = String(activeSportValue || row?.Sport || row?.sport || "").toUpperCase();
    if (!["MLB", "KBO"].includes(sportKey)) return null;
    if (getMlbPlayerType(row) === "PITCHER") return null;

    const pitcherName = getOpposingPitcherName(row, sportKey);
    const chips = getMlbEnvironmentChips(row, sportKey);
    if (!pitcherName && !chips.length) return null;

    return (
      <div style={{ marginTop: 8 }}>
        {pitcherName && (
          <div
            title={`Opposing pitcher: ${pitcherName}`}
            style={{
              fontSize: compact ? 11 : 12,
              fontWeight: 900,
              color: darkModeValue ? "#e5e7eb" : "#334155",
              marginBottom: chips.length ? 6 : 0,
            }}
          >
            Facing: {pitcherName}
          </div>
        )}

        {chips.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {chips.slice(0, compact ? 2 : 4).map((chip, idx) => (
              <span
                key={`pitcher-context-${idx}-${chip.label}`}
                title={chip.title || chip.label}
                style={{
                  ...mlbEnvChipStyle(chip.tone),
                  padding: compact ? "4px 8px" : "5px 10px",
                  fontSize: compact ? 10 : 11,
                }}
              >
                {chip.label}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };
  // --------------------------------------------------------


  function Sparkline({
    points = [],
    last20Points = [],
    lineValue = null,
    height = 120,
    chartRange = "L10",
    setChartRange = () => {},
    showBands = false,
    setShowBands = () => {},
    opportunityLabel = "Minutes",
    enableCombinedMode = false,
    auditMode = false,
    statLabel = "Stat",
    showControls = true,
    controlledMetricMode = null,
  }) {
    const [activeIdx, setActiveIdx] = useState(null);
    const [metricMode, setMetricMode] = useState("PROP"); // PROP | OPPORTUNITY
    const effectiveMetricMode = controlledMetricMode || metricMode;
    const isPropMode = effectiveMetricMode === "PROP";
    const isCombinedMode = enableCombinedMode && effectiveMetricMode === "BOTH";
    const showsProp = isPropMode || isCombinedMode;
    const showsOpportunity = effectiveMetricMode === "OPPORTUNITY" || isCombinedMode;
  
    const normalizePoints = useCallback((arr) => {
      return Array.isArray(arr)
        ? arr
            .map((p) => ({
              date: p?.date || "",
              matchup: p?.matchup || "",
              value: Number(p?.value),
              opportunity: Number.isFinite(Number(p?.opportunity)) ? Number(p.opportunity) : null,
              secondaryOpportunity: Number.isFinite(Number(p?.secondaryOpportunity))
                ? Number(p.secondaryOpportunity)
                : null,
            }))
            .filter((p) => Number.isFinite(p.value))
        : [];
    }, []);
  
    const l10Data = useMemo(() => normalizePoints(points), [points, normalizePoints]);
    const l20Data = useMemo(() => normalizePoints(last20Points), [last20Points, normalizePoints]);
  
    // For today:
    // - L10 uses points
    // - L20 uses last20Points if available, otherwise safely falls back to points
    const data = useMemo(() => {
      if (chartRange === "L20" && l20Data.length > 0) return l20Data;
      return l10Data;
    }, [chartRange, l10Data, l20Data]);
  
    if (!data.length) return null;
  
    // chart geometry
    const W = 520;
    const H = Math.max(70, Number(height) || 120);
    const pad = 14;
    const topPad = 34;
  
    // helpers
    const mean = (arr) => {
      if (!arr?.length) return null;
      return arr.reduce((a, b) => a + b, 0) / arr.length;
    };
  
    const stdDev = (arr) => {
      if (!arr?.length) return null;
      const m = mean(arr);
      const variance =
        arr.reduce((sum, v) => sum + Math.pow(v - m, 2), 0) / arr.length;
      return Math.sqrt(variance);
    };

    const values = showsProp
      ? data.map((d) => d.value).filter((v) => Number.isFinite(v))
      : data
          .map((d) => Number(d?.opportunity))
          .filter((v) => Number.isFinite(v));
    
    const opportunitySeries = Array.isArray(data)
      ? data.map((d) => Number(d?.opportunity)).filter((v) => Number.isFinite(v))
      : [];
  
    const avg = (arr) =>
      arr.length ? arr.reduce((sum, v) => sum + v, 0) / arr.length : null;
    
    const recentOpp = opportunitySeries.slice(-3);
    const priorOpp =
      opportunitySeries.length >= 6
        ? opportunitySeries.slice(-6, -3)
        : opportunitySeries.length >= 4
          ? opportunitySeries.slice(0, Math.max(1, opportunitySeries.length - 3))
          : [];
    
    const recentOppAvg = avg(recentOpp);
    const priorOppAvg = avg(priorOpp);
    
    const opportunityDelta =
      Number.isFinite(recentOppAvg) && Number.isFinite(priorOppAvg)
        ? recentOppAvg - priorOppAvg
        : null;
    
    const hasSmallOpportunitySample =
      opportunitySeries.length > 0 && opportunitySeries.length < 4;
    
    const opportunityStable =
      opportunityDelta != null && opportunityDelta >= -1.5;
    
    const opportunityRising =
      opportunityDelta != null && opportunityDelta >= 1.5;
    
    const opportunityRisk =
      opportunityDelta != null && opportunityDelta <= -3;
  
    // include lineValue in bounds so dashed line always visible
    const allVals = showsProp && Number.isFinite(Number(lineValue))
    ? [...values, Number(lineValue)]
    : values;
  
    let minV = Math.min(...allVals);
    let maxV = Math.max(...allVals);
  
    // Temporary Bollinger calculation:
    // today it uses the currently visible chart data
    // tomorrow we can switch this to always use true L20 if you want
    ///const bandSource = values;
    const bandSource = showsProp
      ? data.map((d) => d.value).filter((v) => Number.isFinite(v))
      : data.map((d) => Number(d?.opportunity)).filter((v) => Number.isFinite(v));
      
    const bandMean = bandSource.length > 0 ? mean(bandSource) : null;
    const bandStd = bandSource.length > 1 ? stdDev(bandSource) : null;
  
    const upperBandValue =
      bandMean != null && bandStd != null ? bandMean + 2 * bandStd : null;
    const lowerBandValue =
      bandMean != null && bandStd != null ? bandMean - 2 * bandStd : null;
  
    if (upperBandValue != null) maxV = Math.max(maxV, upperBandValue);
    if (lowerBandValue != null) minV = Math.min(minV, lowerBandValue);
  
    // avoid flatline division by zero
    if (minV === maxV) {
      minV -= 1;
      maxV += 1;
    }
  
    const xStep = data.length > 1 ? (W - pad * 2) / (data.length - 1) : 0;
  
    const xAt = (i) => pad + i * xStep;
    const yAt = (v) => {
      const t = (v - minV) / (maxV - minV);
      return H - pad - t * (H - (topPad + pad));
    };

    const opportunityBounds = opportunitySeries.length
      ? { min: Math.min(...opportunitySeries), max: Math.max(...opportunitySeries) }
      : { min: 0, max: 1 };
    if (opportunityBounds.min === opportunityBounds.max) {
      opportunityBounds.min -= 1;
      opportunityBounds.max += 1;
    }
    const opportunityYAt = (v) => {
      const t = (v - opportunityBounds.min) / (opportunityBounds.max - opportunityBounds.min);
      return H - pad - t * (H - (topPad + pad));
    };
  
    const pathD = data
      .map((d, i) => `${i === 0 ? "M" : "L"} ${xAt(i)} ${yAt(d.value)}`)
      .join(" ");

    const oppPathD =
    showsOpportunity
      ? data
          .map((d, i) => {
            if (!Number.isFinite(d.opportunity)) return null;
            return `${i === 0 ? "M" : "L"} ${xAt(i)} ${isCombinedMode ? opportunityYAt(d.opportunity) : yAt(d.opportunity)}`;
          })
          .filter(Boolean)
          .join(" ")
      : null;
  
    const lineY =
      showsProp && Number.isFinite(Number(lineValue))
        ? yAt(Number(lineValue))
        : null;
    const upperBandY = upperBandValue != null ? yAt(upperBandValue) : null;
    const lowerBandY = lowerBandValue != null ? yAt(lowerBandValue) : null;
    const middleBandY = bandMean != null ? yAt(bandMean) : null;
  
    const active = activeIdx != null ? data[activeIdx] : null;
  
    // This must not be a hook below the early `if (!data.length) return null`.
    // Sparkline can first render with no points and then receive KBO points,
    // which would otherwise change the number of hooks between renders.
    const maxIdx = (() => {
      if (!data.length) return null;
      let mi = 0;
      for (let i = 1; i < data.length; i++) {
        if (data[i].value > data[mi].value) mi = i;
      }
      return mi;
    })();
  
    const formatOneDate = (s) => {
      const raw = String(s || "").trim();
      if (!raw) return "";
      const d = new Date(raw);
      if (!Number.isFinite(d.getTime())) return raw;
      return d.toLocaleDateString(undefined, {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });
    };
  
    const formatDateLabel = (raw) => {
      const s = String(raw || "").trim();
      if (!s) return "";
  
      if (s.includes("|")) {
        const [a, b] = s.split("|").map((x) => x.trim());
        const fa = formatOneDate(a);
        const fb = formatOneDate(b);
        return fb ? `${fa} | ${fb}` : fa;
      }
  
      return formatOneDate(s);
    };

    const latestValue = values.length ? values[values.length - 1] : null;

    const deltaFromMean =
    Number.isFinite(latestValue) && Number.isFinite(bandMean)
      ? latestValue - bandMean
      : null;
  
    return (
      <div style={{ width: "100%", marginTop: 10 }}>
        {/* Chart controls */}
        {showControls && <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {["L10", "L20"].map((mode) => {
              const activeMode = chartRange === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    setChartRange(mode);
                    setActiveIdx(null);
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: activeMode ? "1px solid #2563eb" : "1px solid #cbd5e1",
                    background: activeMode ? "#2563eb" : "#fff",
                    color: activeMode ? "#fff" : "#0f172a",
                    fontWeight: 800,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  {mode}
                </button>
              );
            })}

            {/* Metric Toggle */}
            <div style={{ display: "flex", gap: 6 }}>
              {(enableCombinedMode ? ["PROP", "OPPORTUNITY", "BOTH"] : ["PROP", "OPPORTUNITY"]).map((m) => {
                const active = effectiveMetricMode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMetricMode(m)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 999,
                      border: active ? "1px solid #14b8a6" : "1px solid #cbd5e1",
                      background: active ? "#14b8a6" : "#fff",
                      color: active ? "#fff" : "#0f172a",
                      fontWeight: 800,
                      fontSize: 11,
                      cursor: "pointer",
                    }}
                  >
                    {enableCombinedMode
                      ? m === "PROP" ? "STAT" : m === "OPPORTUNITY" ? String(opportunityLabel).toUpperCase() : `STAT + ${String(opportunityLabel).toUpperCase()}`
                      : m === "PROP" ? "Prop" : opportunityLabel}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setShowBands((v) => !v)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: showBands ? "1px solid #2563eb" : "1px solid #cbd5e1",
                background: showBands ? "rgba(37,99,235,0.10)" : "#fff",
                color: "#0f172a",
                fontWeight: 800,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Bands
            </button>
          </div>
  
          <div style={{ fontSize: 12, fontWeight: 900, color: "#111827" }}>
            {Number.isFinite(Number(lineValue)) ? `Line: ${Number(lineValue)}` : ""}
          </div>
        </div>}
  
         {/* Readout */}
         <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "baseline",
            marginBottom: 6,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, color: "#374151" }}>
            {active
              ? auditMode
                ? `${formatDateLabel(active.date)}${active.matchup ? ` • ${active.matchup}` : ""} • ${statLabel}: ${active.value} • Minutes: ${Number.isFinite(active.opportunity) ? active.opportunity : "—"} • Current Prop Line: ${Number.isFinite(Number(lineValue)) ? Number(lineValue) : "—"} • ${Number.isFinite(Number(lineValue)) ? (active.value > Number(lineValue) ? "OVER" : active.value < Number(lineValue) ? "UNDER" : "PUSH") : "NO LINE"}`
                : `${formatDateLabel(active.date)}${
                  active.matchup ? ` • ${active.matchup}` : ""
                } • ${opportunityLabel === "Minutes" && !isPropMode
                    ? "MIN"
                    : opportunityLabel === "PA" && !isPropMode
                      ? "PA"
                      : opportunityLabel === "AB" && !isPropMode
                        ? "AB"
                        : opportunityLabel === "Outs" && !isPropMode
                          ? "OUTS"
                          : "Value"}: ${effectiveMetricMode === "OPPORTUNITY" && Number.isFinite(active.opportunity) ? active.opportunity : active.value}`
              : "Hover/tap a point to see value"}
          </div>
        </div>

        {/* Legend / indicator values */}
        <div
          style={{
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 8,
            fontSize: 12,
            fontWeight: 800,
            color: "#6b7280",
          }}
        >
          {showsProp && Number.isFinite(Number(lineValue)) && (
            <span>Prop Line: {Number(lineValue).toFixed(1)}</span>
          )}

          {isCombinedMode && <span>Black = {statLabel} • Teal = Minutes (separate scale)</span>}

          {bandMean != null && (
            <span>
              {showsProp
                ? `Recent Avg (${chartRange}): ${bandMean.toFixed(1)}`
                : `Recent Avg (${opportunityLabel} ${chartRange}): ${bandMean.toFixed(1)}`}
            </span>
          )}

          {showBands && upperBandValue != null && (
            <span>
              {isPropMode
                ? `Band Ceiling: ${upperBandValue.toFixed(1)}`
                : `${opportunityLabel} Ceiling: ${upperBandValue.toFixed(1)}`}
            </span>
          )}

          {showBands && lowerBandValue != null && (
            <span>
              {showsProp
                ? `Band Floor: ${lowerBandValue.toFixed(1)}`
                : `${opportunityLabel} Floor: ${lowerBandValue.toFixed(1)}`}
            </span>
          )}
          {!showsProp && Number.isFinite(deltaFromMean) && (
            <span>
              Δ vs Avg: {deltaFromMean > 0 ? "+" : ""}{deltaFromMean.toFixed(1)}
            </span>
          )}
        </div>

        {!auditMode && (() => {

          const isStrongLow =
            Number.isFinite(latestValue) &&
            lowerBandValue != null &&
            bandStd != null &&
            latestValue <= lowerBandValue + bandStd * 0.35;

          const isLow =
            !isStrongLow &&
            Number.isFinite(latestValue) &&
            bandMean != null &&
            bandStd != null &&
            latestValue <= bandMean - bandStd;

          const isHigh =
            Number.isFinite(latestValue) &&
            upperBandValue != null &&
            bandStd != null &&
            latestValue >= upperBandValue - bandStd * 0.35;

          let label = "Neutral";
          let bg = "#f3f4f6";
          let color = "#374151";
          let border = "#d1d5db";

          const prefix = showsProp ? "" : (opportunityLabel || "Opportunity");

          if (isStrongLow) {
            label = showsProp ? "Strong Bounce" : `${prefix} Compressed`;
            bg = "#dcfce7";
            color = "#166534";
            border = "#86efac";
          } else if (isLow) {
            label = showsProp ? "Bounce Candidate" : `${prefix} Soft`;
            bg = "#ecfccb";
            color = "#3f6212";
            border = "#bef264";
          } else if (isHigh) {
            label = showsProp ? "Extended / Hot" : `${prefix} Elevated`;
            bg = "#fef3c7";
            color = "#92400e";
            border = "#fcd34d";
          }

          return (
            <div style={{ marginBottom: 8 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "6px 10px",
                  borderRadius: 999,
                  background: bg,
                  color,
                  border: `1px solid ${border}`,
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {label}
              </span>
            </div>
          );
        })()}

        {!auditMode && (() => {
          const oppName = opportunityLabel || "Minutes";

          let label = `${oppName} Unknown`;
          let bg = "#f3f4f6";
          let color = "#374151";
          let border = "#d1d5db";

          if (hasSmallOpportunitySample) {
            label = `${oppName} Small Sample (${opportunitySeries.length})`;
            bg = "#f3f4f6";
            color = "#374151";
            border = "#d1d5db";
          } else if (opportunityRising) {
            label = `${oppName} Rising (${opportunityDelta.toFixed(1)})`;
            bg = "#dcfce7";
            color = "#166534";
            border = "#86efac";
          } else if (opportunityStable) {
            label = `${oppName} Stable (${opportunityDelta?.toFixed(1) ?? "0.0"})`;
            bg = "#ecfeff";
            color = "#155e75";
            border = "#67e8f9";
          } else if (opportunityRisk) {
            label = `${oppName} Risk (${opportunityDelta.toFixed(1)})`;
            bg = "#fee2e2";
            color = "#991b1b";
            border = "#fca5a5";
          } else if (opportunityDelta != null) {
            label = `${oppName} Soft (${opportunityDelta.toFixed(1)})`;
            bg = "#fef3c7";
            color = "#92400e";
            border = "#fcd34d";
          }

          return (
            <div style={{ marginBottom: 8 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "6px 10px",
                  borderRadius: 999,
                  background: bg,
                  color,
                  border: `1px solid ${border}`,
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {label}
              </span>
            </div>
          );
        })()}
  
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{
            width: "100%",
            height: H,
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            background: "#fff",
          }}
        >
          {/* Bollinger middle band */}
          {showBands && middleBandY != null && (
            <line
              x1={pad}
              x2={W - pad}
              y1={middleBandY}
              y2={middleBandY}
              stroke="#93c5fd"
              strokeWidth="1.5"
              strokeDasharray="4 4"
              opacity="0.9"
            />
          )}
  
          {/* Bollinger upper band */}
          {showBands && upperBandY != null && (
            <line
              x1={pad}
              x2={W - pad}
              y1={upperBandY}
              y2={upperBandY}
              stroke="#2563eb"
              strokeWidth="2"
              opacity="0.95"
            />
          )}
  
          {/* Bollinger lower band */}
          {showBands && lowerBandY != null && (
            <line
              x1={pad}
              x2={W - pad}
              y1={lowerBandY}
              y2={lowerBandY}
              stroke="#2563eb"
              strokeWidth="2"
              opacity="0.95"
            />
          )}
  
          {/* dashed prop line */}
          {lineY != null && (
            <line
              x1={pad}
              x2={W - pad}
              y1={lineY}
              y2={lineY}
              stroke="#94a3b8"
              strokeWidth="2"
              strokeDasharray="6 6"
            />
          )}
  
          {/* series path */}
          {/* PROP line */}
          {showsProp && (
            <path d={pathD} fill="none" stroke="#111827" strokeWidth="3" />
          )}

          {/* OPPORTUNITY line */}
          {showsOpportunity && oppPathD && (
            <path
              d={oppPathD}
              fill="none"
              stroke="#14b8a6" // teal
              strokeWidth="3"
            />
          )}
  
          {/* dots */}
          {data.map((d, i) => {
            const cx = xAt(i);
            const propCy = yAt(d.value);
            const opportunityCy = Number.isFinite(d.opportunity)
              ? (isCombinedMode ? opportunityYAt(d.opportunity) : yAt(d.opportunity))
              : null;
            const cy = effectiveMetricMode === "OPPORTUNITY" && opportunityCy != null ? opportunityCy : propCy;
  
            const isActive = i === activeIdx;
            const isMax = maxIdx != null && i === maxIdx;
            const showLabel = isActive || isMax;
  
            const lv = Number(lineValue);
            const hasLine = Number.isFinite(lv);
  
            let dotFill = "#111827";
            if (showsProp && hasLine) {
              if (d.value > lv) dotFill = "#16a34a";
              else if (d.value < lv) dotFill = "#dc2626";
              else dotFill = "#6b7280";
            } else if (effectiveMetricMode === "OPPORTUNITY") {
              dotFill = "#14b8a6"; // teal dots
            }
  
            const labelAboveY = cy - 10;
            const labelY = labelAboveY < 14 ? cy + 18 : labelAboveY;
  
            return (
              <g key={i}>
                {showsProp && (
                  <circle
                    cx={cx}
                    cy={propCy}
                    r={isActive ? 6 : 5}
                    fill={dotFill}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setActiveIdx(i)}
                    onMouseLeave={() => setActiveIdx(null)}
                    onClick={() => setActiveIdx(i)}
                  >
                    <title>{`${formatDateLabel(d.date)}${d.matchup ? ` • ${d.matchup}` : ""} • ${statLabel}: ${d.value} • Minutes: ${Number.isFinite(d.opportunity) ? d.opportunity : "—"} • Line: ${Number.isFinite(lv) ? lv : "—"} • ${Number.isFinite(lv) ? (d.value > lv ? "OVER" : d.value < lv ? "UNDER" : "PUSH") : "NO LINE"}`}</title>
                  </circle>
                )}

                {showsOpportunity && opportunityCy != null && (
                  <circle
                    cx={cx}
                    cy={opportunityCy}
                    r={isActive ? 5 : 4}
                    fill="#14b8a6"
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setActiveIdx(i)}
                    onMouseLeave={() => setActiveIdx(null)}
                    onClick={() => setActiveIdx(i)}
                  >
                    <title>{`${formatDateLabel(d.date)}${d.matchup ? ` • ${d.matchup}` : ""} • Minutes: ${d.opportunity} • ${statLabel}: ${d.value} • Line: ${Number.isFinite(lv) ? lv : "—"} • ${Number.isFinite(lv) ? (d.value > lv ? "OVER" : d.value < lv ? "UNDER" : "PUSH") : "NO LINE"}`}</title>
                  </circle>
                )}
  
                {showLabel && (
                  <text
                    x={cx}
                    y={labelY}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="900"
                    fill="#111827"
                    style={{
                      paintOrder: "stroke",
                      stroke: "#ffffff",
                      strokeWidth: 4,
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                    }}
                  >
                    {effectiveMetricMode === "OPPORTUNITY" && Number.isFinite(d.opportunity)
                      ? d.opportunity
                      : d.value}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
  
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 6,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
              {showsProp && Number.isFinite(Number(lineValue))
                ? `Dotted line = prop line (${Number(lineValue)})`
                : ""}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            {showBands
              ? `${chartRange} • Light blue dotted = recent average • Solid blue = ${
                  showsProp ? "band ceiling/floor" : `${opportunityLabel} ceiling/floor`
                }`
              : `${chartRange}`}
          </div>
        </div>
      </div>
    );
  }
  
  
  
  
  
  
////////////////////////GURU LIST //////////////////////////////////////////////////////////////
function useMyGuruList(user) {
  const [myGuruList, setMyGuruList] = useState([]);
  const [myGuruItems, setMyGuruItems] = useState({});
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);

  const myListKey = useMemo(
    () => `guru:mylist:${user?.uid || "anon"}`,
    [user?.uid]
  );

  const myItemsKey = useMemo(
    () => `guru:myitems:${user?.uid || "anon"}`,
    [user?.uid]
  );

  const favoritesCollection = useMemo(() => {
    if (!user?.uid) return null;
    return collection(db, "users", user.uid, "favorites");
  }, [user?.uid]);

  const cardKey = useCallback(
    (p) => projectionIdentity(p,
      `${p.Sport || ""}|${p.Player}|${p["Prop Type"] || p.PropType}|${
        p["Prop Value"] || p.PropValue
      }|${p.GameTime || p["Start Time"] || ""}`),
    []
  );

  useEffect(() => {
    let cancelled = false;

    async function loadFavorites() {
      if (!user?.uid || !favoritesCollection) {
        setMyGuruList([]);
        setMyGuruItems({});
        setFavoritesLoaded(true);
        return;
      }

      try {
        const snapshot = await getDocs(favoritesCollection);

        const list = [];
        const items = {};

        snapshot.forEach((favoriteDoc) => {
          const data = favoriteDoc.data() || {};
          const id = data.__pwgCardKey;

          if (!id) return;

          list.push(id);
          items[id] = data;
        });

        // One-time migration from legacy localStorage if Firestore is empty.
        if (list.length === 0) {
          let legacyList = [];
          let legacyItems = {};

          try {
            legacyList = JSON.parse(localStorage.getItem(myListKey)) || [];
          } catch {}

          try {
            legacyItems = JSON.parse(localStorage.getItem(myItemsKey)) || {};
          } catch {}

          if (legacyList.length) {
            for (const id of legacyList) {
              const item = legacyItems[id];
              if (!item) continue;

              await setDoc(doc(favoritesCollection, encodeURIComponent(id)), {
                ...item,
                __pwgCardKey: id,
                __pwgSavedAt:
                  item.__pwgSavedAt || new Date().toISOString(),
              });

              list.push(id);
              items[id] = item;
            }

            localStorage.removeItem(myListKey);
            localStorage.removeItem(myItemsKey);
          }
        }

        if (!cancelled) {
          setMyGuruList(list);
          setMyGuruItems(items);
          setFavoritesLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load Guru favorites:", error);

        if (!cancelled) {
          setMyGuruList([]);
          setMyGuruItems({});
          setFavoritesLoaded(true);
        }
      }
    }

    loadFavorites();

    return () => {
      cancelled = true;
    };
  }, [
    user?.uid,
    favoritesCollection,
    myListKey,
    myItemsKey,
  ]);

  const isInMyList = useCallback(
    (p) => myGuruList.includes(cardKey(p)),
    [myGuruList, cardKey]
  );

  const toggleMyGuru = useCallback(
    async (p) => {
      if (!p || !user?.uid || !favoritesCollection) return;

      const id = cardKey(p);
      const removing = myGuruList.includes(id);
      const ref = doc(favoritesCollection, encodeURIComponent(id));

      try {
        if (removing) {
          await deleteDoc(ref);

          setMyGuruList((list) => list.filter((x) => x !== id));

          setMyGuruItems((items) => {
            const next = { ...(items || {}) };
            delete next[id];
            return next;
          });
        } else {
          const saved = {
            ...p,
            __pwgCardKey: id,
            __pwgSavedAt: new Date().toISOString(),
          };

          await setDoc(ref, saved);

          setMyGuruList((list) => [id, ...list]);

          setMyGuruItems((items) => ({
            ...(items || {}),
            [id]: saved,
          }));
        }
      } catch (error) {
        console.error("Failed to update Guru favorite:", error);
      }
    },
    [
      user?.uid,
      favoritesCollection,
      cardKey,
      myGuruList,
    ]
  );

  const clearMyGuruList = useCallback(async () => {
    if (!favoritesCollection) return;

    try {
      const snapshot = await getDocs(favoritesCollection);

      await Promise.all(
        snapshot.docs.map((favoriteDoc) =>
          deleteDoc(favoriteDoc.ref)
        )
      );

      setMyGuruList([]);
      setMyGuruItems({});
    } catch (error) {
      console.error("Failed to clear Guru favorites:", error);
    }
  }, [favoritesCollection]);

  const addManyToMyGuruList = useCallback(async (rows) => {
    if (!user?.uid || !favoritesCollection) return { added: 0 };

    const additions = favoriteAdditions(
      rows,
      myGuruList,
      cardKey,
      new Date().toISOString()
    );

    for (const batchEntries of chunks(additions)) {
      const batch = writeBatch(db);
      batchEntries.forEach(({ id, saved }) => {
        batch.set(doc(favoritesCollection, encodeURIComponent(id)), saved);
      });
      await batch.commit();

      setMyGuruList((list) => [
        ...batchEntries.map(({ id }) => id).filter((id) => !list.includes(id)),
        ...list,
      ]);
      setMyGuruItems((items) => ({
        ...(items || {}),
        ...Object.fromEntries(batchEntries.map(({ id, saved }) => [id, saved])),
      }));
    }

    return { added: additions.length };
  }, [user?.uid, favoritesCollection, myGuruList, cardKey]);

  const removeManyFromMyGuruList = useCallback(async (rows) => {
    if (!user?.uid || !favoritesCollection) return { removed: 0 };

    const removals = favoriteRemovals(rows, myGuruList, cardKey);

    for (const batchIds of chunks(removals)) {
      const batch = writeBatch(db);
      batchIds.forEach((id) => {
        batch.delete(doc(favoritesCollection, encodeURIComponent(id)));
      });
      await batch.commit();

      setMyGuruList((list) => list.filter((id) => !batchIds.includes(id)));
      setMyGuruItems((items) => {
        const next = { ...(items || {}) };
        batchIds.forEach((id) => delete next[id]);
        return next;
      });
    }

    return { removed: removals.length };
  }, [user?.uid, favoritesCollection, myGuruList, cardKey]);

  return {
    myGuruList,
    setMyGuruList,
    myGuruItems,
    setMyGuruItems,
    clearMyGuruList,
    cardKey,
    isInMyList,
    toggleMyGuru,
    addManyToMyGuruList,
    removeManyFromMyGuruList,
    favoritesLoaded,
  };
}

///////////////////////////////////////////////////////////////////////////////////////////
// narrow screen detector (<= 860px)
const useIsNarrow = () => {
  const [narrow, setNarrow] = React.useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 860px)").matches : false
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return narrow;
};


const useIsTouch = () => {
  const [isTouch, setIsTouch] = React.useState(false);
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setIsTouch(window.matchMedia("(pointer: coarse)").matches);
    }
  }, []);
  return isTouch;
};

const getRowTag = (row) => {
  const v =
    row?.Tag ??
    row?.tag ??
    row?.["Result Tag"] ??
    row?.["TAG"] ??
    "";
  return String(v || "").trim();
};

// Backend-owned Bounce Classification v1.
const getBounceLabel = (row) => {
  const raw =
    row?.Bounce_Label ??
    row?.bounce_label ??
    row?.["Bounce Label"] ??
    row?.BounceLabel ??
    "";
  const value = String(raw ?? "").trim();
  if (!value || ["NAN", "NULL", "NONE", "UNKNOWN"].includes(value.toUpperCase())) return "";
  return value;
};

const getBounceFamily = (row) => {
  const raw =
    row?.Bounce_Family ??
    row?.bounce_family ??
    row?.["Bounce Family"] ??
    row?.BounceFamily ??
    "";
  const value = String(raw ?? "").trim().toUpperCase();
  if (!value || ["NAN", "NULL", "NONE", "UNKNOWN"].includes(value)) return "";
  return value;
};

const getBounceStateScore = (row) => {
  const raw =
    row?.Bounce_State_Score ??
    row?.bounce_state_score ??
    row?.["Bounce State Score"] ??
    row?.BounceStateScore ??
    "";
  return observedNumber(raw);
};

const BOUNCE_LABEL_DISPLAY_ORDER = [
  "Strong Bounce",
  "Bounce Candidate",
  "Heating Up",
  "Holding Gains",
  "Sustaining",
  "Stable",
  "Extended / Hot",
  "Cooling Off",
  "Softening",
  "Failed Bounce",
];

const BOUNCE_FAMILY_DISPLAY_ORDER = [
  "REVERSAL",
  "CONSTRUCTIVE",
  "NEUTRAL",
  "EXHAUSTION",
  "FAILURE",
];

const BOUNCE_FAMILY_LABELS = {
  REVERSAL: "Reversal",
  CONSTRUCTIVE: "Constructive",
  NEUTRAL: "Neutral",
  EXHAUSTION: "Exhaustion",
  FAILURE: "Failure",
};


// Backtest replay helpers (WNBAB / future MLBB).
// These keep the normal live-card layout but surface settled actuals.
const isBacktestSportKey = (sport) => {
  const s = String(sport || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return s === "WNBAB" || s === "WNBABACKTEST" || s === "MLBB" || s === "MLBBACKTEST";
};

const getActualValue = (row) => {
  const v =
    row?.Actual_Value ??
    row?.["Actual Value"] ??
    row?.actual_value ??
    row?.Actual ??
    row?.actual ??
    row?.ActualStat ??
    row?.actual_stat ??
    null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getActualOutcome = (row) => {
  const v =
    row?.Actual_Outcome ??
    row?.["Actual Outcome"] ??
    row?.actual_outcome ??
    row?.Outcome ??
    row?.outcome ??
    "";
  return String(v || "").trim().toUpperCase();
};

const getBacktestLineValue = (row) => {
  const v =
    row?.["Prop Value"] ??
    row?.PropValue ??
    row?.Line ??
    row?.line ??
    row?.["Prop Line"] ??
    null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getBacktestResult = (row) => {
  // PUSH must be decided before reading Hit_Result because your settler may store
  // pushes as hit_result=false. On a backtest card, actual == line is always PUSH.
  const actual = getActualValue(row);
  const line = getBacktestLineValue(row);
  if (Number.isFinite(actual) && Number.isFinite(line) && Math.abs(actual - line) < 1e-9) {
    return "PUSH";
  }

  const outcome = getActualOutcome(row);
  if (outcome === "PUSH") return "PUSH";

  const raw =
    row?.Backtest_Result ??
    row?.["Backtest Result"] ??
    row?.Hit_Result ??
    row?.["Hit Result"] ??
    row?.hit_result ??
    row?.HitResult ??
    row?.Result ??
    row?.result ??
    row?.Outcome_Result ??
    "";

  if (raw === true) return "HIT";
  if (raw === false) return "MISS";

  const s = String(raw || "").trim().toUpperCase();
  if (s === "PUSH") return "PUSH";
  if (["HIT", "WIN", "W", "TRUE", "1", "YES"].includes(s)) return "HIT";
  if (["MISS", "LOSS", "LOSE", "L", "FALSE", "0", "NO"].includes(s)) return "MISS";

  // Last-resort inference from actual outcome vs predicted direction.
  const side = inferDirection(row);
  if ((outcome === "OVER" || outcome === "UNDER") && (side === "OVER" || side === "UNDER")) {
    return outcome === side ? "HIT" : "MISS";
  }

  return "";
};

const getBacktestResultTheme = (result) => {
  const r = String(result || "").toUpperCase();
  if (r === "HIT") return { label: "HIT", icon: "✅", bg: "#dcfce7", color: "#166534", border: "#86efac" };
  if (r === "MISS") return { label: "MISS", icon: "❌", bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" };
  if (r === "PUSH") return { label: "PUSH", icon: "⚖️", bg: "#fef3c7", color: "#92400e", border: "#fcd34d" };
  return { label: "RESULT", icon: "📌", bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
};


function inferDirection(card) {
  const direct = String(card.direction ?? card.Direction ?? "").trim().toUpperCase();
  if (direct === "OVER" || direct === "UNDER") return direct;

  const tag = String(card.Tag ?? card["Result Tag"] ?? card.tag ?? "").toUpperCase();
  if (/UNDER|FADE/.test(tag)) return "UNDER";
  if (/OVER|SMASH/.test(tag))  return "OVER";

  const delta = Number(card.DELTA ?? card.Delta ?? card.delta);
  if (Number.isFinite(delta)) return delta >= 0 ? "OVER" : "UNDER";
  return "";
}



// Runtime-safe Last10 stat resolver used by getL10Counts before the larger detail-grid resolver is declared.
// This prevents ReferenceError during card rendering and supports 2PT/3PT made/attempted fields.
function getL10Value(row, propType, activeSport = "") {
  if (!row) return null;

  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  if (String(activeSport || "").toUpperCase() === "NBA") {
    return resolveNbaHistoricalStat(row, propType);
  }

  if (String(activeSport || "").toUpperCase() === "MLB") {
    return mlbHistoricalValue(row, propType);
  }

  if (String(activeSport || "").toUpperCase() === "NFL") {
    return num(row?.["Selected Stat Actual"] ?? row?.actual);
  }

  const raw = String(propType || "").trim();
  const key = raw.toLowerCase().replace(/[^a-z0-9+]/g, "");

  const directKeysByProp = {
    points: ["Points", "PTS", "points", "pts"],
    rebounds: ["Rebounds", "REB", "TRB", "rebounds", "reb"],
    assists: ["Assists", "AST", "assists", "ast"],
    steals: ["Steals", "STL", "steals", "stl"],
    blockedshots: ["Blocks", "BLK", "Blocked Shots", "blocks", "blk"],
    blocks: ["Blocks", "BLK", "blocks", "blk"],
    turnovers: ["Turnovers", "TOV", "TO", "turnovers", "tov"],
    fgmade: ["FG Made", "FGM", "FG", "fgm"],
    fgattempted: ["FG Attempted", "FGA", "fga"],
    threemail: [],
    threeptm: ["3PTM", "3PM", "3-PT Made", "3P", "threePointersMade"],
    threeptmade: ["3PTM", "3PM", "3-PT Made", "3P", "threePointersMade"],
    threepointersmade: ["3PTM", "3PM", "3-PT Made", "3P", "threePointersMade"],
    threepta: ["3PTA", "3PA", "3-PT Attempted", "3PA", "threePointersAttempted"],
    threeptattempted: ["3PTA", "3PA", "3-PT Attempted", "threePointersAttempted"],
    twoptmade: ["2-PT Made", "2PT Made", "2PM", "2P", "twoPointersMade"],
    twopointersmade: ["2-PT Made", "2PT Made", "2PM", "2P", "twoPointersMade"],
    twopta: ["2-PT Att", "2-PT Attempted", "2PTA", "2PA", "twoPointersAttempted"],
    twoptatt: ["2-PT Att", "2-PT Attempted", "2PTA", "2PA", "twoPointersAttempted"],
    twoptattempted: ["2-PT Att", "2-PT Attempted", "2PTA", "2PA", "twoPointersAttempted"],
    ftm: ["FTM", "Free Throws Made", "FT Made"],
    fta: ["FTA", "Free Throws Attempted", "FT Attempted"],
    offensiverebounds: ["Offensive Rebounds", "OREB", "ORB"],
    defensiverebounds: ["Defensive Rebounds", "DREB", "DRB"],
    doubledouble: ["Double-Double", "DoubleDouble"],
    tripledouble: ["Triple-Double", "TripleDouble"],
  };

  const readAny = (keys) => {
    for (const k of keys || []) {
      const n = num(row?.[k]);
      if (n !== null) return n;
    }
    return null;
  };

  let v = readAny(directKeysByProp[key]);
  if (v !== null) return v;

  const pts = readAny(["Points", "PTS", "points", "pts"]);
  const reb = readAny(["Rebounds", "REB", "TRB", "rebounds", "reb"]);
  const ast = readAny(["Assists", "AST", "assists", "ast"]);
  const blk = readAny(["Blocks", "BLK", "blocks", "blk"]);
  const stl = readAny(["Steals", "STL", "steals", "stl"]);
  const fgm = readAny(["FG Made", "FGM", "FG", "fgm"]);
  const fga = readAny(["FG Attempted", "FGA", "fga"]);
  const tpm = readAny(["3PTM", "3PM", "3-PT Made", "3P"]);
  const tpa = readAny(["3PTA", "3PA", "3-PT Attempted"]);

  if (key === "pra" || key === "ptsrebsasts" || key === "pointsreboundsassists") {
    if (pts !== null || reb !== null || ast !== null) return (pts || 0) + (reb || 0) + (ast || 0);
  }
  if (key === "ptsrebs" || key === "pointsrebounds") {
    if (pts !== null || reb !== null) return (pts || 0) + (reb || 0);
  }
  if (key === "ptsasts" || key === "pointsassists") {
    if (pts !== null || ast !== null) return (pts || 0) + (ast || 0);
  }
  if (key === "rebsasts" || key === "reboundsassists") {
    if (reb !== null || ast !== null) return (reb || 0) + (ast || 0);
  }
  if (key === "blksstls" || key === "blocksstls") {
    if (blk !== null || stl !== null) return (blk || 0) + (stl || 0);
  }
  if (key === "fantasyscore" || key === "fantasyscorepp") {
    const tov = readAny(["Turnovers", "TOV", "TO", "turnovers", "tov"]);
    if (pts !== null || reb !== null || ast !== null || blk !== null || stl !== null || tov !== null) {
      return (pts || 0) + 1.2 * (reb || 0) + 1.5 * (ast || 0) + 3 * (stl || 0) + 3 * (blk || 0) - (tov || 0);
    }
  }
  if ((key === "twoptmade" || key === "twopointersmade") && fgm !== null && tpm !== null) return fgm - tpm;
  if ((key === "twopta" || key === "twoptatt" || key === "twoptattempted" || key === "twopointersattempted") && fga !== null && tpa !== null) return fga - tpa;

  // Last fallback: exact source key.
  return num(row?.[raw]) ?? num(row?.Value) ?? num(row?.value) ?? null;
}


function getL10Counts(card) {
  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  // MLB analysis already publishes the authoritative Last-10 result. Customer
  // delivery must display that intelligence, not recalculate a new answer from
  // the smaller Last-5 detail sample.
  const isMlb = String(card?.Sport || "").trim().toUpperCase() === "MLB";
  const hasPublishedMlbCounts = ["L10_Hits", "L10_Misses", "L10_Pushes"]
    .some((key) => Object.prototype.hasOwnProperty.call(card || {}, key));
  if (isMlb && hasPublishedMlbCounts) {
    return {
      hit: toNum(card.L10_Hits),
      miss: toNum(card.L10_Misses),
      neutral: toNum(card.L10_Pushes),
    };
  }

  // fallback from any stored counts on the row
  let hit     = toNum(card.hit     ?? card.L10_Hit);
  let miss    = toNum(card.miss    ?? card.L10_Miss);
  let neutral = toNum(card.neutral ?? card.L10_Even);

  // ---- try ALL likely Last-10 containers on the card ----
  const candidates = [
    card.Last10,
    card.Last10Stats,
    card.Last10Rows,
    card.last10Rows,
    card.L10Rows,
    card.l10Rows,
    card.L10,
    card.l10,
    card["L10 Games"],
    card["Last 10 Games"],
  ];

  const gameLog =
    candidates.find((a) => Array.isArray(a) && a.length) || null;

  const rowValue = (row) => {
    if (!row) return NaN;

    // most generic “value” fields first
    if (row.Value != null && Number.isFinite(Number(row.Value))) {
      return Number(row.Value);
    }

    // label coming from the prop itself – PRA, Points, 2PTA, 3PTM, etc.
    const label = String(
      card["Prop Type Label"] ??
      card["Prop Type"] ??
      card.PropType ??
      ""
    ).trim();

    const resolved = getL10Value(row, label, card.Sport || "");
    if (Number.isFinite(resolved)) return resolved;

    const tryKeys = [
      label,
      "FantasyScore_PP",
      "value",
      "stat",
      "Stat",
      "StatValue",
      "V",
      "v",
    ];

    for (const k of tryKeys) {
      if (k && row[k] != null && Number.isFinite(Number(row[k]))) {
        return Number(row[k]);
      }
    }

    return NaN;
  };

  // 🔥 If we have a game log, ALWAYS recompute hits/misses from it
  if (gameLog && gameLog.length) {
    const propValue = Number(card["Prop Value"]);
    if (Number.isFinite(propValue)) {
      let h = 0, m = 0, n = 0;

      for (const row of gameLog) {
        const v = rowValue(row);
        if (!Number.isFinite(v)) continue;

        if (v > propValue) h++;
        else if (v < propValue) m++;
        else n++;
      }

      hit = h;
      miss = m;
      neutral = n;
    }
  }

  return { hit, miss, neutral };
}



/** Return a NUMBER (or null). Always called the same way. */
function directionalPctForCard(p) {
  // re-use the same L10 counts you show in the details block
  const { hit = 0, miss = 0, neutral = 0 } = getL10Counts(p) || {};

  const total = hit + miss + neutral;
  if (!total) return NaN;

  // For OVER-type tags we care about hits; for UNDER-type tags we care about misses
  const isUnder = isUnderTag(p.Tag);  // you already have this helper
  //const primary = isUnder ? miss : hit; //we are going to represnet only hits on cards so will comment this out
  const primary = hit; 

  return Math.round((primary / total) * 100);
}




function chipTheme(pct) {
  if (!Number.isFinite(pct)) return {bg:"#f3f4f6", fg:"#374151", bd:"#e5e7eb"};
  if (pct >= 80) return {bg:"#dcfce7", fg:"#166534", bd:"#bbf7d0"};
  if (pct >= 70) return {bg:"#dbeafe", fg:"#1d4ed8", bd:"#bfdbfe"};
  if (pct >= 60) return {bg:"#e0e7ff", fg:"#3730a3", bd:"#c7d2fe"};
  if (pct < 50)  return {bg:"#fee2e2", fg:"#991b1b", bd:"#fecaca"};
  return {bg:"#f3f4f6", fg:"#374151", bd:"#e5e7eb"};
}


// Simple, app-consistent pill for the premium presets
function PresetPill({ label, active, onClick, direction = "mixed" }) {
  // base pill like your filter controls
  let className = "preset-pill";

  if (active) {
    if (direction === "over") {
      className += " preset-pill--active-over";
    } else if (direction === "under") {
      className += " preset-pill--active-under";
    } else {
      className += " preset-pill--active-mixed";
    }
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      style={{ marginRight: "0.75rem", marginBottom: "0.5rem" }}
      
    >
      {label}
    </button>
  );
}







// put this above PropsDashboard() and NOT inside any other function
// put this above PropsDashboard() and NOT inside any other function
function TagWithInfo({
  tag,
  confidence,
  projected,
  delta,         // optional
  line,          // optional (only used if delta not provided)
  guruAlert,     // optional: 0/1/2
  guruAlertTooltip, // optional
  tagColorMap,
  getConfidenceLabel,
  activeSport,
  publicMode = false,
}) {
  const [isTouch, setIsTouch] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setIsTouch(window.matchMedia("(pointer: coarse)").matches);
    }
  }, []);

  const bg = (tagColorMap && tagColorMap[tag]) || "#e0e0e0";
  const publicConfidenceLabel = getDisplayConfidenceLabel(confidence, activeSport);

  // ---- derive Edge (Δ) ----
  const projNum = Number(projected);
  const lineNum = Number(line);

  let deltaNum = Number(delta);
  const hasDelta = Number.isFinite(deltaNum);

  // If delta not passed, compute from projected - line (only if both exist)
  if (!hasDelta && Number.isFinite(projNum) && Number.isFinite(lineNum)) {
    deltaNum = projNum - lineNum;
  }

  const showDelta = Number.isFinite(deltaNum);

  const formatDelta = (n) => {
    if (!Number.isFinite(n)) return "—";
    const sign = n >= 0 ? "+" : "−";
    return `${sign}${Math.abs(n).toFixed(2)}`;
  };

  const deltaColor = !showDelta ? "#6b7280" : (deltaNum >= 0 ? "#16a34a" : "#dc2626");

  const gaLevel = Number(guruAlert ?? 0);
  const gaPick = (() => {
    const t = String(guruAlertTooltip || "").toUpperCase();
    if (t.includes("UNDER")) return "UNDER";
    if (t.includes("OVER")) return "OVER";
    return "";
  })();

  // GuruAlert color: direction-coded (OVER=green, UNDER=red). WATCH uses amber outline.
  const gaDirColor = gaPick === "UNDER" ? "#dc2626" : (gaPick === "OVER" ? "#16a34a" : "#16a34a");
  const gaOutlineColor = gaLevel === 1 ? "#f59e0b" : gaDirColor;
  const gaWatchStyle = gaLevel === 1;

  const pill = (
    <span
      style={{
        fontSize: "0.8rem",
        fontWeight: 600,
        borderRadius: "999px",
        padding: "0.25rem 0.75rem",
        backgroundColor: bg,
        color: "white",
        marginRight: "0.5rem",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.45rem",
        minWidth: "5rem",
        textAlign: "center",
      }}
      title={gaLevel ? (guruAlertTooltip || "") : undefined}
    >
      {gaLevel ? (
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: gaWatchStyle ? "transparent" : gaDirColor,
            border: `2px solid ${gaOutlineColor}`,
            boxShadow: gaWatchStyle ? "none" : (gaPick === "UNDER" ? "0 0 0 2px rgba(220, 38, 38, 0.10)" : "0 0 0 2px rgba(22, 163, 74, 0.10)"),
          }}
        />
      ) : null}
      <span>{tag}</span>
    </span>
  );

  const content = (
    <span style={{ fontSize: "0.85rem", color: "#333" }}>
      <strong>{publicConfidenceLabel || "Unknown"}</strong> confidence

      {!publicMode && showDelta && (
        <>
          <br />
          Edge (Δ):{" "}
          <strong style={{ color: deltaColor }}>
            {formatDelta(deltaNum)}
          </strong>
        </>
      )}

      {!publicMode && gaLevel ? (
        <>
          <br />
          <span style={{ color: "#374151" }}>
            {guruAlertTooltip || (gaLevel === 2 ? "GURU ALERT" : "GURU WATCH")}
          </span>
        </>
      ) : null}
    </span>
  );

  return isTouch ? (
    <Popover.Root>
      <Popover.Trigger asChild>{pill}</Popover.Trigger>
      <Popover.Content
        side="bottom"
        sideOffset={8}
        style={{
          backgroundColor: "#f0f0f0",
          borderRadius: 8,
          padding: "0.5rem 1rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        {content}
        <Popover.Arrow />
      </Popover.Content>
    </Popover.Root>
  ) : (
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{pill}</Tooltip.Trigger>
        <Tooltip.Content
          side="right"
          sideOffset={8}
          style={{
            backgroundColor: "#f0f0f0",
            borderRadius: 8,
            padding: "0.5rem 1rem",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          {content}
          <Tooltip.Arrow />
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}


const STRATEGY_MARKER_META = {
  "MEGA SMASH": { color: "#0095e0", symbol: "○" },
  "SMASH": { color: "#2e7d32", symbol: "●" },
  "LEAN OVER": { color: "#8e24aa", symbol: "■" },
  "MEGA FADE": { color: "#b71c1c", symbol: "▲" },
  "FADE": { color: "#ef6c00", symbol: "◆" },
  "LEAN UNDER": { color: "#f9a825", symbol: "◇" },
  "COIN TOSS": { color: "#90a4ae", symbol: "⠿" },
};

const confidenceRailFillPct = (directionMeta, row, sport) => {
  if (String(sport || "").trim().toUpperCase() === "NHL") {
    return nhlPublishedConfidencePct(row);
  }
  const level = Number(directionMeta?.confidenceLevel);
  return Number.isFinite(level) && level >= 1 && level <= 4 ? level * 25 : 0;
};

function StrategyMarker({ tag, decorative = false }) {
  const cleanTag = String(tag || "COIN TOSS").trim().toUpperCase();
  const meta = STRATEGY_MARKER_META[cleanTag] || STRATEGY_MARKER_META["COIN TOSS"];
  return (
    <span
      className="pwg-strategy-marker"
      style={{ "--pwg-strategy-color": meta.color }}
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? "true" : undefined}
      aria-label={decorative ? undefined : `PWG Rating ${cleanTag}`}
      title={decorative ? undefined : cleanTag}
      data-strategy={cleanTag}
    >
      <span aria-hidden="true">{meta.symbol}</span>
    </span>
  );
}

function BaseballModelSignalsDisclosure({ player, signals, darkMode = false }) {
  const [expanded, setExpanded] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const reactId = React.useId();
  const panelId = `baseball-model-signals-${reactId.replace(/:/g, "")}`;
  const count = Array.isArray(signals) ? signals.length : 0;
  const accent = darkMode ? "#60A5FA" : "#2563EB";

  if (!count) return null;

  return (
    <div
      data-card-interactive="true"
      onClick={(event) => event.stopPropagation()}
      style={{ marginTop: 10 }}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={`${expanded ? "Hide" : "Show"} ${count} model signals for ${player || "this player"}`}
        onClick={(event) => {
          event.stopPropagation();
          setExpanded((current) => !current);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          marginLeft: 9,
          border: 0,
          borderRadius: 6,
          padding: "4px 6px",
          background: hovered || focused
            ? (darkMode ? "rgba(96,165,250,0.12)" : "rgba(37,99,235,0.08)")
            : "transparent",
          color: accent,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 850,
          lineHeight: 1.25,
          boxShadow: focused ? `0 0 0 2px ${darkMode ? "#1E3A8A" : "#BFDBFE"}` : "none",
          outline: "none",
          transition: "background-color 140ms ease, box-shadow 140ms ease, color 140ms ease",
        }}
      >
        <span aria-hidden="true" style={{ width: 10, textAlign: "center" }}>
          {expanded ? "▼" : "▶"}
        </span>
        <span>Model Signals ({count})</span>
      </button>

      {expanded && (
        <div
          id={panelId}
          style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}
        >
          {signals}
        </div>
      )}
    </div>
  );
}




// ————————————————— SportRail (local component)
function SportRail({ sports, active, onSelect }) {
  return (
    <nav className="sport-rail" aria-label="Sports">
      {sports.map((s) => (
        <button
          key={s}
          type="button"
          className={`sport-pill${active === s ? " is-active" : ""}`}
          aria-current={active === s ? "page" : undefined}
          onClick={() => onSelect(s)}
        >
          {s}
        </button>
      ))}
    </nav>
  );
}

// --- robust: read the current prop line from a card, regardless of key naming
function getPropLine(card) {
  const candidates = [
    card?.["Prop Value"],
    card?.PropValue,
    card?.PropLine,
    card?.Line,
    card?.value,
    card?.Value,
  ];
  for (const c of candidates) {
    if (c === null || c === undefined) continue;
    const n = Number(String(c).trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
}

// ---------- Player hover helpers (L10 + season avg vs line) ----------
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getDirectionKey = (row) => {
  // Badge/pill direction should follow FINAL tag first.
  // SelectionStrategy can disagree; that disagreement should be surfaced in text,
  // but the badge should not contradict the displayed Tag.
  const tag =
    String(row?.Tag_Final ?? row?.["Tag_Final"] ?? row?.Tag ?? row?.["Tag"] ?? "")
      .toUpperCase()
      .trim();

  if (tag.includes("COIN")) return "COIN";
  if (tag.includes("UNDER") || tag.includes("FADE")) return "UNDER";
  if (tag.includes("OVER") || tag.includes("SMASH")) return "OVER";

  const s =
    String(
      row?.SelectionStrategy ??
        row?.["SelectionStrategy"] ??
        row?.["Sel Strat"] ??
        row?.SelStrat ??
        row?.SS_Direction ??
        row?.["SS_Direction"] ??
        ""
    )
      .toUpperCase()
      .trim();

  if (s.includes("COIN")) return "COIN";
  if (s.includes("OVER")) return "OVER";
  if (s.includes("UNDER")) return "UNDER";

  return "NEUTRAL";
};

const narrativeTheme = (dirKey) => {
  // NOTE: these are soft, premium tints (not neon)
  switch (dirKey) {
    case "OVER":
      return {
        rail: "#16a34a",
        border: "#86efac",
        bg: "#f0fdf4",
        pillBg: "#dcfce7",
        pillText: "#166534",
        label: "Over lean",
      };
    case "UNDER":
      return {
        rail: "#dc2626",
        border: "#fca5a5",
        bg: "#fef2f2",
        pillBg: "#fee2e2",
        pillText: "#991b1b",
        label: "Under lean",
      };
    case "COIN":
      return {
        rail: "#64748b",
        border: "#cbd5e1",
        bg: "#f8fafc",
        pillBg: "#e2e8f0",
        pillText: "#334155",
        label: "Coin toss",
      };
    default:
      return {
        rail: "#9ca3af",
        border: "#e5e7eb",
        bg: "#ffffff",
        pillBg: "#f3f4f6",
        pillText: "#374151",
        label: "Analyst note",
      };
  }
};


// ---------- PlaysWithGuru taxonomy tier (Studs / Cores / Fillers) ----------
// Users can't edit this classification; it's derived from player contribution signals we already have.
// Heuristic Phase 1: use minutes if present (preferred), else fall back to projection as a proxy.
const deriveTierFromRow = (row) => {
  const minutes = safeNum(
    row.minutes_recent_mpg ??
      row.mpg ??
      row.Minutes ??
      row.minutes ??
      row["Minutes"] ??
      row["MPG"]
  );

  const proj = safeNum(
    row.Proj ?? row.proj ?? row.Projection ?? row["Projection"] ?? row["Proj"]
  );

  // Minutes-based (most stable)
  if (minutes != null) {
    if (minutes >= 34) return "STUD";
    if (minutes >= 28) return "CORE";
    return "FILLER";
  }

  // Fallback: projection-based proxy
  if (proj != null) {
    if (proj >= 30) return "STUD";
    if (proj >= 18) return "CORE";
    return "FILLER";
  }

  return "CORE";
};

const getCompareScore = (row) => {
  const delta = getDeltaValue(row) ?? 0;

  const conf =
    safeNum(
      row["Confidence"] ??
      row.confidence ??
      row["Conf"] ??
      row["confidence"] ??
      row["ConfidenceScore"] ??
      row["Confidence Score"]
    ) ?? 0;

  // Accept 0–1 OR 0–100
  let l10 = safeNum(
    row["L10_HitPct"] ??
    row["L10 Hit %"] ??
    row["L10 Hit%"] ??
    row["L10_Hit%"] ??
    row["L10"] ??
    row["L10Pct"]
  ) ?? 0;

  if (l10 > 1.01) l10 = l10 / 100;

  return Math.abs(delta) * 2 + conf * 1 + l10 * 10;
};

  // =========================
  // Decision Quality Score (DQS)
  // =========================
  // DQS is a *process score* that blends signal strength (Guru Score) with feasibility/risk compression.
  // It is not a guarantee. Use it to rank decisions, not to promise outcomes.

// Helper: extract Guru Score (numeric) from any row shape

const getGuruScoreValue = (row) => {
  const candidates = [
    row?.guruScore,
    row?.["Guru Score"],
    row?.GuruScore,
    row?.score,
  ];
  for (const c of candidates) {
    if (c === null || c === undefined) continue;
    const n = Number(c);
    if (Number.isFinite(n)) return n;
    const s = String(c).trim();
    const n2 = Number(s);
    if (Number.isFinite(n2)) return n2;
  }
  return null;
};

function getGuruAlertMeta(row) {
  const raw = String(
    row?.GuruAlert ??
    row?.guruAlert ??
    row?.["Guru Alert"] ??
    ""
  ).trim();

  const tooltip = String(
    row?.GuruAlert_Tooltip ??
    row?.guruAlertTooltip ??
    ""
  ).toUpperCase();

  const insight = String(
    row?.["AI Insight"] ??
    row?.GuruInsight ??
    row?.AnalystNote ??
    ""
  ).toUpperCase();

  const sourceText = `${tooltip} ${insight}`;

  let direction = "";
  if (sourceText.includes("UNDER")) direction = "UNDER";
  else if (sourceText.includes("OVER")) direction = "OVER";

  const isAlert = raw === "2";
  const isWatch = raw === "1";

  if (!isAlert && !isWatch) {
    return {
      label: "",
      bg: "transparent",
      color: "transparent",
      border: "transparent",
      title: "",
    };
  }

  // ---------------------------
  // 🔍 CONTEXT DETECTION
  // ---------------------------
  const sport = String(row?.Sport ?? "").toUpperCase();
  const playerType = String(
    row?.["Player Type"] ??
    row?.PlayerType ??
    ""
  ).toUpperCase();

  const prop = String(row?.["Prop Type"] ?? "").toUpperCase();

  const l2pa = Number(row?.["L2 PA Δ"] ?? row?.l2_pa_delta);
  const l2outs = Number(row?.["L2 Outs Δ"] ?? row?.l2_outs_delta);
  const l2min = Number(row?.["L2 MIN Δ"] ?? row?.l2_min_delta);
  const l2stat = Number(row?.["L2 Stat Δ"] ?? row?.l2_stat_delta);

  const dqs = Number(row?.DQS);
  const tag = String(row?.Tag ?? "").toUpperCase();

  let reason = "";

  // ---------------------------
  // ⚾ MLB LOGIC
  // ---------------------------
  if (sport === "MLB" || sport === "KBO") {
    if (playerType === "PITCHER") {
      if (Number.isFinite(l2outs)) {
        const trend = l2outs > 2 ? "SURGE" : l2outs < -2 ? "COOLING" : "STABLE";
        reason = `L2 OUTS Δ: ${l2outs.toFixed(1)} (${trend})`;
      }
    } else {
      if (Number.isFinite(l2pa)) {
        const trend = l2pa > 0.5 ? "SURGE" : l2pa < -0.5 ? "COOLING" : "STABLE";
        reason = `L2 PA Δ: ${l2pa.toFixed(1)} (${trend})`;
      }
    }
  }

  // ---------------------------
  // 🏀 NBA LOGIC
  // ---------------------------
  else if (sport === "NBA") {
    if (Number.isFinite(l2min)) {
      const trend = l2min > 2 ? "SURGE" : l2min < -2 ? "COOLING" : "STABLE";
      reason = `L2 MIN Δ: ${l2min.toFixed(1)} (${trend})`;
    }
  }

  // ---------------------------
  // 📊 STAT CONFIRMATION
  // ---------------------------
  let statNote = "";
  if (Number.isFinite(l2stat) && Math.abs(l2stat) > 0.5) {
    statNote = ` | L2 STAT Δ: ${l2stat.toFixed(1)}`;
  }

  // ---------------------------
  // 🧠 FALLBACK CONTEXT
  // ---------------------------
  let fallback = "";
  if (!reason) {
    if (Number.isFinite(dqs)) fallback = `DQS: ${dqs.toFixed(1)}`;
    else if (tag) fallback = `TAG: ${tag}`;
  }

  const fullReason = [reason, statNote, fallback].filter(Boolean).join("");

  // ---------------------------
  // 🎨 VISUALS
  // ---------------------------
  const isOver = direction === "OVER";
  const isUnder = direction === "UNDER";

  if (isAlert && isOver) {
    return {
      label: "ALERT • OVER",
      bg: "#16a34a",
      color: "#ffffff",
      border: "#16a34a",
      title: `${tooltip || "Guru Alert — Over"}${fullReason ? "\n" + fullReason : ""}`,
    };
  }

  if (isAlert && isUnder) {
    return {
      label: "ALERT • UNDER",
      bg: "#dc2626",
      color: "#ffffff",
      border: "#dc2626",
      title: `${tooltip || "Guru Alert — Under"}${fullReason ? "\n" + fullReason : ""}`,
    };
  }

  if (isWatch && isOver) {
    return {
      label: "WATCH • OVER",
      bg: "#ffffff",
      color: "#15803d",
      border: "#22c55e",
      title: `${tooltip || "Guru Watch — Over"}${fullReason ? "\n" + fullReason : ""}`,
    };
  }

  if (isWatch && isUnder) {
    return {
      label: "WATCH • UNDER",
      bg: "#ffffff",
      color: "#b91c1c",
      border: "#ef4444",
      title: `${tooltip || "Guru Watch — Under"}${fullReason ? "\n" + fullReason : ""}`,
    };
  }

  return {
    label: isAlert ? "ALERT" : "WATCH",
    bg: "#ffffff",
    color: "#374151",
    border: "#cbd5e1",
    title: `${tooltip || "Guru alert"}${fullReason ? "\n" + fullReason : ""}`,
  };
}

const scoreToGrade = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n)) return "";
  if (n >= 26) return "A+";
  if (n >= 24) return "A";
  if (n >= 22) return "A-";
  if (n >= 19) return "B+";
  if (n >= 16) return "B";
  if (n >= 14) return "B-";
  if (n >= 12) return "C+";
  if (n >= 10) return "C";
  if (n >= 8) return "C-";
  return "D";
};

const getGuruGradeValue = (row) => {
  // Direction-aware: grade reflects conviction regardless of OVER/UNDER.
  // We grade off the same blended score used for Guru Score / Compare ranking.
  return scoreToGrade(getCompareScore(row));
};

const getSelStratValue = (row) =>
  row?.["Sel Strat"] ??
  row?.SelStrat ??
  row?.selStrat ??
  row?.SelectionStrategy ??
  row?.selectionStrategy ??
  "";

// Confidence sometimes comes as a label (Elite/Strong/etc) and/or a number.
// WNBA now publishes Decision-Engine confidence through several possible export keys.
// Keep this broad so the Grid, Cards, and Tooltips all read the same backend value.
const getConfidenceValue = (row) =>
  row?.ConfidenceLabel ??
  row?.confidenceLabel ??
  row?.ConfidenceTier ??
  row?.confidenceTier ??
  row?.Guru_Pick_Confidence ??
  row?.["Guru_Pick_Confidence"] ??
  row?.GuruPickConfidence ??
  row?.guru_pick_confidence ??
  row?.PWG_Prob_Tier ??
  row?.["PWG_Prob_Tier"] ??
  row?.PWGProbTier ??
  row?.pwg_prob_tier ??
  row?.Confidence ??
  row?.confidence ??
  row?.ConfScore ??
  row?.confidence_score ??
  row?.["Confidence Score"] ??
  "";



// Helper: extract Minutes/TOI from any row shape
const getMinutesValue = (entry, activeSport = "") => {
  const sport = String(activeSport || "").toUpperCase();

  // MLB should never show minutes in Last 10
  if (sport === "MLB" || sport === "KBO") return null;

  const raw =
    entry?.MIN ??
    entry?.Min ??
    entry?.Minutes ??
    entry?.minutes ??
    entry?.mp ??
    entry?.MP ??
    entry?.["MINUTES"] ??
    entry?.["Minutes"] ??
    entry?.["Minutes Played"] ??
    entry?.["TOI (min)"] ??
    entry?.TOI ??
    entry?.toi ??
    entry?.toi_min ??
    entry?.TOI_min;

  if (raw === undefined || raw === null || raw === "") return null;

  if (typeof raw === "string" && /^\s*\d{1,2}:\d{2}\s*$/.test(raw)) {
    const [mm, ss] = raw.trim().split(":").map((x) => Number(x));
    if (Number.isFinite(mm) && Number.isFinite(ss)) return mm + ss / 60;
  }

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const getPlayerTypeValue = (row) =>
  String(
    row?.["Player Type"] ??
    row?.PlayerType ??
    row?.playerType ??
    ""
  ).trim().toUpperCase();

const getMLBOpportunityValue = (entry, playerType = "") => {
  const pt = String(playerType || "").toUpperCase();

  if (pt === "PITCHER") {
    const outs = safeNum(entry?.outs);
    if (Number.isFinite(outs)) return outs;

    const ip = safeNum(entry?.inningsPitched);
    if (Number.isFinite(ip)) return ip * 3;

    return null;
  }

  // hitter default = PA
  const pa = mlbPaValue(entry);
  return Number.isFinite(pa) ? pa : null;
};

const getMLBSecondaryOpportunityValue = (entry, playerType = "") => {
  const pt = String(playerType || "").toUpperCase();

  if (pt === "BATTER") {
    const ab = mlbAbValue(entry);
    return Number.isFinite(ab) ? ab : null;
  }

  return null;
};

const getOpportunityMetaForDetail = (row, activeSport = "") => {
  const sport = String(activeSport || "").toUpperCase();
  const playerType = getPlayerTypeValue(row);

  if (sport === "MLB" || sport === "KBO") {
    if (playerType === "PITCHER") {
      return {
        label: "Outs",
        shortLabel: "OUTS",
        secondaryLabel: null,
      };
    }
    return {
      label: "PA",
      shortLabel: "PA",
      secondaryLabel: "AB",
    };
  }

  return {
    label: "Minutes",
    shortLabel: "MIN",
    secondaryLabel: null,
  };
};

// Treat NHL TOI/Minutes props as the same metric as the MIN column in the L10 tables.
// If the selected prop itself is Minutes/TOI, we should NOT render a duplicate value column.
const isMinutesLikePropType = (propType) => {
  const p = String(propType || "").trim().toUpperCase();
  if (!p) return false;
  return (
    p === "MIN" ||
    p === "MINUTES" ||
    p.includes("TOI") ||
    p.includes("TIME ON ICE")
  );
};







// ---- Numeric getters (robust across feeds) ----
const fmtNum = (v, decimals = 2) => {
  const n = safeNum(v);
  return Number.isFinite(n) ? n.toFixed(decimals) : "—";
};

const pickNum = (obj, keys) => {
  if (!obj) return null;
  for (const k of keys) {
    // support keys like "Δ"
    const v = obj[k];
    const n = safeNum(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

// ---- Flexible key getter (handles spaces/underscores/dashes/Δ symbols) ----
const canonKey = (k) =>
  String(k ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const pickVal = (obj, keys, fallback = null) => {
  if (!obj) return fallback;

  // 1) direct hits
  for (const k of keys) {
    if (k == null) continue;
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }

  // 2) normalized key match (minutes_heat == Minutes Heat == minutes-heat)
  const objKeys = Object.keys(obj);
  for (const k of keys) {
    const target = canonKey(k);
    if (!target) continue;
    for (const ok of objKeys) {
      if (canonKey(ok) === target) {
        const v = obj[ok];
        if (v !== undefined && v !== null && v !== "") return v;
      }
    }
  }

  return fallback;
};

const getTagValue = (row) =>
  String(
    pickVal(row, ["Tag", "Result Tag", "tag", "TAG"], "")
  ).trim().toUpperCase();


const getMinutesHeatValue = (row) =>
  String(
    pickVal(row, [
      "Minutes Heat",
      "MinutesHeat",
      "minutesHeat",
      "minutes_heat",
      "Minutes_Heat",
      "Minutes-Heat",
      "MINUTES_HEAT",
    ], "")
  ).trim();

const getHeatNoteValue = (row) =>
  String(
    pickVal(row, [
      "Heat Note",
      "HeatNote",
      "heatNote",
      "heat_note",
      "Heat_Note",
      "Heat-Note",
      "HEAT_NOTE",
    ], "")
  ).trim();

const getL2MinDeltaValue = (row) => {
  const v = pickVal(row, [
    "L2 MIN Δ",
    "L2 Min Δ",
    "L2 MIN A",
    "L2 Min A",
    "L2 MIN Delta",
    "L2 Min Delta",
    "L2_MIN_DELTA",
    "L2MinDelta",
    "l2MinDelta",
  ]);
  const n = safeNum(v);
  return Number.isFinite(n) ? n : null;
};

const getL2PaDeltaValue = (row) => {
  const v = pickVal(row, [
    "L2 PA Δ",
    "L2 Pa Δ",
    "L2 PA Delta",
    "L2 Pa Delta",
    "L2_PA_DELTA",
    "L2PADelta",
    "l2PaDelta",
    "L2 Opportunity Δ",
    "L2OpportunityDelta",
    "l2OpportunityDelta",
  ]);
  const n = safeNum(v);
  return Number.isFinite(n) ? n : null;
};

const getL2StatDeltaValue = (row) => {
  const v = pickVal(row, [
    "L2 Stat Δ",
    "L2 STAT Δ",
    "L2 Stat A",
    "L2 STAT A",
    "L2 Stat Delta",
    "L2 STAT Delta",
    "L2_STAT_DELTA",
    "L2StatDelta",
    "l2StatDelta",
  ]);
  const n = safeNum(v);
  return Number.isFinite(n) ? n : null;
};

const getL2OutsDeltaValue = (row) => {
  const v = pickVal(row, [
    "L2 Outs Δ",
    "L2 OUTS Δ",
    "L2 Outs Delta",
    "L2 OUTS Delta",
    "L2_OUTS_DELTA",
    "L2OutsDelta",
    "l2OutsDelta",
    "L2 Opportunity Δ",
    "L2OpportunityDelta",
    "l2OpportunityDelta",
  ]);
  const n = safeNum(v);
  return Number.isFinite(n) ? n : null;
};

const getLineValue = (row) =>
  pickNum(row, ["Line", "line", "PropValue", "propValue", "lineValue", "LineValue"]);

const getProjValue = (row) => {
  // NBA source projection key (preferred): FinalAdjustedScore
  // Fallbacks support older/other sports sources.
  const candidates = [
    row.FinalAdjustedScore,
    row["FinalAdjustedScore"],
    row.Proj,
    row.proj,
    row.Projection,
    row.projection,
    row.Projected,
    row.projected,
    row["Proj"],
  ];

  // Prefer a non-zero finite number (some feeds use 0 as placeholder)
  for (const c of candidates) {
    const n = safeNum(c);
    if (Number.isFinite(n) && n !== 0) return n;
  }

  // If we only see a finite 0, treat as missing unless the line itself is 0 (rare)
  const line = getLineValue(row);
  for (const c of candidates) {
    const n = safeNum(c);
    if (Number.isFinite(n) && n === 0) {
      return (Number.isFinite(line) && line === 0) ? 0 : null;
    }
  }

  // Last resort: pickNum from common keys
  const p = pickNum(row, ["FinalAdjustedScore", "Proj", "proj", "Projection", "projection", "Projected", "projected"]);
  return Number.isFinite(p) ? p : null;
};

const getDeltaValue = (row) => {
  const explicit = pickNum(row, ["Δ","DELTA", "Delta", "delta", "Diff", "diff", "Edge", "edge"]);
  if (Number.isFinite(explicit)) return explicit;

  const line = getLineValue(row);
  const proj = getProjValue(row);
  if (Number.isFinite(line) && Number.isFinite(proj)) return proj - line;

  return null;
};

  // --- Line Tax helper (covers varied sheet schemas) ---
  const getLineTaxValue = (row) => {
    if (!row) return "";
    return (
      row.LineTaxTier ??
      row.Line_Tax_Tier ??
      row["LineTaxTier"] ??
      row["Line_Tax_Tier"] ??
      row["Line Tax Tier"] ??
      row["Line Tax"] ??
      row.LineTax ??
      row.lineTax ??
      row.tax ??
      row.Tax ??
      row["Tax"] ??
      ""
    );
  };



  const computeDQS = (row) => {
    const gs = Number(getGuruScoreValue(row));
    if (!Number.isFinite(gs)) return null;

    const minutes = Number(getMinutesValue(row));
    const mt = Number.isFinite(minutes) ? minutes : null;

    // Minutes feasibility multiplier (keep, but don't let it nuke DQS)
    let minutesFactor = 1.0;
    if (mt == null) minutesFactor = 0.92;
    else if (mt >= 32) minutesFactor = 1.00;
    else if (mt >= 26) minutesFactor = 0.96;
    else if (mt >= 20) minutesFactor = 0.90;
    else if (mt >= 14) minutesFactor = 0.84;
    else minutesFactor = 0.78;

    // Line Tax multiplier (light touch)
    const lt = String(getLineTaxValue(row) || "").toUpperCase();
    let taxFactor = 1.0;
    if (lt.includes("DISC")) taxFactor = 1.03;
    else if (lt.includes("FAIR")) taxFactor = 1.00;
    else if (lt.includes("TAX")) taxFactor = 0.95;

    // Unified risk factor (preferred over stacking multiple penalties)
    const rs =
      Number(
        row?.["Risk Score"] ??
        row?.RiskScore ??
        row?.riskScore ??
        row?.["RiskScore"] ??
        null
      );

    let riskFactor = 1.0;
    if (Number.isFinite(rs)) {
      // cap to an 8% hit max so risk doesn't "erase" strong edges
      riskFactor = 1.0 - Math.min(0.08, Math.max(0, rs) * 0.08);
    } else {
      // fallback: a very light blowout nudge (legacy rows)
      const bo = normalizeBlowoutTier(row);
      if (bo === "high") riskFactor = 0.96;
      else if (bo === "medium") riskFactor = 0.98;
    }

    // Prevent over-penalization: clamp the combined factor
    let totalFactor = minutesFactor * taxFactor * riskFactor;
    totalFactor = Math.max(0.78, Math.min(1.08, totalFactor));

    const dqs = gs * totalFactor;
    return Number.isFinite(dqs) ? Math.round(dqs * 100) / 100 : null;
  };

  // DQS tiering (reconciles earlier HIGH/MED/LOW language with numeric DQS)
  // You can tune these thresholds without changing the UI contract.
  const DQS_HIGH_MIN = 20;
  const DQS_MED_MIN  = 16;

  const dqsTier = (v) => {
    const d = Number(v);
    if (!Number.isFinite(d)) return "";
    if (d >= DQS_HIGH_MIN) return "HIGH";
    if (d >= DQS_MED_MIN)  return "MED";
    return "LOW";
  };

  const dqsPass = (v, filterKey) => {
  const d = Number(v);
  if (!Number.isFinite(d)) return false;
  const key = (filterKey || "ALL");
  // Filter semantics:
  // ALL  -> pass-through
  // HIGH -> d >= DQS_HIGH_MIN
  // MED  -> DQS_MED_MIN <= d < DQS_HIGH_MIN
  // LOW  -> d < DQS_MED_MIN
  if (key === "ALL") return true;
  if (key === "HIGH") return d >= DQS_HIGH_MIN;
  if (key === "MED")  return d >= DQS_MED_MIN && d < DQS_HIGH_MIN;
  if (key === "LOW")  return d < DQS_MED_MIN;
  return true;
};

const buildPlayerHover = (row) => {
  // L10 summary (already in your Excel output)
  const l10Hits   = safeNum(row["L10_Hits"]);
  const l10Misses = safeNum(row["L10_Misses"]);
  const l10Pushes = safeNum(row["L10_Pushes"]);
  // Recompute from the record so mixed legacy 0–1/0–100 fields cannot be
  // multiplied twice (WNBA stores L10_HitPct as percentage points).
  const l10Record = recentRecord(row);

  // season + line
  const seasonAvg =
    safeNum(row["Season_Avg_for_Prop"]) ??  // dedicated per-prop avg (if present)
    safeNum(row["Season_Avg"]);             // fallback to generic Season_Avg

  const line =
    safeNum(row["Line"]) ??
    safeNum(row["Prop Value"]);             // fallback to prop line

  // context
  const minutes = safeNum(
    row.minutes_recent_mpg ??
      row.mpg ??
      row.Minutes ??
      row.minutes
  );
  const ha = (row["H/A"] || row["HA"] || row["Home/Away"] || "")
    .toString()
    .trim();
  const blowout = getBlowoutTierRaw(row).toString();

  const parts = [];

  // 1) L10 vs PROP
  if (l10Hits != null || l10Misses != null || l10Pushes != null) {
    const chunks = [];
    if (l10Hits   != null) chunks.push(`${l10Hits} hits`);
    if (l10Misses != null) chunks.push(`${l10Misses} misses`);
    if (l10Pushes != null) chunks.push(`${l10Pushes} pushes`);

    let lineTxt = `L10 vs PROP: ${chunks.join(" / ")}`;
    if (l10Record.hitRate != null) {
      lineTxt += ` (${formatRate(l10Record.hitRate)} hit)`;
    }
    parts.push(lineTxt);
  }

  // 2) Season avg vs line
  if (seasonAvg != null && line != null) {
    const diff = seasonAvg - line;
    const sign = diff >= 0 ? "+" : "";
    parts.push(
      `Season avg: ${seasonAvg.toFixed(1)} vs line ${line.toFixed(
        1
      )} (${sign}${diff.toFixed(1)})`
    );
  }

  // 3) Context row
  const ctx = [];
  if (minutes != null) ctx.push(`Minutes: ${minutes.toFixed(1)}`);
  if (ha) ctx.push(`H/A: ${ha}`);
  if (blowout) ctx.push(`Blowout: ${blowout.toLowerCase()}`);
  if (ctx.length) parts.push(ctx.join(" | "));

  return parts.join(" | ");
};

//L5 Setup
function renderL5SetupCell(row) {
  const raw =
    (row?.L5_Setup ??
      row?.["L5_Setup"] ??
      row?.["L5 Setup"] ??
      row?.L5Setup ??
      "") + "";

  const v = raw.trim().toUpperCase();
  if (!v) return "";

  // Compact label (grid)
  let short = "L5";
  let bg = "#eef2ff";
  let fg = "#3730a3";

// v is already: const v = raw.trim().toUpperCase();
if (v.includes("PERFECT 5")) {
  short = "P5";   bg = "#dcfce7"; fg = "#166534";
} else if (v.includes("ICE COLD")) {
  short = "ICE";  bg = "#e0f2fe"; fg = "#075985";
} else if (v.includes("TURNING UP")) {
  short = "UP";   bg = "#dcfce7"; fg = "#166534";
} else if (v.includes("COOLING OFF")) {
  short = "COOL"; bg = "#fee2e2"; fg = "#991b1b";
} else if (v.includes("RUN THEN SLIP")) {
  short = "DIP";  bg = "#fee2e2"; fg = "#991b1b";
} else if (v.includes("SLIP THEN RUN")) {
  short = "SURGE"; bg = "#dcfce7"; fg = "#166534";
} else if (v.includes("BOUNCE")) {
  short = "BNC";  bg = "#fef9c3"; fg = "#854d0e";
} else if (v.includes("INCOMPLETE")) {
  short = "INC";  bg = "#e5e7eb"; fg = "#374151";
} else if (v.includes("3/5") || v.includes("MIXED")) {
  short = "MIX";  bg = "#fef9c3"; fg = "#854d0e";
} else if (v.includes("4/5") || v.includes("STRONG")) {
  short = "STR";  bg = "#dcfce7"; fg = "#166534";
} else if (v.includes("0-2/5") || v.includes("WEAK")) {
  short = "WEAK"; bg = "#fee2e2"; fg = "#991b1b";
}

  const tooltip = raw ? `L5 Setup: ${raw}` : "L5 Setup";

  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "3px 8px",
        borderRadius: 999,
        background: bg,
        color: fg,
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: 0.4,
        whiteSpace: "nowrap",
      }}
    >
      {short}
    </span>
  );
}


// --- Line Tax pill for grid (minimal: DISC / FAIR / TAX / HYP) ---
// ----- Line Tax (DOT ONLY) -----
function renderLineTaxCell(row) {
  // accept multiple possible field names (safe)
  const raw =
    (row?.Line_Tax_Tier ??
      row?.["Line_Tax_Tier"] ??
      row?.["Line Tax Tier"] ??
      row?.["Line Tax"] ??
      row?.Tax_Tier ??
      row?.["Tax_Tier"] ??
      row?.Tax ??
      row?.["Tax"] ??
      "") + "";

  const tier = raw.toString().trim().toUpperCase();
  if (!tier || tier === "NAN" || tier === "NONE" || tier === "NULL") return "";

  // Map tier -> dot color
  let dot = "#9ca3af"; // default gray
  let label = tier;

  if (tier.includes("DISC")) {
    dot = "#16a34a"; // green
    label = "DISC";
  } else if (tier.includes("FAIR")) {
    dot = "#f59e0b"; // amber
    label = "FAIR";
  }
  // ✅ IMPORTANT: check HYP/HYPER BEFORE TAX so "HYPER TAX" turns red
  else if (tier.includes("HYP") || tier.includes("HYPER")) {
    dot = "#dc2626"; // red
    label = "HYP";
  } else if (tier.includes("TAX") || tier.includes("EXP") || tier.includes("PRICEY")) {
    dot = "#ef6c00"; // orange
    label = "TAX";
  }


  const tooltip = `Line Tax: ${label}${raw ? ` (${raw})` : ""}`;

  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: dot,
        boxShadow: "0 0 0 2px rgba(255,255,255,0.9), 0 0 0 1px rgba(0,0,0,0.18)",
      }}
    />
  );
}


const getBlowoutTierRaw = (row) => {
  if (!row) return "";

  const keys = [
    "Blowout_Tier",
    "blowout_tier",
    "Blowout Tier",
    "BlowoutTier",
    "blowoutTier",
    "Blowout_Level",
    "blowout_level",
    "Blowout Level",
    "Blowout",
    "blowout",
    "blowout_risk",
    "Blowout_Risk",
    "Blowout Risk",
    "blowoutRisk",
    "Risk_Level",
    "risk_level",
  ];

  for (const key of keys) {
    const v = row?.[key];
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (!s || ["nan", "null", "undefined", "n/a", "na", "none"].includes(s.toLowerCase())) continue;
    return s;
  }

  // Last-resort fallback: derive display tier from spread when the loader/export
  // has not promoted blowout_tier yet.
  const spreadRaw =
    row?.Spread_Abs ??
    row?.spread_abs ??
    row?.SpreadAbs ??
    row?.["Spread Abs"] ??
    row?.Game_Spread_Abs ??
    row?.game_spread_abs ??
    row?.Spread ??
    row?.spread;

  const spread = Math.abs(Number(spreadRaw));
  if (Number.isFinite(spread)) {
    if (spread >= 8) return "HIGH";
    if (spread >= 5.5) return "MEDIUM";
    return "LOW";
  }

  return "";
};

const normalizeBlowoutTier = (rowOrValue) => {
  const raw =
    rowOrValue && typeof rowOrValue === "object"
      ? getBlowoutTierRaw(rowOrValue)
      : String(rowOrValue ?? "");

  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "";

  if (s.includes("high") || s.includes("severe")) return "high";
  if (s.includes("moderate") || s.includes("medium") || s.includes("med") || s.includes("mod")) return "medium";
  if (s.includes("low") || s.includes("competitive")) return "low";
  if (s.includes("unknown")) return "unknown";

  const n = Number(s);
  if (Number.isFinite(n)) {
    if (n >= 8) return "high";
    if (n >= 5.5) return "medium";
    if (n >= 0) return "low";
  }

  return "";
};

const renderBlowoutCell = (row) => {
  const tier = normalizeBlowoutTier(row);

  let color = "#9ca3af";
  let label = "Unknown blowout risk";

  if (tier === "low") {
    color = "#16a34a";
    label = "Low blowout risk";
  } else if (tier === "medium") {
    color = "#f59e0b";
    label = "Moderate blowout risk";
  } else if (tier === "high") {
    color = "#dc2626";
    label = "High blowout risk";
  }

  return (
    <span
      title={label}
      aria-label={`Blowout: ${label}`}
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: color,
        boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
      }}
    />
  );
};

// --- DvP Tier pill (minimal: ELT/TGH/NEU/SFT/TGT + rank) ---
const renderDvPTierCell = (row) => {
  const pos  = (row.POS || row["POS"] || "").toString().trim();
  const tierRaw = (row["Opp_DvP_Tier"] || "").toString().trim();
  const rankRaw = row["Opp_DvP_Rank"];

  // If missing, treat combo rows as R0
// If missing, don't label combos as "R0" (that's misleading). Show N/A.
if (!tierRaw) {
  const player = (row?.Player || row?.["Player"] || "").toString();
  const prop   = (row?.Prop || row?.["Prop"] || row?.["Prop Type"] || row?.["Prop_Type"] || "").toString();
  const isCombo = player.includes("+") || prop.includes("+");

  // show a subtle neutral dot so the grid stays clean + consistent
  const label = isCombo ? "DvP N/A (combo)" : "DvP N/A";

  return (
    <span
      title={label}
      aria-label={label}
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: "#d1d5db",     // neutral gray
        boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
      }}
    />
  );
}


  const tierUp = tierRaw.toUpperCase();
  const rankNum = Number(rankRaw);
  const rankText = Number.isFinite(rankNum) && rankNum > 0 ? String(Math.round(rankNum)) : "";

  let bg = "#d1d5db"; // default gray dot

  if (tierUp.includes("ELITE")) {
    bg = "#dc2626";
  } else if (tierUp.includes("TOUGH")) {
    bg = "#dc2626";
  } else if (tierUp.includes("NEUTRAL")) {
    bg = "#f59e0b";
  } else if (tierUp.includes("SOFT")) {
    bg = "#16a34a";
  } else if (tierUp.includes("TARGET")) {
    bg = "#16a34a";
  } else if (tierUp.startsWith("R0")) {
    bg = "#d1d5db";
  }
  
  //const pillText = `${shortTier}${rankText ? " " + rankText : ""}`;
  const full = `${pos ? pos + " • " : ""}${tierUp}${rankText ? " (R" + rankText + ")" : ""}`;

  return (
    <span
      title={full}
      aria-label={full}
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: bg,                 // reuse your existing bg mapping
        boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
      }}
    />
  );  
};

// --- MLB-aware grid helpers: batting order + matchup context ----------------
const getBattingOrderValue = (row) => {
  const raw = firstNonEmpty(row, [
    "battingOrder",
    "batting_order",
    "Batting Order",
    "Lineup Order",
    "lineup_order",
    "BattingOrder",
  ]);
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= 9 ? n : null;
};

const isMLBHitterRow = (row, activeSportValue = "") => {
  const sportKey = String(activeSportValue || row?.Sport || row?.sport || "").toUpperCase();
  if (sportKey !== "MLB") return false;
  return getMlbPlayerType(row) !== "PITCHER";
};

const renderBattingOrderCell = (row, activeSportValue = "") => {
  if (!isMLBHitterRow(row, activeSportValue)) return "—";
  const bo = getBattingOrderValue(row);
  if (!Number.isInteger(bo)) return "—";

  let bg = "#f8fafc";
  let fg = "#334155";
  let label = "Lineup spot";

  if (bo >= 1 && bo <= 4) {
    bg = "#dcfce7";
    fg = "#166534";
    label = "Top-order hitter";
  } else if (bo >= 5 && bo <= 6) {
    bg = "#fef3c7";
    fg = "#92400e";
    label = "Middle-order hitter";
  } else if (bo >= 7 && bo <= 8) {
    bg = "#dbeafe";
    fg = "#1d4ed8";
    label = "Lower-order value pocket";
  } else if (bo === 9) {
    bg = "#fee2e2";
    fg = "#991b1b";
    label = "Nine-hole hitter";
  }

  return (
    <span
      title={`${label}: batting order ${bo}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 34,
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        background: bg,
        color: fg,
        whiteSpace: "nowrap",
      }}
    >
      #{bo}
    </span>
  );
};

const renderMatchupTierCell = (row, activeSportValue = "") => {
  const sportKey = String(activeSportValue || row?.Sport || row?.sport || "").toUpperCase();

  // MLB needs different matchup context for hitters vs pitchers.
  if (["MLB", "KBO"].includes(sportKey)) {
    const chips = getMlbEnvironmentChips(row, sportKey);
    if (!chips.length) return "—";

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
          maxWidth: 280,
        }}
      >
        {chips.slice(0, 3).map((chip, idx) => (
          <span
            key={`${chip.label}-${idx}`}
            title={chip.title || chip.label}
            style={{
              ...mlbEnvChipStyle(chip.tone),
              padding: "3px 8px",
              fontSize: 10.5,
            }}
          >
            {chip.label}
          </span>
        ))}
      </div>
    );
  }

  // Non-MLB sports keep the existing DvP tier behavior.
  return renderDvPTierCell(row);
};
// --------------------------------------------------------------------------

const soccerDetailNumber = (value, digits = 2) => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("en-US", { maximumFractionDigits: digits });
};

const soccerDetailPercent = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  const percent = Math.abs(number) <= 1 ? number * 100 : number;
  return `${percent.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
};

const soccerHistoryArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === "object") return Object.values(value).filter(Boolean);
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const getSoccerHistoryRows = (row, range = "L10") => {
  const source = range === "L20" ? row?.Last20 : row?.Last10;
  const limit = range === "L20" ? 20 : 10;
  return soccerHistoryArray(source).slice(0, limit);
};

const soccerActualValue = (entry) => {
  const raw = entry?.["Selected Stat Actual"] ?? entry?.Actual;
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const soccerMinutesValue = (entry) => {
  const raw = entry?.Minutes ?? entry?.MIN;
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const soccerAverage = (values) => {
  const clean = values.filter(Number.isFinite);
  return clean.length ? clean.reduce((sum, value) => sum + value, 0) / clean.length : null;
};

const soccerMinutesTrendKey = (row) => {
  const minutes = getSoccerHistoryRows(row, "L10")
    .map(soccerMinutesValue)
    .filter(Number.isFinite);
  if (!minutes.length) return "";

  const recent = minutes.slice(0, Math.min(3, minutes.length));
  const prior = minutes.slice(3, 6);
  const recentAverage = soccerAverage(recent);
  const priorAverage = soccerAverage(prior);
  const l5Raw = row?.["L5 Avg Minutes"];
  const l5Average = l5Raw === null || l5Raw === undefined || l5Raw === "" ? null : Number(l5Raw);
  const effectiveL5Average = Number.isFinite(l5Average)
    ? l5Average
    : soccerAverage(minutes.slice(0, 5));
  const mean = soccerAverage(minutes);
  const variance = Number.isFinite(mean)
    ? minutes.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / minutes.length
    : null;
  const standardDeviation = Number.isFinite(variance) ? Math.sqrt(variance) : null;

  if (Number.isFinite(effectiveL5Average) && effectiveL5Average < 45) return "LOW";
  if (Number.isFinite(standardDeviation) && standardDeviation >= 18) return "VOLATILE";
  if (Number.isFinite(recentAverage) && Number.isFinite(priorAverage)) {
    const delta = recentAverage - priorAverage;
    if (delta >= 8) return "RISING";
    if (delta <= -8) return "FALLING";
  }
  return "STABLE";
};

const soccerPatternKey = (row) => {
  const lineRaw = row?.["Prop Value"] ?? row?.Line;
  if (lineRaw === null || lineRaw === undefined || lineRaw === "") return "";
  const line = Number(lineRaw);
  const values = getSoccerHistoryRows(row, "L10")
    .map(soccerActualValue)
    .filter(Number.isFinite);
  if (!Number.isFinite(line) || values.length < 3) return "";

  const recentFive = values.slice(0, 5);
  const outcomes = recentFive.map((value) => value > line ? "O" : value < line ? "U" : "P");
  const overCount = outcomes.filter((outcome) => outcome === "O").length;
  const latest = values[0];
  const previous = values[1];
  const twoBack = values[2];

  if (recentFive.length === 5 && overCount === 5) return "PERFECT_5";
  if (recentFive.length === 5 && overCount === 4) return "FOUR_OF_FIVE";
  if (latest > line && previous < line && twoBack < line) return "BOUNCE";
  if (latest < line && previous > line && twoBack > line) return "RUN_THEN_SLIP";
  if (latest > previous && previous > twoBack) return "TURNING_UP";
  if (latest < previous && previous < twoBack) return "COOLING_OFF";

  if (values.length >= 5) {
    const latestTwoAverage = soccerAverage(values.slice(0, 2));
    const priorThreeAverage = soccerAverage(values.slice(2, 5));
    const materialMove = Math.max(Math.abs(line) * 0.1, 1);
    if (Number.isFinite(latestTwoAverage) && Number.isFinite(priorThreeAverage) && latestTwoAverage - priorThreeAverage >= materialMove) {
      return "SURGING";
    }
  }

  const transitions = outcomes.reduce((count, outcome, index) => (
    index > 0 && outcome !== outcomes[index - 1] ? count + 1 : count
  ), 0);
  return transitions >= 2 ? "CHOPPY_MIXED" : "";
};

const soccerHistoryStatusLabel = (value) => {
  const raw = String(value || "").trim().toUpperCase();
  if (raw === "AVAILABLE_COMPETITION_HISTORY") return "Available";
  if (raw === "NO_HISTORICAL_DATA") return "No Historical Data";
  return raw ? raw.replaceAll("_", " ") : "—";
};

const soccerMinutesTrendLabel = (key) => ({
  RISING: "Minutes Rising",
  STABLE: "Minutes Stable",
  FALLING: "Minutes Falling",
  VOLATILE: "Minutes Volatile",
  LOW: "Low Minutes",
}[key] || "Minutes Unknown");

function SoccerRecentFormStrip({ row }) {
  const lineRaw = row?.["Prop Value"] ?? row?.Line;
  const line = lineRaw === null || lineRaw === undefined || lineRaw === "" ? null : Number(lineRaw);
  const recentGames = selectCompletedHistory(soccerHistoryArray(row?.Last10), {
    getValue: soccerActualValue,
    limit: 5,
  });
  const recentValues = recentGames.map((game) => game.value);
  const average = soccerAverage(recentValues);
  const overCount = Number.isFinite(line)
    ? recentValues.filter((value) => value > line).length
    : null;
  const datePresentation = historyDatePresentation(recentGames, {
    sport: row?.Sport || "SOCCER",
    referenceDate: row?.GameDate || row?.Slate || new Date(),
  });
  return (
    <section style={{ marginTop: 14, border: "1px solid #E2E8F0", borderRadius: 14, padding: 14, background: "#FFFFFF" }}>
      {recentValues.length ? (
        <>
          {datePresentation.seasonLabel && <div data-testid="history-season-indicator" style={{ color: "#64748B", fontSize: 12, fontWeight: 800, marginBottom: 8 }}>{datePresentation.seasonLabel}</div>}
          <HistoricalPerformanceChart
            games={recentGames.map((game, index) => ({
              key: `${game.row?.Date || game.row?.GameDate || index}-${index}`,
              value: game.value,
              dateLabel: formatHistoryDate(game, { includeYear: datePresentation.includeYear }),
              opponentLabel: game.row?.Opponent || game.row?.opponent || game.row?.Opp || "—",
            }))}
            line={line}
            range="L5"
            period={DETAILS_PERIODS.FULL_GAME}
            formatValue={(value) => soccerDetailNumber(value)}
          />
          <div style={{ marginTop: 11, display: "flex", flexWrap: "wrap", gap: 16, color: "#475569", fontSize: 13, fontWeight: 850 }}>
            {overCount !== null && <span>Over line: {overCount} of {recentValues.length}</span>}
            {Number.isFinite(average) && <span>Average: {soccerDetailNumber(average)}</span>}
          </div>
        </>
      ) : (
        <div style={{ marginTop: 8, color: "#64748B", fontSize: 13, fontWeight: 700 }}>Recent-game data unavailable.</div>
      )}
    </section>
  );
}

function SoccerResearchContext({ row, metric }) {
  const context = row?.["Research Context"];
  if (!context) return null;
  const tendency = context.team_tendency || {};
  const environment = context.expected_possession_environment || {};
  const allowance = context.opponent_allowance || {};
  const usage = context.player_usage || {};
  const stability = context.starting_minutes_stability || {};
  const formation = context.formation_role || {};
  const continuity = context.role_continuity || {};
  const evidence = context.evidence_sample || {};
  const percent = (value) => value == null ? "Unavailable" : `${soccerDetailNumber(Number(value) * 100, 1)}%`;
  const missing = context.missing_reasons?.length ? context.missing_reasons.join("; ") : "None recorded";
  return (
    <section data-testid="soccer-research-context" style={{ marginTop: 14, border: "1px solid #BFDBFE", borderRadius: 14, padding: 14, background: "#EFF6FF", overflowWrap: "anywhere" }}>
      <div style={{ color: "#1E3A8A", fontWeight: 950 }}>{context.disclaimer || "Research context — not included in PWG rating"}</div>
      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
        {metric("Team tendency", tendency.value != null ? `${soccerDetailNumber(tendency.value)}% expected possession • L10 ${soccerDetailNumber(tendency.team_l10)}% • ${tendency.sample_size || 0} matches` : "Unavailable")}
        {metric("Expected possession environment", environment.team_expected_possession != null ? `${soccerDetailNumber(environment.team_expected_possession)}% team • opponent L10 ${soccerDetailNumber(environment.opponent_l10_possession)}%` : "Unavailable")}
        {metric("Opponent allowance", allowance.l10 != null ? `${soccerDetailNumber(allowance.l10)} L10 • ${allowance.metric} • ${allowance.sample_size || 0} matches` : `Insufficient sample • ${allowance.sample_size || 0} matches`)}
        {metric("Qualified opponent rank", allowance.rank != null ? `#${allowance.rank} of ${allowance.ranked_population}` : allowance.rank_status || "Insufficient sample")}
        {metric("Player role / usage", usage.share_l10 != null ? `${percent(usage.share_l10)} L10 share • ${usage.sample_size || 0} matches` : "Unavailable")}
        {metric("Starting / minutes stability", stability.starting_rate != null ? `${percent(stability.starting_rate)} starts • median ${soccerDetailNumber(stability.minutes_median)} min • ${stability.sample_size || 0} matches` : "Unavailable")}
        {metric("Formation role", formation.position != null ? `Position ${formation.position} • stability ${percent(formation.stability)}` : "Unavailable")}
        {metric("Role continuity", continuity.identity_status === "RESOLVED" ? `${continuity.home_away || ""} • team identity resolved` : continuity.uncertainty || "Unavailable")}
        {metric("Evidence quality", `${context.quality || "UNAVAILABLE"} • ${context.evidence_label || context.evidence_season || ""}`)}
        {metric("Evidence sample", `${evidence.player_prior || 0} player matches • ${evidence.team_possession || 0} team • ${evidence.opponent_possession || 0} opponent`)}
        {context.missing_reasons?.length ? metric("Missing factors", missing) : null}
      </div>
    </section>
  );
}

export function SoccerSubscriberDetails({ row }) {
  const line = row?.["Prop Value"] ?? row?.Line;
  const factStyle = {
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: "10px 12px",
    background: "#FFFFFF",
    minWidth: 0,
  };
  const labelStyle = { color: "#64748B", fontSize: 12, fontWeight: 800 };
  const valueStyle = { color: "#0F172A", fontSize: 16, fontWeight: 950, marginTop: 3, overflowWrap: "anywhere" };
  const metric = (label, value) => (
    <div key={label} style={factStyle}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value}</div>
    </div>
  );

  return (
    <div data-testid="soccer-subscriber-details" style={{ padding: 16, overflowY: "auto", background: "#F8FAFC" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
        {metric("Prop", row?.["Prop Type"] || "—")}
        {metric("Current Line", soccerDetailNumber(line))}
        {metric("Guru Direction", row?.["Guru Direction"] || "—")}
        {metric("Guru Confidence", row?.["Guru Confidence"] !== null && row?.["Guru Confidence"] !== undefined ? `${soccerDetailNumber(row["Guru Confidence"], 0)}%` : "—")}
        {showCustomerGuruTierTile(row) && metric("Guru Tier", row?.["Guru Tier"] || "—")}
      </div>

      <SoccerRecentFormStrip row={row} />

      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
        {metric("L5 Average", soccerDetailNumber(row?.["L5 Avg"]))}
        {metric("L5 Hit Rate", soccerDetailPercent(row?.["L5 Hit Rate"]))}
        {metric("L10 Average", soccerDetailNumber(row?.["L10 Avg"]))}
        {metric("L10 Hit Rate", soccerDetailPercent(row?.["L10 Hit Rate"]))}
        {metric("Season Average", soccerDetailNumber(row?.["Season Avg"]))}
        {metric("Deltas L5 / L10 / Season", `${soccerDetailNumber(row?.["L5 Delta"])} / ${soccerDetailNumber(row?.["L10 Delta"])} / ${soccerDetailNumber(row?.["Season Delta"])}`)}
        {metric("Opponent Defense", row?.["Opponent Defense Tier"] ? `${row?.["Opponent Defense Metric Label"] || "Defense"}: ${row["Opponent Defense Tier"]} • #${row["Opponent Defense Rank"]}` : "—")}
        {metric("Home/Away Average", soccerDetailNumber(row?.["Home/Away Avg"]))}
        {metric("Recent Minutes", soccerMinutesTrendLabel(soccerMinutesTrendKey(row)))}
        {metric("L5 Relative Edge", soccerDetailPercent(row?.["L5 Relative Edge %"]))}
      </div>
      <SoccerResearchContext row={row} metric={metric} />
    </div>
  );
}

const nflHistoryRows = (row, range = "L10") => {
  const source = range === "L20" ? row?.Last20 : row?.Last10;
  return soccerHistoryArray(source).slice(0, range === "L20" ? 20 : 10);
};

const nflActualValue = (entry) => {
  const value = Number(entry?.["Selected Stat Actual"] ?? entry?.actual);
  return Number.isFinite(value) ? value : null;
};

const nflOpportunityMeta = (row) => {
  const prop = String(row?.["Prop Type"] || "");
  if (/receiving yards|receptions|rec targets|rec tds/i.test(prop)) return { label: "Targets", keys: ["targets"] };
  if (/rush yards|rush tds/i.test(prop)) return { label: "Carries", keys: ["carries", "rush_attempts"] };
  if (/pass yards|pass tds|\bint\b/i.test(prop)) return { label: "Pass Attempts", keys: ["attempts", "pass_attempts"] };
  if (/pass\+rush/i.test(prop)) return { label: "Total Attempts", keys: ["attempts", "pass_attempts", "rush_attempts"] };
  if (/fg made/i.test(prop)) return { label: "FG Attempts", keys: ["field_goal_attempts", "fg_attempts"] };
  if (/sacks/i.test(prop)) return { label: "Defensive Snaps", keys: ["defensive_snaps"] };
  return { label: "Opportunity", keys: [] };
};

const nflOpportunityValue = (entry, meta) => {
  const values = meta.keys
    .map((key) => entry?.[key])
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(Number)
    .filter(Number.isFinite);
  if (meta.label === "Total Attempts") {
    const pass = [entry?.attempts, entry?.pass_attempts].filter((value) => value !== null && value !== undefined && value !== "").map(Number).find(Number.isFinite);
    const rush = [entry?.carries, entry?.rush_attempts].filter((value) => value !== null && value !== undefined && value !== "").map(Number).find(Number.isFinite);
    return Number.isFinite(pass) || Number.isFinite(rush) ? (pass || 0) + (rush || 0) : null;
  }
  // The remaining keys are provider aliases for one metric, not additive fields.
  if (values.length) return values[0];
  return null;
};

function NFLRecentFormStrip({ row }) {
  const line = Number(row?.["Prop Value"]);
  const games = selectCompletedHistory(soccerHistoryArray(row?.Last10), {
    getValue: nflActualValue,
    limit: 5,
  });
  const values = games.map((game) => game.value);
  const average = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
  const overCount = Number.isFinite(line) ? values.filter((value) => value > line).length : null;
  const sport = row?.Sport || "NFL";
  const datePresentation = historyDatePresentation(games, {
    sport,
    referenceDate: row?.GameDate || row?.Slate || new Date(),
  });
  return (
    <section style={{ marginTop: 14, border: "1px solid #E2E8F0", borderRadius: 14, padding: 14, background: "#FFFFFF" }}>
      {datePresentation.seasonLabel && <div data-testid="history-season-indicator" style={{ color: "#64748B", fontSize: 12, fontWeight: 800, marginBottom: 8 }}>{datePresentation.seasonLabel}</div>}
      {values.length ? (
        <>
          <HistoricalPerformanceChart
            games={games.map((game, index) => {
              return {
                key: `${game.timestamp || index}-${index}`,
                value: game.value,
                dateLabel: formatHistoryDate(game, { includeYear: datePresentation.includeYear }),
                opponentLabel: game.row?.Opponent || game.row?.opponent || game.row?.Opp || "—",
              };
            })}
            line={line}
            range="L5"
            period={DETAILS_PERIODS.FULL_GAME}
            formatValue={(value) => soccerDetailNumber(value)}
          />
          <div style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap", color: "#475569", fontSize: 13, fontWeight: 850 }}>
            {overCount !== null && <span>Over line: {overCount} of {values.length}</span>}
            {Number.isFinite(average) && <span>Average: {soccerDetailNumber(average)}</span>}
          </div>
        </>
      ) : <div style={{ marginTop: 8, color: "#64748B", fontWeight: 700 }}>Recent-game data unavailable.</div>}
    </section>
  );
}

function NFLSubscriberDetails({ row }) {
  const isCFB = String(row?.Sport || "").toUpperCase() === "CFB";
  const metric = (label, value) => (
    <div key={label} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: "10px 12px", background: "#FFFFFF" }}>
      <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>{label}</div>
      <div style={{ color: "#0F172A", fontSize: 16, fontWeight: 950, marginTop: 3 }}>{value ?? "—"}</div>
    </div>
  );
  return (
    <div data-testid="nfl-subscriber-details" style={{ padding: 16, overflowY: "auto", background: "#F8FAFC" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
        {metric("Prop", `${row?.["Prop Type"] || "—"} ${row?.["Prop Value"] ?? ""}`)}
        {metric("Guru Direction", row?.["Guru Direction"])}
        {metric("Guru Confidence", row?.["Guru Confidence"] != null ? `${soccerDetailNumber(row["Guru Confidence"], 0)}%` : "—")}
        {showCustomerGuruTierTile(row) && metric("Guru Tier", row?.["Guru Tier"])}
        {isCFB && <CustomerLineTypeTile row={row} />}
        {isCFB && metric("Guru Eligibility", <CFBEligibilityDisplay row={row} />)}
      </div>
      <NFLRecentFormStrip row={row} />
      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
        {metric("L5 Average", soccerDetailNumber(row?.["L5 Avg"]))}
        {metric("L5 Hit Rate", soccerDetailPercent(row?.["L5 Hit Rate"]))}
        {metric("L10 Average", soccerDetailNumber(row?.["L10 Avg"]))}
        {metric("L10 Hit Rate", soccerDetailPercent(row?.["L10 Hit Rate"]))}
        {metric("Season Average", soccerDetailNumber(row?.["Season Avg"]))}
        {metric("Role Trend", row?.["Role Trend"])}
        {metric("Matchup Environment", row?.["Matchup Environment"])}
      </div>
      {isCFB && row?.["Research Context"] && (() => {
        const context = row["Research Context"];
        const tendency = context.team_tendency || {};
        const allowance = context.opponent_allowance || {};
        const usage = context.player_usage || {};
        const continuity = context.continuity || {};
        const environment = context.game_environment || {};
        const pct = usage.share != null ? `${soccerDetailNumber(Number(usage.share) * 100, 1)}%` : "Unavailable";
        const rank = allowance.rank != null
          ? `#${allowance.rank} of ${allowance.ranked_teams} (${allowance.rank_direction}); ${allowance.sample_size} games, ${context.source_season}`
          : `Insufficient sample; ${allowance.sample_size || 0} games, ${context.source_season}`;
        return (
          <section data-testid="cfb-research-context" style={{ marginTop: 14, border: "1px solid #BFDBFE", borderRadius: 14, padding: 14, background: "#EFF6FF", overflowWrap: "anywhere" }}>
            <div style={{ color: "#1E3A8A", fontWeight: 950 }}>{context.disclaimer || "Research context — not included in PWG rating"}</div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
              {metric("Team tendency", tendency.value_per_game != null ? `${soccerDetailNumber(tendency.value_per_game)} ${String(tendency.metric || "").replaceAll("_", " ")}/game • ${tendency.sample_size} games` : "Unavailable")}
              {metric("Opponent allowance", allowance.value_per_game != null ? `${soccerDetailNumber(allowance.value_per_game)} • ${allowance.metric}` : "Unavailable")}
              {metric("Qualified opponent rank", rank)}
              {metric("Player role / usage", usage.recent_role ? `${usage.recent_role} • share ${pct}` : `Share ${pct}`)}
              {metric("Role continuity", continuity.team_continuity || "Unavailable")}
              {metric("Game environment", environment.value != null ? `${soccerDetailNumber(environment.value)} combined points/game • ${environment.sample_size} games` : "Unavailable")}
              {metric("Evidence quality", `${context.quality || "UNAVAILABLE"} • ${context.season_label || ""}`)}
              {context.missing_reasons?.length ? metric("Missing factors", context.missing_reasons.join("; ")) : null}
            </div>
          </section>
        );
      })()}
    </div>
  );
}

function NFLIntelligenceDetails({ row, isNarrow = false }) {
  const isCFB = String(row?.Sport || "").toUpperCase() === "CFB";
  const [chartRange, setChartRange] = React.useState("L10");
  const [showBands, setShowBands] = React.useState(false);
  const opportunity = nflOpportunityMeta(row);
  const points = (range) => [...nflHistoryRows(row, range)].map((game) => ({
    date: game?.Date || game?.game_date || "",
    matchup: game?.Opponent || game?.opponent ? `vs ${game?.Opponent || game?.opponent}` : "",
    value: nflActualValue(game),
    opportunity: nflOpportunityValue(game, opportunity),
  })).filter((point) => Number.isFinite(point.value)).reverse();
  const metric = (label, value) => (
    <div key={label} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 12, background: "#FFFFFF" }}>
      <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>{label}</div>
      <div style={{ color: "#0F172A", fontWeight: 950, marginTop: 3 }}>{value ?? "—"}</div>
    </div>
  );
  return (
    <div data-testid="nfl-intelligence-details" style={{ padding: 16, overflowY: "auto", background: "#F8FAFC" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
        {metric("Line Type", canonicalLineType(row))}
        {metric("Primary Market Line", soccerDetailNumber(row?.["Primary Market Line"]))}
        {metric("Guru Eligibility", isCFB ? <CFBEligibilityDisplay row={row} admin /> : (row?.["Guru Pick Eligible"] ? "ELIGIBLE" : row?.["Guru Pick Exclusion Reason"] || "RESEARCH ONLY"))}
      </div>
      <NFLRecentFormStrip row={row} />
      {(points("L10").length || points("L20").length) ? (
        <HistoricalGamesDisclosure defaultOpen={!isNarrow} resetKey={`${row?.Player || ""}|${row?.["Prop Type"] || ""}|${row?.["Prop Value"] ?? ""}`}>
          <Sparkline points={points("L10")} last20Points={points("L20")} lineValue={Number(row?.["Prop Value"])} height={150} chartRange={chartRange} setChartRange={setChartRange} showBands={showBands} setShowBands={setShowBands} opportunityLabel={opportunity.label} enableCombinedMode auditMode statLabel={row?.["Prop Type"] || "Stat"} />
        </HistoricalGamesDisclosure>
      ) : null}
      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
        {metric("Season / L10 / L5", `${soccerDetailNumber(row?.["Season Avg"])} / ${soccerDetailNumber(row?.["L10 Avg"])} / ${soccerDetailNumber(row?.["L5 Avg"])}`)}
        {metric("Hit Rates S / L10 / L5", `${soccerDetailPercent(row?.["Season Hit Rate"])} / ${soccerDetailPercent(row?.["L10 Hit Rate"])} / ${soccerDetailPercent(row?.["L5 Hit Rate"])}`)}
        {metric("Deltas S / L10 / L5", `${soccerDetailNumber(row?.["Season Delta"])} / ${soccerDetailNumber(row?.["L10 Delta"])} / ${soccerDetailNumber(row?.["L5 Delta"])}`)}
        {metric("Defense Metric", row?.["Opponent Defense Metric"])}
        {metric("Defense Value", soccerDetailNumber(row?.["Opponent Defense Value"]))}
        {metric("Defense Rank / Field", row?.["Opponent Defense Rank"] ? `#${row["Opponent Defense Rank"]} of ${row["Defense Ranked Teams"]}` : "—")}
        {metric("Defense Tier", row?.["Opponent Defense Tier"])}
        {metric("Defense Sample", row?.["Defense Sample"])}
        {metric("Defense Rank Type", row?.["Defense Rank Type"])}
        {metric("Role Trend", row?.["Role Trend"])}
        {metric("Matchup Environment", row?.["Matchup Environment"])}
        {metric("Team Continuity", row?.["Team Continuity"])}
        {metric("Current Team", row?.["Current Team"] || row?.Team)}
        {metric("Historical Team", row?.["Historical Team"])}
        {metric("Role Continuity", row?.["Role Continuity"])}
        {metric("Data Quality", row?.["Data Quality"])}
        {metric("Decision Strength (internal)", row?.["Decision Strength (internal)"])}
      </div>
    </div>
  );
}

export function SoccerIntelligenceDetails({ row, showInternalStrength = false, isNarrow = false }) {
  const [chartRange, setChartRange] = React.useState("L10");
  const [showBands, setShowBands] = React.useState(false);
  const line = row?.["Prop Value"];
  const strengthNumber = Number(row?.["Guru Edge Score"]);
  const hasStrength = Number.isFinite(strengthNumber);
  const strengthText = hasStrength ? `${strengthNumber > 0 ? "+" : ""}${soccerDetailNumber(strengthNumber, 1)}` : "—";
  const eligible = row?.["Guru Pick Eligible"] === true;
  const primary = row?.["Primary Guru Pick"] === true;
  const performanceRows = [
    { label: "Season", avg: row?.["Season Avg"], hitRate: row?.["Season Hit Rate"], sample: row?.["Season Stat Samples"], edge: row?.["Season Stat Edge"], relativeEdge: row?.["Season Relative Edge %"] },
    { label: "Last 10", avg: row?.["L10 Avg"], hitRate: row?.["L10 Hit Rate"], sample: row?.["L10 Stat Samples"], edge: row?.["L10 Stat Edge"], relativeEdge: row?.["L10 Relative Edge %"] },
    { label: "Last 5", avg: row?.["L5 Avg"], hitRate: row?.["L5 Hit Rate"], sample: row?.["L5 Stat Samples"], edge: row?.["L5 Stat Edge"], relativeEdge: row?.["L5 Relative Edge %"] },
    { label: "Home/Away", avg: row?.["Home/Away Avg"], hitRate: null, sample: null, edge: row?.["Home/Away Stat Edge"], relativeEdge: row?.["Home/Away Relative Edge %"] },
  ];
  const per90Rows = [
    { label: "Season", value: row?.["Season Per90"] },
    { label: "Last 10", value: row?.["L10 Per90"] },
    { label: "Last 5", value: row?.["L5 Per90"] },
  ];
  const l10History = getSoccerHistoryRows(row, "L10");
  const l20History = getSoccerHistoryRows(row, "L20");
  const historyPoints = (rows) => [...rows]
    .map((entry) => ({
      date: entry?.Date || entry?.GameDate || "",
      matchup: entry?.Matchup || (entry?.Opponent ? `vs ${entry.Opponent}` : ""),
      value: soccerActualValue(entry),
      opportunity: soccerMinutesValue(entry),
    }))
    .filter((point) => Number.isFinite(point.value))
    .reverse();
  const quickFacts = [
    ["L5 average vs line", row?.["L5 Avg"] !== null && row?.["L5 Avg"] !== undefined ? `${soccerDetailNumber(row["L5 Avg"])} vs ${soccerDetailNumber(line)}` : null],
    ["L10 average vs line", row?.["L10 Avg"] !== null && row?.["L10 Avg"] !== undefined ? `${soccerDetailNumber(row["L10 Avg"])} vs ${soccerDetailNumber(line)}` : null],
    ["L5 hit rate", row?.["L5 Hit Rate"] !== null && row?.["L5 Hit Rate"] !== undefined ? soccerDetailPercent(row["L5 Hit Rate"]) : null],
    ["L10 hit rate", row?.["L10 Hit Rate"] !== null && row?.["L10 Hit Rate"] !== undefined ? soccerDetailPercent(row["L10 Hit Rate"]) : null],
    ["Season average", row?.["Season Avg"] !== null && row?.["Season Avg"] !== undefined ? soccerDetailNumber(row["Season Avg"]) : null],
    ["Home/Away average", row?.["Home/Away Avg"] !== null && row?.["Home/Away Avg"] !== undefined ? soccerDetailNumber(row["Home/Away Avg"]) : null],
    ["L5 relative edge", row?.["L5 Relative Edge %"] !== null && row?.["L5 Relative Edge %"] !== undefined ? soccerDetailPercent(row["L5 Relative Edge %"]) : null],
    ["L10 relative edge", row?.["L10 Relative Edge %"] !== null && row?.["L10 Relative Edge %"] !== undefined ? soccerDetailPercent(row["L10 Relative Edge %"]) : null],
    ["Guru confidence", row?.["Guru Confidence"] !== null && row?.["Guru Confidence"] !== undefined ? `${soccerDetailNumber(row["Guru Confidence"], 0)}%` : null],
    ["Guru tier", row?.["Guru Tier"] || null],
  ].filter(([, value]) => value !== null && value !== "");

  const sectionStyle = {
    border: "1px solid #E2E8F0",
    borderRadius: 16,
    padding: 16,
    background: "#FFFFFF",
  };
  const headingStyle = {
    margin: 0,
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  };
  const labelStyle = { color: "#64748B", fontSize: 12, fontWeight: 850 };
  const valueStyle = { color: "#0F172A", fontSize: 16, fontWeight: 950, marginTop: 3 };
  const metric = (label, value) => (
    <div key={label} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: "10px 12px", background: "#FFFFFF", minWidth: 0 }}>
      <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>{label}</div>
      <div style={{ color: "#0F172A", fontSize: 16, fontWeight: 950, marginTop: 3, overflowWrap: "anywhere" }}>{value}</div>
    </div>
  );

  return (
    <div data-testid="soccer-intelligence-details" style={{ padding: 16, overflowY: "auto", background: "#F8FAFC" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
        {[
          ["Prop", row?.["Prop Type"] || "—"],
          ["Current Line", soccerDetailNumber(line)],
          ["L5 Edge", soccerDetailPercent(row?.["L5 Relative Edge %"])],
          ["L10 Edge", soccerDetailPercent(row?.["L10 Relative Edge %"])],
        ].map(([label, value]) => (
          <div
            key={label}
            style={sectionStyle}
            title={label === "L5 Edge"
              ? "L5 Edge shows how far the player's Last-5 average is above or below today's line."
              : label === "L10 Edge"
                ? "L10 Edge shows how far the player's Last-10 average is above or below today's line."
                : undefined}
          >
            <div style={labelStyle}>{label}</div>
            <div style={valueStyle}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <SoccerRecentFormStrip row={row} />

      <SoccerResearchContext row={row} metric={metric} />

      {(l10History.length > 0 || l20History.length > 0) && (
        <HistoricalGamesDisclosure defaultOpen={!isNarrow} resetKey={`${row?.Player || ""}|${row?.["Prop Type"] || ""}|${row?.["Prop Value"] ?? ""}`} style={{ marginTop: 12 }}>
          <Sparkline
            points={historyPoints(l10History)}
            last20Points={historyPoints(l20History)}
            lineValue={Number(line)}
            height={150}
            chartRange={chartRange}
            setChartRange={setChartRange}
            showBands={showBands}
            setShowBands={setShowBands}
            opportunityLabel="Minutes"
            enableCombinedMode
            auditMode
            statLabel={row?.["Prop Type"] || "Stat"}
          />
        </HistoricalGamesDisclosure>
      )}

      <section style={{ ...sectionStyle, marginTop: 12 }}>
        <h3 style={headingStyle}>Performance</h3>
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table style={{ width: "100%", minWidth: 480, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {['Window', 'Avg', 'Hit Rate', 'Sample', 'Stat Edge', 'Relative Edge'].map((header) => (
                  <th key={header} style={{ padding: "8px 10px", borderBottom: "1px solid #CBD5E1", textAlign: header === 'Window' ? 'left' : 'right', ...labelStyle }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {performanceRows.map((item) => (
                <tr key={item.label}>
                  <td style={{ padding: "10px", borderBottom: "1px solid #E2E8F0", fontWeight: 900, color: "#0F172A" }}>{item.label}</td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #E2E8F0", textAlign: "right", fontWeight: 850 }}>{soccerDetailNumber(item.avg)}</td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #E2E8F0", textAlign: "right", fontWeight: 850 }}>{soccerDetailPercent(item.hitRate)}</td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #E2E8F0", textAlign: "right", fontWeight: 850 }}>{soccerDetailNumber(item.sample, 0)}</td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #E2E8F0", textAlign: "right", fontWeight: 850 }}>{soccerDetailNumber(item.edge)}</td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #E2E8F0", textAlign: "right", fontWeight: 850 }}>{soccerDetailPercent(item.relativeEdge)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ ...sectionStyle, marginTop: 12 }}>
        <h3 style={headingStyle}>Per-90</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 12 }}>
          {per90Rows.map((item) => (
            <div key={item.label} style={{ padding: 12, borderRadius: 12, background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
              <div style={labelStyle}>{item.label}</div>
              <div style={valueStyle}>{soccerDetailNumber(item.value, 4)}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
          <span style={{ ...labelStyle, padding: "7px 10px", borderRadius: 999, background: "#F1F5F9" }}>L5 Avg Minutes: {soccerDetailNumber(row?.["L5 Avg Minutes"], 1)}</span>
          <span style={{ ...labelStyle, padding: "7px 10px", borderRadius: 999, background: "#F1F5F9" }}>L10 Avg Minutes: {soccerDetailNumber(row?.["L10 Avg Minutes"], 1)}</span>
        </div>
      </section>

      <section style={{ ...sectionStyle, marginTop: 12 }}>
        <h3 style={headingStyle}>Guru Decision</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 10, marginTop: 12 }}>
          {[
            ["Direction", row?.["Guru Direction"] || "—"],
            ["Confidence", row?.["Guru Confidence"] !== null && row?.["Guru Confidence"] !== undefined ? `${soccerDetailNumber(row["Guru Confidence"], 0)}%` : "—"],
            ["Tier", row?.["Guru Tier"] || "—"],
            ["Guru Pick Eligible", eligible ? "Yes" : "No"],
            ["Primary Guru Pick", primary ? "Yes" : "No"],
            ...(showInternalStrength ? [["Decision Strength (internal)", strengthText]] : []),
          ].map(([label, value]) => (
            <div key={label} style={{ padding: 12, borderRadius: 12, background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
              <div style={labelStyle}>{label}</div>
              <div style={valueStyle}>{value}</div>
            </div>
          ))}
        </div>
        {row?.["Guru Rationale"] && (
          <div style={{ marginTop: 12, color: "#334155", lineHeight: 1.5, fontWeight: 700 }}>{row["Guru Rationale"]}</div>
        )}
      </section>

      <section style={{ ...sectionStyle, marginTop: 12 }}>
        <h3 style={headingStyle}>Data Quality</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10, marginTop: 12 }}>
          {[
            ["Guru Data Quality", row?.["Guru Data Quality"] || "—"],
            ["Historical Data Status", soccerHistoryStatusLabel(row?.["Historical Data Status"])],
            ["History Source Competition", row?.["History Source Competition"] || "—"],
            ["Current Competition", row?.Competition || "—"],
            ["Cross League History", row?.["Cross League History"] === true ? "Yes" : "No"],
            ["Season / L10 / L5 samples", `${soccerDetailNumber(row?.["Season Stat Samples"], 0)} / ${soccerDetailNumber(row?.["L10 Stat Samples"], 0)} / ${soccerDetailNumber(row?.["L5 Stat Samples"], 0)}`],
            ["Same Player Pick Count", soccerDetailNumber(row?.["Same Player Pick Count"], 0)],
          ].map(([label, value]) => (
            <div key={label} title={label === "Historical Data Status" ? String(row?.["Historical Data Status"] || "") : undefined} style={{ padding: 12, borderRadius: 12, background: "#F8FAFC", border: "1px solid #E2E8F0", minWidth: 0 }}>
              <div style={labelStyle}>{label}</div>
              <div style={{ ...valueStyle, fontSize: 14, overflowWrap: "anywhere", wordBreak: "break-word" }}>{value}</div>
            </div>
          ))}
        </div>
        {!eligible && row?.["Guru Pick Exclusion Reason"] && (
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 12, border: "1px solid #FCD34D", background: "#FFFBEB", color: "#92400E", fontWeight: 850 }}>
            Exclusion reason: {row["Guru Pick Exclusion Reason"]}
          </div>
        )}
      </section>

      {quickFacts.length > 0 && (
        <section style={{ ...sectionStyle, marginTop: 12 }}>
          <h3 style={headingStyle}>Quick Facts</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8, marginTop: 12 }}>
            {quickFacts.map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "9px 10px", borderRadius: 10, background: "#F8FAFC" }}>
                <span style={labelStyle}>{label}</span>
                <strong style={{ color: "#0F172A", textAlign: "right" }}>{value}</strong>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}




export default function PropsDashboard() {

  // Responsive: treat narrow screens as mobile (used for UI-only behavior)
  const isNarrow =
    typeof useIsNarrow === "function"
      ? useIsNarrow()
      : typeof window !== "undefined"
        ? window.innerWidth <= 860
        : false;


      ///////// Grid and Card paging
      const [gridPage, setGridPage] = React.useState(1);
      const [gridPageSize, setGridPageSize] = React.useState(50); // 25/50/100/etc

      // ✅ Card paging (add this)
      const [cardPage, setCardPage] = React.useState(1);
      const [cardPageSize, setCardPageSize] = React.useState(24);

      const [activeView, setActiveView] = useState("props"); // public launch defaults to plays
      const [soccerMatchCards, setSoccerMatchCards] = useState([]);
      const [selectedSoccerMatchId, setSelectedSoccerMatchId] = useState(null);
      const [soccerAdminResearchMode, setSoccerAdminResearchMode] = useState(false);

      // parent-owned range state so chart + table + summary stay in sync
      const [detailChartRange, setDetailChartRange] = React.useState("L10");
      const [detailShowBands, setDetailShowBands] = React.useState(false);

      
      // ---- SPORT INIT (single source of truth) ----
      const urlSportParam = new URLSearchParams(window.location.search).get("sport");
      const initialSport  = (urlSportParam || localStorage.getItem("activeSport") || "MLB").toUpperCase();

      const [activeSport, setActiveSport] = useState(initialSport);
      const [selectedDirection, setSelectedDirection] = useState("ALL");
      const directionPresetActiveRef = useRef(false);
      const [sportAvailability, setSportAvailability] = useState(null);
      const [lastPWGSport, setLastPWGSport] = useState(() => (initialSport && initialSport !== "TEG" ? initialSport : "MLB"));
      const [viewMode, setViewMode] = React.useState("card"); // card | grid

      // Mobile: lock to Cards view (clean UX)
      React.useEffect(() => {
        if (isNarrow && viewMode !== "card") setViewMode("card");
      }, [isNarrow, viewMode]);

      

      // Mobile-first: force Cards view on narrow screens
      React.useEffect(() => {
        if (isNarrow && viewMode !== "card") setViewMode("card");
      }, [isNarrow, viewMode]);

        useEffect(() => {
          if (activeSport && !["TEG", "MSI", "PWG"].includes(activeSport)) {
            setLastPWGSport(activeSport);
          }
        }, [activeSport]);

      useEffect(() => {
        setSelectedDirection("ALL");
        if (directionPresetActiveRef.current) setSelectedTags([]);
        directionPresetActiveRef.current = false;
      }, [activeSport]);

      React.useLayoutEffect(() => {
        if (activeSport !== "NHL") return;
        if (selectedDirection === "WAIT") {
          setSelectedDirection("ALL");
          if (directionPresetActiveRef.current) setSelectedTags([]);
          directionPresetActiveRef.current = false;
        }
        const normalizedUrl = normalizeUnsupportedDirectionUrl(window.location.href, activeSport);
        if (normalizedUrl) window.history.replaceState(window.history.state, "", normalizedUrl);
      }, [activeSport, selectedDirection]);

      useEffect(() => {
        clearUnsupportedDetailsPeriodFromUrl(detailsPeriodOptions(null, activeSport));
      }, [activeSport]);

      // Keep left panel + lineup UI in sync when switching between PWG sports and TEG
      useEffect(() => {
        if (activeSport === "TEG") {
          // In TEG, don't let PWG-only panels or lineup state bleed through
          setLeftPanelTab("teg");
      // Lineups are a PWG-only view; ensure the "Show Lineups" focus is off when entering TEG.
      setShowLineupsFocus(false);
        } else if (activeSport && activeSport !== "PWG") {
          // In PWG, default to Filters whenever the panel is opened
          setLeftPanelTab("filters");
        }
      }, [activeSport]);

      // ---- NBA GRID Row selection helper  DEFINITIONS ----
      // --- NBA GRID SELECTION STATE ---
      const [selectedGridKeys, setSelectedGridKeys] = useState(new Set());

      // ---- PlaysWithGuru Tier filter (Studs / Cores / Fillers) ----
      const [gridTierFilter, setGridTierFilter] = useState("ALL");

      // ---- Grid Details / Compare modals ----
      const [gridDetailsOpen, setGridDetailsOpen] = useState(false);
      const [gridDetailsRow, setGridDetailsRow] = useState(null);
      const [gridDetailsPeriod, setGridDetailsPeriod] = useState(DEFAULT_DETAILS_PERIOD);
      const [gridDetailsLoading, setGridDetailsLoading] = useState(false);
      const [gridDetailsError, setGridDetailsError] = useState("");
      const [gridDetailsCompactRow, setGridDetailsCompactRow] = useState(null);
      const gridDetailsCacheRef = useRef(new Map());
      const gridDetailsLoaderRef = useRef(null);
      if (!gridDetailsLoaderRef.current) {
        gridDetailsLoaderRef.current = createMlbDetailLoader({
          cache: gridDetailsCacheRef.current,
          concurrency: 2,
          load: (row, version) => loadMlbProjection(row, version),
        });
      }
      const gridDetailsRequestRef = useRef(0);
      const [secureMlbGames, setSecureMlbGames] = useState([]);
      const [secureMlbVersion, setSecureMlbVersion] = useState("");
      const [secureMlbProgress, setSecureMlbProgress] = useState(null);
      const [localMlbCandidate, setLocalMlbCandidate] = useState(null);
      const [localSoccerCandidate, setLocalSoccerCandidate] = useState(null);
      const [localNflCandidate, setLocalNflCandidate] = useState(null);
      const [localCfbCandidate, setLocalCfbCandidate] = useState(null);
      const secureMlbBoardRequestRef = useRef(0);
      const secureMlbLoadedKeyRef = useRef("");
      const secureMlbInitialLoadRef = useRef(null);
      const secureMlbRenderedRowsRef = useRef([]);

      const [gridDetailsPool, setGridDetailsPool] = useState([]);
      const gridDetailsScrollRef = React.useRef(0);
      const gridDetailsTriggerRef = React.useRef(null);
      const gridDetailsDialogRef = React.useRef(null);

  // ===================== EDGE ANALYZER (Phase 1) =====================
  const [edgeAnalyzerOpen, setEdgeAnalyzerOpen] = useState(false);
  const [edgeAnalyzerPlayer, setEdgeAnalyzerPlayer] = useState("");
  const [edgeAnalyzerPropType, setEdgeAnalyzerPropType] = useState("");
  const [edgeAnalyzerLine, setEdgeAnalyzerLine] = useState("");
  const [edgeAnalyzerStatus, setEdgeAnalyzerStatus] = useState("");

  const openEdgeAnalyzer = () => {
    setEdgeAnalyzerStatus("");
    // Avoid stacked overlays on mobile
    setTaxonomyOpen(false);
    setShowLineupsFocus(false);
    setEdgeAnalyzerOpen(true);
  };

const openEdgeAnalyzerWithRow = (row) => {
  if (!row) return openEdgeAnalyzer();
  const p = row.Player || row.player || "";
  const pt = row["Prop Type"] || row.PropType || row.propType || "";
  const line = row["Prop Value"] || row.PropValue || row.propValue || "";
  setEdgeAnalyzerPlayer(String(p || ""));
  setEdgeAnalyzerPropType(String(pt || ""));
  setEdgeAnalyzerLine(String(line || ""));
  openEdgeAnalyzer();
};



      const [gridDetailsIndex, setGridDetailsIndex] = useState(-1);


      const [gridCompareOpen, setGridCompareOpen] = useState(false);
      const [gridCompareRows, setGridCompareRows] = useState([]);
      const [gridCompareNote, setGridCompareNote] = useState("");


      const makeGridKey = (row) =>
        [
          row["Projection ID"] || "",
          row.Period || "",
          row.Player || "",
          row.Team || "",
          row["Prop Type"] || row.PropType || "",
          row["Prop Value"] || row.PropValue || "",
        ].join("|");

      const isGridRowSelected = (row) => selectedGridKeys.has(makeGridKey(row));

      const toggleGridRowSelected = (row) => {
        const key = makeGridKey(row);
        setSelectedGridKeys((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
      };

      const toggleSelectAllVisibleGridRows = (visibleRows) => {
        if (!visibleRows || !visibleRows.length) return;
        const allSelected = visibleRows.every((r) => isGridRowSelected(r));

        setSelectedGridKeys((prev) => {
          const next = new Set(prev);
          if (allSelected) {
            visibleRows.forEach((r) => next.delete(makeGridKey(r)));
          } else {
            visibleRows.forEach((r) => next.add(makeGridKey(r)));
          }
          return next;
        });
      };


      // --- BULK ADD / REMOVE FROM My Plays USING GRID SELECTION ---
      const addSelectedGridRowsToGuruList = async (rows) => {
        if (!rows || !rows.length) return;
        try {
          await addManyToMyGuruList(rows);
          setSelectedGridKeys(new Set());
        } catch (error) {
          console.error("Failed to add selected My Plays:", error);
        }
      };
      
      const removeSelectedGridRowsFromGuruList = async (rows) => {
        if (!rows || !rows.length) return;
        try {
          await removeManyFromMyGuruList(rows);
          setSelectedGridKeys(new Set());
        } catch (error) {
          console.error("Failed to remove selected My Plays:", error);
        }
      };

      const canPrev = gridDetailsIndex > 0;
      const canNext =
        Array.isArray(gridDetailsPool) &&
        gridDetailsIndex >= 0 &&
        gridDetailsIndex < gridDetailsPool.length - 1;

      const prefetchGridDetail = React.useCallback((row) => {
        const sport = row?.Sport || activeSport;
        if (!secureSportsDataEnabledFor(sport) || String(sport).toUpperCase() !== "MLB") return;
        const projectionId = projectionIdFor(row);
        const version = String(row?.__pwgVersion || secureMlbVersion || "");
        const key = detailCacheKey(version, projectionId);
        if (!projectionId || !version || gridDetailsCacheRef.current.has(key)) return;
        gridDetailsLoaderRef.current.prefetch(key, row, version);
      }, [activeSport, secureMlbVersion]);

      const prefetchGridDetailWindow = React.useCallback((pool, index) => {
        if (!Array.isArray(pool) || index < 0) return;
        detailWindowIndexes(index, pool.length).forEach((adjacent) => prefetchGridDetail(pool[adjacent]));
      }, [prefetchGridDetail]);

      const selectGridDetail = React.useCallback((row, { pool = gridDetailsPool, index = -1 } = {}) => {
        if (!row) return;
        const sport = row?.Sport || activeSport;
        const projectionId = projectionIdFor(row);
        const version = String(row?.__pwgVersion || secureMlbVersion || "");
        const secureMlb = secureSportsDataEnabledFor(sport) && String(sport).toUpperCase() === "MLB";
        const generation = gridDetailsRequestRef.current + 1;
        gridDetailsRequestRef.current = generation;
        setGridDetailsCompactRow(row);
        setGridDetailsRow(row);
        setGridDetailsError("");
        setGridDetailsPeriod(DEFAULT_DETAILS_PERIOD);
        if (!secureMlb || !projectionId || !version) {
          setGridDetailsLoading(false);
          return;
        }
        const key = detailCacheKey(version, projectionId);
        const cached = gridDetailsCacheRef.current.get(key);
        if (cached) {
          setGridDetailsLoading(false);
          setGridDetailsRow(cached);
          prefetchGridDetailWindow(pool, index);
          return;
        }
        setGridDetailsLoading(true);
        gridDetailsLoaderRef.current.selected(key, row, version).then((detail) => {
          if (!detail) throw new Error("Detail temporarily unavailable");
          if (gridDetailsRequestRef.current !== generation) return;
          setGridDetailsRow(detail);
          setGridDetailsLoading(false);
          prefetchGridDetailWindow(pool, index);
        }).catch(() => {
          if (gridDetailsRequestRef.current !== generation) return;
          setGridDetailsLoading(false);
          setGridDetailsError("Detail temporarily unavailable");
        });
      }, [activeSport, gridDetailsPool, prefetchGridDetailWindow, secureMlbVersion]);

      const goToIndex = (i) => {
        if (!Array.isArray(gridDetailsPool)) return;
        if (i < 0 || i >= gridDetailsPool.length) return;
        setGridDetailsIndex(i);
        selectGridDetail(gridDetailsPool[i], { pool: gridDetailsPool, index: i });
      };


  // ---- GRID ACTIONS: Details + Compare ----
  // Details opens the full-screen analysis modal (single row)
  const handleOpenGridDetails = (row, pool = [], triggerElement = null) => {
    const safePool = Array.isArray(pool) ? pool : [];

    if (!gridDetailsOpen) {
      setGridDetailsPeriod(DEFAULT_DETAILS_PERIOD);
      gridDetailsScrollRef.current = window.scrollY || window.pageYOffset || 0;
      gridDetailsTriggerRef.current = triggerElement || document.activeElement || null;
    }
  
    setGridDetailsPool(safePool);
  
    // Find index (prefer your existing key function if you have one)
    let idx = -1;
    try {
      if (typeof makeGridKey === "function") {
        const k = makeGridKey(row);
        idx = safePool.findIndex((r) => makeGridKey(r) === k);
      }
    } catch (e) {
      idx = -1;
    }
  
    // fallback if makeGridKey isn't in scope for some reason
    if (idx < 0) idx = safePool.findIndex((r) => r === row);
  
    setGridDetailsIndex(idx);
    selectGridDetail(row, { pool: safePool, index: idx });
    setGridDetailsOpen(true);
  };

  const closeGridDetails = React.useCallback(() => {
    const restoreY = gridDetailsScrollRef.current;
    const trigger = gridDetailsTriggerRef.current;
    setGridDetailsOpen(false);
    setGridDetailsRow(null);
    setGridDetailsCompactRow(null);
    setGridDetailsLoading(false);
    setGridDetailsError("");
    gridDetailsRequestRef.current += 1;
    gridDetailsLoaderRef.current.reset();

    window.requestAnimationFrame(() => {
      window.scrollTo(0, restoreY);
      if (trigger && document.contains(trigger) && typeof trigger.focus === "function") {
        trigger.focus({ preventScroll: true });
      }
    });
  }, []);

  React.useEffect(() => {
    if (!gridDetailsOpen) return undefined;
    window.requestAnimationFrame(() => gridDetailsDialogRef.current?.focus?.({ preventScroll: true }));

    const handleDialogKeyboard = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeGridDetails();
        return;
      }

      if (event.key === "Tab" && gridDetailsDialogRef.current) {
        const focusable = Array.from(
          gridDetailsDialogRef.current.querySelectorAll(
            "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
          )
        ).filter((element) => element.offsetParent !== null);
        if (!focusable.length) {
          event.preventDefault();
          gridDetailsDialogRef.current.focus({ preventScroll: true });
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", handleDialogKeyboard);
    return () => window.removeEventListener("keydown", handleDialogKeyboard);
  }, [gridDetailsOpen, closeGridDetails]);
  

  // Compare opens the compare modal (max 8 rows)
  const handleOpenGridCompare = (rowsOrRow) => {
    const rows = Array.isArray(rowsOrRow) ? rowsOrRow.filter(Boolean) : [rowsOrRow].filter(Boolean);
    if (!rows.length) return;

    // Enforce max compare (Phase 1: 8). This keeps it useful for PrizePicks-style pool selection.
    if (rows.length > COMPARE_MAX) {
      setGridCompareNote(`Compare is limited to ${COMPARE_MAX} lines. We'll compare the first ${COMPARE_MAX} selected.`);
      setGridCompareRows(rows.slice(0, COMPARE_MAX));
    } else {
      setGridCompareNote("");
      setGridCompareRows(rows);
    }

    setGridCompareOpen(true);
  };


      //////////////////////////////////////////////////////////////////////////////////

      // ---- NBA GRID COLUMN DEFINITIONS ----
const defaultNBAColumns = [
  {
    key: "player",
    label: "Player",
    visible: true,
    getValue: (row) => row.Player || row.player || "",
    render: (row) => {
      const name = row.Player || row.player || "";
      if (!name) return "";

      const hover = buildPlayerHover(row);
      if (!hover) return name;

      return (
        <span
          title={hover}
          style={{
            cursor: "help",
            textDecoration: "underline dotted",
            textUnderlineOffset: "2px",
          }}
        >
          {name}
        </span>
      );
    },
  },

  {
    key: "team",
    label: "Team",
    visible: true,
    getValue: (row) => row.Team || "",
    render: (row) => row.Team || "",
  },
  {
    key: "opponent",
    label: "Opponent",
    visible: true,
    getValue: (row) => row.Opponent || "",
    render: (row) => row.Opponent || "",
  },

  {
    key: "battingOrder",
    label: "BO",
    visible: true, // MLB-only in the grid renderer
    sortable: true,
    align: "center",
    getValue: (row) => getBattingOrderValue(row),
    render: (row) => renderBattingOrderCell(row, "MLB"),
  },

  {
    key: "ha",
    label: "H/A",
    visible: false, // ✅ default OFF (optional)
    getValue: (row) => {
      const ha = String(
        row["Home/Away"] ?? row.homeAway ?? row.home_away ?? ""
      ).toLowerCase();
      if (ha === "home") return "HOME";
      if (ha === "away") return "AWAY";
      return "";
    },
    render: (row) => {
      const ha = String(
        row["Home/Away"] ?? row.homeAway ?? row.home_away ?? ""
      ).toLowerCase();

      if (ha === "home") {
        return (
          <span role="img" aria-label="Home">
            🏠 Home
          </span>
        );
      }
      if (ha === "away") {
        return (
          <span role="img" aria-label="Away">
            ✈️ Away
          </span>
        );
      }
      return "";
    },
  },

  {
    key: "prop",
    label: "Prop",
    visible: true,
    getValue: (row) =>
      (row["Prop Type Label"] || row["Prop Type"] || "").toString(),
    render: (row) => row["Prop Type Label"] || row["Prop Type"] || "",
  },

  {
    key: "line",
    label: "Line",
    visible: true,
    align: "right",
    getValue: (row) => Number(row["Prop Value"]),
    render: (row) => {
      const v = Number(row["Prop Value"]);
      return Number.isFinite(v) ? v.toFixed(2) : "";
    },
  },

  { key: "lineType", label: "Line Type", visible: false, getValue: canonicalLineType, render: canonicalLineType },
  { key: "primaryMarketLine", label: "Primary Line", visible: false, align: "right", getValue: (row) => Number(row["Primary Market Line"]), render: (row) => row["Primary Market Line"] ?? "—" },
  { key: "alternateLine", label: "Alternate Line", visible: false, align: "right", getValue: (row) => Number(row["Alternate Line"]), render: (row) => row["Alternate Line"] ?? "—" },
  { key: "lineFamilyCount", label: "Family Count", visible: false, align: "right", getValue: (row) => Number(row["Line Family Count"]), render: (row) => row["Line Family Count"] ?? "—" },
  { key: "goblinAttainability", label: "Goblin Attainability", visible: false, align: "right", getValue: (row) => Number(row["Goblin Attainability"]), render: (row) => {
    const attainability = goblinAttainability(row);
    if (!attainability) return "—";
    return attainability.score == null ? attainability.classification : `${attainability.score} ${attainability.classification}`.trim();
  } },
  { key: "l5Avg", label: "L5 Avg", visible: false, align: "right", getValue: (row) => Number(row["L5 Avg"]), render: (row) => row["L5 Avg"] ?? "—" },
  { key: "l10Avg", label: "L10 Avg", visible: false, align: "right", getValue: (row) => Number(row["L10 Avg"]), render: (row) => row["L10 Avg"] ?? "—" },
  { key: "seasonAvg", label: "Season Avg", visible: false, align: "right", getValue: (row) => Number(row["Season Avg"]), render: (row) => row["Season Avg"] ?? "—" },
  { key: "l5HitPct", label: "L5 Hit %", visible: false, align: "right", getValue: (row) => Number(row["L5 Hit Rate"]), render: (row) => formatStoredRate(row["L5 Hit Rate"], row.Sport) },
  { key: "seasonHitPct", label: "Season Hit %", visible: false, align: "right", getValue: (row) => Number(row["Season Hit Rate"]), render: (row) => formatStoredRate(row["Season Hit Rate"], row.Sport) },
  { key: "l5Delta", label: "L5 Δ", visible: false, align: "right", getValue: (row) => Number(row["L5 Delta"]), render: (row) => row["L5 Delta"] ?? "—" },
  { key: "l10Delta", label: "L10 Δ", visible: false, align: "right", getValue: (row) => Number(row["L10 Delta"]), render: (row) => row["L10 Delta"] ?? "—" },
  { key: "seasonDelta", label: "Season Δ", visible: false, align: "right", getValue: (row) => Number(row["Season Delta"]), render: (row) => row["Season Delta"] ?? "—" },
  { key: "defenseRank", label: "Defense Rank", visible: false, align: "right", getValue: defenseRankValue, render: defenseRankLabel },
  { key: "defenseFieldSize", label: "Defense Field Size", visible: false, align: "right", getValue: (row) => Number(row["Defense Ranked Teams"]), render: (row) => row["Defense Ranked Teams"] == null ? "—" : row["Defense Ranked Teams"] },
  { key: "defenseTier", label: "Defense Tier", visible: false, getValue: (row) => row["Opponent Defense Tier"] || "", render: (row) => row["Opponent Defense Tier"] || "—" },
  { key: "defenseMetric", label: "Defense Metric", visible: false, getValue: (row) => row["Opponent Defense Metric"] || "", render: (row) => row["Opponent Defense Metric"] || "—" },
  { key: "defenseValue", label: "Defense Value", visible: false, align: "right", getValue: (row) => Number(row["Opponent Defense Value"]), render: (row) => row["Opponent Defense Value"] ?? "—" },
  { key: "historySeason", label: "History Season", visible: false, getValue: (row) => row["History Season"] || "", render: (row) => row["History Season"] || "—" },
  { key: "teamContinuity", label: "Team Continuity", visible: false, getValue: (row) => row["Team Continuity"] || "", render: (row) => row["Team Continuity"] || "—" },

  {
    key: "proj",
    label: "Proj",
    visible: false, // ✅ default OFF (optional)
    align: "right",
    getValue: (row) =>
      Number(
        row.FinalAdjustedScore ??
          row.finalAdjustedScore ??
          row.Proj ??
          row.projection ??
          NaN
      ),
    render: (row) => {
      const v = Number(
        row.FinalAdjustedScore ??
          row.finalAdjustedScore ??
          row.Proj ??
          row.projection
      );
      return Number.isFinite(v) ? v.toFixed(2) : "";
    },
  },

  {
    key: "delta",
    label: "Δ",
    visible: true,
    align: "right",
    getValue: (row) => gridDeltaValue(row, activeSport),
    render: (row) => {
      const v = gridDeltaValue(row, activeSport);
      if (v === null) return "—";

      const isPositive = v > 0;
      const color = isPositive ? "#16a34a" : v < 0 ? "#dc2626" : "#4b5563";
      const sign = isPositive ? "+" : v < 0 ? "−" : "";
      const mag = Math.abs(v).toFixed(2);
      return (
        <span style={{ color, fontWeight: 600 }}>
          {sign}
          {mag}
        </span>
      );
    },
  },

  {
    key: "l5Setup",
    label: "L5 Setup",
    visible: false, // ✅ default OFF (optional)
    getValue: (p) =>
      String(
        p?.L5_Setup ??
          p?.["L5_Setup"] ??
          p?.["L5 Setup"] ??
          p?.l5Setup ??
          p?.L5Setup ??
          ""
      ).trim(),
    sortValue: (p) =>
      String(
        p?.L5_Setup ??
          p?.["L5_Setup"] ??
          p?.["L5 Setup"] ??
          p?.l5Setup ??
          p?.L5Setup ??
          ""
      ).trim(),
  },

  {
    key: "guruScore",
    label: "Guru Score",
    visible: false, // ✅ default OFF (optional; DQS is the always-on quality metric)
    sortable: true,
    align: "right",
    getValue: (row) => Number(getCompareScore(row) ?? NaN),
    render: (row) => {
      const s = Number(getCompareScore(row) ?? NaN);
      return Number.isFinite(s) ? s.toFixed(2) : "";
    },
  },

  {
    label: "DQS",
    key: "dqs",
    visible: true, // ✅ ALWAYS ON (quality metric)
    sortable: true,
    sortKey: "dqs",
    getValue: (row) => computeDQS(row),
    render: (row) => {
      const v = computeDQS(row);
      return v == null ? "" : v.toFixed(2);
    },
    tooltip: "Decision Quality Score — conviction × feasibility (process score)",
  },

  {
    key: "guruRank",
    label: "Guru Rank",
    visible: false, // ✅ default OFF (only useful when filtered)
    align: "center",
    getValue: () => "",
    render: () => "",
  },

  {
    key: "confidence",
    label: "Confidence",
    visible: true,
    align: "right",
    getValue: (row) => {
      const raw = getConfidenceValue(row);
      const label = getDisplayConfidenceLabel(raw, activeSport);
      const rank = {
        "Volatile Elite": 5,
        "Elite": 4,
        "Strong": 3,
        "Moderate": 2,
        "Low": 1,
        "Unknown": 0,
      };
      return rank[label] ?? Number(raw) ?? 0;
    },
    render: (row) => {
      const raw = getConfidenceValue(row);
      const label = getDisplayConfidenceLabel(raw, activeSport);
      if (!label || label === "Unknown") return "—";

      const tone = String(label).toUpperCase();
      const bg = tone.includes("ELITE") ? "#dbeafe"
        : tone.includes("STRONG") ? "#dcfce7"
        : tone.includes("MODERATE") ? "#fef3c7"
        : "#e5e7eb";
      const color = tone.includes("ELITE") ? "#1d4ed8"
        : tone.includes("STRONG") ? "#166534"
        : tone.includes("MODERATE") ? "#92400e"
        : "#374151";

      return (
        <div style={{ textAlign: "right" }}>
          <div
            title={`Confidence: ${label}${Number.isFinite(Number(raw)) ? ` (${Number(raw).toFixed(2)})` : ""}`}
            style={{
              fontSize: "0.75rem",
              fontWeight: 800,
              padding: "0.15rem 0.5rem",
              borderRadius: 999,
              background: bg,
              color,
              display: "inline-block",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </div>
        </div>
      );
    },
  },

  {
    key: "tag",
    label: "Tag",
    visible: true,
    align: "center",
    getValue: (row) => (row.Tag || "").toString(),
    render: (row) => {
      const tag = getRowTag(row).toUpperCase();
      if (!tag) return "";
      const bg = tagColorMap[tag] || "#4b5563";

      return (
        <span
          title={tag}
          aria-label={`Tag: ${tag}`}
          style={{
            display: "inline-block",
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: bg,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
          }}
        />
      );
    },
  },

  {
    key: "minutes",
    label: "Minutes",
    visible: false,
    align: "right",
    getValue: (row) => {
      const v = Number(
        row.minutes_recent_mpg ?? row.mpg ?? row.Minutes ?? row.minutes ?? NaN
      );
      return Number.isFinite(v) ? v : NaN;
    },
    render: (row) => {
      const v = Number(row.minutes_recent_mpg ?? row.mpg ?? row.Minutes ?? row.minutes);
      if (!Number.isFinite(v)) return "";
      return v.toFixed(1);
    },
  },

  {
    key: "blowout",
    label: "Blowout",
    visible: false,
    align: "center",
    getValue: (row) => normalizeBlowoutTier(row),
    render: (row) => renderBlowoutCell(row),
  },

  {
    key: "dvpTier",
    label: "Matchup",
    visible: false, // ✅ default OFF (optional)
    sortable: true,
    width: 150,
    sortFn: (a, b) => {
      const tierOrder = {
        "ELITE DEFENSE": 1,
        "TOUGH": 2,
        "NEUTRAL": 3,
        "SOFT": 4,
        "TARGET MATCHUP": 5,
        "R0": 99,
        "": 999,
      };

      const cleanTier = (row) => {
        const raw = (row?.["Opp_DvP_Tier"] ?? "").toString();
        let s = raw ? String(raw).toUpperCase().trim() : "";

        if (!s) {
          const player = (row?.Player || row?.["Player"] || "").toString();
          const prop = (row?.Prop || row?.["Prop"] || row?.["Prop Type"] || row?.["Prop_Type"] || "").toString();
          if (player.includes("+") || prop.includes("+")) return "R0";
          return "";
        }

        if (s.startsWith("R0")) return "R0";

        if (
          s.startsWith("SG") ||
          s.startsWith("PG") ||
          s.startsWith("PF") ||
          s.startsWith("SF") ||
          s.startsWith("C")
        ) {
          const inner = s.split("•")[1]?.trim() || "";
          return inner.split("(")[0].trim();
        }

        return s;
      };

      const ta = tierOrder[cleanTier(a)] ?? 999;
      const tb = tierOrder[cleanTier(b)] ?? 999;

      return ta - tb;
    },
    getValue: (row) => {
      const pos = (row.POS || row["POS"] || "").toString();
      const tier = (row["Opp_DvP_Tier"] || "").toString();
      const rank = row["Opp_DvP_Rank"];
      if (!tier && !pos) return "R0";

      if (pos && Number.isFinite(rank)) return `${pos} • ${tier} (R${rank})`;
      if (pos) return `${pos} • ${tier || "R0"}`;
      return tier || "R0";
    },
    render: (row) => {
      const pos = (row.POS || row["POS"] || "").toString().trim();
      const tierRaw = (row["Opp_DvP_Tier"] || "").toString().trim();
      const rankRaw = row["Opp_DvP_Rank"];

      if (!tierRaw) {
        const player = (row?.Player || row?.["Player"] || "").toString();
        const prop = (row?.Prop || row?.["Prop"] || row?.["Prop Type"] || row?.["Prop_Type"] || "").toString();
        if (player.includes("+") || prop.includes("+")) {
          return (
            <span
              style={{
                padding: "2px 10px",
                borderRadius: 999,
                fontSize: "0.72rem",
                fontWeight: 700,
                background: "#e5e7eb",
                color: "#374151",
                whiteSpace: "nowrap",
              }}
            >
              R0
            </span>
          );
        }
        return "";
      }

      const tierUp = tierRaw.toUpperCase();
      const rankNum = Number(rankRaw);
      const rankText = Number.isFinite(rankNum) && rankNum > 0 ? String(Math.round(rankNum)) : "";

      let shortTier = tierUp;
      let bg = "#e5e7eb";
      let fg = "#374151";

      if (tierUp.includes("ELITE")) {
        shortTier = "ELT";
        bg = "#fee2e2"; fg = "#b91c1c";
      } else if (tierUp.includes("TOUGH")) {
        shortTier = "TGH";
        bg = "#fee2e2"; fg = "#b91c1c";
      } else if (tierUp.includes("NEUTRAL")) {
        shortTier = "NEU";
        bg = "#fef9c3"; fg = "#854d0e";
      } else if (tierUp.includes("SOFT")) {
        shortTier = "SFT";
        bg = "#dcfce7"; fg = "#166534";
      } else if (tierUp.includes("TARGET")) {
        shortTier = "TGT";
        bg = "#dcfce7"; fg = "#166534";
      } else if (tierUp.startsWith("R0")) {
        shortTier = "R0";
        bg = "#e5e7eb"; fg = "#374151";
      }

      const pillText = `${shortTier}${rankText ? " " + rankText : ""}`;
      const full = `${pos ? pos + " • " : ""}${tierUp}${rankText ? " (R" + rankText + ")" : ""}`;

      return (
        <span
          title={full}
          style={{
            padding: "2px 10px",
            borderRadius: 999,
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.02em",
            background: bg,
            color: fg,
            whiteSpace: "nowrap",
          }}
        >
          {pillText}
        </span>
      );
    },
  },
  // --- Line Tax ---
{
  key: "tax",
  label: "Line Tax",
  visible: false,
  align: "center",
  getValue: (row) =>
    String(
      row["Line Tax"] ??
      row.LineTax ??
      row.lineTax ??
      row.Tax ??
      row.tax ??
      ""
    ),
  render: (row) => {
    const v = String(
      row["Line Tax"] ??
      row.LineTax ??
      row.lineTax ??
      row.Tax ??
      row.tax ??
      ""
    ).trim();
    return v || "";
  },
},

// --- L10 Hit% ---
{
  key: "l10HitPct",
  label: "L10 Hit%",
  visible: false,
  align: "right",
  getValue: (row) => {
    const raw =
      row["L10 Hit%"] ??
      row["L10 Hit Rate"] ??
      row.L10_HitPct ??
      row.l10HitPct ??
      row.l10_hit_pct ??
      row.L10HitPct;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  },
  render: (row) => {
    const raw =
      row["L10 Hit%"] ??
      row["L10 Hit Rate"] ??
      row.L10_HitPct ??
      row.l10HitPct ??
      row.l10_hit_pct ??
      row.L10HitPct;

    const n = Number(raw);
    if (!Number.isFinite(n)) return "";

    // handle 0-1 vs 0-100
    const pct = n <= 1.01 ? n * 100 : n;
    return `${pct.toFixed(0)}%`;
  },
},

// --- Selection Strategy summary ---
{
  key: "selStrat",
  label: "Sel Strat",
  visible: false,
  getValue: (row) =>
    String(
      row["Sel Strat"] ??
      row.SelStrat ??
      row.selStrat ??
      row.SelectionStrategy ??
      row.selectionStrategy ??
      ""
    ).trim(),
  render: (row) => {
    const v = String(
      row["Sel Strat"] ??
      row.SelStrat ??
      row.selStrat ??
      row.SelectionStrategy ??
      row.selectionStrategy ??
      ""
    ).trim();
    return v || "";
  },
},

// --- Guru Grade ---
{
  key: "guruGrade",
  label: "Guru Grade",
  visible: false,
  align: "center",
  getValue: (row) =>
    String(row["Guru Grade"] ?? row.guruGrade ?? row.GuruGrade ?? "").trim(),
  render: (row) => {
    const g = String(row["Guru Grade"] ?? row.guruGrade ?? row.GuruGrade ?? "").trim();
    return g || "";
  },
},

// --- Minutes Heat ---
{
  key: "minutesHeat",
  label: "Minutes Heat",
  visible: false,
  getValue: (row) =>
    String(
      row["Minutes Heat"] ??
      row.minutesHeat ??
      row.MinutesHeat ??
      row.minutes_heat ??
      ""
    ).trim(),
  render: (row) => {
    const v = String(
      row["Minutes Heat"] ??
      row.minutesHeat ??
      row.MinutesHeat ??
      row.minutes_heat ??
      ""
    ).trim();
    return v || "";
  },
},

// --- Bounce State (backend-owned) ---
{
  key: "bounceLabel",
  label: "Bounce State",
  visible: false,
  getValue: (row) => getBounceLabel(row),
  render: (row) => getBounceLabel(row) || "—",
},

{
  key: "bounceScore",
  label: "Bounce Score",
  visible: false,
  align: "right",
  getValue: (row) => getBounceStateScore(row),
  render: (row) => {
    const score = getBounceStateScore(row);
    return Number.isFinite(score) ? Math.round(score) : "—";
  },
},

// --- Heat Note ---
{
  key: "heatNote",
  label: "Heat Note",
  visible: false,
  getValue: (row) =>
    String(
      row["Heat Note"] ??
      row.heatNote ??
      row.HeatNote ??
      row.heat_note ??
      ""
    ).trim(),
  render: (row) => {
    const v = String(
      row["Heat Note"] ??
      row.heatNote ??
      row.HeatNote ??
      row.heat_note ??
      ""
    ).trim();
    return v || "";
  },
},

// --- L2 MIN Δ ---
{
  key: "l2MinDelta",
  label: "L2 MIN Δ",
  visible: false,
  align: "right",
  getValue: (row) => {
    const n = Number(
      row["L2 MIN Δ"] ??
      row.L2_Min_Delta ??
      row.l2MinDelta ??
      row.l2_min_delta ??
      NaN
    );
    return Number.isFinite(n) ? n : NaN;
  },
  render: (row) => {
    const n = Number(
      row["L2 MIN Δ"] ??
      row.L2_Min_Delta ??
      row.l2MinDelta ??
      row.l2_min_delta
    );
    return Number.isFinite(n) ? n.toFixed(1) : "";
  },
},

// --- L2 Stat Δ ---
{
  key: "l2StatDelta",
  label: "L2 Stat Δ",
  visible: false,
  align: "right",
  getValue: (row) => {
    const n = Number(
      row["L2 Stat Δ"] ??
      row.L2_Stat_Delta ??
      row.l2StatDelta ??
      row.l2_stat_delta ??
      NaN
    );
    return Number.isFinite(n) ? n : NaN;
  },
  render: (row) => {
    const n = Number(
      row["L2 Stat Δ"] ??
      row.L2_Stat_Delta ??
      row.l2StatDelta ??
      row.l2_stat_delta
    );
    return Number.isFinite(n) ? n.toFixed(2) : "";
  },
},

];




      const [nbaGridColumns, setNbaGridColumns] = useState(defaultNBAColumns);
      // --- NBA grid sorting state (shared with NBAGridTable) ---
      const [nbaGridSortConfig, setNbaGridSortConfig] = React.useState({
        key: "Proj",
        direction: "desc",
      });


      
      // init PROPS DATA
      const [propsData, setPropsData] = useState([]);
      const [loadedPropsSport, setLoadedPropsSport] = useState("");
      const propsRequestGenerationRef = useRef(0);
      const staticSportIdentityRef = useRef({});
      const [injuryReport, setInjuryReport] = useState([]);
      const [injuryReportCache, setInjuryReportCache] = useState({});

const [dailyBrief, setDailyBrief] = useState(null);           // current sport
const [dailyBriefCache, setDailyBriefCache] = useState({});   // sport -> brief

      const [propsCache, setPropsCache] = useState({});

      // All unique prop types across the current propsData
      // (used for the "prop type" dropdown)
      const allPropTypes = useMemo(() => {
        if (!Array.isArray(propsData)) return [];

        const types = propsData
          .map((row) =>
            row["Prop Type"] ??
            row.PropType ??
            row.propType ??
            row["PropType"] ??
            row["PROP_TYPE"] ??
            ""
          )
          .filter(Boolean);

        return Array.from(new Set(types)).sort();
      }, [propsData]);


      // ⬇️ ADD THIS BLOCK
      const allSectors = React.useMemo(() => {
        if (!Array.isArray(propsData)) return [];

        const sectors = propsData
          .map((row) => row.Sector || row.sector || row.industry || row.Industry || "")
          .filter(Boolean);

        return Array.from(new Set(sectors)).sort();
      }, [propsData]);
      // ⬆️ ADD THIS BLOCK

      // cache key should depend on the *current* sport
      const listCacheKey = useMemo(() => sportListCacheKey(activeSport), [activeSport]);

      // keep URL + localStorage in sync when user changes sport
      useEffect(() => {
        try { localStorage.setItem("activeSport", activeSport); } catch {}
        const usp = new URLSearchParams(window.location.search);
        if ((usp.get("sport") || "").toUpperCase() !== activeSport) {
          usp.set("sport", activeSport);
          window.history.replaceState(null, "", `?${usp.toString()}`);
        }
      }, [activeSport]);

      //////////////////////////////////////////////////////////

      const [playerQuery, setPlayerQuery] = useState("");   // canonical filter value
      const [playerInput, setPlayerInput] = useState(playerQuery); 
  // Mobile: compact search control
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchRef = useRef(null);
// mobile field mirror

    

    
      useEffect(() => { setPlayerInput(playerQuery); }, [playerQuery]);
    
      const applySearch = useCallback(() => {
        setPlayerQuery(playerInput.trim());
      }, [playerInput]);

      // Read from ?sport= in URL or localStorage; default to WNBA
      // Top of component – remove the extra helper entirely

      const [selectedSports, setSelectedSports] = useState([initialSport]);
      const [lineupNotice, setLineupNotice] = useState("");
      // Advanced panel pick count (default 6)
      const [advLineupSize, setAdvLineupSize] = useState(6);

      const [uniqueGames, setUniqueGames] = useState([]);
      const [governedNhlGames, setGovernedNhlGames] = useState([]);

      const [aboutOpen, setAboutOpen] = useState(false);
      const openAbout = () => window.dispatchEvent(new Event("pwg:open-about"));
      const closeAbout = () => setAboutOpen(false);


      // 👇 alias for legacy JSX *after* the state exists
      const games = uniqueGames; // `uniqueGames` already defaults to []

      // one hook call, inside the component
      const {
        user,
        loading: authLoading,
        authInitialized,
        userDoc,
        userDocLoading,
        updateProfile,
      } = useAuth();
      const entitlement = useEntitlements();

  // USER DOC (features/role/status/profile) — source of truth for gating
  const userRole = String(userDoc?.role || "observer").trim().toLowerCase();
  // One source of truth for admin identity across filters, cards, sports, and lineups.
  const isAdminIdentity = entitlement.isAdmin;
  const [discordMembership, setDiscordMembership] = useState(null);
  const discordLabel = discordActionLabel(discordMembership);
  useEffect(() => {
    let active = true;
    if (!user) {
      setDiscordMembership(null);
      return () => { active = false; };
    }
    getDiscordStatus()
      .then((status) => { if (active) setDiscordMembership(status); })
      .catch(() => { if (active) setDiscordMembership(null); });
    return () => { active = false; };
  }, [user]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("discord") !== "error") return;
    alert("Discord connection was not completed. Please try again.");
    params.delete("discord");
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, []);
  useEffect(() => {
    clearAdminMlbShardCache();
    secureMlbLoadedKeyRef.current = "";
  }, [user?.uid, isAdminIdentity]);

  const isPremiumRole = entitlement.isPremium || ["premium", "pro", "guru", "mvp"].includes(userRole);
  const isBetaUser = !isAdminIdentity && !isPremiumRole &&
    ["observer", "beta", "tester"].includes(userRole);
    
    

  // Internal diagnostic filters expose Guru/THEO research signals and remain
  // admin-only. Premium unlocks paid product capabilities, not admin tooling.
  const showAdvancedFilters = isAdminIdentity;
  const showAdvancedFilterControls = isAdminIdentity;


  const applications = entitlement.applications || {};
  const pwgGuruAccess = useMemo(() => pwgGuruModeView(entitlement.capabilities), [entitlement.capabilities]);
  useEffect(() => {
    if (gridDetailsRow && !canViewPwgRow(gridDetailsRow, pwgGuruAccess)) {
      setGridDetailsOpen(false);
      setGridDetailsRow(null);
    }
  }, [gridDetailsRow, pwgGuruAccess]);
  const hasPWGAccess = applications.PWG?.enabled === true;
  const hasTEGAccess = isAdminIdentity;
  const hasMSIAccess = isAdminIdentity;

  useEffect(() => {
    if (!isAdminIdentity || activeSport !== "SOCCER" || !user) {
      setSoccerAdminResearchMode(false);
    }
  }, [isAdminIdentity, activeSport, user]);

  // ---------------------------------------------------------
  // Sports rail control:
  // 1) Hide sports you haven't built yet (READY_SPORTS)
  // 2) Hide sports the user doesn't have enabled (features.<sportKey>)
  // ---------------------------------------------------------
  const READY_SPORTS_ = {
    // Flip any sport to false to hide it everywhere (rail + deep-link guard)
    NBA: true,
    MLB: true,
    MLBB: true,
    WNBA: true,
    WNBAB: true,
    SOCCER: true,
    NFL: true,
    NHL: true,
    CFB: true,
    CBB: true,
    MMA: true,
    TEG: true,
    MSI: true,
    FPA: true,
    LBSA: true,
    BBL: true,
    SACB: true,
    EURO: true,
    BCL: true,
    GBL: true,
    NBB: true,
    AUSNBL: true,
    KBO: true,

  };

  const READY_SPORTS = {
    // Flip any sport to false to hide it everywhere (rail + deep-link guard)
    NBA: true,
    MLB: true,
    MLBB: true,
    WNBA: true,
    WNBAB: true,
    SOCCER: true,
    NFL: true,
    NHL: true,
    CFB: true,
    CBB: false,
    MMA: false,
    TEG: true,
    MSI: true,
    FPA: true,
    LBSA: true,
    BBL: true,
    SACB: true,
    EURO: true,
    BCL: false,
    GBL: true,
    NBB: false,
    AUSNBL: false,
    KBO: true,

  };

  const SPORT_FEATURE_KEY = {
    NBA: "nba",
    MLB: "mlb",
    MLBB: "mlbb",
    WNBA: "wnba",
    WNBAB: "wnbab",
    SOCCER: "soccer",
    NFL: "nfl",
    NHL: "nhl",
    CFB: "cfb",
    CBB: "cbb",
    MMA: "mma",
    TEG: "teg",
    MSI: "msi",
    FPA: "fpa",
    LBSA: "lbsa",
    BBL: "bbl",
    SACB: "sacb",
    EURO: "euro",
    GBL: "gbl",
    NBB: "nbb",
    AUSNBL: "ausnbl",
    BCL: "bcl",
    KBO: "kbo",
  };

  const baseSports = ["NBA","NBB","KBO","AUSNBL","CBB","GBL","LBSA","BBL","BCL","SACB","EURO","MLB","MLBB","NHL","SOCCER","NFL","CFB","MMA","WNBA","WNBAB","FPA","MSI","TEG"];
  // --- Sport visibility policy ---
  // Regular Sports Guru users see the live core sports inside PWG.
  // Admins see every globally-ready product they are entitled to.
  // This same policy now drives the main sport rail as well as lineup eligibility.
  const canSeeAllSports = isAdminIdentity;
  const availablePwgSports = visiblePwgSports({ baseSports, readySports: READY_SPORTS, availability: sportAvailability, pwgEnabled: hasPWGAccess, isAdmin: canSeeAllSports });
  const availableSports = baseSports.filter((sport) => (
    sport === "TEG" ? !!hasTEGAccess
      : sport === "MSI" ? !!hasMSIAccess
        : availablePwgSports.includes(sport)
  ));
  const availabilityBySport = Object.fromEntries(baseSports.map((sport) => [sport, sportAccess(sportAvailability?.[sport], { isAdmin: canSeeAllSports })]));
  const activeSportAvailability = availabilityBySport[activeSport] || { visible: true, disabled: false, state: "ACTIVE" };

  useEffect(() => {
    if (!authInitialized || !user) return;
    let live = true;
    const refreshAvailability = () => fetchSportAvailability()
      .then((sports) => { if (live) setSportAvailability(sports); })
      .catch(() => { if (live) setSportAvailability(null); });
    refreshAvailability();
    const timer = window.setInterval(refreshAvailability, 60000);
    return () => { live = false; window.clearInterval(timer); };
  }, [authInitialized, user?.uid]);

  // A saved URL/localStorage preference may point at an out-of-season or
  // unavailable sport (for example NBA on a regular account). Never render
  // data for a sport that is absent from the user's visible sport rail.
  useEffect(() => {
    if (!authInitialized || userDocLoading || !hasPWGAccess) return;
    if (!availableSports.length || availableSports.includes(activeSport)) return;

    const resolved = resolveDirectSport({ requested: activeSport, availableSports, availability: sportAvailability, isAdmin: canSeeAllSports });
    if (resolved.sport) setActiveSport(resolved.sport);
  }, [
    activeSport,
    authInitialized,
    userDocLoading,
    hasPWGAccess,
    availableSports.join("|"),
  ]);

  // ---------------------------------------------------------
  // Product rail UX:
  // - In TEG context, collapse the long sports list into: PWG | TEG
  // - Clicking PWG returns to the last non-TEG sport (PWG "home")
  // ---------------------------------------------------------
  //const defaultSportsRail = ["NBA","MLB","NFL","WNBA","NHL","SOCCER","MMA"];
    const defaultSportsRail = ["MLB"];

    const defaultRailWithTEG = [
      ...defaultSportsRail,
      ...(hasMSIAccess ? ["MSI"] : []),
      ...(hasTEGAccess ? ["TEG"] : []),
    ];
    
  const hasNonTEGSport = Array.isArray(availableSports)
    && availableSports.some((s) => s && s !== "MSI" && s !== "TEG" && s !== "PWG");

  // Rail behavior:
  // - In TEG: show a simple 2-pill toggle (PWG ↔ TEG) when both are accessible.
  // - In PWG: show sport pills (plus optional TEG) — never collapse to just ["TEG"].
    const railSports =
      activeSport === "TEG" || activeSport === "MSI"
        ? [
            ...(hasPWGAccess ? ["PWG"] : []),
            ...(hasMSIAccess ? ["MSI"] : []),
            ...(hasTEGAccess ? ["TEG"] : []),
          ]
        : availableSports;

        // Safe wrapper around hasAccess to avoid runtime crashes if mapping is misconfigured
      const canUseFeature = (featureKey) => {
        try {
          return hasAccess(user, featureKey);
        } catch (err) {
          console.error("hasAccess error for feature", featureKey, err);
          return false;
        }
      };



      const navigate = useNavigate();  // If not used, remove it. Only keep if needed for navigation

      /////////////////////// TRACK SUBSCRIPTION///////////////////////////////////////////////
      // Centralized, authenticated entitlement state.
      const isSubscribed = entitlement.isPremium;
      const isSubscriptionLoading = entitlement.loading;

      // UI prefs coming from SettingsDrawer/localStorage
      const [compact, setCompact] = useState(
        typeof window !== "undefined" && localStorage.getItem("pw_compact") === "1"
      );
      const [oddsFormat, setOddsFormat] = useState(
        (typeof window !== "undefined" && localStorage.getItem("pw_odds_format")) || "american"
      );

      // check settings
      useEffect(() => {
        const onSettings = () => {
          // re-read flags and update state
          setCompact(localStorage.getItem("pw_compact") === "1");
          setOddsFormat(localStorage.getItem("pw_odds_format") || "american");
          // …any other state you derive from settings
        };
        window.addEventListener("pw:settings-changed", onSettings);
        return () => window.removeEventListener("pw:settings-changed", onSettings);
      }, [isNarrow]);
      

      // define BEFORE any hook that uses it
      const fetchProps = async (sport) => {
        const generation = propsRequestGenerationRef.current + 1;
        propsRequestGenerationRef.current = generation;
        const isCurrent = () => propsRequestGenerationRef.current === generation;
        setPropsLoading(true);
        setPropsDataError("");
        setSelectedSports([sport]);

        try {
          let payload;
          if (String(sport).toUpperCase() === "TEG") {
            const isLocalDev = process.env.REACT_APP_PWG_PRODUCTION_RELEASE !== "1" && (
              window.location.hostname === "localhost" ||
              window.location.hostname === "127.0.0.1"
            );

            if (isLocalDev) {
              const response = await fetch("/__local/teg-data.json", {
                cache: "no-store",
              });
              if (!response.ok) throw new Error(`Local TEG data failed (${response.status})`);
              payload = { props: await response.json(), injuryReport: [] };
            } else {
              const firebaseUser = getAuth().currentUser;
              if (!firebaseUser) throw new Error("Sign in is required to load Premium TEG data");
              const token = await firebaseUser.getIdToken();
              const response = await fetch(`${API_BASE}/premium/teg-data`, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: "include",
                cache: "no-store",
              });
              if (!response.ok) throw new Error(`Premium TEG data failed (${response.status})`);
              payload = { props: await response.json(), injuryReport: [] };
            }
          } else if (String(sport).toUpperCase() === "MLB" && secureSportsDataEnabledFor("MLB")) {
            if (secureMlbInitialLoadRef.current) return secureMlbInitialLoadRef.current;
            const generation = secureMlbBoardRequestRef.current + 1;
            secureMlbBoardRequestRef.current = generation;
            const isCurrent = () => secureMlbBoardRequestRef.current === generation;
            const publishProgressiveBoard = (partial) => {
              if (!isCurrent()) return;
              const rows = Array.isArray(partial?.props) ? partial.props : [];
              const versionId = String(partial?.version?.version_id || "");
              setSecureMlbGames(Array.isArray(partial?.games) ? partial.games : []);
              setSecureMlbVersion(versionId);
              setSecureMlbProgress(partial?.page ? { loaded: partial.page.loaded, total: partial.page.total } : null);
              setLocalMlbCandidate(partial?.localCandidate || null);
              secureMlbLoadedKeyRef.current = `${versionId}|regular|`;
              setPropsData(rows);
              setLoadedPropsSport("MLB");
              setPropsCache((current) => ({ ...current, MLB: rows }));
              setPropsLoading(false);
            };
            const request = loadMlbBoard({
              onFirstPage: publishProgressiveBoard,
              onPage: publishProgressiveBoard,
              onProgress: (progress) => { if (isCurrent()) setSecureMlbProgress(progress); },
              isCurrent,
            });
            secureMlbInitialLoadRef.current = request;
            try {
              payload = await request;
              if (!isCurrent() || payload?.page?.cancelled) return;
            } finally {
              if (secureMlbInitialLoadRef.current === request) secureMlbInitialLoadRef.current = null;
            }
          } else {
            payload = await loadProps(sport);
          }
          if (!isCurrent()) return;
          const cleaned = Array.isArray(payload?.props) ? payload.props : [];
          setGovernedNhlGames(String(sport).toUpperCase() === "NHL" && Array.isArray(payload?.games) ? payload.games : []);
          const injuries = Array.isArray(payload?.injuryReport) ? payload.injuryReport : [];
          if (productionStaticCustomerSportEnabled(sport) && payload?.version) {
            staticSportIdentityRef.current[String(sport).toUpperCase()] = {
              version_id: String(payload.version.version_id || ""),
              source_sha256: String(payload.version.source_sha256 || ""),
            };
          }

          if (String(sport).toUpperCase() === "MLB" && secureSportsDataEnabledFor("MLB")) {
            setSecureMlbGames(Array.isArray(payload?.games) ? payload.games : []);
            setSecureMlbVersion(String(payload?.version?.version_id || ""));
            setSecureMlbProgress(payload?.page ? { loaded: payload.page.loaded, total: payload.page.total } : null);
            setLocalMlbCandidate(payload?.localCandidate || null);
            secureMlbLoadedKeyRef.current = `${String(payload?.version?.version_id || "")}|regular|`;
          }
          if (String(sport).toUpperCase() === "SOCCER") {
            setLocalSoccerCandidate(payload?.localCandidate || null);
          }
          if (String(sport).toUpperCase() === "NFL") {
            setLocalNflCandidate(payload?.localCandidate || null);
          }
          if (process.env.REACT_APP_PWG_PRODUCTION_RELEASE !== "1" && String(sport).toUpperCase() === "CFB" && ["localhost", "127.0.0.1"].includes(window.location.hostname)) {
            try {
              const response = await fetch("/data/cfb-local-candidate.json", { cache: "no-store" });
              setLocalCfbCandidate(response.ok ? await response.json() : null);
            } catch (_) {
              setLocalCfbCandidate(null);
            }
          }

          setPropsCache(prev => ({ ...prev, [sport]: cleaned }));
          setInjuryReportCache(prev => ({ ...prev, [sport]: injuries }));
          setPropsData(cleaned);
          setLoadedPropsSport(String(sport || "").toUpperCase());
          setPropsDataError("");
          setInjuryReport(injuries);

          // Optional: slate-level Daily Guru Brief (companion JSON)
          try {
            const brief = await loadDailyBrief(sport);
            setDailyBriefCache(prev => ({ ...prev, [sport]: brief }));
            setDailyBrief(brief);
          } catch (e) {
            // Missing file is OK during rollout
            setDailyBriefCache(prev => ({ ...prev, [sport]: null }));
            setDailyBrief(null);
          }

          const gameTimeMap = {};

          const games = cleaned
            .filter(p => !String(p.Player || "").includes(" + "))
            .map(p => {
              const key = singleSportGameKey(p);
              if (!key) return "";
          
              const gt =
                p.GameTime ??
                p["GameTime"] ??
                p.gameTime ??
                p["Game Time"] ??
                "";
          
              if (!gameTimeMap[key] && gt) {
                gameTimeMap[key] = formatLocalEventTime(p, sport);
              }
          
              return key;
            });
          
          console.log("sample cleaned row:", cleaned[0]);
          console.log("games:", games);
          console.log("gameTimeMap:", gameTimeMap);
          
          console.log("propsData", propsData.length);
        console.log("filteredProps", filteredProps.length);
        console.log("visiblePool", visiblePool.length);
        console.log("pageRows", pageRows.length);
          
          setUniqueGames(Array.from(new Set(games.filter(Boolean))).sort());
          setGameTimes(gameTimeMap);

        } catch (err) {
          if (!isCurrent()) return;
          const source = String(sport).toUpperCase() === "MLB" ? "MLB dashboard source" : `${sport} data source`;
          console.error(`[props] ${source} failed:`, err);
          setPropsData([]);
          setLoadedPropsSport(String(sport || "").toUpperCase());
          setUniqueGames([]);
          setPropsCache((current) => ({ ...current, [sport]: [] }));
          if (String(sport).toUpperCase() === "MLB") setSecureMlbGames([]);
          setPropsDataError(err?.message || `${source} is unavailable.`);
        } finally {
          if (isCurrent()) setPropsLoading(false);
        }
      };

      // ✅ Clear All Filters new
      const clearAllFilters = useCallback(() => {
        // core filters
        setSelectedTags([]);
        setSelectedGames([]);
        setSelectedPropType("");
        setSelectedPropTypes([]);
        setSelectedConfidence("");
        setPlayerInput("");
        setPlayerQuery("");
        setHomeAwayFilter("");
        setPlayerTypeFilter("");
        setSelectedTeams([]);
        setMomentumFilter("");
        setConfirmedMomentumFilter("");
        setMomentumPatternFilter("");
        setSelectedGuruPotential("");
        setSelectedZGuruTag("");
        setSelectedLeanDirection("");
        setSelectedGuruConflict("");
        setSelectedGuruPicks("");
        setSelectedIsGuruPicks("");
        setSelectedCommentary("");
        setShowCommentary(false);
        setSelectedGuruMagic("");
        setSelectedTime("");
        setSelectedRisk([]);
        setGuruListFilter("all");
        setGuruAlert("ALL");

        // MLB / matchup filters
        setPitcherTierFilter("");
        setHitterTierFilter([]);
        setOppHitterTierFilter([]);
        setTeamPitcherTierFilter("");
        setTeamAttackabilityFilter("");
        setTeamWhipTierFilter("");
        setTeamKpctTierFilter("");
        setOppPitcherHandFilter("");
        setBattingOrderFilter("");
        setMinL10Pct("");
        setMinMpgFilter("");

        // multi-select matchup arrays
        setOppWhipTierFilter([]);
        setOppKpctTierFilter([]);
        setOppAttackabilityFilter([]);
        setOppPitcherTierFilter([]);

        // advanced toggles
        setTaxFilter("ALL");
        setBlowoutLevelFilter("all");
        setDvpTierFilter("ALL");
        setSelStratFilter("ALL");
        setStocksHeatOnly(false);
        setActivePreset(null);

        // minutes / trend
        setMinutesTrendMin(null);
        setMinutesTrendMax(null);
        setOversMinutesGate(false);

        // zodiac / taxonomy
        setZodiacFilter("ALL");
        setTaxonomyScope("ALL");
        setTaxonomyTier("ALL");
        setTaxonomyPlayer("");
        if (isNarrow) setTaxonomyOpen(false);

        // shapes / commentary / card expansion
        setSelectedL5Shapes([]);
        setSelectedFormationFamilies([]);
        setSelectedFormationCodes([]);
        setSelectedPatterns([]);
        setSelectedPatternComponents([]);
        setPatternMatchMode("ALL");
        setSelectedBounceLabels([]);
        setSelectedBounceFamilies([]);
        setSelectedMinutesTrends([]);
        setCardToggles({});

        // TEG scoped
        try { setSelectedSector("ALL"); } catch (e) {}
      }, [isNarrow]);


      const resetSportScopedUI = useCallback(() => {
        setGeneratedLineups([]);
        setLineupNotice("");
        setShowLineupsFocus(false);
        setShowLineupSection(false);

        setSelectedGridKeys(new Set());

        setGridDetailsOpen(false);
        setGridDetailsRow(null);
        setGridDetailsPool([]);
        setGridDetailsIndex(-1);

        setGridCompareOpen(false);
        setGridCompareRows([]);
        setGridCompareNote("");

        setGridPage(1);
        setCardPage(1);

        setDetailChartRange("L10");
        setDetailShowBands(false);
      }, []);
  

      //updates sport, URL, and data
      const handleSelectSport = useCallback((s) => {
        if (!isActualSportTransition(activeSport, s)) return;
        clearAllFilters();
        resetSportScopedUI();
        setLoadedPropsSport("");
        setSelectedDirection("ALL");
        setSelectedLineTypes([]);
      
        setSelectedSports([s]);
        setLineupSportFilter(s);
        setActiveSport(s);
      
        const url = sportOnlyUrl(window.location.href, s);
        window.history.pushState({ sport: s }, "", url);
      
      }, [activeSport, clearAllFilters, resetSportScopedUI]);

      useEffect(() => {
        const restoreFromHistory = () => {
          const sport = new URLSearchParams(window.location.search).get("sport")?.toUpperCase();
          if (isActualSportTransition(activeSport, sport)) {
            clearAllFilters();
            resetSportScopedUI();
            setLoadedPropsSport("");
            setSelectedDirection("ALL");
            setSelectedLineTypes([]);
            setSelectedSports([sport]);
            setLineupSportFilter(sport);
            setActiveSport(sport);
          }
        };
        window.addEventListener("popstate", restoreFromHistory);
        return () => window.removeEventListener("popstate", restoreFromHistory);
      }, [activeSport, clearAllFilters, resetSportScopedUI]);


      // --- TEG helpers ---------------------------------------------------------
      const [selectedSector, setSelectedSector] = useState("");   // "" = all sectors

      const clearTEGFilters = useCallback(() => {
        setPlayerInput("");
        // Sector filter (TEG) uses selectedSector state
        try { setSelectedSector("ALL"); } catch (e) {}
      }, [setPlayerInput, setSelectedSector]);



        const handleRailSportChange = useCallback((s) => {
          if (!s) return;

          if (s === "MSI") {
            navigate("/msi");
            return;
          }

          if (s === "PWG") {
            const backTo =
              lastPWGSport && lastPWGSport !== "TEG" && lastPWGSport !== "MSI"
                ? lastPWGSport
                : "NBA";

            handleSelectSport(backTo);
            return;
          }

          handleSelectSport(s);
        }, [navigate, handleSelectSport, lastPWGSport]);

      //

      // loading state for the Subscribe pill
      // wherever you decide if the CTA shows:
      const showSubscribeCTA = !!user && !entitlement.entitled && !isSubscriptionLoading;


        // PropsDashboard.jsx
        const handleBilling = async () => {
          if (!user) return;
          try {
            await entitlement.manageBilling();
          } catch (e) {
            console.error("Billing portal open failed:", e);
            alert("Could not open billing right now. Please try again.");
          }
        };

        // 2) your existing subscribe flow, with a tiny branch added up front

        const handleSubscribe = async () => {
          if (!user) return;
          if (entitlement.isAdmin) return;
          if (entitlement.isStripePremium) return handleBilling();
          if (entitlement.isPremium) return navigate("/");
          navigate("/pricing");
        };
            

        const handleDiscordConnect = async () => {
          try {
            const result = await getDiscordDestinationAction();

            if (!result?.url) {
              throw new Error("Discord destination was not returned.");
            }
            window.location.assign(result.url);
          } catch (error) {
            console.error("Discord connection failed:", error);
            alert(error?.message || "Unable to connect Discord.");
          }
        };

        
      useEffect(() => {
        localStorage.setItem("activeSport", activeSport);
        const params = new URLSearchParams(window.location.search);
        params.set("sport", activeSport);
        window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
      }, [activeSport]);
      

   /////////////////////////////////////////////////////////////////////////////////
    // --- read helpers
    const g = (k, d) => localStorage.getItem(k) ?? d;

    useEffect(() => {
      function apply() {
        // Theme
        const theme = g("pw_theme", "system");
        const root = document.documentElement;
        if (theme === "dark") root.classList.add("dark");
        else if (theme === "light") root.classList.remove("dark");
        else {
          // system: optional, rely on prefers-color-scheme
          root.classList.toggle("dark", window.matchMedia("(prefers-color-scheme: dark)").matches);
        }

        // Compact cards (example: body class)
        document.body.classList.toggle("pw-compact", g("pw_compact", "0") === "1");

        // Default sport on load (only if none already set)
        const defSport = g("pw_default_sport", "MLB");
        // if you store active sport in state/localStorage, initialize from defSport

        // Odds / time zone / tooltips
        // Keep these in a ref or state if you use them in render:
        // setOddsFmt(g("pw_odds_format","american"));
        // setTz(g("pw_tz","local"));
        // setShowTips(g("pw_tooltips","1")==="1");
      }

      apply();
      window.addEventListener("pw:settings-changed", apply);
      return () => window.removeEventListener("pw:settings-changed", apply);
    }, []);

  //////////////////////////Subsctiption Tracking section///////////////////////////


  //////////////////////////////
  ///////////////GURU LIST INITIALIZATION

  const {
    myGuruList,
    myGuruItems,
    setMyGuruItems,
    clearMyGuruList,
    cardKey,
    isInMyList,
    toggleMyGuru,
    addManyToMyGuruList,
    removeManyFromMyGuruList,
  } = useMyGuruList(user);

  //////////////





// FOR ADVANCED 
// Build a clean, cross-sport games list from what's in propsCache
// All games across cached sports, e.g. "NYK vs BOS (NBA)"
// All games across all cached sports, canonicalized (used by Advanced panel)
// All games we have in cache (across sports)
const allGamesAllSports = useMemo(() => {
  const s = new Set();
  Object.entries(propsCache || {}).forEach(([sportKey, rows]) => {
    (Array.isArray(rows) ? rows : []).forEach(p => {
      const key = canonicalGameKey(p, sportKey);
      if (key) s.add(key);
    });
  });
  return Array.from(s).sort();
}, [propsCache]);


// Small helpers reusing your existing single-sport logic
const norm = (v) => String(v ?? "").trim().toLowerCase();
const teamOf = (p) => p.Team || p["Team Name"] || "";
const oppOf  = (p) => p.Opponent || p["Opponent Name"] || "";
const isMMArow = (p) => String(p.Sport || "").toUpperCase() === "MMA";
const matchupOf = (p) => {
  const direct = p.Matchup && !/nan/i.test(String(p.Matchup)) ? String(p.Matchup) : "";
  if (direct) return direct;
  const t = teamOf(p), o = oppOf(p);
  if (!t || !o) return "";
  const ha  = norm(p["Home/Away"] || p.HomeAway);
  const sep = ha === "away" ? " @ " : " vs ";
  return `${t}${sep}${o}`;
};
const playerKey = (x) => `${x.Sport || ""}:${x.Player}`; // avoid cross-sport collisions

// helpers to stop suspect players

// Treat a pick as an OVER based on its Tag.
// Adjust this if you have a different convention.
function isOverPick(p) {
  const t = up(p.Tag || "");
  // e.g. "MEGA SMASH", "SMASH", "LEAN OVER"
  return t.includes("OVER") || t.includes("SMASH");
}

// Minutes guardrail: undefined/null => OK; explicit false => block
function minutesOkForLineup(p) {
  return p.lineup_minutes_ok !== false;
}


// ---- MULTI-SPORT generation (pure) --------------------------------
const generateMultiSportLineups = ({
  sports,
  tags = [],
  games = [],
  lineupSize = 6,
  max = 20,
  mixType = "Even Mix",
}) => {
  // 1) Merge base
  let base = [];
  (sports || []).forEach((s) => {
    const key = up(s);
    base = base.concat(Array.isArray(propsCache[key]) ? propsCache[key] : []);
  });
  if (!base.length) return [];

  // 2) Normalize filters
  const tagSet   = new Set((tags  || []).map((t) => up(t)));
  const gamesSet = new Set((games || []).map((g) => up(g)));

  // 3) Filter pool with canonical matchup keys
  const pool = base.filter((p) => {
    if (!p.Player) return false;

    // team/opponent required for team sports; MMA allowed without team
    if (!isMMArow(p) && (!teamOf(p) || !oppOf(p))) return false;

    const k = matchupKey(p); // includes (SPORT)
    if (!k) return false;

    if (gamesSet.size && !gamesSet.has(up(k))) return false;

    if (tagSet.size) {
      const t = up(p.Tag || "");
      if (!tagSet.has(t)) return false;
    }
    return true;
  });

  if (!pool.length) return [];

  /////////////////////// Added 11/11/2025 to filter out risky players based on minutes played
  // 3b) Split into overs and unders, with minutes guardrail on overs
  const overs = pool.filter((p) => {
    if (!isOverPick(p)) return false;
    // block fragile-minutes OVERS from lineups
    return minutesOkForLineup(p);
  });

  const unders = pool.filter((p) => !isOverPick(p));
///////////////////////////////////////////////////////////////////////////

  // 4) Mix chooser (supports "3_OVER_3_UNDER", "3 OVER 3 UNDER", or fall back)
  const parseMix = (mix, size) => {
    const s = up(mix).replace(/_/g, " ");
    const m = s.match(/(\d+)\s*OVER\s*(\d+)\s*UNDER/);
    if (m) return { over: Number(m[1]), under: Number(m[2]) };
    return computeOUCounts(size, /*overs*/ 999, /*unders*/ 999, "Even Mix");
  };
    const { over, under } =
    getMixCounts(lineupSize, mixType, overs.length, unders.length);

  const takeOvers  = shuffleArray(overs).slice(0, over);
  const takeUnders = shuffleArray(unders).slice(0, under);


  const lineups = [];
  for (let i = 0; i < max; i++) {
    let attempts = 0;
    while (attempts++ < 200) {
      const takeOvers  = shuffleArray(overs).slice(0, targetMix.over);
      const takeUnders = shuffleArray(unders).slice(0, targetMix.under);

      let tmp = [...takeOvers, ...takeUnders];

      if (tmp.length < lineupSize) {
        const need  = lineupSize - tmp.length;
        const taken = new Set(tmp.map((x) => `${up(x.Sport)}:${x.Player}`));

        const restPool = pool.filter((p) => {
          if (taken.has(`${up(p.Sport)}:${p.Player}`)) return false;
          // still block fragile-minutes OVERS from filler
          if (isOverPick(p) && !minutesOkForLineup(p)) return false;
          return true;
        });

        const rest = shuffleArray(restPool).slice(0, need);
        tmp = tmp.concat(rest);
      }


      if (tmp.length !== lineupSize) continue;

      // unique by sport+player
      const uniq = new Set(tmp.map((x) => `${up(x.Sport)}:${x.Player}`));
      if (uniq.size !== tmp.length) continue;

      lineups.push(tmp);
      break;
    }
  }

  return lineups;
};


// Warm caches whenever the Advanced sports set changes
const handleAdvSportsChange = (next) => {
  setSelectedSports(next);
  next.forEach((s) => {
    const key = String(s).toUpperCase();
    if (!(Array.isArray(propsCache[key]) && propsCache[key].length)) ensureSportInCache(key);
  });
};

/// Build the “Filter by Game” list for the *checked* sports
const gamesForSelected = useMemo(() => {
  const chosen = new Set((selectedSports || []).map((s) => String(s).toUpperCase()));
  if (chosen.size === 0) return [];   // nothing checked -> empty list

  const out = new Set();

  for (const [sportKey, rows] of Object.entries(propsCache || {})) {
    const sk = String(sportKey).toUpperCase();
    if (!chosen.has(sk)) continue;    // only include checked sports

    (Array.isArray(rows) ? rows : []).forEach((p) => {
      const key = canonicalGameKey(p, sk);   // your existing helper
      if (key && !/nan/i.test(key)) out.add(key);
    });
  }

  return Array.from(out).sort();
}, [propsCache, selectedSports]);


// Accepts "3_OVER_3_UNDER" or "3 OVER 3 UNDER" (any mix of spaces/underscores)
const parseFixedMix = (mix = "") => {
  const s = String(mix).toUpperCase().replace(/[_\s]+/g, " ").trim();
  const m = s.match(/^(\d+)\s+OVER\s+(\d+)\s+UNDER$/);
  return m ? { over: +m[1], under: +m[2] } : null;
};

// Decide O/U targets for a given pick size and mixType
// Returns { over, under } for either a fixed recipe ("3_OVER_3_UNDER")
// or a named mix ("Even", "Overs Heavy", "Unders Heavy", "Flex")
// Decide O/U targets for a given pick size and mixType
// Returns { over, under } for either a fixed recipe ("3_OVER_1_UNDER")
// or a named mix ("Even", "Overs Heavy", "Unders Heavy", "Flex")
function getMixCounts(size, mix, oversLen = 999, undersLen = 999) {
  // 1) fixed recipe? accept spaces or underscores
  const fixed = parseFixedMix?.(mix) ||
                (function (m) {
                  const mm = String(m || "").toUpperCase().match(/(\d+)_?OVER_?(\d+)_?UNDER/);
                  return mm ? { over: +mm[1], under: +mm[2] } : null;
                })(mix);
  if (fixed) return fixed;

  // 2) named recipe -> translate to computeOUCounts() dialect
  const m = String(mix || "").toLowerCase().replace(/\s+/g, "_");
  let pref = "Even";
  if (m.includes("over") && (m.includes("heavy") || m === "overs" || m === "moreovers")) {
    pref = "MoreOvers";
  } else if (m.includes("under") && (m.includes("heavy") || m === "unders" || m === "moreunders")) {
    pref = "MoreUnders";
  } else if (m === "flex") {
    // treat Flex as Even; backfill logic still keeps size correct
    pref = "Even";
  }
  return computeOUCounts(size, oversLen, undersLen, pref);
}

// Nice-looking pill toggle for the NBA grid column preferences
function renderColumnChip(label, key, nbaGridColumns, setNbaGridColumns) {
  const on = !!nbaGridColumns[key];

  return (
    <button
      type="button"
      onClick={() =>
        setNbaGridColumns(prev => ({
          ...prev,
          [key]: !prev[key],
        }))
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        border: on ? "1px solid #0f172a" : "1px solid #e5e7eb",
        background: on ? "#0f172a" : "#f9fafb",
        color: on ? "#f9fafb" : "#4b5563",
        fontSize: "0.72rem",
        fontWeight: 600,
        cursor: "pointer",
        boxShadow: on ? "0 1px 4px rgba(15,23,42,0.35)" : "none",
        transition: "background 120ms ease, color 120ms ease, box-shadow 120ms ease",
      }}
    >
      {/* little status dot */}
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "999px",
          border: on ? "none" : "1px solid #9ca3af",
          background: on ? "#22c55e" : "transparent",
        }}
      />
      {label}
    </button>
  );
}



const handleAdvancedGenerate = async ({
  mixType,
  maxLineups,
  tags,
  games,
  sports,
  lineupSize, // from panel (Pick Count)
}) => {
  const generationStartedAt = new Date();
  const wantSize = Number(lineupSize || advLineupSize || 6);
  const wantLUs  = Number(maxLineups || 20);
  const sportsU  = (sports?.length ? sports : [activeSport]).map(s => String(s).toUpperCase());

  // clear old message/results
  setLineupNotice("");
  setGeneratedLineups([]);

  // helper for mix resolution
  const getMixCounts = (size, mix, oversLen = 999, undersLen = 999) => {
    const m = String(mix || "").toUpperCase().match(/(\d+)_?OVER_?(\d+)_?UNDER/);
    if (m) return { over: +m[1], under: +m[2] };

    const key = String(mix || "").toLowerCase();
    let pref = "Even";
    if (key.includes("overs_heavy") || (key.includes("over") && key.includes("heavy"))) pref = "MoreOvers";
    else if (key.includes("unders_heavy") || (key.includes("under") && key.includes("heavy"))) pref = "MoreUnders";
    else if (key.includes("flex")) pref = "Even";

    return computeOUCounts(size, oversLen, undersLen, pref);
  };

  setPreloadBusy(true);
  try {
    // warm caches
    await Promise.all(
      sportsU.map(s => (propsCache[s]?.length ? Promise.resolve() : ensureSportInCache(s)))
    );

    // combine bases
    let base = [];
    sportsU.forEach(s => { base = base.concat(Array.isArray(propsCache[s]) ? propsCache[s] : []); });

    const norm   = (v) => String(v ?? "").trim().toLowerCase();
    const isMMA  = (p) => String(p.Sport || "").toUpperCase() === "MMA";
    const teamOf = (p) => p.Team || p["Team Name"] || "";
    const oppOf  = (p) => p.Opponent || p["Opponent Name"] || "";
    const matchupKey = (p) => {
      const sportKey = String(p.Sport || "").toUpperCase();
      if (isMMA(p)) {
        const a = norm(p.Player), b = norm(p.Opponent);
        if (!a && !b) return "";
        return `${[a, b].filter(Boolean).sort().join(" vs ")} (${sportKey})`;
      }
      const t = teamOf(p), o = oppOf(p);
      if (t && o) return `${[t, o].filter(Boolean).sort().join(" vs ")} (${sportKey})`;
      const m = p.Matchup && !/nan/i.test(String(p.Matchup)) ? String(p.Matchup) : "";
      return m ? `${m} (${sportKey})` : "";
    };

    const tagSet  = new Set((tags  || []).map(String));
    const gameSet = new Set((games || []).map(norm));

    const pool = base.filter(p => {
      if (!p.Player) return false;
      if (!eligibleLineupPool([p], { sport: p.Sport, now: generationStartedAt }).eligible.length) return false;
      if (!isMMA(p) && (!teamOf(p) || !oppOf(p))) return false;
      if (tagSet.size && !tagSet.has(String(p.Tag))) return false;
      if (gameSet.size) {
        const key = norm(matchupKey(p));
        if (!key || !gameSet.has(key)) return false;
      }
      return true;
    });

    if (!pool.length) {
      setGeneratedLineups([]);
      setLineupNotice("No players match these options. Try relaxing Games/Tags or selecting more sports.");
      return;
    }

    // split OU
    const overs  = shuffleArray(pool.filter(p => isOverTag(p.Tag)));
    const unders = shuffleArray(pool.filter(p => isUnderTag(p.Tag)));

    // final mix
    const fixed   = parseFixedMix?.(mixType);
    const pickMix = (fixed && fixed.over + fixed.under === wantSize)
      ? fixed
      : getMixCounts(wantSize, mixType, overs.length, unders.length);

    // early feasibility note (insufficient pool for requested mix)
    if (overs.length < pickMix.over || unders.length < pickMix.under) {
      setLineupNotice(
        `Requested mix needs at least ${pickMix.over} OVER and ${pickMix.under} UNDER picks, ` +
        `but pool has ${overs.length} OVER and ${unders.length} UNDER. We’ll backfill from the rest of the pool.`
      );
    }

    const takeOne = () => {
      const takeOvers  = shuffleArray(overs).slice(0, pickMix.over);
      const takeUnders = shuffleArray(unders).slice(0, pickMix.under);
      let tmp = [...takeOvers, ...takeUnders];
      if (tmp.length < wantSize) {
        const need  = wantSize - tmp.length;
        const taken = new Set(tmp.map(x => lineupPlayerKey(x, x.Sport)));
        const rest  = shuffleArray(pool.filter(p => !taken.has(lineupPlayerKey(p, p.Sport)))).slice(0, need);
        tmp = [...tmp, ...rest];
      }
      return tmp;
    };

    const lineups = [];
    for (let i = 0; i < wantLUs; i++) {
      let tries = 0;
      while (tries++ < 200) {
        const tmp = takeOne();
        if (tmp.length !== wantSize) continue;
        const uniq = new Set(tmp.map(x => lineupPlayerKey(x, x.Sport)));
        if (uniq.size !== tmp.length) continue;
        lineups.push(tmp);
        break;
      }
    }

    const commitTime = new Date();
    const safeLineups = lineups.filter((lineup) => lineupStillEligible(lineup, { now: commitTime }));
    setGeneratedLineups(safeLineups);
    setShowLineupsFocus(true);
    setLineupsTab('lineups');
    setShowLineupSection(true);

    if (isNarrow && safeLineups.length > 0) {
      setTaxonomyOpen(false);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          document.getElementById("pwg-lineups-dock")?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      });
    }

    // notices for none/fewer
    if (safeLineups.length === 0) {
      setLineupNotice(
        "No valid lineups could be built with the current pick count, mix, and filters. " +
        "Try a different mix, fewer constraints, or more sports."
      );
    } else if (safeLineups.length < wantLUs) {
      setLineupNotice(`Generated ${safeLineups.length} of ${wantLUs} requested (limited by filters or pool size).`);
    } else {
      setLineupNotice("");
    }

  } finally {
    setPreloadBusy(false);
  }
};





//END FOR ADVANCED

  // --- Splash control: leave as soon as the first usable board has painted. ---
  const [showSplash, setShowSplash] = useState(true);


  
  /////////////////////////////////// Optimization for loads ////////////////////////////
  // show featured first, then reveal full grid on click
  const [showAllProps, setShowAllProps] = useState(true);
  const [showLineupSection, setShowLineupSection] = useState(false);
  /////////////////////////////
  // use your real state names:
// Single source of truth for search text used by both desktop + mobile
  // Bottom action bar should appear whenever we are in the app's "narrow/mobile" breakpoint.
  const isMobileBar = isNarrow;

  // ---- Lineups focus (Option B) ----
  // When true and we have results, the main content switches from Cards/Grid to the Lineups workspace.
  const [showLineupsFocus, setShowLineupsFocus] = useState(false);
  const [lineupsTab, setLineupsTab] = useState('lineups'); // 'lineups' | 'exposure'
  const [hideLineupControls, setHideLineupControls] = useState(false);
  const [draftKingsResult, setDraftKingsResult] = useState(null);



  const [showAll, setShowAll] = useState(false); // false = Highlights, true = Show All
  const [filtersOpen, setFiltersOpen] = useState(false);

  //const [selectedGame, setSelectedGame] = useState("");
  // multi-game filter for the main grid
  const [selectedGames, setSelectedGames] = useState([]); // array of matchup strings
  const [collapsedSoccerLeagues, setCollapsedSoccerLeagues] = useState({});
  const [collapsedNFLWindows, setCollapsedNFLWindows] = useState({});
  const [collapsedMLBSections, setCollapsedMLBSections] = useState({});
  const [collapsedMLBMatchups, setCollapsedMLBMatchups] = useState({});
  const localTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time", []);
  const regularNFLGameOptions = useMemo(
    () => String(activeSport || "").toUpperCase() === "NFL" ? buildNFLFixtureOptions(propsData) : [],
    [activeSport, propsData]
  );
  const regularNFLGameGroups = useMemo(
    () => groupNFLFixtureOptions(regularNFLGameOptions, "America/Chicago"),
    [regularNFLGameOptions]
  );
  const cfbGameGroups = useMemo(
    () => String(activeSport || "").toUpperCase() === "CFB" ? buildCFBFixtureGroups(propsData) : [],
    [activeSport, propsData]
  );
  const awayAtHomeGameOptions = useMemo(
      () => ["NFL", "CFB", "WNBA"].includes(String(activeSport || "").toUpperCase())
      ? buildAwayAtHomeGameOptions(propsData)
      : [],
    [activeSport, propsData]
  );
  const [gameMenuOpen, setGameMenuOpen] = useState(false);

  const L5_DISPLAY_ORDER = [
    "P5",
    "STRONG",
    "BOUNCE",
    "TURNING_UP",
    "SURGING",
    "COOLING_OFF",
    "DIP",
    "MIXED",
    "WEAK",
    "INCOMPLETE",
  ];

  const L5_LABELS = {
    P5: "PERFECT 5",
    STRONG: "4/5 STRONG",
    BOUNCE: "BOUNCE SIGNAL",
    TURNING_UP: "TURNING UP",
    SURGING: "SURGING",
    COOLING_OFF: "COOLING OFF",
    DIP: "RUN THEN SLIP",
    MIXED: "3/5 MIXED",
    WEAK: "0-2/5 WEAK",
    INCOMPLETE: "INCOMPLETE",
  };

  const normalizeL5Raw = (value) =>
    String(value ?? "")
      .trim()
      .toUpperCase()
      .replace(/^L5:\s*/, "");

  const getL5CanonicalKey = (valueOrRow) => {
    const isRow = valueOrRow && typeof valueOrRow === "object";

    const rawValue = isRow
      ? (
          valueOrRow?.L5_Setup ??
          valueOrRow?.["L5_Setup"] ??
          valueOrRow?.["L5 Setup"] ??
          valueOrRow?.l5Setup ??
          valueOrRow?.L5Setup ??
          valueOrRow?.l5Shape ??
          valueOrRow?.L5_Shape ??
          valueOrRow?.["L5 Shape"] ??
          valueOrRow?.L5Shape ??
          ""
        )
      : valueOrRow;

    const raw = normalizeL5Raw(rawValue);

    const hits = isRow
      ? Number(
          valueOrRow?.L5_Hits ??
          valueOrRow?.["L5_Hits"] ??
          valueOrRow?.["L5 Hits"] ??
          valueOrRow?.l5Hits ??
          valueOrRow?.L5Hits
        )
      : NaN;

    if (raw === "P5" || raw.includes("PERFECT 5")) return "P5";
    if (raw === "TURNING_UP" || raw.includes("TURNING UP")) return "TURNING_UP";
    if (raw === "SURGING" || raw.includes("SLIP THEN RUN")) return "SURGING";
    if (raw === "BOUNCE" || raw.includes("BOUNCE")) return "BOUNCE";
    if (raw === "COOLING_OFF" || raw.includes("COOLING OFF")) return "COOLING_OFF";
    if (raw === "DIP" || raw.includes("RUN THEN SLIP")) return "DIP";
    if (raw === "STRONG" || raw.includes("4/5") || raw.includes("STRONG")) return "STRONG";
    if (raw === "MIXED" || raw.includes("3/5") || raw.includes("MIXED")) return "MIXED";
    if (
      raw === "WEAK" ||
      raw.includes("0-2/5") ||
      raw.includes("0/5") ||
      raw.includes("WEAK")
    ) return "WEAK";
    if (raw === "INCOMPLETE" || raw.includes("INCOMPLETE")) return "INCOMPLETE";

    if (Number.isFinite(hits)) {
      if (hits >= 5) return "P5";
      if (hits === 4) return "STRONG";
      if (hits === 3) return "MIXED";
      if (hits <= 2) return "WEAK";
    }

    return raw;
  };

  const getL5Label = (valueOrKey) => {
    const key = getL5CanonicalKey(valueOrKey);
    return L5_LABELS[key] || key || "";
  };


  // ---------------- Formation Filter System (WNBA-first, sport-safe) ----------------
  // Raw formation comes from WNBA_PropAnalysis_Output as L5_FormCode, e.g. OOUUU.
  // This is separate from L5_Setup buckets like "BOUNCE SIGNAL" or "0-2/5 WEAK".
  const FORMATION_FAMILY_ORDER = ["BULLISH", "BEARISH", "TRAP", "MIXED"];

  const FORMATION_FAMILY_LABELS = {
    BULLISH: "Bullish",
    BEARISH: "Bearish",
    TRAP: "Trap / Choppy",
    MIXED: "Mixed",
  };

  const FORMATION_BULLISH = new Set([
    "OOOOO", "OOUOO", "OOUOU", "OOOU", "OOOUO", "OOOOU",
    // recent WNBA watchlist formations that showed strong short-window results
    "OOUUU", "OOUUO",
  ]);

  const FORMATION_BEARISH = new Set([
    "UUUUU", "UUUOO", "UUUOU", "UUOOU", "UUOUU", "UOUUU",
  ]);

  const FORMATION_TRAP = new Set([
    "OUOUU", "UOUOU", "OUOUO", "UOOOU", "OUUUU", "OUUOO", "UOOUU", "UOOUO",
  ]);

  const getFormationCode = (row) => {
    const raw =
      row?.L5_FormCode ??
      row?.["L5_FormCode"] ??
      row?.l5_form_code ??
      row?.l5FormCode ??
      row?.Formation_Code ??
      row?.["Formation Code"] ??
      row?.formation_code ??
      "";

    const code = String(raw ?? "")
      .trim()
      .toUpperCase()
      .replace(/\.0$/, "")
      .replace(/[^OU P]/g, "")
      .replace(/\s+/g, "");

    // only show actual O/U/P formation strings
    if (!code || !/^[OUP]+$/.test(code)) return "";
    return code;
  };

  const getFormationFamily = (rowOrCode) => {
    const code = typeof rowOrCode === "string" ? getFormationCode({ L5_FormCode: rowOrCode }) : getFormationCode(rowOrCode);
    if (!code) return "";
    if (FORMATION_BULLISH.has(code)) return "BULLISH";
    if (FORMATION_BEARISH.has(code)) return "BEARISH";
    if (FORMATION_TRAP.has(code)) return "TRAP";
    return "MIXED";
  };

  const formationFamilyStyle = (family, dark = false, active = false) => {
    const base = {
      borderRadius: 999,
      padding: "0.35rem 0.7rem",
      fontWeight: 900,
      border: "1px solid rgba(15,23,42,0.14)",
      cursor: "pointer",
      whiteSpace: "nowrap",
    };

    const f = String(family || "").toUpperCase();
    if (!active) {
      return {
        ...base,
        background: "transparent",
        color: dark ? "#e5e7eb" : "#111827",
      };
    }

    if (f === "BULLISH") return { ...base, background: "#dcfce7", color: "#166534", border: "1px solid #86efac" };
    if (f === "BEARISH") return { ...base, background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" };
    if (f === "TRAP") return { ...base, background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" };
    return { ...base, background: dark ? "rgba(59,130,246,0.25)" : "rgba(59,130,246,0.12)", color: dark ? "#e5e7eb" : "#111827", border: "1px solid rgba(59,130,246,0.55)" };
  };

  const formationBadgeStyle = (family) => {
    const base = {
      display: "inline-flex",
      alignItems: "center",
      padding: "5px 10px",
      borderRadius: 999,
      border: "1px solid #d1d5db",
      fontSize: 11,
      fontWeight: 900,
      lineHeight: 1,
      whiteSpace: "nowrap",
    };
    if (family === "BULLISH") return { ...base, background: "#dcfce7", color: "#166534", borderColor: "#86efac" };
    if (family === "BEARISH") return { ...base, background: "#fee2e2", color: "#991b1b", borderColor: "#fca5a5" };
    if (family === "TRAP") return { ...base, background: "#fef3c7", color: "#92400e", borderColor: "#fcd34d" };
    return { ...base, background: "#f8fafc", color: "#475569", borderColor: "#cbd5e1" };
  };

  const renderFormationChip = (row, compact = false) => {
    const code = getFormationCode(row);
    if (!code) return null;
    const family = getFormationFamily(code);
    const label = compact
      ? `${FORMATION_FAMILY_LABELS[family] || "Formation"}: ${code}`
      : `${FORMATION_FAMILY_LABELS[family] || "Formation"} Formation: ${code}`;
    return (
      <span
        title={`L5 Formation: ${code} • ${FORMATION_FAMILY_LABELS[family] || family}`}
        style={formationBadgeStyle(family)}
      >
        {label}
      </span>
    );
  };
  // -------------------------------------------------------------------------------


  // ---------------- PWG Pattern System v1 UI helpers ----------------
  const PATTERN_DISPLAY_ORDER = [
    "PERFECT_5",
    "FOUR_OF_FIVE",
    "BOUNCE",
    "TURNING_UP",
    "SURGING",
    "COOLING_OFF",
    "RUN_THEN_SLIP",
    "CHOPPY_MIXED",
    "UPTREND",
    "DOWNTREND",
    "HEAD_SHOULDERS",
    "INV_HEAD_SHOULDERS",
    "TRIANGLE",
    "MEGAPHONE",
    "NEUTRAL",
  ];

  const PATTERN_LABELS = {
    PERFECT_5: "PERFECT 5",
    FOUR_OF_FIVE: "4/5 STRONG",
    BOUNCE: "BOUNCE",
    TURNING_UP: "TURNING UP",
    SURGING: "SURGING",
    COOLING_OFF: "COOLING OFF",
    RUN_THEN_SLIP: "RUN THEN SLIP",
    CHOPPY_MIXED: "CHOPPY / MIXED",
    UPTREND: "Rising",
    DOWNTREND: "Fading",
    HEAD_SHOULDERS: "Topping",
    INV_HEAD_SHOULDERS: "Bottoming",
    TRIANGLE: "Coiling",
    MEGAPHONE: "Volatile",
    NEUTRAL: "Neutral",
  };

  const getPatternCanonicalKey = (valueOrRow) => {
    const isRow = valueOrRow && typeof valueOrRow === "object";
    const isSoccerRow = isRow && String(valueOrRow?.Sport || "").toUpperCase() === "SOCCER";
    const isNbaRow = isRow && String(valueOrRow?.Sport || "").toUpperCase() === "NBA";
    if (isNbaRow) return canonicalExactPattern(valueOrRow);
    const rawValue = isRow
      ? (
          valueOrRow?.Pattern_L10 ??
          valueOrRow?.["Pattern_L10"] ??
          valueOrRow?.Pattern ??
          valueOrRow?.Pattern_Label ??
          valueOrRow?.["Pattern_Label"] ??
          valueOrRow?.["Pattern Label"] ??
          ""
        )
      : valueOrRow;

    const raw = String(rawValue ?? "").trim().toUpperCase().replace(/\s+/g, "_");
    if (!raw || raw === "NAN" || raw === "NONE" || raw === "INSUFFICIENT") {
      return isSoccerRow
        ? soccerPatternKey(valueOrRow)
        : "";
    }

    if (isSoccerRow) {
      if (raw.includes("PERFECT_5")) return "PERFECT_5";
      if (raw.includes("4/5") || raw.includes("FOUR_OF_FIVE")) return "FOUR_OF_FIVE";
      if (raw === "BOUNCE") return "BOUNCE";
      if (raw.includes("TURNING_UP")) return "TURNING_UP";
      if (raw === "SURGING") return "SURGING";
      if (raw.includes("COOLING_OFF")) return "COOLING_OFF";
      if (raw.includes("RUN_THEN_SLIP")) return "RUN_THEN_SLIP";
      if (raw.includes("CHOPPY") || raw === "MIXED") return "CHOPPY_MIXED";
    }

    if (raw.includes("UPTREND") || raw === "RISING") return "UPTREND";
    if (raw.includes("DOWNTREND") || raw === "FADING") return "DOWNTREND";
    if ((raw.includes("INV") || raw.includes("INVERT")) && raw.includes("HEAD")) return "INV_HEAD_SHOULDERS";
    if (raw.includes("HEAD") || raw.includes("TOPPING")) return "HEAD_SHOULDERS";
    if (raw.includes("TRIANGLE") || raw.includes("COILING")) return "TRIANGLE";
    if (raw.includes("MEGAPHONE") || raw.includes("BROAD") || raw.includes("VOLATILE")) return "MEGAPHONE";
    if (raw.includes("NEUTRAL")) return "NEUTRAL";
    return raw;
  };

  const getPatternLabel = (valueOrRow) => {
    const key = getPatternCanonicalKey(valueOrRow);
    if (!key) return "";
    return PATTERN_LABELS[key] || String(valueOrRow?.Pattern_Label ?? valueOrRow?.["Pattern_Label"] ?? key).replaceAll("_", " ");
  };

  const getPatternTone = (valueOrRow) => {
    const key = getPatternCanonicalKey(valueOrRow);
    if (["PERFECT_5", "FOUR_OF_FIVE", "BOUNCE", "TURNING_UP", "SURGING", "UPTREND", "INV_HEAD_SHOULDERS"].includes(key)) return "ok";
    if (["COOLING_OFF", "RUN_THEN_SLIP", "DOWNTREND", "HEAD_SHOULDERS"].includes(key)) return "warn";
    if (key === "TRIANGLE") return "info";
    if (key === "MEGAPHONE") return "danger";
    return "neutral";
  };

  const patternChipStyle = (valueOrRow) => {
    const tone = getPatternTone(valueOrRow);
    const base = {
      display: "inline-flex",
      alignItems: "center",
      padding: "5px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 900,
      lineHeight: 1,
      border: "1px solid #d1d5db",
      whiteSpace: "nowrap",
    };
    if (tone === "ok") return { ...base, background: "#dcfce7", color: "#166534", borderColor: "#86efac" };
    if (tone === "warn") return { ...base, background: "#fef3c7", color: "#92400e", borderColor: "#fcd34d" };
    if (tone === "danger") return { ...base, background: "#fce7f3", color: "#9d174d", borderColor: "#f9a8d4" };
    if (tone === "info") return { ...base, background: "#e0f2fe", color: "#075985", borderColor: "#7dd3fc" };
    return { ...base, background: "#f3f4f6", color: "#374151", borderColor: "#d1d5db" };
  };

  const renderPatternChip = (row, compact = false) => {
    const key = getPatternCanonicalKey(row);
    const label = getPatternLabel(row);
    if (!key || !label) return null;

    const action = String(row?.Pattern_Action ?? row?.["Pattern_Action"] ?? "").trim();
    const direction = String(row?.Pattern_Direction ?? row?.["Pattern_Direction"] ?? "").trim();
    const note = String(row?.Pattern_Note ?? row?.["Pattern_Note"] ?? "").trim();
    const title = [
      `Pattern: ${label}`,
      direction ? `Direction: ${direction}` : "",
      action ? `Action: ${action.replaceAll("_", " ")}` : "",
      note,
    ].filter(Boolean).join(" | ");

    return (
      <span title={title} style={patternChipStyle(row)}>
        {compact ? label : `Pattern: ${label}`}
      </span>
    );
  };
  // ------------------------------------------------------------------



  // ---------------- MLB Graph Intelligence UI helpers ----------------
  const cleanNum = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const getGraphIntelValue = (row, keys = []) => firstNonEmpty(row, keys);

  const graphIntelChipStyle = (tone = "neutral") => {
    const base = {
      display: "inline-flex",
      alignItems: "center",
      padding: "5px 10px",
      borderRadius: 999,
      border: "1px solid #d1d5db",
      fontSize: 11,
      fontWeight: 950,
      lineHeight: 1,
      whiteSpace: "nowrap",
    };

    if (tone === "reclaim") return { ...base, background: "#dcfce7", color: "#166534", borderColor: "#86efac" };
    if (tone === "watch") return { ...base, background: "#fef3c7", color: "#92400e", borderColor: "#fcd34d" };
    if (tone === "trap") return { ...base, background: "#fee2e2", color: "#991b1b", borderColor: "#fca5a5" };
    if (tone === "info") return { ...base, background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" };
    return { ...base, background: "#f8fafc", color: "#475569", borderColor: "#cbd5e1" };
  };

  const renderMlbGraphIntelChips = (row) => {
    const sportKey = String(activeSport || row?.Sport || "").toUpperCase();
    if (sportKey !== "MLB") return null;

    const chips = [];
    const seen = new Set();

    const addChip = (key, label, tone, title) => {
      if (!label) return;
      const cleanKey = String(key || label).toLowerCase();
      if (seen.has(cleanKey)) return;
      seen.add(cleanKey);
      chips.push(
        <span key={`graph-intel-${cleanKey}`} title={title || label} style={graphIntelChipStyle(tone)}>
          {label}
        </span>
      );
    };

    const graphShape = getGraphIntelValue(row, ["Graph_Shape", "Graph Shape", "graph_shape"]);
    const graphNote = getGraphIntelValue(row, ["Graph_Note", "Graph Note", "graph_note"]);
    const reclaim = getGraphIntelValue(row, ["Reclaim_Signal", "Reclaim Signal", "reclaim_signal"]);
    const guruQuality = getGraphIntelValue(row, ["Guru_Pick_Quality", "Guru Pick Quality", "Guru_Quality", "Guru Pick Quality"]);
    const guruFinal = getGraphIntelValue(row, ["Guru_Final", "Guru Final", "Guru_Pick_Label", "Guru Pick Label"]);
    const guruReason = getGraphIntelValue(row, ["Guru_Reason", "Guru Reason", "Guru_Pick_Reason", "Guru Pick Reason"]);

    const recoveryScore = cleanNum(row?.Recovery_Score ?? row?.["Recovery_Score"] ?? row?.["Recovery Score"]);
    const bounceScore = cleanNum(row?.Bounce_Score ?? row?.["Bounce_Score"] ?? row?.["Bounce Score"]);
    const compressionScore = cleanNum(row?.Compression_Score ?? row?.["Compression_Score"] ?? row?.["Compression Score"]);
    const conversionScore = cleanNum(row?.Conversion_Score ?? row?.["Conversion_Score"] ?? row?.["Conversion Score"]);
    const ceilingScore = cleanNum(row?.Ceiling_Score ?? row?.["Ceiling_Score"] ?? row?.["Ceiling Score"]);
    const qualityScore = cleanNum(row?.Guru_Quality_Score ?? row?.["Guru_Quality_Score"] ?? row?.Guru_Pick_Score ?? row?.["Guru_Pick_Score"]);

    const upperFinal = String(guruFinal || "").toUpperCase();
    const upperQuality = String(guruQuality || "").toUpperCase();
    const upperShape = String(graphShape || "").toUpperCase();

    if (upperFinal.includes("FALSE FADE") || upperQuality.includes("BOUNCE")) {
      addChip("false-fade", "⚡ False Fade Watch", "watch", guruReason || guruFinal);
    }

    if (String(reclaim || "").toUpperCase().includes("ACTIVE") || upperShape.includes("RECLAIM")) {
      addChip("reclaim", "📈 Reclaim Active", "reclaim", graphNote || "Recent form has reclaimed the prop line.");
    }

    if (upperShape.includes("BOUNCE") || (Number.isFinite(bounceScore) && bounceScore >= 60)) {
      addChip("bounce", `🔥 Bounce ${Number.isFinite(bounceScore) ? Math.round(bounceScore) : ""}`.trim(), "watch", graphNote || "Bounce setup detected.");
    }

    if (Number.isFinite(recoveryScore) && recoveryScore >= 70) {
      addChip("recovery", `🟢 Recovery ${Math.round(recoveryScore)}`, "reclaim", graphNote || "Recovery score is elevated.");
    }

    if (Number.isFinite(compressionScore) && compressionScore >= 75) {
      addChip("compression", "🧲 Compressed", "info", "Recent outcomes are compressed near the prop line.");
    }

    if (Number.isFinite(conversionScore) && conversionScore >= 70) {
      addChip("conversion", `⚙️ Convert ${Math.round(conversionScore)}`, "reclaim", "Player is converting opportunity into fantasy/stat output.");
    }

    if (Number.isFinite(ceilingScore) && ceilingScore >= 70) {
      addChip("ceiling", `🚀 Ceiling ${Math.round(ceilingScore)}`, "watch", "Ceiling games still exist inside the recent sample.");
    }

    if (Number.isFinite(qualityScore) && qualityScore >= 70 && !upperFinal.includes("STAY")) {
      addChip("guru-quality", `⭐ Guru ${Math.round(qualityScore)}`, "reclaim", guruReason || "High Guru quality score.");
    }

    return chips.length ? chips.slice(0, 4) : null;
  };
  // ------------------------------------------------------------------

  // ---------------- PWG Guru Pick v1 UI helpers ----------------
  // Keep reasoning hidden by default so proprietary logic is not exposed.
  const SHOW_GURU_PICK_REASON = false;

  const normalizeGuruPick = (rowOrValue) => {
    const raw = typeof rowOrValue === "object" && rowOrValue !== null
      ? (
          rowOrValue?.Guru_Final ??
          rowOrValue?.["Guru_Final"] ??
          rowOrValue?.Guru_Pick_Label ??
          rowOrValue?.["Guru_Pick_Label"] ??
          rowOrValue?.Guru_Pick ??
          rowOrValue?.["Guru_Pick"] ??
          rowOrValue?.GuruPick ??
          rowOrValue?.["Guru Pick"] ??
          rowOrValue?.Final_Pick ??
          ""
        )
      : rowOrValue;

    const v = String(raw ?? "").trim().toUpperCase().replace(/[_\s]+/g, " ");
    if (!v || v === "NAN" || v === "NONE") return "";
    if (v.includes("STAY")) return "STAY AWAY";
    if (v.includes("COIN")) return "COIN TOSS";
    if (v.includes("UNDER") && v.includes("EARLY")) return "UNDER (EARLY)";
    if (v.includes("OVER") && v.includes("EARLY")) return "OVER (EARLY)";
    if (v.includes("UNDER")) return "UNDER";
    if (v.includes("OVER")) return "OVER";
    return v;
  };

  const guruPickChipStyle = (rowOrValue) => {
    const pick = normalizeGuruPick(rowOrValue);
    const base = {
      display: "inline-flex",
      alignItems: "center",
      padding: "5px 11px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 950,
      lineHeight: 1,
      border: "1px solid #d1d5db",
      whiteSpace: "nowrap",
      letterSpacing: 0.15,
    };

    if (pick === "OVER") {
      return { ...base, background: "#16a34a", color: "#ffffff", borderColor: "#15803d" };
    }
    if (pick === "OVER (EARLY)") {
      return { ...base, background: "#dcfce7", color: "#166534", borderColor: "#86efac" };
    }
    if (pick === "UNDER") {
      return { ...base, background: "#dc2626", color: "#ffffff", borderColor: "#b91c1c" };
    }
    if (pick === "UNDER (EARLY)") {
      return { ...base, background: "#fee2e2", color: "#991b1b", borderColor: "#fca5a5" };
    }
    const rawDisplay = typeof rowOrValue === "object" && rowOrValue !== null
      ? String(
          rowOrValue?.Guru_Pick_Label ??
          rowOrValue?.["Guru_Pick_Label"] ??
          rowOrValue?.Guru_Final ??
          rowOrValue?.["Guru_Final"] ??
          rowOrValue?.Guru_Pick_Quality ??
          rowOrValue?.["Guru_Pick_Quality"] ??
          ""
        ).toUpperCase()
      : "";

    if (rawDisplay.includes("FALSE FADE") || rawDisplay.includes("BOUNCE")) {
      return { ...base, background: "#fef3c7", color: "#92400e", borderColor: "#fcd34d" };
    }

    if (pick === "STAY AWAY") {
      return { ...base, background: "#e5e7eb", color: "#374151", borderColor: "#cbd5e1" };
    }
    if (pick === "COIN TOSS") {
      return { ...base, background: "#f3f4f6", color: "#4b5563", borderColor: "#d1d5db" };
    }
    return { ...base, background: "#f3f4f6", color: "#374151", borderColor: "#d1d5db" };
  };

  const renderGuruPickChip = (row, compact = false) => {
    const pick = normalizeGuruPick(row);
    if (!pick) return null;

    const conf = String(
      row?.Guru_Pick_Confidence ??
      row?.["Guru_Pick_Confidence"] ??
      row?.GuruPickConfidence ??
      ""
    ).trim();

    const reason = String(
      row?.Guru_Pick_Reason ??
      row?.["Guru_Pick_Reason"] ??
      row?.GuruPickReason ??
      ""
    ).trim();

    const score = String(
      row?.Guru_Pick_Score ??
      row?.["Guru_Pick_Score"] ??
      row?.GuruPickScore ??
      ""
    ).trim();

    const rawDisplay = String(
      row?.Guru_Pick_Label ??
      row?.["Guru_Pick_Label"] ??
      row?.Guru_Final ??
      row?.["Guru_Final"] ??
      ""
    ).trim();

    const quality = String(
      row?.Guru_Pick_Quality ??
      row?.["Guru_Pick_Quality"] ??
      ""
    ).trim();

    let displayPick = pick;
    const upperDisplay = rawDisplay.toUpperCase();
    const upperQuality = quality.toUpperCase();

    if (upperDisplay.includes("FALSE FADE")) displayPick = "FALSE FADE WATCH";
    else if (upperQuality.includes("BOUNCE")) displayPick = "BOUNCE WATCH";
    else if (rawDisplay && rawDisplay.length <= 30) displayPick = rawDisplay;

    const label = compact ? `Guru: ${displayPick}` : `Guru Pick: ${displayPick}`;
    const meta = [conf, quality, score ? `${score}` : ""].filter(Boolean).join(" • ");
    const title = reason
      ? `${label}${meta ? ` • ${meta}` : ""} • ${reason}`
      : `${label}${meta ? ` • ${meta}` : ""}`;

    return (
      <span title={title} style={guruPickChipStyle(row)}>
        {label}
      </span>
    );
  };

  const GURU_PICK_OPTIONS = ["OVER", "OVER (EARLY)", "UNDER", "UNDER (EARLY)", "STAY AWAY", "COIN TOSS"];

  const guruPickFilterButtonStyle = (pick, active, darkMode = false) => {
    const base = {
      borderRadius: 999,
      padding: "0.35rem 0.75rem",
      fontWeight: 900,
      border: "1px solid rgba(15,23,42,0.14)",
      cursor: "pointer",
      whiteSpace: "nowrap",
    };

    if (!active) {
      return {
        ...base,
        background: "transparent",
        color: darkMode ? "#e5e7eb" : "#111827",
      };
    }

    if (pick === "OVER") return { ...base, background: "#16a34a", color: "#fff", border: "1px solid #15803d" };
    if (pick === "OVER (EARLY)") return { ...base, background: "#dcfce7", color: "#166534", border: "1px solid #86efac" };
    if (pick === "UNDER") return { ...base, background: "#dc2626", color: "#fff", border: "1px solid #b91c1c" };
    if (pick === "UNDER (EARLY)") return { ...base, background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" };
    return { ...base, background: "#e5e7eb", color: "#374151", border: "1px solid #cbd5e1" };
  };
  // ------------------------------------------------------------------

  const [generatedLineups, setGeneratedLineups] = useState([]);
  const [lineupResults, setLineupResults] = useState([]);

  const [selectedPropType, setSelectedPropType] = useState("");
  const [selectedPropTypes, setSelectedPropTypes] = useState([]); // multi-select prop types
  const togglePropType = useCallback((pt) => {
    setSelectedPropTypes((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (!pt) return [];
      return arr.includes(pt) ? arr.filter((x) => x !== pt) : [...arr, pt];
    });
  }, []);
  const resetPropTypes = useCallback(() => { setSelectedPropTypes([]); setSelectedPropType(""); }, []);
  const [selectedConfidence, setSelectedConfidence] = useState("");
  const [selectedGuruGrade, setSelectedGuruGrade] = useState(""); // Conviction Grade (A/B+/...)
  const [maxLineups, setMaxLineups] = useState(10);
  const [mixType, setMixType] = useState("3_OVER_3_UNDER");
  const [homeAwayFilter, setHomeAwayFilter] = useState("");
  const [momentumFilter, setMomentumFilter] = useState("");
  const [momentumPatternFilter, setMomentumPatternFilter] = useState("");
  const [confirmedMomentumFilter, setConfirmedMomentumFilter] = useState("");
  const [playerTypeFilter, setPlayerTypeFilter] = useState("");
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedGuruPotential, setSelectedGuruPotential] = useState("");
  const [dqsFilter, setDqsFilter] = useState("ALL"); // DQS filter: ALL | HIGH | MED | LOW
  const [selectedZGuruTag, setSelectedZGuruTag] = useState("");
  const [selectedLeanDirection, setSelectedLeanDirection] = useState("");
  const [selectedGuruConflict, setSelectedGuruConflict] = useState("");
  const [selectedGuruPicks, setSelectedGuruPicks] = useState("");
  const [guruListFilter, setGuruListFilter] = useState("all"); // "all" | "my-list"
  const [myPlaysSportFilter, setMyPlaysSportFilter] = useState("ALL");
  const [clearMyPlaysDialogOpen, setClearMyPlaysDialogOpen] = useState(false);

  // --- MASTER DATA Taxonomy (Phase 1): Studs / Cores / Fillers + Tree View ---
  // Users do NOT define these tiers; we compute them from player averages / projections.
  // Tree is used to filter (and later to drive lineup inclusion/exclusion by node).
  const [taxonomyOpen, setTaxonomyOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const isMobileWidth = window.matchMedia?.("(max-width: 860px)")?.matches;
    if (isMobileWidth) return false;

    const saved = window.localStorage.getItem(LEFT_RAIL_STATE_KEY);
    if (saved === "false") return false;
    if (saved === "true") return true;

    // Phase 1 desktop default: left rail is open.
    return true;
  }); // left tree drawer (mobile) / panel (desktop)
  const [leftPanelTab, setLeftPanelTab] = useState("filters"); // default
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const [commandRailPanel, setCommandRailPanel] = useState("games_quick");
  const [injuryStatusFilter, setInjuryStatusFilter] = useState("ALL");

  // Phase 2.1: desktop chip wall can collapse so cards/grid get more vertical space.
  const [desktopTopFiltersOpen, setDesktopTopFiltersOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem(TOP_FILTERS_STATE_KEY);
    return saved === "false" ? false : true;
  });

  useEffect(() => {
    if (typeof window === "undefined" || isNarrow) return;
    window.localStorage.setItem(TOP_FILTERS_STATE_KEY, desktopTopFiltersOpen ? "true" : "false");
  }, [desktopTopFiltersOpen, isNarrow]);

  useEffect(() => {
    if (typeof window === "undefined" || isNarrow) return;
    window.localStorage.setItem(LEFT_RAIL_STATE_KEY, taxonomyOpen ? "true" : "false");
  }, [taxonomyOpen, isNarrow]);

  const [taxonomyScope, setTaxonomyScope] = useState("ALL"); // "ALL" | "GURULIST"
  const [taxonomyTier, setTaxonomyTier] = useState("ALL");   // "ALL" | "STUDS" | "CORES" | "FILLERS"
  const [taxonomyPlayer, setTaxonomyPlayer] = useState("");  // exact player name (optional)

  const [selectedIsGuruPicks, setSelectedIsGuruPicks] = useState("");
  const [selectedGuruMagic, setSelectedGuruMagic] = useState("");
  const [showCommentary, setShowCommentary] = useState(false);
  const [selectedCommentary, setSelectedCommentary] = useState("");
  const lineupRefs = useRef([]);
  const detailRefs = useRef([]);
  const [cardToggles, setCardToggles] = useState({});
  const [selectedTime, setSelectedTime] = useState("All");
  const now = new Date();
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsDataError, setPropsDataError] = useState("");
  //const [selectedSports, setSelectedSports] = useState(["MLB"]); // Default to NBA or []
  const [pitcherTierFilter, setPitcherTierFilter] = useState("");
  const [hitterTierFilter, setHitterTierFilter] = useState([]);
  const [oppHitterTierFilter, setOppHitterTierFilter] = useState([]);
  // NEW filters for pitcher props (team perspective)
  const [teamPitcherTierFilter, setTeamPitcherTierFilter] = useState("");
  const [teamAttackabilityFilter, setTeamAttackabilityFilter] = useState("");
  const [teamWhipTierFilter, setTeamWhipTierFilter] = useState("");
  const [teamKpctTierFilter, setTeamKpctTierFilter] = useState("");
  // NEW filters for pitcher props (opp perspective)

  const [oppWhipTierFilter, setOppWhipTierFilter] = useState([]);
  const [oppKpctTierFilter, setOppKpctTierFilter] = useState([]);
  const [oppAttackabilityFilter, setOppAttackabilityFilter] = useState([]);
  const [oppPitcherTierFilter, setOppPitcherTierFilter] = useState([]);

  const [minL10Pct, setMinL10Pct] = React.useState(''); // '', '50', '60', '70', ...
  const [minOppL10Pct, setMinOppL10Pct] = useState("");

  // NEW: opponent pitcher hand + batting order filters
  const [oppPitcherHandFilter, setOppPitcherHandFilter] = useState(""); // "RHP" | "LHP" | ""
  const [battingOrderFilter, setBattingOrderFilter] = useState("");     // "", "TOP12", "CORE35", "BOTTOM69", "1".."9"

// GURU MODE
const [guruMode, setGuruMode] = useState(false);
const [guruType, setGuruType] = useState("overs");

// MINUTES (advanced lineup guardrail)
const [minMpgFilter, setMinMpgFilter] = useState("0"); // "0", "20", "25", "30", ...

// CARD FILTERS (main board)
const [propFilter, setPropFilter] = useState("");
const [confidenceFilter, setConfidenceFilter] = useState("");

// STOCKS / TEG
const [stocksHeatOnly, setStocksHeatOnly] = useState(false);

// Line-tax filter: ALL / DISCOUNT / FAIR / TAXED / HYPER-TAXED
const [taxFilter, setTaxFilter] = useState("ALL");

// Blowout filters
// - blowoutLevelFilter: coarse preset-style filtering (All / No High / Low only)
// - blowoutFilter: detailed filtering (All / Low / No High / Low+Moderate / High)
const [blowoutLevelFilter, setBlowoutLevelFilter] = React.useState("all");
const [blowoutFilter, setBlowoutFilter] = React.useState("all"); 
// values: "all" | "no-high" | "low-only"

// NEW: DvP Tier + Selection Strategy filters
const [dvpTierFilter, setDvpTierFilter] = useState("ALL");   // TARGET / SOFT / TOUGH / ELITE / R0
const [selStratFilter, setSelStratFilter] = useState("ALL"); // OVER / UNDER / MIXED / NO_EDGE
const [guruAlertFilter, setGuruAlert] = useState("ALL"); // "ALL" | "0" | "1" | "2"

// --- RIGHT DOCK: Lineups / Exposure (desktop-first) ---
const [dockOpen, setDockOpen] = useState(false);
const [dockTab, setDockTab] = useState("lineups"); // "lineups" | "exposure"
const [dockKey, setDockKey] = useState(0);         // forces a clean re-render on generate


// Minutes Trend filter (min_trend from backend; numeric like -2.3, +1.8)
const [minutesTrendMin, setMinutesTrendMin] = useState(null); // null = no lower bound
const [minutesTrendMax, setMinutesTrendMax] = useState(null); // null = no upper bound

// OVERS minutes gate: hide OVER tags when minutes trend is negative
const [oversMinutesGate, setOversMinutesGate] = useState(false);

// ✅ ADD: Attack UNDERS gate
const [attackUndersMinutesGate, setAttackUndersMinutesGate] = useState(false);

const [zodiacFilter, setZodiacFilter] = useState("ALL");

const [openPctKey, setOpenPctKey] = useState(null);

useEffect(() => {
  const close = () => setOpenPctKey(null);
  document.addEventListener("pointerdown", close);
  return () => document.removeEventListener("pointerdown", close);
}, []);



// L5 Shape filter (pattern descriptor)
const [selectedL5Shapes, setSelectedL5Shapes] = useState([]);

// Raw L5 formation filters, e.g. OOUUU / UOUOU, and family buckets.
const [selectedFormationFamilies, setSelectedFormationFamilies] = useState([]);
const [selectedFormationCodes, setSelectedFormationCodes] = useState([]);

// PWG Pattern System v1 filter
const [selectedPatterns, setSelectedPatterns] = useState([]);
const [selectedPatternComponents, setSelectedPatternComponents] = useState([]);
const [patternMatchMode, setPatternMatchMode] = useState("ALL");

// Backend-owned Bounce Classification v1 filters
const [selectedBounceLabels, setSelectedBounceLabels] = useState([]);
const [selectedBounceFamilies, setSelectedBounceFamilies] = useState([]);

// Minutes Trend pill filter (backend categorical values: STABLE/RISING/SURGING/FALLING/COLLAPSING/etc.)
const [selectedMinutesTrends, setSelectedMinutesTrends] = useState([]);

const toggleL5Shape = (shape) => {
  const canonical = getL5CanonicalKey(shape);

  setSelectedL5Shapes((prev) =>
    prev.includes(canonical)
      ? prev.filter((x) => x !== canonical)
      : [...prev, canonical]
  );
};

const resetL5Shapes = () => setSelectedL5Shapes([]);

const toggleFormationFamily = (family) => {
  const key = String(family || "").toUpperCase();
  if (!key) return;
  setSelectedFormationFamilies((prev) =>
    prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
  );
};

const toggleFormationCode = (code) => {
  const key = getFormationCode({ L5_FormCode: code });
  if (!key) return;
  setSelectedFormationCodes((prev) =>
    prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
  );
};

const resetFormations = () => {
  setSelectedFormationFamilies([]);
  setSelectedFormationCodes([]);
};

const allFormationCodes = useMemo(() => {
  const src = Array.isArray(propsData) ? propsData : [];
  const set = new Set();
  for (const r of src) {
    const code = getFormationCode(r);
    if (code) set.add(code);
  }
  return ["ALL", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
}, [propsData]);

const allL5Shapes = useMemo(() => {
  const src = Array.isArray(propsData) ? propsData : [];
  const set = new Set();

  for (const r of src) {
    const key = getL5CanonicalKey(r);
    if (key) set.add(key);
  }

  const ordered = L5_DISPLAY_ORDER.filter((key) => set.has(key));
  const extras = Array.from(set)
    .filter((key) => !L5_DISPLAY_ORDER.includes(key))
    .sort((a, b) => a.localeCompare(b));

  return ["ALL", ...ordered, ...extras];
}, [propsData]);


const togglePattern = (pattern) => {
  const canonical = getPatternCanonicalKey(pattern);
  if (!canonical) return;
  setSelectedPatterns((prev) =>
    prev.includes(canonical)
      ? prev.filter((x) => x !== canonical)
      : [...prev, canonical]
  );
};

const resetPatterns = () => setSelectedPatterns([]);

const togglePatternComponent = (component) => {
  setSelectedPatternComponents((prev) => prev.includes(component) ? prev.filter((key) => key !== component) : [...prev, component]);
};

const resetPatternIntelligence = () => {
  setSelectedPatternComponents([]);
  setSelectedPatterns([]);
  setPatternMatchMode("ALL");
};

const toggleBounceLabel = (label) => {
  const key = String(label || "").trim();
  if (!key) return;
  setSelectedBounceLabels((prev) =>
    prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
  );
};

const toggleBounceFamily = (family) => {
  const key = String(family || "").trim().toUpperCase();
  if (!key) return;
  setSelectedBounceFamilies((prev) =>
    prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
  );
};

const resetBounceFilters = () => {
  setSelectedBounceLabels([]);
  setSelectedBounceFamilies([]);
};

const allBounceLabels = useMemo(() => {
  const found = new Set(
    (Array.isArray(propsData) ? propsData : [])
      .map(getBounceLabel)
      .filter(Boolean)
  );
  const ordered = BOUNCE_LABEL_DISPLAY_ORDER.filter((label) => found.has(label));
  const extras = Array.from(found)
    .filter((label) => !BOUNCE_LABEL_DISPLAY_ORDER.includes(label))
    .sort((a, b) => a.localeCompare(b));
  return [...ordered, ...extras];
}, [propsData]);

const allBounceFamilies = useMemo(() => {
  const found = new Set(
    (Array.isArray(propsData) ? propsData : [])
      .map(getBounceFamily)
      .filter(Boolean)
  );
  const ordered = BOUNCE_FAMILY_DISPLAY_ORDER.filter((family) => found.has(family));
  const extras = Array.from(found)
    .filter((family) => !BOUNCE_FAMILY_DISPLAY_ORDER.includes(family))
    .sort((a, b) => a.localeCompare(b));
  return [...ordered, ...extras];
}, [propsData]);

const MINUTES_TREND_DISPLAY_ORDER = [
  "RISING",
  "STABLE",
  "FALLING",
  "VOLATILE",
  "LOW",
  "SURGING",
  "SOFT",
  "COLLAPSING",
];

const MINUTES_TREND_LABELS = {
  SURGING: "Minutes Surging",
  RISING: "Minutes Rising",
  STABLE: "Minutes Stable",
  SOFT: "Minutes Soft",
  FALLING: "Minutes Falling",
  VOLATILE: "Minutes Volatile",
  LOW: "Low Minutes",
  COLLAPSING: "Minutes Collapsing",
};

const getMinutesTrendCanonicalKey = (valueOrRow) => {
  const supportedKey = minutesTrendKey(valueOrRow);
  if (supportedKey) return supportedKey;
  const isRow = valueOrRow && typeof valueOrRow === "object";
  const rawValue = isRow
    ? (
        valueOrRow?.minutes_trend ??
        valueOrRow?.Minutes_Trend ??
        valueOrRow?.["Minutes_Trend"] ??
        valueOrRow?.["Minutes Trend"] ??
        valueOrRow?.minutesTrend ??
        valueOrRow?.MinutesTrend ??
        ""
      )
    : valueOrRow;

  const raw = String(rawValue ?? "").trim().toUpperCase();
  if (!raw || raw === "NAN" || raw === "NULL" || raw === "NONE") {
    return isRow && String(valueOrRow?.Sport || "").toUpperCase() === "SOCCER"
      ? soccerMinutesTrendKey(valueOrRow)
      : "";
  }

  if (raw.includes("SURGING")) return "SURGING";
  if (raw.includes("RISING")) return "RISING";
  if (raw.includes("STABLE")) return "STABLE";
  if (raw.includes("COLLAPS")) return "COLLAPSING";
  if (raw.includes("FALLING")) return "FALLING";
  if (raw.includes("SOFT")) return "SOFT";
  if (raw.includes("VOLATILE")) return "VOLATILE";
  if (raw.includes("LOW")) return "LOW";

  // If a numeric min_trend accidentally comes through here, bucket it safely.
  const n = Number(rawValue);
  if (Number.isFinite(n)) {
    if (n >= 4) return "SURGING";
    if (n >= 1.5) return "RISING";
    if (n >= -1.5) return "STABLE";
    if (n <= -4) return "COLLAPSING";
    if (n <= -1.5) return "FALLING";
    return "SOFT";
  }

  return raw;
};

const toggleMinutesTrend = (trend) => {
  const canonical = getMinutesTrendCanonicalKey(trend);
  if (!canonical) return;
  setSelectedMinutesTrends((prev) =>
    prev.includes(canonical)
      ? prev.filter((x) => x !== canonical)
      : [...prev, canonical]
  );
};

const resetMinutesTrends = () => setSelectedMinutesTrends([]);

const allMinutesTrends = useMemo(() => {
  const src = Array.isArray(propsData) ? propsData : [];
  const set = new Set();
  for (const r of src) {
    const key = getMinutesTrendCanonicalKey(r);
    if (key) set.add(key);
  }
  const ordered = MINUTES_TREND_DISPLAY_ORDER.filter((key) => set.has(key));
  const extras = Array.from(set)
    .filter((key) => !MINUTES_TREND_DISPLAY_ORDER.includes(key))
    .sort((a, b) => a.localeCompare(b));
  return ["ALL", ...ordered, ...extras];
}, [propsData]);

const allPatterns = useMemo(() => {
  const src = Array.isArray(propsData) ? propsData : [];
  const set = new Set();
  for (const r of src) {
    const key = getPatternCanonicalKey(r);
    if (key && key !== "NEUTRAL") set.add(key);
  }
  const ordered = PATTERN_DISPLAY_ORDER.filter((key) => set.has(key));
  const extras = Array.from(set)
    .filter((key) => !PATTERN_DISPLAY_ORDER.includes(key))
    .sort((a, b) => a.localeCompare(b));
  return ["ALL", ...ordered, ...extras];
}, [propsData]);

const exactPatternOptions = useMemo(() => Array.from(new Set((Array.isArray(propsData) ? propsData : [])
  .map(canonicalExactPattern).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [propsData]);
const patternComponentGroups = useMemo(() => availablePatternComponents(propsData), [propsData]);
const renderPatternIntelligencePanel = () => (
  <PatternIntelligencePanel
    groups={patternComponentGroups}
    selectedComponents={selectedPatternComponents}
    onToggleComponent={togglePatternComponent}
    matchMode={patternMatchMode}
    onMatchModeChange={setPatternMatchMode}
    exactPatterns={exactPatternOptions}
    selectedExactPatterns={selectedPatterns}
    onToggleExactPattern={(pattern) => setSelectedPatterns((prev) => prev.includes(pattern) ? prev.filter((value) => value !== pattern) : [...prev, pattern])}
    onClear={resetPatternIntelligence}
    darkMode={darkMode}
  />
);

const [quickFactsOpen, setQuickFactsOpen] = useState(false);

// put the effect RIGHT AFTER that (still inside PropsDashboard component)
useEffect(() => {
  if (gridDetailsOpen) setQuickFactsOpen(false);
}, [
  gridDetailsOpen,
  gridDetailsRow?.Player,
  gridDetailsRow?.["Prop Type"],
  gridDetailsRow?.["Prop Value"],
]);


const [activePreset, setActivePreset] = useState(null);

const confidenceRank = (label) => {
  const key = String(label || "").trim().toUpperCase();
  if (key.includes("VOLATILE") || key === "ELITE") return 4;
  if (key === "HIGH" || key === "STRONG") return 3;
  if (key === "MED" || key === "MEDIUM" || key === "MODERATE") return 2;
  if (key === "LOW") return 1;
  return 0;
};

const rowConfidenceRank = (row) => {
  const label = getDisplayConfidenceLabel(
    row?.Confidence ??
      row?.confidence ??
      row?.Guru_Pick_Confidence ??
      row?.guru_pick_confidence ??
      row?.PWG_Prob_Tier ??
      row?.pwg_prob_tier,
    activeSport
  );
  return confidenceRank(label);
};

const isGuruEliteUnderRow = (row) => {
  const direction = normalizeGuruPick(row);
  if (direction !== "UNDER") return false;

  const tag = String(row?.Tag ?? row?.tag ?? "").toUpperCase();
  if (!(tag.includes("FADE") || tag.includes("UNDER"))) return false;

  // Guru Elite Under starts with Strong+ confidence.
  // The exact pattern/minutes combos below came from the WNBA research audit.
  if (rowConfidenceRank(row) < 3) return false;

  const pattern = getPatternCanonicalKey(row);
  const minutes = getMinutesTrendCanonicalKey(row);

  if (pattern === "FAILED_BOUNCE") {
    return minutes === "FALLING" || minutes === "COLLAPSING";
  }

  if (pattern === "RECOVERY_RECLAIM") {
    return minutes === "FALLING" || minutes === "COLLAPSING";
  }

  if (pattern === "DOWNTREND" || pattern === "FADING") {
    return minutes === "STABLE" || minutes === "FALLING" || minutes === "COLLAPSING";
  }

  return false;
};

const getRowTagCanonical = (row) =>
  String(
    row?.Tag ??
      row?.tag ??
      row?.Display_Tag ??
      row?.Final_Tag ??
      row?.Base_Tag ??
      ""
  )
    .trim()
    .toUpperCase();

const getLineTaxCanonical = (row) =>
  String(
    row?.Line_Tax_Tier ??
      row?.line_tax_tier ??
      row?.["Line Tax Tier"] ??
      row?.LineTaxTier ??
      ""
  )
    .trim()
    .toUpperCase();

const getL5FormCodeCanonical = (row) =>
  String(
    row?.L5_FormCode ??
      row?.l5_form_code ??
      row?.Formation_Code ??
      row?.formation_code ??
      row?.Form_Code ??
      row?.form_code ??
      ""
  )
    .trim()
    .toUpperCase();

const isGuruEliteOverRow = (row) => {
  // WNBA-only certified OVER v1. Do not use this for NBA/MLB yet.
  if (String(activeSport || row?.Sport || row?.sport || "").toUpperCase() !== "WNBA") return false;

  const direction = normalizeGuruPick(row);
  if (direction !== "OVER") return false;

  const pattern = getPatternCanonicalKey(row);
  const minutes = getMinutesTrendCanonicalKey(row);
  const lineTax = getLineTaxCanonical(row);
  const tag = getRowTagCanonical(row);
  const formCode = getL5FormCodeCanonical(row);

  // Certified OVER #1:
  // Fading + Stable minutes + Discount line + Lean Over.
  // Formation is intentionally not required yet because historical wins were spread
  // across multiple low-sample formations.
  if (
    (pattern === "DOWNTREND" || pattern === "FADING") &&
    minutes === "STABLE" &&
    lineTax === "DISCOUNT" &&
    tag === "LEAN OVER"
  ) {
    return true;
  }

  // Certified OVER #2:
  // Rising + Stable minutes + Discount line + Smash/Mega Smash + OOOOO formation.
  if (
    (pattern === "UPTREND" || pattern === "RISING") &&
    minutes === "STABLE" &&
    lineTax === "DISCOUNT" &&
    (tag === "SMASH" || tag === "MEGA SMASH") &&
    formCode === "OOOOO"
  ) {
    return true;
  }

  return false;
};

function applyPreset(preset) {
  setActivePreset(preset);

  const isMLB = activeSport === "MLB";

  //
  // 1) UNIVERSAL RESET
  //
  const resetAll = () => {
    setSelectedTags([]);
    setPropFilter("");
    setConfidenceFilter("");
    setSelectedConfidence("");
    setTaxFilter("ALL");
    setBlowoutFilter("all");
    setMinMpgFilter("0");
    setSelectedGames([]);
    setSelectedTeams([]);
    setHomeAwayFilter("");
    setSelectedTime("");
    setMomentumFilter("");
    setMomentumPatternFilter("");
    setConfirmedMomentumFilter("");
    setSelectedGuruMagic("");
    setSelectedGuruPotential("");
    setSelectedZGuruTag("");
    setSelectedLeanDirection("");
    setSelectedGuruConflict("");
    setSelectedGuruPicks("");
    setGuruListFilter("all");
    setGuruAlert("ALL");
    setDvpTierFilter("ALL");
    setStocksHeatOnly(false);
    setPlayerQuery("");
    setPlayerInput("");
    setMinutesTrendMin(null);
    setMinutesTrendMax(null);
    setOversMinutesGate(false);
    setSelectedMinutesTrends([]);
    setSelectedPatterns([]);
    setSelectedL5Shapes([]);
    setSelectedFormationFamilies([]);
    setSelectedFormationCodes([]);
    setSelectedPropTypes([]);
    setSelectedPropType("");
    setSelectedRisk([]);
    setSelectedIsGuruPicks("");
    setSelectedGuruGrade("");
    setZodiacFilter("ALL");

    // taxonomy
    setTaxonomyScope("ALL");
    setTaxonomyTier("ALL");
    setTaxonomyPlayer("");

    // MLB
    setPlayerTypeFilter("");
    setOppPitcherHandFilter("");
    setBattingOrderFilter("");
    setTeamPitcherTierFilter("");
    setTeamAttackabilityFilter("");
    setTeamWhipTierFilter("");
    setTeamKpctTierFilter("");
    setOppWhipTierFilter([]);
    setOppKpctTierFilter([]);
    setOppAttackabilityFilter("");
    setOppPitcherTierFilter("");
    setOppHitterTierFilter([]);
    setHitterTierFilter([]);
  };

  switch (preset) {
    //
    // 🏆 Guru Elite Under — learned WNBA flagship under archetypes
    //
    case "guru_elite_under": {
      resetAll();
      // Do NOT set selectedTags / selectedGuruPicks / selectedConfidence here.
      // The activePreset gate below calls isGuruEliteUnderRow(p), which applies
      // the certified archetype logic without lighting up raw Tag filters.
      return;
    }

    //
    // 🚀 Certified Over (WNBA) — WNBA-only certified over archetypes
    //
    case "guru_elite_over": {
      resetAll();
      // Same rule: preset is a certification, not a visible stack of raw filters.
      return;
    }

    //
    // 🔥 Fade Elite Defense
    //
    case "fade_elite": {
      resetAll();
    
      if (activeSport === "MLB") {
        setSelectedTags(["LEAN UNDER", "FADE", "MEGA FADE"]);
        setOppPitcherTierFilter(["Tough", "Elite"]);
        return;
      }
    
      setSelectedTags(["LEAN UNDER", "FADE", "MEGA FADE"]);
      setDvpTierFilter("ELITE");
      return;
    }

    //
    // 💚 Smash Soft Matchups
    //
    case "smash_soft": {
      resetAll();
    
      if (isMLB) {
        setSelectedTags(["SMASH", "MEGA SMASH"]);
        setOppPitcherTierFilter(["Weak (Target)", "Poor"]);
        setOppAttackabilityFilter(["Very High (Target)", "High (Target)"]);
        return;
      }
    
      setSelectedTags(["SMASH", "MEGA SMASH"]);
      setDvpTierFilter("SOFT");
      return;
    }

    //
    // ⚖️ Tax-Adjusted Unders
    //
    case "tax_unders": {
      resetAll();

      if (isMLB) {
        setSelectedTags(["FADE", "MEGA FADE"]);
        setTaxFilter("HYPER-TAXED");
        // under-friendly pitcher environment
        setOppPitcherTierFilter(["Tough", "Elite"]);
        return;
      }

      setSelectedTags(["FADE", "MEGA FADE"]);
      setTaxFilter("HYPER-TAXED");
      setDvpTierFilter("TOUGH");
      return;
    }

    //
    // 🧠 Neutral Overs
    //
    case "neutral_overs": {
      resetAll();
    
      if (isMLB) {
        setSelectedTags(["LEAN OVER", "SMASH", "MEGA SMASH"]);
        setOppPitcherTierFilter(["Neutral", "Poor"]);
        setOppAttackabilityFilter(["Medium Risk", "High (Target)"]);
        return;
      }
    
      setSelectedTags(["LEAN OVER", "SMASH", "MEGA SMASH"]);
      setDvpTierFilter("NEUTRAL");
      setOversMinutesGate(true);
      return;
    }

    //
    // 🧠 Neutral Unders
    //
    case "neutral_unders": {
      resetAll();

      if (isMLB) {
        setSelectedTags(["LEAN UNDER", "FADE", "MEGA FADE"]);
        setOppPitcherTierFilter("Neutral");
        setOppAttackabilityFilter("Medium Risk");
        return;
      }

      setSelectedTags(["LEAN UNDER", "FADE", "MEGA FADE"]);
      setDvpTierFilter("NEUTRAL");
      setOversMinutesGate(false);
      return;
    }

    //
    // 🎯 Target Fade  (advanced)
    //
    case "target_fade": {
      resetAll();

      if (isMLB) {
        // stronger under environment than fade_elite, but tied to true pitcher hostility
        setSelectedTags(["FADE", "MEGA FADE"]);
        setOppPitcherTierFilter("Elite");
        setOppKpctTierFilter(["Whiff Machine"]);
        setOppWhipTierFilter(["Great"]);
        return;
      }

      setSelectedTags(["FADE", "MEGA FADE"]);
      setDvpTierFilter("TARGET");
      return;
    }

    //
    // 🟠 Target Lean Under  (advanced)
    //
    case "target_lean_under": {
      resetAll();

      if (isMLB) {
        setSelectedTags(["LEAN UNDER"]);
        setOppPitcherTierFilter("Tough");
        setOppKpctTierFilter(["High K"]);
        return;
      }

      setSelectedTags(["LEAN UNDER"]);
      setDvpTierFilter("TARGET");
      return;
    }

    //
    // 💚 Elite Lean Over  (advanced / contrarian)
    //
    case "elite_lean_over": {
      resetAll();

      if (isMLB) {
        // contrarian hitter overs against neutral-to-respectable arms
        // but not fully avoid-tier
        setSelectedTags(["LEAN OVER"]);
        setOppPitcherTierFilter("Neutral");
        setOppWhipTierFilter(["Average"]);
        return;
      }

      setSelectedTags(["LEAN OVER"]);
      setDvpTierFilter("ELITE");
      return;
    }

    default:
      return;
  }
}


function computeDirectionSummary(rows) {
  if (!rows || rows.length === 0) return null;

  const total = rows.length;
  const count = (bucket) => rows.filter(r => r.DirectionBucket === bucket).length;

  return {
    pctSmashOvers:  count("SMASH OVERS") / total,
    pctLeanOvers:   count("LEAN OVERS") / total,
    pctCoinToss:    count("COIN TOSS") / total,
    pctLeanUnders:  count("LEAN UNDERS") / total,
    pctFades:       count("FADES / UNDERS") / total,
  };
}

const buildExposure = (lineups = []) => {
  const total = lineups.length || 0;
  const byPlayer = new Map();
  const byTeam = new Map();

  if (!total) return { total, players: [], teams: [] };

  lineups.forEach((lu) => {
    const seenPlayers = new Set(); // ensure per-lineup uniqueness
    const seenTeams = new Set();

    (lu || []).forEach((p) => {
      const player = p?.Player || "";
      const team = p?.Team || p?.["Team Name"] || "";

      if (player && !seenPlayers.has(player)) {
        seenPlayers.add(player);
        byPlayer.set(player, (byPlayer.get(player) || 0) + 1);
      }
      if (team && !seenTeams.has(team)) {
        seenTeams.add(team);
        byTeam.set(team, (byTeam.get(team) || 0) + 1);
      }
    });
  });

  const players = Array.from(byPlayer.entries())
    .map(([name, count]) => ({
      name,
      count,
      pct: Math.round((count / total) * 100),
    }))
    .sort((a, b) => b.count - a.count);

  const teams = Array.from(byTeam.entries())
    .map(([name, count]) => ({
      name,
      count,
      pct: Math.round((count / total) * 100),
    }))
    .sort((a, b) => b.count - a.count);

  return { total, players, teams };
};





useEffect(() => {
  if (minMpgFilter) {
    setMinMpgFilter(String(minMpgFilter));
  }
}, [minMpgFilter, setMinMpgFilter]);


  // Are we currently in the lineup experience?

  //help with closing mobile Filters display
  const withCloseFilters = (fn) => () => {
    if (filtersOpen) setFiltersOpen(false);
    fn?.();
  };



function pctForFilter(card) {
  const tag = card.Tag ?? card.tag ?? "";
  const { hit, miss, neutral } = getL10Counts(card);
  const denom = hit + miss + neutral;
  if (denom <= 0) return null; // no basis
  const hitRate  = (hit  / denom) * 100;
  const missRate = (miss / denom) * 100;
  if (isOverTag(tag))  return Math.round(hitRate);   // OVER: use hit%
  if (isUnderTag(tag)) return Math.round(missRate);  // UNDER: use miss%
  return Math.round(hitRate);                        // default
}
// ---- end canonical helpers ----
function pctColor(pct) {
  if (!Number.isFinite(pct)) return "bg-gray-300 text-gray-800";
  if (pct >= 80) return "bg-green-600 text-white";
  if (pct >= 70) return "bg-amber-500 text-white";
  if (pct >= 60) return "bg-sky-600 text-white";
  return "bg-gray-300 text-gray-800";
}

function HitRatePill({ hit = 0, miss = 0, neutral = 0 }) {
  const total = hit + miss + neutral;
  if (!total) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-[2px] text-xs font-semibold bg-gray-300 text-gray-800"
        title="Last-10 vs PROP: no comparable games"
      >
        N/A
      </span>
    );
  }
  const pct = Math.round((hit / total) * 100);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-[2px] text-xs font-semibold ${pctColor(pct)}`}
      title={`Last 10 vs PROP: ${hit}-${miss}-${neutral} (${pct}%)`}
    >
      {pct}%
    </span>
  );
}




  //////////////// end filter by hit rate /////////////////////


  const highlightsReady = useMemo(() => shouldDismissSportsSplash({
    loading: propsLoading,
    loadedSport: loadedPropsSport,
    activeSport,
  }), [propsLoading, loadedPropsSport, activeSport]);

  useEffect(() => {
    if (!highlightsReady) return;
    let cancelled = false;
    let secondFrame;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (!cancelled) setShowSplash(false);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [highlightsReady]);


  /////////////////////////////

  // --- TAGS
  const TAGS = ["MEGA SMASH","SMASH","LEAN OVER","MEGA FADE","FADE","LEAN UNDER","COIN TOSS"];
  const TEG_TAGS = ["BULLISH","BEARISH","FLAT"];
  //const TEG_TAGS = [""];

  // Use TEG-specific tags when TEG is the active sport
  const CURRENT_TAGS = useMemo(
    () => (activeSport === "TEG" ? TEG_TAGS : TAGS),
    [activeSport]
  );

  // If user switches sports, drop any tags that don't exist in the new list
  useEffect(() => {
    const allowed = new Set(CURRENT_TAGS.map(String));
    setSelectedTags(prev => prev.filter(t => allowed.has(String(t))));
  }, [CURRENT_TAGS]);


  const [showAccount, setShowAccount] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [dailyBriefOpen, setDailyBriefOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);


  ////////
  //const [activeSport, setActiveSport] = useState("MLB");

  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedLineTypes, setSelectedLineTypes] = useState(() => defaultLineTypesForSport(initialSport));
  React.useEffect(() => {
    gridDetailsLoaderRef.current.reset();
  }, [secureMlbVersion, selectedGames, selectedLineTypes]);
  const [selectedDefenseTiers, setSelectedDefenseTiers] = useState([]);
  const [selectedRisk, setSelectedRisk] = useState([]);

  const entitlementVisibleProps = useMemo(
    () => (Array.isArray(propsData) ? propsData.filter((row) => canViewPwgRow(row, pwgGuruAccess) && (isAdminIdentity || visibleForCustomerAnalysis(row, activeSport))) : []),
    [propsData, pwgGuruAccess, isAdminIdentity, activeSport]
  );
  const availableLineTypes = useMemo(() => {
    if (String(activeSport || "").toUpperCase() === "MLB" && secureSportsDataEnabledFor("MLB") && secureMlbGames.length) {
      return ["regular", "goblin", "demon"].filter((type) => secureMlbGames.some((game) => Number(game?.line_type_counts?.[type] || 0) > 0));
    }
    return supportedLineTypes(entitlementVisibleProps);
  }, [activeSport, entitlementVisibleProps, secureMlbGames]);
  const lineTypeVisibleProps = useMemo(
    () => filterRowsByLineTypes(entitlementVisibleProps, selectedLineTypes),
    [entitlementVisibleProps, selectedLineTypes]
  );
  const directionMetaByRow = useMemo(() => new Map(
    lineTypeVisibleProps.map((row) => [row, directionPresentation(row, activeSport)])
  ), [lineTypeVisibleProps, activeSport]);
  const gameCountRows = useMemo(() => {
    const propTypes = new Set(selectedPropTypes);
    const tags = new Set(selectedTags.map((value) => String(value || "").toUpperCase()));
    const searchText = String(playerInput || playerQuery || "").trim().toLowerCase();
    return lineTypeVisibleProps.filter((row) => {
      if (selectedDirection !== "ALL" && directionMetaByRow.get(row)?.direction !== selectedDirection) return false;
      const propType = dashboardCanonicalPropType(row?.["Prop Type"] ?? row?.PropType ?? row?.Prop ?? "");
      if (propTypes.size ? !propTypes.has(propType) : (selectedPropType && propType !== dashboardCanonicalPropType(selectedPropType))) return false;
      if (tags.size && !tags.has(String(row?.Tag || "").toUpperCase())) return false;
      if (searchText) {
        const searchable = [row?.Player, row?.Name, row?.Team, row?.Opponent, row?.Prop, row?.["Prop Type"]]
          .map((value) => String(value ?? "").toLowerCase()).join(" ");
        if (!searchable.includes(searchText)) return false;
      }
      return true;
    });
  }, [lineTypeVisibleProps, selectedPropTypes, selectedPropType, selectedTags, playerInput, playerQuery, selectedDirection, directionMetaByRow]);
  const customerGameCounts = useMemo(() => projectionCountsByGame(gameCountRows, activeSport), [gameCountRows, activeSport]);
  const genericGameOptions = useMemo(
    () => buildGenericGameOptions(gameCountRows, activeSport),
    [gameCountRows, activeSport]
  );
  // The Soccer sidebar must describe the same rows the customer can render.
  // In particular, do not advertise an alternate-only fixture while the
  // PRIMARY-first line-type view has no corresponding cards. No date/start-time
  // condition belongs here: future supported rows remain eligible.
  const soccerGameRows = useMemo(() => {
    if (String(activeSport || "").toUpperCase() !== "SOCCER") return [];
    return lineTypeVisibleProps;
  }, [activeSport, lineTypeVisibleProps]);
  const mlbGameRows = useMemo(() => {
    if (String(activeSport || "").toUpperCase() !== "MLB") return [];
    if (secureSportsDataEnabledFor("MLB") && secureMlbGames.length) {
      return authoritativeMlbGameRows(secureMlbGames, selectedMlbLineType(selectedLineTypes));
    }
    return lineTypeVisibleProps;
  }, [activeSport, lineTypeVisibleProps, secureMlbGames, selectedLineTypes]);
  const mlbGameGroups = useMemo(
    () => buildMLBGameGroups(mlbGameRows),
    [mlbGameRows]
  );
  useEffect(() => {
    if (String(activeSport || "").toUpperCase() !== "MLB") return;
    const available = new Set(mlbGameGroups.flatMap((group) => group.matchups.flatMap((matchup) => matchup.games.filter((game) => game.projectionCount > 0).map((game) => game.key))));
    setSelectedGames((current) => {
      const valid = current.filter((key) => available.has(key));
      return valid.length === current.length ? current : valid;
    });
  }, [activeSport, mlbGameGroups]);
  const soccerGameGroups = useMemo(
    () => buildSoccerFixtureGroups(soccerGameRows),
    [soccerGameRows]
  );
  useEffect(() => {
    if (String(activeSport || "").toUpperCase() !== "SOCCER") return;
    const available = new Set(soccerGameGroups.flatMap((group) => group.games.map((game) => game.key)));
    setSelectedGames((current) => {
      const valid = current.filter((key) => available.has(key));
      return valid.length === current.length ? current : valid;
    });
  }, [activeSport, soccerGameGroups]);
  const availableDefenseTiers = useMemo(() => {
    if (!["NFL", "CFB"].includes(String(activeSport || "").toUpperCase())) return [];
    const present = new Set((Array.isArray(propsData) ? propsData : []).map((row) => row["Opponent Defense Tier"]).filter(Boolean));
    return ["ELITE", "STRONG", "AVERAGE", "WEAK", "VERY WEAK"].filter((tier) => present.has(tier));
  }, [propsData, activeSport]);
  useEffect(() => {
    setSelectedLineTypes(String(activeSport || "").toUpperCase() === "NHL" ? ["all"] : lineTypesAfterSportChange(activeSport));
  }, [activeSport]);

  useEffect(() => {
    if (String(activeSport || "").toUpperCase() !== "MLB" || !secureSportsDataEnabledFor("MLB") || !secureMlbVersion) return undefined;
    const lineType = selectedMlbLineType(selectedLineTypes);
    const gameIds = selectedMlbGameIds(selectedGames);
    const requestKey = `${secureMlbVersion}|${lineType}|${gameIds.join(",")}`;
    if (secureMlbLoadedKeyRef.current === requestKey) return undefined;
    secureMlbLoadedKeyRef.current = requestKey;
    const generation = secureMlbBoardRequestRef.current + 1;
    secureMlbBoardRequestRef.current = generation;
    const isCurrent = () => secureMlbBoardRequestRef.current === generation;
    setPropsLoading(true);
    setPropsDataError("");
    setSecureMlbProgress({ loaded: 0, total: null });
    loadMlbBoard({
      versionId: secureMlbVersion,
      lineType,
      gameIds,
      isCurrent,
      onProgress: (progress) => {
        if (isCurrent()) setSecureMlbProgress(progress);
      },
      onFirstPage: (payload) => {
        if (!isCurrent()) return;
        const rows = Array.isArray(payload?.props) ? payload.props : [];
        secureMlbRenderedRowsRef.current = rows;
        setSecureMlbGames(Array.isArray(payload?.games) ? payload.games : []);
        setLocalMlbCandidate(payload?.localCandidate || null);
        setPropsData(rows);
        setLoadedPropsSport("MLB");
        setPropsCache((current) => ({ ...current, MLB: rows }));
        setPropsLoading(false);
      },
      onPage: (payload) => {
        if (!isCurrent()) return;
        const rows = Array.isArray(payload?.props) ? payload.props : [];
        secureMlbRenderedRowsRef.current = rows;
        setPropsData(rows);
        setLoadedPropsSport("MLB");
        setPropsCache((current) => ({ ...current, MLB: rows }));
        setSecureMlbProgress({ loaded: rows.length, total: payload?.page?.total ?? null });
      },
    }).then((payload) => {
      if (!isCurrent() || payload?.page?.cancelled) return;
      const rows = Array.isArray(payload?.props) ? payload.props : [];
      secureMlbRenderedRowsRef.current = rows;
      setSecureMlbGames(Array.isArray(payload?.games) ? payload.games : []);
      setLocalMlbCandidate(payload?.localCandidate || null);
      setPropsData(rows);
      setLoadedPropsSport("MLB");
      setPropsCache((current) => ({ ...current, MLB: rows }));
      setSecureMlbProgress({
        loaded: rows.length,
        total: payload?.page?.total ?? rows.length,
        error: String(payload?.page?.partial_error || ""),
      });
    }).catch((error) => {
      if (!isCurrent()) return;
      console.warn("[secure sports] MLB board unavailable", error?.status || error?.message);
      if (secureMlbRenderedRowsRef.current.length) {
        setSecureMlbProgress((current) => ({
          ...(current || {}),
          error: "One selected MLB game could not be loaded. Previously validated games remain available.",
        }));
      } else {
        setPropsDataError("Sports data temporarily unavailable.");
      }
    }).finally(() => {
      if (isCurrent()) setPropsLoading(false);
    });
    return () => {
      if (secureMlbBoardRequestRef.current === generation) secureMlbBoardRequestRef.current += 1;
    };
  }, [activeSport, secureMlbVersion, selectedGames, selectedLineTypes]);

  const toggleRisk = (risk) => {
    setSelectedRisk((prev) => {
      if (prev.includes(risk)) {
        return prev.filter((r) => r !== risk);
      } else {
        return [...prev, risk];
      }
    });
  };

  const [gameTimes, setGameTimes] = useState({});


  const [darkMode, setDarkMode] = useState(false);
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [darkMode]);

  const gamesDropdownRef = useRef(null);
  
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        gamesDropdownRef.current &&
        !gamesDropdownRef.current.contains(event.target)
      ) {
        setGameMenuOpen(false);
      }
    }
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


// pw:scroll-lock-guard
// Fix mobile "frozen / can’t scroll" states by ONLY locking scroll when a true overlay is open
// (mobile taxonomy drawer, modals, etc). Also aggressively clears common lock styles on unlock.
useEffect(() => {
  const anyOverlayOpen =
    (isNarrow && !!taxonomyOpen) ||
    !!edgeAnalyzerOpen ||
    !!gridDetailsOpen ||
    !!gridCompareOpen ||
    !!dailyBriefOpen ||
    !!showHelp ||
    !!showCommentary;

  // Save prior inline styles (so we can restore whatever was there before)
  const prev = {
    bodyOverflow: document.body.style.overflow,
    htmlOverflow: document.documentElement.style.overflow,
    bodyPos: document.body.style.position,
    bodyTop: document.body.style.top,
    bodyWidth: document.body.style.width,
    bodyTouch: document.body.style.touchAction,
  };

  // Capture scroll position if we ever lock via position:fixed
  const scrollY = window.scrollY || window.pageYOffset || 0;

  const lock = () => {
    // Background lock (keep it simple and reliable)
    document.body.style.overflow = "hidden";
    // Do NOT force html overflow hidden (this can cause weirdness in mobile emulation / iOS)
    // document.documentElement.style.overflow = "hidden";

    // Some browsers still allow scroll unless body is fixed; optional, but safest:
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.touchAction = "none";
  };

  const unlock = () => {
    // Clear inline locks first (most reliable way to “unfreeze”)
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    document.body.style.touchAction = "";

    // Restore previous inline styles if they existed
    if (prev.bodyOverflow) document.body.style.overflow = prev.bodyOverflow;
    if (prev.htmlOverflow) document.documentElement.style.overflow = prev.htmlOverflow;
    if (prev.bodyPos) document.body.style.position = prev.bodyPos;
    if (prev.bodyTop) document.body.style.top = prev.bodyTop;
    if (prev.bodyWidth) document.body.style.width = prev.bodyWidth;
    if (prev.bodyTouch) document.body.style.touchAction = prev.bodyTouch;

    // If we used position:fixed, restore scroll
    if (!anyOverlayOpen) {
      const y = Math.abs(parseInt(prev.bodyTop || `-${scrollY}`, 10)) || scrollY;
      window.scrollTo(0, y);
    }
  };

  if (anyOverlayOpen) lock();
  else unlock();

  // Safety: devtools/mobile emulation can “stick” overflow after viewport changes.
  const unlockIfNeeded = () => {
    if (!anyOverlayOpen) {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.touchAction = "";
    }
  };

  window.addEventListener("resize", unlockIfNeeded);
  window.addEventListener("orientationchange", unlockIfNeeded);

  return () => {
    window.removeEventListener("resize", unlockIfNeeded);
    window.removeEventListener("orientationchange", unlockIfNeeded);
    // Restore exactly what was there
    document.body.style.overflow = prev.bodyOverflow;
    document.documentElement.style.overflow = prev.htmlOverflow;
    document.body.style.position = prev.bodyPos;
    document.body.style.top = prev.bodyTop;
    document.body.style.width = prev.bodyWidth;
    document.body.style.touchAction = prev.bodyTouch;
  };
}, [taxonomyOpen, edgeAnalyzerOpen, gridDetailsOpen, gridCompareOpen, showHelp, dailyBriefOpen, showCommentary]);

  const multiSportMode = selectedSports.length > 1;
  const stocksData = Array.isArray(propsData) ? propsData : [];
  // --- TEG-only (stocks) filters ---
  

  // Build unique sectors from your stocks data (defensively)
// Build unique sectors from your TEG rows (defensive on keys)
  const uniqueSectors = useMemo(() => {
    const set = new Set();
    (Array.isArray(propsData) ? propsData : []).forEach((r) => {
      const sec = (
        r.Sector ??
        r.sector ??
        r.Industry ??
        r.Category ??
        r.SubSector ??
        ""
      ).toString().trim();
      if (sec) set.add(sec);
    });
    return [...set].sort();
  }, [propsData]);


  //////////////////////////////////////////////////////////////
  
//////////////////FILTERED PROPS////////////////////////////////////

    // 1) Fast path: don't filter everything until user expands "Show all props"
    const filteredProps = useMemo(() => {
      if (!propsData || !Array.isArray(propsData)) return [];
      if (String(loadedPropsSport || "").toUpperCase() !== String(activeSport || "").toUpperCase()) return [];
      const accessibleProps = activeSport === "TEG" ? entitlementVisibleProps : lineTypeVisibleProps;
      const directionVisibleProps = activeSport === "TEG" || selectedDirection === "ALL"
        ? accessibleProps
        : accessibleProps.filter((row) => directionMetaByRow.get(row)?.direction === selectedDirection);


        // --- TEG: simple search + sector filter ---
        // --- TEG: simple search + sector filter ---
        // --- TEG: simple search + sector filter ---
        if (activeSport === "TEG") {
          const q = (playerInput || playerQuery || "").trim().toLowerCase();

          const rawSector = (selectedSector || "").trim();
          const sectorSel =
            rawSector.toUpperCase() === "ALL" ? "" : rawSector.toLowerCase();

          return accessibleProps.filter((r) => {
            const textParts = [
              r.Symbol,
              r.ticker,
              r.Stock,
              r.Company,
              r.company,
              r.company_tech,
              r.Name,
            ].map((v) => String(v ?? "").toLowerCase());

            const text = textParts.join(" ").trim();

            const sector = String(
              r.Sector ?? r.sector ?? r.Industry ?? r.Category ?? r.SubSector ?? ""
            ).toLowerCase();

            const matchesQuery = !q || text.includes(q);
            const matchesSector =
              !sectorSel || sector === sectorSel || sector.includes(sectorSel);

            return matchesQuery && matchesSector;
          });
        }


      
      // If we're still in highlights mode, do NOT bail early — run Guru if toggled.
      // Keep the fast path for sports, but NOT for TEG (we want live filtering)
      if (!showAllProps && activeSport !== "TEG" && selectedLineTypes.length === 0 && selectedDefenseTiers.length === 0) {
        return guruMode ? applyGuru(directionVisibleProps, guruType) : directionVisibleProps;
      }

      // Precompute helpers used in the loop
      const wantTime  = selectedTime; // "All" | "Early" | ...
      const wantsTeam = Array.isArray(selectedTeams) && selectedTeams.length > 0;
      const wantsGame = Array.isArray(selectedGames) && selectedGames.length > 0;
      const wantsTags = selectedTags?.length > 0;
      

      // search text used for both sports & TEG
      const searchText = (playerInput || playerQuery || "").trim().toLowerCase();
      const wantsPlayer = !!searchText;

    
      // For multi-sport game keys: normalize once
      const mkGameKey = (p) =>
        multiSportMode
          ? `${[p.Team, p.Opponent].sort().join(' vs ')} (${p.Sport})`
          : `${[p.Team, p.Opponent].sort().join(' vs ')}`;
    
      const levels = ["Low", "Moderate", "Strong", "Elite", "Volatile Elite"];
    
      // 1) run all your existing filters (unchanged)
      let rows = directionVisibleProps.filter((p) => {
        // Hide bad rows (e.g., NAN vs NAN or missing names)
        {
          const tn = String(p['Team Name'] ?? p.Team ?? '').trim().toUpperCase();
          const on = String(p['Opponent Name'] ?? p.Opponent ?? '').trim().toUpperCase();
          if (!tn || !on || tn === 'NAN' || on === 'NAN') return false;
        }
    
        // --- PLAYER SEARCH FILTER (sports) ---
        if (wantsPlayer) {
          const textParts = [
            p.Player,
            p["Player"],
            p.Name,
            p["Name"],
            p.Team,
            p.Opponent,
            p.Prop,
            p["Prop Type"],
          ].map((v) => String(v ?? "").toLowerCase());

          const haystack = textParts.join(" ");
          if (!haystack.includes(searchText)) return false;
        }


        if (wantsTags) {
          const tagValue =
            activeSport === "TEG"
              ? (p.Tag ?? p.Signal ?? p.Direction ?? p.Trend ?? p["Tag"] ?? p["Signal"] ?? p["Direction"] ?? p["Trend"])
              : (p.Tag ?? p["Tag"]);
          if (!selectedTags.includes(String(tagValue || "").toUpperCase())) return false;
        }
        if (selectedDefenseTiers.length > 0 && !selectedDefenseTiers.includes(String(p["Opponent Defense Tier"] || ""))) return false;
        if (selectedGuruPotential && p["GuruPotential"] !== selectedGuruPotential) return false;
        // Guru Potential filter — supports "Guru Potential" and "GuruPotential"
        if (selectedGuruPotential && selectedGuruPotential !== "All Guru Potential") {
          const gpVal = String(p["Guru Potential"] ?? p["GuruPotential"] ?? "")
            .trim()
            .toUpperCase();           // normalize case
          const want = String(selectedGuruPotential).trim().toUpperCase();
          if (gpVal !== want) return false;
        }
        
        if (wantsGame && String(activeSport || "").toUpperCase() === "NFL") {
          const fixtureKey = canonicalNFLFixtureKey(p);
          if (!fixtureKey || !selectedGames.includes(fixtureKey)) return false;
        } else if (wantsGame && String(activeSport || "").toUpperCase() === "CFB") {
          const fixtureKey = canonicalCFBFixtureKey(p);
          if (!fixtureKey || !selectedGames.includes(fixtureKey)) return false;
        } else if (wantsGame && String(activeSport || "").toUpperCase() === "SOCCER") {
          const fixtureKey = soccerFixtureSelectionKey(p, soccerGameGroups);
          if (!fixtureKey || !selectedGames.includes(fixtureKey)) return false;
        } else if (wantsGame && String(activeSport || "").toUpperCase() === "MLB") {
          const fixtureKey = canonicalMLBGameKey(p);
          if (!fixtureKey || !selectedGames.includes(fixtureKey)) return false;
        } else if (wantsGame && String(activeSport || "").toUpperCase() === "WNBA") {
          const fixtureKey = canonicalTeamSportFixtureKey(p);
          if (!fixtureKey || !selectedGames.includes(fixtureKey)) return false;
        } else if (wantsGame) {
          const fixtureKey = canonicalGameSelectionKey(p, activeSport);
          if (!fixtureKey || !selectedGames.includes(fixtureKey)) return false;
        }
        
    
        // Time bucket filter
        if (wantTime && wantTime !== "All") {
          const timeStr = p["GameTime"];
          if (!timeStr) return false;
          const [time, meridian] = timeStr.split(" ");
          if (!time || !meridian) return false;
          let [hour, minute] = time.split(":").map(Number);
          if (Number.isNaN(hour)) return false;
          if (meridian === "PM" && hour !== 12) hour += 12;
          if (meridian === "AM" && hour === 12) hour = 0;
    
          const isEarly = hour < 15;
          const isAfternoon = hour >= 15 && hour < 17;
          const isEvening = hour >= 17 && hour < 20;
          const isLate = hour >= 20;
    
          if (
            (wantTime === "Early" && !isEarly) ||
            (wantTime === "Afternoon" && !isAfternoon) ||
            (wantTime === "Evening" && !isEvening) ||
            (wantTime === "Late" && !isLate)
          ) return false;
        }
    
        if ((Array.isArray(selectedPropTypes) && selectedPropTypes.length > 0)) {
          const pt = dashboardCanonicalPropType(p["Prop Type"] ?? p.PropType ?? p.Prop ?? "");
          if (!selectedPropTypes.includes(pt)) return false;
        }
        if ((selectedPropTypes?.length || 0) === 0 && selectedPropType) {
          const pt = dashboardCanonicalPropType(p["Prop Type"] ?? p.PropType ?? p.Prop ?? "");
          if (pt !== dashboardCanonicalPropType(selectedPropType)) return false;
        }
        if (selectedZGuruTag && p["ZGuruTag"] !== selectedZGuruTag) return false;
        if (selectedLeanDirection && p["LeanDirection"] !== selectedLeanDirection) return false;
        if (selectedGuruConflict && p["GuruConflict"] !== selectedGuruConflict) return false;
        if (selectedIsGuruPicks !== "" && p["IsGuruPick"] !== selectedIsGuruPicks) return false;
        if (selectedGuruPicks && normalizeGuruPick(p) !== selectedGuruPicks) return false;
        if (selectedGuruMagic && p["GuruMagic"] !== selectedGuruMagic) return false;

        // Preset archetype gates. These are intentionally stricter than regular filters.
        // They let Guru Elite presets pull only the learned combinations instead of a broad pool.
        if (activePreset === "guru_elite_under" && !isGuruEliteUnderRow(p)) return false;
        if (activePreset === "guru_elite_over" && !isGuruEliteOverRow(p)) return false;
    
        // Confidence logic (supports "Over X" or exact label)
        // Confidence logic (supports "Over X" or exact label)
        if (selectedConfidence) {
          const label = getDisplayConfidenceLabel(p.Confidence, activeSport);
          if (selectedConfidence.startsWith("Over ")) {
            const threshold = selectedConfidence.replace("Over ", "");
            const levels = ["Low","Moderate","Strong","Elite","Volatile Elite"];
            const i = levels.indexOf(threshold);
            const levelIndex = levels.indexOf(label);
            if (i === -1 || levelIndex === -1) return false;
            if (levelIndex <= i) return false;
          } else {
            if (label !== selectedConfidence) return false;
          }
        }


        // L5 Shape / Setup filter
        // L5 Shape / Setup filter
        if (Array.isArray(selectedL5Shapes) && selectedL5Shapes.length > 0) {
          const rowCanonical = getL5CanonicalKey(p);
          const normalizedSelected = selectedL5Shapes.map((x) => getL5CanonicalKey(x));
          if (!normalizedSelected.includes(rowCanonical)) return false;
        }


        // Minutes Trend pill filter (STABLE / RISING / SURGING / FALLING / COLLAPSING)
        if (String(activeSport || "").toUpperCase() !== "CFB" && Array.isArray(selectedMinutesTrends) && selectedMinutesTrends.length > 0) {
          const rowMinutesTrend = getMinutesTrendCanonicalKey(p);
          if (!selectedMinutesTrends.includes(rowMinutesTrend)) return false;
        }

        // Raw Formation filter (WNBA-first): filters exact codes like OOUUU and family buckets like BULLISH/TRAP.
        if (Array.isArray(selectedFormationFamilies) && selectedFormationFamilies.length > 0) {
          const fam = getFormationFamily(p);
          if (!fam || !selectedFormationFamilies.includes(fam)) return false;
        }

        if (Array.isArray(selectedFormationCodes) && selectedFormationCodes.length > 0) {
          const code = getFormationCode(p);
          if (!code || !selectedFormationCodes.includes(code)) return false;
        }


        // Pattern Intelligence is presentation-only: component matching derives
        // from the backend-owned raw Pattern while Advanced keeps exact matching.
        if (!matchesPatternFilters(p, {
          components: selectedPatternComponents,
          exactPatterns: selectedPatterns,
          mode: patternMatchMode,
        })) return false;

        // Backend-owned Bounce Classification v1 filters
        if (Array.isArray(selectedBounceLabels) && selectedBounceLabels.length > 0) {
          const rowBounceLabel = getBounceLabel(p);
          if (!rowBounceLabel || !selectedBounceLabels.includes(rowBounceLabel)) return false;
        }

        if (Array.isArray(selectedBounceFamilies) && selectedBounceFamilies.length > 0) {
          const rowBounceFamily = getBounceFamily(p);
          if (!rowBounceFamily || !selectedBounceFamilies.includes(rowBounceFamily)) return false;
        }

        if (selectedRisk.length > 0) {
          console.log("RISK FILTER ACTIVE:", selectedRisk, {
            player: p?.Player,
            risk: p?.Risk ?? p?.risk ?? p?.["Risk"] ?? p?.["Risk Tier"],
          });
        }
        // Risk Profile filter (LOW / MODERATE / HIGH)
        // Risk Tier filter (LOW / MODERATE / HIGH)
        if (Array.isArray(selectedRisk) && selectedRisk.length > 0) {
          const riskTier = String(p?.["Risk Tier"] ?? p?.Risk_Tier ?? "").toUpperCase().trim();

          // bucketize: treat MOD* as MODERATE, everything else as LOW/HIGH if present
          let bucket = "";
          if (riskTier.includes("LOW")) bucket = "LOW";
          else if (riskTier.includes("MOD")) bucket = "MODERATE";
          else if (riskTier.includes("HIGH") || riskTier.includes("ELEV")) bucket = "HIGH";

          // when filtering is active, if we can't bucket it, exclude it
          if (!bucket) return false;

          if (!selectedRisk.includes(bucket)) return false;
        }
      
        // Guru Grade (Conviction) filter
        if (selectedGuruGrade) {
          const gg = String(
            p["Guru Grade"] ??
            p.GuruGrade ??
            p.guruGrade ??
            p["Guru_Grade"] ??
            p["GuruGrade"] ??
            ""
          ).trim();
          if (!gg) return false;
          if (gg !== selectedGuruGrade) return false;
        }

       // --- TAX FILTER: keep TAXED vs HYPER-TAXED distinct ---
        if (taxFilter !== "ALL") {
          const tierRaw =
            p.Line_Tax_Tier ||
            p["Line_TaxTier"] ||
            p["Tax Tier"] ||
            "";
          const tier = String(tierRaw).toUpperCase();

          if (taxFilter === "HYPER") {
            // Only HYPER-TAXED
            if (!tier.includes("HYPER")) return false;

          } else if (taxFilter === "NO_HYPER") {
            // Exclude HYPER-TAXED lines (keep everything else)
            if (tier.includes("HYPER")) return false;

          } else if (taxFilter === "TAXED") {
            // TAXED but NOT HYPER-TAXED
            if (!(tier.includes("TAXED") && !tier.includes("HYPER"))) return false;

          } else if (taxFilter === "TAXED_HYPER") {
            // ⭐ NEW OPTION: TAXED + HYPER-TAXED (Overpriced lines)
            if (!(tier.includes("TAXED") || tier.includes("HYPER"))) return false;

          } else if (taxFilter === "FAIR") {
            // FAIR but not discounted
            if (!tier.includes("FAIR") || tier.includes("DISCOUNT")) return false;

          } else if (taxFilter === "DISCOUNT") {
            if (!tier.includes("DISCOUNT")) return false;

          } else if (taxFilter === "FAIR-DISCOUNT") {
            if (!(tier.includes("FAIR") || tier.includes("DISCOUNT"))) return false;
          }
        }


        // --- DvP TIER FILTER ---
        // Normalize raw DvP string into a simple bucket
        const rawDvp = String(
          p.Opp_DvP_Tier ||
          p["Opp_DvP_Tier"] ||
          p["DVP Tier"] ||
          ""
        ).toUpperCase();

        let dvpBucket = "NEUTRAL";

        // ✅ detect combos (R0) from player name
        const playerName = String(p.Player || p.player || "").toUpperCase();
        const isCombo = playerName.includes("+");
        if (isCombo) {
          dvpBucket = "R0";
        } else if (!rawDvp) {
          dvpBucket = "NEUTRAL";
        } else if (rawDvp.includes("R0")) {
          dvpBucket = "R0";
        } else if (rawDvp.includes("ELITE")) {
          dvpBucket = "ELITE";
        } else if (rawDvp.includes("TOUGH")) {
          dvpBucket = "TOUGH";
        } else if (rawDvp.includes("SOFT")) {
          dvpBucket = "SOFT";
        } else if (rawDvp.includes("TARGET")) {
          dvpBucket = "TARGET";
        }
        

        // Apply DvP filter (with special handling for "SOFT" = SOFT OR TARGET)
        if (dvpTierFilter && dvpTierFilter !== "ALL") {
          let passesDvpTier = true;

          if (dvpTierFilter === "SOFT") {
            // Smash preset: treat "Soft matchups" as SOFT or TARGET
            passesDvpTier = dvpBucket === "SOFT" || dvpBucket === "TARGET";
          } else if (dvpTierFilter === "TARGET") {
            passesDvpTier = dvpBucket === "TARGET";
          } else if (dvpTierFilter === "ELITE") {
            passesDvpTier = dvpBucket === "ELITE";
          } else if (dvpTierFilter === "TOUGH") {
            passesDvpTier = dvpBucket === "TOUGH";
          } else if (dvpTierFilter === "R0") {
            passesDvpTier = dvpBucket === "R0";
          } else {
            // fallback – exact match
            passesDvpTier = dvpBucket === dvpTierFilter;
          }

          if (!passesDvpTier) return false;
        }


        // --- SELECTION STRATEGY FILTER ---
        // --- SELECTION STRATEGY FILTER ---
        if (selStratFilter !== "ALL") {
          const ssDir = String(
            p.SS_Direction ?? p["SS_Direction"] ?? ""
          ).toUpperCase().trim();

          const ssLabel = String(
            p.SelectionStrategy ??
            p["SelectionStrategy"] ??
            p["Selection Strategy"] ??
            p["Sel Strat"] ??
            ""
          ).toUpperCase().trim();

          if (selStratFilter === "OVER"  && !ssDir.startsWith("OVER")) return false;
          if (selStratFilter === "UNDER" && !ssDir.startsWith("UNDER")) return false;
          if (selStratFilter === "MIXED" && !ssDir.startsWith("MIXED")) return false;

          // "No clear edge" lives in SelectionStrategy, NOT SS_Direction
          if (selStratFilter === "NO_EDGE" && !ssLabel.includes("NO CLEAR EDGE")) return false;
        }

          // --- GURU ALERT FILTER ---
          // guruAlertFilter: "ALL" | "0" | "1" | "2"
          if (guruAlertFilter !== "ALL") {
            const want = Number(guruAlertFilter);

            const lvlRaw =
              p.GuruAlert ??
              p["GuruAlert"] ??
              p.guruAlert ??
              p["guruAlert"] ??
              0;

            const lvl = Number(lvlRaw);

            // missing -> treat as 0
            const safeLvl = Number.isFinite(lvl) ? lvl : 0;

            if (safeLvl !== want) return false;
          }


                // 🔥 NEW: blowout LEVEL filter (coarse)
        if (blowoutLevelFilter !== "all") {
          const risk = normalizeBlowoutTier(p);

          if (blowoutLevelFilter === "no-high" && risk === "high") return false;
          if (blowoutLevelFilter === "low-only" && risk !== "low") return false;
          if (blowoutLevelFilter === "high-only" && risk !== "high") return false;
        }

      // 🔥 NEW: blowout filter
        if (blowoutFilter !== "all") {
          const risk = normalizeBlowoutTier(p); // "low" | "medium" | "high" | ""

          // 1) Exclude only HIGH games
          if (blowoutFilter === "no-high" && risk === "high") {
            return false;
          }

          // 2) LOW only  (supports both "low-only" and "low")
          if ((blowoutFilter === "low-only" || blowoutFilter === "low") && risk !== "low") {
            return false;
          }

          // 3) LOW + MODERATE only
          if (blowoutFilter === "low-moderate" &&
              !(risk === "low" || risk === "medium")) {
            return false;
          }

          // 4) HIGH only
          if ((blowoutFilter === "high" || blowoutFilter === "high-only") && risk !== "high") {
            return false;
          }
        }


        ////////////// Adding stuff for minutes ////////////////
        // Minutes filter: only applies to NBA / WNBA, and only if user picked a threshold
        // Minutes threshold filter (NBA / WNBA only)
        if (minMpgFilter && minMpgFilter !== "0") {
          const sport = (p.Sport || "").toUpperCase();
          if (sport === "NBA" || sport === "WNBA" || sport === "WNBAB") {
            const mpgVal = Number(p.mpg);          // comes from backend "mpg" field
            const minMpg = Number(minMpgFilter) || 0;

            // If MPG is missing or below the selected threshold, hide this row
            if (!Number.isFinite(mpgVal) || mpgVal < minMpg) return false;
          }
        }
        ////////////// end stuff for minutes ////////////////////

        /////////////// mins trend ///////////////////////////////

        // ---------- Minutes Trend (min_trend) ----------
        const mtRaw =
        p.min_trend ??
        p.Min_Trend ??
        p["min_trend"] ??
        p["Min_Trend"];

        const mnt = Number(mtRaw);

        // Range filter
        if (String(activeSport || "").toUpperCase() !== "CFB" && Number.isFinite(mnt)) {
        if (minutesTrendMin !== null && mnt < Number(minutesTrendMin)) return false;
        if (minutesTrendMax !== null && mnt > Number(minutesTrendMax)) return false;
        } else if (String(activeSport || "").toUpperCase() !== "CFB") {
        // If user set bounds and mnt is missing -> hide it
        if (minutesTrendMin !== null || minutesTrendMax !== null) return false;
        }

        // OVERS minutes gate (if ON, hide OVER tags when trend is negative)
        const tagRaw = p.Tag ?? p.tag ?? "";
        const tag = String(tagRaw).toUpperCase();

        if (String(activeSport || "").toUpperCase() !== "CFB" && oversMinutesGate) {
        if (isOverTag(tag) && Number.isFinite(mnt) && mnt < 0) return false;
        }

        // ✅ Attack UNDERS gate (if ON, only keep UNDER tags when minutes trend is negative)
        if (String(activeSport || "").toUpperCase() !== "CFB" && attackUndersMinutesGate) {
          if (isUnderTag(tag) && Number.isFinite(mnt) && mnt >= 0) return false;
        }

        ////////////// end min trend ////////////////////////////

        // --- Zodiac filter (Chinese zodiac by year) ---
        if (zodiacFilter && zodiacFilter !== "ALL") {
          const signRaw = (p.ZODIAC_SIGN ?? "")
            .toString()
            .trim()
            .toUpperCase(); // e.g. "RABBIT"

          const comboRaw = (p.ZODIAC_COMBO ?? "")
            .toString()
            .trim()
            .toUpperCase(); // e.g. "ROOSTER + RABBIT"

          // If no zodiac info at all, drop the row when a filter is active
          if (!signRaw && !comboRaw) return false;

          // For combos, we want "Rabbit" filter to catch "Rooster + Rabbit"
          if (!signRaw.includes(zodiacFilter) && !comboRaw.includes(zodiacFilter)) {
            return false;
          }
        }

        
        
        if (stocksHeatOnly) {
          const flag = String(p.StocksHeat || p["StocksHeat"] || "").toUpperCase();
          if (flag !== "YES") return false;
        }
        
    
        if (homeAwayFilter) {
          const ha = p["Home/Away"]?.toLowerCase();
          if (!ha || ha !== homeAwayFilter.toLowerCase()) return false;
        }
    
        if (wantsPlayer) {
          const nameField =
            activeSport === "TEG"
              ? String(
                  p.Symbol ||
                  p.Ticker ||
                  p.Stock ||
                  p.Company ||
                  p.Name ||
                  ""
                ).toLowerCase()
              : String(p.Player || "").toLowerCase();
        
          if (!nameField.includes(searchText)) {
            return false;
          }
        }
        
        // Momentum filters — support both spaced and compact keys
        const mt  = p["Momentum Tag"] ?? p["MomentumTag"];
        const mp  = p["Momentum Pattern"] ?? p["MomentumPattern"];
        const cm  = p["Confirmed Momentum"] ?? p["ConfirmedMomentum"];

        if (momentumFilter === "TRENDING UP"   && mt !== "TRENDING UP") return false;
        if (momentumFilter === "TRENDING DOWN" && mt !== "TRENDING DOWN") return false;

        if (momentumPatternFilter === "TRENDING UP"   && mp !== "TRENDING UP") return false;
        if (momentumPatternFilter === "TRENDING DOWN" && mp !== "TRENDING DOWN") return false;

        if (confirmedMomentumFilter === "CONFIRMED TRENDING UP"   && cm !== "CONFIRMED TRENDING UP") return false;
        if (confirmedMomentumFilter === "CONFIRMED TRENDING DOWN" && cm !== "CONFIRMED TRENDING DOWN") return false;

        if (playerTypeFilter && p["Player Type"] && p["Player Type"] !== playerTypeFilter) return false;
    
        // Hitter tiers (multi-select pills)
        if (Array.isArray(hitterTierFilter) && hitterTierFilter.length > 0) {
          const teamHitterAVG = parseFloat(p["team_hitterAvg"]);
          if (Number.isNaN(teamHitterAVG)) return false;
          if (!hitterTierFilter.includes(getHitterTier(teamHitterAVG))) return false;
        }
        if (Array.isArray(oppHitterTierFilter) && oppHitterTierFilter.length > 0) {
          const oppHitterAVG = parseFloat(p["opp_hitterAvg"]);
          if (Number.isNaN(oppHitterAVG)) return false;
          if (!oppHitterTierFilter.includes(getHitterTier(oppHitterAVG))) return false;
        }
    
        // Pitcher tiers & team/opponent metrics
        if (teamPitcherTierFilter && p.team_pitcher_tier !== teamPitcherTierFilter) return false;
        if (teamAttackabilityFilter && p.team_pitcher_attackability !== teamAttackabilityFilter) return false;
        if (teamWhipTierFilter && p.team_whip_tier !== teamWhipTierFilter) return false;
        if (teamKpctTierFilter && p.team_kpct_tier !== teamKpctTierFilter) return false;
  

        if (
          Array.isArray(oppPitcherTierFilter) &&
          oppPitcherTierFilter.length > 0 &&
          !oppPitcherTierFilter.includes(String(p.opp_pitcher_tier || "").trim())
        ) {
          return false;
        }
        
        if (
          Array.isArray(oppWhipTierFilter) &&
          oppWhipTierFilter.length > 0 &&
          !oppWhipTierFilter.includes(String(p.opp_whip_tier || "").trim())
        ) {
          return false;
        }
        
        if (
          Array.isArray(oppKpctTierFilter) &&
          oppKpctTierFilter.length > 0 &&
          !oppKpctTierFilter.includes(String(p.opp_kpct_tier || "").trim())
        ) {
          return false;
        }
        
        if (
          Array.isArray(oppAttackabilityFilter) &&
          oppAttackabilityFilter.length > 0 &&
          !oppAttackabilityFilter.includes(String(p.opp_pitcher_attackability || "").trim())
        ) {
          return false;
        }

    
        if (wantsTeam && !selectedTeams.includes(p.Team)) return false;
    
        // L10 vs PROP % threshold
   
        if (minL10Pct) {
          const pct = pctForFilter(p);
          if (pct === null || pct < Number(minL10Pct)) return false;
        }

        const oppL10Raw =
          p?.Opp_L10_HitPct ??
          p?.["Opp L10 Hit%"] ??
          p?.["Opp L10 HitPct"] ??
          p?.opp_l10_hitpct ??
          null;

        const oppL10Pct = Number(oppL10Raw);

        if (minOppL10Pct !== "" && Number.isFinite(oppL10Pct)) {
          if (oppL10Pct * 100 < Number(minOppL10Pct)) return false;
        } else if (minOppL10Pct !== "") {
          return false;
        }
        
    
        // --- Opp pitcher hand & batting order helpers ---
        const oppThrowsRaw = String(
          p.opp_hand ??
          p.opp_pitcher_hand ??
          p.opp_pitch_hand ??
          p["OppPitcherThrows"] ??
          p["Opp Pitcher Hand"] ??
          p["Opp Hand"] ??
          ""
        ).toUpperCase();
    
        const oppHand = oppThrowsRaw.startsWith("R") ? "RHP"
                      : oppThrowsRaw.startsWith("L") ? "LHP"
                      : "";
    
        const bo = Number(
          p.battingOrder ?? p.bat_order ?? p["BattingOrder"] ?? p["Bat Order"] ?? p.bat_order_num
        );
    
        // -- Filter: Opp Pitcher Hand
        if (oppPitcherHandFilter && oppHand !== oppPitcherHandFilter) return false;
    
        // -- Filter: Batting Order
        if (battingOrderFilter) {
          if (!Number.isFinite(bo)) return false;
          if (battingOrderFilter === "TOP12"    && !(bo >= 1 && bo <= 2)) return false;
          if (battingOrderFilter === "CORE35"   && !(bo >= 3 && bo <= 5)) return false;
          if (battingOrderFilter === "BOTTOM69" && !(bo >= 6 && bo <= 9)) return false;
          if (/^[1-9]$/.test(battingOrderFilter) && bo !== Number(battingOrderFilter)) return false;
        }
    
        // Exclude unsupported tags
        if (p.Tag === "UNSUPPORTED" || p.Tag === "INSUFFICIENT" || p.Tag === "MISSINGDATA" || p.Tag === "NO_PROP") return false;

        // --- Filter: Zodiac (Chinese sign / combos) ---
        if (zodiacFilter && zodiacFilter !== "ALL") {
          const rawZodiac = String(
            p.ZODIAC_SIGN ??
            p.ZODIAC_COMBO ??
            ""
          ).toUpperCase();

          // if we have no zodiac data on the row, drop it
          if (!rawZodiac) return false;

          // support combos like "RABBIT + DOG", "RABBIT/DOG", etc.
          const tokens = rawZodiac
            .split(/[\s,+/&-]+/)
            .map((t) => t.trim())
            .filter(Boolean);

          if (!tokens.includes(zodiacFilter.toUpperCase())) {
            return false;
          }
        }
        
    
        return true;
      });
    
      // 2) apply Guru once, to the filtered array
      if (guruMode) {
        rows = applyGuru(rows, guruType);
      }
      

      // ---- DQS (Decision Quality Score) ----
      // Compute once so it can render + sort + filter consistently.
      rows = (Array.isArray(rows) ? rows : []).map((r) => ({ ...r, dqs: supportsDqs(r?.Sport || activeSport) ? computeDQS(r) : null }));
      if (supportsDqs(activeSport) && dqsFilter && dqsFilter !== "ALL") {
        rows = rows.filter((r) => dqsPass(r?.dqs, dqsFilter));
      }

      return rows;

    }, [
      propsData,
      loadedPropsSport,
      entitlementVisibleProps,
      lineTypeVisibleProps,
      showAllProps,
      selectedTags,
      selectedLineTypes,
      selectedDefenseTiers,
      selectedGames,
      soccerGameGroups,
      activeSport,
      selectedDirection,
      directionMetaByRow,
      isAdminIdentity,
      selectedPropType,
      selectedPropTypes,
      selectedConfidence,
      selectedGuruGrade,
      selectedGuruPotential,
      playerInput,
      playerQuery,
      selectedSector,
      selectedGuruMagic,
      selectedZGuruTag,
      selectedLeanDirection,
      selectedGuruConflict,
      selectedGuruPicks,
      activePreset,
    selectedMinutesTrends,
      selectedIsGuruPicks,
      homeAwayFilter,
      momentumFilter,
      momentumPatternFilter,
      confirmedMomentumFilter,
      playerTypeFilter,
      minL10Pct,
      minOppL10Pct,
      selectedTeams,
      selectedTime,
      hitterTierFilter,
      oppPitcherTierFilter,
      oppHitterTierFilter,
      teamPitcherTierFilter,
      teamAttackabilityFilter,
      teamWhipTierFilter,
      teamKpctTierFilter,
      oppWhipTierFilter,
      oppKpctTierFilter,
      oppAttackabilityFilter,
      oppPitcherHandFilter,
      battingOrderFilter,
      guruMode, 
      guruType,
      minMpgFilter,
      stocksHeatOnly,
      taxFilter,
      blowoutFilter,
      dvpTierFilter,
      selStratFilter,
      guruAlertFilter,
      minutesTrendMin,
      minutesTrendMax,
      oversMinutesGate,
      attackUndersMinutesGate,
      zodiacFilter,
      dqsFilter,
      selectedL5Shapes,
      selectedFormationFamilies,
      selectedFormationCodes,
      selectedPatterns,
      selectedPatternComponents,
      patternMatchMode,
      selectedBounceLabels,
      selectedBounceFamilies,
      selectedMinutesTrends,
      selectedRisk,
    ]);
    
    // after const filteredProps = useMemo(...)

    const directionSummary = useMemo(() => {
      if (!Array.isArray(filteredProps) || !filteredProps.length) return null;
    
      // Bucket counts (for "type of day" pill)
      let smashOvers = 0;
      let leanOvers  = 0;
      let coinToss   = 0;
      let leanUnders = 0;
      let fades      = 0;
    
      // Per-tag counts
      let megaSmash = 0;
      let smash     = 0;
      let leanOver  = 0;
      let leanUnder = 0;
      let fade      = 0;
      let megaFade  = 0;
      let other     = 0;
    
      for (const row of filteredProps) {
        const tag = String(row.Tag || "").toUpperCase().trim();
    
        switch (tag) {
          case "MEGA SMASH":
            megaSmash++;
            smashOvers++;         // bucket
            break;
          case "SMASH":
            smash++;
            smashOvers++;         // bucket
            break;
          case "LEAN OVER":
            leanOver++;
            leanOvers++;          // bucket
            break;
          case "LEAN UNDER":
            leanUnder++;
            leanUnders++;         // bucket
            break;
          case "FADE":
            fade++;
            fades++;              // bucket
            break;
          case "MEGA FADE":
            megaFade++;
            fades++;              // bucket
            break;
          case "COIN TOSS":
            coinToss++;
            break;
          default:
            other++;
            coinToss++;           // treat unknown/neutral as coin-toss bucket
            break;
        }
      }
    
      const total = smashOvers + leanOvers + coinToss + leanUnders + fades;
      const pct = (n) => (total ? n / total : 0);
    
      return {
        // totals
        total,
        smashOvers,
        leanOvers,
        coinToss,
        leanUnders,
        fades,
    
        // bucket percentages (what the pill shows now)
        pctSmashOvers: pct(smashOvers),
        pctLeanOvers:  pct(leanOvers),
        pctCoinToss:   pct(coinToss),
        pctLeanUnders: pct(leanUnders),
        pctFades:      pct(fades),
    
        // per-tag counts
        megaSmash,
        smash,
        leanOver,
        leanUnder,
        fade,
        megaFade,
        other,
    
        // per-tag percentages
        pctMegaSmash: pct(megaSmash),
        pctSmash:     pct(smash),
        pctLeanOver:  pct(leanOver),
        pctLeanUnder: pct(leanUnder),
        pctFade:      pct(fade),
        pctMegaFade:  pct(megaFade),
        pctOther:     pct(other),
      };
    }, [filteredProps]);
    
// headline buckets for the slate pill
const pctOvers =
  (directionSummary?.pctSmashOvers + directionSummary?.pctLeanOvers || 0) * 100;
const pctUnders =
  (directionSummary?.pctLeanUnders + directionSummary?.pctFades || 0) * 100;
const pctCoin =
  (directionSummary?.pctCoinToss || 0) * 100;


// ---- Taxonomy derivation (Studs / Cores / Fillers) -------------------------------
// Phase 1 approach: compute a per-player "impact" from whatever numeric season/projection signals exist,
// then bucket by quantiles (top 20% = Studs, next 50% = Cores, rest = Fillers).
const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.+-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const getImpactSignal = (row) => {
  // Prefer explicit season avg if present; otherwise fallback to projection.
  const candidates = [
    row["Season Avg"], row["SEASON AVG"], row.SeasonAvg, row.seasonAvg, row.season_avg, row["Season_Avg"],
    row["Avg"], row.avg,
    row["Proj"], row.Proj, row.proj, row["Projection"], row.Projection,
  ];
  for (const c of candidates) {
    const n = toNum(c);
    if (n != null) return n;
  }
  return null;
};

const activeTaxonomySlateDate = useMemo(() => {
  const rows = Array.isArray(propsData) ? propsData : [];
  const dates = rows
    .map((r) => r?.GameDate ?? r?.["Game Date"] ?? r?.Slate ?? r?.Date)
    .map((v) => {
      if (v == null || String(v).trim() === "") return "";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    })
    .filter(Boolean);

  if (!dates.length) return "";
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  if (dates.includes(today)) return today;
  return [...new Set(dates)].sort()[0];
}, [propsData]);

const taxonomySlateRows = useMemo(() => {
  const rows = Array.isArray(propsData) ? propsData : [];
  if (!activeTaxonomySlateDate) return rows;
  return rows.filter((r) => {
    const raw = r?.GameDate ?? r?.["Game Date"] ?? r?.Slate ?? r?.Date;
    if (raw == null || String(raw).trim() === "") return false;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10) === activeTaxonomySlateDate;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return key === activeTaxonomySlateDate;
  });
}, [propsData, activeTaxonomySlateDate]);

const playerTierMap = useMemo(() => {
  const rows = Array.isArray(taxonomySlateRows) ? taxonomySlateRows : [];
  const byPlayer = new Map();

  for (const r of rows) {
    const name = String(r.Player || r.player || "").trim();
    if (!name) continue;
    const sig = getImpactSignal(r);
    if (sig == null) continue;

    const prev = byPlayer.get(name) || { sum: 0, cnt: 0 };
    prev.sum += sig;
    prev.cnt += 1;
    byPlayer.set(name, prev);
  }

  const impacts = Array.from(byPlayer.entries())
    .map(([player, { sum, cnt }]) => ({ player, impact: cnt ? (sum / cnt) : 0 }))
    .filter((x) => Number.isFinite(x.impact))
    .sort((a, b) => b.impact - a.impact);

  const n = impacts.length;
  if (!n) return {};

  // Quantile cutoffs (ensure at least 1 player per tier when possible)
  const studsCut = Math.max(1, Math.floor(n * 0.20));
  const coresCut = Math.max(studsCut + 1, Math.floor(n * 0.70)); // 20% studs + next 50% cores

  const map = {};
  impacts.forEach((x, idx) => {
    if (idx < studsCut) map[x.player] = "STUDS";
    else if (idx < coresCut) map[x.player] = "CORES";
    else map[x.player] = "FILLERS";
  });

  return map;
}, [taxonomySlateRows, activeSport]);

const taxonomyCounts = useMemo(() => {
  const map = playerTierMap || {};
  const counts = { STUDS: 0, CORES: 0, FILLERS: 0 };
  Object.values(map).forEach((t) => { if (counts[t] != null) counts[t] += 1; });
  return counts;
}, [playerTierMap]);

const guruListPlayerSet = useMemo(() => {
  const ids = new Set(myGuruList || []);
  const set = new Set();
  (Array.isArray(taxonomySlateRows) ? taxonomySlateRows : []).forEach((r) => {
    if (!ids.has(cardKey(r))) return;
    const name = String(r.Player || r.player || "").trim();
    if (name) set.add(name);
  });
  return set;
}, [myGuruList, taxonomySlateRows, cardKey]);

const guruListSlateCount = useMemo(() => {
  const ids = new Set(myGuruList || []);
  if (!ids.size) return 0;
  const slateIds = new Set();
  (Array.isArray(taxonomySlateRows) ? taxonomySlateRows : []).forEach((r) => {
    const id = cardKey(r);
    if (ids.has(id)) slateIds.add(id);
  });
  return slateIds.size;
}, [myGuruList, taxonomySlateRows, cardKey]);

const taxonomyPlayersByTier = useMemo(() => {
  const map = playerTierMap || {};
  const out = { STUDS: [], CORES: [], FILLERS: [] };
  Object.keys(map).forEach((player) => {
    const t = map[player];
    if (!out[t]) return;
    out[t].push(player);
  });
  // alpha sort within tier for scanability
  out.STUDS.sort((a, b) => a.localeCompare(b));
  out.CORES.sort((a, b) => a.localeCompare(b));
  out.FILLERS.sort((a, b) => a.localeCompare(b));
  return out;
}, [playerTierMap]);


///////////////////END FILTERED PROPS
  // Cross-sport My Plays: saved snapshots make the collection independent of the active sport.
  // Load sports referenced by legacy saved IDs so older lists also render after a fresh login.
  useEffect(() => {
    const { governed: sports, rejected } = governedSavedSportKeys(myGuruList);
    rejected.forEach((sport) => {
      console.warn(`[My Plays] ${sport} is not a governed sport key; saved projection was not preloaded.`);
    });
    const missing = sports.filter((sport) => !(propsCache?.[sport]?.length));
    if (!missing.length) return;
    let cancelled = false;
    Promise.all(missing.map(async (sport) => [sport, await loadProps(sport)])).then((entries) => {
      if (cancelled) return;
      setPropsCache((prev) => {
        const next = { ...(prev || {}) };
        entries.forEach(([sport, payload]) => {
          const rows = Array.isArray(payload?.props) ? payload.props : [];
          if (rows.length) next[sport] = rows;
        });
        return next;
      });
    }).catch((err) => console.warn("[My Plays] unable to preload saved sports", err));
    return () => { cancelled = true; };
  }, [myGuruList, propsCache, setPropsCache]);

  // Backfill snapshots for older saved IDs whenever matching rows are available in cache.
  useEffect(() => {
    if (!myGuruList.length) return;
    const known = [];
    Object.values(propsCache || {}).forEach((rows) => { if (Array.isArray(rows)) known.push(...rows); });
    if (Array.isArray(propsData)) known.push(...propsData);
    const byId = new Map(known.map((r) => [cardKey(r), r]));
    let changed = false;
    const next = { ...(myGuruItems || {}) };
    myGuruList.forEach((id) => {
      if (!next[id] && byId.has(id)) { next[id] = { ...byId.get(id), __pwgSavedAt: new Date().toISOString() }; changed = true; }
    });
    if (changed) setMyGuruItems(next);
  }, [myGuruList, myGuruItems, propsCache, propsData, cardKey, setMyGuruItems]);

  const crossSportMyPlays = useMemo(
    () => (myGuruList || []).map((id) => myGuruItems?.[id]).filter(Boolean),
    [myGuruList, myGuruItems]
  );

  const myPlaysSportCounts = useMemo(() => {
    const counts = {};
    crossSportMyPlays.forEach((r) => {
      const sport = String(r?.Sport || "OTHER").toUpperCase();
      counts[sport] = (counts[sport] || 0) + 1;
    });
    return counts;
  }, [crossSportMyPlays]);

  const visiblePool = useMemo(() => {
    let base = Array.isArray(filteredProps) ? filteredProps : [];

    // My Plays is a global, cross-sport collection and is not constrained by activeSport.
    if (guruListFilter === "my-list") {
      base = Array.isArray(crossSportMyPlays) ? crossSportMyPlays : [];
      if (myPlaysSportFilter !== "ALL") {
        base = base.filter((p) => String(p?.Sport || "").toUpperCase() === myPlaysSportFilter);
      }

      // Cross-sport My Plays must honor the same public Tag pills and player search
      // as the normal Props workspace. These filters are intentionally applied AFTER
      // the saved collection is assembled so they work across every saved sport.
      if (Array.isArray(selectedTags) && selectedTags.length > 0) {
        const allowedTags = new Set(selectedTags.map((t) => String(t || "").toUpperCase()));
        base = base.filter((p) => allowedTags.has(String(getRowTag(p) || "").toUpperCase()));
      }

      const savedSearchText = String(playerInput || playerQuery || "").trim().toLowerCase();
      if (savedSearchText) {
        base = base.filter((p) => [
          p?.Player,
          p?.Team,
          p?.Opponent,
          p?.Sport,
          p?.["Prop Type"],
          p?.PropType,
          getRowTag(p),
        ].some((v) => String(v || "").toLowerCase().includes(savedSearchText)));
      }
    }

    // Tree scope (optional): "My Plays" node can force list scope without fighting the dropdown
    if (taxonomyScope === "GURULIST") {
      const ids = new Set(myGuruList || []);
      base = base.filter((p) => ids.has(cardKey(p)));
    }

    // Tier bucketing (Studs / Cores / Fillers)
    if (taxonomyTier && taxonomyTier !== "ALL") {
      base = base.filter((p) => {
        const name = String(p.Player || p.player || "").trim();
        return name && playerTierMap?.[name] === taxonomyTier;
      });
    }

    // Specific player drill-down
    if (taxonomyPlayer) {
      base = base.filter((p) => String(p.Player || p.player || "").trim() === taxonomyPlayer);
    }

    return base;
  }, [filteredProps, guruListFilter, crossSportMyPlays, myPlaysSportFilter, selectedTags, playerInput, playerQuery, myGuruList, cardKey, taxonomyScope, taxonomyTier, taxonomyPlayer, playerTierMap]);

  // -------------------- Paging (NO hooks inside renderPlayerCards) --------------------
  const tierFilteredPool =
        gridTierFilter === "ALL"
          ? (Array.isArray(visiblePool) ? visiblePool : [])
          : (Array.isArray(visiblePool) ? visiblePool : []).filter(
              (r) => deriveTierFromRow(r) === gridTierFilter
            );

      const total = tierFilteredPool.length;

  const totalPages = useMemo(() => {
    const size = Number(gridPageSize) || 25;
    return Math.max(1, Math.ceil(total / size));
  }, [total, gridPageSize]);

  // Clamp page if filters shrink results
  useEffect(() => {
    if (gridPage > totalPages) setGridPage(totalPages);
    if (gridPage < 1) setGridPage(1);
  }, [gridPage, totalPages, setGridPage]);

  
 // ✅ Global sort (across ALL pages) for NBA Grid
const getNbaGridSortValue = (row, key) => {
    if (!row || !key) return null;

    // Normalize key (helps when headers include spaces/symbols like "Δ")
    const rawKey = String(key);
    const keyNorm = rawKey.replace(/[^a-zA-Z0-9]/g, "");

    // Special computed fields
    if (keyNorm === "GuruScore") return getGuruScoreValue(row);
    // Treat GuruRank sort as a proxy on GuruScore (rank 1 = highest score)
    if (keyNorm === "GuruRank") return getGuruScoreValue(row);

    // Defensive numeric parse that tolerates "+4.6", " 4.6 ", "—", etc.
    const num = (v) => {
      if (v === null || v === undefined) return null;
      if (typeof v === "number") return Number.isFinite(v) ? v : null;

      // Common dash placeholders
      const s0 = String(v).trim();
      if (!s0 || s0 === "—" || s0 === "-" || s0.toLowerCase() === "na" || s0.toLowerCase() === "n/a") return null;

      // Strip everything except digits, sign, and decimal point
      const cleaned = s0.replace(/[^0-9+\-\.]/g, "");
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : null;
    };

    // Minutes Heat: force an ordinal so it sorts meaningfully (HOT > WATCH > blank)
    const minutesHeatRank = (v) => {
      const s = (v ?? "").toString().trim().toUpperCase();
      if (s === "HOT") return 2;
      if (s === "WATCH") return 1;
      if (!s || s === "—" || s === "-") return 0;
      // Unknown labels: keep them but below HOT/WATCH
      return 0;
    };

    // Handle known fields (including synonyms / header variations)
    switch (keyNorm) {

      // Heat / delta fields need custom coercion (avoid string/lexicographic sort)
      case "MinutesHeat": {
        // Enforce semantic ordering: HOT > WATCH > blank/unknown
        const v = getMinutesHeatValue(row);
        const s = (v == null) ? "" : String(v).trim().toUpperCase();
        if (!s || s === "-" || s === "—") return null;
        if (s === "HOT") return 2;
        if (s === "WATCH") return 1;
        return null;
      }
      case "Minutes": {
        // Minutes sometimes arrive as strings; coerce safely
        return num(row.Minutes ?? row.MIN ?? row.min ?? row["Minutes"] ?? row["MIN"]);
      }
      case "L2Min":
      case "L2MinDelta": {
        // Displayed as +x.y; parse as number for correct ordering
        return num(getL2MinDeltaValue(row));
      }
      case "L2Stat":
      case "L2StatDelta": {
        return num(getL2StatDeltaValue(row));
      }

      case "L2PaDelta":
        return num(getL2PaDeltaValue(row));

      case "L2PA":
        if (rawKey.includes("Δ") || /delta/i.test(rawKey)) return num(getL2PaDeltaValue(row));
        return num(row.L2PA ?? row["L2 PA"] ?? row.l2PA);

      case "L2OutsDelta":
        return num(getL2OutsDeltaValue(row));

      case "L2Outs":
        if (rawKey.includes("Δ") || /delta/i.test(rawKey)) return num(getL2OutsDeltaValue(row));
        return num(row.L2Outs ?? row["L2 Outs"] ?? row.l2Outs);

      case "l5Setup":
      case "L5Setup":
        return String(
          row?.L5_Setup ??
            row?.["L5_Setup"] ??
            row?.["L5 Setup"] ??
            row?.l5Setup ??
            row?.L5Setup ??
            ""
        ).toUpperCase();
        

      case "L10HitPct":
        return num(row.L10_HitPct ?? row["L10_HitPct"] ?? row.l10HitPct ?? row["L10HitPct"]);

      case "L10Avg":
        return num(row.L10_Avg ?? row["L10_Avg"] ?? row.l10Avg ?? row["L10Avg"]);

      case "L10Median":
        return num(row.L10_Median ?? row["L10_Median"] ?? row.l10Median ?? row["L10Median"]);

      case "Proj":
        return num(row.Proj ?? row.proj ?? row.Projection ?? row["Projection"]);

      case "Player":
        return String(row.Player ?? row.player ?? "").toUpperCase();

      case "Sport":
        return String(row.Sport ?? "").toUpperCase();

      case "Team":
        return String(row.Team ?? row["Team Name"] ?? "").toUpperCase();

      case "Opponent":
        return String(row.Opponent ?? row["Opponent Name"] ?? "").toUpperCase();

      case "GameTime":
        return String(row.GameTime ?? row["Start Time"] ?? row.game_time ?? "");

      case "Prop":
        return String(row["Prop Type"] ?? row.PropType ?? row.prop_type ?? "").toUpperCase();

      case "Tag":
      case "PWGTag":
        return String(getRowTag(row) || "").toUpperCase();

      case "Line":
        return num(row.Line ?? row.line ?? row["Prop Value"] ?? row["PropValue"] ?? row.propValue);
      case "Delta":
        return gridDeltaValue(row, activeSport);

      case "LineType":
        return canonicalLineType(row);
      case "PrimaryMarketLine":
        return num(row["Primary Market Line"]);
      case "AlternateLine":
        return num(row["Alternate Line"]);
      case "LineFamilyCount":
        return num(row["Line Family Count"]);
      case "GoblinAttainability":
        return num(row["Goblin Attainability"]);
      case "DefenseRank":
        return defenseRankValue(row);
      case "DefenseFieldSize":
        return num(row["Defense Ranked Teams"]);
      case "DefenseTier":
        return String(row["Opponent Defense Tier"] || "");
      case "DefenseMetric":
        return String(row["Opponent Defense Metric"] || "");
      case "DefenseValue":
        return num(row["Opponent Defense Value"]);
      case "L5Delta":
        return num(row["L5 Delta"]);
      case "L10Delta":
        return num(row["L10 Delta"]);
      case "SeasonDelta":
        return num(row["Season Delta"]);

      case "Edge":
        return num(row.Edge ?? row.edge ?? row.Delta ?? row.delta);

      case "Minutes":
        return num(getMinutesValue(row, activeSport)) ?? num(row.Minutes ?? row.minutes);

      // "MinutesHeat" OR header like "Minutes Heat"
      case "MinutesHeat":
        return minutesHeatRank(getMinutesHeatValue(row) ?? row.MinutesHeat ?? row["Minutes Heat"] ?? row.minutesHeat);

      case "BounceLabel":
        return String(getBounceLabel(row) || "").toUpperCase();

      case "BounceScore":
        return num(getBounceStateScore(row));

      // Some column configs may use "MinutesHeatLabel" etc.
      case "MinutesHeatLabel":
        return minutesHeatRank(getMinutesHeatValue(row) ?? row.MinutesHeat ?? row["Minutes Heat"] ?? row.minutesHeat);

      // L2 deltas (handle both "L2MinDelta" and header "L2 Min Δ")
      case "L2MinDelta":
        return num(getL2MinDeltaValue(row));

      case "L2StatDelta":
        return num(getL2StatDeltaValue(row));

      // If the key got normalized to "L2Min" or "L2Stat" because of a "Δ" symbol, treat it as delta.
      case "L2Min":
        if (rawKey.includes("Δ") || /delta/i.test(rawKey)) return num(getL2MinDeltaValue(row));
        return num(row.L2Min ?? row["L2 Min"] ?? row.l2Min);

      case "L2Stat":
        if (rawKey.includes("Δ") || /delta/i.test(rawKey)) return num(getL2StatDeltaValue(row));
        return num(row.L2Stat ?? row["L2 Stat"] ?? row.l2Stat);

      default: {
        // Generic fallbacks
        if (typeof row[rawKey] === "number") return row[rawKey];
        const n = num(row[rawKey]);
        if (n !== null) return n;

        // Try normalized lookup if rawKey doesn't exist
        if (rawKey !== keyNorm && row[keyNorm] !== undefined) {
          if (typeof row[keyNorm] === "number") return row[keyNorm];
          const n2 = num(row[keyNorm]);
          if (n2 !== null) return n2;
          return row[keyNorm];
        }

        return row[rawKey] ?? null;
      }
    }
  };


  const globallySortedPool = useMemo(() => {
    const pool = Array.isArray(visiblePool) ? visiblePool : [];
    const key = nbaGridSortConfig?.key ?? null;
    if (!key) return pool;

    const dir = (nbaGridSortConfig?.direction === "desc") ? "desc" : "asc";
    // If sorting by GuruRank, asc means rank 1 first => score desc
    const effectiveDir = (key === "GuruRank")
      ? (dir === "asc" ? "desc" : "asc")
      : dir;

    const arr = [...pool];
    arr.sort((a, b) => {
      const va = getNbaGridSortValue(a, key);
      const vb = getNbaGridSortValue(b, key);

      // nulls last
      const aNull = (va === null || va === undefined || va === "");
      const bNull = (vb === null || vb === undefined || vb === "");
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;

      // numbers
      if (typeof va === "number" && typeof vb === "number") {
        return effectiveDir === "asc" ? (va - vb) : (vb - va);
      }

      // strings
      const sa = String(va);
      const sb = String(vb);
      return effectiveDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });

    return arr;
  }, [visiblePool, nbaGridSortConfig]);

  const normalizeName = (s) => String(s || "").trim().toLowerCase();

  const normalizePct = (raw) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    // your pipeline stores as decimal (0.3 = 30%)
    return Math.max(0, Math.min(100, Math.round(n * 100)));
  };
  
  const hitPctByPlayerProp = React.useMemo(() => {
    const map = new Map();
  
    const pool = Array.isArray(globallySortedPool) ? globallySortedPool : [];
    for (const r of pool) {
      const player = normalizeName(r.Player || r["Player"]);
      const propType = normalizeName(r["Prop Type"] || r.PropType || r.Prop || "");
      const pct = normalizePct(r.L10_HitPct ?? r["L10_HitPct"] ?? r["L10 Hit%"]);
      if (!player || !propType || pct == null) continue;
  
      map.set(`${player}__${propType}`, pct);
    }
  
    return map;
  }, [globallySortedPool]);
  


  const pageRows = useMemo(() => {
    const size = Number(gridPageSize) || 25;
    const page = Number(gridPage) || 1;
    // Mobile: ignore pagination; show full filtered pool
    if (isNarrow) return (globallySortedPool || []);
    const start = (page - 1) * size;
    const end = start + size;
    return (globallySortedPool || []).slice(start, end);
  }, [globallySortedPool, gridPage, gridPageSize]);
const showingFrom = total === 0 ? 0 : (Number(gridPage) - 1) * Number(gridPageSize) + 1;
  const showingTo = total === 0
    ? 0
    : Math.min(total, (Number(gridPage) - 1) * Number(gridPageSize) + Number(gridPageSize));


  // compute a quick 1-per-tag snapshot from whatever list you already have
  const featuredByTag = useMemo(() => {
    const source = filteredProps?.length ? filteredProps : propsData;
    return pickPerTag(source, 1);
  }, [filteredProps, propsData]);
  //////////////////////////////////////////////////////////////////////////////////////
  // ---- Redirect effect (keep this separate) ----
  useEffect(() => {
    // This runs whenever `authloading`, `authInitialized`, or `user` changes
    if (!authLoading && authInitialized && !user) {
      navigate("/login");  // This should not cause the hook to be conditionally rendered
    }
  }, [authLoading, authInitialized, user, navigate]);  // Make sure the dependencies are complete
    

  // ---- Admin gating (you see all columns; beta users see a locked set) ----
  const isAdminUser = useMemo(
    () => isAdminIdentity,
    [isAdminIdentity]
  );
  const isGuruPlayManager = useMemo(
    () => tegExperienceForAccess({ accessUser: entitlement.user, profile: userDoc }) === "COMMAND_CENTER",
    [entitlement.user, userDoc]
  );

  const lineupRowsBySport = useMemo(() => {
    const rowsBySport = { ...(propsCache || {}) };
    rowsBySport[String(activeSport || "").toUpperCase()] = Array.isArray(propsData) ? propsData : [];
    return rowsBySport;
  }, [propsCache, propsData, activeSport]);

  // One capability decision drives Basic, Advanced, and every navigation entry.
  // availableSports is already the Sport Availability + entitlement intersection.
  const lineupEntry = useMemo(() => lineupEntryCapability({
    activeSport,
    availableSports,
    rowsBySport: lineupRowsBySport,
  }), [activeSport, availableSports, lineupRowsBySport]);
  const basicLineupCapability = lineupEntry.basic;
  const basicLineupCanGenerate = basicLineupCapability.allowed && basicLineupCapability.hasEligiblePool;
  const lineupAllowedSports = lineupEntry.advancedSports;
  const lineupEntryVisible = lineupEntry.visible;

  // Sports-only tags for the lineup generator.
  // Keep this independent of tagColorMap so it is safe during initialization
  // and MSI/TEG market signals can never appear in the sports generator.
  const lineupAllowedTags = useMemo(() => [
    "MEGA SMASH",
    "SMASH",
    "LEAN OVER",
    "COIN TOSS",
    "LEAN UNDER",
    "FADE",
    "MEGA FADE",
  ], []);

  // Card experience tiers:
  // - Basic/free/beta/tester: decision + confidence only.
  // - Premium/Pro/Guru/MVP/Admin: full research card.
  const normalizedUserRole = String(userRole || "").trim().toLowerCase();
  const isPremiumUser = ["premium", "pro", "guru", "mvp"].includes(normalizedUserRole);
  const canSeeAdvancedCard = isAdminUser || isPremiumUser;
  const isBasicCardExperience = !canSeeAdvancedCard;
  const isBaseballSport = ["MLB", "KBO"].includes(String(activeSport || "").toUpperCase());
  const usesPublicEstimateCopy = ["MLB", "KBO", "WNBA"].includes(
    String(activeSport || "").toUpperCase()
  );
  // Details are role-based, not sport-based. Every non-admin sport receives
  // the same simplified public dialog; admin accounts retain full research.
  const isRegularDetailsExperience = usesSharedCustomerDetails(activeSport || gridDetailsRow?.Sport, isAdminUser);
  const isSoccerDetailsExperience = String(activeSport || gridDetailsRow?.Sport || "").toUpperCase() === "SOCCER";
  const isNFLDetailsExperience = String(activeSport || gridDetailsRow?.Sport || "").toUpperCase() === "NFL";
  const isCFBDetailsExperience = String(activeSport || gridDetailsRow?.Sport || "").toUpperCase() === "CFB";
  const detailDirectionMeta = directionPresentation(gridDetailsRow || {}, activeSport);

  // Proprietary Player DNA remains restricted to advanced experiences.
  const canSeePlayerDNA = canSeeAdvancedCard;

  // Public/free users always land on the plays experience.
  // Dashboard remains available only to premium/admin users for now.
  useEffect(() => {
    if (!canSeeAdvancedCard && activeView !== "props") {
      setActiveView("props");
    }
  }, [canSeeAdvancedCard, activeView]);


  // PUBLIC COLUMN GUARDRAIL:
  // Non-admin users can toggle columns, but ONLY within PUBLIC_NBA_GRID_KEYS.
  // This ensures we never expose internal/private columns to beta users.

  const PUBLIC_NBA_GRID_KEYS = ["delta", "confidence", "dqs", "tag", "selStrat", "bounceLabel", "bounceScore", "lineType", "primaryMarketLine", "alternateLine", "lineFamilyCount", "goblinAttainability", "l5Avg", "l10Avg", "seasonAvg", "l5HitPct", "l10HitPct", "seasonHitPct", "l5Delta", "l10Delta", "seasonDelta", "defenseRank", "defenseFieldSize", "defenseTier", "defenseMetric", "defenseValue", "historySeason", "teamContinuity"];

  useEffect(() => {
    if (isAdminUser) return;
  
    setNbaGridColumns((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
  
      // If we are already restricted to ONLY public keys, do NOTHING
      // (this is what prevents the "I unchecked but can't re-check" feeling)
      const alreadyPublicOnly =
        prevArr.length > 0 &&
        prevArr.every((c) => PUBLIC_NBA_GRID_KEYS.includes(c.key));
  
      if (alreadyPublicOnly) return prevArr;
  
      // Otherwise, restrict to public keys and default them ON
      const prevMap = new Map(prevArr.map((c) => [c.key, c]));
      return PUBLIC_NBA_GRID_KEYS.map((key) => {
        const existing = prevMap.get(key);
        return existing ? { ...existing, key, visible: true } : { key, visible: true };
      });
    });
  }, [isAdminUser, setNbaGridColumns]);
  


  const handleLogout = async () => {
    const auth = getAuth();
    try {
      // No need for 'manualLogout' localStorage flag if AuthContext is managing state
      await signOut(auth); // Sign out from Firebase session.
                          // AuthContext's onAuthStateChanged will detect this and set user to null.
      localStorage.removeItem("user"); // Clean up old localStorage entry if it exists (good practice)
      localStorage.removeItem("token"); // Clean up old localStorage entry if it exists (good practice)
      // The useEffect above will handle the navigate("/login") once AuthContext updates
    } catch (error) {
      console.error("Logout error:", error);
    }
  };


function pickPerTag(items, per = 1) {
  const by = Object.fromEntries(TAGS.map(t => [t, []]));
  for (const it of Array.isArray(items) ? items : []) {
    const tag = it?.Tag || it?.tag;
    if (!by[tag]) continue;
    if (by[tag].length < per) by[tag].push(it);
    if (Object.values(by).every(a => a.length >= per)) break;
  }
  return by;
}


  const toggleCardSections = (key) => {
    const isExpanded = cardToggles[key] || false;
    setCardToggles(prev => ({ ...prev, [key]: !isExpanded }));
  };
  

// Stable per-card expand key (you already call this as `detailsKeyFor(p)`)
function detailsKeyFor(p) {
  return [
    String(p.Sport || activeSport || "").toUpperCase(),
    String(p.Player || ""),
    String(p["Prop Type"] || p.Prop || ""),
    String(p["Prop Value"] ?? p.PropValue ?? p.value ?? "")
  ].join("::");
}

  const expandAllSections = () => {
    setCardToggles(prev => {
      const next = { ...prev };
      visiblePool.forEach(p => { next[detailsKeyFor(p)] = true; });
      return next;
    });
  };

  const collapseAllSections = () => {
    // clear everything at once
    setCardToggles({});
  };
  
useEffect(() => {
  setSelectedSports([activeSport]);
  const cachedRows = Array.isArray(propsCache[activeSport]) ? propsCache[activeSport] : null;
  if (cachedRows && !productionStaticCustomerSportEnabled(activeSport)) {
    setPropsData(cachedRows);
    setLoadedPropsSport(String(activeSport || "").toUpperCase());
    setInjuryReport(Array.isArray(injuryReportCache[activeSport]) ? injuryReportCache[activeSport] : []);
    if (Object.prototype.hasOwnProperty.call(dailyBriefCache || {}, activeSport)) setDailyBrief(dailyBriefCache[activeSport] || null);
    setPropsLoading(false);
    return;
  }
  setPropsLoading(true);
  fetchProps(activeSport);
  return () => {
    propsRequestGenerationRef.current += 1;
    abortStaticSportLoad(activeSport);
  };
}, [activeSport]);

  useEffect(() => {
    const sport = String(activeSport || "").toUpperCase();
    if (!productionStaticCustomerSportEnabled(sport)) return undefined;
    const monitor = createStaticCustomerReleaseMonitor({
      sport,
      getCurrentIdentity: () => staticSportIdentityRef.current[sport],
      onRelease: (payload, identity) => {
        const normalizedPayload = sport === "NHL" ? normalizeNhlCustomerPayload(payload) : payload;
        const rows = Array.isArray(normalizedPayload?.props) ? normalizedPayload.props : [];
        const injuries = Array.isArray(normalizedPayload?.injuryReport) ? normalizedPayload.injuryReport : [];
        const gameTimeMap = {};
        const games = rows.filter((row) => !String(row.Player || "").includes(" + ")).map((row) => {
          const key = singleSportGameKey(row);
          if (!key) return "";
          const time = row.GameTime ?? row["Game Time"] ?? row.game_time ?? "";
          if (!gameTimeMap[key] && time) gameTimeMap[key] = formatLocalEventTime(row, sport);
          return key;
        }).filter(Boolean);
        const available = new Set(games);
        staticSportIdentityRef.current[sport] = identity;
        setPropsCache((current) => ({ ...current, [sport]: rows }));
        setInjuryReportCache((current) => ({ ...current, [sport]: injuries }));
        setPropsData(rows);
        setGovernedNhlGames(sport === "NHL" && Array.isArray(normalizedPayload?.games) ? normalizedPayload.games : []);
        setLoadedPropsSport(sport);
        setInjuryReport(injuries);
        setUniqueGames([...available].sort());
        setGameTimes(gameTimeMap);
        setSelectedGames((current) => current.filter((key) => available.has(key)));
        setPropsDataError("");
      },
      onError: (error) => console.warn(`[static customer refresh] ${sport}`, error?.message || error),
    });
    return () => monitor.stop();
  }, [activeSport]);

  
  useEffect(() => {
    if (!propsLoading && propsCache[activeSport]) {
      const cachedRows = Array.isArray(propsCache[activeSport])
        ? propsCache[activeSport]
        : Array.isArray(propsCache[activeSport]?.props)
          ? propsCache[activeSport].props
          : [];
      setPropsData(cachedRows);
      setLoadedPropsSport(String(activeSport || "").toUpperCase());
      if (dailyBriefCache && Object.prototype.hasOwnProperty.call(dailyBriefCache, activeSport)) {
        setDailyBrief(dailyBriefCache[activeSport] || null);
      }
    }
  }, [propsLoading, activeSport, propsCache, dailyBriefCache]);


  // Additional filters for lineup generation
  const [lineupType, setLineupType] = useState("PROP");
  const effectiveLineupType = normalizeLineupMode(lineupType, isAdminUser);
  const draftKingsModeActive = isDraftKingsLineupMode(effectiveLineupType, isAdminUser);
  const [lineupSize, setLineupSize] = useState(6);
  const [overUnderPreference, setOverUnderPreference] = useState("Even");
  const [lineupGameTimeFilter, setLineupGameTimeFilter] = useState("");
  const [lineupHomeAwayFilter, setLineupHomeAwayFilter] = useState("");
  const [lineupSportFilter, setLineupSportFilter] = useState(activeSport);

  useEffect(() => {
    if (draftKingsModeActive) {
      setLineupSportFilter("NFL");
      ensureSportInCache("NFL");
    }
  }, [draftKingsModeActive]); // DraftKings contest modes are NFL-only by contract.

  useEffect(() => {
    if (lineupType !== effectiveLineupType) setLineupType(effectiveLineupType);
  }, [effectiveLineupType, lineupType]);

  // Keep the basic lineup generator aligned with the sport selected on the PWG rail.
  // Users may still choose any other sport they are authorized to access.
  useEffect(() => {
    const sport = String(activeSport || "").toUpperCase();
    if (sport && !["TEG", "MSI", "PWG"].includes(sport)) {
      setLineupSportFilter(sport);
      setGeneratedLineups([]);
    }
  }, [activeSport]);
  const [lineupGameFilter, setLineupGameFilter] = useState("");
  const [lineupMode, setLineupMode] = useState("Basic"); // Basic or Advanced

  const [lineupPool, setLineupPool] = useState("all"); // "all" | "filtered" | "guru"


  ////////////////////magic lineup generator setup //////////////////
// Preload data for a sport into propsCache without touching the visible cards
const [preloadBusy, setPreloadBusy] = useState(false);

const ensureSportInCache = useCallback(async (sport) => {
  const key = String(sport || '').toUpperCase();
  if (key === "TEG") return;
  if (Array.isArray(propsCache[key])) return; // already cached in normalized form

  setPreloadBusy(true);
  try {
    const payload = await loadProps(key); // { props, injuryReport } or normalized legacy payload
    const rows = Array.isArray(payload?.props) ? payload.props : [];
    const injuries = Array.isArray(payload?.injuryReport) ? payload.injuryReport : [];

    setPropsCache(prev => ({ ...prev, [key]: rows }));
    setInjuryReportCache(prev => ({ ...prev, [key]: injuries }));
  } finally {
    setPreloadBusy(false);
  }
}, [propsCache, setPropsCache, setInjuryReportCache]);

// whenever the lineup “Sport” dropdown changes, silently cache that sport
useEffect(() => {
  if (!lineupSportFilter) return;
  ensureSportInCache(lineupSportFilter);
}, [lineupSportFilter, ensureSportInCache]);

///////////////////end magic lineup generator setup /////////////

const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// ✅ Global SORTED pool (applies sort BEFORE paging so it works across ALL pages)
const sortedTierFilteredPool = useMemo(() => {
  const base = Array.isArray(tierFilteredPool) ? [...tierFilteredPool] : [];
  const key = nbaGridSortConfig?.key;
  if (!key) return base;

  const dir = nbaGridSortConfig?.direction === "asc" ? 1 : -1;

  base.sort((a, b) => {
    const av = getNbaGridSortValue(a, key);
    const bv = getNbaGridSortValue(b, key);

    // nulls / undefined last (both directions)
    const aNull = av === null || av === undefined || (typeof av === "number" && !Number.isFinite(av));
    const bNull = bv === null || bv === undefined || (typeof bv === "number" && !Number.isFinite(bv));
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;

    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;

    return String(av).localeCompare(String(bv)) * dir;
  });

  return base;
}, [tierFilteredPool, nbaGridSortConfig]);

const inputStyle = {

  padding: "0.5rem 1rem",
  fontSize: "0.95rem",
  borderRadius: "12px",
  border: "1px solid #ccc",
  background: "#fff",
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
  appearance: "none",
  fontFamily: "Inter, sans-serif",
  minWidth: "140px"
};

//guarantees the lineup size is met using whatever stock exists.
function computeOUCounts(size, oversLen, undersLen, pref) {
  // desired mix
  let wantOver, wantUnder;
  if (pref === "MoreOvers") {
    wantOver = Math.ceil(size * 0.7);
  } else if (pref === "MoreUnders") {
    wantOver = size - Math.ceil(size * 0.7);
  } else { // "Even" or anything else
    wantOver = Math.floor(size / 2);
  }
  wantUnder = size - wantOver;

  // cap to available stock
  let over = Math.min(wantOver, oversLen);
  let under = Math.min(wantUnder, undersLen);

  // backfill remainder from whichever side still has stock
  let rem = size - (over + under);
  if (rem > 0) {
    const addOver = Math.min(rem, oversLen - over);
    over += addOver; rem -= addOver;
  }
  if (rem > 0) {
    const addUnder = Math.min(rem, undersLen - under);
    under += addUnder; rem -= addUnder;
  }
  return { over, under }; // sums to <= size, never exceeds stock
}


// --- ONE AND ONLY generateLineups ---
const generateLineups = (maxLineupsArg, evaluationNow = new Date()) => {
  const sportKey = String(activeSport || "").toUpperCase();

  // 1) Choose base pool (now aware of lineupPool === "guru")
  const sameSportAsCards =
    sportKey === String(activeSport).toUpperCase();

  let base;

  // (a) Cards → Filtered pool
  if (lineupPool === "filtered" && sameSportAsCards) {
    base = (filteredProps || []).filter((row) => {
      const rowSport = String(
        row.Sport || row.League || row.LeagueKey || activeSport
      ).toUpperCase();
      return rowSport === sportKey;
    });

  // (b) My Plays pool
  } else if (lineupPool === "guru") {
    // prefer the filtered cards if we're on the same sport,
    // otherwise fall back to the preloaded cache for that sport
    const source =
      sameSportAsCards &&
      Array.isArray(filteredProps) &&
      filteredProps.length
        ? filteredProps
        : (Array.isArray(propsCache[sportKey]) ? propsCache[sportKey] : []);

    base = source.filter((row) => {
      const rowSport = String(
        row.Sport || row.League || row.LeagueKey || activeSport
      ).toUpperCase();
      if (rowSport !== sportKey) return false;
      // use your existing MyGuru helper
      return isInMyList(row);
    });

  // (c) Full preloaded pool for that sport
  } else {
    base = Array.isArray(propsCache[sportKey]) ? propsCache[sportKey] : [];
  }

  if (!base.length) {
    // For non-guru pools, nudge cache warm-up and bail
    if (lineupPool !== "guru") {
      ensureSportInCache(sportKey);
      console.warn("[lineups] No data for", sportKey);
    }
    return [];
  }

  // The dashboard payload is not sufficient by itself: every pick must have
  // the governed identity, explicit pregame status, and future timestamp.
  base = eligibleLineupPool(base, { sport: sportKey, now: evaluationNow }).eligible;
  if (!base.length) return [];

  // ---- helpers (local, safe) ----
  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const teamOf = (p) => p.Team || p["Team Name"] || "";
  const oppOf  = (p) => p.Opponent || p["Opponent Name"] || "";
  const isMMArow = (p) =>
    sportKey === "MMA" || String(p.Sport || "").toUpperCase() === "MMA";

  const matchupOf = (p) => {
    // prefer provided Matchup if it exists and isn't "nan"
    const m = p.Matchup && !/nan/i.test(String(p.Matchup)) ? String(p.Matchup) : "";
    if (m) return m;

    // fall back to Team vs/ @ Opponent
    const t = teamOf(p), o = oppOf(p);
    if (!t || !o) return "";
    const ha = norm(p["Home/Away"] || p.HomeAway);
    const sep = ha === "away" ? " @ " : " vs ";
    return `${t}${sep}${o}`;
  };

  const bucketFrom = (val) => {
    const s = String(val || "");
    if (/^(early|afternoon|evening|late)$/i.test(s)) return s;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    const h = d.getHours();
    if (h < 13) return "Early";
    if (h < 17) return "Afternoon";
    if (h < 20) return "Evening";
    return "Late";
  };

  // 2) Apply lineup-level filters (more tolerant)
  const pool = base.filter((p) => {
    if (!p.Player) return false;

    // Team required for team sports, not for MMA
    if (!isMMArow(p) && (!teamOf(p) || !oppOf(p))) return false;

    const matchup = matchupOf(p);
    if (!matchup) return false;

    // Home/Away (fall back to parsing matchup when HA is missing)
    if (lineupHomeAwayFilter) {
      let ha = norm(p["Home/Away"] || p.HomeAway || "");
      if (!ha) {
        const m = norm(matchup);
        ha = m.includes("@") ? "away" : (m.includes("vs") ? "home" : "");
      }
      if (ha && ha !== norm(lineupHomeAwayFilter)) return false;
    }

    // Time bucket
    if (lineupGameTimeFilter) {
      const bucket = bucketFrom(
        p.GameTime || p.GameTimeBucket || p["Time Bucket"]
      );
      if (bucket && bucket !== lineupGameTimeFilter) return false;
    }

    // Game text filter should use robust matchup string
    if (
      lineupGameFilter &&
      !norm(matchup).includes(norm(lineupGameFilter))
    ) {
      return false;
    }

    return true;
  });

  // 3) Build overs/unders groups
  const overs  = shuffleArray(pool.filter((p) => isOverTag(p.Tag)));
  const unders = shuffleArray(pool.filter((p) => isUnderTag(p.Tag)));

  // 4) Assemble lineups
  const want = Number(maxLineupsArg ?? maxLineups);
  const lineups = [];

  for (let i = 0; i < want; i++) {
    let attempts = 0;
    while (attempts++ < 200) {
      const { over, under } =
        getMixCounts(lineupSize, overUnderPreference, overs.length, unders.length);

      const takeOvers  = shuffleArray(overs).slice(0, over);
      const takeUnders = shuffleArray(unders).slice(0, under);

      let tempLineup = [...takeOvers, ...takeUnders];

      if (tempLineup.length < lineupSize) {
        const need = lineupSize - tempLineup.length;
        const takenPlayers = new Set(tempLineup.map((x) => lineupPlayerKey(x, sportKey)));
        const rest = shuffleArray(
          pool.filter((p) => !takenPlayers.has(lineupPlayerKey(p, sportKey)))
        ).slice(0, need);
        tempLineup = [...tempLineup, ...rest];
      }

      if (tempLineup.length !== lineupSize) continue;

      const playerSet = new Set(tempLineup.map((p) => lineupPlayerKey(p, sportKey)));
      if (playerSet.size !== tempLineup.length) continue;

      // Team diversity rule: skip for MMA
      const lineupIsMMA =
        sportKey === "MMA" ||
        String(tempLineup[0]?.Sport || "").toUpperCase() === "MMA";
      if (!lineupIsMMA) {
        const teamSet = new Set(tempLineup.map((p) => teamOf(p)));
        if (teamSet.size < 2) continue;
      }

      lineups.push(tempLineup);
      break;
    }
  }

  return lineups;
};


//


const shortTagLabel = (t = "") => {
  const k = t.toUpperCase();
  if (k === "MEGA SMASH") return "MEGA";
  if (k === "SMASH")      return "SMASH";
  if (k === "LEAN OVER")  return "LEAN O";
  if (k === "LEAN UNDER") return "LEAN U";
  if (k === "MEGA FADE")  return "M FADE";
  if (k === "FADE")       return "FADE";
  if (k === "COIN TOSS")  return "TOSS";
  return t || "";
};
const tagBgFor = (t = "", map = {}) => map?.[t] || "#64748B";

const renderLineupResult = () => {
  if (!generatedLineups.length) return null;

  // Desktop grid: # · Name · Prop · Line · Pick
  const gridColsDesktop = "0.32fr 2.40fr 2.20fr 0.70fr 1.25fr";

  return (
    <div className="lineups-wrap" style={{ marginTop: "2rem" }}>
      <h3 style={{ marginBottom: "1rem" }}>Generated Lineups</h3>

      <div className="lineups">
        {generatedLineups.map((lineup, i) => (
          <article key={i} className="lineup-card card">
            {/* Header with extra space */}
            <div className="header-row"
              style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}
            >
              <div style={{ display:"flex", alignItems:"center", gap:".5rem" }}>
                <img src="/playswithguru-logo.png" alt="Guru" style={{ height: 36 }} />
                <span style={{
                  background:"#e3f2fd", color:"#1565c0", fontWeight:700,
                  fontSize:"1.05rem", padding:"2px 10px", borderRadius:8
                }}>
                  Lineup {i + 1}
                </span>
              </div>
              <span style={{ fontSize:".85rem", color:"#6b7280", paddingLeft:12 }}>
                {new Date().toLocaleString(undefined, {
                  month:"short", day:"2-digit", hour:"2-digit", minute:"2-digit", hour12:true, timeZoneName:"short"
                })}
              </span>
            </div>

            {/* Desktop table head */}
            {!isNarrow && (
              <div className="table-head" style={{
                display:"grid", gridTemplateColumns:gridColsDesktop, fontWeight:700,
                borderBottom:"1px solid #e5e7eb", paddingBottom:".5rem", marginBottom:".5rem"
              }}>
                <div>#</div>
                <div>Name</div>
                <div>Prop</div>
                <div style={{ textAlign:"left" }}>Line</div>
                <div style={{ textAlign:"center" }}>Pick</div>
              </div>
            )}

            {/* Rows */}
            {lineup.map((p, idx) => {
              const sport = p.Sport || activeSport;
              const game  = p.Matchup || `${p.Team} ${p['Home/Away'] === 'away' ? '@' : 'vs'} ${p.Opponent}`;
              const meta  = `(${sport}) ${game}`; // <-- parentheses, no separator

              const under    = isUnderTag(p.Tag);
              const dirClass = under ? 'is-under' : 'is-over';
              const ouLabel  = under ? '↓ Under' : '↑ Over';
              const tagBg    = tagColorMap?.[p.Tag] || '#64748b';

              if (!isNarrow) {
                return (
                  <div key={idx} className="lineup-row">
                    <div>{idx + 1}</div>

                    {/* name block */}
                    <div className="name-cell">
                      <div className="player">{p.Player}</div>
                      <div className="meta">{meta}</div>
                    </div>

                    <div className="cell-prop">{p['Prop Type']}</div>
                    <div className="line-cell">{p['Prop Value']}</div>

                    <div className="pick-col">
                      <span className={`pill ou-chip ${dirClass}`}>{ouLabel}</span>
                      <span className="pill tag-pill" style={{ backgroundColor: tagBg }}>
                        {shortTagLabel(p.Tag)}
                      </span>
                    </div>
                  </div>
                );
              }

              // MOBILE
              return (
                <div key={idx} className="m-row">
                  <div className="m-top">
                    <span className="idx">{idx + 1}</span>
                    <div className="m-name">
                      <div className="player">{p.Player}</div>
                      <div className="meta">{meta}</div>
                    </div>
                    <div className="pick-col">
                      <div className={`ou-chip ${dirClass}`}>{ouLabel}</div>
                      <div className="tag-pill" style={{ backgroundColor: tagBg }}>
                        {shortTagLabel(p.Tag)}
                      </div>
                    </div>
                  </div>

                  <div className="m-prop-line">
                    <span className="prop">{p['Prop Type']}</span>
                    <span className="line">Line: {p['Prop Value']}</span>
                  </div>
                </div>
              );
            })}
          </article>
        ))}
      </div>
    </div>
  );
};
  const _tagQualityWeight = (tag) => {
    const t = String(tag || "").toUpperCase();
    // Higher is better
    if (t.includes("MEGA SMASH")) return 6;
    if (t.includes("SMASH")) return 5;
    if (t.includes("LEAN OVER")) return 4;
    if (t.includes("COIN")) return 3;
    if (t.includes("LEAN UNDER")) return 2;
    if (t.includes("FADE") && t.includes("MEGA")) return 0;
    if (t.includes("FADE")) return 1;
    return 3;
  };

  const _pickGuruScore = (p) => {
    const v = p?.GuruScore ?? p?.guruScore ?? p?.["Guru Score"] ?? p?.["GuruScore"] ?? p?.score ?? 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const _lineupQuality = (lineup) => {
    const picks = Array.isArray(lineup) ? lineup : [];
    const sumGuru = picks.reduce((acc, p) => acc + _pickGuruScore(p), 0);
    const sumTag = picks.reduce((acc, p) => acc + _tagQualityWeight(p?.Tag || p?.tag), 0);
    // Weighted blend; guru dominates, tag breaks ties.
    return sumGuru * 10 + sumTag;
  };

  const _buildExposure = (lineups) => {
    const counts = new Map();
    for (const lu of lineups || []) {
      for (const p of lu || []) {
        const player = p?.Player || p?.player || "Unknown";
        const sport = p?.League || p?.Sport || p?.sport || "";
        const key = sport ? `${player} • ${sport}` : player;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    const rows = Array.from(counts.entries()).map(([key, ct]) => ({ key, ct }));
    rows.sort((a, b) => b.ct - a.ct || a.key.localeCompare(b.key));
    return rows;
  };

  const _renderLineupCard = (lineup, index) => {
    const picks = Array.isArray(lineup) ? lineup : [];
    const q = _lineupQuality(picks);

    return (
      <article
        style={{
          border: "1px solid rgba(148,163,184,0.35)",
          borderRadius: 14,
          padding: 14,
          background: "#fff",
          boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
          <div style={{ fontFamily: "Georgia, serif", fontWeight: 900, fontSize: 18 }}>
            Lineup {index + 1}
            <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 800, color: "#64748b" }}>Score: {Math.round(q)}</span>
          </div>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>{new Date().toLocaleString()}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 10 }}>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>Player</div>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>Prop / Line</div>
        </div>

        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 12 }}>
          {picks.map((p, i) => {
            const player = p?.Player || p?.player || "Unknown";
            const rawProp = p?.Prop || p?.PropType || p?.prop || p?.propType || p?.["Prop Type"] || "Prop";
            const prop = customerPropTypeLabel(rawProp, p?.League || p?.Sport || p?.sport || activeSport);
            const line = p?.Line ?? p?.PropValue ?? p?.line ?? p?.propValue ?? p?.["Prop Value"];
            const sport = p?.League || p?.Sport || p?.sport || "";
            const tag = p?.Tag || p?.tag || "";
            const tagColor = tagColorMap?.[tag] || "#0f172a";

            return (
              <div
                key={i}
                role={isNarrow && canSeeAdvancedCard ? "button" : undefined}
                tabIndex={isNarrow && canSeeAdvancedCard ? 0 : undefined}
                onClick={() => {
                  if (isNarrow) handleOpenGridDetails(p, sortedTierFilteredPool || []);
                }}
                onKeyDown={(e) => {
                  if (!isNarrow) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenGridDetails(p, sortedTierFilteredPool || []);
                  }
                }}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.35fr 1fr",
                  gap: 10,
                  alignItems: "start",
                }}
              >
                <div style={{ fontWeight: 900 }}>
                  {player}
                  {sport ? (
                    <span
                      style={{
                        display: "inline-block",
                        marginLeft: 8,
                        padding: "2px 8px",
                        borderRadius: 999,
                        border: "1px solid rgba(148,163,184,0.55)",
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#0f172a",
                        background: "#fff",
                      }}
                    >
                      {sport}
                    </span>
                  ) : null}
                </div>

                <div>
                  <div style={{ fontWeight: 900 }}>{prop}</div>
                  {line !== undefined && line !== null && line !== "" ? (
                    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Line: {line}</div>
                  ) : null}
                  {tag ? (
                    <div
                      style={{
                        display: "inline-block",
                        marginTop: 6,
                        padding: "2px 10px",
                        borderRadius: 999,
                        background: tagColor,
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 900,
                      }}
                    >
                      {tag}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </article>
    );
  };

  const renderLineupsWorkspace = () => {
    const lineups = Array.isArray(generatedLineups) ? generatedLineups : [];
    if (!lineups.length) return null;

    const sorted = [...lineups].sort((a, b) => _lineupQuality(b) - _lineupQuality(a));
    const exposure = _buildExposure(sorted);
    const generatedSports = Array.from(new Set(sorted.flatMap((lineup) => lineup.map((row) => String(row?.Sport || activeSport || "").toUpperCase())).filter(Boolean))).join(" + ");

    return (
      <section id="pwg-lineups-dock" style={{ width: "100%" }}>
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 25,
            background: "rgba(255,255,255,0.98)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(148,163,184,0.35)",
            borderRadius: 14,
            padding: 10,
            marginBottom: 12,
            boxShadow: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontFamily: "Georgia, serif", fontWeight: 800, fontSize: 20 }}>
                {generatedSports || String(activeSport || "").toUpperCase()} Lineups <span style={{ fontWeight: 600, color: "#64748b" }}>{sorted.length} generated</span>
              </div>

              <div style={{ display: "flex", gap: 6, marginLeft: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setLineupsTab("lineups")}
                  style={{
                    padding: "0.35rem 0.6rem",
                    borderRadius: 10,
                    border: "1px solid rgba(148,163,184,0.55)",
                    background: lineupsTab === "lineups" ? "#0f172a" : "#fff",
                    color: lineupsTab === "lineups" ? "#fff" : "#111827",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Lineups
                </button>
                <button
                  type="button"
                  onClick={() => setLineupsTab("exposure")}
                  style={{
                    padding: "0.35rem 0.6rem",
                    borderRadius: 10,
                    border: "1px solid rgba(148,163,184,0.55)",
                    background: lineupsTab === "exposure" ? "#0f172a" : "#fff",
                    color: lineupsTab === "exposure" ? "#fff" : "#111827",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Exposure
                </button>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => {
                  setShowLineupsFocus(false);
                  setLineupsTab("lineups");
                }}
                style={{
                  padding: "0.4rem 0.65rem",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: "#fff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                ← Back to Props (Cards/Grid)
              </button>

              <button
                type="button"
                onClick={() => setHideLineupControls((v) => !v)}
                style={{
                  padding: "0.4rem 0.65rem",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: hideLineupControls ? "#fff" : "#0f172a",
                  color: hideLineupControls ? "#111827" : "#fff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {hideLineupControls ? "Show" : "Hide"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setGeneratedLineups([]);
                  setShowLineupsFocus(false);
                  setLineupsTab("lineups");
                }}
                style={{
                  padding: "0.4rem 0.65rem",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: "#fff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {exposure.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>Top exposure:</div>
              {exposure.slice(0, 6).map((r) => (
                <div
                  key={r.key}
                  style={{
                    padding: "0.25rem 0.55rem",
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.55)",
                    background: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {r.key} · {r.ct}x
                </div>
              ))}
            </div>
          )}
        </div>

        {hideLineupControls ? null : lineupsTab === "exposure" ? (
          <div style={{ border: "1px solid rgba(148,163,184,0.35)", borderRadius: 14, padding: 12, background: "#fff" }}>
            <div style={{ fontWeight: 900, marginBottom: 8 }}>Exposure Summary</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
              {exposure.map((r) => (
                <div
                  key={r.key}
                  style={{
                    border: "1px solid rgba(148,163,184,0.35)",
                    borderRadius: 12,
                    padding: 10,
                    background: "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <div style={{ fontWeight: 800 }}>{r.key}</div>
                  <div style={{ fontWeight: 900, color: "#0f172a" }}>{r.ct}x</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
            {sorted.map((lineup, idx) => _renderLineupCard(lineup, idx))}
          </div>
        )}
      </section>
    );
  };


  const renderDraftKingsLineupsWorkspace = () => (
    <DraftKingsLineupResults
      contract={draftKingsResult?.contract}
      result={draftKingsResult?.result}
      hidden={hideLineupControls}
      onBack={() => setShowLineupsFocus(false)}
      onToggleHidden={() => setHideLineupControls((value) => !value)}
      onClear={() => {
        setDraftKingsResult(null);
        setShowLineupsFocus(false);
      }}
    />
  );


const renderLineupDock = () => {
  const exposure = buildExposure(generatedLineups);

  return (
    <div style={{ padding: 12 }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontWeight: 900, fontSize: 18 }}>
          Lineups <span style={{ opacity: 0.6, fontWeight: 800 }}>{generatedLineups.length} generated</span>
        </div>

        <div style={{ flex: 1 }} />

        <button
          type="button"
          className="pill"
          onClick={() => setDockTab("lineups")}
          style={{
            background: dockTab === "lineups" ? (darkMode ? "#1f2937" : "#0b1220") : "transparent",
            color: dockTab === "lineups" ? "#fff" : (darkMode ? "#e5e7eb" : "#111"),
            border: "1px solid rgba(0,0,0,0.10)",
            fontWeight: 900,
          }}
        >
          Lineups
        </button>

        <button
          type="button"
          className="pill"
          onClick={() => setDockTab("exposure")}
          style={{
            background: dockTab === "exposure" ? (darkMode ? "#1f2937" : "#0b1220") : "transparent",
            color: dockTab === "exposure" ? "#fff" : (darkMode ? "#e5e7eb" : "#111"),
            border: "1px solid rgba(0,0,0,0.10)",
            fontWeight: 900,
          }}
        >
          Exposure
        </button>

        <button
          type="button"
          className="pill"
          onClick={() => setDockOpen(false)}
          style={{ fontWeight: 900 }}
          title="Hide dock"
        >
          Hide
        </button>

        <button
          type="button"
          className="pill"
          onClick={() => {
            setGeneratedLineups([]);
            setLineupNotice("");
            setDockTab("lineups");
            setDockKey((k) => k + 1);
          }}
          style={{ fontWeight: 900 }}
          title="Clear generated lineups"
        >
          Clear
        </button>
      </div>

      {/* Body */}
      {dockTab === "lineups" ? (
        <div>
          {generatedLineups?.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {generatedLineups.map((lu, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid rgba(148,163,184,0.35)",
                    borderRadius: 14,
                    padding: 10,
                    background: darkMode ? "#101826" : "#fff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ fontWeight: 900 }}>Lineup {idx + 1}</div>
                    <div style={{ opacity: 0.7, fontWeight: 700, fontSize: 12 }}>
                      {new Date().toLocaleString()}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 70px", gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.7 }}>Player</div>
                    <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.7 }}>Prop</div>
                    <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.7 }}>Pick</div>

                    {(lu || []).map((p, i) => (
                      <React.Fragment key={i}>
                        <div style={{ fontWeight: 800 }}>{p?.Player || "-"}</div>
                        <div style={{ fontWeight: 700, opacity: 0.85 }}>{p?.PropType || p?.Prop || "-"}</div>
                        <div style={{ fontWeight: 900 }}>
                          {String(p?.Tag || "").toLowerCase().includes("under") ? "UNDER" : "OVER"}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ opacity: 0.8, fontWeight: 700 }}>
              No lineups yet. Generate from the left panel.
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 10, opacity: 0.85, fontWeight: 800 }}>
            Exposure across <b>{exposure.total}</b> lineups
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Player exposure */}
            <div
              style={{
                border: "1px solid rgba(148,163,184,0.35)",
                borderRadius: 14,
                padding: 10,
                background: darkMode ? "#101826" : "#fff",
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Players</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {exposure.players.slice(0, 20).map((r) => (
                  <div key={r.name} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.name}
                    </div>
                    <div style={{ fontWeight: 900 }}>{r.pct}%</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Team exposure */}
            <div
              style={{
                border: "1px solid rgba(148,163,184,0.35)",
                borderRadius: 14,
                padding: 10,
                background: darkMode ? "#101826" : "#fff",
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Teams</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {exposure.teams.slice(0, 20).map((r) => (
                  <div key={r.name} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontWeight: 800 }}>{r.name}</div>
                    <div style={{ fontWeight: 900 }}>{r.pct}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 10, opacity: 0.7, fontWeight: 700, fontSize: 12 }}>
            (We can add per-prop exposure + DK salary exposure later — this dock design supports it cleanly.)
          </div>
        </div>
      )}
    </div>
  );
};

// SINGLE-SPORT
const handleGenerate = async () => {
  // Clear stale results so we never show previous sport’s lineups
  setGeneratedLineups([]);
  setLineupNotice("");

  const sportKey = String(activeSport || "").toUpperCase();
  if (!basicLineupCanGenerate) {
    setLineupNotice(basicLineupCapability.reason);
    return;
  }

  // If using Cards' filtered pool but the generator sport doesn't match Cards sport, bail early
  const usingFilteredFromCards =
    lineupPool === "filtered" && sportKey === String(activeSport).toUpperCase();
  if (lineupPool === "filtered" && !usingFilteredFromCards) {
    // (optional) toast/snackbar: “Switch Cards to the same sport or change Pool to Full Player Pool”
    return;
  }

  setPreloadBusy(true);
  try {
    // Warm the cache only when not using the already-filtered Cards pool
    if (!usingFilteredFromCards) {
      const hasPool = (propsCache[sportKey]?.length ?? 0) > 0;
      if (!hasPool) {
        await ensureSportInCache(sportKey);
      }
    }

    const maxRequested = Number(maxLineups || 0);
    setShowLineupSection(isNarrow); // mobile uses the bottom results block; desktop uses the main workspace
    setLeftPanelTab("lineup");      // (optional) keeps UI consistent if user is elsewhere

    const generationStartedAt = new Date();
    const generated = generateLineups(maxRequested, generationStartedAt);
    const commitTime = new Date();
    const lineups = generated.filter((lineup) => lineupStillEligible(lineup, { sport: sportKey, now: commitTime }));

    if (lineups.length > 0) {
      setGeneratedLineups(lineups);
      setShowLineupsFocus(true);
      setShowLineupSection(true);
      setLineupsTab("lineups");
      setHideLineupControls(false);
      setLineupNotice(`Generated ${lineups.length} lineups successfully.`);

      if (isNarrow) {
        setTaxonomyOpen(false);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            document.getElementById("pwg-lineups-dock")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          });
        });
      }
    } else {
      setGeneratedLineups([]);
      setShowLineupsFocus(false);
      setShowLineupSection(false);
      setLineupsTab("lineups");
      const allRows = Array.isArray(propsCache[sportKey]) ? propsCache[sportKey] : propsData;
      const selectedRows = lineupPool === "filtered"
        ? filteredProps
        : lineupPool === "guru"
          ? allRows.filter((row) => isInMyList(row))
          : allRows;
      setLineupNotice(lineupEmptyState({
        sport: sportKey,
        allRows,
        selectedRows,
        now: commitTime,
        dataUnavailable: Boolean(propsDataError),
      }));
    }
    if (lineups.length > 0 && lineups.length < maxRequested) {
      setLineupNotice(`Generated ${lineups.length} of ${maxRequested} requested (limited by filters).`);
    }
  } finally {
    setPreloadBusy(false);
  }
};


// ✅ Lineup Generator Control Styling Upgrade
const lineupControlStyles = {
  base: {
    borderRadius: '10px',
    padding: '0.6rem 0.75rem',
    fontSize: '1rem',
    fontWeight: '500',
    border: '1px solid #ccc',
    backgroundColor: '#fefefe',
    color: '#333',
    minWidth: '160px'
  },
  label: {
    fontWeight: '600',
    fontSize: '0.85rem',
    marginBottom: '0.25rem',
    display: 'inline-block'
  }
};

// Render lineup generation section
const renderLineupGenerator = () => {
  // derive the same key your generator uses
  const sportKey = String(lineupSportFilter || activeSport).toUpperCase();

  // if the user chose "Use Current Filters" *and* the generator sport
  // matches the active Cards sport, we can generate from filteredProps.
  const usingFilteredFromCards =
    lineupPool === "filtered" && sportKey === String(activeSport).toUpperCase();

  // user can generate if either:
  // - using current Filters (and there are filtered props), or
  // - the cache for the selected sport already has data
  const canGenerate = usingFilteredFromCards
    ? (filteredProps?.length ?? 0) > 0
    : (propsCache[sportKey]?.length ?? 0) > 0;

  return (
    <div
      style={{
        padding: "1.5rem",
        background: "#f9f9f9",
        borderRadius: "8px",
        marginTop: "2rem",
      }}
    >
      {renderLineupFilters()}

      <button
        onClick={handleGenerate}                 // ← async handler you added
        disabled={preloadBusy || !canGenerate}   // ← guard
        aria-busy={preloadBusy ? "true" : "false"}
        style={{
          padding: "0.75rem 1.5rem",
          background: "black",
          color: "white",
          border: "none",
          borderRadius: "8px",
          marginBottom: "0.5rem",
          opacity: preloadBusy || !canGenerate ? 0.6 : 1,
          cursor: preloadBusy || !canGenerate ? "not-allowed" : "pointer",
        }}
      >
        {preloadBusy ? "Loading…" : "Generate Lineup"}
      </button>

      {/* optional tiny hint while warming the cache */}
      {preloadBusy && (
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: "0.5rem" }}>
          Fetching {sportKey} props…
        </div>
      )}

      {renderLineupResult()}
    </div>
  );
};


  const renderLineupFilters = () => (
    <div className="lineup-basic-controls" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
      <div>
        <label style={lineupControlStyles.label}>Lineup Type</label><br />
        <select style={lineupControlStyles.base} value={effectiveLineupType} onChange={(e) => { const next = normalizeLineupMode(e.target.value, isAdminUser); setLineupType(next); if (next.startsWith("DK_")) setTaxonomyOpen(false); }}>
          {lineupModesForUser(isAdminUser).map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
        </select>
      </div>
      {effectiveLineupType === "PROP" && <>
      <div>
        <label style={lineupControlStyles.label}>Max Lineups</label><br />
        <select style={lineupControlStyles.base} value={maxLineups} onChange={(e) => setMaxLineups(Number(e.target.value))}>
          {[1, 2, 3, 5, 10, 20].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={lineupControlStyles.label}>Lineup Size</label><br />
        <select style={lineupControlStyles.base} value={lineupSize} onChange={(e) => setLineupSize(Number(e.target.value))}>
          {[2, 3, 4, 5, 6, 8, 12].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={lineupControlStyles.label}>Over/Under Mix</label><br />
        <select style={lineupControlStyles.base} value={overUnderPreference} onChange={(e) => setOverUnderPreference(e.target.value)}>
          <option value="Even">Even Mix</option>
          <option value="MoreOvers">More Overs</option>
          <option value="MoreUnders">More Unders</option>
        </select>
      </div>
      <div>
        <label style={lineupControlStyles.label}>Pool</label><br />
        <select
          style={lineupControlStyles.base}
          value={lineupPool}
          onChange={(e) => setLineupPool(e.target.value)}
        >
          <option value="all">Full Player Pool</option>
          <option value="filtered">Use Current Filters</option>
          <option value="guru">My Plays</option>
        </select>
      </div>
      <div className="lineup-basic-sport-desktop" data-testid="basic-lineup-sport" style={lineupControlStyles.base}>
        Sport: <strong>{String(activeSport || "").toUpperCase()}</strong>
      </div>
      </>}
    </div>
  );


  useEffect(() => {
    setSelectedSports((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const allowed = safePrev.filter((sport) => lineupAllowedSports.includes(String(sport).toUpperCase()));
      if (allowed.length) return allowed;
      if (lineupAllowedSports.includes(String(activeSport).toUpperCase())) return [String(activeSport).toUpperCase()];
      return lineupAllowedSports.length ? [lineupAllowedSports[0]] : [];
    });

    if (effectiveLineupType === "PROP") setLineupSportFilter(String(activeSport || "").toUpperCase());
  }, [activeSport, lineupAllowedSports, effectiveLineupType]);


  // Lineup mode toggle UI (Basic/Advanced)
  const renderLineupModeToggle = () => (
    <div style={{ display: "flex", gap: "1rem" }}>
      <button
        onClick={() => setLineupMode("Basic")}
        style={{
          padding: "0.5rem 1rem",
          background: lineupMode === "Basic" ? "#333" : "#eee",
          color: lineupMode === "Basic" ? "white" : "black",
          border: "none",
          borderRadius: "8px"
        }}
      >
        Basic
      </button>
      <button
        onClick={() => setLineupMode("Advanced")}
        style={{
          padding: "0.5rem 1rem",
          background: lineupMode === "Advanced" ? "#333" : "#eee",
          color: lineupMode === "Advanced" ? "white" : "black",
          border: "none",
          borderRadius: "8px"
        }}
      >
        Advanced
      </button>
    </div>
  );
  


  
  // Phase 2: move Lineup Generator controls into the LEFT panel.
  // Main page will show only results (and a small anchor for scrolling).
  const renderLineupLeftPanel = () => (
    <div style={{ padding: 10 }}>
      {renderLineupModeToggle()}

      <div style={{ marginTop: 14 }}>
        {draftKingsModeActive ? (
          <div>
            <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>DraftKings uses the full main workspace. The left rail remains available for navigation.</p>
            <button type="button" onClick={() => setLineupType("PROP")}>Back to PROP generator</button>
          </div>
        ) : lineupMode === "Advanced" ? (
          <>
            {lineupAllowedSports.length === 0 && (
              <div role="status" style={{ marginTop: 10, padding: "0.6rem 0.75rem", borderRadius: 8, background: "#FFFAEB", color: "#92400E", border: "1px solid #FDE68A" }}>
                No available sport currently has a complete upcoming-game lineup pool.
              </div>
            )}
            <AdvancedLineupPanel
              allTags={lineupAllowedTags}
              allGames={gamesForSelected}
              allSports={lineupAllowedSports}
              selectedSports={selectedSports}
              setSelectedSports={setSelectedSports}
              onSportsChange={handleAdvSportsChange}
              advLineupSize={advLineupSize}
              setAdvLineupSize={setAdvLineupSize}
              handleLineupGenerate={handleAdvancedGenerate}
            />

            {lineupNotice && (
              <div
                style={{
                  marginTop: "0.5rem",
                  padding: "0.5rem 0.75rem",
                  borderRadius: 8,
                  background: "#FFFAEB",
                  color: "#92400E",
                  border: "1px solid #FDE68A",
                }}
              >
                ⚠️ {lineupNotice}
              </div>
            )}
          </>
        ) : (
          <div className="lineup-basic-layout" style={{ marginTop: 10 }}>
            {renderLineupFilters()}

            <div className="lineup-basic-final-row">
              <div className="lineup-basic-sport lineup-basic-sport-mobile" data-testid="basic-lineup-sport-mobile" style={lineupControlStyles.base}>
                Sport: <strong>{String(activeSport || "").toUpperCase()}</strong>
              </div>
              <button
                className="lineup-basic-generate"
                onClick={handleGenerate}
                disabled={preloadBusy || !basicLineupCanGenerate}
                aria-busy={preloadBusy ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "0.75rem 1rem",
                  background: "black",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  opacity: preloadBusy || !basicLineupCanGenerate ? 0.7 : 1,
                  cursor: preloadBusy || !basicLineupCanGenerate ? "not-allowed" : "pointer",
                }}
              >
                {preloadBusy ? "Loading…" : "Generate Lineups"}
              </button>
            </div>

            {!basicLineupCanGenerate && (
              <div className="lineup-basic-message" role="status" data-testid="basic-lineup-unavailable" style={{ fontSize: 12, color: "#92400E", marginTop: 8 }}>
                {basicLineupCapability.reason}
              </div>
            )}

            {preloadBusy && (
              <div className="lineup-basic-message" style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                Fetching {String(lineupSportFilter || activeSport).toUpperCase()} props…
              </div>
            )}

            {lineupNotice && (
              <div
                className="lineup-basic-message"
                style={{
                  marginTop: 10,
                  padding: "0.5rem 0.75rem",
                  borderRadius: 8,
                  background: "#FFFAEB",
                  color: "#92400E",
                  border: "1px solid #FDE68A",
                }}
              >
                ⚠️ {lineupNotice}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, opacity: 0.75, fontSize: 12, lineHeight: 1.35 }}>
        Results render in the main page to the right.
      </div>
    </div>
  );

const [lineupTagFilter, setLineupTagFilter] = useState([]);
  const [lineupConfidenceFilter, setLineupConfidenceFilter] = useState([]);
  const [lineupPropTypeFilter, setLineupPropTypeFilter] = useState([]);
  const [lineupMomentumTagFilter, setLineupMomentumTagFilter] = useState([]);
  const [lineupMomentumPatternFilter, setLineupMomentumPatternFilter] = useState([]);
  const [lineupConfirmedMomentumFilter, setLineupConfirmedMomentumFilter] = useState([]);
  const [lineupZGuruTagFilter, setLineupZGuruTagFilter] = useState([]);
  const [lineupGuruPotentialFilter, setLineupGuruPotentialFilter] = useState([]);
  const [lineupGuruConflictFilter, setLineupGuruConflictFilter] = useState([]);





  
  const tagColorMap = {
    "MEGA SMASH": "#0095e0",    // Guru Blue (unchanged)
    "SMASH": "#2e7d32",         // Deep Green
    "LEAN OVER": "#8e24aa",     // Purple
    "MEGA FADE": "#b71c1c",     // Deeper Red (Blood Red)
    "FADE": "#ef6c00",          // Strong Orange (Dark Amber)
    "LEAN UNDER": "#f9a825",    // Yellow (unchanged)
    "COIN TOSS": "#90a4ae",     // Slate Gray (unchanged)
    "BUY": "#2e7d32",           // Deep Green
    "SELL": "#b71c1c",          // Deeper Red (Blood Red)
    "CALLs": "#2e7d32",         // Deep Green
    "PUTs": "#b71c1c",          // Deeper Red (Blood Red)
    "LEAPS":"#0095e0",          // Guru Blue (unchanged)
  };

// Chinese Zodiac (by YEAR) - matches ZODIAC_SIGN in the data
const ZODIAC_META = {
  RAT:     { key: "RAT",     label: "🐀 Rat" },
  OX:      { key: "OX",      label: "🐂 Ox" },
  TIGER:   { key: "TIGER",   label: "🐯 Tiger" },
  RABBIT:  { key: "RABBIT",  label: "🐇 Rabbit" },
  DRAGON:  { key: "DRAGON",  label: "🐉 Dragon" },
  SNAKE:   { key: "SNAKE",   label: "🐍 Snake" },
  HORSE:   { key: "HORSE",   label: "🐎 Horse" },
  GOAT:    { key: "GOAT",    label: "🐐 Goat" },
  MONKEY:  { key: "MONKEY",  label: "🐒 Monkey" },
  ROOSTER: { key: "ROOSTER", label: "🐓 Rooster" },
  DOG:     { key: "DOG",     label: "🐕 Dog" },
  PIG:     { key: "PIG",     label: "🐖 Pig" },
};

const ZODIAC_KEYS = Object.keys(ZODIAC_META);
  

  /*function getTeamLogo(abbr, sport) {
    if (sport === "WNBA" && wnbaTeamLogos?.[abbr]) return wnbaTeamLogos[abbr];
    if (sport === "NBA"  && nbaTeamLogos?.[abbr])  return nbaTeamLogos[abbr];
    if (sport === "MLB"  && mlbTeamLogos?.[abbr])  return mlbTeamLogos[abbr];
    return null;
  }*/
  
// Human-friendly short header for the stat column in the "Last 10 Games" tables.
// We support both your shorthand ("PR") and the full prop strings from All_Picks ("Pts+Rebs").
const nbaPropTypeLabelMap = {
  // your legacy keys
  "R": "REB",
  "PRA": "PRA",
  "PR": "P+R",
  "PA": "P+A",
  "RA": "R+A",
  "3PTM": "3PTM",
  "2PTM": "2PM",
  "2-PT Att": "2-PT Att",
  "Blocks": "Blocks",
  "Steals": "Steals",
  "FTM": "FTM",
  "FTA": "FTA",
  "OREB": "OREB",
  "DREB": "DREB",
  "FS": "Fantasy",
  "FGM": "FGM",
  "FGA": "FGA",
  "PF": "PF",
  "Turnovers": "TO",

  // full prop types coming from All_Picks
  "Points": "PTS",
  "Rebounds": "REB",
  "Rebounds In First 5 Minutes": "REB",
  "Assists": "AST",
  "Blocked Shots": "BLK",
  "Blocks": "BLK",
  "Steals": "STL",
  "Personal Fouls": "PF",
  "Offensive Rebounds": "OREB",
  "Defensive Rebounds": "DREB",
  "3PTM": "3PTM",
  "3-PT Attempted": "3PTA",
  "3PTA": "3PTA",
  "Field Goals Made": "FGM",
  "Fields Goals Made": "FG Made",
  "Field Goals Attempted": "FGA",
  "Free Throws Made": "FTM",
  "Free Throws Attempted": "FTA",
  "Two Pointers Made": "2PM",
  "2-PT Made": "2PM",
  "Two Pointers Attempted": "2PA",
  "2-PT Att": "2PA",
  "Turnovers": "TO",
  "Fantasy Score": "Fantasy",
  "FantasyScore_PP": "Fantasy",
  "Fantasy Score (DK)": "Fantasy",

  // combos
  "Pts+Rebs": "P+R",
  "Points + Rebounds": "P+R",
  "Pts+Asts": "P+A",
  "Points + Assists": "P+A",
  "Rebs+Asts": "R+A",
  "Rebounds + Assists": "R+A",
  "Pts+Rebs+Asts": "PRA",
  "Points + Rebounds + Assists": "PRA",
  "PRA": "PRA",
  "PRA In First 5 Minutes": "PRA",
  "Blks+Stls": "BLK+STL",
  "Blocks + Steals": "BLK+STL",
};

  
const mlbPropTypeLabelMap = {
  "Hits": "Hits",
  "Runs": "Runs",
  "RBIs": "RBIs",
  "Home Runs": "Home Runs",
  "Stolen Bases": "Stolen Bases",
  "SB": "Stolen Bases",
  "Walks": "Walks",
  "Walks Allowed": "Walks Allowed",
  "TB": "Total Bases",
  "Total Bases": "Total Bases",

  "Ks": "Pitcher Strikeouts",
  "Pitcher Strikeouts": "Pitcher Strikeouts",
  "Pitcher Strikeouts (Combo)": "Pitcher Strikeouts",

  "Hitter Ks": "Hitter Strikeouts",

  "Pitcher FS": "Pitcher Fantasy Score",
  "Pitcher Fantasy Score": "Pitcher Fantasy Score",

  "Hitter FS": "Hitter Fantasy Score",
  "Hitter Fantasy Score": "Hitter Fantasy Score",

  "PO": "Pitching Outs",
  "PO": "PO",
  "Pitching Outs": "Pitching Outs",

  "HA": "Hits Allowed",
  "Hits Allowed": "Hits Allowed",

  "Hits+Runs+RBIs": "Hits + Runs + RBIs",
  "HRR": "Hits + Runs + RBIs",

  "ERA": "Earned Runs Allowed",
};

  const nhlPropTypeLabelMap = {
    "SOG": "SOG",                      // Shots on Goal           // alt wording safety
    "Goalie Saves": "Goalie Saves",
    "Saves": "Goalie Saves",
    "Goals Allowed": "Goals Allowed",
    "Goals": "Goals",
    "Assists": "Assists",
    "Points": "Points",
    "Hits": "Hits",
    "Blocked Shots": "Blocks",
    "Blocks": "Blocks",
    "Faceoffs Won": "Faceoffs Won",
    "Time On Ice": "TOI",
    "TOI (min)": "TOI",
    "TOI": "TOI",
  };
  
  

  // Which key should we read in L10 rows for this prop?
// ---- Cross-source helpers (Flask JSON <-> baked JSON) ----

// For a given pick row `p`, figure out what the *stat column name* is for value
// Example results: "Pitcher Strikeouts", "Hitter Fantasy Score", "Total Bases"
// --- L10 helpers (support both Flask fields and baked JSON fields) ---

// Canonicalize the label we expect for this prop
// ---- Cross-source helpers (Flask JSON <-> baked JSON) ----

// Canonical column header for the current prop (reuses your propTypeLabelMap)
function getPropValueKeyFor(p) {
  const t = String(p?.["Prop Type"] || p?.Prop || "").trim();
  return (propTypeLabelMap?.[t] || t).trim();
}

// put this near your other small helpers
// Canonical, sport-agnostic L10 readers
// Coerce safely
// --- Canonical Last10 helpers (single source of truth) ---
// --- Canonical Last-log helpers (single source of truth) --------------------
const coerceArray = (x) =>
  Array.isArray(x) ? x : (x && typeof x === "object" ? Object.values(x) : []);

function getL10Rows(p, { vsOpp = false, max = 10, range = "L10" } = {}) {
  const candidates = vsOpp
    ? [
        p?.Last10vsOpp,
        p?.Last10vsOpp_GameLogs,
        p?.Last10vsOppStats,
        p?.["Last 10 vs Opp"],
        p?.Last10_Vs_Opp,
      ]
    : range === "L20"
    ? [
        p?.Last20,
        p?.Last20_GameLogs,
        p?.Last20Stats,
        p?.["Last 20"],
        p?.L20,
      ]
    : [
        p?.Last10,
        p?.Last10_GameLogs,
        p?.Last10Stats,
        p?.["Last 10"],
        p?.L10,
      ];

  // 1) choose the first non-empty candidate
  let rows = [];
  for (const cand of candidates) {
    const arr = coerceArray(cand).filter(Boolean);
    if (arr.length) {
      rows = arr;
      break;
    }
  }

  // 2) flatten accidental double-wrapped arrays: [[ ... ]]
  if (rows.length === 1 && Array.isArray(rows[0])) {
    rows = rows[0].filter(Boolean);
  }

  // 3) nothing found? stop
  if (!rows.length) return rows;

  // NHL_CUSTOMER_V1 intentionally publishes a bounded numeric evidence vector,
  // not provider game-log objects. Adapt those proven values at presentation
  // time without manufacturing dates, opponents, or current-season context.
  if (String(p?.Sport || "").toUpperCase() === "NHL") {
    const propType = String(p?.["Prop Type"] || "").trim();
    rows = rows.map((entry) => {
      if (entry && typeof entry === "object") return entry;
      const value = Number(entry);
      if (!Number.isFinite(value)) return null;
      const normalized = { Value: value, [propType]: value };
      if (/^(?:time on ice|toi|minutes?)$/i.test(propType)) normalized.MIN = value;
      return normalized;
    }).filter(Boolean);
  }

  // 4) for vsOpp, keep only rows that match current opponent
  if (vsOpp) {
    const oppKey = String(p?.Opponent || p?.["Opponent Name"] || "")
      .trim()
      .toUpperCase();

    if (oppKey) {
      rows = rows.filter((r) => {
        const rOpp = String(
          r?.Opponent ??
          r?.["Opponent Name"] ??
          r?.Opp ??
          r?.OppTeam ??
          ""
        ).trim().toUpperCase();
        return !rOpp || rOpp === oppKey;
      });
    }
  }

  // 5) newest first, then cap
  rows.sort(
    (a, b) =>
      new Date(b?.Date || b?.GameDate || b?.date || 0) -
      new Date(a?.Date || a?.GameDate || a?.date || 0)
  );

  return rows.slice(0, max);
}

function isComboPlayerName(name) {
  return typeof name === "string" && name.includes("+");
}

function splitComboPlayers(name) {
  return String(name || "")
    .split("+")
    .map((s) => s.trim())
    .filter(Boolean);
}

// Find a "normal" row for a given player that actually contains Last10 logs.
// We try: same prop type first, then anything for that player that has Last10.
function findRowWithLogsForPlayer(pool, playerName, preferredPropType) {
  if (!Array.isArray(pool) || !playerName) return null;

  // 1) Prefer same prop type (more likely your logs are present there)
  const exact = pool.find((r) => {
    const p = String(r?.Player || "").trim();
    const pt = String(r?.["Prop Type"] || r?.PropType || r?.Prop || "").trim();
    return p === playerName && (!preferredPropType || pt === preferredPropType) && getL10Rows(r, { max: 10 }).length;
  });
  if (exact) return exact;

  // 2) Otherwise: any row for that player that has logs
  const any = pool.find((r) => {
    const p = String(r?.Player || "").trim();
    return p === playerName && getL10Rows(r, { max: 10 }).length;
  });
  return any || null;
}

// Build chart points for combo props by summing each player's L10 values by index (oldest -> newest).
function buildComboPoints({ pool, comboRow }) {
  const comboName = comboRow?.Player || "";
  const players = splitComboPlayers(comboName);
  if (players.length < 2) return [];

  const propType = comboRow?.["Prop Type"] || comboRow?.PropType || comboRow?.Prop || "";

  const rA = findRowWithLogsForPlayer(pool, players[0], propType);
  const rB = findRowWithLogsForPlayer(pool, players[1], propType);
  if (!rA || !rB) return [];

  // L10 rows newest->oldest from getL10Rows; reverse to oldest->newest for chart
  const a = getL10Rows(rA, { max: 10 }).slice().reverse();
  const b = getL10Rows(rB, { max: 10 }).slice().reverse();

  const n = Math.min(a.length, b.length);
  if (n === 0) return [];

  const pts = [];
  for (let i = 0; i < n; i++) {
    const vA = getL10Value(a[i], propType);
    const vB = getL10Value(b[i], propType);
    const val = (Number.isFinite(vA) ? vA : 0) + (Number.isFinite(vB) ? vB : 0);

    // Dates won't align perfectly (two different teams sometimes), but we still give a readable timeline.
    const dateA = a[i]?.Date || a[i]?.GameDate || a[i]?.date || "";
    const dateB = b[i]?.Date || b[i]?.GameDate || b[i]?.date || "";
    const date = dateA || dateB || "";

    pts.push({
      date,
      matchup: `Combo: ${players[0]} + ${players[1]}`,
      value: val,
    });
  }

  return pts;
}


// short form last 10 and last 10 vs headers
// Abbreviate long prop headers for table columns
function shortPropHeader(longLabel) {
  const s = (longLabel || "").toLowerCase();

  // combos first
  if (s.includes("points + rebounds + assists") || s === "pra") return "PRA";
  if (s.includes("points + rebounds"))          return "P+R";
  if (s.includes("points + assists"))           return "P+A";
  if (s.includes("rebounds + assists"))         return "R+A";

  // made/attempted props must be checked before generic "points"
  if (s.includes("2-pt") && (s.includes("att") || s.includes("attempt"))) return "2PA";
  if (s.includes("2pt") && (s.includes("att") || s.includes("attempt"))) return "2PA";
  if (s.includes("two pointer") && (s.includes("att") || s.includes("attempt"))) return "2PA";
  if (s.includes("2-pt") && s.includes("made")) return "2PM";
  if (s.includes("2pt") && s.includes("made")) return "2PM";
  if (s.includes("two pointer") && s.includes("made")) return "2PM";

  if ((s.includes("3-pt") || s.includes("3pt") || s.includes("three pointer")) && (s.includes("att") || s.includes("attempt"))) return "3PA";
  if ((s.includes("3-pt") || s.includes("3pt") || s.includes("three pointer")) && s.includes("made")) return "3PM";

  // single stats
  if (s.includes("three pointers"))   return "3PM";
  if (s.includes("points"))           return "PTS";
  if (s.includes("rebounds"))         return "REB";
  if (s.includes("assists"))          return "AST";
  if (s.includes("steals"))           return "STL";
  if (s.includes("blocks"))           return "BLK";
  if (s.includes("fantasy"))          return "FS";

  // generic fallback: collapse spaces & pluses a bit
  return longLabel?.replaceAll(" + ", "+") ?? "Stat";
}
// short form last 10 and last 10 vs headers



// maps whatever the prop card calls it → the column name in Last10Stats rows
const PROP_COLUMN_MAP__ = {
  // NBA-style / what Python now outputs in Last10Stats rows:
  "Points": "Points",
  "Rebounds": "Rebounds",
  "Assists": "Assists",
  "Blocks": "Blocks",
  "Steals": "Steals",
  "Turnovers": "Turnovers",
  "Field Goals Made": "Field Goals Made",
  "Field Goals Attempted": "Field Goals Attempted",
  "FG Made": "Field Goals Made",
  "FG Attempted": "Field Goals Attempted",
  "3PT Made": "3PT Made",
  "3PT Attempted": "3PT Attempted",
  "3PTA": "3PT Attempted",
  "3PTM": "3PT Made",
  "2-PT Made": "2-PT Made",
  "2-PT Att": "2-PT Att",
  "Free Throws Made": "Free Throws Made",
  "Free Throws Attempted": "Free Throws Attempted",
  "FTM": "Free Throws Made",
  "FTA": "Free Throws Attempted",
  "Offensive Rebounds": "Offensive Rebounds",
  "Defensive Rebounds": "Defensive Rebounds",
  "Personal Fouls": "Personal Fouls",
  "Blks+Stls": "Blks+Stls",
  "PRA": "PRA",
  "Pts+Rebs": "Pts+Rebs",
  "Pts+Asts": "Pts+Asts",
  "Rebs+Asts": "Rebs+Asts",
  "Fantasy Score": "FantasyScore_PP",
  "FantasyScore_PP": "FantasyScore_PP",

  // shorthand / legacy prop labels from All_Picks:
  "Pts+Rebs+Asts": "PRA",
  "Pts+Asts": "Pts+Asts",
  "Pts+Rbs": "Pts+Rebs",
  "Rebs+Asts": "Rebs+Asts",
  "Blk+Stl": "Blks+Stls",
  "Blks+Stls": "Blks+Stls",
  "FS": "FantasyScore_PP",
  "PRA ": "PRA", // sometimes trailing space

  // ---------- NHL ----------
  // ---------- NHL ----------
  "SOG": "SOG",                          // Shots On Goal
  "Shots On Goal": "SOG",
  "Shots on Goal": "SOG",

  "Hits": "Hits",

  // Blocks / Blocked Shots
  "Blocks": "Blocks",
  "BLK": "Blocks",                       // if your Last10 rows use "BLK"
  "Blocked Shots": "Blocks",
  "Shots Blocked": "Blocks",

  "Goals": "Goals",
  "Assists": "Assists",                  // same label works for NHL too
  "Points": "Points",                    // same
  "PTS": "PTS",
  "Power Play Points": "Power Play Points",
  "PPP": "Power Play Points",           // common shorthand
  "Saves": "Saves",                      // goalie saves
  "Time On Ice": "Time On Ice",
  "TOI (min)": "TOI (min)",

  "__SBH__": "__SBH__",                  // computed SOG+Blocks+Hits

};



// --- normalize a prop label to a canonical lookup key
function normPropType(label) {
  const s = String(label || "").trim().toLowerCase();

  // NBA (kept from your current map)
  if (["fs","fantasy","fantasy score","fantasyscore_pp"].includes(s)) return "FantasyScore_PP";
  if (["pra","pts+rebs+asts","pts+rebs+ast","pts+ast+reb"].includes(s)) return "PRA";
  if (["pts+rebs","pts+rbs"].includes(s)) return "Pts+Rebs";
  if (["pts+asts","pts+ast"].includes(s)) return "Pts+Asts";
  if (["rebs+asts","rbs+ast"].includes(s)) return "Rebs+Asts";
  if (["blk+stl","blocks+steals","stocks","stl+blk"].includes(s)) return "Blks+Stls";

  // NHL
  if (["sog","shots on goal","shots-on-goal","shots"].includes(s)) return "SOG";
  if (["hits"].includes(s)) return "Hits";
  if (["blocks","blocked shots","blk","blocked-shots"].includes(s)) return "Blocks";
  if (["goals","g"].includes(s)) return "Goals";
  if (["assists","a"].includes(s)) return "Assists";
  if (["points","pts"].includes(s)) return "Points";
  if (["power play points","pp points","ppp","power-play-points"].includes(s)) return "Power Play Points";
  if (["saves","goalie saves","gs"].includes(s)) return "Saves";
  if (["sog+blocks+hits","shots+blocks+hits","s+b+h","sbh"].includes(s)) return "__SBH__"; // computed if needed

  // fallback: title-case the string so it can match row keys like "Field Goals Made"
  return label;
}

// Map canonical key -> column name in Last10 row (when a single column exists)
const PROP_COLUMN_MAP = {
  // ---------- NBA (your existing keys) ----------
  "Points": "Points",
  "Rebounds": "Rebounds",
  "Assists": "Assists",
  "Blocks": "Blocks",
  "Steals": "Steals",
  "Turnovers": "Turnovers",
  "Field Goals Made": "Field Goals Made",
  "Field Goals Attempted": "Field Goals Attempted",
  "FG Made": "Field Goals Made",
  "FG Attempted": "Field Goals Attempted",
  "3PT Made": "3PT Made",
  "3PT Attempted": "3PT Attempted",
  "3PTA": "3PT Attempted",
  "3PTM": "3PT Made",
  "2-PT Made": "2-PT Made",
  "2-PT Att": "2-PT Att",
  "Free Throws Made": "Free Throws Made",
  "Free Throws Attempted": "Free Throws Attempted",
  "FTM": "Free Throws Made",
  "FTA": "Free Throws Attempted",
  "Offensive Rebounds": "Offensive Rebounds",
  "Defensive Rebounds": "Defensive Rebounds",
  "Personal Fouls": "Personal Fouls",
  "Blks+Stls": "Blks+Stls",
  "PRA": "PRA",
  "Pts+Rebs": "Pts+Rebs",
  "Pts+Asts": "Pts+Asts",
  "Rebs+Asts": "Rebs+Asts",
  "Fantasy Score": "FantasyScore_PP",
  "FantasyScore_PP": "FantasyScore_PP",

  // ---------- NHL ----------
  "SOG": "SOG",                          // Shots On Goal
  "Hits": "Hits",
  "Blocks": "Blocks",
  "Goals": "Goals",
  "Assists": "Assists",                  // same label works for NHL too
  "Points": "Points",                    // same
  "Power Play Points": "Power Play Points",
  "Saves": "Saves",                      // goalie saves
  "__SBH__": "__SBH__",                  // computed if not present
};

// ---------- SORT NORMALIZATION ----------

const normalizeNumber = (v) => {
  if (v === null || v === undefined) return null;
  if (v === "—" || v === "") return null;
  if (typeof v === "string") {
    const n = Number(v.replace("+", ""));
    return Number.isFinite(n) ? n : null;
  }
  return Number.isFinite(v) ? v : null;
};

const MINUTES_HEAT_RANK = {
  HOT: 2,
  WATCH: 1,
};

const normalizeMinutesHeat = (v) => {
  if (!v || v === "—") return 0;
  return MINUTES_HEAT_RANK[v] ?? 0;
};


// Robust extractor with fallbacks + computed SBH
function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function getMinutesValue(row, activeSport) {
  const sport = String(activeSport || "").toUpperCase();

  // MLB does not use minutes in L10 details
  if (sport === "MLB" || sport === "KBO") return null;

  for (const key of ["MIN", "Minutes", "minutes", "mins"]) {
    const n = safeNum(row?.[key]);
    if (n !== null) return n;
  }

  return null;
}

function getL10Value(row, propType, activeSport = "") {
  if (!row) return null;

  const sport = String(activeSport || "").toUpperCase();
  const rawProp = String(propType || "").trim();

  if (sport === "NBA") return resolveNbaHistoricalStat(row, rawProp);

  if (sport === "MLB") return mlbHistoricalValue(row, rawProp);

  if (sport === "SOCCER") {
    return safeNum(row?.["Selected Stat Actual"] ?? row?.Actual);
  }

  if (sport === "NFL" || sport === "CFB") {
    return safeNum(row?.["Selected Stat Actual"] ?? row?.actual);
  }

  // ---------------- BASEBALL: MLB / KBO ----------------
  if (sport === "MLB" || sport === "KBO") {
    const pt = rawProp;

    const runs = safeNum(row?.runs) ?? 0;
    const doubles = safeNum(row?.doubles) ?? 0;
    const triples = safeNum(row?.triples) ?? 0;
    const homeRuns = safeNum(row?.homeRuns) ?? 0;
    const strikeOuts = safeNum(row?.strikeOuts) ?? 0;
    const baseOnBalls = safeNum(row?.baseOnBalls) ?? 0;
    const hits = safeNum(row?.hits) ?? 0;
    const hitByPitch = safeNum(row?.hitByPitch) ?? 0;
    const totalBases = safeNum(row?.totalBases) ?? 0;
    const rbi = safeNum(row?.rbi) ?? 0;
    const singles = safeNum(row?.singles) ?? 0;
    const stolenBases = safeNum(row?.stolenBases) ?? 0;

    const inningsPitched = safeNum(row?.inningsPitched);
    const outs = safeNum(row?.outs);
    const earnedRuns = safeNum(row?.earnedRuns) ?? 0;
    const hitsAllowed = safeNum(row?.hits) ?? 0;

    const ppFantasy = safeNum(row?.PP_Fantasy);
    const dkFantasy = safeNum(row?.DK_Fantasy);
    const fdFantasy = safeNum(row?.FanDuel_Fantasy);

    switch (pt) {
      case "Hits":
        return hits;
      case "Runs":
        return runs;
      case "RBIs":
        return rbi;
      case "Hits+Runs+RBIs":
        return hits + runs + rbi;
      case "Singles":
        return singles;
      case "Doubles":
        return doubles;
      case "Home Runs":
        return homeRuns;
      case "Stolen Bases":
      case "SB":
        return stolenBases;
      case "Walks":
        return baseOnBalls;
      case "Hitter Strikeouts":
      case "Hitter Ks":
        return strikeOuts;
      case "Total Bases":
        return totalBases;
      case "Hitter Fantasy Score":
      case "Hitter FS":
        return ppFantasy ?? dkFantasy ?? fdFantasy;

      case "Ks":
      case "Pitcher Strikeouts":
      case "Pitcher Strikeouts (Combo)":
        return strikeOuts;
      case "Walks Allowed":
      case "1st Inning Walks Allowed":
        return baseOnBalls;
      case "Hits Allowed":
        return hitsAllowed;
      case "Earned Runs Allowed":
      case "ERA":
        return earnedRuns;
      case "Pitching Outs":
      case "PO":
        if (outs !== null) return outs;
        if (inningsPitched !== null) return inningsPitched * 3;
        return null;
      case "Pitcher Fantasy Score":
      case "Pitcher FS":
        return ppFantasy ?? dkFantasy ?? fdFantasy;

      default:
        return null;
    }
  }

  // ---------------- NBA / WNBA / CBB / etc ----------------
  const pt = String(rawProp).trim();

  // PrizePicks currently names this WNBA market `3-PT Attempted`, while the
  // historical payload stores the authoritative value as `3PA`. Resolve this
  // family explicitly and preserve null (unknown) versus a genuine zero.
  if (sport === "WNBA" || sport === "WNBAB") {
    const threePointAttempts = getWnbaThreePointAttempts(row, pt);
    if (threePointAttempts.applicable) return threePointAttempts.value;
  }

  // 1) direct stat read first
  const direct = readStat(row, pt);
  if (Number.isFinite(direct)) return direct;

  // 2) common label aliases -> canonical labels
  const canonicalMap = {
    "PRA": "Points + Rebounds + Assists",
    "PTS+REBS": "Points + Rebounds",
    "PTS+ASTS": "Points + Assists",
    "REBS+ASTS": "Rebounds + Assists",

    "FG ATTEMPTED": "FG Attempted",
    "FIELD GOAL ATTEMPTED": "FG Attempted",
    "FIELD GOALS ATTEMPTED": "FG Attempted",

    "FG MADE": "Field Goals Made",
    "FIELD GOAL MADE": "Field Goals Made",
    "FIELD GOALS MADE": "Field Goals Made",

    "3PTA": "Three Pointers Attempted",
    "3-PT ATT": "Three Pointers Attempted",
    "3-PT ATTEMPTED": "Three Pointers Attempted",
    "THREE POINTERS ATTEMPTED": "Three Pointers Attempted",

    "3PTM": "Three Pointers Made",
    "3-PT MADE": "Three Pointers Made",
    "3PT MADE": "Three Pointers Made",
    "THREE POINTERS MADE": "Three Pointers Made",

    "2-PT MADE": "Two Pointers Made",
    "2PT MADE": "Two Pointers Made",
    "2PTM": "Two Pointers Made",
    "2PM": "Two Pointers Made",
    "TWO POINTERS MADE": "Two Pointers Made",

    "2-PT ATT": "Two Pointers Attempted",
    "2-PT ATTEMPTED": "Two Pointers Attempted",
    "2PT ATT": "Two Pointers Attempted",
    "2PT ATTEMPTED": "Two Pointers Attempted",
    "2PTA": "Two Pointers Attempted",
    "2PA": "Two Pointers Attempted",
    "TWO POINTERS ATTEMPTED": "Two Pointers Attempted",

    "DOUBLE-DOUBLE": "Double-Double",
    "DOUBLE DOUBLE": "Double-Double",
    "DD": "Double-Double",

    "TRIPLE-DOUBLE": "Triple-Double",
    "TRIPLE DOUBLE": "Triple-Double",
    "TD": "Triple-Double",
    
  };

  const canonical = canonicalMap[pt.toUpperCase()] || pt;

  // 3) try the canonical stat label before deriving.
  // This is important for WNBA 2-PT/3-PT rows that already export 2PA/2PM/3PA/3PM.
  const canonicalDirect = readStat(row, canonical);
  if (Number.isFinite(canonicalDirect)) return canonicalDirect;

  // 4) derived props
  const derived = computeDerived(row, canonical);
  if (Number.isFinite(derived)) return derived;

  return null;
}




// result vs line
function compareToLine(val, p) {
  const value = Number(val);
  const line = Number(p?.["Prop Value"] ?? p?.Value);
  if (val === null || val === undefined || !Number.isFinite(value) || !Number.isFinite(line)) return null;
  if (value > line) return 1;
  if (value < line) return -1;
  return 0;
}

// tag helpers you already rely on
function isOverTag(tag) {
  const t = String(tag || "").toUpperCase();
  return t === "MEGA SMASH" || t === "SMASH" || t === "LEAN OVER";
}
function isUnderTag(tag) {
  const t = String(tag || "").toUpperCase();
  return t === "MEGA FADE" || t === "FADE" || t === "LEAN UNDER";
}

// row background
function rowResultStyle(val, p) {
  const contractStyle = historicalResultStyle(val, p?.["Prop Value"] ?? p?.Value);
  if (contractStyle.outcome === "UNAVAILABLE") return { background: "transparent" };
  //const wantsOver = isOverTag(p.Tag);
  const wantsOver = true; //we set to true becasue we wnat the row to be highlight green for ever hit regardless of tag.
  const cmp  = compareToLine(val, p);
  const even = cmp === 0;
  const won  = wantsOver ? cmp > 0 : cmp < 0;

  if (even) return { background: '#FDE68A' };   // deeper amber
  return won ? { background: '#BBF7D0' }        // deeper green
             : { background: '#FECACA' };       // deeper red
}


  //end additinal helpers

  const soccerPropTypeLabelMap = {
  
  };

  const propTypeLabelMap =
  activeSport === "NBA"
    ? nbaPropTypeLabelMap
    : activeSport === "MLB"
    ? mlbPropTypeLabelMap
    : activeSport === "NHL"
    ? nhlPropTypeLabelMap
    : {};

  const customerPropTypeLabel = (value, sport = activeSport) => {
    const raw = String(value || "").trim();
    if (String(sport || "").toUpperCase() === "NHL") return nhlDisplayPropType(raw);
    return propTypeLabelMap?.[raw] || raw;
  };



    // Loose aliases from card label → possible L10 column names
  // Put near your other helpers (top-level in PropsDashboard.jsx)
/** ------------------------------------------------------------------
 *  L10 value resolver — supports direct cols, aliases, and derived stats
 *  Paste this near your other helpers, above where it’s used.
 *  It reuses your existing getPropValueKeyFor(p) and propTypeLabelMap.
 * ------------------------------------------------------------------**/

// ---------- Aliases for single-box stats (case/spacing-insensitive checked) ----------
// ---------- L10 helpers (robust) ----------
// --- Singles (canonical = human-friendly label) ---
const STAT_ALIASES = {
  "Points": ["PTS", "Point"],
  "Rebounds": ["REB"],
  "Assists": ["AST"],
  "Blocks": ["BLK", "Blocked Shots"],
  "Steals": ["STL"],
  "Turnovers": ["TOV", "TO"],

  "Field Goals Made": ["FGM", "FG Made"],
  "FG Attempted": ["FGA", "FG Attempted", "Field Goals Attempted"],

  "Three Pointers Made": ["3PTM", "3PM", "3PT Made", "3-PT Made"],
  "Three Pointers Attempted": ["3PA", "3PT Attempted", "3-PT Attempted"],

  "2-PT Made": ["2PM", "2PTM", "2PT Made", "2-PT Made", "Two Pointers Made", "fieldGoalsMadeMinusThrees"],
  "2-PT Att": ["2PA", "2PTA", "2PT Att", "2-PT Att", "2PT Attempted", "2-PT Attempted", "Two Pointers Attempted", "fieldGoalsAttemptedMinusThrees"],
  "2-PT Attempted": ["2PA", "2PTA", "2PT Att", "2-PT Att", "2PT Attempted", "2-PT Attempted", "Two Pointers Attempted", "fieldGoalsAttemptedMinusThrees"],
  "3PTM": ["3PTM", "3PM", "3PT Made", "3-PT Made", "Three Pointers Made"],
  "3PTA": ["3PA", "3PT Attempted", "3-PT Attempted", "Three Pointers Attempted"],
  "Two Pointers Made": ["2PM", "2PTM", "2PT Made", "2-PT Made", "Two Pointers Made", "fieldGoalsMadeMinusThrees"],
  "Two Pointers Attempted": ["2PA", "2PTA", "2PT Att", "2-PT Att", "2PT Attempted", "2-PT Attempted", "Two Pointers Attempted", "fieldGoalsAttemptedMinusThrees"],

  "Offensive Rebounds": ["OREB", "OffensiveReb", "Off Reb"],
  "Defensive Rebounds": ["DREB", "DefensiveReb", "Def Reb"],

  "Free Throws Made": ["FTM"],
  "Free Throws Attempted": ["FTA"],

  // PrizePicks / DK fantasy columns we’ve seen
  "Fantasy Score": ["FantasyScore_PP", "FantasyScore", "FPTS", "Fantasy_Points", "FantasyScore_DK"],

  // ✅ FIELD GOALS
  "FG Attempted": ["fieldGoalsAttempted", "fga", "FGA"],
  "Field Goals Attempted": ["fieldGoalsAttempted", "fga", "FGA"],
  "FG Att": ["fieldGoalsAttempted", "fga", "FGA"],

  "Field Goals Made": ["fieldGoalsMade", "fgm", "FGM"],
  "FG Made": ["fieldGoalsMade", "fgm", "FGM"],

  // ✅ FREE THROWS
  "Free Throws Made": ["freeThrowsMade", "ftm", "FTM"],
  "FTM": ["freeThrowsMade", "ftm", "FTM"],

  "Free Throws Attempted": ["freeThrowsAttempted", "fta", "FTA"],
  "FTA": ["freeThrowsAttempted", "fta", "FTA"],

  // (optional but good coverage)
  "FT Attempted": ["freeThrowsAttempted", "fta", "FTA"],
};

// --- Combos (canonical = human-friendly) ---
const COMBO_ALIASES = {
  "Points + Rebounds":           ["PTS+REB", "PR", "Pts+Rebs"],
  "Points + Assists":            ["PTS+AST", "PA", "Pts+Ast"],
  "Rebounds + Assists":          ["REB+AST", "RA", "Reb+Ast"],
  "Points + Rebounds + Assists": ["PTS+REB+AST", "PRA", "Pts+Rebs+Asts"],
};

// normalize a header: lowercase, strip non-letters/digits
const normKey = k => String(k || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// index row once for fast lookups
function buildRowIndex(row) {
  const idx = {};
  for (const [k, v] of Object.entries(row || {})) idx[normKey(k)] = v;
  return idx;
}

// try a list of keys (with aliases) against a row (case/space/punct-insensitive)
function pick(row, keys) {
  const idx = buildRowIndex(row);
  for (const k of keys) {
    const v = idx[normKey(k)];
    if (v !== null && v !== undefined && Number.isFinite(+v)) return +v;
  }
  return null;
}

function readStat(row, canonicalLabel) {
  const aliases = STAT_ALIASES[canonicalLabel] || [];
  return pick(row, [canonicalLabel, ...aliases]);
}

const sum = (...xs) => xs.every(Number.isFinite) ? xs.reduce((a,b)=>a+b,0) : null;

function computeDerived(row, canonicalLabel) {
  const pts = readStat(row, "Points");
  const reb = readStat(row, "Rebounds");
  const ast = readStat(row, "Assists");
  const stl = readStat(row, "Steals");
  const blk = readStat(row, "Blocks");

  const fgm = readStat(row, "Field Goals Made");
  const fga = readStat(row, "FG Attempted");
  const tpm = readStat(row, "Three Pointers Made");
  const tpa = readStat(row, "Three Pointers Attempted");

  const tenPlusCount = [pts, reb, ast, stl, blk].filter(
    (v) => Number.isFinite(v) && v >= 10
  ).length;

  switch (canonicalLabel) {
    case "Points + Rebounds":
      return sum(pts, reb);

    case "Points + Assists":
      return sum(pts, ast);

    case "Rebounds + Assists":
      return sum(reb, ast);

    case "Points + Rebounds + Assists":
      return sum(pts, reb, ast);

    case "Two Pointers Made":
      return Number.isFinite(fgm) && Number.isFinite(tpm) ? (fgm - tpm) : null;

    case "Two Pointers Attempted":
      return Number.isFinite(fga) && Number.isFinite(tpa) ? (fga - tpa) : null;

    case "Double-Double":
      return tenPlusCount >= 2 ? 1 : 0;

    case "Triple-Double":
      return tenPlusCount >= 3 ? 1 : 0;

    default:
      return null;
  }
}


// For table header abbreviation
const getPropAbbr = (label) => {
  const entries = Object.entries(propTypeLabelMap); // abbr -> human
  const match = entries.find(([, full]) => full === label);
  return match ? match[0] : label;
};




// --- Projection parser fix ---
const getProjectedValue = (p = {}) => {
  // Try all possible keys safely, preferring DvP-adjusted projections
  const candidates = [
    p["AdjustedScore"],                 // new DvP-adjusted projection (primary)
    p["DvP_AdjustedScore"],             // alternate key, if you rename later
    p["FinalAdjustedScore"],            // existing final projection
    p["Final Projection"],
    p[`Projected_${p["Prop Type"]}_Boosted`],
    p[`Projected_${p["Prop Type"]}`],
    p["Projection"],
  ];

  for (const raw of candidates) {
    if (raw === undefined || raw === null || raw === "") continue;
    const n = parseFloat(raw);
    if (Number.isFinite(n)) return n;
  }
  return null; // nothing valid found
};

const formatSelectionStrategy = (p = {}) => {
  const dir = (p["SS_Direction"] || p["SelectionStrategy"] || "").toString();
  const over = Number(p["SS_OverCount"] ?? 0);
  const under = Number(p["SS_UnderCount"] ?? 0);
  const neutral = Number(p["SS_NeutralCount"] ?? 0);

  if (!dir && over === 0 && under === 0 && neutral === 0) {
    return null;
  }

  const prettyDir = dir || "NO CLEAR EDGE";
  // Example: "OVER (3 / 0 / 1)" = 3 metrics lean Over, 0 Under, 1 Neutral
  const breakdown = `${over}/${under}/${neutral}`;

  return {
    label: prettyDir,
    breakdown, // you can show or hide this
  };
};





  const isNumber = val => !isNaN(parseFloat(val)) && isFinite(val);


  useEffect(() => {
    if (!propsData || propsData.length === 0) return;
  
    const gameTimeMap = {};
  
    const gameInventoryRows = String(activeSport || "").toUpperCase() === "NHL" && governedNhlGames.length
      ? governedNhlGames.map((game) => ({
          Team: game["Away Team"], Opponent: game["Home Team"], Sport: "NHL",
          "Game ID": game["Game ID"], "Game Start Timestamp": game["Game Start Timestamp"],
        }))
      : propsData;
    const allGames = gameInventoryRows
      .filter(p =>
        p.Team &&
        p.Opponent &&
        p.Team.toLowerCase() !== "nan" &&
        p.Opponent.toLowerCase() !== "nan" &&
        (selectedSports.includes(p.Sport || activeSport) || (
          String(activeSport || "").toUpperCase() === "SOCCER" &&
          String(p.Sport || "").toUpperCase() === "SOCCER"
        )) &&
        !String(p.Player || "").includes(" + ")
      )
      .map(p => {
        const teams = [p.Team, p.Opponent].sort();
        const key = multiSportMode
          ? `${teams[0]} vs ${teams[1]} (${p.Sport})`
          : `${teams[0]} vs ${teams[1]}`;
  
        if (!gameTimeMap[key] && (p["Game Start Timestamp"] || p["Start Time"] || p.GameTime || p["Game Time"])) {
          gameTimeMap[key] = formatLocalEventTime(p, activeSport);
        }
  
        return key;
      });
  
    setUniqueGames([...new Set(allGames)].sort());
    setGameTimes(gameTimeMap);
  
  }, [propsData, governedNhlGames, selectedSports, multiSportMode, activeSport]);

const CommentaryModal = () => (
  showCommentary ? (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundColor: "white", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "600px", boxShadow: "0 0 10px rgba(0,0,0,0.25)" }}>
  
        <p style={{ fontSize: "1rem", lineHeight: 1.4 }}>{selectedCommentary}</p>
        <button onClick={() => setShowCommentary(false)} style={{ marginTop: "1rem", padding: "0.5rem 1rem", borderRadius: "6px", backgroundColor: "#3498db", color: "white", border: "none" }}>Close</button>
      </div>
    </div>
  ) : null
);

const shouldShowProps = !activeSportAvailability.disabled && !draftKingsModeActive && !(showLineupsFocus && ((generatedLineups?.length ?? 0) > 0 || (draftKingsResult?.result?.lineups?.length ?? 0) > 0))
  && ["NBA","NBB","KBO","AUSNBL","WNBA","WNBAB","MLB","MLBB","KBO","NFL","NHL","SOCCER","MMA","CFB","CBB","FPA","SACB","BBL","LBSA","GBL","EURO","TEG"].includes(activeSport);
  const soccerMatchIntelligenceActive = activeSport === "SOCCER" && activeView === "match-intelligence";
  const receiveSoccerMatchBoard = useCallback((cards) => {
    const safeCards = Array.isArray(cards) ? cards : [];
    setSoccerMatchCards(safeCards);
    // Null is the explicit All Games view.  Retain it so the customer lands on
    // the slate's game cards rather than silently opening the first fixture.
    setSelectedSoccerMatchId((current) => current === null || safeCards.some((card) => card?.match?.match_id === current) ? current : null);
  }, []);


  const visibleRecommendationTags = useMemo(
    () => recommendationTagsForDirection(CURRENT_TAGS, selectedDirection),
    [CURRENT_TAGS, selectedDirection]
  );

  const handleDirectionSelection = useCallback((direction) => {
    const preset = isNarrow
      ? toggleDirectionPreset(selectedDirection, direction)
      : directionPreset(direction);
    setSelectedDirection(preset.direction);
    setSelectedTags(preset.selectedTags);
    directionPresetActiveRef.current = preset.direction !== "ALL";
  }, [isNarrow, selectedDirection]);

  const toggleTag = useCallback((tag) => {
    setSelectedTags(prev => toggleCompatibleRecommendation(prev, tag, selectedDirection));
  }, [selectedDirection]);
  
  const uniquePropTypes = useMemo(() => {
       const activeSportKey = String(activeSport || "").toUpperCase();
       const backtestMode = isBacktestSportKey(activeSportKey);
       const activeSportFamily = backtestMode
         ? activeSportKey.replace(/B(?:ACKTEST)?$/, "")
         : activeSportKey;

       const readPropType = (row) => dashboardCanonicalPropType(
         row?.["Prop Type"] ??
         row?.PropType ??
         row?.Prop ??
         row?.prop_type ??
         row?.Prop_Type ??
         row?.market ??
         row?.Market ??
         row?.stat_type ??
         row?.StatType ??
         ""
       );

       const filterSource = isAdminUser ? (propsData || []) : entitlementVisibleProps;
       const rowsForSport = filterSource.filter((row) => {
         // Backtest JSON like WNBAB can keep row.Sport as WNBA. Treat WNBAB as the WNBA family.
         if (backtestMode) return true;
         const rowSport = String(row?.Sport || activeSportKey).toUpperCase();
         return rowSport === activeSportKey;
       });

       const cleanTypes = (rows) => [...new Set(
         rows
           .map(readPropType)
           .filter(Boolean)
           .filter(pt => activeSportFamily !== "WNBA" || !isUnsupportedWNBAPropType(pt))
       )].sort((a, b) => String(a).localeCompare(String(b)));

      if (activeSportFamily === "MLB") {
         if (playerTypeFilter === "Batter") {
           return cleanTypes(rowsForSport.filter(p => p["Player Type"] === "Batter"));
         } else if (playerTypeFilter === "Pitcher") {
           return cleanTypes(rowsForSport.filter(p => p["Player Type"] === "Pitcher"));
         }
       }

       const types = cleanTypes(rowsForSport);

       // Safety fallback: if a backtest file has missing/mismatched Sport fields, still expose
       // prop filters from whatever rows are currently loaded.
       if (backtestMode && types.length === 0) {
         return cleanTypes(propsData || []);
       }

       return types;
     }, [propsData, entitlementVisibleProps, activeSport, playerTypeFilter, isAdminUser]);

const props = Array.isArray(propsCache[selectedSports[0]]) ? propsCache[selectedSports[0]] : [];

const uniqueOppPitcherTiers = [...new Set(props.map(p => p.opp_pitcher_tier).filter(Boolean))];
const uniqueOppAttackability = [...new Set(props.map(p => p.opp_pitcher_attackability).filter(Boolean))];
const uniqueOppWhipTiers = [...new Set(props.map(p => p.opp_whip_tier).filter(Boolean))];
const uniqueOppKpctTiers = [...new Set(props.map(p => p.opp_kpct_tier).filter(Boolean))];
const uniqueTeamWhipTiers = [...new Set(props.map(p => p.team_whip_tier).filter(Boolean))];
const uniqueTeamKpctTiers = [...new Set(props.map(p => p.team_kpct_tier).filter(Boolean))];
const uniqueTeamPitcherTiers = [...new Set(props.map(p => p.team_pitcher_tier).filter(Boolean))];
const uniqueTeamAttackability = [...new Set(props.map(p => p.team_pitcher_attackability).filter(Boolean))];


// --- TEG: simple tag→color (reuse your player colors if you already have a map)
const tegTagColor = (tag = "") => {
  const t = String(tag).toUpperCase();
  if (t.includes("BUY"))  return "#1f7a1f";
  if (t.includes("SELL")) return "#b91c1c";
  if (t.includes("HOLD")) return "#374151";
  if (t.includes("CALL")) return "#1d4ed8";
  if (t.includes("PUT"))  return "#b45309";
  if (t.includes("LEAP")) return "#0e7490";
  return "#6b7280";
};

const handleDashboardOpenTopPlay = (row) => {
  setActiveView("props");
  setTimeout(() => {
    handleOpenGridDetails(row, sortedTierFilteredPool);
  }, 0);
};


const handleDashboardDrillDown = ({ type, value }) => {
  clearAllFilters();

  switch (type) {
    case "risk":
      setSelectedRisk([value]);
      break;

    case "tagDirection":
      if (value === "OVERS") {
        setSelectedTags(["MEGA SMASH", "SMASH", "LEAN OVER"]);
      } else if (value === "UNDERS/FADES") {
        setSelectedTags(["MEGA FADE", "FADE", "LEAN UNDER"]);
      } else if (value === "COIN TOSS") {
        setSelectedTags(["COIN TOSS"]);
      }
      break;

    case "propType":
      setSelectedPropTypes([value]);
      break;

    case "l5":
      setSelectedL5Shapes([value]);
      break;

    case "dvp":
      setDvpTierFilter(value);
      break;

    case "guruAlert":
      if (value === "ALERT") setGuruAlert("2");
      else if (value === "WATCH") setGuruAlert("1");
      else if (value === "NONE") setGuruAlert("0");
      break;

    default:
      break;
  }

  setActiveView("props");
};

  // --- Helpers to turn numeric TEG fields into display strings ---

// --- TEG: one ticker card, styled like your player cards (title on left, pill on right)
function TEGCard({ row }) {
  const [expanded, setExpanded] = React.useState(false);
  const [momentumView, setMomentumView] = React.useState("numeric"); // "numeric" | "label"
  const [showPlayDetails, setShowPlayDetails] = React.useState(false); // 👈 NEW
  const asLabels = momentumView === "label";
  // add this:
  const [showMaPct, setShowMaPct] = React.useState(false);
  
  ///////////////////////////////////////////////////////////////////////////////////
  // ----- SIGNAL + PLAY HELPERS -----

const SIGNAL_COLORS = {
  STRONG_BULLISH: "#0FBF66", // strong green
  BULLISH: "#6CD681",
  NEUTRAL: "#B0B0B0",
  BEARISH: "#E76A6A",
  STRONG_BEARISH: "#D72638",
};

function normalizeSignal(row) {
  // Try to read from new model first, then fall back
  const direction =
    row.signalDirection ||
    row.SignalDirection ||
    row.signal_dir ||
    null;

  const rawLabel =
    row.signalLabel ||
    row.SignalLabel ||
    row.Signal ||
    row.signal ||
    null;

  const label = rawLabel || (direction ? String(direction).toUpperCase() : "N/A");

  const key = (label || "").toLowerCase();

  let strengthKey = "NEUTRAL";
  if (key.includes("strong") && key.includes("bull")) strengthKey = "STRONG_BULLISH";
  else if (key.includes("bull")) strengthKey = "BULLISH";
  else if (key.includes("strong") && key.includes("bear")) strengthKey = "STRONG_BEARISH";
  else if (key.includes("bear")) strengthKey = "BEARISH";

  const color = SIGNAL_COLORS[strengthKey];

  return {
    label,
    color,
    direction: direction || null,
    strengthKey,
  };
}

function normalizeGuruPlay(row) {
  const gp = row.guruPlay || row.play || null;

  if (!gp || gp.hasPlay === false) {
    return {
      hasPlay: false,
      direction: null,
      strike: null,
      expiryShort: null,
      confidencePct: null,
      timeframe: null,
      status: gp?.status || "NOT_PLAYABLE",
    };
  }

  const confidenceRaw = gp.confidence ?? gp.confidencePct ?? null;

  return {
    hasPlay: true,
    direction: gp.direction || gp.type || null, // "CALL" | "PUT"
    strike: gp.strike ?? gp.strikePrice ?? null,
    expiryShort: gp.expiryShort || gp.expiry || null,
    confidencePct:
      typeof confidenceRaw === "number"
        ? Math.round(confidenceRaw * (confidenceRaw <= 1 ? 100 : 1))
        : null,
    timeframe: gp.timeframe || gp.horizon || null,
    status: gp.status || "ACTIVE",
  };
}

function formatPlayOneLiner(play) {
  if (!play || !play.hasPlay || !play.direction || !play.strike || !play.expiryShort) {
    return "N/A";
  }
  const dirShort = play.direction === "PUT" ? "P" : "C";
  return `${play.strike}${dirShort} ${play.expiryShort}`;
}

function playColor(play) {
  if (!play || !play.hasPlay) return "#4b5563"; // gray
  return play.direction === "PUT" ? "#DC2626" : "#16A34A";
}

  ////////////////////////////////////

  const {
    Ticker = row?.ticker,
    Company = row?.company,
    Sector = row?.sector,
    Signal = row?.Signal || row?.Tag || row?.signal, // BUY/SELL/HOLD/CALLs/PUTs/LEAPs
    Confidence = row?.Confidence || row?.confidence,
    Note = row?.Note || row?.note,
  } = row || {};

  const guruPlay = row?.guruPlay || null; // 👈 NEW
  const shortPlayLabel = React.useMemo(() => {
    if (!guruPlay) return null;
    const dir = guruPlay.direction === "PUT" ? "P" : "C";
    const strike =
      guruPlay.strike != null ? String(guruPlay.strike) : "";
    const expiry =
      guruPlay.expiryShort || guruPlay.expiryFull || "";
    if (!strike || !expiry) return null;
    return `${strike}${dir} ${expiry}`; // e.g. "50C 10/25"
  }, [guruPlay]);

  const playConfidencePct = (() => {
    if (guruPlay && typeof guruPlay.confidence === "number") {
      return Math.round(guruPlay.confidence * 100);
    }
    if (typeof Confidence === "number") {
      return Math.round(Confidence);
    }
    return null;
  })();


  const ticker = Ticker || row?.Symbol || row?.symbol;
  const tradingViewUrl = ticker
    ? `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(ticker)}`
    : null;

  // price + as-of date
  const price = row?.price;
  const asOf = row?.as_of;

  const dayChange = row?.daily_change;
  const dayChangePct = row?.daily_change_pct;

  // performance + MA deltas
  const pct1w = row?.pct_1w;
  const pct1m = row?.pct_1m;
  const pct3m = row?.pct_3m;
  const pctYtd = row?.pct_ytd;

  const d20 = row?.daily_delta_from_ma20;
  const d50 = row?.daily_delta_from_ma50;
  const d200 = row?.daily_delta_from_ma200;

  const is3Below50 = row?.is_3day_below_50ma === "TRUE";

  // 52-week and MA levels
  const high52 = row?.high_52w;
  const low52 = row?.low_52w;

  // Daily MAs
  const ma10 = row?.daily_ma10;
  const ma20 = row?.daily_ma20;
  const ma50 = row?.daily_ma50;
  const ma200 = row?.daily_ma200;

  // Short-term MAs (3D / 1W)
  const ma3d10 = row?.ma3d_10;
  const ma3d20 = row?.ma3d_20;
  const ma3d50 = row?.ma3d_50;
  const ma3d200 = row?.ma3d_200;

  const ma1w10 = row?.ma1w_10;
  const ma1w20 = row?.ma1w_20;
  const ma1w50 = row?.ma1w_50;
  const ma1w200 = row?.ma1w_200;

  // stochastic summary labels from backend
  const tf4h_stochSignal = row?.tf_4h_stoch_signal;
  const tf1d_stochSignal = row?.tf_1d_stoch_signal;
  const tf3d_stochSignal = row?.tf_3d_stoch_signal;
  const tf1w_stochSignal = row?.tf_1w_stoch_signal;

  const dailyCandlePattern = row?.daily_candle_pattern;
  const dailyCandleBias = row?.daily_candle_bias;
  const hasDailyCandle = Boolean(dailyCandlePattern || dailyCandleBias);

  let dailyBiasBgColor = "#e5e7eb";
  let dailyBiasTextColor = "#111827";
  if (dailyCandleBias) {
    const biasLower = String(dailyCandleBias).toLowerCase();
    if (biasLower.includes("bull")) {
      dailyBiasBgColor = "#dcfce7";      // light green
      dailyBiasTextColor = "#166534";    // dark green
    } else if (biasLower.includes("bear")) {
      dailyBiasBgColor = "#fee2e2";      // light red
      dailyBiasTextColor = "#991b1b";    // dark red
    } else {
      dailyBiasBgColor = "#e5e7eb";
      dailyBiasTextColor = "#374151";
    }
  }


  // --- Momentum fields coming from TEG backend (tf_* columns) ---
  // Prefer *_macd_hist if present, else fall back to *_macd
  const tf4h_macd =
    typeof row?.tf_4h_macd_hist === "number"
      ? row.tf_4h_macd_hist
      : row?.tf_4h_macd;
  const tf4h_volRel = row?.tf_4h_volume_rel;
  const tf4h_stochRsiK = row?.tf_4h_stoch_rsi_k;

  const tf1d_macd =
    typeof row?.tf_1d_macd_hist === "number"
      ? row.tf_1d_macd_hist
      : row?.tf_1d_macd;
  const tf1d_volRel = row?.tf_1d_volume_rel;
  const tf1d_stochRsiK = row?.tf_1d_stoch_rsi_k;

  const tf3d_macd =
    typeof row?.tf_3d_macd_hist === "number"
      ? row.tf_3d_macd_hist
      : row?.tf_3d_macd;
  const tf3d_volRel = row?.tf_3d_volume_rel;
  const tf3d_stochRsiK = row?.tf_3d_stoch_rsi_k;

  const tf1w_macd =
    typeof row?.tf_1w_macd_hist === "number"
      ? row.tf_1w_macd_hist
      : row?.tf_1w_macd;
  const tf1w_volRel = row?.tf_1w_volume_rel;
  const tf1w_stochRsiK = row?.tf_1w_stoch_rsi_k;

  const guruBias = row?.guru_bias;
  const guruPlayable = row?.guru_playable === "TRUE";

  const pctOffHigh =
    typeof high52 === "number" && typeof price === "number"
      ? ((price - high52) / high52) * 100
      : null;

  const pctFromLow =
    typeof low52 === "number" && typeof price === "number"
      ? ((price - low52) / low52) * 100
      : null;

  const formattedPrice =
    typeof price === "number" ? price.toFixed(2) : price;

  const fmtPct = (v) =>
    typeof v === "number" ? `${v.toFixed(1)}%` : "—";

  const fmtDelta = (v) =>
    typeof v === "number"
      ? `${v > 0 ? "+" : ""}${v.toFixed(1)}%`
      : "—";

  const fmtMoney = (v) =>
    typeof v === "number" ? `$${v.toFixed(2)}` : "—";

  const colorForPct = (v) => {
    if (typeof v !== "number" || Number.isNaN(v)) return "#4b5563"; // gray
    if (v > 0) return "#16a34a"; // green
    if (v < 0) return "#dc2626"; // red
    return "#4b5563";
  };

  // Map MA label → identity color (used in table header)
  const colorForMA = (label) => {
    if (!label) return "#4b5563";
    const l = String(label).toLowerCase();

    if (l.includes("200")) return "#dc2626"; // 200MA red
    if (l.includes("50")) return "#f97316"; // 50MA orange
    if (l.includes("20")) return "#16a34a"; // 20MA green
    if (l.includes("10")) return "#38bdf8"; // 10MA blue

    return "#4b5563";
  };

  // ---- Momentum formatters for numeric + label toggle ----
  const fmtMacdCell = (v) => {
    if (typeof v !== "number" || Number.isNaN(v)) return "—";

    if (momentumView === "label") {
      let text = "Neutral";
      if (v > 0.5) text = "Bullish";
      else if (v < -0.5) text = "Bearish";

      const color =
        text === "Bullish"
          ? "#16a34a"
          : text === "Bearish"
          ? "#dc2626"
          : "#4b5563";

      return (
        <span style={{ color, fontWeight: 700 }}>
          {text}
        </span>
      );
    }

    const color = v > 0 ? "#16a34a" : v < 0 ? "#dc2626" : "#4b5563";
    const arrow = v > 0 ? "↑" : v < 0 ? "↓" : "→";
    const mag = Math.abs(v).toFixed(2);
    return (
      <span style={{ color, fontWeight: 700 }}>
        {arrow} {mag}
      </span>
    );
  };

  const fmtStochRsiCell = (k) => {
    if (typeof k !== "number" || Number.isNaN(k)) return "—";

    // numeric view
    if (momentumView === "numeric") {
      let color = "#4b5563";
      if (k >= 80) color = "#dc2626"; // overbought
      else if (k <= 20) color = "#16a34a"; // oversold
      return (
        <span style={{ color, fontWeight: 600 }}>
          {k.toFixed(0)}%
        </span>
      );
    }

    // label view
    let text = "Normal";
    let color = "#4b5563";
    if (k >= 80) {
      text = "Overbought";
      color = "#dc2626";
    } else if (k <= 20) {
      text = "Oversold";
      color = "#16a34a";
    }

    return (
      <span style={{ color, fontWeight: 600 }}>
        {text}
      </span>
    );
  };

  const fmtVolCell = (v) => {
    if (typeof v !== "number" || !Number.isFinite(v)) return "—";

    if (momentumView === "label") {
      let text = "Normal";
      if (v >= 1.8) text = "Climactic";
      else if (v >= 1.3) text = "High";
      else if (v <= 0.6) text = "Low";

      const color =
        text === "High" || text === "Climactic"
          ? "#16a34a"
          : text === "Low"
          ? "#dc2626"
          : "#4b5563";

      return (
        <span style={{ color, fontWeight: 600 }}>
          {text}
        </span>
      );
    }

    let color = "#4b5563";
    if (v >= 1.2) color = "#16a34a"; // 20%+ above avg
    else if (v <= 0.8) color = "#dc2626"; // 20%+ below avg
    return (
      <span style={{ color, fontWeight: 600 }}>
        {v.toFixed(2)}×
      </span>
    );
  };

  const arrowForMA = (priceVal, ma) => {
    if (typeof priceVal !== "number" || typeof ma !== "number") {
      return <span style={{ marginRight: 4 }}>—</span>;
    }

    const above = priceVal > ma;

    return (
      <span
        style={{
          color: above ? "#16a34a" : "#dc2626",
          fontWeight: 700,
          marginRight: 4,
        }}
      >
        {above ? "↑" : "↓"}
      </span>
    );
  };

  const maCellStyle = {
    width: 70,
    textAlign: "right",
    display: "inline-flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 4,
  };

  function renderMAValue(maValue) {
    const baseStyle = {
      width: 70,
      textAlign: "right",
      color: "#111827",
    };
  
    if (maValue == null) {
      return (
        <span style={{ ...baseStyle, color: "#9ca3af" }}>—</span>
      );
    }
  
    return (
      <span style={baseStyle}>
        ${maValue.toFixed(2)}
      </span>
    );
  }
  
  function renderMADiff(maValue, price) {
    const baseStyle = {
      width: 70,
      textAlign: "right",
      fontSize: "0.78rem",
    };
  
    if (maValue == null || price == null) {
      return (
        <span style={{ ...baseStyle, color: "#9ca3af" }}>—</span>
      );
    }
  
    // price vs MA: how far is price from this MA, as % of the MA
    const diff = price - maValue;           // >0 => price above MA, <0 => below
    const pct = (diff / maValue) * 100;     // % of MA
    const color = pct >= 0 ? "#16a34a" : "#dc2626";
  
    // sign + color carry the meaning (no arrows)
    return (
      <span style={{ ...baseStyle, color }}>
        {pct.toFixed(1)}%
      </span>
    );
  }
  
  
  
  
  
  

  const renderPct = (label, v) => (
    <span style={{ marginLeft: label ? 6 : 0 }}>
      {label && <>{label}{" "}</>}
      <span style={{ color: colorForPct(v), fontWeight: 600 }}>
        {fmtPct(v)}
      </span>
    </span>
  );

  const renderStochSignal = (label, sig) => {
    if (!sig || sig === "Normal") return null; // only show interesting ones

    const s = String(sig);
    let color = "#4b5563";
    if (s.includes("Bullish") || s === "Overbought") color = "#16a34a";
    else if (s.includes("Bearish") || s === "Oversold") color = "#dc2626";

    return (
      <span style={{ marginRight: 10 }}>
        <span style={{ fontWeight: 600 }}>{label}:</span>{" "}
        <span style={{ color, fontWeight: 700 }}>{s}</span>
      </span>
    );
  };

  const candleBiasColor = (() => {
    const b = (dailyCandleBias || "").toLowerCase();
    if (b === "bullish") return "#16a34a";
    if (b === "bearish") return "#dc2626";
    return "#4b5563";
  })();

  const renderDelta = (label, v) => (
    <span style={{ marginLeft: label ? 6 : 0 }}>
      {label && (
        <span style={{ color: "#111827", fontWeight: 600 }}>
          {label}{" "}
        </span>
      )}
      <span style={{ color: colorForPct(v), fontWeight: 600 }}>
        {fmtDelta(v)}
      </span>
    </span>
  );

  const badgeBg = tegTagColor(Signal);

  return (
    <article
      className="prop-card"
      style={{
        borderRadius: 16,
        padding: 18,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        background: "white",
        position: "relative",
      }}
    >
      {/* top: company / ticker bubble */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 800 }}>
          {Company || Ticker}
        </h3>
        <span
          style={{
            padding: "0.35rem 0.8rem",
            borderRadius: 12,
            border: "2px solid #0f172a",
            fontWeight: 800,
            fontSize: "1.1rem",
            color: "#0f172a",
            background: "#eef2ff",
          }}
        >
          {Ticker}
        </span>
      </header>

      {/* subline */}
      {(Company || Sector) && (
        <div
          style={{
            marginTop: 8,
            color: "#374151",
            fontSize: "1.05rem",
          }}
        >
          {Sector ? `${Company || Ticker} — ${Sector}` : Company || Ticker}
        </div>
      )}

      {/* Price row with day-over-day change */}
      {price != null && (
        <div
          style={{
            marginTop: 8,
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 700 }}>Price:</span>

          <span
            style={{
              fontWeight: 800,
              fontSize: "1.2rem",
            }}
          >
            ${formattedPrice}
          </span>

          {asOf && (
            <span
              style={{
                fontSize: "0.85rem",
                color: "#6b7280",
              }}
            >
              as of {asOf}
            </span>
          )}

          {typeof dayChange === "number" &&
            typeof dayChangePct === "number" && (
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: colorForPct(dayChange),
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span>{dayChange > 0 ? "↑" : dayChange < 0 ? "↓" : "→"}</span>
                <span>
                  {`${dayChange > 0 ? "+" : ""}${dayChange.toFixed(2)}`} (
                  {`${dayChangePct > 0 ? "+" : ""}${dayChangePct.toFixed(2)}%`}
                  )
                </span>
              </span>
            )}
        </div>
      )}

    {/* Trend row */}
      <div
        style={{
          marginTop: 10,
          fontSize: "0.9rem",
          color: "#4b5563",
        }}
      >
        <strong>Trend:</strong>
        {[
          ["1W", pct1w],
          ["1M", pct1m],
          ["3M", pct3m],
          ["YTD", pctYtd],
        ]
          .filter(([, value]) => value != null)
          .map(([label, value], idx, arr) => (
            <React.Fragment key={label}>
              {/* add a pipe before every item except the first */}
              {idx > 0 && (
                <span style={{ margin: "0 6px", color: "#9ca3af" }}>|</span>
              )}
              {renderPct(label, value)}
            </React.Fragment>
          ))}
      </div>

   
      {/* Signal + Play row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginTop: 14,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontWeight: 800, fontSize: "1.1rem" }}>📈 Play:</span>

        {/* primary one-liner play pill */}
        <span
          style={{
            background: guruPlay
              ? guruPlay.direction === "PUT"
                ? "#dc2626"
                : "#16a34a"
              : "#6b7280",
            color: "white",
            borderRadius: 999,
            padding: "0.25rem 0.9rem",
            fontWeight: 800,
            fontSize: "0.85rem",
            textTransform: "uppercase",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {guruPlay?.label || shortPlayLabel || String(Signal || "N/A")}
          {guruPlay?.timeframe && (
            <span
              style={{
                fontSize: "0.7rem",
                fontWeight: 600,
                opacity: 0.9,
              }}
            >
              · {guruPlay.timeframe}
            </span>
          )}
        </span>


        {/* confidence chip */}
        {playConfidencePct != null && (
          <span
            style={{
              padding: "0.15rem 0.55rem",
              borderRadius: 999,
              background: "#e0f2fe",
              color: "#075985",
              fontWeight: 700,
              fontSize: "0.75rem",
            }}
          >
            {playConfidencePct}% Conf
          </span>
        )}

        {/* Open Chart button (as you already had) */}
        {tradingViewUrl && (
          <a
            href={tradingViewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-full border border-blue-500 px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 hover:border-blue-600 transition-colors"
            style={{ marginLeft: "auto" }}
          >
            <span>Open Chart</span>
            <span className="ml-1 text-[10px]">↗</span>
          </a>
        )}
      </div>
      {/* Guru Play drill-down (optional) */}
      {guruPlay && (
        <div
          style={{
            marginTop: 6,
            fontSize: "0.78rem",
            color: "#4b5563",
          }}
        >
          <button
            type="button"
            onClick={() => setShowPlayDetails((v) => !v)}
            style={{
              border: "none",
              background: "transparent",
              color: "#2563eb",
              fontWeight: 600,
              cursor: "pointer",
              padding: 0,
            }}
          >
            {showPlayDetails ? "Hide play details" : "View play details"}
          </button>

          {showPlayDetails && (
            <div
              style={{
                marginTop: 4,
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#f9fafb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  rowGap: 4,
                }}
              >
                {/* status + timeframe + confidence */}
                {guruPlay.status && (
                  <div style={{ width: "50%" }}>
                    <strong>Status:</strong> {guruPlay.status}
                  </div>
                )}
                {guruPlay.timeframe && (
                  <div style={{ width: "50%" }}>
                    <strong>Timeframe:</strong> {guruPlay.timeframe}
                  </div>
                )}
                {typeof playConfidencePct === "number" && (
                  <div style={{ width: "50%" }}>
                    <strong>Confidence:</strong> {playConfidencePct}%
                  </div>
                )}

                {/* price zones */}
                {guruPlay.initiatedAt && (
                  <div style={{ width: "50%" }}>
                    <strong>Initiated:</strong> {guruPlay.initiatedAt}
                  </div>
                )}
                {guruPlay.entryZone && (
                  <div style={{ width: "50%" }}>
                    <strong>Entry zone:</strong> {guruPlay.entryZone}
                  </div>
                )}
                {guruPlay.targetZone && (
                  <div style={{ width: "50%" }}>
                    <strong>Target zone:</strong> {guruPlay.targetZone}
                  </div>
                )}
                {guruPlay.stopZone && (
                  <div style={{ width: "50%" }}>
                    <strong>Risk guard (stop):</strong> {guruPlay.stopZone}
                  </div>
                )}

                {/* profit targets */}
                {guruPlay.takeProfit1 && (
                  <div style={{ width: "50%" }}>
                    <strong>TP1:</strong> {guruPlay.takeProfit1}
                  </div>
                )}
                {guruPlay.takeProfit2 && (
                  <div style={{ width: "50%" }}>
                    <strong>TP2:</strong> {guruPlay.takeProfit2}
                  </div>
                )}

                {/* running performance */}
                {typeof guruPlay.currentGainPct === "number" && (
                  <div style={{ width: "50%" }}>
                    <strong>Since init:</strong>{" "}
                    {guruPlay.currentGainPct > 0 ? "+" : ""}
                    {guruPlay.currentGainPct.toFixed(1)}%
                  </div>
                )}
              </div>

              {guruPlay.note && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: "0.75rem",
                    color: "#6b7280",
                  }}
                >
                  {guruPlay.note}
                </div>
              )}
            </div>
          )}
        </div>
      )}




      {Note && (
        <div style={{ marginTop: 10, color: "#111827" }}>
          {Note}
        </div>
      )}

      {/* Expanded technical details */}
      {expanded && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 10,
            borderTop: "1px solid #e5e7eb",
            fontSize: "0.9rem",
            color: "#374151",
          }}
        >
          <div>
            <strong>52W Range:</strong>{" "}
            {fmtMoney(low52)} – {fmtMoney(high52)}
          </div>

          <div style={{ marginTop: 4 }}>
            <strong>From 52W High:</strong>{" "}
            {pctOffHigh != null ? (
              <span
                style={{
                  color: colorForPct(pctOffHigh),
                  fontWeight: 600,
                }}
              >
                {pctOffHigh > 0 ? "+" : ""}
                {pctOffHigh.toFixed(1)}%
              </span>
            ) : (
              "—"
            )}
          </div>

          <div style={{ marginTop: 2 }}>
            <strong>From 52W Low:</strong>{" "}
            {pctFromLow != null ? (
              <span
                style={{
                  color: colorForPct(pctFromLow),
                  fontWeight: 600,
                }}
              >
                {pctFromLow > 0 ? "+" : ""}
                {pctFromLow.toFixed(1)}%
              </span>
            ) : (
              "—"
            )}
          </div>

          {dailyCandlePattern && (
            <div style={{ marginTop: 4 }}>
              <strong>Daily Candle:</strong>{" "}
              <span style={{ color: candleBiasColor, fontWeight: 600 }}>
                {dailyCandlePattern}
              </span>
              {dailyCandleBias && (
                  <span
                  style={{
                    padding: "0.1rem 0.5rem",
                    borderRadius: "999px",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    background:
                    dailyCandleBias.toLowerCase() === "bullish"
                        ? "#dcfce7"
                        : dailyCandleBias.toLowerCase() === "bearish"
                        ? "#fee2e2"
                        : "#e5e7eb",
                    color:
                    dailyCandleBias.toLowerCase() === "bullish"
                        ? "#166534"
                        : dailyCandleBias.toLowerCase() === "bearish"
                        ? "#991b1b"
                        : "#374151",
                  }}
                >
                  {dailyCandleBias.toLowerCase()}
                </span>
              )}
            </div>
          )}
          {/* Stoch cross/OB/OS summary under the table */}
          {(tf4h_stochSignal ||
            tf1d_stochSignal ||
            tf3d_stochSignal ||
            tf1w_stochSignal) && (
            <div
              style={{
                marginTop: 6,
                fontSize: "0.75rem",
                color: "#4b5563",
              }}
            >
              <strong>Stoch Signals:</strong>{" "}
              {renderStochSignal("4H", tf4h_stochSignal)}
              {renderStochSignal("1D", tf1d_stochSignal)}
              {renderStochSignal("3D", tf3d_stochSignal)}
              {renderStochSignal("1W", tf1w_stochSignal)}
            </div>
            
          )}
          {/* Momentum Snapshot – MACD / Stoch RSI / Volume by timeframe */}
<div style={{ marginTop: 12 }}>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 4,
    }}
  >
    <div style={{ fontWeight: 700 }}>Momentum Snapshot</div>
    <button
      type="button"
      onClick={() =>
        setMomentumView((v) => (v === "numeric" ? "label" : "numeric"))
      }
      style={{
        fontSize: "0.7rem",
        borderRadius: 999,
        border: "1px solid #e5e7eb",
        padding: "2px 8px",
        background: momentumView === "label" ? "#eff6ff" : "#f9fafb",
        color: "#1d4ed8",
        cursor: "pointer",
      }}
    >
      {momentumView === "numeric" ? "Show labels" : "Show numbers"}
    </button>
  </div>

  <div
    style={{
      display: "inline-block",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      padding: 6,
      fontSize: "0.8rem",
    }}
  >
    {/* header row */}
    <div
      style={{
        display: "flex",
        fontWeight: 700,
        marginBottom: 4,
        paddingBottom: 4,
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      <span style={{ width: 52 }} />
      <span style={{ width: 80, textAlign: "center" }}>MACD</span>
      <span style={{ width: 100, textAlign: "center" }}>Stoch RSI</span>
      <span style={{ width: 80, textAlign: "center" }}>Volume</span>
    </div>

    {[
      ["4H", tf4h_macd, tf4h_stochRsiK, tf4h_volRel],
      ["1D", tf1d_macd, tf1d_stochRsiK, tf1d_volRel],
      ["3D", tf3d_macd, tf3d_stochRsiK, tf3d_volRel],
      ["1W", tf1w_macd, tf1w_stochRsiK, tf1w_volRel],
    ].map(([label, macd, stoch, vol]) => (
      <div
        key={label}
        style={{
          display: "flex",
          marginBottom: label === "1W" ? 0 : 2,
          alignItems: "center",
        }}
      >
        <span style={{ width: 52, fontWeight: 600 }}>{label}</span>
        <span
          style={{
            width: 80,
            textAlign: "center",
            fontWeight: 400, // match MA grid body
          }}
        >
          {fmtMacdCell(macd)}
        </span>
        <span
          style={{
            width: 100,
            textAlign: "center",
            fontWeight: 400,
          }}
        >
          {fmtStochRsiCell(stoch)}
        </span>
        <span
          style={{
            width: 80,
            textAlign: "center",
            fontWeight: 400,
          }}
        >
          {fmtVolCell(vol)}
        </span>
      </div>
    ))}
  </div>
</div>


          {/* MA snapshot table */}
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <div style={{ fontWeight: 700 }}>MAs Snapshot</div>
              <button
                type="button"
                onClick={() => setShowMaPct((v) => !v)}
                style={{
                  fontSize: "0.7rem",
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  padding: "2px 8px",
                  background: showMaPct ? "#eff6ff" : "#f9fafb",
                  color: "#1d4ed8",
                  cursor: "pointer",
                }}
              >
                {showMaPct ? "Show MA price" : "Show % from price"}
              </button>
            </div>

            <div
              style={{
                display: "inline-block",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 6,
                fontSize: "0.8rem",
              }}
            >
              {/* header row */}
              <div
                style={{
                  display: "flex",
                  fontWeight: 700,
                  marginBottom: 4,
                  paddingBottom: 4,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <span style={{ width: 52 }} />
                <span style={{ width: 70, textAlign: "right", color: colorForMA("10MA") }}>
                  10MA
                </span>
                <span style={{ width: 70, textAlign: "right", color: colorForMA("20MA") }}>
                  20MA
                </span>
                <span style={{ width: 70, textAlign: "right", color: colorForMA("50MA") }}>
                  50MA
                </span>
                <span style={{ width: 70, textAlign: "right", color: colorForMA("200MA") }}>
                  200MA
                </span>
              </div>

              {/* Daily row */}
              <div style={{ display: "flex", marginBottom: 2, alignItems: "center" }}>
                <span style={{ width: 52, fontWeight: 600 }}>Daily</span>
                {showMaPct ? renderMADiff(ma10, price) : renderMAValue(ma10)}
                {showMaPct ? renderMADiff(ma20, price) : renderMAValue(ma20)}
                {showMaPct ? renderMADiff(ma50, price) : renderMAValue(ma50)}
                {showMaPct ? renderMADiff(ma200, price) : renderMAValue(ma200)}
              </div>

              {/* 3D row */}
              <div style={{ display: "flex", marginBottom: 2, alignItems: "center" }}>
                <span style={{ width: 52, fontWeight: 600 }}>3D</span>
                {showMaPct ? renderMADiff(ma3d10, price) : renderMAValue(ma3d10)}
                {showMaPct ? renderMADiff(ma3d20, price) : renderMAValue(ma3d20)}
                {showMaPct ? renderMADiff(ma3d50, price) : renderMAValue(ma3d50)}
                {showMaPct ? renderMADiff(ma3d200, price) : renderMAValue(ma3d200)}
              </div>

              {/* 1W row */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ width: 52, fontWeight: 600 }}>1W</span>
                {showMaPct ? renderMADiff(ma1w10, price) : renderMAValue(ma1w10)}
                {showMaPct ? renderMADiff(ma1w20, price) : renderMAValue(ma1w20)}
                {showMaPct ? renderMADiff(ma1w50, price) : renderMAValue(ma1w50)}
                {showMaPct ? renderMADiff(ma1w200, price) : renderMAValue(ma1w200)}
              </div>
            </div>
          </div>



          <div style={{ marginTop: 8 }}>
            <strong>Guru Bias:</strong>{" "}
            <span style={{ fontWeight: 600 }}>
              {guruBias || "N/A"}
            </span>
            {"  "}
            <span
              style={{
                marginLeft: 8,
                padding: "0.1rem 0.55rem",
                borderRadius: 999,
                background: guruPlayable ? "#dcfce7" : "#e5e7eb",
                color: guruPlayable ? "#166534" : "#4b5563",
                fontSize: "0.75rem",
                fontWeight: 700,
              }}
            >
              {guruPlayable ? "PLAYABLE" : "NOT PLAYABLE"}
            </span>
          </div>
        </div>
      )}


    </article>
  );
}




const [layoutMode, setLayoutMode] = React.useState("cards"); // "cards" | "grid"

// --- NBA GRID TABLE --------------------------------------------------------
// ---- NBA GRID TABLE (sortable) ----
// ---- NBA GRID TABLE (self-contained, sortable) ----
const NBAGridTable = useMemo(() => function NBAGridTable({
  rows,
  columns,
  setColumns,
  getConfidenceLabel,
  tagColorMap,
  isRowSelected,
  onToggleRowSelected,
  onToggleAllVisible,
  onBulkAddToGuruList,
  onBulkRemoveFromGuruList,
  onOpenDetails,
  onOpenCompare,
  sortConfig,
  setSortConfig,
  isAdminUser,
  guruModeAccess,
  isBasicCardExperience = false,
  isInMyList,
  onToggleMyPlay,
}) {
  const inputRows = Array.isArray(rows) ? rows : [];
  const [matchupGradeFilter, setMatchupGradeFilter] = useState("ANY");
  const supportsCanonicalMatchup = candidateSupportsCanonicalMatchup(inputRows);
  const safeRows = filterRowsByMatchupGrade(inputRows, matchupGradeFilter);

  useEffect(() => {
    if (!supportsCanonicalMatchup && matchupGradeFilter !== "ANY") setMatchupGradeFilter("ANY");
  }, [supportsCanonicalMatchup, matchupGradeFilter]);

  // --- columns dropdown state ---
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsRef = useRef(null);

  useEffect(() => {
    if (!columnsOpen) return;
    const onDown = (e) => {
      if (columnsRef.current && !columnsRef.current.contains(e.target)) {
        setColumnsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [columnsOpen]);

// --- column toggle metadata (what can be turned on/off) ---
// --- column toggle metadata (what can be turned on/off) ---
const columnToggles = [
  { key: "battingOrder", label: "BO",          visible: true },
  { key: "ha",          label: "H/A",         visible: false },
  { key: "proj",  label: "Proj",        visible: false }, // ✅ FIXED (was proj)
  { key: "delta",       label: "Δ",           visible: true },
  { key: "pwgProb",     label: "PWG Prob",    visible: false },
  { key: "lineType", label: "Line Type", visible: false },
  { key: "primaryMarketLine", label: "Primary Line", visible: false },
  { key: "alternateLine", label: "Alternate Line", visible: false },
  { key: "lineFamilyCount", label: "Family Count", visible: false },
  { key: "goblinAttainability", label: "Goblin Attainability", visible: false },
  { key: "l5Avg", label: "L5 Avg", visible: false },
  { key: "l10Avg", label: "L10 Avg", visible: false },
  { key: "seasonAvg", label: "Season Avg", visible: false },
  { key: "l5HitPct", label: "L5 Hit %", visible: false },
  { key: "seasonHitPct", label: "Season Hit %", visible: false },
  { key: "l5Delta", label: "L5 Δ", visible: false },
  { key: "l10Delta", label: "L10 Δ", visible: false },
  { key: "seasonDelta", label: "Season Δ", visible: false },
  { key: "defenseRank", label: "Defense Rank", visible: false },
  { key: "defenseFieldSize", label: "Defense Field Size", visible: false },
  { key: "defenseTier", label: "Defense Tier", visible: false },
  { key: "defenseMetric", label: "Defense Metric", visible: false },
  { key: "defenseValue", label: "Defense Value", visible: false },
  { key: "historySeason", label: "History Season", visible: false },
  { key: "teamContinuity", label: "Team Continuity", visible: false },

  { key: "minutesHeat", label: "Minutes Heat", visible: false },
  { key: "bounceLabel", label: "Bounce State", visible: false },
  { key: "bounceScore", label: "Bounce Score", visible: false },
  { key: "heatNote",    label: "Heat Note",    visible: false },
  { key: "l2MinDelta",  label: "L2 MIN Δ",     visible: false },
  { key: "l2StatDelta", label: "L2 Stat Δ",    visible: false },
  { key: "l2PaDelta",   label: "L2 PA Δ",      visible: false },
  { key: "l2OutsDelta", label: "L2 Outs Δ",    visible: false },

  { key: "confidence",  label: "Confidence",  visible: true },
  { key: "guruScore",   label: "Guru Score",  visible: true },
  { key: "dqs",         label: "DQS",         visible: true },
  { key: "guruRank",    label: "Guru Rank",   visible: false },
  { key: "guruGrade",   label: "Guru Grade",  visible: false },

  { key: "l10HitPct",   label: "L10 Hit%",    visible: false },
  { key: "tag",         label: "Tag",         visible: true },
  { key: "minutes",     label: "Minutes",     visible: false },
  { key: "blowout",     label: "Blowout",     visible: false },
  { key: "dvpTier",     label: "Matchup",     visible: false },
  { key: "l5Setup",     label: "L5 Setup",    visible: false },

  { key: "selStrat",    label: "Sel Strat",   visible: false },
  { key: "tax",         label: "Line Tax",    visible: false },
  { key: "guruAlert",   label: "GuruAlert",   visible: false },
];



// PUBLIC COLUMN GUARDRAIL:
// ---- Beta-safe columns (what users are allowed to toggle) ----
const USER_SAFE_KEYS = new Set(["delta", "confidence", "dqs", "tag", "selStrat", "bounceLabel", "bounceScore", "lineType", "primaryMarketLine", "alternateLine", "lineFamilyCount", "goblinAttainability", "l5Avg", "l10Avg", "seasonAvg", "l5HitPct", "l10HitPct", "seasonHitPct", "l5Delta", "l10Delta", "seasonDelta", "defenseRank", "defenseFieldSize", "defenseTier", "defenseMetric", "defenseValue", "historySeason", "teamContinuity"]);
const GURU_MODE_GRID_KEYS = new Set(["lineType", "primaryMarketLine", "alternateLine", "lineFamilyCount", "goblinAttainability"]);

const handleToggleCol = (key) => {
  if (!setColumns) return;

  // PUBLIC COLUMN GUARDRAIL:
  // Non-admin users can toggle columns, but ONLY within USER_SAFE_KEYS.
  // This prevents exposing internal/private columns to beta users.
  if (!isAdminUser && !USER_SAFE_KEYS.has(key)) return;
  if (!isAdminUser && GURU_MODE_GRID_KEYS.has(key) && !guruModeAccess?.advancedFilters) return;

  setColumns((prev) => {
    const arr = Array.isArray(prev) ? prev : [];

    // if key doesn't exist yet, ADD it as visible
    const exists = arr.some((c) => c?.key === key);
    if (!exists) return [...arr, { key, visible: true }];

    // otherwise flip visibility
    return arr.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c));
  });
};

const MINUTES_ONLY_GRID_KEYS = new Set(["minutesHeat", "heatNote", "l2MinDelta", "minutes"]);
const sportKeyForGrid = String(activeSport || "").toUpperCase();

const togglesForMenu = (isAdminUser
  ? columnToggles
  : columnToggles.filter(t => USER_SAFE_KEYS.has(t.key))
)
  .filter((t) => t.key !== "battingOrder" || sportKeyForGrid === "MLB")
  .filter((t) => !["l2PaDelta", "l2OutsDelta"].includes(t.key) || sportKeyForGrid === "MLB")
  .filter((t) => t.key !== "dqs" || supportsDqs(sportKeyForGrid))
  .filter((t) => isAdminUser || guruModeAccess?.advancedFilters || !GURU_MODE_GRID_KEYS.has(t.key))
  .filter((t) => (allowsMinutesUi(sportKeyForGrid) && sportKeyForGrid !== "MLB") || !MINUTES_ONLY_GRID_KEYS.has(t.key));


const showCol = (key) => {
  if (key === "dqs" && !supportsDqs(sportKeyForGrid)) return false;
  if (!isAdminUser && GURU_MODE_GRID_KEYS.has(key) && !guruModeAccess?.advancedFilters) return false;
  if (!Array.isArray(columns)) return false;
  const col = columns.find((c) => c.key === key);
  return !!col?.visible;
};

const registryRenderedColumns = selectedRegistryColumns(columns, defaultNBAColumns);
const renderedOptionalColumnCount = togglesForMenu.filter((column) => showCol(column.key)).length;





  const taxChipStyle = {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    background: "#F1F5F9",
    color: "#111827",
    border: "1px solid #CBD5E1",
    whiteSpace: "nowrap",
  };

  const toNum = (v, fallback = 0) => {
    if (v === null || v === undefined) return fallback;
    const n = Number(String(v).replace(/[^\d.+-eE]/g, ""));
    return Number.isFinite(n) ? n : fallback;
  };

// --- Guru Score / Rank helpers (for sorting + rendering) ---
// (uses getGuruScoreValue defined above)

// Rank within the *current filtered rows* (safeRows), highest Guru Score = rank 1.
const guruRankMap = React.useMemo(() => {
  try {
    const arr = (safeRows || []).map((r) => ({
      k: cardKey(r),
      s: getGuruScoreValue(r),
    }));

    arr.sort((a, b) => (b.s - a.s));

    const map = new Map();
    let rank = 1;
    for (const item of arr) {
      // Keep a stable rank even if score is non-finite
      map.set(item.k, Number.isFinite(item.s) ? rank : null);
      if (Number.isFinite(item.s)) rank += 1;
    }
    return map;
  } catch (e) {
    return new Map();
  }
}, [safeRows]);



  const getSortValue = (row, key) => {
    switch (key) {
      case "Player":
        return row.Player || "";
      case "Team":
        return row.Team || "";
      case "Opponent":
        return row.Opponent || row["Opponent Name"] || "";
      case "Sport":
        return String(row.Sport || "").toUpperCase();
      case "GameTime":
        return String(row.GameTime || row["Start Time"] || row.game_time || "");
      case "Prop":
        return String(row["Prop Type"] || row.PropType || row.prop_type || "").toUpperCase();
      case "PWGTag":
      case "Tag":
        return String(getRowTag(row) || "").toUpperCase();
      case "BattingOrder":
      case "battingOrder": {
        const bo = getBattingOrderValue(row);
        return Number.isInteger(bo) ? bo : 99;
      }
      case "Line":
        return Number(row["Prop Value"] ?? row.Line ?? 0);
      case "Proj":
        return Number(
          row.FinalAdjustedScore ??
            row.Projection ??
            row.Proj ??
            row.Projected ??
            0
        );
      case "Delta":
        return gridDeltaValue(row, activeSport);
      case "NhlMatchup":
        return nhlMatchupPresentation(row).grade;
      case "NhlMatchupRank":
        return nhlMatchupPresentation(row).rank ?? Infinity;
      case "NhlMatchupFactor":
        return nhlMatchupPresentation(row).factor ?? 1;
      case "NhlAdjustedProjection":
        return nhlMatchupPresentation(row).adjustedProjection ?? -Infinity;
      case "NhlAdjustedEdge":
        return nhlMatchupPresentation(row).adjustedEdge ?? -Infinity;
      case "NhlMatchupSample":
        return nhlMatchupPresentation(row).sample ?? -Infinity;
      case "NhlMatchupReliability":
        return nhlMatchupPresentation(row).reliability ?? -Infinity;
      case "defenseRank":
      case "DefenseRank":
        return defenseRankValue(row);
      case "PWGProb": {
        const pwg = pwgProbLabelForRow(row);
        return Number.isFinite(pwg?.pct) ? pwg.pct : 0;
      }

      case "MinutesHeat":
      case "minutesHeat":
        return String(getMinutesHeatValue(row) || "");

      case "HeatNote":
      case "heatNote":
        return String(getHeatNoteValue(row) || "");

      case "L2MinDelta":
      case "l2MinDelta": {
        const n = getL2MinDeltaValue(row);
        return Number.isFinite(n) ? n : -Infinity;
      }

      case "L2StatDelta":
      case "l2StatDelta": {
        const n = getL2StatDeltaValue(row);
        return Number.isFinite(n) ? n : -Infinity;
      }
      case "Confidence":
        return Number(row.Confidence ?? 0);



      // Guru Score / Rank (accept both header keys and toggle keys)
      case "GuruScore":
      case "guruScore": {
        return getGuruScoreValue(row);
      }
      case "DQS":
      case "dqs": {
        const v = row?.dqs ?? computeDQS(row);
        const n = Number(v);
        return Number.isFinite(n) ? n : -Infinity;
      }
      case "GuruRank":
      case "guruRank": {
        const k = cardKey(row);
        const r = guruRankMap?.get ? guruRankMap.get(k) : null;
        const n = Number(r);
        return Number.isFinite(n) ? n : Infinity;
      }
      const colorBlowout = (v) => {
        const raw = String(v ?? "").trim();
        const lower = raw.toLowerCase();
      
        // default/unknown
        let color = "#9ca3af"; // neutral gray
        let label = raw || "Unknown";
      
        if (lower.includes("low")) {
          color = "#16a34a";
          label = "Low blowout risk";
        } else if (lower.includes("mod")) {
          color = "#f59e0b";
          label = "Moderate blowout risk";
        } else if (lower.includes("high")) {
          color = "#dc2626";
          label = "High blowout risk";
        }
      
        const style = {
          display: "inline-block",
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: color,
          boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
        };
      
        return { label, style };
      };
      

      case "L10HitPct": {
        const v = row.L10_HitPct ?? row["L10_HitPct"] ?? row.l10HitPct ?? row["l10HitPct"];
        const n = Number(v);
        return Number.isFinite(n) ? n : -1;
      }
      case "Tag":
        return row.Tag || "";
      case "Minutes":
        return Number(
          row.mpg ??
            row.Minutes ??
            row["minutes_recent_mpg"] ??
            0
        );
      case "Blowout": {
        const tier = normalizeBlowoutTier(row);
        if (tier === "low") return 1;
        if (tier === "medium") return 2;
        if (tier === "high") return 3;
        return 0;
      }
      case "dvpTier": {
        if (String(activeSport).toUpperCase() === "MLB") {
          const chips = getMlbEnvironmentChips(row, "MLB");
          return chips.map((c) => c.label).join(" | ");
        }
        // Same tier order you use in the column definition
        const tierOrder = {
          "ELITE DEFENSE": 1,
          "TOUGH": 2,
          "NEUTRAL": 3,
          "SOFT": 4,
          "TARGET MATCHUP": 5,
          "R0": 99,
          "": 999,
        };
      
        const clean = (val) => {
          if (!val) return "";
          const s = String(val).toUpperCase();
      
          // if we ever pass something like "SG • SOFT (R27)", strip position + rank
          if (
            s.startsWith("SG") ||
            s.startsWith("PG") ||
            s.startsWith("PF") ||
            s.startsWith("SF") ||
            s.startsWith("C")
          ) {
            const inner = s.split("•")[1]?.trim() || "";
            return inner.split("(")[0].trim();
          }
      
          if (s === "R0") return "R0";
          return s;
        };
      
        const tierKey = clean(row["Opp_DvP_Tier"]);
        const tierScore = tierOrder[tierKey] ?? 999;
      
        // optional: use rank as a tiny tie-breaker within each tier
        const rank = Number(row["Opp_DvP_Rank"]);
        const rankScore = Number.isFinite(rank) ? rank / 100 : 0;
      
        return tierScore * 100 + rankScore;
      }
      
      case "SS_Score":
        return Number(row.SS_Score ?? 0);

      // handle both "Tax" (from header) and "tax"
      case "Tax":
      case "tax": {
        const score = Number(row["Line_Tax_Score"]);
        if (Number.isFinite(score)) return score;

        const tier = String(row["Line_Tax_Tier"] || "").toUpperCase();
        if (tier.includes("HYPER")) return 3;
        if (tier.includes("TAXED")) return 2;
        if (tier.includes("FAIR")) return 1;
        if (tier.includes("DISCOUNT")) return 0;
        return -1;
      }

      // handle both "GuruAlert" (from header) and "guruAlert"
      case "GuruAlert":
      case "guruAlert": {
        const v =
          row?.GuruAlert ??
          row?.guruAlert ??
          row?.["GuruAlert"] ??
          row?.["guruAlert"];

        const n = Number(v);
        if (Number.isFinite(n)) return n;

        // no fallback — this column is explicitly provided by backend now
        return 0; // treat missing as NONE
      }


      default:
        return "";
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!sortConfig?.key) return safeRows;
    const { key, direction } = sortConfig;
    const dir = direction === "desc" ? -1 : 1;

    return [...safeRows].sort((a, b) => {
      const av = getSortValue(a, key);
      const bv = getSortValue(b, key);

      if (typeof av === "string" || typeof bv === "string") {
        return av.toString().localeCompare(bv.toString()) * dir;
      }
      if (key === "defenseRank" || key === "DefenseRank") {
        return compareNullableNumbers(av, bv, direction);
      }
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return (av - bv) * dir;
    });
  }, [safeRows, sortConfig]);

  const onHeaderClick = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const arrowFor = (key) => {
    if (sortConfig.key !== key) return "▾";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };



  // --- formatting helpers ---

  const formatProj = (row) => {
    const v = Number(
      row.FinalAdjustedScore ??
      row.Projection ??
      row.Proj ??
      row.Projected ??
      NaN
    );
    return Number.isFinite(v) ? v.toFixed(2) : "—";
  };

  const formatDelta = (row) => {
    const val = gridDeltaValue(row, activeSport);
    if (val === null) {
      return { text: "—", color: "#374151" };
    }
    const sign = val >= 0 ? "+" : "";
    const text = `${sign}${val.toFixed(2)}`;
    const color = val >= 0 ? "#16a34a" : "#dc2626";
    return { text, color };
  };

  const haLabel = (row) => {
    const ha = String(
      row["Home/Away"] ?? row.homeAway ?? row["homeAway"] ?? ""
    )
      .trim()
      .toLowerCase();
    if (ha === "home") return "🏠 Home";
    if (ha === "away") return "✈️ Away";
    return "";
  };

  const minutesValue = (row) => {
    // Supports NBA (mpg/Minutes) + NHL (TOI) + any standardized MIN fields.
    const v =
      getMinutesValue?.(row) ??
      row.mpg ??
      row.Minutes ??
      row.MIN ??
      row.min ??
      row["minutes_recent_mpg"] ??
      row.TOI ??
      row["TOI (min)"] ??
      row.toi ??
      row.toi_min ??
      row.TOI_MIN ??
      null;
    return v;
  };

  const colorBlowout = (v) => {
    if (v == null || v === "") return { text: "", style: {} };
  
    const raw = String(v).trim();
    const lower = raw.toLowerCase();
  
    let style = {
      display: "inline-flex",
      alignItems: "center",
      padding: "0.15rem 0.5rem",
      borderRadius: "999px",
      fontSize: "0.75rem",
      fontWeight: 600,
    };
  
    if (lower.includes("high")) {
      // high blowout risk
      style = { ...style, background: "#FEE2E2", color: "#991b1b" };
    } else if (lower.includes("mod")) {
      // moderate blowout risk
      style = { ...style, background: "#FEF3C7", color: "#92400e" };
    } else if (lower.includes("low")) {
      // low blowout risk
      style = { ...style, background: "#ECFDF5", color: "#166534" };
    } else {
      // unknown / other → treat as low-ish but keep raw text
      style = { ...style, background: "#ECFDF5", color: "#166534" };
    }
  
    return { text: raw, style };
  };
  
  // --- hover helpers for player cell ---

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};



  const headerStyle = {
    fontSize: 13,
    fontWeight: 600,
    textAlign: "left",
    padding: "0.45rem 0.75rem",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    cursor: "pointer",
  };

  const cellStyle = {
    fontSize: 13,
    padding: "0.45rem 0.75rem",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  };

  // derive which rows are selected (using parent function)
  const selectedRows = React.useMemo(
    () =>
      typeof isRowSelected === "function"
        ? sortedRows.filter((r) => isRowSelected(r))
        : [],
    [sortedRows, isRowSelected]
  );

  const allVisibleSelected =
    sortedRows.length > 0 &&
    selectedRows.length === sortedRows.length;

  // --- render ---

  if (isBasicCardExperience) {
    const isCanonicalMatchupGrid = supportsCanonicalMatchup;
    const fmtTime = (row) => row?.GameTime || row?.["Start Time"] || row?.game_time || "—";
    const fmtProp = (row) => row?.["Prop Type"] || row?.PropType || row?.prop_type || "—";
    const fmtLine = (row) => row?.["Prop Value"] ?? row?.PropValue ?? row?.Line ?? "—";
    return (
      <div style={{ marginTop: "0.75rem", marginBottom: "0.5rem" }}>
        {isCanonicalMatchupGrid && <label style={{ display:"inline-flex",alignItems:"center",gap:8,fontSize:12,fontWeight:800,color:"#334155",marginBottom:8 }}>
          Matchup
          <select aria-label="Matchup" value={matchupGradeFilter} onChange={(event)=>setMatchupGradeFilter(event.target.value)} style={{ padding:"7px 10px",border:"1px solid #CBD5E1",borderRadius:999,background:"#fff" }}>
            {MATCHUP_FILTER_OPTIONS.map(({ value, label })=><option key={value} value={value}>{label}</option>)}
          </select>
        </label>}
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 880, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
          <thead><tr style={{ background: "#f8fafc" }}>
            {[
              ["Player", "Player"],
              ["Sport", "Sport"],
              ["Team", "Team"],
              ["Opponent", "Opponent"],
              ["Game Time", "GameTime"],
              ["Prop", "Prop"],
              ["Line", "Line"],
              ["PWG Tag", "PWGTag"],
            ].map(([label, key]) => (
              <th
                key={key}
                onClick={() => onHeaderClick(key)}
                title={`Sort by ${label}`}
                style={{ ...headerStyle, userSelect: "none" }}
              >
                {label} <span style={{ opacity: sortConfig?.key === key ? 1 : 0.35 }}>{arrowFor(key)}</span>
              </th>
            ))}
            {isCanonicalMatchupGrid && <>
              <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchup")}>Matchup {arrowFor("NhlMatchup")}</th>
              <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchupRank")}>Matchup Rank {arrowFor("NhlMatchupRank")}</th>
              <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchupFactor")}>Opponent Factor {arrowFor("NhlMatchupFactor")}</th>
              <th style={headerStyle} onClick={() => onHeaderClick("NhlAdjustedProjection")}>Adjusted Projection {arrowFor("NhlAdjustedProjection")}</th>
              <th style={headerStyle} onClick={() => onHeaderClick("NhlAdjustedEdge")}>Adjusted Edge {arrowFor("NhlAdjustedEdge")}</th>
              <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchupSample")}>Sample {arrowFor("NhlMatchupSample")}</th>
              <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchupReliability")}>Reliability {arrowFor("NhlMatchupReliability")}</th>
            </>}
            <th style={{ ...headerStyle, cursor: "default" }}>My Plays</th>
          </tr></thead>
          <tbody>
            {sortedRows.map((row, i) => {
              const saved = typeof isInMyList === "function" ? isInMyList(row) : false;
              const tag = getRowTag(row) || "COIN TOSS";
              const tagBg = tagColorMap?.[tag] || tagColorMap?.[String(tag).toUpperCase()] || "#94a3b8";
              return <tr key={`${row?.Sport || ""}-${row?.Player || i}-${fmtProp(row)}-${fmtLine(row)}`}>
                <td style={{...cellStyle,fontWeight:800}}>{row?.Player || "—"}</td>
                <td style={cellStyle}><span style={{display:"inline-flex",padding:"3px 8px",borderRadius:999,background:"#e2e8f0",color:"#0f172a",fontWeight:900,fontSize:11}}>{String(row?.Sport || "—").toUpperCase()}</span></td>
                <td style={cellStyle}>{row?.Team || row?.["Team Name"] || "—"}</td>
                <td style={cellStyle}>{row?.Opponent || row?.["Opponent Name"] || "—"}</td>
                <td style={cellStyle}>{fmtTime(row)}</td>
                <td style={cellStyle}>{fmtProp(row)}</td>
                <td style={{...cellStyle,fontWeight:800}}>{fmtLine(row)}</td>
                <td style={cellStyle}><span style={{display:"inline-flex",padding:"4px 10px",borderRadius:999,background:tagBg,color:"#fff",fontWeight:900,fontSize:12,whiteSpace:"nowrap"}}>{tag}</span></td>
                {isCanonicalMatchupGrid && (() => {
                  const matchupCells = nhlGridMatchupCells(row);
                  return <>
                    {matchupCells.map((value, index) => <td key={index} style={cellStyle}>{value}</td>)}
                  </>;
                })()}
                <td style={cellStyle}><button type="button" onClick={() => onToggleMyPlay?.(row)} style={{padding:"7px 12px",borderRadius:999,border:saved?"1px solid #2563eb":"1px solid #cbd5e1",background:saved?"#eff6ff":"#fff",color:saved?"#1d4ed8":"#0f172a",fontWeight:900,cursor:"pointer",whiteSpace:"nowrap"}}>{saved ? "✓ Added" : "☆ Add"}</button></td>
              </tr>;
            })}
          </tbody>
        </table>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "0.75rem", marginBottom: "0.5rem" }}>
      {/* header row: columns pill + Add / Remove buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.75rem",
        }}
      >
        {/* Columns dropdown */}
        <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
        {supportsCanonicalMatchup && <label style={{ fontSize:12,fontWeight:800,color:"#334155" }}>
          Matchup&nbsp;
          <select aria-label="Matchup" value={matchupGradeFilter} onChange={(event)=>setMatchupGradeFilter(event.target.value)} style={{ padding:"7px 10px",border:"1px solid #CBD5E1",borderRadius:999,background:"#fff" }}>
            {MATCHUP_FILTER_OPTIONS.map(({ value, label })=><option key={value} value={value}>{label}</option>)}
          </select>
        </label>}
        <div ref={columnsRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setColumnsOpen((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "0.45rem 0.9rem",
              borderRadius: 999,
              background: "#ffffff",
              border: "1px solid rgba(148,163,184,0.6)",
              boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#111827",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            aria-haspopup="menu"
            aria-expanded={columnsOpen}
          >
            Columns
            <span style={{ opacity: 0.7, fontWeight: 700 }}>
              ({renderedOptionalColumnCount})
            </span>
            <span style={{ opacity: 0.7 }}>{columnsOpen ? "▴" : "▾"}</span>
          </button>

          {columnsOpen && (
            <div
              role="menu"
              style={{
                position: "absolute",
                zIndex: 50,
                top: "calc(100% + 8px)",
                left: 0,
                minWidth: 260,
                background: "#ffffff",
                border: "1px solid rgba(148,163,184,0.6)",
                borderRadius: 12,
                boxShadow: "0 12px 24px rgba(15,23,42,0.12)",
                padding: "0.6rem",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: "0.85rem", color: "#111827" }}>Show columns</span>
                <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() =>
                    setColumns((prev) => {
                      const prevMap = new Map((Array.isArray(prev) ? prev : []).map((c) => [c.key, c]));
                      return togglesForMenu.map((t) => ({
                        key: t.key,
                        visible: true,
                      }));
                    })
                  }                  
                  style={{ fontSize: "0.75rem", border: "none", background: "transparent", color: "#2563eb", cursor: "pointer" }}
                >
                  All
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setColumns((prev) => {
                      return togglesForMenu.map((t) => ({
                        key: t.key,
                        visible: false,
                      }));
                    })
                  }
                  
                  style={{ fontSize: "0.75rem", border: "none", background: "transparent", color: "#2563eb", cursor: "pointer" }}
                >
                  None
                </button>

                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {togglesForMenu.map((col) => {
                  const checked = showCol(col.key);
                  return (
                    <label
                      key={col.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "0.35rem 0.5rem",
                        borderRadius: 10,
                        border: checked ? "1px solid rgba(17,24,39,0.65)" : "1px solid rgba(148,163,184,0.45)",
                        background: checked ? "rgba(17,24,39,0.04)" : "transparent",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggleCol(col.key)}
                        style={{ accentColor: "#111827" }}
                      />
                      <span style={{ fontSize: "0.82rem", color: "#111827" }}>{col.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div></div>

{/* ⭐ Add / Remove selected from My Plays */}
        <div style={{ display: "flex", gap: "0.5rem" }}>
        {onBulkAddToGuruList && (
          <button
            type="button"
            disabled={!selectedRows || selectedRows.length === 0}
            onClick={() => onBulkAddToGuruList(selectedRows)}
            title={
              !selectedRows || selectedRows.length === 0
                ? "Select at least 1 line"
                : "Add selected to My Plays"
            }
            style={{
              padding: "0.45rem 0.9rem",
              borderRadius: 999,
              border: "1px solid #e5e7eb",
              background: "#f4f4f5",
              color: "#374151",
              fontSize: "0.8rem",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
              cursor:
                !selectedRows || selectedRows.length === 0
                  ? "not-allowed"
                  : "pointer",
              opacity: !selectedRows || selectedRows.length === 0 ? 0.55 : 1,
              boxShadow: "0 1px 1px rgba(15,23,42,0.04)",
              whiteSpace: "nowrap",
            }}
          >
            <span role="img" aria-label="guru add">
              ⭐
            </span>
            Guru +
          </button>
        )}


        {onBulkRemoveFromGuruList && (
          <button
            type="button"
            disabled={!selectedRows || selectedRows.length === 0}
            onClick={() => onBulkRemoveFromGuruList(selectedRows)}
            title={
              !selectedRows || selectedRows.length === 0
                ? "Select at least 1 line"
                : "Remove selected from My Plays"
            }
            style={{
              padding: "0.45rem 0.9rem",
              borderRadius: 999,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#b91c1c",
              fontSize: "0.8rem",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
              cursor:
                !selectedRows || selectedRows.length === 0
                  ? "not-allowed"
                  : "pointer",
              opacity: !selectedRows || selectedRows.length === 0 ? 0.55 : 1,
              boxShadow: "0 1px 1px rgba(15,23,42,0.04)",
              whiteSpace: "nowrap",
            }}
          >
            <span role="img" aria-label="guru remove">
              ⭐
            </span>
            Guru −
          </button>
        )}
        

        {/* Details (full-screen) */}
        <button
          type="button"
          disabled={!selectedRows || selectedRows.length !== 1}
          onClick={() => onOpenDetails && onOpenDetails(selectedRows[0])}
          title={
            !selectedRows || selectedRows.length !== 1
              ? "Select exactly 1 line to view details"
              : "Open details"
          }
          style={{
            padding: "0.45rem 0.9rem",
            borderRadius: 999,
            border: "1px solid #dbeafe",
            background: "#eff6ff",
            color: "#1d4ed8",
            fontSize: "0.8rem",
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
            cursor:
              !selectedRows || selectedRows.length !== 1 ? "not-allowed" : "pointer",
            opacity: !selectedRows || selectedRows.length !== 1 ? 0.55 : 1,
            whiteSpace: "nowrap",
          }}
        >
          <span role="img" aria-label="details">
            🔎
          </span>
          Details
        </button>

        {/* Compare (max 8) */}
        <button
          type="button"
          disabled={!selectedRows || selectedRows.length < 2 || selectedRows.length > COMPARE_MAX}
          onClick={() => onOpenCompare && onOpenCompare(selectedRows)}
          title={
            !selectedRows || selectedRows.length < 2
              ? "Select 2–8 lines to compare"
              : selectedRows.length > COMPARE_MAX
                ? "Max 8 lines for Compare"
                : "Compare selected (max 8)"
          }
          style={{
            padding: "0.45rem 0.9rem",
            borderRadius: 999,
            border: "1px solid #e9d5ff",
            background: "#faf5ff",
            color: "#6b21a8",
            fontSize: "0.8rem",
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
            cursor:
              !selectedRows || selectedRows.length < 2 || selectedRows.length > COMPARE_MAX
                ? "not-allowed"
                : "pointer",
            opacity:
              !selectedRows || selectedRows.length < 2 || selectedRows.length > COMPARE_MAX
                ? 0.55
                : 1,
            whiteSpace: "nowrap",
          }}
        >
          <span role="img" aria-label="compare">
            ⚖️
          </span>
          Compare
        </button>


        </div>
      </div>

      {/* table wrapper */}
      <div
        style={{
          borderRadius: 12,
          overflowX: "auto",
          overflowY: "hidden",
          boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
          background: "#ffffff",
          marginTop: "0.5rem",
        }}
      >
        <table style={{ width: "100%", minWidth: "1100px", borderCollapse: "collapse" }}>
          <thead style={{ background: "#f9fafb" }}>
            <tr>
              {/* NEW: select-all header checkbox */}
              <th
                style={{
                  ...headerStyle,
                  width: "2.5rem",
                  textAlign: "center",
                  cursor: "default",
                }}
              >
                {typeof onToggleAllVisible === "function" && (
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => onToggleAllVisible(sortedRows)}
                  />
                )}
              </th>

              <th style={headerStyle} onClick={() => onHeaderClick("Player")}>
                Player {arrowFor("Player")}
              </th>
              <th style={headerStyle} onClick={() => onHeaderClick("Team")}>
                Team {arrowFor("Team")}
              </th>
              <th style={headerStyle} onClick={() => onHeaderClick("Opponent")}>
                Opponent {arrowFor("Opponent")}
              </th>
              {showCol("battingOrder") && activeSport === "MLB" && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("BattingOrder")}
                  title="Batting Order — MLB hitter props only"
                >
                  BO {arrowFor("BattingOrder")}
                </th>
              )}
              {showCol("ha") && (
                <th style={headerStyle} onClick={() => onHeaderClick("HA")}>
                  H/A {arrowFor("HA")}
                </th>
              )}
              <th style={headerStyle} onClick={() => onHeaderClick("Prop")}>
                Prop {arrowFor("Prop")}
              </th>
              <th style={headerStyle} onClick={() => onHeaderClick("Line")}>
                Line {arrowFor("Line")}
              </th>
              {showCol("proj") && (
                <th style={headerStyle} onClick={() => onHeaderClick("Proj")}>
                  Proj {arrowFor("Proj")}
                </th>
              )}
              {showCol("delta") && (
                <th style={headerStyle} onClick={() => onHeaderClick("Delta")}>
                  Δ {arrowFor("Delta")}
                </th>
              )}
              
              {showCol("pwgProb") && (
                <th style={headerStyle} onClick={() => onHeaderClick("PWGProb")}>
                  PWG Prob {arrowFor("PWGProb")}
                </th>
              )}
              {showCol("minutesHeat") && allowsMinutesUi(activeSport) && activeSport !== "MLB" && (
                <th
                  style={headerStyle}
                  onClick={() => onHeaderClick("MinutesHeat")}
                  title="Minutes Heat (HOT/WATCH/etc.)"
                >
                  Minutes Heat {arrowFor("MinutesHeat")}
                </th>
              )}
              {showCol("bounceLabel") && (
                <th
                  style={headerStyle}
                  onClick={() => onHeaderClick("BounceLabel")}
                  title="Backend Bounce Classification"
                >
                  Bounce State {arrowFor("BounceLabel")}
                </th>
              )}
              {showCol("bounceScore") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("BounceScore")}
                  title="How strongly the recent series fits the Bounce State"
                >
                  Bounce Score {arrowFor("BounceScore")}
                </th>
              )}
              {showCol("l2MinDelta") && allowsMinutesUi(activeSport) && activeSport !== "MLB" && (
                <th
                  style={headerStyle}
                  onClick={() => onHeaderClick("L2MinDelta")}
                  title="Last 2 Minutes Δ"
                >
                  L2 Min Δ {arrowFor("L2MinDelta")}
                </th>
              )}

              {showCol("l2PaDelta") && activeSport === "MLB" && (
                <th
                  style={headerStyle}
                  onClick={() => onHeaderClick("L2PaDelta")}
                  title="Last 2 Plate Appearances Δ"
                >
                  L2 PA Δ {arrowFor("L2PaDelta")}
                </th>
              )}

              {showCol("l2OutsDelta") && activeSport === "MLB" && (
                <th
                  style={headerStyle}
                  onClick={() => onHeaderClick("L2OutsDelta")}
                  title="Last 2 Outs Δ"
                >
                  L2 Outs Δ {arrowFor("L2OutsDelta")}
                </th>
              )}

              {showCol("l2StatDelta") && (
                <th
                  style={headerStyle}
                  onClick={() => onHeaderClick("L2StatDelta")}
                  title="Last 2 Stat Δ"
                >
                  L2 Stat Δ {arrowFor("L2StatDelta")}
                </th>
              )}
              {showCol("heatNote") && (
                <th
                  style={headerStyle}
                  onClick={() => onHeaderClick("HeatNote")}
                  title="Heat Note"
                >
                  Heat Note {arrowFor("HeatNote")}
                </th>
              )}
              {showCol("minutes") && allowsMinutesUi(activeSport) && (
                <th style={headerStyle} onClick={() => onHeaderClick("Minutes")}>
                  Minutes {arrowFor("Minutes")}
                </th>
              )}
              {showCol("tax") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("tax")}
                >
                  Line Tax {arrowFor("tax")}
                </th>
              )}
              {showCol("blowout") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("blowout")}
                >
                  Blowout {arrowFor("blowout")}
                </th>
              )}



              {showCol("dvpTier") && (
                <th
                  style={headerStyle}
                  onClick={() => onHeaderClick("dvpTier")}
                  title={["MLB", "KBO"].includes(String(activeSport || "").toUpperCase()) ? "Baseball matchup context: hitters show opposing pitcher context; pitchers show opposing hitter/K context" : "DvP matchup tier"}
                >
                  {["MLB", "KBO"].includes(String(activeSport || "").toUpperCase()) ? "Matchup" : "DvP Tier"} {arrowFor("dvpTier")}
                </th>
              )}

              {supportsCanonicalMatchup && <>
                <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchup")}>Matchup {arrowFor("NhlMatchup")}</th>
                <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchupRank")}>Matchup Rank {arrowFor("NhlMatchupRank")}</th>
                <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchupFactor")}>Opponent Factor {arrowFor("NhlMatchupFactor")}</th>
                <th style={headerStyle} onClick={() => onHeaderClick("NhlAdjustedProjection")}>Adjusted Projection {arrowFor("NhlAdjustedProjection")}</th>
                <th style={headerStyle} onClick={() => onHeaderClick("NhlAdjustedEdge")}>Adjusted Edge {arrowFor("NhlAdjustedEdge")}</th>
                <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchupSample")}>Sample {arrowFor("NhlMatchupSample")}</th>
                <th style={headerStyle} onClick={() => onHeaderClick("NhlMatchupReliability")}>Reliability {arrowFor("NhlMatchupReliability")}</th>
              </>}


              {showCol("confidence") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("Confidence")}
                >
                  Confidence {arrowFor("Confidence")}
                </th>
              )}
              {showCol("l5Setup") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("l5Setup")}
                  title="L5 Setup (Last 5 shape)"
                >
                  L5 Setup {arrowFor("l5Setup")}
                </th>
              )}


              {showCol("l10HitPct") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("L10HitPct")}
                >
                  L10 Hit% {arrowFor("L10HitPct")}
                </th>
              )}
              {showCol("tag") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("Tag")}
                >
                  Tag {arrowFor("Tag")}
                </th>
              )}

              {showCol("guruScore") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("GuruScore")}
                >
                  Guru Score {arrowFor("GuruScore")}
                </th>
              )}
              {showCol("dqs") && (
                <th
                  style={{ ...headerStyle, cursor: "pointer" }}
                  onClick={() => onHeaderClick("DQS")}
                >
                  DQS {arrowFor("DQS")}
                </th>
              )}

              {showCol("guruRank") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("GuruRank")}
                >
                  Guru Rank {arrowFor("GuruRank")}
                </th>
              )}
              {showCol("guruGrade") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("GuruGrade")}
                  title="Guru Grade (Conviction)"
                >
                  Guru Grade {arrowFor("GuruGrade")}
                </th>
              )}

              {showCol("selStrat") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("SS_Score")}
                >
                  Sel Strat {arrowFor("SS_Score")}
                </th>
              )}

              <GridOptionalHeaders definitions={registryRenderedColumns} headerStyle={headerStyle} onSort={onHeaderClick} arrowFor={arrowFor} />

              {showCol("guruAlert") && (
                <th
                  style={{ ...headerStyle, textAlign: "center" }}
                  onClick={() => onHeaderClick("GuruAlert")}
                >
                  Guru Alert {arrowFor("GuruAlert")}
                </th>
              )}

            </tr>
          </thead>

          <tbody>
            {sortedRows.map((row, idx) => {
              const projText = formatProj(row);
              const delta = formatDelta(row);
              // Confidence can now be a backend label for WNBA (Elite/Strong/Moderate/Low)
              // or an older numeric value for other sports. Use the shared resolver instead
              // of Number(row.Confidence), otherwise label-based WNBA rows render as "—".
              const confRaw = getConfidenceValue(row);
              const confLabel = getDisplayConfidenceLabel(confRaw, activeSport);
              const confVal = Number(confRaw);
              const mins = minutesValue(row);
              const blowout = getBlowoutTierRaw(row);

              const l10Record =
                row["L10 vs PROP"] ||
                row["L10_vsPROP"] ||
                row["L10_vs_PROP"] ||
                "";

              const l10HitPct =
                row["L10 vs PROP Hit%"] ||
                row["L10_vsPROP_Hit%"] ||
                row["L10_vsPROP_HitPct"] ||
                row["H% L10 vs PROP"] ||
                "";
              
              const tag = row.Tag || row.tag || "";
              const tagColor = tagColorMap?.[tag] || "#4b5563";

              const selected =
                typeof isRowSelected === "function"
                  ? isRowSelected(row)
                  : false;

              return (
                <tr
                    key={`${row.Player}-${row["Prop Type"]}-${idx}`}
                    onDoubleClick={() =>
                      typeof onOpenDetails === "function" && onOpenDetails(row)
                    }
                    style={{
                      cursor: typeof onOpenDetails === "function" ? "pointer" : "default",
                    }}
                  >

                  {/* row checkbox */}
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={!!selected}
                      onClick={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                      onChange={() =>
                        typeof onToggleRowSelected === "function" &&
                        onToggleRowSelected(row)
                      }
                    />
                  </td>

                  <td
                    style={cellStyle}
                    title={buildPlayerHover(row)}
                  >
                    {row.Player}
                  </td>

                  <td style={cellStyle}>{row.Team}</td>
                  <td style={cellStyle}>{row.Opponent}</td>

                  {showCol("battingOrder") && activeSport === "MLB" && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {renderBattingOrderCell(row, activeSport)}
                    </td>
                  )}

                  {showCol("ha") && (
                    <td style={cellStyle}>{haLabel(row) || "—"}</td>
                  )}

                  <td style={cellStyle}>{row["Prop Type"]}</td>

                  <td style={cellStyle}>
                    {Number.isFinite(Number(row["Prop Value"]))
                      ? Number(row["Prop Value"]).toFixed(2)
                      : row["Prop Value"]}
                  </td>

                  {showCol("proj") && <td style={cellStyle}>{projText}</td>}

                  {showCol("delta") && (
                    <td style={{ ...cellStyle, color: delta.color }}>
                      {delta.text}
                    </td>
                  )}
                  
                  {showCol("pwgProb") && (
                    <td style={{ ...cellStyle, fontWeight: 700 }}>
                      {(() => {
                        const pwg = pwgProbLabelForRow(row);
                        return pwg?.text || "—";
                      })()}
                    </td>
                  )}
                  {showCol("minutesHeat") && allowsMinutesUi(activeSport) && activeSport !== "MLB" && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const v = getMinutesHeatValue(row);
                        if (!v) return "—";
                        const up = v.toUpperCase();
                        const bg =
                          up === "HOT" ? "#dcfce7" :
                          up === "WATCH" ? "#fef9c3" :
                          up === "COLD" ? "#fee2e2" :
                          "#e5e7eb";
                        const fg =
                          up === "HOT" ? "#166534" :
                          up === "WATCH" ? "#854d0e" :
                          up === "COLD" ? "#b91c1c" :
                          "#374151";
                        return (
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "2px 10px",
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 800,
                            background: bg,
                            color: fg,
                            whiteSpace: "nowrap",
                          }}>
                            {up}
                          </span>
                        );
                      })()}
                    </td>
                  )}

                  {showCol("bounceLabel") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const label = getBounceLabel(row);
                        if (!label) return "—";
                        const family = getBounceFamily(row);
                        const tone = family === "CONSTRUCTIVE" || family === "REVERSAL"
                          ? { bg: "#dcfce7", fg: "#166534", bd: "#86efac" }
                          : family === "EXHAUSTION" || family === "FAILURE"
                            ? { bg: "#fef3c7", fg: "#92400e", bd: "#fcd34d" }
                            : { bg: "#f1f5f9", fg: "#334155", bd: "#cbd5e1" };
                        return (
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "2px 9px",
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 800,
                            background: tone.bg,
                            color: tone.fg,
                            border: `1px solid ${tone.bd}`,
                            whiteSpace: "nowrap",
                          }}>
                            {label}
                          </span>
                        );
                      })()}
                    </td>
                  )}

                  {showCol("bounceScore") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const score = getBounceStateScore(row);
                        if (!Number.isFinite(score)) return "—";
                        return (
                          <span
                            title="Bounce State Score: how strongly the recent series fits its assigned state."
                            style={{
                              display: "inline-flex",
                              minWidth: 34,
                              justifyContent: "center",
                              padding: "2px 8px",
                              borderRadius: 999,
                              background: "#f1f5f9",
                              color: "#334155",
                              border: "1px solid #cbd5e1",
                              fontSize: 12,
                              fontWeight: 900,
                            }}
                          >
                            {Math.round(score)}
                          </span>
                        );
                      })()}
                    </td>
                  )}

                  {showCol("l2MinDelta") && allowsMinutesUi(activeSport) && activeSport !== "MLB" && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const n = getL2MinDeltaValue(row);
                        if (!Number.isFinite(n)) return "—";
                        const s = n >= 0 ? `+${n.toFixed(1)}` : n.toFixed(1);
                        return (
                          <span style={{ color: n >= 0 ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                            {s}
                          </span>
                        );
                      })()}
                    </td>
                  )}

                  {showCol("l2PaDelta") && activeSport === "MLB" && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const n = getL2PaDeltaValue(row);
                        if (!Number.isFinite(n)) return "—";
                        const s = n >= 0 ? `+${n.toFixed(1)}` : n.toFixed(1);
                        return (
                          <span style={{ color: n >= 0 ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                            {s}
                          </span>
                        );
                      })()}
                    </td>
                  )}

                  {showCol("l2StatDelta") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const n = getL2StatDeltaValue(row);
                        if (!Number.isFinite(n)) return "—";
                        const s = n >= 0 ? `+${n.toFixed(1)}` : n.toFixed(1);
                        return (
                          <span style={{ color: n >= 0 ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                            {s}
                          </span>
                        );
                      })()}
                    </td>
                  )}

                  {showCol("l2OutsDelta") && activeSport === "MLB" && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const n = getL2OutsDeltaValue(row);
                        if (!Number.isFinite(n)) return "—";
                        const s = n >= 0 ? `+${n.toFixed(1)}` : n.toFixed(1);
                        return (
                          <span style={{ color: n >= 0 ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                            {s}
                          </span>
                        );
                      })()}
                    </td>
                  )}

                  {showCol("heatNote") && (
                    <td style={{ ...cellStyle, maxWidth: 360 }}>
                      {getHeatNoteValue(row) || "—"}
                    </td>
                  )}

                  {showCol("minutes") && allowsMinutesUi(activeSport) && (
                    <td style={cellStyle}>
                      {mins != null ? Number(mins).toFixed(1) : "—"}
                    </td>
                  )}
                  {showCol("tax") && (
                    <td style={cellStyle}>
                      {renderLineTaxCell(row) || "—"}
                    </td>
                  )}

                  {showCol("blowout") && (
                    <td style={cellStyle}>
                      {renderBlowoutCell(row)}
                    </td>
                  )}


                  {showCol("dvpTier") && (
                    <td style={cellStyle}>
                      {renderMatchupTierCell(row, activeSport) || "—"}
                    </td>
                  )}

                  {supportsCanonicalMatchup && (()=>{ const matchupCells=nhlGridMatchupCells(row); return <>
                    {matchupCells.map((value,index)=><td key={index} style={cellStyle}>{value}</td>)}
                  </>; })()}

                  {showCol("confidence") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {confLabel && confLabel !== "Unknown" ? (
                        <div
                          style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <span
                            title={`Confidence: ${confLabel}${Number.isFinite(confVal) ? ` (${confVal.toFixed(2)})` : ""}`}
                            style={{
                              padding: "0.15rem 0.5rem",
                              borderRadius: 999,
                              background: "#EEF2FF",
                              color: "#3730a3",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {confLabel}
                          </span>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                  )}
                  {showCol("l5Setup") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {renderL5SetupCell(row)}
                    </td>
                  )}

                  {showCol("l10HitPct") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const v =
                          row.L10_HitPct ??
                          row["L10_HitPct"] ??
                          row.l10HitPct ??
                          row["l10HitPct"] ??
                          null;
                        const n = Number(v);
                        return Number.isFinite(n) ? `${Math.round(n * 100)}%` : "—";
                      })()}
                    </td>
                  )}
                  {showCol("tag") && (
                    <td style={{ ...cellStyle, textAlign: "center", fontSize: 9,
                    padding: "2px 8px"
                    }}>
                      {(() => {
                        const tag = getTagValue(row);
                        if (!tag) return "—";

                        const bg = tagColorMap?.[tag] || "#9ca3af";

                        return (
                          <span
                            title={tag}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "2px 10px",
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 800,
                              letterSpacing: 0.3,
                              backgroundColor: bg,
                              color: "#fff",
                              border: "1px solid rgba(0,0,0,0.10)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tag}
                          </span>
                        );
                      })()}
                    </td>
                  )}


                  {showCol("guruScore") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const s = getGuruScoreValue(row);
                        return Number.isFinite(s) ? s.toFixed(2) : "—";
                      })()}
                    </td>
                  )}
                  {showCol("dqs") && (
                    <td style={cellStyle}>
                      {(() => {
                        const v = row?.dqs ?? computeDQS(row);
                        const n = Number(v);
                        return Number.isFinite(n) ? n.toFixed(2) : "—";
                      })()}
                    </td>
                  )}

                  {showCol("guruRank") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const k = cardKey(row);
                        const r = guruRankMap?.get ? guruRankMap.get(k) : null;
                        const n = Number(r);
                        return Number.isFinite(n) ? n : "—";
                      })()}
                    </td>
                  )}
                  {showCol("guruGrade") && (
                    <td style={{ ...cellStyle, textAlign: "center" }} title="Guru Grade (Conviction)">
                      {(() => {
                        const g =
                          row.GuruGrade ??
                          row["Guru Grade"] ??
                          row.Guru_Grade ??
                          row.GuruGr ??
                          row["Guru Gr"] ??
                          row.guruGrade ??
                          row.guru_grade;
                        const s = (g ?? "").toString().trim();
                        return s ? s : "—";
                      })()}
                    </td>
                  )}

                  {showCol("selStrat") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const label = String(row.SelectionStrategy || "").trim();
                        const over = Number(row.SS_OverCount ?? 0);
                        const under = Number(row.SS_UnderCount ?? 0);
                        const neutral = Number(row.SS_NeutralCount ?? 0);
                        const dir = String(row.SS_Direction || "").toUpperCase();
                        const score = Number(row.SS_Score ?? NaN);

                        if (!label && !dir) return "—";

                        const isNoEdge = label.toUpperCase().includes("NO CLEAR EDGE");
                        const main = isNoEdge ? "NO CLEAR EDGE" : (dir || label);
                        
                        const triplet = `(${over}/${under}/${neutral})`;

                        let bg = "#e5e7eb";
                        let fg = "#374151";
                        const lower = main.toLowerCase();

                        if (lower.includes("no clear edge") || lower.includes("no_edge")) {
                          bg = "#f3f4f6"; fg = "#374151";   // gray pill for NO CLEAR EDGE
                        } else if (lower.includes("over")) {
                          bg = "#dcfce7"; fg = "#166534";   // green for OVER
                        } else if (lower.includes("under")) {
                          bg = "#fee2e2"; fg = "#b91c1c";   // red for UNDER
                        } else if (lower.includes("mixed")) {
                          bg = "#fef9c3"; fg = "#92400e";   // yellow for MIXED
                        } else if (lower.includes("coin")) {
                          bg = "#e5e7eb"; fg = "#374151";   // neutral for COIN TOSS
                        }

                        const text = `${main} ${triplet}`;

                        return (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "2px 8px",
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 600,
                              background: bg,
                              color: fg,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {text}
                          </span>
                        );
                      })()}
                    </td>
                  )}

                  <GridOptionalCells definitions={registryRenderedColumns} row={row} cellStyle={cellStyle} />

                  {showCol("guruAlert") && (
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {(() => {
                        const level = Number(row?.GuruAlert ?? row?.["GuruAlert"] ?? 0);
                        if (!Number.isFinite(level) || level <= 0) return "—";

                        const meta = getGuruAlertMeta(row);

                        // fallback direction if needed
                        const pick = String(
                          row?.Contrarian_Pick ??
                          row?.["Contrarian_Pick"] ??
                          ""
                        ).toUpperCase().trim();

                        const dir =
                          pick === "OVER" || pick === "UNDER"
                            ? pick
                            : (String(meta?.title || "").toUpperCase().includes("UNDER")
                                ? "UNDER"
                                : String(meta?.title || "").toUpperCase().includes("OVER")
                                  ? "OVER"
                                  : "");

                        let dotColor = "#9ca3af";
                        if (dir === "OVER") dotColor = "#16a34a";
                        if (dir === "UNDER") dotColor = "#dc2626";

                        const isWatch = level === 1;

                        return (
                          <span
                            title={
                              meta?.title ||
                              (isWatch ? `GURU WATCH — ${dir}` : `GURU ALERT — ${dir}`)
                            }
                            style={{
                              display: "inline-block",
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              background: isWatch ? "transparent" : dotColor,
                              border: `2px solid ${dotColor}`,
                              boxShadow: "0 0 0 2px rgba(0,0,0,0.06)",
                            }}
                          />
                        );
                      })()}
                    </td>
                  )}

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}, [activeSport]);







// --- TEG: table view for all tickers (Grid mode) ---
function TEGGridTable({ rows = [] }) {
  const [sortKey, setSortKey] = React.useState("ticker");
  const [sortDir, setSortDir] = React.useState("asc");
  const [showColPicker, setShowColPicker] = React.useState(false);
  const [visibleCols, setVisibleCols] = React.useState({
    ticker: true,
    company: true,
    sector: true,
    signal: true,
    price: true,
    pct1w: true,
    pct1m: true,
    pct3m: true,
    pctYtd: true,
  });

  // NEW: which ticker is currently hovered (for mini-card preview)
  const [hoveredTicker, setHoveredTicker] = React.useState(null);

  const colMeta = [
    { key: "ticker", label: "Ticker" },
    { key: "company", label: "Company" },
    { key: "sector", label: "Sector" },
    { key: "signal", label: "Signal" },
    { key: "price", label: "Price" },
    { key: "pct1w", label: "1W" },
    { key: "pct1m", label: "1M" },
    { key: "pct3m", label: "3M" },
    { key: "pctYtd", label: "YTD" },
  ];

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const toggleColumn = (key) => {
    setVisibleCols((prev) => {
      const currentlyOn = Object.values(prev).filter(Boolean).length;
      // prevent turning off the last visible column
      if (prev[key] && currentlyOn <= 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const sortValue = (row, key) => {
    switch (key) {
      case "ticker":
        return (
          row.Ticker || row.ticker || row.Symbol || row.symbol || ""
        );
      case "company":
        return row.Company || row.company || "";
      case "sector":
        return row.Sector || row.sector || "";
      case "signal":
        return row.Signal || row.Tag || row.signal || "";
      case "price":
        return row.price ?? null;
      case "pct1w":
        return row.pct_1w ?? null;
      case "pct1m":
        return row.pct_1m ?? null;
      case "pct3m":
        return row.pct_3m ?? null;
      case "pctYtd":
        return row.pct_ytd ?? null;
      default:
        return null;
    }
  };

  const sortedRows = React.useMemo(() => {
    const data = Array.isArray(rows) ? [...rows] : [];
    data.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === "number" && typeof bv === "number") {
        return av - bv;
      }
      return String(av).localeCompare(String(bv));
    });
    if (sortDir === "desc") data.reverse();
    return data;
  }, [rows, sortKey, sortDir]);

  const fmtPct = (v) =>
    v == null || isNaN(v) ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;

  const colorFor = (v) =>
    v == null || isNaN(v)
      ? "#374151"
      : v > 0
      ? "#166534"
      : "#b91c1c";

  const sortArrow = (key) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  // --- badge helpers -------------------------------------------------

  const sectorBadge = (sector) => {
    if (!sector) return "—";
    return (
      <span
        style={{
          fontSize: "0.75rem",
          color: "#6b7280",      // soft gray text
          fontWeight: 500,
          letterSpacing: "0.01em",
        }}
      >
        {sector}
      </span>
    );
  };


  const signalBadge = (signalRaw) => {
    const signal = String(signalRaw || "N/A").toUpperCase();

    let bg = "#e5e7eb";
    let fg = "#111827";

    if (["BUY", "CALLS", "CALL", "BULLISH", "LONG", "LEAPS"].includes(signal)) {
      bg = "#dcfce7";
      fg = "#166534";
    } else if (
      ["SELL", "PUTS", "PUT", "BEARISH", "SHORT"].includes(signal)
    ) {
      bg = "#fee2e2";
      fg = "#b91c1c";
    } else if (["HOLD", "NEUTRAL", "N/A"].includes(signal)) {
      bg = "#e5e7eb";
      fg = "#4b5563";
    } else if (["WATCH", "ALERT"].includes(signal)) {
      bg = "#fef3c7";
      fg = "#92400e";
    }

    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 999,
          fontSize: "0.7rem",
          fontWeight: 700,
          background: bg,
          color: fg,
          textTransform: "uppercase",
        }}
      >
        {signal}
      </span>
    );
  };

  // --- mini-card hover preview ---------------------------------------

  // --- mini-card hover preview ---------------------------------------



  const MiniCard = ({ row, placeAbove }) => {
    const ticker =
      row.Ticker || row.ticker || row.Symbol || row.symbol || "";
    const company = row.Company || row.company || "";
    const sector = row.Sector || row.sector || "";
    const price = row.price;
    const pct1w = row.pct_1w;
    const pct1m = row.pct_1m;
    const pct3m = row.pct_3m;
    const pctYtd = row.pct_ytd;
    const signal = row.Signal || row.Tag || row.signal || "N/A";

    return (
      <div
        style={{
          position: "absolute",
          // if placeAbove is true, attach above the cell, otherwise below
          ...(placeAbove
            ? { bottom: "110%" }
            : { top: "110%" }),
          left: 0,
          zIndex: 40,
          width: 260,
          padding: "10px 12px",
          background: "#ffffff",
          borderRadius: 12,
          boxShadow: "0 10px 25px rgba(15,23,42,0.18)",
          border: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            fontWeight: 800,
            fontSize: "0.9rem",
            marginBottom: 2,
          }}
        >
          {ticker} · {company}
        </div>
        <div
          style={{
            fontSize: "0.78rem",
            color: "#6b7280",
            marginBottom: 4,
          }}
        >
          {sector || "—"}
        </div>
        <div
          style={{
            fontSize: "0.8rem",
            marginBottom: 4,
          }}
        >
          <strong>Price:</strong>{" "}
          {price != null ? `$${price.toFixed(2)}` : "—"}
        </div>
        <div
          style={{
            fontSize: "0.78rem",
            marginBottom: 4,
          }}
        >
          <strong>Trend:</strong>{" "}
          <span style={{ color: colorFor(pct1w) }}>1W {fmtPct(pct1w)}</span>{" "}
          ·{" "}
          <span style={{ color: colorFor(pct1m) }}>1M {fmtPct(pct1m)}</span>{" "}
          ·{" "}
          <span style={{ color: colorFor(pct3m) }}>3M {fmtPct(pct3m)}</span>{" "}
          ·{" "}
          <span style={{ color: colorFor(pctYtd) }}>YTD {fmtPct(pctYtd)}</span>
        </div>
        <div style={{ fontSize: "0.78rem" }}>
          <strong>Signal:</strong> {signalBadge(signal)}
        </div>
      </div>
    );
  };


  if (!sortedRows.length) {
    return <p style={{ padding: 12 }}>No tickers available.</p>;
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      {/* column picker button */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 6,
        }}
      >
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setShowColPicker((v) => !v)}
            style={{
              fontSize: "0.75rem",
              padding: "4px 12px",
              borderRadius: 999,
              border: "1px solid #e5e7eb",
              background: "#ffffff",
              color: "#111827",
              cursor: "pointer",
            }}
          >
            Columns ▾
          </button>
          {showColPicker && (
            <div
              style={{
                position: "absolute",
                right: 0,
                marginTop: 4,
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(15,23,42,0.12)",
                padding: "8px 10px",
                zIndex: 30,
                fontSize: "0.8rem",
                minWidth: 170,
              }}
            >
              {colMeta.map((col) => (
                <label
                  key={col.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 4,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!visibleCols[col.key]}
                    onChange={() => toggleColumn(col.key)}
                  />
                  <span>{col.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.85rem",
          }}
        >
          <thead>
            <tr
              style={{
                background: "#f9fafb",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              {visibleCols.ticker && (
                <th
                  style={{
                    textAlign: "left",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("ticker")}
                >
                  Ticker{sortArrow("ticker")}
                </th>
              )}
              {visibleCols.company && (
                <th
                  style={{
                    textAlign: "left",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("company")}
                >
                  Company{sortArrow("company")}
                </th>
              )}
              {visibleCols.sector && (
                <th
                  style={{
                    textAlign: "left",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("sector")}
                >
                  Sector{sortArrow("sector")}
                </th>
              )}
              {visibleCols.signal && (
                <th
                  style={{
                    textAlign: "left",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("signal")}
                >
                  Signal{sortArrow("signal")}
                </th>
              )}
              {visibleCols.price && (
                <th
                  style={{
                    textAlign: "right",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("price")}
                >
                  Price{sortArrow("price")}
                </th>
              )}
              {visibleCols.pct1w && (
                <th
                  style={{
                    textAlign: "right",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("pct1w")}
                >
                  1W{sortArrow("pct1w")}
                </th>
              )}
              {visibleCols.pct1m && (
                <th
                  style={{
                    textAlign: "right",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("pct1m")}
                >
                  1M{sortArrow("pct1m")}
                </th>
              )}
              {visibleCols.pct3m && (
                <th
                  style={{
                    textAlign: "right",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("pct3m")}
                >
                  3M{sortArrow("pct3m")}
                </th>
              )}
              {visibleCols.pctYtd && (
                <th
                  style={{
                    textAlign: "right",
                    padding: "6px 8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("pctYtd")}
                >
                  YTD{sortArrow("pctYtd")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => {
              const ticker =
                row.Ticker || row.ticker || row.Symbol || row.symbol;
              const company = row.Company || row.company;
              const sector = row.Sector || row.sector;
              const signal = row.Signal || row.Tag || row.signal;
              const price = row.price;

              const pct1w = row.pct_1w;
              const pct1m = row.pct_1m;
              const pct3m = row.pct_3m;
              const pctYtd = row.pct_ytd;

              return (
                <tr
                  key={ticker || i}
                  style={{
                    borderBottom: "1px solid #f3f4f6", // subtle row separators
                  }}
                >
                  {visibleCols.ticker && (
                    <td
                      style={{
                        padding: "6px 8px",
                        position: "relative",
                        fontWeight: 600,
                        cursor: "default",
                      }}
                      onMouseEnter={() => setHoveredTicker(ticker)}
                      onMouseLeave={() => setHoveredTicker(null)}
                    >
                      {ticker}
                      {hoveredTicker === ticker && (
                        <MiniCard
                          row={row}
                          // flip the card for the last 3 rows so it doesn’t get cut off
                          placeAbove={i >= sortedRows.length - 3}
                        />
                      )}
                    </td>
                  )}

                  {visibleCols.company && (
                    <td style={{ padding: "6px 8px" }}>{company}</td>
                  )}
                  {visibleCols.sector && (
                    <td style={{ padding: "6px 8px" }}>
                      {sectorBadge(sector)}
                    </td>
                  )}
                  {visibleCols.signal && (
                    <td style={{ padding: "6px 8px" }}>
                      {signalBadge(signal)}
                    </td>
                  )}
                  {visibleCols.price && (
                    <td
                      style={{
                        padding: "6px 8px",
                        textAlign: "right",
                      }}
                    >
                      {price != null ? `$${price.toFixed(2)}` : "—"}
                    </td>
                  )}
                  {visibleCols.pct1w && (
                    <td
                      style={{
                        padding: "6px 8px",
                        textAlign: "right",
                        color: colorFor(pct1w),
                      }}
                    >
                      {fmtPct(pct1w)}
                    </td>
                  )}
                  {visibleCols.pct1m && (
                    <td
                      style={{
                        padding: "6px 8px",
                        textAlign: "right",
                        color: colorFor(pct1m),
                      }}
                    >
                      {fmtPct(pct1m)}
                    </td>
                  )}
                  {visibleCols.pct3m && (
                    <td
                      style={{
                        padding: "6px 8px",
                        textAlign: "right",
                        color: colorFor(pct3m),
                      }}
                    >
                      {fmtPct(pct3m)}
                    </td>
                  )}
                  {visibleCols.pctYtd && (
                    <td
                      style={{
                        padding: "6px 8px",
                        textAlign: "right",
                        color: colorFor(pctYtd),
                      }}
                    >
                      {fmtPct(pctYtd)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- TEG: grid of cards (NO filters applied)
// --- TEG: grid of cards OR table, depending on viewMode ---
function renderTEGCards() {
  const list = filteredProps || [];
  if (!Array.isArray(list) || list.length === 0) {
    return <p style={{ padding: 12 }}>No tickers available.</p>;
  }

  // 🔁 When top-right toggle is set to "Grid", show the big table
  if (viewMode === "grid") {
    return <TEGGridTable rows={list} />;
  }

  // 🃏 Default: card layout (what you already had)
  return (
    <div
      className="cards-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: 16,
      }}
    >
      {list.map((row, i) => (
        <TEGCard key={(row.Ticker || row.ticker || i) + i} row={row} />
      ))}
    </div>
  );
}

  const PagingBar = () => (isNarrow || viewMode !== "grid" ? null : (
    <div style={{ position: "sticky", top: 0, zIndex: 40, background: darkMode ? "#0b1220" : "#ffffff", paddingTop: 8, paddingBottom: 8, borderBottom: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 12, margin: "10px 0 12px", flexWrap: "wrap" }}>
      <div style={{ fontWeight: 700 }}>
        Showing {showingFrom}-{showingTo} of {total}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button type="button" onClick={() => setGridPage(1)} disabled={gridPage <= 1} className="pill">⏮</button>
        <button type="button" onClick={() => setGridPage((p) => Math.max(1, p - 1))} disabled={gridPage <= 1} className="pill">
          Prev
        </button>

        <div style={{ fontWeight: 600 }}>
          Page {gridPage} / {totalPages}
        </div>

        <button type="button" onClick={() => setGridPage((p) => Math.min(totalPages, p + 1))} disabled={gridPage >= totalPages} className="pill">
          Next
        </button>
        <button type="button" onClick={() => setGridPage(totalPages)} disabled={gridPage >= totalPages} className="pill">⏭</button>
      </div>

      <select
        value={gridPageSize}
        onChange={(e) => {
          setGridPageSize(Number(e.target.value));
          setGridPage(1);
        }}
        style={{ borderRadius: "12px", padding: "0.5rem" }}
        title="Rows per page"
      >
        {[25, 50, 100, 200].map((n) => (
          <option key={n} value={n}>
            {n} / page
          </option>
        ))}
      </select>
    </div>
  ));


  const renderPlayerCards = () => {
    if (draftKingsModeActive) {
      return <DraftKingsNflWorkspace
        mode={effectiveLineupType}
        nflPlayers={Array.isArray(propsCache.NFL) ? propsCache.NFL : []}
        advanced={lineupMode === "Advanced"}
        onModeChange={setLineupType}
        onBack={() => { setLineupType("PROP"); setShowLineupsFocus(false); }}
        onResult={(payload) => setDraftKingsResult(payload)}
      />;
    }
    if (showLineupsFocus && (draftKingsResult?.result?.lineups?.length ?? 0) > 0) {
      return renderDraftKingsLineupsWorkspace();
    }
    if (showLineupsFocus && Array.isArray(generatedLineups) && generatedLineups.length) {
      return renderLineupsWorkspace();
    }
    // 🔁 NBA grid mode: use the big table
    if (viewMode === "grid") {
      const total = Array.isArray(sortedTierFilteredPool) ? sortedTierFilteredPool.length : 0;
    
      const totalPages = Math.max(1, Math.ceil(total / gridPageSize));
      const page = Math.min(gridPage, totalPages);
    
      const startIdx = (page - 1) * gridPageSize;
      const endIdx = Math.min(total, startIdx + gridPageSize);
    
      const pageRows = (sortedTierFilteredPool || []).slice(startIdx, endIdx);
    
      const showingFrom = total === 0 ? 0 : startIdx + 1;
      const showingTo = endIdx;

    
      return (
        <div>
          {/* Results + paging bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              margin: "6px 0 12px",
              flexWrap: "wrap",
            }}
          >
            {/* LEFT: count */}
            <div
              style={{
                border: "1px solid #d0d5dd",
                borderRadius: 999,
                padding: "0.45rem 0.75rem",
                background: "#ffffff",
                fontWeight: 700,
              }}
            >
              Showing {showingFrom}-{showingTo} of {total}
            </div>

            {/* MIDDLE: paging buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button type="button" onClick={() => setGridPage(1)} disabled={page <= 1} className="pill">
                ⏮
              </button>

              <button
                type="button"
                onClick={() => setGridPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="pill"
              >
                Prev
              </button>

              <div
                style={{
                  border: "1px solid #d0d5dd",
                  borderRadius: 999,
                  padding: "0.45rem 0.75rem",
                  background: "#ffffff",
                  fontWeight: 700,
                }}
              >
                Page {page} / {totalPages}
              </div>

              <button
                type="button"
                onClick={() => setGridPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="pill"
              >
                Next
              </button>

              <button type="button" onClick={() => setGridPage(totalPages)} disabled={page >= totalPages} className="pill">
                ⏭
              </button>
            </div>

            {/* RIGHT: page size */}
            <select
              value={gridPageSize}
              onChange={(e) => {
                setGridPageSize(Number(e.target.value));
                setGridPage(1);
              }}
              style={{
                borderRadius: "12px",
                padding: "0.5rem",
                border: "1px solid #d0d5dd",
                background: "#ffffff",
                fontWeight: 600,
              }}
              title="Rows per page"
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>

    
          {/* The actual table */}
          <NBAGridTable
            rows={pageRows}
            columns={nbaGridColumns}
            setColumns={setNbaGridColumns}
            getConfidenceLabel={getConfidenceLabel}
            tagColorMap={tagColorMap}
            isRowSelected={isGridRowSelected}
            onToggleRowSelected={toggleGridRowSelected}
            onToggleAllVisible={() => toggleSelectAllVisibleGridRows(pageRows)}   // ✅ page-aware
            onBulkAddToGuruList={addSelectedGridRowsToGuruList}
            onBulkRemoveFromGuruList={removeSelectedGridRowsFromGuruList}
            //onOpenDetails={handleOpenGridDetails}
            onOpenDetails={(row) => {
              setDetailChartRange("L10");
              setDetailShowBands(false);
              handleOpenGridDetails(row, sortedTierFilteredPool);
            }}
            onOpenCompare={handleOpenGridCompare}
            sortConfig={nbaGridSortConfig}
            setSortConfig={setNbaGridSortConfig}
            isAdminUser={isAdminUser}
            guruModeAccess={pwgGuruAccess}
            isBasicCardExperience={isBasicCardExperience}
            isInMyList={isInMyList}
            onToggleMyPlay={toggleMyGuru}
          />
        </div>
      );
    }
    

    const total = Array.isArray(visiblePool) ? visiblePool.length : 0;
    const totalPages = Math.max(1, Math.ceil(total / gridPageSize));
    const page = Math.min(gridPage, totalPages);

    const startIdx = (page - 1) * gridPageSize;
    const endIdx = startIdx + gridPageSize;

    const pageRows = (visiblePool || []).slice(startIdx, endIdx);
    const showingFrom = total === 0 ? 0 : startIdx + 1;
    const showingTo = Math.min(endIdx, total);

    
    // ✅ Card paging (Cards view)
    const cardTotal = Array.isArray(visiblePool) ? visiblePool.length : 0;
    const cardPages = Math.max(1, Math.ceil(cardTotal / cardPageSize));
    const cardPageSafe = Math.min(cardPages, Math.max(1, cardPage));
    const cardFrom = cardTotal === 0 ? 0 : (cardPageSafe - 1) * cardPageSize + 1;
    const cardTo = cardTotal === 0 ? 0 : Math.min(cardTotal, cardPageSafe * cardPageSize);

    const cardPageRows = Array.isArray(visiblePool)
      ? visiblePool.slice(
          (cardPageSafe - 1) * cardPageSize,
          (cardPageSafe - 1) * cardPageSize + cardPageSize
        )
      : [];

    const destinationState = destinationDatasetState({
      activeSport,
      loadedSport: loadedPropsSport,
      loading: propsLoading,
      error: propsDataError,
      rawCount: Array.isArray(propsData) ? propsData.length : 0,
      filteredCount: cardTotal,
    });
    if (destinationState !== "READY" && destinationState !== "UNAVAILABLE") {
      const filteredEmpty = destinationState === "FILTERED_EMPTY";
      const noRows = destinationState === "NO_GOVERNED_ROWS";
      return (
        <section data-testid="sports-rows-empty-state" style={{ margin:"18px 0",padding:"24px",border:"1px solid #CBD5E1",borderRadius:14,background:darkMode ? "#0F172A" : "#F8FAFC",textAlign:"center" }}>
          <strong>{destinationState === "LOADING" ? `Loading ${activeSport}…` : noRows ? `${activeSport} has no governed rows available.` : `${activeSport} has no props matching the selected filters.`}</strong>
          {filteredEmpty && <div style={{ marginTop:12 }}><button type="button" className="pwActionBtn" onClick={clearAllFilters}>Clear Filters</button></div>}
        </section>
      );
    }


    // ...your existing card rendering for NBA + other sports
    
    //const list = filteredProps || [];   // <-- required for NBA Grid + Cards
    const list = pageRows; // ✅ page-aware cards


    
    // ---- Line-tax helper for cards (DISCOUNT / FAIR / TAXED / HYPER-TAXED) ----
    // ---- Line-tax helper for cards (minimal pill: DISC / FAIR / TAX / HYP) ----
    // ---- Line-tax helper for cards (minimal pill: DISC / FAIR / TAX / HYP) ----
    const getLineTaxMeta = (row) => {
      // accept multiple possible column names (your excel/json keys vary across builds)
      const tierRaw =
        row["Line_Tax_Tier"] ??
        row["Line Tax Tier"] ??
        row["Line_Tax"] ??
        row["LineTaxTier"] ??
        "";

      const pctRaw =
        row["Line_Tax_Pct"] ??
        row["Line Tax Pct"] ??
        row["LineTaxPct"];

      const diffRaw =
        row["Line_Tax_Diff"] ??
        row["Line Tax Diff"] ??
        row["LineTaxDiff"];

      const reasonRaw =
        row["Line_Tax_Reason"] ??
        row["Line Tax Reason"] ??
        row["LineTaxReason"] ??
        "";

      const scoreRaw =
        row["Line_Tax_Score"] ??
        row["Line Tax Score"] ??
        row["LineTaxScore"];

      const tier = (tierRaw || "").toString().trim();
      if (!tier) return null;

      const t = tier.toUpperCase();

      // minimal label + colors
      let short = "FAIR";
      let bg = "#e5e7eb";
      let fg = "#111827";

      if (t.includes("DISCOUNT")) {
        short = "DISC";
        bg = "#dcfce7";
        fg = "#166534";
      } else if (t === "FAIR") {
        short = "FAIR";
        bg = "#e5e7eb";
        fg = "#111827";
      } else if (t.includes("HYPER")) {
        short = "HYP";
        bg = "#fee2e2";
        fg = "#b91c1c";
      } else if (t.includes("TAX")) {
        short = "TAX";
        bg = "#fef3c7";
        fg = "#92400e";
      }

      const pct = Number(pctRaw);
      const diff = Number(diffRaw);
      const score = Number(scoreRaw);
      const reason = (reasonRaw || "").toString().trim();

      // pct can be:
      // - 0.94 (ratio) meaning 94% of avg
      // - 94 (already percent-of-avg)
      // - sometimes already "over/under" style (rare) but we treat it as percent-of-avg consistently
      const pctOfAvg = Number.isFinite(pct)
        ? (Math.abs(pct) <= 1.5 ? pct * 100 : pct)
        : null;

      // Convert "percent-of-avg" into "over/under avg" delta percent:
      // 111% of avg -> +11% over avg
      // 94% of avg  ->  -6% under avg
      const ouPct = pctOfAvg != null ? (pctOfAvg - 100) : null;

      const pctTxt =
        ouPct != null && Number.isFinite(ouPct) && Math.abs(ouPct) >= 0.5
          ? ` • ${Math.abs(ouPct).toFixed(0)}% ${ouPct >= 0 ? "over" : "under"} avg`
          : "";

      const titleParts = [];
      if (pctOfAvg != null && Number.isFinite(pctOfAvg)) {
        titleParts.push(`${pctOfAvg.toFixed(0)}% of avg`);
      }
      if (ouPct != null && Number.isFinite(ouPct)) {
        const sgn = ouPct >= 0 ? "+" : "−";
        titleParts.push(`${sgn}${Math.abs(ouPct).toFixed(0)}% vs avg`);
      }
      if (Number.isFinite(diff)) {
        titleParts.push(`Δ vs avg: ${diff >= 0 ? "+" : ""}${diff.toFixed(1)}`);
      }
      if (Number.isFinite(score)) titleParts.push(`Tier score: ${score}`);
      if (reason) titleParts.push(reason);

      return {
        bg,
        fg,
        short,   // "DISC" | "FAIR" | "TAX" | "HYP"
        pctTxt,  // " • 11% over avg"  (corrected)
        title: titleParts.join(" • "),
      };
    };




  return (
    <>
      {shouldShowProps && !showAllProps && featuredByTag && (
        <section style={{ marginTop: "1.25rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Today’s Highlights</h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1rem", marginTop: "0.75rem" }}>
            {Object.entries(featuredByTag).map(([tag, items]) =>
              (items || []).slice(0, 1).map((p, i) => {
                const badge = tagColorMap?.[tag] || "#666";
                const propLabel = customerPropTypeLabel(p["Prop Type"], p?.Sport || activeSport);
                const vsSymbol = p["Home/Away"]?.toLowerCase() === "away" ? "@" : "vs";
                const snapshotCardStyle = {
                  background: 'var(--card-bg)',
                  color: 'var(--card-text)',
                  border: '1px solid var(--card-border)',
                  borderRadius: 12,
                  boxShadow: 'var(--card-shadow)',
                  padding: '0.75rem'
                };                  

                return (
                  <div key={`${tag}-${i}`} style={{ ...snapshotCardStyle, borderLeft: `4px solid ${badge}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <strong>{p.Player}</strong>
                      <span style={{
                        background: badge, color: "#fff",
                        fontSize: 12, fontWeight: 700, borderRadius: 999, padding: "0.15rem 0.5rem"
                      }}>
                        {tag}
                      </span>
                    </div>

                    <div style={{ marginTop: 6, fontSize: 13, color: 'var(--card-muted)' }}>
                      {p.Team} {vsSymbol} {p.Opponent}
                    </div>

                    <div style={{ marginTop: 6, fontSize: 18 }}>
                      <strong>Prop:</strong> {propLabel} — <strong>{Number(p["Prop Value"]).toFixed(2)}</strong>
                    </div>
                    {/* Selection Strategy */}
                    {(() => {
                      const ss = formatSelectionStrategy(p);
                      if (!ss) return null;

                      // Direction color: green for OVER, red for UNDER, neutral grey otherwise
                      const dirLower = ss.label.toLowerCase();
                      let bg = "#e5e7eb";
                      let color = "#111827";
                      if (dirLower.includes("over")) {
                        bg = "#dcfce7";
                        color = "#166534";
                      } else if (dirLower.includes("under")) {
                        bg = "#fee2e2";
                        color = "#b91c1c";
                      }

                      return (
                        <div
                          style={{
                            marginTop: 4,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "0.35rem",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              color: darkMode ? "#9CA3AF" : "#6B7280",
                            }}
                          >
                            Selection strategy:
                          </span>
                          <span
                            style={{
                              background: bg,
                              color,
                              borderRadius: 999,
                              padding: "2px 8px",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {ss.label}
                            {ss.breakdown && ` (${ss.breakdown})`}
                          </span>
                        </div>
                      );
                    })()}

                  </div>
                );
              })
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <button
              onClick={() => setShowAllProps(true)}
              className="action-button"
              style={{
                padding: "0.55rem 1rem",
                borderRadius: 10,
                border: "1px solid var(--chip-border)",
                background: "var(--chip-bg)",
                color: "var(--link)"
              }}
            >
              Show all props
            </button>
          </div>
        </section>
      )}


      {shouldShowProps && showAllProps &&(
        <Tooltip.Provider>
        <PagingBar />
        <div
          className="pwg-prop-cards-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: "1rem",
            marginTop: "2rem",
          }}
        >
          {pageRows.map((p, i) => {
            // ---- keys / expand state ------------------------------------------------
            const expandKey = detailsKeyFor(p);
            const isExpanded = !!cardToggles[expandKey];
            
            // ---- L10 accessors (work for JSON or Flask) -----------------------------
            const propHeader = getPropValueKeyFor(p);
                  // compute once, above your JSX
            // Last-10 data (works whether Flask or JSON feeds)
            const l10    = getL10Rows(p, { vsOpp: false, max: 10 });
            const l10Opp = getL10Rows(p, { vsOpp: true,  max: 10 });

            // show the expand button only if at least one table has rows
            const isBacktestReplay = isBacktestSportKey(activeSport || p?.Sport);

            const hasDetailData =
              isBacktestReplay ||
              (Array.isArray(l10)    && l10.length > 0) ||
              (Array.isArray(l10Opp) && l10Opp.length > 0);

              // DEBUG — show what we actually have per card
            /*console.debug("L10 lens:", p.Player, p.Opponent, {
              l10: l10.length,
              l10Opp: l10Opp.length,
              hasDetailData,
            });*/

            function getCardSignals(row) {
              const sportKey = String(activeSport || row?.Sport || "").toUpperCase();
              const propType = row?.["Prop Type"] || row?.PropType || row?.Prop || "";
              const isMinutesProp = isMinutesLikePropType(propType);
            
              const l10 = getL10Rows(row, { max: 10, range: "L10" });
              const l20 = getL10Rows(row, { max: 20, range: "L20" });

              // IMPORTANT: match detail view orientation.
              // Details can show L20 + Bands, so card-level bounce must prefer L20 when present.
              // This prevents cases like Natasha Mack where Details says Strong Bounce but the card hides it.
              const signalRows = Array.isArray(l20) && l20.length ? l20 : l10;
              const signalRange = Array.isArray(l20) && l20.length ? "L20" : "L10";
              const activeRows = Array.isArray(signalRows) ? [...signalRows].reverse() : []; // oldest -> newest
            
              // ---------- MLB QUICK CONTEXT CHIP ----------
              const getMlbContextLabel = () => {
                const playerType = String(
                  row?.["Player Type"] ?? row?.PlayerType ?? ""
                ).toUpperCase();
            
                // Pitcher cards: show opponent attackability / K environment
                if (playerType === "PITCHER") {
                  const oppAttackability =
                    row?.oppAttackability ??
                    row?.OppAttackability ??
                    row?.["Opp Attackability"] ??
                    row?.["Opponent Attackability"] ??
                    row?.Attackability ??
                    row?.attackability ??
                    "";
            
                  const oppKpctTier =
                    row?.oppKpctTier ??
                    row?.OppKpctTier ??
                    row?.["Opp K% Tier"] ??
                    row?.["Opponent K% Tier"] ??
                    row?.["Opp_Kpct_Tier"] ??
                    "";
            
                  const pitcherTier =
                    row?.oppPitcherTier ??
                    row?.OppPitcherTier ??
                    row?.["Opp Pitcher Tier"] ??
                    row?.["Opponent Pitcher Tier"] ??
                    "";
            
                  const attackText = String(oppAttackability || "").trim();
                  const kpctText = String(oppKpctTier || "").trim();
                  const pitcherTierText = String(pitcherTier || "").trim();
            
                  if (attackText) return attackText;
                  if (kpctText) return `Opp K%: ${kpctText}`;
                  if (pitcherTierText) return `Opp Tier: ${pitcherTierText}`;
                  return "";
                }
            
                // Hitter cards: show batting order first, then hand, then opp pitcher tier
                const rawBO = String(row?.battingOrder ?? "").trim();
                const bo = Number.isInteger(parseInt(rawBO, 10)) ? parseInt(rawBO, 10) : null;
                if (Number.isInteger(bo) && bo > 0) return `Batting Order ${bo}`;
            
                const handChar = String(
                  row?.opp_hand ??
                  row?.opp_pitcher_hand ??
                  row?.opp_pitch_hand ??
                  row?.["OppPitcherThrows"] ??
                  row?.["Opp Pitcher Hand"] ??
                  row?.["Opp Hand"] ??
                  ""
                )
                  .trim()
                  .toUpperCase()
                  .slice(0, 1);
            
                if (handChar === "L" || handChar === "R") return `vs ${handChar}HP`;
            
                const oppPitcherTier =
                  row?.oppPitcherTier ??
                  row?.OppPitcherTier ??
                  row?.["Opp Pitcher Tier"] ??
                  row?.["Opponent Pitcher Tier"] ??
                  "";
            
                if (String(oppPitcherTier || "").trim()) return String(oppPitcherTier).trim();
            
                return "";
              };
            
              if (!activeRows.length) {
                return {
                  bounceLabel: "",
                  contextLabel: sportKey === "MLB" ? getMlbContextLabel() : "",
                };
              }
            
              const values = activeRows
                .map((entry) =>
                  isMinutesProp
                    ? getMinutesValue(entry, activeSport)
                    : getL10Value(entry, propType, activeSport)
                )
                .filter((v) => Number.isFinite(v));
            
              const minutesSeries = activeRows
                .map((entry) => getMinutesValue(entry, activeSport))
                .filter((v) => Number.isFinite(v));
            
              if (!values.length) {
                return {
                  bounceLabel: "",
                  contextLabel: sportKey === "MLB" ? getMlbContextLabel() : "",
                };
              }
            
              const avg = (arr) =>
                arr.length ? arr.reduce((sum, v) => sum + v, 0) / arr.length : null;

              const stdDev = (arr) => {
                if (!arr?.length) return null;
                const m = avg(arr);
                if (!Number.isFinite(m)) return null;
                const variance = arr.reduce((sum, v) => sum + Math.pow(v - m, 2), 0) / arr.length;
                return Math.sqrt(variance);
              };

              // Same bounce-band logic used in the Details Sparkline.
              // Latest value near the lower band = Strong Bounce risk, even when guru/tag still lean UNDER.
              const bandMean = avg(values);
              const bandStd = values.length > 1 ? stdDev(values) : null;
              const lowerBandValue =
                Number.isFinite(bandMean) && Number.isFinite(bandStd)
                  ? bandMean - 2 * bandStd
                  : null;
              const upperBandValue =
                Number.isFinite(bandMean) && Number.isFinite(bandStd)
                  ? bandMean + 2 * bandStd
                  : null;

              const latestForBand = values.length ? values[values.length - 1] : null;
              const isStrongBounceBand =
                !isMinutesProp &&
                Number.isFinite(latestForBand) &&
                Number.isFinite(lowerBandValue) &&
                Number.isFinite(bandStd) &&
                latestForBand <= lowerBandValue + bandStd * 0.35;

              const isBounceCandidateBand =
                !isStrongBounceBand &&
                !isMinutesProp &&
                Number.isFinite(latestForBand) &&
                Number.isFinite(bandMean) &&
                Number.isFinite(bandStd) &&
                latestForBand <= bandMean - bandStd;

              const isExtendedBand =
                !isMinutesProp &&
                Number.isFinite(latestForBand) &&
                Number.isFinite(upperBandValue) &&
                Number.isFinite(bandStd) &&
                latestForBand >= upperBandValue - bandStd * 0.35;
            
              const recent = values.slice(-3);
              const prior =
                values.length >= 6
                  ? values.slice(-6, -3)
                  : values.length >= 4
                    ? values.slice(0, Math.max(1, values.length - 3))
                    : [];
            
              const recentAvg = avg(recent);
              const priorAvg = avg(prior);
            
              const delta =
                Number.isFinite(recentAvg) && Number.isFinite(priorAvg)
                  ? recentAvg - priorAvg
                  : null;
            
              let bounceLabel = "";
              if (isStrongBounceBand) {
                bounceLabel = signalRange === "L20" ? "Strong Bounce" : "Strong Bounce";
              } else if (isBounceCandidateBand) {
                bounceLabel = "Bounce Candidate";
              } else if (isExtendedBand) {
                bounceLabel = "Extended / Hot";
              } else if (delta != null) {
                const lineNum = getPropLine(row);
                const latest = values.length ? values[values.length - 1] : null;
                const prev = values.length >= 2 ? values[values.length - 2] : null;
                const twoBack = values.length >= 3 ? values[values.length - 3] : null;
                const recentHits = Number.isFinite(lineNum)
                  ? values.slice(-5).filter((v) => Number.isFinite(v) && v > lineNum).length
                  : 0;
                const last2Above =
                  Number.isFinite(lineNum) &&
                  Number.isFinite(latest) &&
                  Number.isFinite(prev) &&
                  latest > lineNum &&
                  prev > lineNum;
                const last2Below =
                  Number.isFinite(lineNum) &&
                  Number.isFinite(latest) &&
                  Number.isFinite(prev) &&
                  latest < lineNum &&
                  prev < lineNum;
                const last3Down =
                  Number.isFinite(latest) &&
                  Number.isFinite(prev) &&
                  Number.isFinite(twoBack) &&
                  latest < prev &&
                  prev < twoBack;
                const last3Up =
                  Number.isFinite(latest) &&
                  Number.isFinite(prev) &&
                  Number.isFinite(twoBack) &&
                  latest > prev &&
                  prev > twoBack;
                const brokeBelowAfterPop =
                  Number.isFinite(lineNum) &&
                  Number.isFinite(latest) &&
                  Number.isFinite(prev) &&
                  Number.isFinite(twoBack) &&
                  latest < lineNum &&
                  prev > lineNum &&
                  twoBack > lineNum;
                const latestBelowLine =
                  Number.isFinite(lineNum) &&
                  Number.isFinite(latest) &&
                  latest < lineNum;
                const latestAboveLine =
                  Number.isFinite(lineNum) &&
                  Number.isFinite(latest) &&
                  latest > lineNum;

                // Human graph read:
                // 1) A recent-3 average can look bullish because of a 1-2 game spike.
                //    If the latest game has already lost the line after that spike, it is NOT Heating Up.
                // 2) A one-game dip that still clears the line is not Cooling Off; it is sustaining/holding gains.
                // 3) Heating Up requires the latest point to still support the trend, not just an older pop.
                if (brokeBelowAfterPop || (last3Down && latestBelowLine)) {
                  bounceLabel = "Failed Bounce";
                } else if (last2Above && recentHits >= 3) {
                  bounceLabel = latest >= prev ? "Holding Gains" : "Sustaining";
                } else if (last2Below || (delta <= -2 && latestBelowLine)) {
                  bounceLabel = "Cooling Off";
                } else if (delta >= 2 && latestAboveLine && (last3Up || latest >= prev || recentHits >= 4)) {
                  bounceLabel = "Heating Up";
                } else if (Math.abs(delta) <= 1 || (delta >= 2 && latestBelowLine)) {
                  bounceLabel = "Stable";
                } else if (delta <= -2) {
                  bounceLabel = "Softening";
                }
              }
            
              // Backend analysis is the source of truth; retain the old read only as a temporary fallback.
              bounceLabel = getBounceLabel(row) || bounceLabel;

              // NBA / WNBA / NHL etc -> keep minutes chip
              if (sportKey !== "MLB") {
                let contextLabel = "";

                if (["NFL", "CFB"].includes(sportKey)) {
                  contextLabel = footballOpportunitySignal(row);
                  return { bounceLabel, contextLabel };
                }

                if (sportKey === "SOCCER") {
                  const soccerMinutesKey = getMinutesTrendCanonicalKey(row);
                  contextLabel = MINUTES_TREND_LABELS[soccerMinutesKey] || "Minutes Unknown";
                  return { bounceLabel, contextLabel };
                }
            
                const recentMin = minutesSeries.slice(-3);
                const priorMin =
                  minutesSeries.length >= 6
                    ? minutesSeries.slice(-6, -3)
                    : minutesSeries.length >= 4
                      ? minutesSeries.slice(0, Math.max(1, minutesSeries.length - 3))
                      : [];
            
                const recentMinAvg = avg(recentMin);
                const priorMinAvg = avg(priorMin);
            
                const minDelta =
                  Number.isFinite(recentMinAvg) && Number.isFinite(priorMinAvg)
                    ? recentMinAvg - priorMinAvg
                    : null;
            
                if (!minutesSeries.length) {
                  contextLabel = (["MLB", "KBO"].includes(String(activeSport || "").toUpperCase()) ? null : "Minutes Unknown");
                } else if (minDelta != null) {
                  if (minDelta >= 2) contextLabel = `Minutes Rising`;
                  else if (minDelta <= -2) contextLabel = `Minutes Risk`;
                  else contextLabel = `Minutes Stable`;
                } else {
                  contextLabel = "Minutes Stable";
                }
            
                return { bounceLabel, contextLabel };
              }
            
              // MLB -> replace minutes chip with MLB-specific context
              return {
                bounceLabel,
                contextLabel: getMlbContextLabel(),
              };
            }
            
            // ---- saved list state ---------------------------------------------------
            const saved = isInMyList(p);

            // ---- labels & basics ----------------------------------------------------
            const propLabel = customerPropTypeLabel(p["Prop Type"], p?.Sport || activeSport);
            const projected  = getProjectedValue(p);
            const propAbbr   = getPropAbbr(propLabel);



            // Fallback: infer HA from "Matchup" like "TOR vs DET" (home) or "TOR @ DET" (away)
            function inferHA(entry) {
              const raw = String(entry.Matchup || entry.matchup || "").toLowerCase();
              if (raw.includes("@"))  return "away";
              if (raw.includes("vs")) return "home";
              return "";
            }

            // Normalize Home/Away from multiple possible keys, then fallback to Matchup
            const haRaw =
              String(p["Home/Away"] ?? p.homeAway ?? p["homeAway"] ?? "")
                .trim()
                .toLowerCase() || inferHA(p);

            const ha = (p["Home/Away"] || "").toLowerCase();

            const myVS = ha === "away" ? "@" : "vs";

            // time string you already have
            //const timeText = formatLocalTime(p.GameTime, activeSport, { showDayIfNotToday: true });
            //const timeText = formatLocalTime(p,         activeSport, { showDayIfNotToday: true });
            // pass the whole card `p`, not p.GameTime
            const timeText = formatLocalTime(p, activeSport); // uses SOURCE_TZ map
            const weekdayText = formatGameWeekday(p, activeSport);




            // Matchup cell builder for WNBA L10 rows (JSON and Flask)
            const matchupCellWNBA = (entry) => {
              if (!(activeSport === "WNBA" || activeSport === "WNBAB")) return entry.Matchup || entry.matchup || "";
              const ha = String(entry["Home/Away"] || entry.homeAway || "").toLowerCase();
              const sym = ha === "away" ? "@" : "vs";

              let opp = entry.Opponent || entry.opponent;
              if (!opp) {
                const m = String(entry.Matchup || entry.matchup || "").match(/(?:vs|@)\s+([A-Za-z]{2,3}|\w+)/i);
                opp = m ? m[1].toUpperCase() : "";
              }
              return `${sym} ${opp}`.trim();
            };

            const matchupLine = compactCustomerMatchup(p, timeText, weekdayText);
            const teamColor     = getTeamColor(p.Team, activeSport);
            const opponentColor = getTeamColor(p.Opponent, activeSport);

            const l10Date    = (r) => r.Date || r.date || r.gameDate || "";
            const l10Matchup = (r) =>
              (activeSport === "WNBA" || activeSport === "WNBAB") ? matchupCellWNBA(r) : (r.Matchup || r.matchup || "");

            // ---- small chip ---------------------------------------------------------
            function MetaChip({ title, children }) {
              return (
                <span
                  title={title}
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1,
                    padding: "0.15rem 0.45rem",
                    borderRadius: 999,
                    border: "1px solid #CBD5E1",
                    color: "#64748B",
                    background: "#F8FAFC",
                    whiteSpace: "nowrap",
                  }}
                >
                  {children}
                </span>
              );
            }

            // ---- card helpers -------------------------------------------------------
            const pos   = String(p.position ?? "").trim();
            const rawBO = String(p.battingOrder ?? "").trim();
            const bo    = Number.isInteger(parseInt(rawBO, 10)) ? parseInt(rawBO, 10) : null;
            const hasBO = Number.isInteger(bo) && bo > 0;
            const isBaseballCard = ["MLB", "KBO"].includes(String(activeSport || "").toUpperCase());
            const cleanBaseballAdminCard = isBaseballCard && isAdminUser;
            const regularBaseballCard = isBaseballCard && !isAdminUser;
            const minimalCardExperience = isBasicCardExperience || regularBaseballCard;
            const dedicatedPitcherContext = cleanBaseballAdminCard
              ? getMlbEnvironmentChips(p, activeSport)
              : [];
            const pitcherContextRendered = cleanBaseballAdminCard
              && getMlbPlayerType(p) !== "PITCHER"
              && Boolean(getOpposingPitcherName(p, activeSport) || dedicatedPitcherContext.length);

            const handChar = (
              (p.opp_hand ??
                p.opp_pitcher_hand ??
                p.opp_pitch_hand ??
                p["OppPitcherThrows"] ??
                p["Opp Pitcher Hand"] ??
                p["Opp Hand"] ??
                "") + ""
            )
              .trim()
              .toUpperCase()
              .slice(0, 1);

            const result = (p["Game Prediction"] || "").toUpperCase();
            const resultColor = result === "WIN" ? "#16a34a" : result === "LOSE" ? "#dc2626" : "#6b7280";
            const { hit, miss, neutral } = getL10Counts(p);
            //const pct = directionalPctForCard(p); // not just a raw calculation
            const getCardHitPct = (row) => {
              // MLB's analysis output owns this metric. The customer detail
              // payload intentionally contains only Last 5 and must never be
              // used to replace the published Last-10 percentage.
              if (String(row?.Sport || activeSport || "").toUpperCase() === "MLB") {
                const published = normalizePct(row.L10_HitPct ?? row["L10_HitPct"] ?? row["L10 Hit%"]);
                if (Number.isFinite(published)) return published;
              }
              const l10 = getL10Rows(row, { vsOpp: false, max: 10 });
              const statKey = row["Prop Type"] || row.PropType || row.Prop || "";
              const line = Number(row["Prop Value"] ?? row.PropValue ?? row.Line);

              // Always trust the visible Last10 rows first. This prevents stale backend
              // L10_HitPct from showing 100% when the detail graph/table clearly has misses.
              if (Array.isArray(l10) && l10.length && statKey && Number.isFinite(line)) {
                let hit = 0, miss = 0, even = 0;

                for (const r of l10) {
                  const v = getL10Value(r, statKey, activeSport);
                  if (!Number.isFinite(v)) continue;

                  if (v === line) even++;
                  else if (v > line) hit++;
                  else miss++;
                }

                const denom = hit + miss; // exclude pushes/even for hit% like prop analysis
                if (denom > 0) return Math.round((hit / denom) * 100);
                if (hit + miss + even > 0) return 0;
              }

              // Fallback only when no usable game log exists.
              const base = normalizePct(row.L10_HitPct ?? row["L10_HitPct"] ?? row["L10 Hit%"]);
              return base;
            };
            
            
            const pct = getCardHitPct(p);
            
            const theme = chipTheme(pct);

            // Pure hit% used for the "H%:" label at the bottom
            const totalL10 = hit + miss + neutral;
            //const pctKey = totalL10 ? Math.round((hit / totalL10) * 100) : NaN;
            const pctKey = `${p.Player}|${p["Prop Type"]}|${p["Prop Value"]}|${p.Team}|${p.Opponent}`;

            // Canonical confidence (value + label) for *everything* UI-side
            // WNBA may publish a label; other sports may still publish numeric confidence.
            const confRaw = getConfidenceValue(p);
            const confValue = Number(confRaw);
            const confLabel = getDisplayConfidenceLabel(confRaw, activeSport);
            const mobileDirectionMeta = directionMetaByRow.get(p) || directionPresentation(p, activeSport);
            const mobileDirectionClass = mobileDirectionMeta.direction === "OVER"
              ? "is-over"
              : mobileDirectionMeta.direction === "UNDER" ? "is-under" : "is-neutral";
            const compactLineValue = Number.isFinite(Number(p["Prop Value"]))
              ? Number(p["Prop Value"]).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")
              : p["Prop Value"];
            const directionToggleLabel = `${saved ? "Remove" : "Add"} ${p.Player}, ${propLabel} ${compactLineValue}, ${mobileDirectionMeta.direction === "UNDER" ? "Under" : "Over"} ${saved ? "from" : "to"} My Plays`;
            const mobileConfidenceCaption = mobileDirectionMeta.confidenceSemantic === "STRENGTH"
              ? "Signal Strength"
              : mobileDirectionMeta.confidenceSemantic === "STATUS" ? "Signal Status" : mobileDirectionMeta.confidenceSemantic === "CONFIDENCE" ? "Confidence" : "Confidence Tier";
            const taxMeta = getLineTaxMeta(p);
            const playerDNAMeta = canSeePlayerDNA ? getPlayerDNAMeta(p, activeSport) : null;

            // ---------- Zodiac chip (uses Chinese zodiac fields from backend) ----------
            const rawSign = (p.ZODIAC_SIGN ?? "").toString().trim();        // "Rabbit"
            const rawElement = (p.ZODIAC_ELEMENT ?? "").toString().trim();  // "Water"
            const rawCombo = (p.ZODIAC_COMBO ?? "").toString().trim();      // "Rooster + Rabbit"

            let zodiacChip = null;

            if (rawCombo) {
              // 2-player props: show "Rooster + Rabbit"
              zodiacChip = {
                key: rawCombo.toUpperCase(),
                label: rawCombo,
              };
            } else if (rawSign) {
              // Single player: "Water Rabbit", etc.
              const label = rawElement ? `${rawElement} ${rawSign}` : rawSign;
              zodiacChip = {
                key: label.toUpperCase(),
                label,
              };
            }






            return (
              <div
                className="pwg-mobile-prop-card"
                key={i}
                onClick={(event) => {
                  const interactive = event.target.closest?.(
                    "button, a, input, select, textarea, [role='button'], [data-card-interactive='true']"
                  );
                  if (interactive && interactive !== event.currentTarget) return;
                  handleOpenGridDetails(p, pageRows, event.currentTarget);
                }}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleOpenGridDetails(p, pageRows, event.currentTarget);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={propCardAccessibleLabel(p, propLabel)}
                style={{
                  cursor: "pointer",
                  borderLeft: "none",
                  backgroundColor: darkMode ? "#1e1e1e" : "#fff",
                  padding: "0.85rem",
                  paddingLeft: minimalCardExperience || cleanBaseballAdminCard ? "1.2rem" : "1rem",
                  borderRadius: "12px",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow:
                    minimalCardExperience && saved
                      ? "0 0 0 2px rgba(14,165,233,0.22), 0 10px 26px rgba(15,23,42,0.14)"
                      : "0 2px 6px rgba(0,0,0,0.1)",
                  outline:
                    minimalCardExperience && saved
                      ? "1px solid rgba(14,165,233,0.55)"
                      : "none",
                  transition: "box-shadow 160ms ease, outline-color 160ms ease, transform 160ms ease",
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: "0.75rem",
                  cursor: "pointer",
                }}
              >
                <div
                    data-testid="confidence-rail"
                    role="img"
                    aria-label={`${mobileDirectionMeta.direction} governed confidence ${mobileDirectionMeta.confidenceTier || "unavailable"}`}
                    title={`${mobileDirectionMeta.confidenceTier || "No governed qualitative"} confidence`}
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 7,
                      background: activeSport === "NHL"
                        ? (darkMode ? "rgba(226,232,240,0.22)" : "#E2E8F0")
                        : mobileDirectionMeta.direction === "OVER"
                          ? (darkMode ? "rgba(21,128,61,0.22)" : "rgba(21,128,61,0.14)")
                          : mobileDirectionMeta.direction === "UNDER"
                            ? (darkMode ? "rgba(185,28,28,0.22)" : "rgba(185,28,28,0.14)")
                            : (darkMode ? "rgba(255,255,255,0.10)" : "#E2E8F0"),
                    }}
                  >
                    <div
                      data-testid="confidence-rail-fill"
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: `${confidenceRailFillPct(mobileDirectionMeta, p, activeSport)}%`,
                        background: directionRailColor(mobileDirectionMeta, activeSport) || "transparent",
                        transition: "height 240ms ease",
                      }}
                    />
                  </div>
                <div className="pwg-mobile-card-composition">
                  <div className="pwg-mobile-card-header">
                    <strong className="pwg-mobile-card-player">{p.Player}</strong>
                    <span className="pwg-mobile-card-team">{p.Team}</span>
                  </div>
                  <div className="pwg-mobile-card-matchup" data-testid="mobile-customer-card-matchup">{matchupLine}</div>
                  <div className="pwg-mobile-card-prop-panel">
                    <div className="pwg-mobile-card-prop-copy">
                      <span className="pwg-mobile-card-prop-type">{propLabel}</span>
                      <strong className="pwg-mobile-card-line">
                        {Number.isFinite(Number(p["Prop Value"]))
                          ? Number(p["Prop Value"]).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")
                          : p["Prop Value"]}
                      </strong>
                    </div>
                    <button
                      type="button"
                      className={`pwg-mobile-card-insight-cell pwg-mobile-card-direction ${mobileDirectionClass}`}
                      data-testid="mobile-card-direction"
                      aria-label={directionToggleLabel}
                      aria-pressed={saved}
                      onClick={(event) => { event.stopPropagation(); toggleMyGuru(p); }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          toggleMyGuru(p);
                        }
                      }}
                    >
                      <div className="pwg-mobile-card-direction-value">
                        <strong>{saved ? "✓ " : ""}{mobileDirectionMeta.direction === "OVER" ? "▲ OVER" : mobileDirectionMeta.direction === "UNDER" ? "▼ UNDER" : "—"}</strong>
                      </div>
                    </button>
                  </div>
                  <div className="pwg-mobile-card-evidence">
                    {(mobileDirectionMeta.confidenceTier || mobileDirectionMeta.confidenceSemantic !== "UNKNOWN") && (
                      <div className={`pwg-mobile-card-insight-cell pwg-mobile-card-tier is-${mobileDirectionMeta.confidenceSemantic.toLowerCase()}`}>
                        {mobileDirectionMeta.confidenceIcon === "SIGNAL" && (
                          <span className="pwg-confidence-meter" aria-label={`${mobileDirectionMeta.confidenceLevel} of 4 signal bars`}>
                            {[1,2,3,4].map((level) => <i key={level} className={level <= mobileDirectionMeta.confidenceLevel ? "is-active" : ""} />)}
                          </span>
                        )}
                        {mobileDirectionMeta.confidenceIcon === "DEVELOPING" && <span className="pwg-confidence-status-icon" aria-hidden="true">◌</span>}
                        {mobileDirectionMeta.confidenceIcon === "AVOID" && <span className="pwg-confidence-status-icon is-avoid" aria-hidden="true">⊘</span>}
                        {mobileDirectionMeta.confidenceTier && <strong className="pwg-mobile-card-confidence-tier">{mobileDirectionMeta.confidenceTier}</strong>}
                        <span className="pwg-mobile-card-insight-label">{mobileConfidenceCaption}</span>
                        {activeSport === "NHL" && nhlAnalyticsSummary(p) && (
                          <span className="pwg-mobile-card-insight-label" title={nhlAnalyticsSummary(p)}>{nhlAnalyticsSummary(p)}</span>
                        )}
                        {hasCanonicalMatchupContract(p) && (()=>{ const matchup=nhlMatchupPresentation(p); return <span data-testid="nhl-mobile-matchup" className="pwg-mobile-card-insight-label" title={matchup.accessibleLabel}>{matchup.compactLabel}{matchup.adjustedProjection !== null ? ` · Adj ${matchup.adjustedProjection.toFixed(2)}` : ""}</span>; })()}
                      </div>
                    )}
                    <StrategyMarker tag={p.Tag} />
                  </div>
                </div>
                {/* HEADER */}
                <h3
                  className="pwg-legacy-card-header"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    margin: 0,
                    fontSize: "1.15rem",
                    lineHeight: 1.2,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <strong>
                      {p.Player}
                      {canSeeAdvancedCard && (!isBaseballCard || cleanBaseballAdminCard) && pos && ` — ${pos}`}
                    </strong>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {canSeeAdvancedCard && (!isBaseballCard || cleanBaseballAdminCard) && hasBO && <MetaChip title="Batting order">{`#${bo}`}</MetaChip>}
                    </div>
                  </div>

                  <span
                    style={{
                      backgroundColor: "#64748B",
                      color: "#fff",
                      fontWeight: "bold",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "999px",
                      lineHeight: 1,
                    }}
                  >
                    {p.Team}
                  </span>
                </h3>

                {/* BODY */}
                <div className="pwg-legacy-card-body">
                  <div style={{ display: "flex", alignItems: "center", margin: "0.35rem 0 0.45rem", fontSize: "0.8rem", lineHeight: 1.35, fontWeight: 650, color: darkMode ? "#CBD5E1" : "#64748B" }}>
                    <span data-testid="customer-card-matchup">{matchupLine}</span>
                  </div>

                  {isAdminUser && isWnbaAnalysisBlocked(p, activeSport) && (
                    <div data-testid="analysis-blocked" style={{ margin: "4px 0 8px", color: darkMode ? "#FCA5A5" : "#991B1B", fontSize: 11, fontWeight: 900 }}>
                      Research only • {analysisBlockLabel(p)}
                    </div>
                  )}

                  

                  {activeSport === "MMA" && (
                    <div style={{ marginTop: 8 }}>
                      <span style={{ fontWeight: 600 }}>🏆 Fight pick:</span>
                      <span style={{ color: resultColor, fontWeight: 800, marginLeft: 8 }}>
                        {result || "TBD"}
                      </span>
                      {p["Game Pred Confidence"] && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280" }}>
                          ({p["Game Pred Confidence"]})
                        </span>
                      )}
                    </div>
                  )}

                  <div
                    className="pwg-mobile-card-prop-row"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "baseline",
                      justifyContent: minimalCardExperience ? "center" : "flex-start",
                      textAlign: minimalCardExperience ? "center" : "left",
                      gap: minimalCardExperience ? "0.45rem" : "0.3rem",
                      marginTop: minimalCardExperience ? 8 : 0,
                      fontSize: minimalCardExperience ? "1rem" : "inherit",
                    }}
                  >
                    {canSeeAdvancedCard && !isBaseballCard && <strong>📈 Prop:</strong>}
                    <span style={{ fontWeight: minimalCardExperience ? 850 : 500 }}>{propLabel}</span>
                    <strong>
                      {Number.isFinite(Number(p["Prop Value"]))
                        ? Number(p["Prop Value"]).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")
                        : p["Prop Value"]}
                    </strong>
                    {(() => {
                      const type = customerVisibleLineType(p);
                      const typeLabel = customerLineTypeLabel(type);
                      const tone = type ? lineTypeTone(type) : null;
                      const attainability = pwgGuruAccess.attainability ? goblinAttainability(p) : null;
                      const primaryContext = pwgGuruAccess.primaryLineComparison ? customerPrimaryLineContext(p) : null;
                      return (
                      <>
                      {type && <span
                        title={`PrizePicks line type: ${typeLabel}${p["Primary Market Line"] != null ? `; primary market line ${p["Primary Market Line"]}` : ""}`}
                        style={{
                          marginLeft: 6,
                          padding: "3px 7px",
                          borderRadius: 999,
                          border: `1px solid ${tone.border}`,
                          background: tone.background,
                          color: tone.color,
                          fontSize: 10,
                          fontWeight: 950,
                        }}
                      >
                        {typeLabel}
                      </span>}
                      {primaryContext && (
                        <span style={{ marginLeft: 4, color: darkMode ? "#CBD5E1" : "#64748B", fontSize: 10, fontWeight: 750 }}>
                          {primaryContext}
                        </span>
                      )}
                      {attainability && (
                        <span
                          title={`${attainability.evidence}${attainability.method ? ` — ${attainability.method}` : ""}`}
                          style={{ marginLeft: 4, padding: "3px 7px", borderRadius: 999, border: "1px solid #34D399", background: "#ECFDF5", color: "#065F46", fontSize: 10, fontWeight: 900 }}
                        >
                          Attainability: {attainability.score == null ? attainability.classification : `${attainability.score} ${attainability.classification}`}
                        </span>
                      )}
                      {p["Opponent Defense Tier"] && (
                        <span
                          title={`${p["Opponent Defense Metric"]}: ${p["Opponent Defense Value"]}; sample ${p["Defense Sample"]}; ${p["Defense Rank Type"] || "team defense"}${p["Defense Rank Type"] === "RAW" ? "; not opponent-adjusted" : ""}`}
                          style={{ marginLeft: 4, padding: "3px 7px", borderRadius: 999, border: "1px solid #94A3B8", background: "#F8FAFC", color: "#334155", fontSize: 10, fontWeight: 900 }}
                        >
                          {p["Opponent Defense Metric Label"] || "Defense"}: {p["Opponent Defense Tier"]} • #{p["Opponent Defense Rank"]}
                        </span>
                      )}
                      </>
                      );
                    })()}
                    {canSeeAdvancedCard && !regularBaseballCard && Number.isFinite(pct) && (
                      <span style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 8 }}>
                        {/* Tap target (keep it tiny/clean) */}
                        <button
                          type="button"
                          className="pwPctTap"
                          aria-label={`Show last 10 vs prop "hit" rate`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenPctKey((cur) => (cur === pctKey ? null : pctKey));
                          }}
                          onMouseDown={(e) => e.stopPropagation()} // prevents weird parent click handlers
                          title={`Last 10 vs PROP (hit%): ${pct}%`} // desktop hover still works
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 18,
                            minWidth: 18,
                            padding: "0 6px",
                            borderRadius: 999,
                            border: `1px solid ${theme.bd}`,
                            background: theme.bg,
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          {/* dot only (or put "i") */}
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              background: theme.fg,
                              opacity: 0.55,
                              display: "inline-block",
                            }}
                          />
                        </button>

                        {/* Popover (mobile tap) */}
                        {openPctKey === pctKey && (
                        <div
                          className="pwPctPopover"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            bottom: "calc(100% + 10px)",
                            zIndex: 50,
                            background: "#111827",
                            color: "#fff",
                            padding: "8px 10px",
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 700,
                            boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Last 10 vs PROP hit%: {pct}%
                          <div
                            style={{
                              position: "absolute",
                              left: "50%",
                              bottom: -6,
                              transform: "translateX(-50%)",
                              width: 0,
                              height: 0,
                              borderLeft: "6px solid transparent",
                              borderRight: "6px solid transparent",
                              borderTop: "6px solid #111827",
                            }}
                          />
                        </div>
                      )}

                      </span>
                    )}

                  </div>

                  {(minimalCardExperience || cleanBaseballAdminCard) && confLabel && confLabel !== "Unknown" && (
                    <div
                      style={{
                        marginTop: 6,
                        display: "flex",
                        justifyContent: minimalCardExperience ? "center" : "flex-start",
                      }}
                    >
                      <span
                        aria-label={`${confLabel} ${getPublicModelLabel("Confidence", {
                          usePublicCopy: usesPublicEstimateCopy,
                          isAdmin: isAdminUser,
                        }).toLowerCase()}`}
                        style={{
                          padding: "3px 8px",
                          borderRadius: 999,
                          background: darkMode ? "rgba(148,163,184,0.16)" : "#F1F5F9",
                          border: darkMode ? "1px solid rgba(203,213,225,0.24)" : "1px solid #CBD5E1",
                          color: darkMode ? "#E2E8F0" : "#475569",
                          fontSize: 11,
                          fontWeight: 800,
                          lineHeight: 1.2,
                        }}
                      >
                        {getPublicModelLabel("Confidence", {
                          usePublicCopy: usesPublicEstimateCopy,
                          isAdmin: isAdminUser,
                        })}: {confLabel}
                      </span>
                    </div>
                  )}

                  {canSeeAdvancedCard && !regularBaseballCard && (() => {
                    const pwg = pwgProbLabelForRow(p);
                    if (!pwg?.text) return null;
                    return (
                      <div style={{ marginTop: 6, fontSize: 13, color: darkMode ? "#D1D5DB" : "#374151" }}>
                        <span style={{ fontWeight: 700 }}>PWG Prob:</span>{" "}
                        <span>{pwg.text}</span>
                      </div>
                    );
                  })()}

                  {playerDNAMeta && !regularBaseballCard && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "8px 10px",
                        borderRadius: 12,
                        background: darkMode ? "rgba(168,85,247,0.10)" : "#faf5ff",
                        border: darkMode ? "1px solid rgba(216,180,254,0.22)" : "1px solid #e9d5ff",
                        color: darkMode ? "#e9d5ff" : "#581c87",
                      }}
                      title={playerDNAMeta.conflictReason || "WNBA prop-specific player identity context"}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontSize: 11, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase" }}>
                          🧬 Player DNA
                        </span>
                        <span style={playerDNATierStyle(playerDNAMeta.tier, darkMode)}>
                          {playerDNAMeta.displayTier || "DNA"}
                          {playerDNAMeta.score != null ? ` ${playerDNAMeta.score.toFixed(1)}` : ""}
                        </span>
                      </div>

                      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 800 }}>
                        {playerDNAMeta.primary ? `Primary: ${playerDNAMeta.primary}` : "Prop identity available"}
                        {playerDNAMeta.secondary ? ` • Secondary: ${playerDNAMeta.secondary}` : ""}
                      </div>

                      {playerDNAMeta.conflict && (
                        <div
                          style={{
                            marginTop: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "5px 8px",
                            borderRadius: 999,
                            background: darkMode ? "rgba(245,158,11,0.16)" : "#fef3c7",
                            color: darkMode ? "#fde68a" : "#92400e",
                            border: darkMode ? "1px solid rgba(245,158,11,0.25)" : "1px solid #fcd34d",
                            fontSize: 11,
                            fontWeight: 900,
                          }}
                        >
                          ⚠ {playerDNAMeta.conflictReason || "Model call conflicts with player identity"}
                        </div>
                      )}
                    </div>
                  )}

                  {canSeeAdvancedCard && !regularBaseballCard && isBacktestReplay && (() => {
                    const actual = getActualValue(p);
                    const outcome = getActualOutcome(p);
                    const result = getBacktestResult(p);
                    const theme = getBacktestResultTheme(result);
                    if (!Number.isFinite(actual) && !outcome && !result) return null;
                    return (
                      <div
                        style={{
                          marginTop: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          title="Settled actual value from backtest DB"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "5px 10px",
                            borderRadius: 999,
                            background: "#eff6ff",
                            color: "#1d4ed8",
                            border: "1px solid #bfdbfe",
                            fontSize: 11,
                            fontWeight: 900,
                            lineHeight: 1,
                          }}
                        >
                          Actual: {Number.isFinite(actual) ? actual : "—"}{outcome ? ` ${outcome}` : ""}
                        </span>
                        <span
                          title="Whether the original PWG tag/pick hit"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "5px 10px",
                            borderRadius: 999,
                            background: theme.bg,
                            color: theme.color,
                            border: `1px solid ${theme.border}`,
                            fontSize: 11,
                            fontWeight: 900,
                            lineHeight: 1,
                          }}
                        >
                          {theme.icon} {theme.label}
                        </span>
                      </div>
                    );
                  })()}


                  {canSeeAdvancedCard && !regularBaseballCard && (() => {
                    const g1 = []; // primary
                    const g2 = []; // situational (schedule/game script)
                    const g3 = []; // other risk flags / notes

                    const seen = new Set();
                    const normKey = (s) =>
                            String(s || "")
                              .toLowerCase()
                              .replace(/[^a-z0-9]/g, "");

                    const pushUnique = (group, label, tone) => {
                      const k = normKey(label);
                      if (!k || seen.has(k)) return;
                      seen.add(k);
                      group.push({ label, tone });
                    };

                    // Risk tier (primary)
                    const riskTier = (p?.["Risk Tier"] ?? p?.Risk_Tier ?? "").toString().trim();
                    const riskScore = p?.["Risk Score"] ?? p?.Risk_Score;
                    if (riskTier) {
                      const tierLabel = riskScore != null && riskScore !== ""
                        ? `Risk: ${riskTier} (${Number(riskScore).toFixed(2)})`
                        : `Risk: ${riskTier}`;
                      const rt = String(riskTier).toUpperCase();
                      // keep MODERATE as warn (not red), ELEVATED as danger
                      const tone = rt.includes("LOW") ? "ok" : rt.includes("MOD") ? "warn" : "danger";
                      pushUnique(g1, tierLabel, tone);
                    }

                    // Opp L10 history (primary)
                    const oppHitPct = p?.Opp_L10_HitPct ?? p?.["Opp L10 Hit%"] ?? p?.["Opp L10 HitPct"] ?? null;
                    const oppN = p?.Opp_L10_N ?? p?.["Opp L10 N"] ?? null;
                    if (oppHitPct != null && oppHitPct !== "" && Number(oppN || 0) > 0) {
                      const pct = Math.round(Number(oppHitPct) * 100);
                      pushUnique(g1, `Opp L10 Hit% ${pct}% (N=${oppN})`, "neutral");
                    }

                    // B2B + Blowout (situational)
                    const b2bToday = p?.b2b_today ?? p?.["b2b_today"];
                    const b2bTomorrow = p?.b2b_tomorrow ?? p?.["b2b_tomorrow"];
                    if (b2bToday === true || String(b2bToday).toLowerCase() === "true") pushUnique(g2, "B2B Today", "warn");
                    if (b2bTomorrow === true || String(b2bTomorrow).toLowerCase() === "true") pushUnique(g2, "B2B Tomorrow", "warn");

                    const blowout = normalizeBlowoutTier(p);
                    const hasBlowout = blowout && blowout !== "none" && blowout !== "na" && blowout !== "n/a";
                    if (hasBlowout) {
                      const bt = blowout.includes("high") ? "danger" : blowout.includes("low") ? "ok" : "warn";
                      pushUnique(g2, `Blowout: ${blowout}`, bt);
                    }

                    // Other risk flags (avoid duplicating blowout/b2b)
                    const rfRaw = p?.Risk_Flags ?? p?.["Risk Flags"] ?? p?.["Risk_Flags"] ?? "";
                    const rfList = String(rfRaw || "")
                      .split(/\||,|;+/g)
                      .map((s) => s.trim())
                      .filter(Boolean);

                    // Keep Fatigue visible (if present), but only as part of the minimal watchouts row.
                    // This prevents the card from becoming cluttered while preserving the signal.
                    const hasFatigue = rfList.some((f) => normKey(f).includes("fatigue"));
                    if (hasFatigue) pushUnique(g2, "FatigueRisk", "warn");

                    rfList.forEach((f) => {
                      const k = normKey(f);
                      if (!k) return;

                      // de-dupe blowout variants when Blowout chip exists
                      if (hasBlowout && k.includes("blowout")) return;

                      // de-dupe b2b variants (we already show them)
                      if ((b2bToday || b2bTomorrow) && (k.includes("b2b") || k.includes("back to back"))) return;

                      // tone mapping
                      let tone = "warn";
                      if (k.includes("minutes") || k.includes("lineup")) tone = "warn";
                      if (k.includes("pricing")) tone = "warn";
                      if (k.includes("fatigue")) tone = "warn";
                      if (k.includes("injur") || k.includes("out") || k.includes("question")) tone = "danger";

                      pushUnique(g3, f, tone);
                    });

                    // --- styles ---
                    const chipStyle = (tone) => {
                      const base = {
                        padding: "4px 8px",
                        borderRadius: 999,
                        border: "1px solid rgba(0,0,0,0.10)",
                        fontSize: 12,
                        fontWeight: 700,
                        marginRight: 6,
                        marginBottom: 6,
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                      };
                      if (tone === "ok")
                        return { ...base, background: "rgba(16,185,129,0.12)", color: "#065f46", borderColor: "rgba(16,185,129,0.25)" };
                      if (tone === "warn")
                        return { ...base, background: "rgba(245,158,11,0.14)", color: "#92400e", borderColor: "rgba(245,158,11,0.28)" };
                      if (tone === "danger")
                        return { ...base, background: "rgba(239,68,68,0.10)", color: "#991b1b", borderColor: "rgba(239,68,68,0.22)" };
                      return { ...base, background: "rgba(148,163,184,0.15)", color: "#334155", borderColor: "rgba(148,163,184,0.35)" };
                    };

                    // Keep cards lean. Full context belongs in the Details modal.
                    const MAX_TOTAL = 4;
                    const g1Out = cleanBaseballAdminCard ? g1.slice(0, 1) : g1.slice(0, 2);
                    const g2Out = cleanBaseballAdminCard ? [] : g2.slice(0, 2);
                    const g3Out = []; // intentionally hidden on card

                    const Row = ({ items }) =>
                      !items?.length ? null : (
                        <div style={{ display: "flex", flexWrap: "wrap", marginTop: 4 }}>
                          {items.map((c, i) => (
                            <span key={`card-chip-${c.label}-${i}`} style={chipStyle(c.tone)} title={c.label}>
                              {c.label}
                            </span>
                          ))}
                        </div>
                      );

                    return (
                      <div style={{ marginTop: 4 }}>
                        <Row items={g1Out} />
                        <Row items={g2Out} />
                        <Row items={g3Out} />
                      </div>
                    );
                  })()}

                  {!isNarrow && !isWnbaAnalysisBlocked(p, activeSport) && (
                    <button
                      type="button"
                      className={`pwg-desktop-direction pwg-desktop-direction--${mobileDirectionMeta.direction.toLowerCase()}`}
                      data-testid="desktop-card-direction"
                      aria-label={directionToggleLabel}
                      aria-pressed={saved}
                      onClick={(event) => { event.stopPropagation(); toggleMyGuru(p); }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          toggleMyGuru(p);
                        }
                      }}
                    >
                      <strong>{saved ? "✓ " : ""}{mobileDirectionMeta.direction === "OVER" ? "▲ OVER" : mobileDirectionMeta.direction === "UNDER" ? "▼ UNDER" : "—"}</strong>
                    </button>
                  )}

                  {activeSport === "NHL" && nhlAnalyticsSummary(p) && (
                    <div data-testid="nhl-analytics-summary" style={{ marginTop: 4, color: darkMode ? "#E2E8F0" : "#334155", fontSize: 12, fontWeight: 800 }}>
                      {nhlAnalyticsSummary(p)}
                    </div>
                  )}
                  {hasCanonicalMatchupContract(p) && (()=>{ const matchup=nhlMatchupPresentation(p); return <div data-testid="nhl-desktop-matchup" title={matchup.accessibleLabel} style={{ marginTop:4,color:darkMode?"#E2E8F0":"#334155",fontSize:12,fontWeight:850 }}>{matchup.compactLabel}{matchup.adjustedProjection !== null ? ` · Adjusted ${matchup.adjustedProjection.toFixed(2)} vs ${Number(p["Prop Value"]).toFixed(2)}` : ""}</div>; })()}

                  {cleanBaseballAdminCard && (
                    <div className="pwg-card-strategy-footer"><StrategyMarker tag={p.Tag} /></div>
                  )}

                  {cleanBaseballAdminCard ? (
                    getMlbPlayerType(p) === "PITCHER" ? (
                      dedicatedPitcherContext.length > 0 && (
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {dedicatedPitcherContext.map((chip, idx) => (
                            <span
                              key={`pitcher-opponent-context-${idx}-${chip.label}`}
                              title={chip.title || chip.label}
                              style={mlbEnvChipStyle(chip.tone)}
                            >
                              {chip.label}
                            </span>
                          ))}
                        </div>
                      )
                    ) : renderHitterPitcherContext(p, activeSport, false, darkMode)
                  ) : regularBaseballCard ? null : (
                    (!isBasicCardExperience || !isBaseballCard) &&
                      renderHitterPitcherContext(p, activeSport, isBasicCardExperience, darkMode)
                  )}

                  <div style={{ marginTop: minimalCardExperience ? 0 : "0.5rem" }}>
                  {isWnbaAnalysisBlocked(p, activeSport) ? null : cleanBaseballAdminCard ? null : (minimalCardExperience || isNarrow) ? (
                    <div className="pwg-card-strategy-footer"><StrategyMarker tag={p.Tag} /></div>
                  ) : (
                    <TagWithInfo
                      tag={p.Tag}
                      confidence={p.Confidence}
                      projected={p.Proj}
                      delta={p.DELTA}
                      line={p.Line}
                      guruAlert={p.GuruAlert}
                      guruAlertTooltip={p.GuruAlert_Tooltip}
                      tagColorMap={tagColorMap}
                      getConfidenceLabel={getConfidenceLabel}
                      activeSport={activeSport}
                      publicMode={false}
                    />
                  )}

                  {canSeeAdvancedCard && !regularBaseballCard && (() => {

                    const { bounceLabel, contextLabel } = getCardSignals(p);
                    const bounceStateScore = getBounceStateScore(p);
                    const bounceCardLabel = bounceLabel && Number.isFinite(bounceStateScore)
                      ? `${bounceLabel} • ${Math.round(bounceStateScore)}`
                      : bounceLabel;
                    const duplicatePitcherContextKinds = new Set([
                      "pitcherTier",
                      "attackability",
                      "kpct",
                      "whip",
                    ]);
                    const mlbEnvChips = getMlbEnvironmentChips(p, activeSport).filter((chip) =>
                      !pitcherContextRendered || !duplicatePitcherContextKinds.has(chip.kind)
                    );

                    const renderChip = (label) => {
                      if (!label || label === "Neutral") return null;
                    
                      const txt = String(label).toUpperCase();
                    
                      let bg = "#f3f4f6";
                      let color = "#374151";
                      let border = "#d1d5db";
                    
                      // ------------------------------
                      // PRIMARY SIGNALS (strong colors)
                      // ------------------------------
                      if (txt.includes("STRONG BOUNCE")) {
                        bg = "#dcfce7";
                        color = "#166534";
                        border = "#86efac";
                      } else if (txt.includes("BOUNCE")) {
                        bg = "#ecfccb";
                        color = "#3f6212";
                        border = "#bef264";
                      } else if (txt.includes("EXTENDED") || txt.includes("HOT")) {
                        bg = "#fef3c7";
                        color = "#92400e";
                        border = "#fcd34d";
                      } else if (txt.includes("FAILED BOUNCE") || txt.includes("COOLING") || txt.includes("ROLLING")) {
                        bg = "#fee2e2";
                        color = "#991b1b";
                        border = "#fca5a5";
                      } else if (txt.includes("HOLDING") || txt.includes("SUSTAINING")) {
                        bg = "#dcfce7";
                        color = "#166534";
                        border = "#86efac";
                      } else if (txt.includes("SOFTENING")) {
                        bg = "#fef3c7";
                        color = "#92400e";
                        border = "#fcd34d";
                      } else if (txt.includes("RISING")) {
                        bg = "#dcfce7";
                        color = "#166534";
                        border = "#86efac";
                      } else if (txt.includes("STABLE")) {
                        bg = "#ecfeff";
                        color = "#155e75";
                        border = "#67e8f9";
                      } else if (txt.includes("RISK")) {
                        bg = "#fee2e2";
                        color = "#991b1b";
                        border = "#fca5a5";
                      } else if (txt.includes("SOFT")) {
                        bg = "#fef3c7";
                        color = "#92400e";
                        border = "#fcd34d";
                      }
                    
                      // ------------------------------
                      // SECONDARY CONTEXT
                      // ------------------------------
                      if (
                        txt.includes("VS ") ||
                        txt.includes("BATTING") ||
                        txt.includes("ORDER")
                      ) {
                        bg = "#f9fafb";
                        color = "#6b7280";
                        border = "#e5e7eb";
                      }
                    
                      return (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "5px 10px",
                            borderRadius: 999,
                            background: bg,
                            color,
                            border: `1px solid ${border}`,
                            fontSize: 11,
                            fontWeight: 800,
                            lineHeight: 1,
                          }}
                        >
                          {label}
                        </span>
                      );
                    };

                    const modelSignals = [
                      renderChip(bounceCardLabel),
                      renderChip(contextLabel),
                      renderFormationChip(p, true),
                      ...React.Children.toArray(renderMlbGraphIntelChips(p)),
                      renderPatternChip(p, true),
                      renderGuruPickChip(p, true),
                    ].filter(Boolean);

                    if (cleanBaseballAdminCard) {
                      if (!modelSignals.length) return null;
                      return (
                        <BaseballModelSignalsDisclosure
                          player={p.Player}
                          signals={modelSignals}
                          darkMode={darkMode}
                        />
                      );
                    }

                    return (
                      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {renderChip(bounceCardLabel)}
                        {renderChip(contextLabel)}
                        {renderFormationChip(p, true)}

                        {mlbEnvChips.map((chip, idx) => (
                          <span
                            key={`mlb-env-${idx}-${chip.label}`}
                            title={chip.title || chip.label}
                            style={mlbEnvChipStyle(chip.tone)}
                          >
                            {chip.label}
                          </span>
                        ))}

                        {renderMlbGraphIntelChips(p)}
                        {renderPatternChip(p, true)}
                        {renderGuruPickChip(p, true)}
                      </div>
                    );
                  })()}

                  </div>
                </div>
                  {/* Advanced-card footer. Basic users select the recommendation pill itself. */}
                  {canSeeAdvancedCard && !regularBaseballCard && (
                  <div
                    className="pwg-legacy-card-footer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: '0.75rem',
                      minHeight: 36
                    }}
                  >
                    {canSeeAdvancedCard && hasDetailData && !isNarrow && !isBaseballCard && (
                      <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // open the same modal mobile tap opens
                        handleOpenGridDetails(p, pageRows);
                      }}
                      style={{
                        fontSize: 12,
                        padding: "0.25rem 0.65rem",
                        background: darkMode ? "rgba(255,255,255,0.06)" : "#fff",
                        color: darkMode ? "#E5E7EB" : "#111827",
                        border: `1px solid ${darkMode ? "rgba(255,255,255,0.10)" : "#CBD5E1"}`,
                        borderRadius: 999,
                        cursor: "pointer",
                        fontWeight: 800,
                        letterSpacing: 0.2,
                      }}
                      title="Open full details"
                    >
                      Details
                    </button>
                    )}

                    <div style={{ marginLeft: 'auto' }} />

                    <button
                      onClick={(e) => { e.stopPropagation(); toggleMyGuru(p); }}
                      title={saved ? 'Remove from My List' : 'Add to My List'}
                      style={{
                        border: '1px solid #CBD5E1',
                        background: saved ? '#FFF7E6' : '#F8FAFC',
                        color: '#111827',
                        fontSize: 12,
                        fontWeight: 700,
                        padding: minimalCardExperience ? '0.35rem 0.55rem' : '0.2rem 0.55rem',
                        borderRadius: 999,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer'
                      }}
                    >
                      <span aria-hidden style={{ fontSize: minimalCardExperience ? 18 : 12 }}>
                        {saved ? '★' : '☆'}
                      </span>
                      {canSeeAdvancedCard ? (saved ? 'In My Plays' : 'Add') : null}
                    </button>
                  </div>
                  )}

                  {canSeeAdvancedCard && !regularBaseballCard && isExpanded && !isNarrow && (
                    <>
                      {/* moved from main card section*/}
                      {/* Positional defense / DvP summary */}
                  {(() => {
                    const pos = (p.POS || p["POS"] || "").toString().trim();
                    const dvpTier = (p["Opp_DvP_Tier"] || "").toString().trim();
                    const dvpRankRaw = p["Opp_DvP_Rank"];
                    const rawMod = p["DvP_Modifier"] ?? p["Opp_Suppression_vs_Pos"];
                    const dvpMod = rawMod === undefined || rawMod === null || rawMod === "" ? null : Number(rawMod);

                    const hasTier = dvpTier.length > 0;
                    const hasRank = dvpRankRaw !== undefined && dvpRankRaw !== null && dvpRankRaw !== "";
                    const dvpRank = hasRank ? Number(dvpRankRaw) : null;
                    const hasMod = Number.isFinite(dvpMod) && dvpMod > 0;
                    if (!hasTier && !hasMod && !hasRank) return null;

                    // Minimal label like the grid: "G-F TGT 21" / "SG NEU 14" / "R0"
                    const tierLower = dvpTier.toLowerCase();
                    const isR0 = tierLower === "r0";

                    let tierShort = "NEU";
                    if (tierLower.includes("target")) tierShort = "TGT";
                    else if (tierLower.includes("soft")) tierShort = "SFT";
                    else if (tierLower.includes("tough")) tierShort = "TGH";
                    else if (tierLower.includes("elite")) tierShort = "ELT";
                    else if (tierLower.includes("neutral")) tierShort = "NEU";
                    else if (isR0) tierShort = "R0";

                    let style = chipBase;
                    if (isR0) style = chipBase;
                    else if (tierShort === "TGT" || tierShort === "SFT") style = goodChip;
                    else if (tierShort === "TGH" || tierShort === "ELT") style = riskChip;

                    const rankTxt = Number.isFinite(dvpRank) ? ` ${Math.round(dvpRank)}` : "";
                    const text = isR0 ? "R0" : `${pos ? pos + " " : ""}${tierShort}${rankTxt}`.trim();

                    // Tooltip keeps the full context without clutter
                    let title = "";
                    if (pos) title += `DvP vs ${pos}`;
                    if (hasTier) title += `${title ? ": " : ""}${dvpTier}`;
                    if (Number.isFinite(dvpRank)) title += ` (Rank ${dvpRank})`;
                    if (hasMod) title += ` • x${dvpMod.toFixed(2)}`;

                    return (
                      <div
                        style={{
                          marginTop: 4,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "0.35rem",
                        }}
                      >
                        <span style={{ fontSize: 12, color: darkMode ? "#9CA3AF" : "#6B7280" }}>
                          Matchup defense:
                        </span>
                        <span style={style} title={title}>
                          {text}
                        </span>
                      </div>
                    );
                  })()}


                      {/* Selection Strategy */}
                    {(() => {
                      const ss = formatSelectionStrategy(p);
                      if (!ss) return null;

                      // Direction color: green for OVER, red for UNDER, neutral grey otherwise
                      const dirLower = ss.label.toLowerCase();
                      let bg = "#e5e7eb";
                      let color = "#111827";
                      if (dirLower.includes("over")) {
                        bg = "#dcfce7";
                        color = "#166534";
                      } else if (dirLower.includes("under")) {
                        bg = "#fee2e2";
                        color = "#b91c1c";
                      }

                      return (
                        <div
                          style={{
                            marginTop: 4,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "0.35rem",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              color: darkMode ? "#9CA3AF" : "#6B7280",
                            }}
                          >
                            Selection strategy:
                          </span>
                          <span
                            style={{
                              background: bg,
                              color,
                              borderRadius: 999,
                              padding: "2px 8px",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {ss.label}
                            {ss.breakdown && ` (${ss.breakdown})`}
                          </span>
                        </div>
                      );
                    })()}
                    {/* Line tax pill */}
                    {taxMeta ? (
                      <div
                        style={{
                          marginTop: 4,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: darkMode ? "#9CA3AF" : "#6B7280",
                          }}
                        >
                          Line tax:
                        </span>

                        <span
                          title={taxMeta.title || ""}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "6px 12px",
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: 0.5,
                            lineHeight: 1,
                            background: taxMeta.bg,
                            color: taxMeta.fg,
                            border: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                          }}
                        >

                          {taxMeta.short}{taxMeta.pctTxt}

                        </span>
                      </div>
                    ) : null}


                    {zodiacChip && (
                      <div
                        style={{
                          marginTop: 4,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "0.35rem",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: darkMode ? "#9CA3AF" : "#6B7280",
                          }}
                        >
                          Zodiac:
                        </span>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 600,
                            background: darkMode ? "#020617" : "#eff6ff",
                            color: darkMode ? "#e5e7eb" : "#111827",
                            border: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {zodiacChip.label}
                        </span>
                      </div>
                    )}


                    {/* --- Divider: drivers -> context --- */}
                      <div style={sectionDivider(darkMode)} />

                          
                      <div style={sectionLabel(darkMode)}>Context</div>

                      <div
                        style={{
                          padding: "8px 10px",
                          borderRadius: 12,
                          background: darkMode ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
                          border: darkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
                          marginTop: 2,
                        }}
                      >
                        {/* existing chips row + risk badges */}
                                              {/* Analytics chips (only if user has access) */}
                      {canUseFeature && (() => {
                        // Only render if NBA is active OR the row actually has NBA context fields
                        const hasNBAKeys =
                          "b2b_today" in p || "b2b_tomorrow" in p || "mpg" in p ||
                          "min_trend" in p || "ft_rate_trend" in p || "shot_vol_trend" in p ||
                          "fouls_trend" in p || "reb_trend" in p || "oreb_trend" in p ||
                          "pts_trend" in p || "stocks_trend" in p || "blowout_risk" in p;

                        if (activeSport !== "NBA" && !hasNBAKeys) return <ContextState available={false} darkMode={darkMode} />;

                        // ---- Styles ----
                        const rowStyle = {
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "6px",
                          margin: ".4rem 0",
                        };

                        // ---- Helpers ----
                        const toNum = (v) => {
                          if (v === null || v === undefined) return null;
                          const n = Number(String(v).replace(/[^\d.+-eE]/g, ""));
                          return Number.isFinite(n) ? n : null;
                        };

                        // Numeric (e.g., +1.2), or direction labels ("up"/"down"/"flat")
                        const parseTrend = (v) => {
                          if (v === null || v === undefined) return { kind: "none" };
                          const s = String(v).trim().toLowerCase();
                          const n = toNum(v);
                          if (n !== null) return { kind: "numeric", value: n };
                          if (s === "up") return { kind: "dir", dir: "up" };
                          if (s === "down") return { kind: "dir", dir: "down" };
                          if (s === "flat" || s === "even" || s === "neutral") return { kind: "dir", dir: "flat" };
                          return { kind: "none" };
                        };

                        // ===== Meaning-aware coloring =====
                        const POLARITY = {
                          min_trend:      "up_good",   // more minutes = good
                          shot_vol_trend: "up_good",
                          ft_rate_trend:  "up_good",
                          fouls_trend:    "down_good", // fewer fouls = good
                          reb_trend:      "up_good",
                          oreb_trend:     "up_good",
                          pts_trend:      "up_good",
                          stocks_trend:   "up_good",
                        };
                        const EPS = { default: 0.2, fouls_trend: 0.15 };

                        const toneForStat = (num, statKey) => {
                          const eps = EPS[statKey] ?? EPS.default;
                          const pol = POLARITY[statKey] ?? "neutral";
                          if (!Number.isFinite(num)) return chipBase;
                        
                          if (pol === "up_good")   return num >  eps ? goodChip : (num < -eps ? warnChip : chipBase);
                          if (pol === "down_good") return num < -eps ? goodChip : (num >  eps ? warnChip : chipBase);
                          return Math.abs(num) > eps ? warnChip : chipBase;
                        };
                        

                        // Pretty trend pill w/ semantics-aware color
                        const trendChip = (label, raw, statKey) => {
                          const t = parseTrend(raw);
                          if (t.kind === "none") return null;
                          const eps = EPS[statKey] ?? EPS.default;

                          if (t.kind === "numeric") {
                            const num = t.value;
                            const arrow = num > eps ? "▲" : num < -eps ? "▼" : "•";
                            const signed = num > 0 ? `+${num.toFixed(1)}` : (num < 0 ? `−${Math.abs(num).toFixed(1)}` : "±0.0");
                            return <span style={toneForStat(num, statKey)}>{label} {arrow} {signed}</span>;
                          }

                          // Directional strings: color by meaning even w/o a number
                          if (t.kind === "dir") {
                            const pol = POLARITY[statKey] || "neutral";
                            const arrow = t.dir === "up" ? "▲" : t.dir === "down" ? "▼" : "•";
                            let tone = chipBase;
                            if (pol === "up_good")   tone = t.dir === "up" ? goodChip : (t.dir === "down" ? warnChip : chipBase);
                            else if (pol === "down_good") tone = t.dir === "down" ? goodChip : (t.dir === "up" ? warnChip : chipBase);
                            return <span style={tone}>{label} {arrow}</span>;
                          }
                          return null;
                        };

                        // B2B: No = good (green), Yes = risk (amber)
                        const boolChip = (label, on) => {
                          if (on === undefined || on === null) return null;
                          return <span style={on ? warnChip : goodChip}>{label}{on ? " • Yes" : " • No"}</span>;
                        };

                        const numChip = (label, v, digits = 1) => {
                          const n = toNum(v);
                          if (n === null) return null;
                          return <span style={chipBase}>{label} {n.toFixed(digits)}</span>;
                        };

                        // Blowout: string label OR numeric percent
                        const blowoutPill = (v) => {
                          if (v === null || v === undefined) return null;
                        
                          const raw = String(v).trim();
                          const s = raw.toLowerCase();
                          let tone = chipBase;
                          const label = "Blowout";
                        
                          // 1) Handle text labels first (low / moderate / high / none)
                          if (s.includes("low") || s === "none") {
                            tone = goodChip;
                            return <span style={tone}>{label} low</span>;
                          }
                          if (s.includes("mod")) {
                            tone = warnChip;
                            return <span style={tone}>{label} moderate</span>;
                          }
                          if (s.includes("high")) {
                            tone = riskChip;
                            return <span style={tone}>{label} high</span>;
                          }
                        
                          // 2) Otherwise, treat as a numeric percentage only if > 0
                          const pctNum = toNum(s.endsWith("%") ? s.slice(0, -1) : s);
                          if (pctNum !== null && pctNum > 0) {
                            let toneForPct = chipBase;
                            if (pctNum >= 20) toneForPct = riskChip;
                            else if (pctNum >= 10) toneForPct = warnChip;
                            else toneForPct = goodChip;
                        
                            const capped = Math.max(0, Math.min(100, pctNum));
                            return (
                              <span style={toneForPct}>
                                {label} {capped.toFixed(0)}%
                              </span>
                            );
                          }
                        
                          // 3) For 0 or anything unrecognized, show nothing (no 0% chip)
                          return null;
                        };

                        // ---- Read fields from row p ----
                        const b2bToday     = Boolean(p["b2b_today"]);
                        const b2bTomorrow  = Boolean(p["b2b_tomorrow"]);
                        const mpg          = p["mpg"];
                        const minTrend     = p["min_trend"];
                        const ftRateTrend  = p["ft_rate_trend"];
                        const shotVolTrend = p["shot_vol_trend"];
                        const foulsTrend   = p["fouls_trend"];
                        const rebTrend     = p["reb_trend"];
                        const orebTrend    = p["oreb_trend"];
                        const ptsTrend     = p["pts_trend"];
                        const stocksTrend  = p["stocks_trend"];
                        const blowoutRisk  = getBlowoutTierRaw(p);

                        // NEW: minutes sample info
                        const minutesGamesSample = p["minutes_games_sample"];
                        const minutesRecentMpg   = p["minutes_recent_mpg"];
                        const minutesStd         = p["minutes_std"];
                        const minutesFlag        = p["minutes_flag"];       // "Tiny Sample" | "Small Sample" | "OK"
                        const lineupMinutesOk    = p["lineup_minutes_ok"];  // boolean

                        // Show the row if *any* signal exists (numeric OR categorical)
                        const hasAnyNBA =
                          b2bToday || b2bTomorrow ||
                          toNum(mpg) !== null ||
                          [minTrend, ftRateTrend, shotVolTrend, foulsTrend, rebTrend, orebTrend, ptsTrend, stocksTrend]
                            .some(v => parseTrend(v).kind !== "none") ||
                          String(blowoutRisk ?? "").trim() ||
                          String(minutesFlag ?? "").trim() ||
                          String(gridDetailsRow?.["Risk Tier"] ?? gridDetailsRow?.RiskTier ?? gridDetailsRow?.riskTier ?? "").trim() ||
                          String(gridDetailsRow?.["Risk Flags"] ?? gridDetailsRow?.RiskFlags ?? gridDetailsRow?.riskFlags ?? "").trim() ||
                          (toNum(gridDetailsRow?.["Risk Score"] ?? gridDetailsRow?.RiskScore ?? gridDetailsRow?.riskScore) !== null);
                                                if (!hasAnyNBA) return null;

                        // Consolidated risk (preferred if provided by backend)
                        const riskTierRaw =
                          gridDetailsRow["Risk Tier"] ??
                          gridDetailsRow.RiskTier ??
                          gridDetailsRow.riskTier ??
                          gridDetailsRow["RiskTier"] ??
                          "";

                        const riskScoreRaw =
                          gridDetailsRow["Risk Score"] ??
                          gridDetailsRow.RiskScore ??
                          gridDetailsRow.riskScore ??
                          gridDetailsRow["RiskScore"] ??
                          null;

                        const riskFlagsRaw =
                          gridDetailsRow["Risk Flags"] ??
                          gridDetailsRow.RiskFlags ??
                          gridDetailsRow.riskFlags ??
                          gridDetailsRow["RiskFlags"] ??
                          "";

                        const riskTier = String(riskTierRaw || "").toUpperCase().trim();
                        const riskScore = toNum(riskScoreRaw);

                        const parsedFlags = String(riskFlagsRaw || "")
                          .split(/[|,]/)
                          .map(s => s.trim())
                          .filter(Boolean);

                        // Build risk badges (use backend flags first; fall back to local heuristics)
                        const riskBadges = [];

                        const addFlagBadge = (key, label, tone = "risk") => {
                          const style = tone === "warn" ? warnChip : (tone === "good" ? goodChip : riskChip);
                          riskBadges.push(<span key={`rb-${key}`} style={style}>{label}</span>);
                        };

                        if (parsedFlags.length > 0) {
                          parsedFlags.forEach((f) => {
                            const k = String(f).toLowerCase();
                            if (k === "blowout_high") addFlagBadge("bo-high", "Blowout Risk: High", "risk");
                            else if (k === "blowout_mod") addFlagBadge("bo-mod", "Blowout Risk: Mod", "warn");
                            else if (k === "b2b_today") addFlagBadge("b2b-today", "B2B Today", "warn");
                            else if (k === "b2b_tomorrow") addFlagBadge("b2b-tmr", "B2B Tomorrow", "warn");
                            else if (k === "minutes_tiny_sample") addFlagBadge("min-tiny", "Minutes Sample: Tiny", "risk");
                            else if (k === "minutes_small_sample") addFlagBadge("min-small", "Minutes Sample: Small", "warn");
                            else if (k === "minutes_not_lineup_safe") addFlagBadge("min-safe", "Minutes Not Safe", "warn");
                            else if (k === "trend_contradiction") addFlagBadge("trend-ctr", "Trend Disagrees", "warn");
                            else if (k === "tag_tempered") addFlagBadge("tempered", "Tag Tempered", "warn");
                          });
                        }

                        if (parsedFlags.length === 0) {

                        // Minutes trend (existing)
                        if (toNum(minTrend) !== null) {
                          const mt = toNum(minTrend);
                          if (mt <= -3.0) riskBadges.push(<span key="rb-min1" style={riskChip}>Minutes Squeeze</span>);
                          else if (mt <= -1.5) riskBadges.push(<span key="rb-min2" style={warnChip}>Minutes Down</span>);
                        }

                        // Foul trends (existing)
                        if (toNum(foulsTrend) !== null) {
                          const ft = toNum(foulsTrend);
                          if (ft >= 1.2) riskBadges.push(<span key="rb-foul1" style={riskChip}>Foul Risk ↑</span>);
                          else if (ft >= 0.7) riskBadges.push(<span key="rb-foul2" style={warnChip}>Fouls Rising</span>);
                        }

                        // NEW: minutes sample quality
                        if (minutesFlag === "Tiny Sample") {
                          riskBadges.push(
                            <span key="rb-min-sample-tiny" style={riskChip}>
                              Minutes Sample: Tiny{typeof minutesGamesSample === "number" ? ` (${minutesGamesSample} g)` : ""}
                            </span>
                          );
                        } else if (minutesFlag === "Small Sample") {
                          riskBadges.push(
                            <span key="rb-min-sample-small" style={warnChip}>
                              Minutes Sample: Small{typeof minutesGamesSample === "number" ? ` (${minutesGamesSample} g)` : ""}
                            </span>
                          );
                        }

                        // Optional: flag non-lineup-safe minutes
                        if (lineupMinutesOk === false) {
                          riskBadges.push(
                            <span key="rb-lineup-minutes" style={riskChip}>
                              Lineup Risk (minutes)
                            </span>
                          );
                        }

                        }

                        // Return a single root (fragment)
                        return (
                          <>
                            <div style={rowStyle} aria-label="Recent trends and context">
                              {/* Schedule / workload */}
                              {boolChip("B2B Today", b2bToday)}
                              {boolChip("B2B Tomorrow", b2bTomorrow)}
                              {numChip("MPG", mpg, 1)}

                              {/* Trends (meaning-aware colors) */}
                              {trendChip("Minutes Δ",   minTrend,     "min_trend")}
                              {trendChip("Shot Vol Δ",  shotVolTrend, "shot_vol_trend")}
                              {trendChip("FT Rate Δ",   ftRateTrend,  "ft_rate_trend")}
                              {trendChip("Fouls Δ",     foulsTrend,   "fouls_trend")}
                              {trendChip("Reb Δ",       rebTrend,     "reb_trend")}
                              {trendChip("OReb Δ",      orebTrend,    "oreb_trend")}
                              {trendChip("Pts Δ",       ptsTrend,     "pts_trend")}
                              {trendChip("Stocks Δ",    stocksTrend,  "stocks_trend")}
                              {riskTier && (
                                <span
                                  style={
                                    riskTier === "LOW"
                                      ? goodChip
                                      : riskTier === "MODERATE"
                                      ? warnChip
                                      : riskTier === "ELEVATED"
                                      ? riskChip
                                      : riskChip
                                  }
                                >
                                  Risk: {riskTier}{Number.isFinite(riskScore) ? ` (${riskScore.toFixed(2)})` : ""}
                                </span>
                              )}
                              {blowoutPill(blowoutRisk)}
                            </div>

                            {/* Risk badges row (goes just below chips row) */}
                            {riskBadges.length > 0 && (
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: "4px" }}>
                                {riskBadges}
                              </div>
                            )}
                          </>
                        );

                      })()}
                      </div>

                    </>
                  )}
              </div>
            );
          })}
        </div>
      </Tooltip.Provider>
      )}
    </>
  );
}; // ← make sure this ends with a semicolon


// login guard (no hooks below this line)
if (!authLoading && authInitialized && !user) {
  return (
    <div className="flex justify-center items-center h-screen">
      <h1 className="text-xl font-semibold">Please log in to access PlaysWithGuru</h1>
    </div>
  );
}
      // Placeholder – we’ll build this next
      // Renders the TEG filter bar (small: search + sector)
      // Renders the TEG filter bar (small: search + sector + card/grid toggle)
      const renderTEGFilters = () => {
        return (
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            {/* left: existing TEG controls */}
            <div
              style={{
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              <input
                type="text"
                placeholder="Search ticker or company…"
                value={playerQuery}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPlayerQuery(v);
                      setPlayerInput(v);
                    }}
                style={{
                  minWidth: 180,
                  padding: "0.35rem 0.6rem",
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  fontSize: "0.85rem",
                }}
              />

              <select
                value={selectedSector}
                onChange={(e) => setSelectedSector(e.target.value)}
                style={{
                  minWidth: 160,
                  padding: "0.35rem 0.6rem",
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  fontSize: "0.85rem",
                  background: "white",
                }}
              >
                <option value="">All sectors</option>
                {Array.from(allSectors).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>


            {/* right: card/grid toggle (shared across sports) */}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => setViewMode("card")}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  background: viewMode === "card" ? "#ffffff" : "#f9fafb",
                  color: viewMode === "card" ? "#111827" : "#6b7280",
                  boxShadow:
                    viewMode === "card" ? "0 1px 4px rgba(15,23,42,0.12)" : "none",
                  cursor: "pointer",
                }}
              >
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border:
                    viewMode === "grid" ? "1px solid #0f172a" : "1px solid #e5e7eb",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  background: viewMode === "grid" ? "#0f172a" : "#f9fafb",
                  color: viewMode === "grid" ? "#f9fafb" : "#6b7280",
                  boxShadow:
                    viewMode === "grid" ? "0 1px 4px rgba(15,23,42,0.35)" : "none",
                  cursor: "pointer",
                }}
              >
                Grid
              </button>
            </div>
          </div>
        );
      };
  

      // START of render filters for PWG
      // Renders the entire existing filter bar (search + dropdowns + tag pills + expand/collapse etc.)
           // Renders the entire existing filter bar (search + dropdowns + tag pills + expand/collapse etc.)
           

  // --------------------------------------------
  // Mobile quick panels (Games / Props shortcuts)
  // --------------------------------------------
  const renderInjuryReportPanel = () => {
    const normalizeStatus = (row) => String(
      row?.Player_Status ?? row?.player_status ?? row?.Injury_Status ??
      row?.injury_status ?? row?.Availability_Status ?? row?.availability_status ??
      row?.Status ?? row?.status ?? ""
    ).trim().toUpperCase();

    const statusOrder = ["OUT", "DOUBTFUL", "QUESTIONABLE", "GTD", "PROBABLE", "AVAILABLE"];
    const statusLabel = (status) => status === "GTD" ? "Game Time Decision" : status.charAt(0) + status.slice(1).toLowerCase();
    const statusColors = {
      OUT: { bg: "#fee2e2", fg: "#dc2626" },
      DOUBTFUL: { bg: "#f3e8ff", fg: "#7e22ce" },
      QUESTIONABLE: { bg: "#fef3c7", fg: "#d97706" },
      GTD: { bg: "#dbeafe", fg: "#2563eb" },
      PROBABLE: { bg: "#dcfce7", fg: "#15803d" },
      AVAILABLE: { bg: "#dcfce7", fg: "#15803d" },
    };

    const formatInjuryReason = (value) => {
      let text = String(value || "").trim();
      if (!text) return "";

      text = text
        .replace(/^injury\s*\/\s*illness\s*[-–—:]?\s*/i, "")
        .replace(/;/g, " ")
        .replace(/[_-]+/g, " ")
        .replace(/\b(injury|illness)\b/gi, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\s+/g, " ")
        .trim();

      // Remove repeated words, e.g. "Right Knee Right Knee".
      const words = text.split(" ");
      const half = Math.floor(words.length / 2);
      if (words.length % 2 === 0 &&
          words.slice(0, half).join(" ").toLowerCase() ===
          words.slice(half).join(" ").toLowerCase()) {
        text = words.slice(0, half).join(" ");
      }

      return text.replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const byPlayer = new Map();
    (Array.isArray(injuryReport) ? injuryReport : []).forEach((row) => {
      const player = String(
        row?.player_name ?? row?.Player_Name ?? row?.Player ?? row?.player ?? row?.name ?? ""
      ).trim();
      const status = normalizeStatus({
        ...row,
        Status: row?.current_status ?? row?.Current_Status ?? row?.Status ?? row?.status,
      });
      if (!player || !statusOrder.includes(status)) return;

      const team = row?.team_abbr ?? row?.Team_Abbr ?? row?.Team ?? row?.["Team Name"] ?? "";
      const rawNote = row?.reason ?? row?.Reason ?? row?.Status_Reason ?? row?.status_reason ?? row?.Injury_Reason ?? row?.injury_reason ?? "";
      const note = formatInjuryReason(rawNote);
      const matchup = row?.matchup ?? row?.Matchup ?? "";
      const gameTime = row?.game_time ?? row?.GameTime ?? row?.["Game Time"] ?? "";
      const key = `${player}|${team}`;

      if (!byPlayer.has(key)) {
        byPlayer.set(key, { player, team, status, note, matchup, gameTime });
      }
    });

    const injuries = Array.from(byPlayer.values()).sort((a, b) => {
      const rank = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
      return rank || a.player.localeCompare(b.player);
    });
    const visible = injuryStatusFilter === "ALL" ? injuries : injuries.filter((item) => item.status === injuryStatusFilter);
    const countFor = (status) => injuries.filter((item) => item.status === status).length;

    return (
      <div style={{ padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button type="button" className="pill" onClick={() => setInjuryStatusFilter("ALL")}>Reset</button>
            <button type="button" className="pill" onClick={() => setTaxonomyOpen(false)}>Done</button>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {["ALL", ...statusOrder].map((status) => {
            const active = injuryStatusFilter === status;
            const count = status === "ALL" ? injuries.length : countFor(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => setInjuryStatusFilter(status)}
                style={{
                  padding: "8px 12px", borderRadius: 999, cursor: "pointer", fontWeight: 850,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: active ? "#111827" : darkMode ? "#111827" : "#fff",
                  color: active ? "#fff" : darkMode ? "#f8fafc" : "#111827",
                }}
              >
                {status === "ALL" ? "All" : statusLabel(status)} ({count})
              </button>
            );
          })}
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          {visible.length === 0 ? (
            <div style={{ padding: "22px 16px", borderRadius: 14, border: "1px solid rgba(148,163,184,0.35)", background: darkMode ? "#0b1220" : "#fff", textAlign: "center" }}>
              <div style={{ fontWeight: 900, marginBottom: 5 }}>No injury reports available</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Player status data will appear here when included in the active {activeSport} feed.</div>
            </div>
          ) : visible.map((item) => {
            const tone = statusColors[item.status] || { bg: "#e2e8f0", fg: "#334155" };
            return (
              <div key={item.player} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 14px", borderRadius: 14, border: "1px solid rgba(148,163,184,0.30)", background: darkMode ? "#0b1220" : "#fff" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: 15 }}>{item.player}</div>
                  <div style={{ fontSize: 12, opacity: 0.68 }}>
                    {[item.team || activeSport, item.matchup, item.gameTime].filter(Boolean).join(" • ")}
                  </div>
                  {item.note && (
                    <div style={{ fontSize: 12, opacity: 0.82, marginTop: 3 }}>{item.note}</div>
                  )}
                </div>
                <span style={{ marginLeft: "auto", whiteSpace: "nowrap", padding: "6px 10px", borderRadius: 999, background: tone.bg, color: tone.fg, fontWeight: 900, fontSize: 11 }}>
                  {statusLabel(item.status)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMobileGamesQuickPanel = () => {
    return (
      <div style={{ padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button type="button" className="pill" onClick={() => soccerMatchIntelligenceActive ? setSelectedSoccerMatchId(null) : setSelectedGames([])}>
              Reset
            </button>
            <button type="button" className="pill" onClick={() => setTaxonomyOpen(false)}>
              Done
            </button>
          </div>
        </div>

        <div
          className="pwg-mobile-games-scroll"
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            maxWidth: "100%",
            boxSizing: "border-box",
            borderRadius: 12,
            border: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
            background: darkMode ? "#0b1220" : "#ffffff",
            padding: "0.6rem 0.75rem",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, minWidth: 0, maxWidth: "100%" }}>
              <button
                type="button"
                onClick={() => soccerMatchIntelligenceActive ? setSelectedSoccerMatchId(null) : setSelectedGames([])}
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: selectedGames?.length ? "#fff" : "#111827",
                  color: selectedGames?.length ? "#111827" : "#fff",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                All Games
              </button>

              <div style={{ width: "100%", padding: "0 2px 2px", fontSize: "0.75rem", opacity: 0.68 }}>
                Times shown in {localTimeZone}
              </div>

              {soccerMatchIntelligenceActive ? (
                soccerMatchCards.length ? soccerMatchCards.map((card) => {
                  const match = card.match || {};
                  const checked = selectedSoccerMatchId === match.match_id;
                  return <GameFilterRow key={match.match_id} primary={`${match.home_team} vs ${match.away_team}`} dateTime={formatLocalEventTime({ "Game Start Timestamp": match.kickoff_utc }, "SOCCER")} propLabel="Match Intelligence" selected={checked} disabled={false} darkMode={darkMode} onClick={() => setSelectedSoccerMatchId(match.match_id)} />;
                }) : <div style={{ width: "100%", padding: "8px 2px", color: darkMode ? "#cbd5e1" : "#475569", fontSize: 13 }}>No active Match Intelligence</div>
              ) : String(activeSport || "").toUpperCase() === "MLB" ? (
                <div style={{ width: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 7, padding: "2px 2px 7px" }}>
                    <button type="button" onClick={() => setCollapsedMLBSections({})} style={{ marginLeft: "auto", border: 0, background: "transparent", fontWeight: 800, cursor: "pointer", color: "inherit" }}>Expand all</button>
                    <button type="button" onClick={() => setCollapsedMLBSections(Object.fromEntries(mlbGameGroups.map((group) => [group.window, true])))} style={{ border: 0, background: "transparent", fontWeight: 800, cursor: "pointer", color: "inherit" }}>Collapse all</button>
                  </div>
                  {mlbGameGroups.map((group) => (
                    <section key={group.window} style={{ width: "100%", borderTop: "1px solid rgba(148,163,184,0.28)", paddingTop: 8, marginTop: 4 }}>
                      <button type="button" aria-expanded={!collapsedMLBSections[group.window]} onClick={() => setCollapsedMLBSections((current) => toggleCollapsedFilter(current, group.window))} style={{ width: "100%", border: 0, background: "transparent", textAlign: "left", fontWeight: 950, color: darkMode ? "#F8FAFC" : "#0F172A", padding: "6px 2px", cursor: "pointer" }}>
                        {collapsedMLBSections[group.window] ? "▸" : "▾"} {group.window} ({labeledCount(group.matchups.reduce((total, matchup) => total + (group.isDoubleheaders ? matchup.games.length : 1), 0), "game")})
                      </button>
                      {!collapsedMLBSections[group.window] && <div style={{ display: "grid", gap: 8, padding: "2px 0 8px 12px" }}>
                        {group.matchups.map((matchup) => {
                          const allSelected = matchup.childKeys.every((key) => selectedGames.includes(key));
                          const toggleKeys = (keys) => setSelectedGames((prev) => toggleAuthoritativeGameSelection(prev, keys));
                          if (!matchup.isDoubleheader) {
                            const game = matchup.games[0];
                            const disabled = game.projectionCount === 0;
                            return (
                              <GameFilterRow key={game.key} primary={matchup.display} dateTime={formatLocalEventTime(game.representativeRow, "MLB")} propLabel={mlbPropCountLabel(game.projectionCount)} selected={allSelected} disabled={disabled} darkMode={darkMode} onClick={() => toggleKeys([game.key])} />
                            );
                          }
                          const matchupCollapsed = collapsedMLBMatchups[matchup.key] === true;
                          const selectableChildKeys = matchup.games.filter((game) => game.projectionCount > 0).map((game) => game.key);
                          const parentSelected = selectableChildKeys.length > 0 && selectableChildKeys.every((key) => selectedGames.includes(key));
                          return (
                            <div key={matchup.key} style={{ display: "grid", gap: 5 }}>
                              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7 }}>
                                <button type="button" aria-expanded={!matchupCollapsed} onClick={() => setCollapsedMLBMatchups((current) => toggleCollapsedFilter(current, matchup.key))} style={{ border: 0, background: "transparent", padding: "4px 2px", color: "inherit", fontWeight: 950, cursor: "pointer" }}>
                                  {matchupCollapsed ? "▸" : "▾"} {matchup.display}
                                </button>
                                <button type="button" disabled={!selectableChildKeys.length} aria-pressed={parentSelected} onClick={() => toggleKeys(selectableChildKeys)} style={{ padding: "4px 8px", borderRadius: 999, border: "1px solid rgba(148,163,184,0.55)", background: parentSelected ? "#111827" : "#fff", color: parentSelected ? "#fff" : "#111827", fontWeight: 850, cursor: selectableChildKeys.length ? "pointer" : "not-allowed", opacity: selectableChildKeys.length ? 1 : 0.55 }}>
                                  Both games • {mlbPropCountLabel(matchup.projectionCount)}
                                </button>
                              </div>
                              {!matchupCollapsed && <div style={{ display: "grid", gap: 5, paddingLeft: 14 }}>
                                {matchup.games.map((game) => {
                                  const checked = selectedGames.includes(game.key);
                                  const disabled = game.projectionCount === 0;
                                  return (
                                    <GameFilterRow key={game.key} primary={`Game ${game.gameNumber}`} dateTime={formatLocalEventTime(game.representativeRow, "MLB")} propLabel={mlbPropCountLabel(game.projectionCount)} selected={checked} disabled={disabled} darkMode={darkMode} onClick={() => toggleKeys([game.key])} />
                                  );
                                })}
                              </div>}
                            </div>
                          );
                        })}
                      </div>}
                    </section>
                  ))}
                </div>
              ) : String(activeSport || "").toUpperCase() === "CFB" ? cfbGameGroups.map((conference) => (
                <section key={conference.conference} style={{ width: "100%", borderTop: "1px solid rgba(148,163,184,0.28)", paddingTop: 8 }}>
                  <div style={{ fontWeight: 950, color: darkMode ? "#F8FAFC" : "#0F172A", padding: "6px 2px" }}>{conference.conference}</div>
                  {conference.windows.map((window) => (
                    <div key={window.window} style={{ padding: "4px 0 8px 14px" }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 850, opacity: 0.72, marginBottom: 6 }}>{window.window}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {window.games.map((game) => {
                          const checked = selectedGames.includes(game.key);
                          const projectionCount = customerGameCounts.get(game.key) || 0;
                          return <GameFilterRow key={game.key} primary={game.display} dateTime={formatLocalEventTime(game.representativeRow, "CFB")} propLabel={mlbPropCountLabel(projectionCount)} selected={checked} disabled={projectionCount === 0} darkMode={darkMode} onClick={() => setSelectedGames((prev) => checked ? prev.filter((key) => key !== game.key) : [...prev, game.key])} />;
                        })}
                      </div>
                    </div>
                  ))}
                </section>
              )) : String(activeSport || "").toUpperCase() === "SOCCER" ? soccerGameGroups.map((group) => {
                const collapsed = collapsedSoccerLeagues[group.competition] === true;
                const groupKeys = group.games.map((game) => game.key);
                const allSelected = groupKeys.length > 0 && groupKeys.every((key) => selectedGames.includes(key));
                return (
                  <section key={group.competition} style={{ width: "100%", borderTop: "1px solid rgba(148,163,184,0.28)", paddingTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        type="button"
                        aria-expanded={!collapsed}
                        onClick={() => setCollapsedSoccerLeagues((prev) => ({ ...prev, [group.competition]: !collapsed }))}
                        style={{ flex: 1, border: 0, background: "transparent", padding: "6px 2px", textAlign: "left", fontWeight: 950, color: darkMode ? "#F8FAFC" : "#0F172A", cursor: "pointer" }}
                      >
                        {collapsed ? "▸" : "▾"} {group.competition} ({group.games.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedGames((prev) => {
                          const current = Array.isArray(prev) ? prev : [];
                          return allSelected
                            ? current.filter((key) => !groupKeys.includes(key))
                            : [...new Set([...current, ...groupKeys])];
                        })}
                        style={{ border: "1px solid rgba(148,163,184,0.55)", borderRadius: 999, padding: "5px 9px", background: allSelected ? "#111827" : "#fff", color: allSelected ? "#fff" : "#111827", fontWeight: 850, cursor: "pointer" }}
                      >
                        {allSelected ? "Clear league" : "All league"}
                      </button>
                    </div>
                    {!collapsed && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "4px 0 8px 18px" }}>
                        {group.games.map((game) => {
                          const checked = selectedGames.includes(game.key);
                          const localizedTime = formatLocalEventTime(game.representativeRow, "SOCCER");
                          const projectionCount = customerGameCounts.get(game.key) || 0;
                          return (
                            <GameFilterRow
                              key={game.key}
                              primary={game.display}
                              dateTime={localizedTime}
                              propLabel={mlbPropCountLabel(projectionCount)}
                              selected={checked}
                              disabled={projectionCount === 0}
                              darkMode={darkMode}
                              onClick={() => setSelectedGames((prev) => {
                                const current = Array.isArray(prev) ? prev : [];
                                return checked ? current.filter((key) => key !== game.key) : [...current, game.key];
                              })}
                            />
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              }) : String(activeSport || "").toUpperCase() === "NFL" ? <div style={{ width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 7, padding: "2px 2px 7px" }}>
                  <button type="button" onClick={() => setCollapsedNFLWindows({})} style={{ marginLeft: "auto", border: 0, background: "transparent", fontWeight: 800, cursor: "pointer", color: "inherit" }}>Expand all</button>
                  <button type="button" onClick={() => setCollapsedNFLWindows(Object.fromEntries(regularNFLGameGroups.map((group) => [group.key, true])))} style={{ border: 0, background: "transparent", fontWeight: 800, cursor: "pointer", color: "inherit" }}>Collapse all</button>
                </div>
                {regularNFLGameGroups.map((group) => {
                const collapsed = collapsedNFLWindows[group.key] === true;
                const groupKeys = group.games.map((game) => game.key);
                const allSelected = groupKeys.length > 0 && groupKeys.every((key) => selectedGames.includes(key));
                return (
                  <section key={group.key} style={{ width: "100%", borderTop: "1px solid rgba(148,163,184,0.28)", paddingTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        type="button"
                        aria-expanded={!collapsed}
                        onClick={() => setCollapsedNFLWindows((prev) => ({ ...prev, [group.key]: !collapsed }))}
                        style={{ flex: 1, border: 0, background: "transparent", padding: "6px 2px", textAlign: "left", fontWeight: 950, color: darkMode ? "#F8FAFC" : "#0F172A", cursor: "pointer" }}
                      >
                        {collapsed ? "▸" : "▾"} {group.label} ({group.games.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedGames((prev) => {
                          const current = Array.isArray(prev) ? prev : [];
                          return allSelected
                            ? current.filter((key) => !groupKeys.includes(key))
                            : [...new Set([...current, ...groupKeys])];
                        })}
                        style={{ border: "1px solid rgba(148,163,184,0.55)", borderRadius: 999, padding: "5px 9px", background: allSelected ? "#111827" : "#fff", color: allSelected ? "#fff" : "#111827", fontWeight: 850, cursor: "pointer" }}
                      >
                        {allSelected ? "Clear window" : "All window"}
                      </button>
                    </div>
                    {!collapsed && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "4px 0 8px 18px" }}>
                        {group.games.map((game) => {
                          const checked = selectedGames.includes(game.key);
                          const localizedTime = formatLocalEventTime(game.representativeRow, "NFL");
                          const projectionCount = customerGameCounts.get(game.key) || 0;
                          return (
                            <GameFilterRow
                              key={game.key}
                              primary={game.display}
                              dateTime={localizedTime}
                              propLabel={mlbPropCountLabel(projectionCount)}
                              selected={checked}
                              disabled={projectionCount === 0}
                              darkMode={darkMode}
                              onClick={() => setSelectedGames((prev) => {
                                const current = Array.isArray(prev) ? prev : [];
                                return checked ? current.filter((key) => key !== game.key) : [...current, game.key];
                              })}
                            />
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
              </div> : ["NFL", "CFB", "MLB", "WNBA"].includes(String(activeSport || "").toUpperCase()) ? awayAtHomeGameOptions.map((game) => {
                const checked = selectedGames.includes(game.key);
                const localizedTime = game.startTimeStatus === "AVAILABLE" && game.representativeRow
                  ? formatLocalEventTime(game.representativeRow, activeSport)
                  : "";
                const timeQuality = isAdminUser && game.startTimeStatus !== "AVAILABLE"
                  ? (game.startTimeStatus === "CONFLICT" ? "TIME CONFLICT" : "START TIME UNAVAILABLE")
                  : "";
                const projectionCount = customerGameCounts.get(game.key) || 0;
                return (
                  <GameFilterRow
                    key={game.key}
                    primary={timeQuality ? `${game.display} · ${timeQuality}` : game.display}
                    dateTime={localizedTime}
                    propLabel={mlbPropCountLabel(projectionCount)}
                    selected={checked}
                    disabled={projectionCount === 0}
                    darkMode={darkMode}
                    onClick={() => setSelectedGames((prev) => {
                      const current = Array.isArray(prev) ? prev : [];
                      return checked ? current.filter((key) => key !== game.key) : [...current, game.key];
                    })}
                  />
                );
              }) : genericGameOptions.map((game) => {
                const checked = Array.isArray(selectedGames) && selectedGames.includes(game.key);
                return (
                  <GameFilterRow
                    key={game.key}
                    primary={game.display}
                    dateTime={formatLocalEventTime(game.representativeRow, activeSport)}
                    propLabel={mlbPropCountLabel(game.projectionCount)}
                    selected={checked}
                    disabled={game.projectionCount === 0}
                    darkMode={darkMode}
                    onClick={() => {
                      setSelectedGames((prev) => {
                        const p = Array.isArray(prev) ? prev : [];
                        return checked ? p.filter((x) => x !== game.key) : [...p, game.key];
                      });
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
    );
  };

  const renderMobilePropsQuickPanel = () => {
    const pts = Array.isArray(uniquePropTypes) ? uniquePropTypes : [];
    return (
      <div style={{ padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ fontWeight: 900, fontSize: 14 }}>Prop Types</div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button type="button" className="pill" onClick={resetPropTypes}>
              Reset
            </button>
            <button type="button" className="pill" onClick={() => setTaxonomyOpen(false)}>
              Done
            </button>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="pill"
            onClick={resetPropTypes}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: !selectedPropType
                ? "1px solid rgba(17,24,39,0.85)"
                : "1px solid rgba(148,163,184,0.65)",
              background: !selectedPropType ? "#111827" : "#ffffff",
              color: !selectedPropType ? "#ffffff" : "#111827",
              fontWeight: !selectedPropType ? 900 : 800,
              fontSize: 13,
              lineHeight: 1,
              boxShadow: !selectedPropType ? "0 10px 22px rgba(0,0,0,0.14)" : "none",
            }}
          >
            All
          </button>

          {pts.map((pt) => {
            const active = selectedPropType === pt;
            return (
              <button
                key={pt}
                type="button"
                className="pill"
                onClick={() => setSelectedPropType(pt)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  border: active
                    ? "1px solid rgba(17,24,39,0.85)"
                    : "1px solid rgba(148,163,184,0.65)",
                  background: active ? "#111827" : "#ffffff",
                  color: active ? "#ffffff" : "#111827",
                  fontWeight: active ? 900 : 800,
                  fontSize: 13,
                  lineHeight: 1,
                  boxShadow: active ? "0 10px 22px rgba(0,0,0,0.14)" : "none",
                }}
              >
                {customerPropTypeLabel(pt)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  function toggleMultiValue(current, value, setter) {
    if (!value) return;
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value));
    } else {
      setter([...current, value]);
    }
  }

const renderFiltersPanel = () => {
  const highMinutesThreshold = "30";
  const highMinutesActive = minMpgFilter === highMinutesThreshold;

  return (
    <div
      style={{
        background: darkMode ? "#0b1220" : "#ffffff",
        border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.12)",
        borderRadius: 14,
        boxShadow: darkMode ? "none" : "0 12px 24px rgba(15,23,42,0.10)",
        padding: 12,
      }}
    >
      {/* Panel title is already rendered in the command-rail header. */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
        <button
          type="button"
          onClick={clearAllFilters}
          style={{
            padding: "6px 10px",
            borderRadius: 999,
            border: "1px solid rgba(148,163,184,0.45)",
            background: darkMode ? "#101826" : "#ffffff",
            color: darkMode ? "#e5e7eb" : "#111827",
            fontSize: "0.8rem",
            fontWeight: 800,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          ✖ Clear
        </button>
      </div>

      <div style={sectionDivider(darkMode)} />

      

      {/* Presets */}
      <div style={sectionLabel(darkMode)}>Guru presets</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
        {String(sportAvailability?.WNBA?.state || "").toUpperCase() !== "OFFSEASON" && <>
        <PresetPill
          label="🏆 Certified Under (WNBA)"
          direction="under"
          active={activePreset === "guru_elite_under"}
          onClick={() => applyPreset("guru_elite_under")}
        />
        <PresetPill
          label="🚀 Certified Over (WNBA)"
          direction="over"
          active={activePreset === "guru_elite_over"}
          onClick={() => applyPreset("guru_elite_over")}
        />
        </>}
        <PresetPill
          label="🔥 Fade Elite Defense"
          direction="under"
          active={activePreset === "fade_elite"}
          onClick={() => applyPreset("fade_elite")}
        />
        <PresetPill
          label="💚 Smash Soft Matchups"
          direction="over"
          active={activePreset === "smash_soft"}
          onClick={() => applyPreset("smash_soft")}
        />
      </div>

      <div style={{ ...sectionLabel(darkMode), marginTop: 12 }}>Research presets</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
        <PresetPill
          label="🟠 Target Lean Under"
          direction="under"
          active={activePreset === "target_lean_under"}
          onClick={() => applyPreset("target_lean_under")}
        />
        <PresetPill
          label="💚 Elite Lean Over"
          direction="over"
          active={activePreset === "elite_lean_over"}
          onClick={() => applyPreset("elite_lean_over")}
        />
      </div>

      <div style={sectionDivider(darkMode)} />

      <div style={sectionDivider(darkMode)} />

      {/* All filters */}
      <div style={sectionLabel(darkMode)}>{canSeeAdvancedCard ? "Controls" : "Find Plays"}</div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "0.9rem",
            marginBottom: "0.75rem",
            alignItems: "start",
            width: "100%",
            overflowX: "visible",
            paddingBottom: 6,
          }}
        >

            {(isAdminIdentity || isPremiumRole) && (
              <>

              <StableFilterSection isNarrow={isNarrow} title="CORE" defaultOpen>
                {/* Prop Type, Games, Team/HomeAway/Time */}

                {/* Prop Types (multi-select) */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0.25rem 0" }}>
                  <button
                    type="button"
                    onClick={resetPropTypes}
                    style={{
                      borderRadius: 999,
                      padding: "0.35rem 0.7rem",
                      fontWeight: 900,
                      border: "1px solid rgba(15,23,42,0.14)",
                      background: (selectedPropTypes?.length || 0) === 0 ? (darkMode ? "#111827" : "#111827") : "transparent",
                      color: (selectedPropTypes?.length || 0) === 0 ? "#fff" : (darkMode ? "#e5e7eb" : "#111827"),
                      cursor: "pointer",
                    }}
                  >
                    All Prop Types
                  </button>

                  {uniquePropTypes.map((pt) => {
                    const active = Array.isArray(selectedPropTypes) && selectedPropTypes.includes(pt);
                    return (
                      <button
                        key={pt}
                        type="button"
                        onClick={() => togglePropType(pt)}
                        style={{
                          borderRadius: 999,
                          padding: "0.35rem 0.7rem",
                          fontWeight: 900,
                          border: active ? "1px solid rgba(59,130,246,0.55)" : "1px solid rgba(15,23,42,0.14)",
                          background: active ? (darkMode ? "rgba(59,130,246,0.25)" : "rgba(59,130,246,0.12)") : "transparent",
                          color: darkMode ? "#e5e7eb" : "#111827",
                          cursor: "pointer",
                        }}
                        title={customerPropTypeLabel(pt)}
                      >
                        {customerPropTypeLabel(pt)}
                      </button>
                    );
                  })}
                </div>

                <select value={homeAwayFilter} onChange={(e) => setHomeAwayFilter(e.target.value)} style={{ borderRadius: "12px", padding: "0.5rem" }}>
                  <option value="">All Home/Away</option>
                  <option value="home">Home</option>
                  <option value="away">Away</option>
                </select>

                <div style={{ width: "100%" }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 7, opacity: 0.72 }}>
                    Teams
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setSelectedTeams([])}
                      style={{
                        borderRadius: 999,
                        padding: "0.42rem 0.75rem",
                        fontWeight: 900,
                        border: "1px solid rgba(148,163,184,0.45)",
                        background: selectedTeams.length === 0 ? "#111827" : "transparent",
                        color: selectedTeams.length === 0 ? "#fff" : (darkMode ? "#e5e7eb" : "#111827"),
                        cursor: "pointer",
                      }}
                    >
                      All Teams
                    </button>
                    {[...new Set(propsData.map(p => p.Team).filter(t => t && t !== "nan"))].sort().map((team) => {
                      const active = selectedTeams.includes(team);
                      return (
                        <button
                          key={team}
                          type="button"
                          onClick={() => setSelectedTeams((current) =>
                            current.includes(team)
                              ? current.filter((value) => value !== team)
                              : [...current, team]
                          )}
                          style={{
                            borderRadius: 999,
                            padding: "0.42rem 0.75rem",
                            fontWeight: 900,
                            border: active ? "1px solid rgba(59,130,246,0.65)" : "1px solid rgba(148,163,184,0.45)",
                            background: active ? (darkMode ? "rgba(59,130,246,0.28)" : "rgba(59,130,246,0.13)") : "transparent",
                            color: darkMode ? "#e5e7eb" : "#111827",
                            cursor: "pointer",
                          }}
                        >
                          {team}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <select
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)} style={{ borderRadius: "12px", padding: "0.5rem" }}>
                  <option value="All">Start Times</option>
                  <option value="Early">Early</option>
                  <option value="Afternoon">Afternoon</option>
                  <option value="Evening">Evening</option>
                  <option value="Late">Late</option>
                </select>

              </StableFilterSection>
              
            {/*  Visible for advanced players */}
            {showAdvancedFilterControls && (
              <>
              <StableFilterSection isNarrow={isNarrow} title="EDGE & CONVICTION" defaultOpen>
                {/* Confidence, Guru Grade, Sel Strat, GuruAlerts */}
                <select
                  value={selectedConfidence}
                  onChange={(e) => setSelectedConfidence(e.target.value)}
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                >
                  <option value="">Confidence Tier</option>
                  <option value="Volatile Elite">Volatile Elite</option>
                  <option value="Elite">Elite</option>
                  <option value="Strong">Strong</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Low">Low</option>
                  <option value="Over Moderate">Over Moderate</option>
                  <option value="Over Low">Over Low</option>
                </select>

                <select
                  value={selectedGuruGrade}
                  onChange={(e) => setSelectedGuruGrade(e.target.value)}
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                  title="Guru Grade (Conviction)"
                >
                  <option value="">Guru Grades</option>
                  <option value="A+">A+</option>
                  <option value="A">A</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B">B</option>
                  <option value="B-">B-</option>
                  <option value="C+">C+</option>
                  <option value="C">C</option>
                  <option value="C-">C-</option>
                  <option value="D">D</option>
                </select>

                {/* Selection Strategy Filter */}
                <select
                  value={selStratFilter}
                  onChange={(e) => setSelStratFilter(e.target.value)}
                  className="pill-select"
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                >
                  <option value="ALL">Selection Strategy</option>
                  <option value="OVER">OVER only</option>
                  <option value="UNDER">UNDER only</option>
                  <option value="MIXED">MIXED</option>
                  <option value="NO_EDGE">No clear edge</option>
                </select>

                <select
                  value={guruAlertFilter}
                  onChange={(e) => setGuruAlert(e.target.value)}
                  className="pill-select"
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                >

                  <option value="ALL">Guru Alerts</option>
                  <option value="2">Alert only</option>
                  <option value="1">Watch only</option>
                  <option value="0">None</option>
                </select>

              </StableFilterSection>

              <StableFilterSection isNarrow={isNarrow} title="FORM & TREND" defaultOpen={false}>
                {/* L5 Shape, Momentum filters */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0.25rem 0" }}>
                  <button
                    type="button"
                    onClick={resetL5Shapes}
                    style={{
                      borderRadius: 999,
                      padding: "0.35rem 0.7rem",
                      fontWeight: 900,
                      border: "1px solid rgba(15,23,42,0.14)",
                      background:
                        (selectedL5Shapes?.length || 0) === 0
                          ? (darkMode ? "#111827" : "#111827")
                          : "transparent",
                      color:
                        (selectedL5Shapes?.length || 0) === 0
                          ? "#fff"
                          : (darkMode ? "#e5e7eb" : "#111827"),
                      cursor: "pointer",
                    }}
                    title="Recent form pattern over last 5 games"
                  >
                    L5 Shape: Any
                  </button>

                  {allL5Shapes.filter((s) => s !== "ALL").map((s) => {
                    const active = Array.isArray(selectedL5Shapes) && selectedL5Shapes.includes(getL5CanonicalKey(s));
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleL5Shape(s)}
                        style={{
                          borderRadius: 999,
                          padding: "0.35rem 0.7rem",
                          fontWeight: 900,
                          border: active
                            ? "1px solid rgba(59,130,246,0.55)"
                            : "1px solid rgba(15,23,42,0.14)",
                          background: active
                            ? (darkMode ? "rgba(59,130,246,0.25)" : "rgba(59,130,246,0.12)")
                            : "transparent",
                          color: darkMode ? "#e5e7eb" : "#111827",
                          cursor: "pointer",
                        }}
                        title="Recent form pattern over last 5 games"
                      >
                        {getL5Label(s)}
                      </button>
                    );
                  })}
                </div>

                {/* Minutes Trend Filter */}
                {!["MLB", "KBO"].includes(String(activeSport || "").toUpperCase()) && allowsMinutesUi(activeSport) && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0.25rem 0", marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={resetMinutesTrends}
                    style={{
                      borderRadius: 999,
                      padding: "0.35rem 0.7rem",
                      fontWeight: 900,
                      border: "1px solid rgba(15,23,42,0.14)",
                      background:
                        (selectedMinutesTrends?.length || 0) === 0
                          ? (darkMode ? "#111827" : "#111827")
                          : "transparent",
                      color:
                        (selectedMinutesTrends?.length || 0) === 0
                          ? "#fff"
                          : (darkMode ? "#e5e7eb" : "#111827"),
                      cursor: "pointer",
                    }}
                    title="Filter by categorical minutes trend from the analysis output"
                  >
                    Minutes: Any
                  </button>

                  {allMinutesTrends.filter((s) => s !== "ALL").map((s) => {
                    const active = Array.isArray(selectedMinutesTrends) && selectedMinutesTrends.includes(getMinutesTrendCanonicalKey(s));
                    return (
                      <button
                        key={`minutes-trend-${s}`}
                        type="button"
                        onClick={() => toggleMinutesTrend(s)}
                        style={{
                          borderRadius: 999,
                          padding: "0.35rem 0.7rem",
                          fontWeight: 900,
                          border: active
                            ? "1px solid rgba(34,197,94,0.65)"
                            : "1px solid rgba(15,23,42,0.14)",
                          background: active
                            ? (darkMode ? "rgba(34,197,94,0.25)" : "rgba(34,197,94,0.12)")
                            : "transparent",
                          color: darkMode ? "#e5e7eb" : "#111827",
                          cursor: "pointer",
                        }}
                        title="Categorical minutes trend"
                      >
                        {MINUTES_TREND_LABELS[getMinutesTrendCanonicalKey(s)] || s}
                      </button>
                    );
                  })}
                </div>
                )}

                {/* Bounce Classification v1 */}
                {(allBounceLabels.length > 0 || allBounceFamilies.length > 0) && (
                  <div style={{ marginTop: 10 }}>
                    <div style={sectionLabel(darkMode)}>Bounce State</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0.25rem 0" }}>
                      <button
                        type="button"
                        onClick={resetBounceFilters}
                        style={{
                          borderRadius: 999,
                          padding: "0.35rem 0.7rem",
                          fontWeight: 900,
                          border: "1px solid rgba(15,23,42,0.14)",
                          background:
                            selectedBounceLabels.length === 0 && selectedBounceFamilies.length === 0
                              ? "#111827"
                              : "transparent",
                          color:
                            selectedBounceLabels.length === 0 && selectedBounceFamilies.length === 0
                              ? "#fff"
                              : (darkMode ? "#e5e7eb" : "#111827"),
                          cursor: "pointer",
                        }}
                      >
                        Bounce: Any
                      </button>

                      {allBounceFamilies.map((family) => {
                        const active = selectedBounceFamilies.includes(family);
                        return (
                          <button
                            key={`bounce-family-${family}`}
                            type="button"
                            onClick={() => toggleBounceFamily(family)}
                            style={{
                              borderRadius: 999,
                              padding: "0.35rem 0.7rem",
                              fontWeight: 900,
                              border: active
                                ? "1px solid rgba(168,85,247,0.65)"
                                : "1px solid rgba(15,23,42,0.14)",
                              background: active
                                ? (darkMode ? "rgba(168,85,247,0.25)" : "rgba(168,85,247,0.12)")
                                : "transparent",
                              color: darkMode ? "#e5e7eb" : "#111827",
                              cursor: "pointer",
                            }}
                          >
                            {BOUNCE_FAMILY_LABELS[family] || family}
                          </button>
                        );
                      })}

                      {allBounceLabels.map((label) => {
                        const active = selectedBounceLabels.includes(label);
                        return (
                          <button
                            key={`bounce-label-${label}`}
                            type="button"
                            onClick={() => toggleBounceLabel(label)}
                            style={{
                              borderRadius: 999,
                              padding: "0.35rem 0.7rem",
                              fontWeight: 900,
                              border: active
                                ? "1px solid rgba(34,197,94,0.65)"
                                : "1px solid rgba(15,23,42,0.14)",
                              background: active
                                ? (darkMode ? "rgba(34,197,94,0.25)" : "rgba(34,197,94,0.12)")
                                : "transparent",
                              color: darkMode ? "#e5e7eb" : "#111827",
                              cursor: "pointer",
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Pattern Filter */}


                {/* Raw Formation Family / Code Filter */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0.25rem 0", marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={resetFormations}
                    style={{
                      borderRadius: 999,
                      padding: "0.35rem 0.7rem",
                      fontWeight: 900,
                      border: "1px solid rgba(15,23,42,0.14)",
                      background:
                        ((selectedFormationFamilies?.length || 0) === 0 && (selectedFormationCodes?.length || 0) === 0)
                          ? (darkMode ? "#111827" : "#111827")
                          : "transparent",
                      color:
                        ((selectedFormationFamilies?.length || 0) === 0 && (selectedFormationCodes?.length || 0) === 0)
                          ? "#fff"
                          : (darkMode ? "#e5e7eb" : "#111827"),
                      cursor: "pointer",
                    }}
                    title="Filter by raw L5 formation code or formation family"
                  >
                    Formation: Any
                  </button>

                  {FORMATION_FAMILY_ORDER.map((family) => {
                    const active = selectedFormationFamilies.includes(family);
                    return (
                      <button
                        key={`formation-family-${family}`}
                        type="button"
                        onClick={() => toggleFormationFamily(family)}
                        style={formationFamilyStyle(family, darkMode, active)}
                        title={`Show ${FORMATION_FAMILY_LABELS[family]} formations`}
                      >
                        {FORMATION_FAMILY_LABELS[family]}
                      </button>
                    );
                  })}
                </div>

                {allFormationCodes.length > 1 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0.25rem 0", marginTop: 4 }}>
                    {allFormationCodes.filter((s) => s !== "ALL").map((code) => {
                      const fam = getFormationFamily(code);
                      const active = selectedFormationCodes.includes(code);
                      return (
                        <button
                          key={`formation-code-${code}`}
                          type="button"
                          onClick={() => toggleFormationCode(code)}
                          style={formationFamilyStyle(fam, darkMode, active)}
                          title={`Formation ${code} • ${FORMATION_FAMILY_LABELS[fam] || fam}`}
                        >
                          {code}
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* Guru Pick Filter */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0.25rem 0", marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => setSelectedGuruPicks("")}
                    style={{
                      borderRadius: 999,
                      padding: "0.35rem 0.7rem",
                      fontWeight: 900,
                      border: "1px solid rgba(15,23,42,0.14)",
                      background: !selectedGuruPicks ? (darkMode ? "#111827" : "#111827") : "transparent",
                      color: !selectedGuruPicks ? "#fff" : (darkMode ? "#e5e7eb" : "#111827"),
                      cursor: "pointer",
                    }}
                    title="Final Guru execution layer: Tag + Context + Pattern"
                  >
                    Guru Pick: Any
                  </button>

                  {GURU_PICK_OPTIONS.map((pick) => {
                    const active = selectedGuruPicks === pick;
                    return (
                      <button
                        key={pick}
                        type="button"
                        onClick={() => setSelectedGuruPicks(active ? "" : pick)}
                        style={guruPickFilterButtonStyle(pick, active, darkMode)}
                        title="Final Guru execution layer: Tag + Context + Pattern"
                      >
                        {pick}
                      </button>
                    );
                  })}
                </div>
              
                <select
                  value={momentumFilter}
                  onChange={(e) => setMomentumFilter(e.target.value)}
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                >
                  <option value="">All Momentum Level</option>
                  <option value="TRENDING UP">Above Baseline</option>
                  <option value="TRENDING DOWN">Below Baseline</option>
                  <option value="STABLE">Near Baseline</option>
                  <option value="MIXED">Mixed Level</option>
                </select>

                <select
                  value={momentumPatternFilter}
                  onChange={(e) => setMomentumPatternFilter(e.target.value)}
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                >
                  <option value="">All Trend Shape (L5)</option>
                  <option value="TRENDING UP">Trending Up</option>
                  <option value="TRENDING DOWN">Trending Down</option>
                  <option value="FLAT">Flat</option>
                  <option value="MIXED">Mixed</option>
                </select>

                <select
                  value={confirmedMomentumFilter}
                  onChange={(e) => setConfirmedMomentumFilter(e.target.value)}
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                >
                  <option value="">All Momentum Signal</option>
                  <option value="CONFIRMED TRENDING UP">Confirmed Uptrend</option>
                  <option value="CONFIRMED TRENDING DOWN">Confirmed Downtrend</option>
                  <option value="UNCONFIRMED">Not Confirmed</option>
                </select>

              </StableFilterSection>

                <StableFilterSection isNarrow={isNarrow} title="MATCHUP & PRICING" defaultOpen>
                  {/* DvP Tier, Tax Tier */}
                    <select
                      value={dvpTierFilter}
                      onChange={(e) => setDvpTierFilter(e.target.value)}
                      className="pill-select"
                      style={{ borderRadius: "12px", padding: "0.5rem" }}
                    >
                      <option value="ALL">Matchup (DvP)</option>
                      <option value="TARGET">Target matchups</option>
                      <option value="SOFT">Soft matchups</option>
                      <option value="NEUTRAL">Neutral</option>
                      <option value="TOUGH">Tough defense</option>
                      <option value="ELITE">Elite defense</option>
                      <option value="R0">R0 (Combos)</option>
                    </select>

                    <select
                      value={taxFilter}
                      onChange={(e) => setTaxFilter(e.target.value)}
                      style={{ borderRadius: "12px", padding: "0.5rem" }}
                    >
                      <option value="ALL">Line Tax</option>
                      <option value="HYPER">Hyper-Taxed only</option>
                      <option value="NO_HYPER">No Hyper-Taxed</option>
                      <option value="TAXED">Taxed only</option>
                      <option value="FAIR">Fair only</option>
                      <option value="DISCOUNT">Discount only</option>
                      <option value="FAIR-DISCOUNT">Fair + Discount</option>
                      {/* ⭐ NEW OPTION */}
                    <option value="TAXED_HYPER">Overpriced (Taxed + Hyper-Taxed)</option>
                    </select>
                </StableFilterSection>

                <StableFilterSection isNarrow={isNarrow} title={String(activeSport || "").toUpperCase() === "CFB" ? "RISK" : "RISK & MINUTES"} defaultOpen={false}>
                  {/* Protect Overs, Attack Unders, Minutes Trend, Blowout, High Minutes */}

                  {allowsMinutesUi(activeSport) && (<>

                  <button
                    type="button"
                    onClick={() => setOversMinutesGate((v) => !v)}
                    style={{
                      borderRadius: 999,
                      padding: "0.55rem 0.9rem",
                      border: "1px solid #d0d5dd",
                      background: oversMinutesGate ? "#111827" : "#ffffff",
                      color: oversMinutesGate ? "#ffffff" : "#111827",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Overs: Rising Minutes Only
                  </button>

                  <button
                    type="button"
                    onClick={() => setAttackUndersMinutesGate((v) => !v)}
                    title="Only show UNDER tags where minutes trend is down"
                    style={{
                      borderRadius: 999,
                      padding: "0.55rem 0.9rem",
                      border: "1px solid #d0d5dd",
                      background: attackUndersMinutesGate ? "#111827" : "#ffffff",
                      color: attackUndersMinutesGate ? "#ffffff" : "#111827",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Unders: Falling Minutes Only
                  </button>

                    <select
                      value={minutesTrendMin ?? ""}
                      onChange={(e) => setMinutesTrendMin(e.target.value === "" ? null : Number(e.target.value))}
                      style={{ borderRadius: "12px", padding: "0.5rem" }}
                    >
                      <option value="">Minutes Trend: Any</option>
                      <option value={0}>Minutes Trend: Stable+</option>
                      <option value={1}>Minutes Trend: Up</option>
                      <option value={2}>Minutes Trend: Strong Up</option>
                    </select>
                  </>)}

                    <select
                      value={blowoutFilter}
                      onChange={(e) => { const v = e.target.value; setBlowoutLevelFilter(v); setBlowoutFilter(v); }}
                      className="pill-select"
                      style={{ borderRadius: "12px", padding: "0.5rem" }}
                      title="Filter by blowout risk"
                    >
                      <option value="all">All Blowout Levels</option>
                      <option value="no-high">No High Blowout</option>
                      <option value="low-only">Low-blowout only</option>
                    </select>

                    <select
                      value={blowoutFilter}
                      onChange={(e) => setBlowoutFilter(e.target.value)}
                      className="pill-select"
                      style={{ borderRadius: "12px", padding: "0.5rem" }}
                    >
                      <option value="all">All Blowouts</option>
                      <option value="low">Low Only</option>
                      <option value="no-high">Exclude High</option>
                      <option value="low-moderate">Low + Moderate</option>
                      <option value="high">High Only</option>
                    </select>

                    {allowsMinutesUi(activeSport) && (<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={() =>
                          setMinMpgFilter(highMinutesActive ? "0" : highMinutesThreshold)
                        }
                        style={{
                          borderRadius: "999px",
                          padding: "0.4rem 0.9rem",
                          border: "1px solid",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          backgroundColor: highMinutesActive ? "#2563EB" : "white",
                          color: highMinutesActive ? "white" : "#2563EB",
                          borderColor: "#2563EB",
                          whiteSpace: "nowrap",
                        }}
                      >
                        High Minutes (≥{highMinutesThreshold})
                      </button>
          
                      <select
                        value={minMpgFilter}
                        onChange={(e) => setMinMpgFilter(e.target.value)}
                        style={{ borderRadius: "12px", padding: "0.5rem" }}
                      >
                        <option value="0">All Minutes</option>
                        <option value="20">MPG ≥ 20</option>
                        <option value="25">MPG ≥ 25</option>
                        <option value="30">MPG ≥ 30</option>
                        <option value="35">MPG ≥ 35</option>
                      </select>
                    </div>)}

                </StableFilterSection>

                <StableFilterSection isNarrow={isNarrow} title="THRESHOLDS" defaultOpen={false}>
                  {/* L10 vs PROP % */}
                  <select
                    value={minL10Pct}
                    onChange={(e) => setMinL10Pct(e.target.value)}
                    style={{ borderRadius: "12px", padding: "0.5rem" }}
                    title="Filter by Last-10 vs PROP % (uses available games)"
                  >
                    <option value="">Last 10 vs Line Hit Rate</option>
                    <option value="50">≥ 50%</option>
                    <option value="60">≥ 60%</option>
                    <option value="70">≥ 70%</option>
                    <option value="80">≥ 80%</option>
                    <option value="90">≥ 90%</option>
                  </select>

                  {/* L10 vs OPP % */}
                  <select
                    value={minOppL10Pct}
                    onChange={(e) => setMinOppL10Pct(e.target.value)}
                    style={{ borderRadius: "12px", padding: "0.5rem" }}
                    title="Filter by Last-10 vs OPP % (uses available games)"
                  >
                    <option value="">Last 10 vs Opp Hit Rate</option>
                    <option value="50">≥ 50%</option>
                    <option value="60">≥ 60%</option>
                    <option value="70">≥ 70%</option>
                    <option value="80">≥ 80%</option>
                    <option value="90">≥ 90%</option>
                  </select>

                </StableFilterSection>

                <StableFilterSection isNarrow={isNarrow} title="EXPERIMENTAL" defaultOpen={false}>
                  {/* ⭐ Zodiac filter */}
                  <select
                    value={zodiacFilter}
                    onChange={(e) => setZodiacFilter(e.target.value)}
                    style={{
                      borderRadius: "999px",
                      padding: "0.45rem 0.9rem",
                      fontSize: 13,
                      border: "1px solid #e5e7eb",
                    }}
                    title="Filter by Chinese zodiac sign"
                  >
                    <option value="ALL">All Zodiac Signs</option>
                    {ZODIAC_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {ZODIAC_META[key].label}
                      </option>
                    ))}

                  </select>
                    <button
                      onClick={() => setStocksHeatOnly(v => !v)}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999,
                        border: "1px solid #6b7280",
                        background: stocksHeatOnly ? "#16a34a" : "transparent",
                        color: stocksHeatOnly ? "white" : "#6b7280",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      🔍 Stocks + Minutes Heat
                  </button>

                    {/* Filters hidden for now (Z-GURU/Lean/Conflicts/GuruPool/Magic/Potential) */}
                    <select
                      value={guruListFilter}
                      onChange={(e) => setGuruListFilter(e.target.value)}
                      style={{ borderRadius: "12px", padding: "0.5rem" }}
                    >
                      <option value="all">All Picks</option>
                      <option value="my-list">My List</option>
                    </select>
                </StableFilterSection>
              </>
            )}

              





              {activeSport === "MLB" && (
                <select value={playerTypeFilter} onChange={(e) => setPlayerTypeFilter(e.target.value)} style={{ borderRadius: "12px", padding: "0.5rem" }}>
                  <option value="">All Player Types</option>
                  <option value="Batter">Batter</option>
                  <option value="Pitcher">Pitcher</option>
                </select>
              )}


              {/* Opp Pitcher Hand */}
              {activeSport === "MLB" && (
                <select
                  value={oppPitcherHandFilter}
                  onChange={(e) => setOppPitcherHandFilter(e.target.value)}
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                  title="Opposing Pitcher Throws"
                >
                  <option value="">Opp Pitcher Hand</option>
                  <option value="RHP">RHP</option>
                  <option value="LHP">LHP</option>
                </select>
              )}
              {/* Batting Order */}
              {activeSport === "MLB" && (
                <select
                  value={battingOrderFilter}
                  onChange={(e) => setBattingOrderFilter(e.target.value)}
                  style={{ borderRadius: "12px", padding: "0.5rem" }}
                  title="Hitter's batting order slot"
                >
                  <option value="">Batting Order</option>
                  <option value="TOP12">Top (1–2)</option>
                  <option value="CORE35">Core (3–5)</option>
                  <option value="BOTTOM69">Bottom (6–9)</option>
                  <option value="1">#1</option><option value="2">#2</option><option value="3">#3</option>
                  <option value="4">#4</option><option value="5">#5</option><option value="6">#6</option>
                  <option value="7">#7</option><option value="8">#8</option><option value="9">#9</option>
                </select>
              )}
            {activeSport === "MLB" && (
                <select value={teamPitcherTierFilter} onChange={(e) => setTeamPitcherTierFilter(e.target.value)} style={{ borderRadius: "12px", padding: "0.5rem" }}>
                  <option value="">Team Pitcher Tier</option>
                  <option value="Weak (Target)">Weak (Target)</option>
                  <option value="Poor">Poor</option>
                  <option value="Neutral">Neutral</option>
                  <option value="Tough">Tough</option>
                  <option value="Elite">Elite</option>
                </select>
              )}
              {activeSport === "MLB" && (
                <select value={teamAttackabilityFilter} onChange={(e) => setTeamAttackabilityFilter(e.target.value)} style={{ borderRadius: "12px", padding: "0.5rem" }}>
                  <option value="">Team Attackability</option>
                  <option value="Very High (Target)">Very High (Target)</option>
                  <option value="High (Target)">High (Target)</option>
                  <option value="Medium Risk">Medium Risk</option>
                  <option value="Low (Avoid)">Low (Avoid)</option>
                </select>
              )}
              {activeSport === "MLB" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 700 }}>Opponent WHIP Tier</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {["Elite", "Great", "Good", "Average", "Below Average", "Poor"].map((value) => {
                      const active = oppWhipTierFilter.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => toggleMultiValue(oppWhipTierFilter, value, setOppWhipTierFilter)}
                          style={{
                            borderRadius: 999,
                            padding: "0.45rem 0.8rem",
                            border: active ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                            background: active ? "#dbeafe" : "#fff",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {activeSport === "MLB" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 700 }}>Opponent K% Tier</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {["Strikeout Ace", "Above Average", "Average", "Below Average", "Contact"].map((value) => {
                    const active = oppKpctTierFilter.includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => toggleMultiValue(oppKpctTierFilter, value, setOppKpctTierFilter)}
                        style={{
                          borderRadius: 999,
                          padding: "0.45rem 0.8rem",
                          border: active ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                          background: active ? "#dbeafe" : "#fff",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {value}
                      </button>
                    );
                  })}
                  </div>
                </div>
              )}

              {activeSport === "MLB" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 700 }}>Opponent Attackability</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {["Very High (Target)", "High (Target)", "Medium Risk", "Low (Avoid)"].map((value) => {
                      const active = oppAttackabilityFilter.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => toggleMultiValue(oppAttackabilityFilter, value, setOppAttackabilityFilter)}
                          style={{
                            borderRadius: 999,
                            padding: "0.45rem 0.8rem",
                            border: active ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                            background: active ? "#dbeafe" : "#fff",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {activeSport === "MLB" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 700 }}>Opponent Pitcher Tier</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {["Weak (Target)", "Poor", "Neutral", "Tough", "Elite"].map((value) => {
                      const active = oppPitcherTierFilter.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => toggleMultiValue(oppPitcherTierFilter, value, setOppPitcherTierFilter)}
                          style={{
                            borderRadius: 999,
                            padding: "0.45rem 0.8rem",
                            border: active ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                            background: active ? "#dbeafe" : "#fff",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {activeSport === "MLB" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 700 }}>Opponent Hitter Tier</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {["Very Weak", "Weak", "Neutral", "Strong", "Very Strong"].map((value) => {
                      const active = oppHitterTierFilter.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => toggleMultiValue(oppHitterTierFilter, value, setOppHitterTierFilter)}
                          style={{
                            borderRadius: 999,
                            padding: "0.45rem 0.8rem",
                            border: active ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                            background: active ? "#dbeafe" : "#fff",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {activeSport === "MLB" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 700 }}>Team Hitter Tier</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {["Very Weak", "Weak", "Neutral", "Strong", "Very Strong"].map((value) => {
                      const active = hitterTierFilter.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => toggleMultiValue(hitterTierFilter, value, setHitterTierFilter)}
                          style={{
                            borderRadius: 999,
                            padding: "0.45rem 0.8rem",
                            border: active ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                            background: active ? "#dbeafe" : "#fff",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
    
              {activeSport === "NFL" && (
                <select value={playerTypeFilter} onChange={(e) => setPlayerTypeFilter(e.target.value)} style={{ borderRadius: "12px", padding: "0.5rem" }}>
                  <option value="">All Player Types</option>
                  <option value="QB">QB</option>
                  <option value="RB">RB</option>
                  <option value="WR">WR</option>
                  <option value="TE">TE</option>
                  <option value="Kicker">Kicker</option>
                  <option value="Defense">Defense</option>
                </select>
              )}

              </>
        )}

          </div>
    
      </div>
    </div>
  );
};

    // END of render filters for PWG

    // Route to TEG when selected
    /*if (activeSport === "TEG") {
      return <TEGDashboard />;
    }*/


  // --- Taxonomy Tree UI (left panel / drawer) ------------------------------------
  const TaxonomyTree = React.useMemo(() => {
    const TierChip = ({ label, count, active, onClick }) => (
      <button
        type="button"
        onClick={onClick}
        style={{
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          borderRadius: 12,
          padding: "8px 10px",
          border: active ? "1px solid #93c5fd" : "1px solid #e5e7eb",
          background: active ? "#eff6ff" : "#ffffff",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 900, color: "#6b7280" }}>{count}</span>
      </button>
    );

    const SectionHeader = ({ title, sub }) => (
      <div style={{ padding: "10px 10px 6px", borderBottom: "1px solid #f3f4f6" }}>
        <div style={{ fontWeight: 950 }}>{title}</div>
        {sub ? <div style={{ color: "#6b7280", fontSize: 12, fontWeight: 700, marginTop: 2 }}>{sub}</div> : null}
      </div>
    );

    const PlayerRow = ({ name, active, onClick }) => (
      <button
        type="button"
        onClick={onClick}
        style={{
          width: "100%",
          textAlign: "left",
          border: "1px solid #f3f4f6",
          background: active ? "#f0fdf4" : "#fafafa",
          borderRadius: 12,
          padding: "7px 10px",
          fontWeight: 800,
          cursor: "pointer",
        }}
        title="Click to drill into this player"
      >
        {name}
      </button>
    );

    const activeTier = taxonomyTier;
    const activePlayer = taxonomyPlayer;

    const listForTier = (tier) => taxonomyPlayersByTier?.[tier] || [];

    const renderPlayers = (tier, scope) => {
      const names = listForTier(tier).filter((n) => (scope === "GURULIST" ? guruListPlayerSet?.has(n) : true));
      if (!names.length) {
        return <div style={{ padding: "6px 10px", color: "#9ca3af", fontWeight: 700, fontSize: 12 }}>No players</div>;
      }
      return (
        <div style={{ display: "grid", gap: 8, padding: "10px" }}>
          {names.slice(0, 40).map((nm) => (
            <PlayerRow
              key={nm}
              name={nm}
              active={activePlayer === nm}
              onClick={() => {
                setTaxonomyScope(scope);
                setTaxonomyTier(tier);
                setTaxonomyPlayer(nm);
                setGridPage(1);
                setCardPage(1);
                if (isNarrow) setTaxonomyOpen(false);
              }}
            />
          ))}
          {names.length > 40 ? (
            <div style={{ color: "#6b7280", fontWeight: 700, fontSize: 12 }}>
              Showing first 40 (Phase 1). We can add search + pagination here next.
            </div>
          ) : null}
        </div>
      );
    };

    const ClearRow = () => (
      <button
        type="button"
        onClick={() => {
          setTaxonomyScope("ALL");
          setTaxonomyTier("ALL");
          setTaxonomyPlayer("");
          setGridPage(1);
          setCardPage(1);
        }}
        style={{
          width: "100%",
          borderRadius: 12,
          padding: "8px 10px",
          border: "1px solid #e5e7eb",
          background: "#ffffff",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        ✖ Clear taxonomy filter
      </button>
    );

    return (
      <div style={{ width: 320, maxWidth: "80vw", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden", background: "#ffffff" }}>
        <SectionHeader
          title="Taxonomy"
          sub="Studs • Cores • Fillers (computed). Drill down to filter props."
        />
        <div style={{ padding: 10, display: "grid", gap: 8 }}>
          <ClearRow />

          <div style={{ fontWeight: 950, marginTop: 6 }}>All Players</div>
          <TierChip
            label="Studs"
            count={taxonomyCounts?.STUDS ?? 0}
            active={taxonomyScope === "ALL" && activeTier === "STUDS" && !activePlayer}
            onClick={() => {
              setTaxonomyScope("ALL");
              setTaxonomyTier("STUDS");
              setTaxonomyPlayer("");
              setGridPage(1);
              setCardPage(1);
              if (isNarrow) setTaxonomyOpen(false);
            }}
          />
          <TierChip
            label="Cores"
            count={taxonomyCounts?.CORES ?? 0}
            active={taxonomyScope === "ALL" && activeTier === "CORES" && !activePlayer}
            onClick={() => {
              setTaxonomyScope("ALL");
              setTaxonomyTier("CORES");
              setTaxonomyPlayer("");
              setGridPage(1);
              setCardPage(1);
              if (isNarrow) setTaxonomyOpen(false);
            }}
          />
          <TierChip
            label="Fillers"
            count={taxonomyCounts?.FILLERS ?? 0}
            active={taxonomyScope === "ALL" && activeTier === "FILLERS" && !activePlayer}
            onClick={() => {
              setTaxonomyScope("ALL");
              setTaxonomyTier("FILLERS");
              setTaxonomyPlayer("");
              setGridPage(1);
              setCardPage(1);
              if (isNarrow) setTaxonomyOpen(false);
            }}
          />

          {activeTier !== "ALL" && taxonomyScope === "ALL" && !activePlayer ? (
            <>
              <div style={{ fontWeight: 950, marginTop: 10 }}>Players</div>
              {renderPlayers(activeTier, "ALL")}
            </>
          ) : null}

          <div style={{ fontWeight: 950, marginTop: 10 }}>My Plays</div>
          <TierChip
            label="Studs"
            count={Array.from(guruListPlayerSet || []).filter((p) => playerTierMap?.[p] === "STUDS").length}
            active={taxonomyScope === "GURULIST" && activeTier === "STUDS" && !activePlayer}
            onClick={() => {
              setTaxonomyScope("GURULIST");
              setTaxonomyTier("STUDS");
              setTaxonomyPlayer("");
              setGuruListFilter("my-list");
              setGridPage(1);
              setCardPage(1);
              if (isNarrow) setTaxonomyOpen(false);
            }}
          />
          <TierChip
            label="Cores"
            count={Array.from(guruListPlayerSet || []).filter((p) => playerTierMap?.[p] === "CORES").length}
            active={taxonomyScope === "GURULIST" && activeTier === "CORES" && !activePlayer}
            onClick={() => {
              setTaxonomyScope("GURULIST");
              setTaxonomyTier("CORES");
              setTaxonomyPlayer("");
              setGuruListFilter("my-list");
              setGridPage(1);
              setCardPage(1);
              if (isNarrow) setTaxonomyOpen(false);
            }}
          />
          <TierChip
            label="Fillers"
            count={Array.from(guruListPlayerSet || []).filter((p) => playerTierMap?.[p] === "FILLERS").length}
            active={taxonomyScope === "GURULIST" && activeTier === "FILLERS" && !activePlayer}
            onClick={() => {
              setTaxonomyScope("GURULIST");
              setTaxonomyTier("FILLERS");
              setTaxonomyPlayer("");
              setGuruListFilter("my-list");
              setGridPage(1);
              setCardPage(1);
              if (isNarrow) setTaxonomyOpen(false);
            }}
          />

          {activeTier !== "ALL" && taxonomyScope === "GURULIST" && !activePlayer ? (
            <>
              <div style={{ fontWeight: 950, marginTop: 10 }}>Players</div>
              {renderPlayers(activeTier, "GURULIST")}
            </>
          ) : null}
        </div>
      </div>
    );
  }, [
    taxonomyTier, taxonomyPlayer, taxonomyScope,
    taxonomyCounts, taxonomyPlayersByTier,
    guruListPlayerSet, playerTierMap,
    isNarrow
  ]);



  // --- Drawer + active chips (desktop/tablet) ---
  const activeFilterChips = useMemo(() => {
    const chips = [];

    const addChip = (key, label, onClear) => {
      chips.push({ key, label, onClear });
    };

    // Search
    if (playerQuery && String(playerQuery).trim() !== "") {
      addChip("search", `Search: ${playerQuery}`, () => {
        setPlayerInput("");
        setPlayerQuery("");
      });
    }

    // Tags
    if (Array.isArray(selectedTags) && selectedTags.length > 0) {
      addChip("tags", `Tags: ${selectedTags.join(", ")}`, () => setSelectedTags([]));
    }

    // Games
    if (Array.isArray(selectedGames) && selectedGames.length > 0) {
      addChip("games", `Games: ${selectedGames.length}`, () => setSelectedGames([]));
    }

    // Key dropdown filters
    if (selectedPropType) addChip("prop", `Prop: ${selectedPropType}`, () => setSelectedPropType(""));
    if (selectedConfidence) addChip("conf", `Confidence: ${selectedConfidence}`, () => setSelectedConfidence(""));
    if (taxFilter && taxFilter !== "ALL") addChip("tax", `Line Tax: ${taxFilter}`, () => setTaxFilter("ALL"));
    if (dvpTierFilter && dvpTierFilter !== "ALL") addChip("dvp", `DvP: ${dvpTierFilter}`, () => setDvpTierFilter("ALL"));
    if (selStratFilter && selStratFilter !== "ALL") addChip("sel", `Sel Strat: ${selStratFilter}`, () => setSelStratFilter("ALL"));
    if (blowoutLevelFilter && blowoutLevelFilter !== "all") addChip("blvl", `Blowout Lvl: ${blowoutLevelFilter}`, () => setBlowoutLevelFilter("all"));
    if (blowoutFilter && blowoutFilter !== "all") addChip("blow", `Blowout: ${blowoutFilter}`, () => setBlowoutFilter("all"));

    if (homeAwayFilter) addChip("ha", `H/A: ${homeAwayFilter}`, () => setHomeAwayFilter(""));
    if (selectedTeams.length) addChip("team", `Teams: ${selectedTeams.join(", ")}`, () => setSelectedTeams([]));
    if (selectedTime && selectedTime !== "All") addChip("time", `Time: ${selectedTime}`, () => setSelectedTime("All"));

    if (momentumFilter) addChip("mom", `Momentum: ${momentumFilter}`, () => setMomentumFilter(""));
    if (momentumPatternFilter) addChip("mpat", `Pattern: ${momentumPatternFilter}`, () => setMomentumPatternFilter(""));
    if (confirmedMomentumFilter) addChip("mconf", `Confirmed: ${confirmedMomentumFilter}`, () => setConfirmedMomentumFilter(""));

    if (selectedGuruMagic) addChip("gmagic", `Guru Magic: ${selectedGuruMagic}`, () => setSelectedGuruMagic(""));
    if (selectedGuruPotential) addChip("gpot", `Guru Potential: ${selectedGuruPotential}
      // DQS threshold
      if (supportsDqs(activeSport) && dqsFilter && dqsFilter !== "ALL") {
        f = f.filter((p) => dqsPass(_get([p.dqs, p.DQS]), dqsFilter));
      }

`, () => setSelectedGuruPotential(""));
    if (selectedZGuruTag) addChip("zguru", `Z-GURU: ${selectedZGuruTag}`, () => setSelectedZGuruTag(""));
    if (selectedLeanDirection) addChip("lean", `Lean: ${selectedLeanDirection}`, () => setSelectedLeanDirection(""));
    if (selectedGuruConflict) addChip("gconflict", `Conflicts: ${selectedGuruConflict}`, () => setSelectedGuruConflict(""));
    if (selectedGuruPicks) addChip("gpicks", `Guru Pick: ${selectedGuruPicks}`, () => setSelectedGuruPicks(""));

    const patternSummary = patternFilterSummary(selectedPatternComponents, patternMatchMode, selectedPatterns);
    if (patternSummary) addChip("patterns", patternSummary, resetPatternIntelligence);

    if (guruListFilter && guruListFilter !== "all") addChip("glist", `My Plays: ${guruListFilter}`, () => setGuruListFilter("all"));
    if (guruAlertFilter && guruAlertFilter !== "ALL") {
      const alertLabel = guruAlertFilter === "2" ? "Alert only" : guruAlertFilter === "1" ? "Watch only" : "None";
      addChip("guru-alert", `Guru Alerts: ${alertLabel}`, () => setGuruAlert("ALL"));
    }

    // Minutes trend filter
    if (String(activeSport || "").toUpperCase() !== "CFB" && minutesTrendMin !== null && minutesTrendMin !== undefined && minutesTrendMin !== "") {
      addChip("mtrend", `Minutes Trend`, () => setMinutesTrendMin(null));
    }
    if (String(activeSport || "").toUpperCase() !== "CFB" && Array.isArray(selectedMinutesTrends) && selectedMinutesTrends.length > 0) {
      addChip(
        "minutes-trend-pills",
        `Minutes: ${selectedMinutesTrends.map((x) => MINUTES_TREND_LABELS[x] || x).join(", ")}`,
        () => setSelectedMinutesTrends([])
      );
    }

    return chips;
  }, [
    playerQuery,
    selectedTags,
    selectedGames,
    selectedPropType,
    selectedConfidence,
    taxFilter,
    dvpTierFilter,
    selStratFilter,
    blowoutLevelFilter,
    blowoutFilter,
    homeAwayFilter,
    selectedTeams,
    selectedTime,
    momentumFilter,
    momentumPatternFilter,
    confirmedMomentumFilter,
    selectedGuruMagic,
    selectedGuruPotential,
    selectedZGuruTag,
    selectedLeanDirection,
    selectedGuruConflict,
    selectedGuruPicks,
    selectedPatternComponents,
    selectedPatterns,
    patternMatchMode,
    guruListFilter,
    guruAlertFilter,
    minutesTrendMin,
  ]);


  // ---------------------------------------------------------
  // PRIVATE BETA GATING (PWG)
  // ---------------------------------------------------------
  // NOTE: Auth routing is handled by <ProtectedRoute />, but we also guard here
  // to prevent any UI/data from flashing before the userDoc loads.
  if (!authInitialized || authLoading || userDocLoading) {
    return <div style={{ padding: "2rem" }}>Loading...</div>;
  }

  // Must be signed in (belt & suspenders)
  if (!user) {
    return <div style={{ padding: "2rem" }}>Loading...</div>;
  }

  // First-login profile completion. Name is required; DOB is optional.
  if (!userDoc?.profileCompleted) {
    return (
      <CompleteProfileModal
        user={user}
        userDoc={userDoc}
        onSave={updateProfile}
      />
    );
  }

  // Enforce PWG invite flag
  const hasActiveProductAccess = activeSport === "TEG"
    ? hasTEGAccess
    : activeSport === "MSI"
    ? hasMSIAccess
    : hasPWGAccess;

  if (!hasActiveProductAccess || entitlement.user?.status !== "ACTIVE") {
    return <AccessPending />;
  }
  const PWG_RAIL_WIDTH = 88;
  const PWG_PANEL_WIDTH = 340;
  // Global stacking contract (all roles: Free, Guru, Premium, Admin):
  // rail/panel 100s < header/account popovers 500-900 < dialogs/portals 1000+.
  // This is role-independent because every account type renders through this shell.
  const pwgDesktopShellActive = !isNarrow && activeSport !== "TEG";
  const pwgShellOffset = pwgDesktopShellActive
    ? PWG_RAIL_WIDTH + (taxonomyOpen ? PWG_PANEL_WIDTH : 0)
    : 0;

  return (//original return
    <>
      {propsDataError ? (
        <div role="alert" data-testid="sports-data-error" style={{ margin: "12px", padding: "12px 16px", borderRadius: 10, background: "#FEF2F2", color: "#991B1B", fontWeight: 800 }}>
          {propsDataError}
        </div>
      ) : null}
    {user && showSplash && (
      <div className={`splash-root ${showSplash ? '' : 'hide'}`}>
        <img src={logo} className="logo-fade-in" alt="logo" />
      </div>
    )}
    <div
      className={isNarrow ? "pwg-mobile-dashboard-shell" : undefined}
      style={{
        paddingBottom: isNarrow ? "calc(var(--pwg-mobile-nav-h, 70px) + 16px + env(safe-area-inset-bottom))" : 0,
        marginLeft: pwgShellOffset,
        width: pwgDesktopShellActive ? `calc(100% - ${pwgShellOffset}px)` : "100%",
        transition: "margin-left 180ms ease, width 180ms ease",
        boxSizing: "border-box",
      }}
    >
    <header
      className="pw-header"
      style={{
        ...(isNarrow ? { padding: "10px 10px" } : {}),
        position: "relative",
        // Header owns account popovers. Keep it above sticky page chrome, but below true dialogs.
        zIndex: 500,
        overflow: "visible",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* ✅ MOBILE LEFT DRAWER (same state as desktop) */}
      {isNarrow && taxonomyOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            justifyContent: "flex-start",
          }}
          onClick={() => setTaxonomyOpen(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "none",
              height: "100%",
              background: "#fff",
              boxShadow: "0 18px 40px rgba(15,23,42,0.25)",
              padding: 12,
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* top row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontWeight: 900 }}>Menu</div>
              <button
                type="button"
                className="pwActionBtn"
                onClick={() => setTaxonomyOpen(false)}
              >
                Close
              </button>
            </div>

            {/* same tabs as desktop */}
            <div className="pw-leftTabs" style={{ marginBottom: 10 }}>
              {activeSport === "TEG" ? (
                <>
                  <button
                    type="button"
                    className={`pw-leftTab ${leftPanelTab === "teg" ? "is-active" : ""}`}
                    onClick={() => setLeftPanelTab("teg")}
                  >
                    TEG Filters
                  </button>
                </>
              ) : (
                <>
                  {soccerMatchIntelligenceActive ? (
                    <button type="button" className={`pw-leftTab ${leftPanelTab === "games_quick" ? "is-active" : ""}`} onClick={() => setLeftPanelTab("games_quick")}>Games</button>
                  ) : <>
                  <button
                    type="button"
                    className={`pw-leftTab ${leftPanelTab === "filters" ? "is-active" : ""}`}
                    onClick={() => setLeftPanelTab("filters")}
                  >
                    Filters
                  </button>

                  <button
                    type="button"
                    className={`pw-leftTab ${leftPanelTab === "taxonomy" ? "is-active" : ""}`}
                    onClick={() => setLeftPanelTab("taxonomy")}
                  >
                    Taxonomy
                  </button>

                  <button
                    type="button"
                    className={`pw-leftTab ${leftPanelTab === "injury_report" ? "is-active" : ""}`}
                    onClick={() => setLeftPanelTab("injury_report")}
                  >
                    Injury Status
                  </button>

                  {lineupEntryVisible && <button
                    type="button"
                    className={`pw-leftTab ${leftPanelTab === "lineup" ? "is-active" : ""}`}
                    onClick={() => {
                      setLeftPanelTab("lineup");
                      setShowLineupSection(true);
                    }}
                  >
                    Lineup Generator
                  </button>}
                  {isAdminIdentity && (
                    <button type="button" className={`pw-leftTab ${leftPanelTab === "pattern_intelligence" ? "is-active" : ""}`} onClick={() => setLeftPanelTab("pattern_intelligence")}>
                      Pattern Intelligence
                    </button>
                  )}
                  </>}
                </>
              )}
            </div>

            {/* content */}
            {activeSport === "TEG" ? (
              <div style={{ padding: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Search company
                </div>
                <input
                  value={playerInput}
                  onChange={(e) => setPlayerInput(e.target.value)}
                  placeholder="Search company"
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.55)",
                    outline: "none",
                    marginBottom: 10,
                  }}
                />

                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Sector
                </div>
                <select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid rgba(148,163,184,0.55)",
                    outline: "none",
                    marginBottom: 12,
                    background: "#fff",
                  }}
                >
                  <option value="ALL">ALL</option>
                  {(uniqueSectors || [])
                    .filter((s) => s && s !== "ALL")
                    .map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                </select>

                <button
                  type="button"
                  className="pwMobileActionBtn"
                  onClick={clearTEGFilters}
                  style={{ width: "100%" }}
                >
                  Clear
                </button>
              </div>
            ) : soccerMatchIntelligenceActive ? (
          <div>{renderMobileGamesQuickPanel()}</div>
            ) : leftPanelTab === "taxonomy" ? (
          TaxonomyTree
        ) : leftPanelTab === "filters" ? (
          <div>{renderFiltersPanel()}</div>
        ) : leftPanelTab === "games_quick" ? (
          <div>{renderMobileGamesQuickPanel()}</div>
        ) : leftPanelTab === "injury_report" ? (
          <div>{renderInjuryReportPanel()}</div>
        ) : leftPanelTab === "props_quick" ? (
          <div>{renderMobilePropsQuickPanel()}</div>
        ) : leftPanelTab === "pattern_intelligence" && isAdminIdentity ? (
          <div>{renderPatternIntelligencePanel()}</div>
        ) : (
          renderLineupLeftPanel()
        )}
          </div>
        </div>
      )}


      <div
        className="pw-brand"
        role="button"
        tabIndex={0}
        onClick={() => {
          setTaxonomyOpen(false);
          window.requestAnimationFrame(openAbout);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setTaxonomyOpen(false);
            window.requestAnimationFrame(openAbout);
          }
        }}
        style={{ cursor: "pointer", ...(isNarrow ? { marginRight: "auto", justifyContent: "flex-start" } : {}) }}
      >
        <img
          src={logo}
          alt="PlaysWithGuru"
          className="pw-logo"
          draggable={false}
        />
        <h1 className="pw-title" aria-label="PlaysWithGuru" style={{ margin: 0, ...(isNarrow ? { fontSize: 22, letterSpacing: "-0.2px" } : {}) }}>
          <span className="brand-accent">P</span>laysWith
          <span className="brand-contrast">Guru</span>
        </h1>
      </div>


      {/* Compact account access remains available on mobile and desktop. */}
      {(
        <div
          className="pw-actions"
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            justifyContent: "flex-end",
            position: "relative",
            zIndex: 700,
            overflow: "visible",
          }}
        >
          {!isNarrow && <InstallPWAButton className="btn-secondary" />}
          <UserBadge
            compact={isNarrow}
            isSubscribed={isSubscribed}
            isStripeSubscriber={entitlement.isStripePremium}
            isAdmin={entitlement.isAdmin}
            subscriptionLoading={isSubscriptionLoading}
            onSubscribe={handleSubscribe}
            onOpenBilling={handleBilling}
            onLogout={handleLogout}
            onOpenAbout={openAbout}
            darkMode={darkMode}
            tone="brand"
          />
        </div>
      )}
    </header>

{/* sport rail goes BELOW the header */}
<div
  className={`pw-container ${isNarrow ? "page-has-mobile-actions" : ""}`}
  style={{
    maxWidth: "none",
    width: "100%",
    boxSizing: "border-box",
    margin: 0,
    paddingLeft: 0,
    paddingRight: isNarrow ? undefined : 18,
    overflow: "visible",
  }}
>
  <div style={{
    position: "sticky",
    top: 64,           // adjust if your header is taller
    // Sticky sports navigation stays below header-owned account menus.
    zIndex: 40,
    background: "var(--bg, #fff)",
    paddingTop: 8,
    paddingBottom: 8,
    borderBottom: "1px solid rgba(148,163,184,0.35)",
  }}>
    <SportsMenu
      activeSport={activeSport}
      onSportChange={handleRailSportChange}
      sports={railSports}
      availability={availabilityBySport}
    />
    {activeSportAvailability.disabled && (
      <div role="status" className="sport-maintenance-message">
        <strong>{activeSport} is under maintenance.</strong> {activeSportAvailability.message}
      </div>
    )}
  </div>

{/* Layout: Tree (left) + Main content (right) */}
<div style={{ display: "flex", gap: 0, alignItems: "stretch", marginTop: 0 }}>

  {/* PWG COMMAND RAIL — persistent icon bar + contextual slide-out panel */}
  {!isNarrow && activeSport !== "TEG" && (
    <>
      <aside
        style={{
          width: PWG_RAIL_WIDTH,
          boxSizing: "border-box",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          padding: "14px 10px",
          borderRight: "1px solid #26344a",
          background: "#0b1424",
          borderRadius: 0,
          boxShadow: "none",
          zIndex: 120,
        }}
      >
        <button
          type="button"
          onClick={() => {
            setTaxonomyOpen(false);
            window.requestAnimationFrame(openAbout);
          }}
          title="About PlaysWithGuru"
          aria-label="About PlaysWithGuru"
          style={{
            width: 52,
            height: 52,
            borderRadius: 16,
            border: "1px solid #334155",
            background: "#111c2e",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 6,
            flex: "0 0 auto",
          }}
        >
          <img
            src={logo}
            alt="PWG"
            draggable={false}
            style={{ width: 38, height: 38, objectFit: "contain", display: "block" }}
          />
        </button>

        <div style={{ width: 40, height: 1, background: "rgba(148,163,184,0.28)", margin: "8px 0 4px" }} />

        {[
          { key: "games_quick", icon: "games", label: "Games" },
          ...(soccerMatchIntelligenceActive ? [] : [
          { key: "injury_report", icon: "injuries", label: "Injury Report" },
          { key: "filters", icon: "filters", label: "Filters" },
          ...(lineupEntryVisible ? [{ key: "lineup", icon: "lineup", label: "Lineup Generator" }] : []),
          ...(isAdminIdentity ? [{ key: "pattern_intelligence", icon: "pattern_intelligence", label: "Pattern Intelligence" }] : []),
          { key: "taxonomy", icon: "taxonomy", label: "Player Groups" },
          { key: "myplays", icon: "myplays", label: `My Plays (${myGuruList.length})` },
          { key: "discord", icon: "discord", label: discordLabel },
          ]),
        ].map((item, idx) => {
          const active = commandRailItemIsActive(item.key, { taxonomyOpen, commandRailPanel, guruListFilter });
          return (
            <CommandRailIconButton
              key={item.key}
              item={item}
              active={active}
              count={myGuruList.length}
              onClick={() => {
                if (item.key === "myplays") {
                  setGuruListFilter((v) => {
                    const next = v === "my-list" ? "all" : "my-list";
                    if (next === "my-list") setMyPlaysSportFilter("ALL");
                    return next;
                  });
                  setGridPage(1);
                  setCardPage(1);
                  return;
                }
                if (item.key === "discord") {
                  handleDiscordConnect();
                  return;
                }
                const isSameOpen = taxonomyOpen && commandRailPanel === item.key;
                if (isSameOpen) {
                  setTaxonomyOpen(false);
                  return;
                }
                setCommandRailPanel(item.key);
                setLeftPanelTab(item.key === "games_quick" ? "games_quick" : item.key);
                if (item.key === "lineup") setShowLineupSection(true);
                setTaxonomyOpen(true);
              }}
            />
          );
        })}
      </aside>

      {taxonomyOpen && (
        <aside
          style={{
            width: PWG_PANEL_WIDTH,
            boxSizing: "border-box",
            position: "fixed",
            left: PWG_RAIL_WIDTH,
            top: 0,
            bottom: 0,
            height: "100vh",
            overflowY: "auto",
            padding: "22px 16px 24px",
            borderRight: "1px solid #cbd5e1",
            background: darkMode ? "#111c2e" : "#f3f6fb",
            color: darkMode ? "#f8fafc" : "#0f172a",
            borderRadius: 0,
            boxShadow: "none",
            zIndex: 110,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: 0.7, textTransform: "uppercase", color: darkMode ? "#94a3b8" : "#64748b" }}>PWG</div>
              <div style={{ fontSize: 18, fontWeight: 950, color: darkMode ? "#f8fafc" : "#0f172a" }}>
                {commandRailPanel === "games_quick" ? "Games" : commandRailPanel === "injury_report" ? "Injury Report" : commandRailPanel === "lineup" ? "Lineup Generator" : commandRailPanel === "pattern_intelligence" ? "Pattern Intelligence" : commandRailPanel === "taxonomy" ? "Player Groups" : "Filters"}
              </div>
            </div>
            <button type="button" onClick={() => setTaxonomyOpen(false)} aria-label="Close panel" title="Close" style={{ width: 34, height: 34, borderRadius: 999, border: "1px solid rgba(148,163,184,0.45)", background: darkMode ? "#111827" : "#f8fafc", color: darkMode ? "#fff" : "#111827", fontWeight: 950, cursor: "pointer" }}>×</button>
          </div>
          {soccerMatchIntelligenceActive || commandRailPanel === "games_quick" ? (
            <div>{renderMobileGamesQuickPanel()}</div>
          ) : commandRailPanel === "injury_report" ? (
            <div>{renderInjuryReportPanel()}</div>
          ) : commandRailPanel === "taxonomy" ? (
            TaxonomyTree
          ) : commandRailPanel === "lineup" ? (
            renderLineupLeftPanel()
          ) : commandRailPanel === "pattern_intelligence" && isAdminIdentity ? (
            renderPatternIntelligencePanel()
          ) : (
            <div>{renderFiltersPanel()}</div>
          )}
        </aside>
      )}
    </>
  )}

  {/*
    ✅ MAIN CONTENT (ALWAYS)
    - When taxonomyOpen is false, this becomes full width (no reserved space).
  */}
  <div className="pwg-mobile-main-content" style={{ flex: 1, minWidth: 0, width: "100%", paddingLeft: 16, paddingRight: 0, boxSizing: "border-box" }}>
    {/* main content */}

    {/* Mobile-only persistent search */}
    {/* SEARCH + TAGS (mobile only in Cards view) */}
    {!hideLineupControls && !draftKingsModeActive && activeSport !== "TEG" && !soccerMatchIntelligenceActive && (
      <section className="lineup-controls" style={{ overflow: "visible" }}>
        {/* Mobile-only persistent search */}
        {isNarrow && (
        <div className="mobile-search" style={{ display: "flex", alignItems: "center", gap: 10 }}>
  {!mobileSearchOpen ? (
            <div className="pwg-mobile-utility-row" style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 12px", marginTop: 6 }}>
              {/* Search (opens inline search bar) */}
              <button
                type="button"
                className="pwg-mobile-search-trigger"
                onClick={() => setMobileSearchOpen(true)}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto",
                }}
                aria-label="Search props"
                title="Search"
              >
                <span style={{ fontSize: 18 }}>🔎</span>
              </button>

              {/* Quick: Games + Prop Types (sit next to search, not far-right) */}
              <button
                type="button"
                className="pwg-mobile-redundant-tool"
                onClick={() => {
                  setShowLineupsFocus(false);
                  setShowLineupSection(false);
                  setLeftPanelTab("games_quick");
                  setTaxonomyOpen(true);
                }}
                style={{
                  padding: "10px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: "#fff",
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <NavigationIcon icon="games" />
                Games
              </button>

              <button
                type="button"
                className="pwg-mobile-redundant-tool"
                onClick={() => {
                  setShowLineupsFocus(false);
                  setShowLineupSection(false);
                  setLeftPanelTab("injury_report");
                  setTaxonomyOpen(true);
                }}
                style={{
                  padding: "10px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: "#fff",
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <NavigationIcon icon="injuries" testId="mobile-injuries-icon" contrastOnLight />
                Injuries
              </button>

              <button
                type="button"
                className="pwg-mobile-redundant-tool"
                onClick={() => {
                  setShowLineupsFocus(false);
                  setShowLineupSection(false);
                  setLeftPanelTab("props_quick");
                  setTaxonomyOpen(true);
                }}
                style={{
                  padding: "10px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: "#fff",
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span aria-hidden="true">🎛️</span>
                Props
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 12px", marginTop: 6 }}>
              <div style={{ position: "relative", flex: 1 }}>
                <input
                  type="search"
                  value={playerInput}
                  onChange={(e) => setPlayerInput(e.target.value)}
                  placeholder="Search player..."
                  autoFocus
                  style={{
                    width: "100%",
                    height: 46,
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.55)",
                    padding: "0 44px 0 16px",
                    fontSize: 16,
                    fontWeight: 700,
                    outline: "none",
                    background: "#fff",
                  }}
                />
                {!!playerInput && (
                  <button
                    type="button"
                    onClick={() => { setPlayerInput(""); setPlayerQuery(""); }}
                    aria-label="Clear search"
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      border: "1px solid rgba(148,163,184,0.45)",
                      background: "#fff",
                      fontWeight: 900,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => setMobileSearchOpen(false)}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.55)",
                  background: "#fff",
                  fontWeight: 900,
                }}
                aria-label="Close search"
                title="Close"
              >
                ✕
              </button>
            </div>
          )}
</div>
        )}

        <div
          className="pwg-mobile-filter-row pwg-direction-utility"
          aria-label="Search and prop direction"
          style={{
            "--pwg-direction-over": tagColorMap.SMASH,
            "--pwg-direction-under": tagColorMap["MEGA FADE"],
          }}
        >
          {isNarrow && <>
            <button type="button" className="pwg-mobile-search-trigger" aria-label="Search props" title="Search props" onClick={() => setMobileSearchOpen(true)}>
              <span className="pwg-mobile-search-glyph" aria-hidden="true">⌕</span>
            </button>
          </>}
          <div className="pwg-direction-buttons" role="group" aria-label="Prop direction; neither selected shows all directions">
            {(isNarrow ? ["OVER", "UNDER"] : ["ALL", "OVER", "UNDER"]).map((direction) => (
              <button key={direction} type="button" className={`pwg-direction-filter is-${direction.toLowerCase()}`} aria-pressed={selectedDirection === direction} aria-label={direction === "ALL" ? "All directions" : `${direction === "OVER" ? "Overs" : "Unders"} only`} onClick={() => handleDirectionSelection(direction)}>
                {direction === "OVER" ? "▲ OVERS" : direction === "UNDER" ? "▼ UNDERS" : "ALL"}
              </button>
            ))}
          </div>
          {isNarrow && <span className="pwg-visually-hidden" role="status" aria-live="polite">{selectedDirection === "ALL" ? "Showing all directions" : `Showing ${selectedDirection.toLowerCase()} directions`}</span>}
        </div>

        {/* Sticky: keep Tags + Prop Types visible while scrolling */}
        {/** Recommendation tags remain visible; the hamburger controls deeper discovery tools. */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 50,
            background: darkMode ? "#0b1220" : "#ffffff",
            paddingTop: 8,
            paddingBottom: isNarrow ? 0 : 8,
            marginTop: 6,
            borderBottom: isNarrow ? "none" : (darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.06)"),
          }}
        >
        <div className="pwg-strategy-filter-row">
        <div className={isNarrow ? "pwg-recommendation-rail-wrap" : undefined}>
        <div className="pwg-recommendation-rail" role="group" aria-label="PWG Rating filters" style={{ display: "flex", gap: "0.6rem", marginBottom: 0, flexWrap: "nowrap", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          {visibleRecommendationTags.map(tag => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`pwg-recommendation-filter ${selectedTags.includes(tag) ? "is-selected" : ""}`}
              aria-pressed={selectedTags.includes(tag)}
              aria-label={`Filter PWG Rating ${tag}`}
              title={tag}
              style={{ opacity: selectedTags.length === 0 || selectedTags.includes(tag) ? 1 : 0.4 }}
            >
              <StrategyMarker tag={tag} decorative />
            </button>
          ))}
        </div>
        </div>
        <button type="button" className="pwg-strategy-info" aria-label="Explain PWG Rating markers" title="PWG Rating marker key" onClick={() => setShowHelp(true)}>ⓘ</button>
        </div>

        {activeSport !== "TEG" && (
          <div className="pwgLineTypeSlateRow" data-testid="line-type-slate-row">
            <LineTypeControls
              className="pwgLineTypeControls"
              selectedTypes={selectedLineTypes}
              availableTypes={availableLineTypes}
              onSelect={setSelectedLineTypes}
              darkMode={darkMode}
              style={{ marginBottom: 0 }}
            />
            {activeSport === "MLB" && secureMlbProgress && Number(secureMlbProgress.loaded) < Number(secureMlbProgress.total) && (
              <span data-testid="mlb-board-progress" style={{ fontSize: 12, fontWeight: 850, color: darkMode ? "#BAE6FD" : "#075985" }}>
                Loading {secureMlbProgress.loaded} of {secureMlbProgress.total} props…
              </span>
            )}
            {activeSport === "MLB" && secureMlbProgress?.error && (
              <span role="status" data-testid="mlb-board-scoped-error" style={{ fontSize: 12, fontWeight: 750, color: darkMode ? "#FECACA" : "#B91C1C" }}>
                {secureMlbProgress.error}
              </span>
            )}
            {directionSummary?.total > 0 && (
              <button
                type="button"
                className="pwgSlateSummary"
                onClick={() => dailyBrief && setDailyBriefOpen((value) => !value)}
                title="Slate distribution"
                style={{
                  border: "1px solid rgba(148,163,184,0.35)",
                  background: darkMode ? "#0b1220" : "#fff",
                  color: darkMode ? "#e5e7eb" : "#111827",
                  cursor: dailyBrief ? "pointer" : "default",
                }}
              >
                <span aria-hidden="true">📊</span>
                <span>Slate</span>
                <span style={{ color: "#ef4444" }}>{Math.round(pctUnders)}%</span>
                <span style={{ opacity: 0.45 }}>•</span>
                <span style={{ color: "#f59e0b" }}>{Math.round(pctCoin)}%</span>
                <span style={{ opacity: 0.45 }}>•</span>
                <span style={{ color: "#22c55e" }}>{Math.round(pctOvers)}%</span>
              </button>
            )}
          </div>
        )}





        {isAdminIdentity && activeSport === "SOCCER" && (
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              data-testid="soccer-admin-research-toggle"
              aria-pressed={soccerAdminResearchMode}
              onClick={() => setSoccerAdminResearchMode((current) => !current)}
              style={{ padding: "6px 12px", borderRadius: 999, border: "1px solid #7C3AED", background: soccerAdminResearchMode ? "#7C3AED" : "transparent", color: soccerAdminResearchMode ? "#FFF" : "#6D28D9", fontWeight: 900 }}
            >
              {soccerAdminResearchMode ? "Customer Board" : "Admin Research"}
            </button>
          </div>
        )}

        {availableDefenseTiers.length > 0 && (
          <div aria-label="Football defense filters" style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 850, color: darkMode ? "#CBD5E1" : "#475569" }}>Defense</span>
            {availableDefenseTiers.map((tier) => (
              <button key={tier} type="button" aria-pressed={selectedDefenseTiers.includes(tier)} onClick={() => setSelectedDefenseTiers((current) => current.includes(tier) ? current.filter((value) => value !== tier) : [...current, tier])} style={{ padding: "4px 9px", borderRadius: 999, border: "1px solid #94A3B8", background: selectedDefenseTiers.length === 0 || selectedDefenseTiers.includes(tier) ? "#F1F5F9" : "transparent", color: darkMode ? "#E2E8F0" : "#334155", fontSize: 11, fontWeight: 850, cursor: "pointer" }}>
                {tier}
              </button>
            ))}
            <button type="button" onClick={() => setNbaGridSortConfig({ key: "defenseRank", direction: "desc" })} style={{ padding: "4px 9px", borderRadius: 999, border: "1px solid #CBD5E1", background: "transparent", fontSize: 11, cursor: "pointer" }}>Weakest Defense</button>
            <button type="button" onClick={() => setNbaGridSortConfig({ key: "defenseRank", direction: "asc" })} style={{ padding: "4px 9px", borderRadius: 999, border: "1px solid #CBD5E1", background: "transparent", fontSize: 11, cursor: "pointer" }}>Strongest Defense</button>
          </div>
        )}

        {showAdvancedFilterControls && (
          <>
        {/* L5 Shape Row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, marginBottom: 10 }}>
          <button
            type="button"
            onClick={resetL5Shapes}
            style={{
              borderRadius: 999,
              padding: "0.35rem 0.75rem",
              fontWeight: 900,
              border: "1px solid rgba(15,23,42,0.14)",
              background: selectedL5Shapes.length === 0 ? "#111827" : "transparent",
              color: selectedL5Shapes.length === 0 ? "#fff" : "#111827",
              cursor: "pointer",
            }}
          >
            L5: Any
          </button>

          {["P5", "STRONG", "BOUNCE", "TURNING_UP", "SURGING", "COOLING_OFF", "DIP"].map((key) => {
            const active = selectedL5Shapes.includes(key);

            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleL5Shape(key)}
                style={{
                  borderRadius: 999,
                  padding: "0.35rem 0.75rem",
                  fontWeight: 900,
                  border: active
                    ? "1px solid rgba(59,130,246,0.55)"
                    : "1px solid rgba(15,23,42,0.14)",
                  background: active ? "rgba(59,130,246,0.12)" : "transparent",
                  color: "#111827",
                  cursor: "pointer",
                }}
              >
                {L5_LABELS[key] || key}
              </button>
            );
          })}
        </div>

        {/* Minutes Trend Row */}
        {!["MLB", "KBO"].includes(String(activeSport || "").toUpperCase()) && allowsMinutesUi(activeSport) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, marginBottom: 10 }}>
          <button
            type="button"
            onClick={resetMinutesTrends}
            style={{
              borderRadius: 999,
              padding: "0.35rem 0.75rem",
              fontWeight: 900,
              border: "1px solid rgba(15,23,42,0.14)",
              background: selectedMinutesTrends.length === 0 ? "#111827" : "transparent",
              color: selectedMinutesTrends.length === 0 ? "#fff" : "#111827",
              cursor: "pointer",
            }}
          >
            Minutes: Any
          </button>

          {allMinutesTrends.filter((s) => s !== "ALL").map((key) => {
            const canonical = getMinutesTrendCanonicalKey(key);
            const active = selectedMinutesTrends.includes(canonical);
            return (
              <button
                key={`top-minutes-trend-${canonical}`}
                type="button"
                onClick={() => toggleMinutesTrend(canonical)}
                style={{
                  borderRadius: 999,
                  padding: "0.35rem 0.75rem",
                  fontWeight: 900,
                  border: active
                    ? "1px solid rgba(34,197,94,0.65)"
                    : "1px solid rgba(15,23,42,0.14)",
                  background: active ? "rgba(34,197,94,0.12)" : "transparent",
                  color: "#111827",
                  cursor: "pointer",
                }}
              >
                {MINUTES_TREND_LABELS[canonical] || canonical}
              </button>
            );
          })}
        </div>
        )}

        {/* Bounce State Row */}
        {(allBounceLabels.length > 0 || allBounceFamilies.length > 0) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, marginBottom: 10 }}>
            <button
              type="button"
              onClick={resetBounceFilters}
              style={{
                borderRadius: 999,
                padding: "0.35rem 0.75rem",
                fontWeight: 900,
                border: "1px solid rgba(15,23,42,0.14)",
                background: selectedBounceLabels.length === 0 && selectedBounceFamilies.length === 0 ? "#111827" : "transparent",
                color: selectedBounceLabels.length === 0 && selectedBounceFamilies.length === 0 ? "#fff" : "#111827",
                cursor: "pointer",
              }}
            >
              Bounce: Any
            </button>

            {allBounceFamilies.map((family) => {
              const active = selectedBounceFamilies.includes(family);
              return (
                <button
                  key={`top-bounce-family-${family}`}
                  type="button"
                  onClick={() => toggleBounceFamily(family)}
                  style={{
                    borderRadius: 999,
                    padding: "0.35rem 0.75rem",
                    fontWeight: 900,
                    border: active ? "1px solid rgba(168,85,247,0.65)" : "1px solid rgba(15,23,42,0.14)",
                    background: active ? "rgba(168,85,247,0.12)" : "transparent",
                    color: "#111827",
                    cursor: "pointer",
                  }}
                >
                  {BOUNCE_FAMILY_LABELS[family] || family}
                </button>
              );
            })}

            {allBounceLabels.map((label) => {
              const active = selectedBounceLabels.includes(label);
              return (
                <button
                  key={`top-bounce-label-${label}`}
                  type="button"
                  onClick={() => toggleBounceLabel(label)}
                  style={{
                    borderRadius: 999,
                    padding: "0.35rem 0.75rem",
                    fontWeight: 900,
                    border: active ? "1px solid rgba(34,197,94,0.65)" : "1px solid rgba(15,23,42,0.14)",
                    background: active ? "rgba(34,197,94,0.12)" : "transparent",
                    color: "#111827",
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Pattern Row */}


        {/* Formation Family / Code Row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, marginBottom: 10 }}>
          <button
            type="button"
            onClick={resetFormations}
            style={{
              borderRadius: 999,
              padding: "0.35rem 0.75rem",
              fontWeight: 900,
              border: "1px solid rgba(15,23,42,0.14)",
              background: (selectedFormationFamilies.length === 0 && selectedFormationCodes.length === 0) ? "#111827" : "transparent",
              color: (selectedFormationFamilies.length === 0 && selectedFormationCodes.length === 0) ? "#fff" : "#111827",
              cursor: "pointer",
            }}
          >
            Formation: Any
          </button>

          {FORMATION_FAMILY_ORDER.map((family) => {
            const active = selectedFormationFamilies.includes(family);
            return (
              <button
                key={`mobile-formation-family-${family}`}
                type="button"
                onClick={() => toggleFormationFamily(family)}
                style={formationFamilyStyle(family, false, active)}
              >
                {FORMATION_FAMILY_LABELS[family]}
              </button>
            );
          })}
        </div>

        {allFormationCodes.length > 1 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, marginBottom: 10 }}>
            {allFormationCodes.filter((s) => s !== "ALL").slice(0, 18).map((code) => {
              const fam = getFormationFamily(code);
              const active = selectedFormationCodes.includes(code);
              return (
                <button
                  key={`mobile-formation-code-${code}`}
                  type="button"
                  onClick={() => toggleFormationCode(code)}
                  style={formationFamilyStyle(fam, false, active)}
                  title={`Formation ${code}`}
                >
                  {code}
                </button>
              );
            })}
          </div>
        )}
        {/* Guru Pick Row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, marginBottom: 10 }}>
          <button
            type="button"
            onClick={() => setSelectedGuruPicks("")}
            style={{
              borderRadius: 999,
              padding: "0.35rem 0.75rem",
              fontWeight: 900,
              border: "1px solid rgba(15,23,42,0.14)",
              background: !selectedGuruPicks ? "#111827" : "transparent",
              color: !selectedGuruPicks ? "#fff" : "#111827",
              cursor: "pointer",
            }}
          >
            Guru Pick: Any
          </button>

          {GURU_PICK_OPTIONS.map((pick) => {
            const active = selectedGuruPicks === pick;
            return (
              <button
                key={pick}
                type="button"
                onClick={() => setSelectedGuruPicks(active ? "" : pick)}
                style={guruPickFilterButtonStyle(pick, active, false)}
              >
                {pick}
              </button>
            );
          })}
        </div>

          </>
        )}

        {/* Risk Filter */}
        {/* Games + Risk Filter */}
        {showAdvancedFilterControls && <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 6,
            marginBottom: 12,
            alignItems: "center",
            flexWrap: "wrap",
            position: "relative",
          }}
        >
 

          {/* Risk pills are premium/research controls */}
          {["LOW", "MODERATE", "HIGH"].map((riskKey) => (
            <button
              key={riskKey}
              onClick={() =>
                setSelectedRisk((prev) =>
                  prev.includes(riskKey)
                    ? prev.filter((r) => r !== riskKey)
                    : [...prev, riskKey]
                )
              }
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                border: "1px solid #6b7280",
                background: selectedRisk.includes(riskKey) ? "#2563eb" : "transparent",
                color: selectedRisk.includes(riskKey) ? "white" : "#6b7280",
                fontSize: "0.8rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Risk: {riskKey}
            </button>
          ))}
        </div>}

        {!isNarrow && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: "1rem" }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#64748b", marginRight: 4 }}>
              Prop Types
            </div>

            <button
              type="button"
              onClick={resetPropTypes}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: "1px solid #cbd5e1",
                background: selectedPropTypes.length === 0 ? "#0f172a" : "#fff",
                color: selectedPropTypes.length === 0 ? "#fff" : "#0f172a",
                fontWeight: 900,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              All
            </button>

            {(uniquePropTypes || []).map((pt) => {
              const active = selectedPropTypes.includes(pt);
              return (
                <button
                  key={pt}
                  type="button"
                  onClick={() => togglePropType(pt)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: "1px solid #cbd5e1",
                    background: active ? "#0ea5e9" : "#fff",
                    color: active ? "#fff" : "#0f172a",
                    fontWeight: 900,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  {pt}
                </button>
              );
            })}
          </div>
        )}
        </div>
        {/* /Sticky */}
      </section>
    )}

    <SoccerAdminResearchPanel
      active={soccerAdminResearchMode && activeSport === "SOCCER" && !soccerMatchIntelligenceActive}
      isAdmin={isAdminIdentity}
      identity={user}
    />

    {/* Desktop/tablet: show full filters inline */}
    {!isNarrow && activeSport !== "TEG" && (
      <div style={{ marginTop: 6, marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {!soccerMatchIntelligenceActive && <div style={{ flex: 1, minWidth: 220 }}>
            <input
              value={activeSport === "TEG" ? playerInput : playerQuery}
              onChange={(e) => {
                const v = e.target.value;
                if (activeSport === "TEG") {
                  setPlayerInput(v);
                } else {
                  setPlayerQuery(v);
                  setPlayerInput(v);
                }
              }}
              placeholder={activeSport === "TEG" ? "Search company" : "Search player"}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.12)",
                outline: "none",
                background: darkMode ? "#0b1220" : "#fff",
                color: darkMode ? "#e5e7eb" : "#111",
                fontSize: 15,
              }}
            />
          </div>}
          
          {/* Primary page navigation stays beside Search / view controls. */}
          {activeSport !== "TEG" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {activeSport === "SOCCER" && (
                <button
                  type="button"
                  data-testid="match-intelligence-mode"
                  onClick={() => { setActiveView("match-intelligence"); setGuruListFilter("all"); }}
                  style={{ padding: "9px 14px", borderRadius: 999, border: "1px solid rgba(148,163,184,0.45)", background: soccerMatchIntelligenceActive ? "#111827" : (darkMode ? "#0b1220" : "#fff"), color: soccerMatchIntelligenceActive ? "#fff" : (darkMode ? "#e5e7eb" : "#111827"), fontWeight: 900, cursor: "pointer" }}
                >
                  Match Intelligence
                </button>
              )}
              {canSeeAdvancedCard && (
                <button
                  type="button"
                  onClick={() => { setActiveView("dashboard"); setGuruListFilter("all"); }}
                  style={{
                    padding: "9px 14px",
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.45)",
                    background: activeView === "dashboard" ? "#111827" : (darkMode ? "#0b1220" : "#fff"),
                    color: activeView === "dashboard" ? "#fff" : (darkMode ? "#e5e7eb" : "#111827"),
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  Prop Dashboard
                </button>
              )}
              <button
                type="button"
                onClick={() => { setActiveView("props"); setGuruListFilter("all"); }}
                style={{
                  padding: "9px 14px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.45)",
                  background: activeView === "props" && guruListFilter !== "my-list" ? "#111827" : (darkMode ? "#0b1220" : "#fff"),
                  color: activeView === "props" && guruListFilter !== "my-list" ? "#fff" : (darkMode ? "#e5e7eb" : "#111827"),
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Player Props
              </button>
              <button
                type="button"
                onClick={() => { setActiveView("props"); setMyPlaysSportFilter("ALL"); setGuruListFilter("my-list"); }}
                style={{
                  padding: "9px 14px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.45)",
                  background: guruListFilter === "my-list" ? "#111827" : (darkMode ? "#0b1220" : "#fff"),
                  color: guruListFilter === "my-list" ? "#fff" : (darkMode ? "#e5e7eb" : "#111827"),
                  fontWeight: 900,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                My Plays ({myGuruList.length})
              </button>
            </div>
          )}

          {activeSport !== "TEG" && !soccerMatchIntelligenceActive && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginLeft: "auto",
                paddingLeft: 14,
                borderLeft: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(148,163,184,0.35)",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.08)",
                overflow: "hidden",
                background: darkMode ? "#0b1220" : "#fff",
                whiteSpace: "nowrap",
              }}
              title="Toggle cards vs grid"
            >
              <button
                type="button"
                onClick={() => setViewMode("card")}
                className="pill"
                style={{
                  borderRadius: 0,
                  background:
                    viewMode === "card" ? (darkMode ? "#1f2937" : "#0b1220") : "transparent",
                  color: viewMode === "card" ? "#fff" : darkMode ? "#e5e7eb" : "#111",
                  border: "none",
                  padding: "8px 14px",
                  fontWeight: 800,
                }}
              >
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className="pill"
                style={{
                  borderRadius: 0,
                  background:
                    viewMode === "grid" ? (darkMode ? "#1f2937" : "#0b1220") : "transparent",
                  color: viewMode === "grid" ? "#fff" : darkMode ? "#e5e7eb" : "#111",
                  border: "none",
                  padding: "8px 14px",
                  fontWeight: 800,
                }}
              >
                Grid
              </button>
            </div>
          )}

          {!soccerMatchIntelligenceActive && <button
            type="button"
            className="pwActionBtn"
            onClick={() => {
              clearAllFilters();
              setGuruListFilter("all");
            }}
            style={{ opacity: 0.9 }}
          >
            ✖ Clear Filters
          </button>}

          {!soccerMatchIntelligenceActive && activeFilterChips.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                minWidth: 0,
              }}
              aria-label="Active filters"
            >
              {activeFilterChips.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className="pill"
                  onClick={c.onClear}
                  title="Remove filter"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 9px",
                    border: "1px solid #d0d7de",
                    background: darkMode ? "#101826" : "#fff",
                    fontSize: 12,
                    lineHeight: 1.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ fontWeight: 700 }}>{c.label}</span>
                  <span style={{ fontWeight: 900, opacity: 0.65 }}>×</span>
                </button>
              ))}
            </div>
          )}

          {!soccerMatchIntelligenceActive && generatedLineups?.length > 0 &&
            !showLineupsFocus &&
            activeSport !== "TEG" && (
              <button
                type="button"
                className={`pwActionBtn ${showLineupsFocus ? "is-active" : ""}`}
                onClick={() => {
                  setShowLineupsFocus(true);
                  setLineupsTab("lineups");
                  setTimeout(() => {
                    document
                      .getElementById("pwg-lineups-dock")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 0);
                }}
                title="Show the last generated lineups"
              >
                <span className="pwShowLineupsDot" />
                Show Lineups ({generatedLineups.length})
              </button>
          )}



          <div style={{ flex: 1 }} />
        </div>

      </div>
    )}

   {guruListFilter === "my-list" && activeSport !== "TEG" && !soccerMatchIntelligenceActive && (
      <section style={{ marginTop: 10, marginBottom: 8, padding: "10px 12px", border: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid #dbe4f0", borderRadius: 14, background: darkMode ? "#0b1220" : "#f8fafc", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ marginRight: 4 }}>My Plays</strong>
        <button type="button" onClick={() => setMyPlaysSportFilter("ALL")} className="pill" style={{fontWeight:900,background:myPlaysSportFilter==="ALL"?"#111827":"#fff",color:myPlaysSportFilter==="ALL"?"#fff":"#111827"}}>All Sports ({myGuruList.length})</button>
        {Object.entries(myPlaysSportCounts).sort(([a],[b]) => a.localeCompare(b)).map(([sport,count]) => (
          <button key={sport} type="button" onClick={() => setMyPlaysSportFilter(sport)} className="pill" style={{fontWeight:900,background:myPlaysSportFilter===sport?"#111827":"#fff",color:myPlaysSportFilter===sport?"#fff":"#111827"}}>{sport} ({count})</button>
        ))}
        <span style={{flex:1}} />
        <button type="button" disabled={!myGuruList.length} onClick={() => setClearMyPlaysDialogOpen(true)} style={{padding:"8px 12px",borderRadius:999,border:"1px solid #fca5a5",background:"#fff",color:"#b91c1c",fontWeight:900,cursor:myGuruList.length?"pointer":"not-allowed"}}>Clear All</button>
      </section>
    )}

   {/* --- MAIN + DOCK ROW (desktop) --- */}
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "stretch",
        marginTop: 4,
      }}
    >
      {/* Main content column */}
      {/* Main content column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {soccerMatchIntelligenceActive ? (
          <SoccerMatchIntelligence active selectedMatchId={selectedSoccerMatchId} onBoard={receiveSoccerMatchBoard} onSelectMatch={setSelectedSoccerMatchId} />
        ) : activeSport === "TEG" ? (
          isGuruPlayManager ? <TEGDashboard
            rows={filteredProps}
            darkMode={darkMode}
            adminMode
            playerInput={playerInput}
            setPlayerInput={setPlayerInput}
            selectedSector={selectedSector}
            setSelectedSector={setSelectedSector}
            uniqueSectors={uniqueSectors}
          /> : <TEGSimpleDashboard rows={filteredProps} />
        ) : (
          <>
            {canSeeAdvancedCard && activeView === "dashboard" ? (
              <PWGDashboard
                propsData={filteredProps}
                darkMode={darkMode}
                onOpenProps={() => setActiveView("props")}
                onDrillDown={handleDashboardDrillDown}
                onOpenTopPlay={(row) => handleDashboardOpenTopPlay(row)}
              />
            ) : (
              renderPlayerCards()
            )}
          </>
        )}
      </div>

      {/* Right Dock (lineups + exposure) */}
      {!isNarrow && dockOpen && !soccerMatchIntelligenceActive && (
        <aside
          key={dockKey}
          style={{
            width: 520,
            flex: "0 0 520px",
            borderLeft: "1px solid rgba(148,163,184,0.35)",
            paddingLeft: 12,
            position: "sticky",
            top: 86,
            alignSelf: "flex-start",
            height: "calc(100vh - 110px)",
            overflow: "auto",
            borderRadius: 16,
            background: darkMode ? "#0b1220" : "#fff",
            boxShadow: darkMode ? "0 10px 30px rgba(0,0,0,0.35)" : "0 10px 30px rgba(15,23,42,0.08)",
          }}
        >
          {renderLineupDock()}
        </aside>
      )}
    </div>




      {/* PWG V101: legacy mobile Lineup Results block removed.
          Mobile lineup output is rendered only by renderLineupsWorkspace(). */}

      

{dailyBriefOpen && dailyBrief && (
  <div
    onClick={() => setDailyBriefOpen(false)}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 10050,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      padding: 12,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "min(920px, 96vw)",
        background: darkMode ? "#0b1220" : "#ffffff",
        color: darkMode ? "#e5e7eb" : "#0f172a",
        border: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
        borderRadius: 16,
        boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        maxHeight: "calc(100vh - 24px)",
      }}
    >
      <div style={{
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        borderBottom: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ fontWeight: 900, fontSize: 14, letterSpacing: 0.2 }}>
            🧠 Daily Guru Brief
          </div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>
            {activeSport} • {dailyBrief?.headline || "Slate context"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDailyBriefOpen(false)}
          style={{
            borderRadius: 10,
            padding: "8px 10px",
            border: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(15,23,42,0.12)",
            background: darkMode ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
            color: "inherit",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {dailyBrief?.take && (
          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: darkMode ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
              border: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
              fontSize: 13,
              lineHeight: 1.35,
              fontWeight: 700,
            }}
          >
            {dailyBrief.take}
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {[
            ["Props", dailyBrief?.props_total],
            ["Strong", dailyBrief?.strong_edges],
            ["Good", dailyBrief?.good_edges],
            ["Blowouts", dailyBrief?.blowouts],
            ["Competitive", dailyBrief?.competitive],
            ["Over %", dailyBrief?.over_pct],
            ["Under %", dailyBrief?.under_pct],
            ["Coin %", dailyBrief?.coin_pct],
          ]
            .filter(([,v]) => v !== null && v !== undefined && v !== "")
            .map(([k,v]) => (
              <div key={k} style={{
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                border: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(15,23,42,0.12)",
                background: darkMode ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.03)",
                whiteSpace: "nowrap",
              }}>
                {k}: {String(v)}
              </div>
            ))}
        </div>

        {!dailyBrief?.take && !dailyBrief?.headline && (
          <div style={{ opacity: 0.7, fontSize: 13 }}>
            No briefing available yet for {activeSport}.
          </div>
        )}
      </div>
    </div>
  </div>
)}
{showHelp && <TagHelpModal onClose={() => setShowHelp(false)} />}

      {isMobileBar && activeSport === "TEG" && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0, // ✅ dock sits on the bottom
            zIndex: 9999,

            // ✅ solid base
            background: "#fff",
            borderTop: "1px solid rgba(148,163,184,0.35)",
            boxShadow: "0 -12px 24px rgba(15,23,42,0.12)",

            // ✅ spacing + safe area
            padding: "10px 12px calc(10px + env(safe-area-inset-bottom))",

            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ display: "flex", gap: 10, pointerEvents: "auto" }}>
            <button
              type="button"
              className={"pwMobileActionBtn" + (taxonomyOpen ? " is-active" : "")}
              onClick={() => {
                setTaxonomyOpen(true);
                setLeftPanelTab("teg");
              }}
            >
              Filters
            </button>

            <button type="button" className="pwMobileActionBtn" onClick={clearTEGFilters}>
              Clear
            </button>
          </div>
        </div>
      )}


      {isMobileBar && activeSport !== "TEG" && (
        <nav
          aria-label="Mobile navigation"
          data-testid="mobile-bottom-navigation"
          className="pwg-mobile-bottom-nav"
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 8500,
            display: "grid",
            gridTemplateColumns: `repeat(${lineupEntryVisible ? 5 : 4}, minmax(0, 1fr))`,
            gap: 4,
            padding: "8px 8px calc(8px + env(safe-area-inset-bottom))",
            background: darkMode ? "rgba(11,18,32,0.98)" : "rgba(255,255,255,0.98)",
            borderTop: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e5e7eb",
            boxShadow: "0 -8px 24px rgba(15,23,42,0.10)",
            backdropFilter: "blur(12px)",
          }}
        >
          {[
            {
              key: "games",
              icon: "games",
              label: "Games",
              active: taxonomyOpen && leftPanelTab === "games_quick",
              onClick: () => {
                setMobileProfileOpen(false);
                setEdgeAnalyzerOpen(false);
                setShowLineupsFocus(false);
                setTaxonomyOpen(true);
                setLeftPanelTab("games_quick");
              },
            },
            {
              key: "discord",
              icon: "discord",
              label: "Discord",
              ariaLabel: discordLabel,
              active: false,
              onClick: () => {
                setMobileProfileOpen(false);
                handleDiscordConnect();
              },
            },
            {
              key: "props",
              icon: "props",
              label: "Props",
              active: !taxonomyOpen && !showLineupsFocus && guruListFilter !== "my-list",
              onClick: () => {
                setMobileProfileOpen(false);
                setTaxonomyOpen(false);
                setShowLineupsFocus(false);
                setShowLineupSection(false);
                setGuruListFilter("all");
              },
            },
            ...(lineupEntryVisible ? [{
              key: "lineups",
              icon: "lineup",
              label: "Lineups",
              active: mobileLineupsIsActive({ taxonomyOpen, leftPanelTab, showLineupsFocus, generatedLineups }),
              onClick: () => {
                setMobileProfileOpen(false);
                const hasLineups = (generatedLineups?.length || 0) > 0;
                setEdgeAnalyzerOpen(false);
                if (!hasLineups) {
                  setTaxonomyOpen(true);
                  setLeftPanelTab("lineup");
                  setShowLineupSection(true);
                  setShowLineupsFocus(false);
                  setLineupsTab("lineups");
                  return;
                }
                setTaxonomyOpen(false);
                setShowLineupsFocus(true);
                setLineupsTab("lineups");
                setShowLineupSection(true);
                setTimeout(() => document.getElementById("pwg-lineups-dock")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
              },
            }] : []),
            {
              key: "myplays",
              icon: "myplays",
              label: guruListFilter === "my-list" ? "All Picks" : "My Plays",
              active: guruListFilter === "my-list",
              onClick: () => {
                setMobileProfileOpen(false);
                setGuruListFilter((v) => (v === "my-list" ? "all" : "my-list"));
                setTaxonomyOpen(false);
                setShowLineupsFocus(false);
                setShowLineupSection(false);
              },
            },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              className="pwgMobileNavButton"
              onClick={item.onClick}
              aria-label={item.ariaLabel || item.label}
              aria-pressed={item.active}
              style={{
                minWidth: 0,
                border: "none",
                borderRadius: 12,
                padding: "7px 4px",
                background: item.active ? (darkMode ? "#1e3a5f" : "#e0f2fe") : "transparent",
                color: item.active ? (darkMode ? "#7dd3fc" : "#0369a1") : (darkMode ? "#cbd5e1" : "#475569"),
                fontWeight: 850,
                fontSize: 11,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              <span className="pwg-mobile-nav-icon-wrap">
                <NavigationIcon icon={item.icon} />
              </span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{item.label}</span>
            </button>
          ))}
        </nav>
      )}

    {isNarrow && mobileProfileOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Profile"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 8000,
          background: darkMode ? "#0b1220" : "#f8fafc",
          color: darkMode ? "#f8fafc" : "#0f172a",
          display: "flex",
          flexDirection: "column",
          paddingBottom: "calc(76px + env(safe-area-inset-bottom))",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderBottom: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid #e2e8f0" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase", color: darkMode ? "#94a3b8" : "#64748b" }}>PWG</div>
            <div style={{ fontSize: 22, fontWeight: 950 }}>Profile</div>
          </div>
          <button type="button" onClick={() => setMobileProfileOpen(false)} style={{ width: 40, height: 40, borderRadius: 999, border: "1px solid #cbd5e1", background: darkMode ? "#111827" : "#fff", color: "inherit", fontWeight: 950, fontSize: 18 }}>×</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, borderRadius: 16, background: darkMode ? "#111827" : "#fff", border: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid #e2e8f0", marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", background: "#0ea5e9", color: "#fff", fontSize: 20, fontWeight: 950 }}>
              {(user?.displayName || user?.email || "U").charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 950, fontSize: 17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.displayName || user?.email?.split("@")[0] || "User"}</div>
              <div style={{ fontSize: 13, opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email || ""}</div>
              <div style={{ marginTop: 4, fontSize: 12, fontWeight: 850, color: entitlement.entitled ? "#16a34a" : "#f59e0b" }}>{entitlement.isAdmin ? "Admin access" : isSubscribed ? "Premium subscription" : "Free plan"}</div>
            </div>
          </div>

          {[
            ...(!entitlement.isAdmin ? [{
              label: entitlement.isStripePremium ? "Manage Subscription" : entitlement.isPremium ? "Premium Access" : "Subscribe",
              icon: "💳",
              action: entitlement.isStripePremium ? handleBilling : handleSubscribe,
            }] : []),
            { label: discordLabel, icon: "💬", action: handleDiscordConnect,},
            { label: "App Settings", icon: "⚙️", action: () => { setMobileProfileOpen(false); setMobileSettingsOpen(true); } },
            { label: "About PlaysWithGuru", icon: "ℹ️", action: () => { setMobileProfileOpen(false); window.requestAnimationFrame(openAbout); } },
            { label: "Help & Support", icon: "❓", action: () => { window.location.href = "mailto:playswithguru@gmail.com?subject=PlaysWithGuru%20Support"; } },
          ].map((item) => (
            <button key={item.label} type="button" onClick={item.action} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "15px 14px", marginBottom: 10, borderRadius: 14, border: darkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid #e2e8f0", background: darkMode ? "#111827" : "#fff", color: "inherit", fontWeight: 850, textAlign: "left", cursor: "pointer" }}>
              <span style={{ fontSize: 19 }}>{item.icon}</span><span>{item.label}</span><span style={{ marginLeft: "auto", opacity: 0.5 }}>›</span>
            </button>
          ))}

          <div style={{ padding: "4px 0 10px" }}>
            <InstallPWAButton className="btn-secondary" />
          </div>

          <button type="button" onClick={handleLogout} style={{ width: "100%", padding: "15px 14px", borderRadius: 14, border: "1px solid #fecaca", background: darkMode ? "rgba(127,29,29,0.28)" : "#fff1f2", color: darkMode ? "#fecaca" : "#be123c", fontWeight: 900, cursor: "pointer" }}>Logout</button>
        </div>
      </div>
    )}

    {mobileSettingsOpen && (
      <SettingsDrawer open={mobileSettingsOpen} onClose={() => setMobileSettingsOpen(false)} />
    )}

    </div>
    </div>
    

    {/* ===================== EDGE ANALYZER MODAL (Phase 1) ===================== */}
    {edgeAnalyzerOpen && (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10000,
          background: "rgba(0,0,0,0.55)",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          padding: isNarrow ? "8px" : "16px",
        }}
        onClick={() => {
          setEdgeAnalyzerOpen(false);
          setEdgeAnalyzerStatus("");
        }}
      >
        <div
          style={{
            width: "min(760px, 100%)",
            background: "#ffffff",
            borderRadius: 16,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            maxHeight: isNarrow ? "calc(100vh - 16px)" : "calc(100vh - 32px)",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isNarrow ? "column" : "row",
              alignItems: isNarrow ? "stretch" : "center",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 950 }}>
              Δ Edge Analyzer
              <span style={{ fontWeight: 800, color: "#6b7280", marginLeft: 10, fontSize: 13 }}>
                Model vs Line
              </span>
            </div>
            <button
              onClick={() => {
                setEdgeAnalyzerOpen(false);
                setEdgeAnalyzerStatus("");
              }}
              style={{
                padding: "8px 12px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              ✕ Close
            </button>
          </div>

          <div style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 160px", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#6b7280", marginBottom: 6 }}>Player</div>
                <input
                  value={edgeAnalyzerPlayer}
                  onChange={(e) => setEdgeAnalyzerPlayer(e.target.value)}
                  placeholder="Type a player name"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    fontWeight: 800,
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#6b7280", marginBottom: 6 }}>Prop Type</div>
                <select
                    value={edgeAnalyzerPropType}
                    onChange={(e) => setEdgeAnalyzerPropType(e.target.value)}
                    style={{
                      width: "100%",
                      border: "1px solid rgba(148,163,184,0.55)",
                      borderRadius: 12,
                      padding: "0.75rem 0.9rem",
                      fontSize: 14,
                      outline: "none",
                      background: "#fff",
                      color: "#111827",
                      fontWeight: 700,
                    }}
                  >
                    <option value="">Select prop type…</option>
                    {allPropTypes.map((pt) => (
                      <option key={pt} value={pt}>
                        {pt}
                      </option>
                    ))}
                  </select>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#6b7280", marginBottom: 6 }}>Line</div>
                <input
                  value={edgeAnalyzerLine}
                  onChange={(e) => setEdgeAnalyzerLine(e.target.value)}
                  placeholder="54.5"
                  inputMode="decimal"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    fontWeight: 900,
                  }}
                />
              </div>
            </div>

            {edgeAnalyzerStatus ? (
              <div style={{ marginTop: 10, color: "#991b1b", fontWeight: 800, fontSize: 13 }}>
                {edgeAnalyzerStatus}
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setEdgeAnalyzerOpen(false);
                  setEdgeAnalyzerStatus("");
                }}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  const pool = Array.isArray(filteredProps) ? filteredProps : [];

                  const pName = String(edgeAnalyzerPlayer || "").trim().toLowerCase();
                  const pProp = String(edgeAnalyzerPropType || "").trim().toLowerCase();
                  const pLine = Number(edgeAnalyzerLine);

                  if (!pName || !pProp || !Number.isFinite(pLine)) {
                    setEdgeAnalyzerStatus("Enter Player, Prop Type, and a valid Line.");
                    return;
                  }

                  const match = pool.find((r) => {
                    const nm = String(r.Player || r.player || "").trim().toLowerCase();
                    const pr = String(r["Prop Type"] || r.PropType || r.propType || r.Prop || "").trim().toLowerCase();
                    return nm === pName && pr === pProp;
                  });

                  if (!match) {
                    setEdgeAnalyzerStatus("No match found in the current filtered pool. Try a different Player/Prop (or loosen filters)." );
                    return;
                  }

                  const idx = pool.indexOf(match);
                  const patched = {
                    ...match,
                    "Prop Value": pLine,
                    PropValue: pLine,
                    propValue: pLine,
                    Line: pLine,
                    __EdgeAnalyzer: true,
                    __OriginalLine: match["Prop Value"] ?? match.PropValue ?? match.propValue ?? match.Line,
                  };

                  setGridDetailsPool(pool);
                  setGridDetailsIndex(idx >= 0 ? idx : 0);
                  setGridDetailsRow(patched);
                  setGridDetailsPeriod(DEFAULT_DETAILS_PERIOD);
                  setGridDetailsOpen(true);
                  setEdgeAnalyzerOpen(false);
                  setEdgeAnalyzerStatus("");
                }}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(16,185,129,0.45)",
                  background: "rgba(16,185,129,0.10)",
                  fontWeight: 950,
                  color: "#065f46",
                  cursor: "pointer",
                }}
              >
                Analyze → Open Details
              </button>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280", fontWeight: 700 }}>
              Tip: this uses your current filtered pool so results match what you're looking at.
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ===================== GRID DETAILS MODAL ===================== */}
    {gridDetailsOpen && gridDetailsRow && (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "rgba(0,0,0,0.55)",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          padding: "16px",
        }}
        onClick={() => {
          closeGridDetails();
        }}
      >
        <div
          ref={gridDetailsDialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={`Details for ${gridDetailsRow.Player || gridDetailsRow.player || "player"}`}
          tabIndex={-1}
          style={{
            width: "min(980px, 100%)",
            background: "#ffffff",
            borderRadius: 16,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>
                {(gridDetailsRow.Player || gridDetailsRow.player || "Player")}{" "}
                {!isRegularDetailsExperience && (
                  <span style={{ fontWeight: 800, color: "#6b7280" }}>
                    • {deriveTierFromRow(gridDetailsRow)}
                  </span>
                )}
              </div>
              <div style={{ color: "#6b7280", fontWeight: 650, fontSize: 13, overflowWrap: "anywhere" }}>
                {compactCustomerMatchup(
                  gridDetailsRow,
                  (gridDetailsRow["Game Start Timestamp"] || gridDetailsRow["Start Time"] || gridDetailsRow.startTime || gridDetailsRow.GameTime)
                    ? formatLocalEventTime(gridDetailsRow, activeSport)
                    : ""
                )}
              </div>
              <div className={`pwg-details-direction is-${detailDirectionMeta.direction.toLowerCase()}`}>
                {activeSport === "NHL" ? (gridDetailsRow.Tag || "COIN TOSS") : detailDirectionMeta.direction === "OVER" ? "▲ OVER" : detailDirectionMeta.direction === "UNDER" ? "▼ UNDER" : "— NEUTRAL"}
              </div>
              {activeSport === "NHL" && nhlEvidenceLabel(gridDetailsRow) && (
                <div style={{ color: "#475569", fontSize: 12, fontWeight: 800 }}>{nhlEvidenceLabel(gridDetailsRow)}</div>
              )}
              {activeSport === "NHL" && nhlAnalyticsSummary(gridDetailsRow) && (
                <div data-testid="nhl-analytics-summary" style={{ color: "#334155", fontSize: 13, fontWeight: 900 }}>{nhlAnalyticsSummary(gridDetailsRow)}</div>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: isNarrow ? "space-between" : "flex-end" }}>
              {isBaseballSport && isAdminUser && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleMyGuru(gridDetailsRow);
                  }}
                  aria-pressed={isInMyList(gridDetailsRow)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 12,
                    border: "1px solid #CBD5E1",
                    background: isInMyList(gridDetailsRow) ? "#FFF7E6" : "#F8FAFC",
                    color: "#111827",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  {isInMyList(gridDetailsRow) ? "✓ In My Plays" : "☆ Add to My Plays"}
                </button>
              )}
              <button
                disabled={!canPrev}
                onClick={() => goToIndex(gridDetailsIndex - 1)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  background: canPrev ? "#fff" : "#f3f4f6",
                  fontWeight: 900,
                  cursor: canPrev ? "pointer" : "not-allowed",
                }}
              >
                ← Prev
              </button>

              {/* ✅ ADD THIS */}
              {Array.isArray(gridDetailsPool) && gridDetailsPool.length > 0 && gridDetailsIndex >= 0 ? (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    color: "#6b7280",
                    minWidth: 84,
                    textAlign: "center",
                    userSelect: "none",
                  }}
                  title="Position in the current filtered pool"
                >
                  {gridDetailsIndex + 1} / {gridDetailsPool.length}
                </div>
              ) : null}

              <button
                disabled={!canNext}
                onClick={() => goToIndex(gridDetailsIndex + 1)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  background: canNext ? "#fff" : "#f3f4f6",
                  fontWeight: 900,
                  cursor: canNext ? "pointer" : "not-allowed",
                }}
              >
                Next →
              </button>


              <button
                onClick={closeGridDetails}
                style={{
                  padding: "8px 12px",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                ✕ Close
              </button>
          </div>

          </div>

          <DetailsPeriodSelector
            period={normalizeDetailsPeriod(gridDetailsPeriod, detailsPeriodOptions(gridDetailsRow, activeSport, gridDetailsPool))}
            onChange={(nextPeriod) => {
              setGridDetailsPeriod(nextPeriod);
            }}
            options={activeSport === "WNBA"
              ? [{ key: DETAILS_PERIODS.FULL_GAME, label: "FULL GAME", authoritative: true }]
              : detailsPeriodOptions(gridDetailsRow, activeSport, gridDetailsPool)}
            darkMode={darkMode}
          />

          {(gridDetailsLoading || gridDetailsError) && (
            <MlbDetailLoadingState
              error={gridDetailsError}
              onRetry={() => selectGridDetail(gridDetailsCompactRow || gridDetailsRow, { pool: gridDetailsPool, index: gridDetailsIndex })}
            />
          )}
          <div style={{ display: gridDetailsLoading || gridDetailsError ? "none" : "contents" }}>

          {isWnbaAnalysisBlocked(gridDetailsRow, activeSport) ? (
            <div data-testid="details-analysis-blocked" style={{ margin: 16, padding: 12, borderRadius: 12, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#991B1B", fontWeight: 900 }}>
              Research only — {analysisBlockLabel(gridDetailsRow)}. No recommendation is available.
            </div>
          ) : <CustomerDetailsGuruBanner
            row={gridDetailsRow}
            saved={isInMyList(gridDetailsRow)}
            onToggle={() => toggleMyGuru(gridDetailsRow)}
            tagColorMap={tagColorMap}
            darkMode={darkMode}
            narrow={isNarrow}
          />}

          {/\(combo\)/i.test(String(gridDetailsRow?.["Prop Type"] ?? "")) && (
            <WnbaComboDetails row={gridDetailsRow} isAdmin={isAdminUser} darkMode={darkMode} narrow={isNarrow} />
          )}

          {normalizeDetailsPeriod(gridDetailsPeriod, detailsPeriodOptions(gridDetailsRow, activeSport, gridDetailsPool)) !== DETAILS_PERIODS.FULL_GAME ? (
            <PeriodUnavailable period={gridDetailsPeriod} reason={gridDetailsRow?.["Analysis Status"] || gridDetailsRow?.["Reconciliation Status"]} propType={gridDetailsRow?.["Prop Type"]} darkMode={darkMode} />
          ) : (
          <>
          {isSoccerDetailsExperience && isAdminUser ? (
            <SoccerIntelligenceDetails row={gridDetailsRow} showInternalStrength={isAdminUser} isNarrow={isNarrow} />
          ) : isSoccerDetailsExperience ? (
            <SoccerSubscriberDetails row={gridDetailsRow} />
          ) : isNFLDetailsExperience && isAdminUser ? (
            <NFLIntelligenceDetails row={gridDetailsRow} isNarrow={isNarrow} />
          ) : isNFLDetailsExperience ? (
            <NFLSubscriberDetails row={gridDetailsRow} />
          ) : isCFBDetailsExperience && isAdminUser ? (
            <NFLIntelligenceDetails row={gridDetailsRow} isNarrow={isNarrow} />
          ) : isCFBDetailsExperience ? (
            <NFLSubscriberDetails row={gridDetailsRow} />
          ) : String(activeSport || gridDetailsRow?.Sport || "").toUpperCase() === "NHL" ? (
            <NhlCustomerDetails row={gridDetailsRow} isAdministrator={isAdminUser} />
          ) : /\(combo\)/i.test(String(gridDetailsRow?.["Prop Type"] ?? "")) ? null : isRegularDetailsExperience ? (() => {
            const propType = gridDetailsRow["Prop Type"] || gridDetailsRow.PropType || gridDetailsRow.propType || "";
            const lineRaw = gridDetailsRow["Prop Value"] ?? gridDetailsRow.PropValue ?? gridDetailsRow.propValue ?? gridDetailsRow.Line;
            const line = Number(lineRaw);
            const historySourceRows = getL10Rows(gridDetailsRow, { max: 100, range: "L10" });
            const historyValue = (row) => getL10Value(row, propType, activeSport);
            const completedHistory = String(activeSport || "").toUpperCase() === "NBA"
              ? bestAvailableCompletedHistory(gridDetailsRow, gridDetailsPool, {
                getRows: (row) => getL10Rows(row, { max: 100, range: "L10" }),
                getValue: historyValue,
              })
              : selectCompletedHistory(historySourceRows, {
                getValue: historyValue,
                limit: 100,
                order: "newest-first",
              });
            const tableGames = completedHistory.slice(0, 10);
            const recentRows = tableGames.map((game) => game.row);
            const recentGamesForDisplay = completedHistory.slice(0, 5).reverse()
              .map((game, recentRank) => ({ ...game, recentRank }));
            const recentValues = recentGamesForDisplay.map((game) => game.value);
            const datePresentation = historyDatePresentation(recentGamesForDisplay, {
              sport: activeSport || gridDetailsRow?.Sport,
              referenceDate: gridDetailsRow?.GameDate || gridDetailsRow?.Slate || new Date(),
            });
            const recentGameTitle = (game, index) => {
              const parts = [];
              parts.push(formatHistoricalTableDate(game) || `Game ${index + 1}`);
              const matchup = game.row?.Matchup ?? game.row?.matchup;
              const opponent = game.row?.Opponent ?? game.row?.["Opponent Name"] ?? game.row?.Opp;
              const venue = game.row?.["Home/Away"] ?? game.row?.HomeAway ?? game.row?.homeAway;
              if (matchup) parts.push(String(matchup));
              else if (opponent) parts.push(`vs ${opponent}`);
              if (venue) parts.push(String(venue));
              parts.push(`${propType}: ${game.value}`);
              return parts.join(" • ");
            };
            const recentAverage = recentValues.length
              ? recentValues.reduce((sum, value) => sum + value, 0) / recentValues.length
              : null;
            const overCount = Number.isFinite(line)
              ? recentValues.filter((value) => value > line).length
              : null;
            const pitcherName = getOpposingPitcherName(gridDetailsRow, activeSport);
            const probability = pwgProbLabelForRow(gridDetailsRow);
            const risk = String(gridDetailsRow["Risk Tier"] ?? gridDetailsRow.Risk_Tier ?? "").trim();
            const detailsConfidenceLabel = getDisplayConfidenceLabel(
              getConfidenceValue(gridDetailsRow),
              activeSport
            );
            const availability = String(
              gridDetailsRow.lineup_status ??
              gridDetailsRow.Lineup_Status ??
              gridDetailsRow.availability_status ??
              ""
            ).trim();
            const saved = isInMyList(gridDetailsRow);

            const factStyle = {
              border: "1px solid #E2E8F0",
              borderRadius: 12,
              padding: "10px 12px",
              background: "#FFFFFF",
            };

            return (
              <div style={{ padding: 16, overflowY: "auto" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                    gap: 10,
                  }}
                >
                  <div style={factStyle}>
                    <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>Prop</div>
                    <div style={{ color: "#0F172A", fontSize: 17, fontWeight: 950 }}>
                      {propType} {Number.isFinite(line) ? lineRaw : String(lineRaw ?? "")}
                    </div>
                  </div>
                  {probability?.text && (
                    <div style={factStyle}>
                      <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>
                        {getPublicModelLabel("PWG Probability", {
                          usePublicCopy: usesPublicEstimateCopy,
                          isAdmin: isAdminUser,
                        })}
                      </div>
                      <div style={{ color: "#0F172A", fontSize: 17, fontWeight: 950 }}>{probability.text}</div>
                    </div>
                  )}
                  {risk && (
                    <div style={factStyle}>
                      <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>Risk</div>
                      <div style={{ color: "#0F172A", fontSize: 17, fontWeight: 950 }}>{risk}</div>
                    </div>
                  )}
                  {detailsConfidenceLabel && detailsConfidenceLabel !== "Unknown" && (
                    <div style={factStyle}>
                      <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>
                        {getPublicModelLabel("Confidence", {
                          usePublicCopy: usesPublicEstimateCopy,
                          isAdmin: isAdminUser,
                        })}
                      </div>
                      <div style={{ color: "#0F172A", fontSize: 17, fontWeight: 950 }}>
                        {detailsConfidenceLabel}
                      </div>
                    </div>
                  )}
                </div>

                {(pitcherName || availability) && (
                  <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
                    {pitcherName && (
                      <div style={{ color: "#334155", fontWeight: 850 }}>Facing: {pitcherName}</div>
                    )}
                    {availability && (
                      <div style={{ color: "#64748B", fontSize: 13, fontWeight: 750 }}>
                        Status: {availability}
                      </div>
                    )}
                  </div>
                )}

                <div
                  style={{
                    marginTop: 16,
                    border: "1px solid #E2E8F0",
                    borderRadius: 14,
                    padding: 14,
                    background: "#F8FAFC",
                  }}
                >
                  {recentValues.length ? (
                    <>
                      {datePresentation.seasonLabel && <div data-testid="history-season-indicator" style={{ color: "#64748B", fontSize: 12, fontWeight: 800, marginBottom: 8 }}>{datePresentation.seasonLabel}</div>}
                      <HistoricalPerformanceChart
                        games={recentGamesForDisplay.map((game) => ({
                          key: `regular-detail-l5-${game.recentRank}`,
                          value: game.value,
                          dateLabel: formatHistoryDate(game, { includeYear: datePresentation.includeYear }),
                          opponentLabel: game.row?.Opponent ?? game.row?.["Opponent Name"] ?? game.row?.Opp ?? "—",
                          title: recentGameTitle(game, game.recentRank),
                        }))}
                        line={line}
                        range="L5"
                        period={normalizeDetailsPeriod(gridDetailsPeriod, detailsPeriodOptions(gridDetailsRow, activeSport, gridDetailsPool))}
                        formatValue={(value) => formatNbaDetailNumber(value)}
                        darkMode={darkMode}
                      />
                      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 14, color: "#475569", fontSize: 13, fontWeight: 800 }}>
                        {overCount !== null && <span>Over line: {overCount} of {recentValues.length}</span>}
                        {Number.isFinite(recentAverage) && <span>Average: {formatNbaDetailNumber(recentAverage)}</span>}
                      </div>
                    </>
                  ) : (
                    <div style={{ marginTop: 8, color: "#64748B", fontSize: 13, fontWeight: 700 }}>
                      Recent-game data unavailable.
                    </div>
                  )}
                </div>

                {String(activeSport || gridDetailsRow?.Sport || "").toUpperCase() === "NBA" && (
                  <>
                    <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 10 }}>
                      {[
                        ["Season", gridDetailsRow.Season_Avg],
                        ["L5", gridDetailsRow.Last5_Avg],
                        ["L10", gridDetailsRow.Last10_Avg],
                        ["H2H", gridDetailsRow.Opp_L10_Avg],
                        ["Minutes", gridDetailsRow.mpg],
                        ["Minutes Trend", gridDetailsRow.min_trend],
                        ["DvP", gridDetailsRow["DvP Evidence Status"]],
                        ["DvP Rank", gridDetailsRow.Opp_DvP_Rank == null ? null : `#${gridDetailsRow.Opp_DvP_Rank}`],
                        ["Rest / B2B", nbaRestB2bLabel(gridDetailsRow)],
                      ].filter(([, value]) => value !== null && value !== undefined && value !== "").map(([label, value]) => (
                        <div key={label} style={factStyle}>
                          <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>{label}</div>
                          <div style={{ color: "#0F172A", fontSize: 15, fontWeight: 950 }}>{typeof value === "number" ? formatNbaDetailNumber(value) : String(value)}</div>
                        </div>
                      ))}
                    </div>
                    {!isAdminUser && <HistoricalGamesDisclosure defaultOpen={!isNarrow} resetKey={`${gridDetailsRow.Player || ""}|${propType}|${lineRaw}`}>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead><tr>{["Date", "Opponent", "Actual", "Minutes"].map((label) => <th key={label} style={{ padding: "7px 8px", textAlign: "left", borderBottom: "1px solid #E2E8F0" }}>{label}</th>)}</tr></thead>
                          <tbody>{recentRows.map((game, index) => {
                            const actual = getL10Value(game, propType, activeSport);
                            return <tr key={`nba-history-${game?.GAME_ID || game?.GameID || index}`}>
                              <td style={{ padding: "7px 8px", borderBottom: "1px solid #F1F5F9" }}>{formatHistoricalTableDate(game)}</td>
                              <td style={{ padding: "7px 8px", borderBottom: "1px solid #F1F5F9" }}>{game?.Opponent || game?.Opp || "—"}</td>
                              <td style={{ padding: "7px 8px", borderBottom: "1px solid #F1F5F9", fontWeight: 900 }}>{Number.isFinite(actual) ? actual : "—"}</td>
                              <td style={{ padding: "7px 8px", borderBottom: "1px solid #F1F5F9" }}>{game?.Minutes ?? game?.MIN ?? "—"}</td>
                            </tr>;
                          })}</tbody>
                        </table>
                      </div>
                    </HistoricalGamesDisclosure>}
                    {canRenderNbaAdminResearch(isAdminUser) && <NBAAdminResearchWorkbench
                      row={gridDetailsRow}
                      getRows={getL10Rows}
                      getValue={getL10Value}
                      isNarrow={isNarrow}
                      renderGraph={({ rows, metric, line: researchLine }) => {
                        const graphPoints = [...rows].map((game) => ({
                          date: game?.Date || game?.GameDate || "",
                          matchup: game?.Matchup || (game?.Opponent ? `vs ${game.Opponent}` : ""),
                          value: getL10Value(game, propType, "NBA"),
                          opportunity: observedNumber(game?.Minutes ?? game?.MIN),
                        })).filter((point) => Number.isFinite(point.value)).reverse();
                        return graphPoints.length ? <Sparkline
                          points={graphPoints}
                          lineValue={researchLine}
                          height={150}
                          chartRange="L10"
                          showControls={false}
                          controlledMetricMode={metric === "MINUTES" ? "OPPORTUNITY" : metric === "STAT_MINUTES" ? "BOTH" : "PROP"}
                          opportunityLabel="Minutes"
                          enableCombinedMode
                          auditMode
                          statLabel={propType || "Stat"}
                        /> : <div>Historical evidence unavailable for this view.</div>;
                      }}
                    />}
                  </>
                )}

                <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleMyGuru(gridDetailsRow);
                    }}
                    aria-pressed={saved}
                    style={{
                      border: "1px solid #CBD5E1",
                      borderRadius: 999,
                      padding: "9px 14px",
                      background: saved ? "#FFF7E6" : "#F8FAFC",
                      color: "#111827",
                      cursor: "pointer",
                      fontWeight: 900,
                    }}
                  >
                    {saved ? "✓ In My Plays" : "☆ Add to My Plays"}
                  </button>
                </div>
              </div>
            );
          })() : (
          <div style={{ padding: 16, overflowY: "auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginBottom: 12,
              }}
            >
              {[
                { k: "Prop", v: gridDetailsRow["Prop Type"] || gridDetailsRow.PropType || gridDetailsRow.propType || "" },
                { k: "Line", v: gridDetailsRow["Prop Value"] || gridDetailsRow.PropValue || gridDetailsRow.propValue || gridDetailsRow.Line || "" },
                ...(isBacktestSportKey(activeSport || gridDetailsRow?.Sport) ? [
                  { k: "Actual", v: (() => { const a = getActualValue(gridDetailsRow); const o = getActualOutcome(gridDetailsRow); return `${Number.isFinite(a) ? a : "—"}${o ? ` ${o}` : ""}`; })() },
                  { k: "Result", v: (() => { const r = getBacktestResult(gridDetailsRow); const t = getBacktestResultTheme(r); return r ? `${t.icon} ${t.label}` : "—"; })() },
                ] : []),
                { k: "Benchmark", v: fmtNum(getProjValue(gridDetailsRow), 2) },
                {
                  k: "Δ Edge",
                  v: (() => {
                    const d = getDeltaValue(gridDetailsRow);
                    return Number.isFinite(d) ? `${d >= 0 ? "+" : ""}${d.toFixed(2)}` : "—";
                  })(),
                },
                {
                  k: "Line Tax",
                  v:
                    gridDetailsRow["Line_Tax_Tier"] ||
                    gridDetailsRow["Line Tax Tier"] ||
                    gridDetailsRow["Line_Tax"] ||
                    gridDetailsRow.LineTax ||
                    gridDetailsRow.lineTax ||
                    "",
                },
                { k: "Tag", v: gridDetailsRow.Tag || gridDetailsRow.tag || "" },
              ].map((item) => {
                const isEdge = item.k === "Δ Edge";
                const isBacktestResultCard = item.k === "Result";
                const backtestTheme = isBacktestResultCard ? getBacktestResultTheme(getBacktestResult(gridDetailsRow)) : null;
                const d = isEdge ? getDeltaValue(gridDetailsRow) : null;
                const edgeSign = Number.isFinite(d) ? (d > 0 ? "pos" : d < 0 ? "neg" : "zero") : "na";

                // Premium, quiet tints
                const edgeTheme =
                  edgeSign === "pos"
                    ? { bg: "rgba(22, 163, 74, 0.08)", border: "rgba(22, 163, 74, 0.45)", label: "#166534", value: "#14532d" }
                    : edgeSign === "neg"
                    ? { bg: "rgba(220, 38, 38, 0.08)", border: "rgba(220, 38, 38, 0.45)", label: "#991b1b", value: "#7f1d1d" }
                    : { bg: "rgba(17, 24, 39, 0.04)", border: "rgba(17, 24, 39, 0.18)", label: "#6b7280", value: "#111827" };

                return (
                  <div
                    key={item.k}
                    style={{
                      border: `1px solid ${isEdge ? edgeTheme.border : isBacktestResultCard ? backtestTheme.border : "#e5e7eb"}`,
                      borderRadius: 14,
                      padding: isEdge ? "12px 14px" : "10px 12px",
                      background: isEdge ? edgeTheme.bg : isBacktestResultCard ? backtestTheme.bg : "#ffffff",
                      boxShadow: isEdge ? "0 8px 20px rgba(17, 24, 39, 0.08)" : "none",
                      transform: isEdge ? "translateY(-1px)" : "none",
                    }}
                  >
                    <div
                      style={{
                        color: isEdge ? edgeTheme.label : isBacktestResultCard ? backtestTheme.color : "#6b7280",
                        fontWeight: 900,
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {item.k}
                      {isEdge && (
                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: 11,
                            fontWeight: 900,
                            padding: "3px 8px",
                            borderRadius: 999,
                            background: "rgba(255,255,255,0.75)",
                            border: `1px solid ${edgeTheme.border}`,
                            color: edgeTheme.label,
                            lineHeight: 1,
                            userSelect: "none",
                          }}
                          title="Difference between PWG benchmark and the entered line"
                        >
                          Model vs Line
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        fontWeight: 950,
                        fontSize: isEdge ? 22 : 16,
                        color: isEdge ? edgeTheme.value : isBacktestResultCard ? backtestTheme.color : "#111827",
                        letterSpacing: isEdge ? "-0.02em" : "normal",
                        marginTop: isEdge ? 2 : 0,
                      }}
                    >
                      {String(item.v ?? "")}
                    </div>
                  </div>
                );
              })}

            </div>


            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, marginBottom: 6 }}>
              <span style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid rgba(0,0,0,0.08)", background: "rgba(0,0,0,0.03)", fontWeight: 700, fontSize: 13 }}>
                Guru Score: {fmtNum(getGuruScoreValue(gridDetailsRow), 2)}
              </span>
              {supportsDqs(gridDetailsRow?.Sport || activeSport) && (
                <span style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid rgba(0,0,0,0.08)", background: "rgba(0,0,0,0.03)", fontWeight: 700, fontSize: 13 }}>
                  DQS: {fmtNum(computeDQS(gridDetailsRow), 2)}
                </span>
              )}
              <span style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid rgba(0,0,0,0.08)", background: "rgba(0,0,0,0.03)", fontWeight: 700, fontSize: 13 }}>
                Guru Grade: {getGuruGradeValue(gridDetailsRow) || "—"}
              </span>
              <span style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid rgba(0,0,0,0.08)", background: "rgba(0,0,0,0.03)", fontWeight: 700, fontSize: 13 }}>
                Sel Strat: {getSelStratValue(gridDetailsRow) || "—"}
              </span>
              {(() => {
                const meta = getGuruAlertMeta(gridDetailsRow);
                if (!meta?.label) return null;

                return (
                  <span
                    title={meta.title || ""}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 999,
                      border: `2px solid ${meta.border}`,
                      background: meta.bg,
                      color: meta.color,
                      fontWeight: 800,
                      fontSize: 13,
                      minWidth: 120,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}
                  >
                    {meta.label}
                  </span>
                );
              })()}
            </div>


            {(() => {
              const missing = [];
              if (!Number.isFinite(getProjValue(gridDetailsRow))) missing.push("Proj");
              if (!Number.isFinite(getDeltaValue(gridDetailsRow))) missing.push("Δ");
              if (!getConfidenceValue(gridDetailsRow)) missing.push("Confidence");
              return missing.length ? (
                <div style={{ marginBottom: 8, fontSize: 12, opacity: 0.7 }}>
                  Missing data: {missing.join(", ")} (will display as “—”)
                </div>
              ) : null;
            })()}
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 14,
                background: "#f9fafb",
                fontWeight: 800,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              
            {/* NarrativeSummary (Excel: NarrativeSummary) */}
            {(() => {
              const ns =
                gridDetailsRow?.NarrativeSummary ||
                gridDetailsRow?.["Narrative Summary"] ||
                gridDetailsRow?.Narrative_Summary ||
                "";

              // Keep narrative consistent with FINAL tag (post-downgrade), and optionally annotate downgrades.
              const finalTag =
                gridDetailsRow?.Tag_Final ||
                gridDetailsRow?.["Tag Final"] ||
                gridDetailsRow?.Tag ||
                gridDetailsRow?.["Tag"] ||
                "";

              const downgradedFrom =
                gridDetailsRow?.["Tag Downgraded From"] ||
                gridDetailsRow?.Tag_Downgraded_From ||
                gridDetailsRow?.tag_downgraded_from ||
                null;

              const downgraded =
                gridDetailsRow?.Downgraded === true ||
                String(gridDetailsRow?.Downgraded || "").toLowerCase() === "true" ||
                !!downgradedFrom;

              const rfRaw =
                gridDetailsRow?.["Risk Flags"] ||
                gridDetailsRow?.RiskFlags ||
                gridDetailsRow?.riskFlags ||
                "";
              const rf = String(rfRaw || "")
                .split(/[|,]/)
                .map((s) => s.trim())
                .filter(Boolean);

              const nsDisplay = (() => {
                if (!ns) return "";
                if (!finalTag) return ns;

                // Replace leading tag phrase in narrative with finalTag (e.g., "MEGA SMASH —" -> "SMASH —")
                const replaced = String(ns).replace(
                  /^(MEGA\s+SMASH|SMASH|GOOD|LEAN\s+OVER|MEGA\s+FADE|FADE|LEAN\s+UNDER)\s+—/i,
                  `${finalTag} —`
                );

                if (!downgraded || !downgradedFrom) return replaced;

                const reasons = rf.length ? ` • ${rf.join(", ")}` : "";
                return `${replaced} (downgraded from ${downgradedFrom}${reasons})`;
              })();

              if (!ns) return null;

              const dirKey = getDirectionKey(gridDetailsRow);
              const theme = narrativeTheme(dirKey);

              // Detect direction conflicts between Tag vs SelectionStrategy.
              const selDir = String(
                gridDetailsRow?.SelectionStrategy ??
                  gridDetailsRow?.["SelectionStrategy"] ??
                  gridDetailsRow?.["Sel Strat"] ??
                  gridDetailsRow?.SelStrat ??
                  ""
              )
                .toUpperCase()
                .trim();
              const selKey = selDir.includes("UNDER")
                ? "UNDER"
                : selDir.includes("OVER")
                ? "OVER"
                : "NEUTRAL";
              const hasDirConflict =
                (dirKey === "OVER" && selKey === "UNDER") ||
                (dirKey === "UNDER" && selKey === "OVER");

              return (
                <div
                  style={{
                    marginTop: 10,
                    padding: "12px 14px",
                    border: `1px solid ${theme.border}`,
                    borderRadius: 14,
                    background: theme.bg,
                    position: "relative",
                    boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
                  }}
                >
                  {/* left rail */}
                  <div
                    style={{
                      position: "absolute",
                      left: 10,
                      top: 12,
                      bottom: 12,
                      width: 6,
                      borderRadius: 999,
                      background: theme.rail,
                      opacity: 0.9,
                    }}
                  />

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 10 }}>
                    <div style={{ fontWeight: 950, color: "#111827", fontSize: 18 }}>
                      Guru Insight (AI)
                    </div>

                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 900,
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: theme.pillBg,
                        color: theme.pillText,
                        border: `1px solid ${theme.border}`,
                        lineHeight: 1,
                      }}
                    >
                      {theme.label}
                    </span>

                    {hasDirConflict && (
                      <span
                        title="Tag direction conflicts with SelectionStrategy direction"
                        style={{
                          fontSize: 12,
                          fontWeight: 900,
                          padding: "4px 10px",
                          borderRadius: 999,
                          background: "#fff3cd",
                          color: "#7a4a00",
                          border: "1px solid #f0c36d",
                          lineHeight: 1,
                        }}
                      >
                        Conflict
                      </span>
                    )}

                    <div style={{ marginLeft: "auto", fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                      Analyst note
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 8,
                      marginLeft: 10,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                      color: "#111827",
                      fontWeight: 600,
                    }}
                  >
                    {nsDisplay || ns}
                    {hasDirConflict && (
                      <div style={{ marginTop: 6, fontWeight: 700 }}>
                        Note: Tag direction is <span style={{ fontWeight: 900 }}>{dirKey}</span> while SelectionStrategy leans{" "}
                        <span style={{ fontWeight: 900 }}>{selKey}</span>.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <br></br>
{/* Quick facts (collapsible) */}
{(() => {
  const quickFacts = buildQuickFactItems(gridDetailsRow);
  if (!quickFacts.length) return null;

  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        onClick={() => setQuickFactsOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 12px",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          background: "#fff",
          cursor: "pointer",
          fontWeight: 900,
          color: "#111827",
        }}
      >
        <span>Quick facts</span>
        <span style={{ fontWeight: 900, color: "#6b7280" }}>
          {quickFactsOpen ? "Hide" : "Show"}
        </span>
      </button>

      {quickFactsOpen && (
        <div
          style={{
            marginTop: 8,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid #f3f4f6",
            background: "#fafafa",
            color: "#374151",
            lineHeight: 1.45,
            fontWeight: 700,
            whiteSpace: "pre-wrap",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            {quickFacts.map((fact) => {
              const tone = fact.tone === "support" ? { border: "#86EFAC", color: "#166534" } : fact.tone === "caution" ? { border: "#FCA5A5", color: "#991B1B" } : { border: "#E2E8F0", color: "#334155" };
              return <div key={fact.label} style={{ border: `1px solid ${tone.border}`, borderRadius: 10, padding: "8px 10px", minWidth: 0 }}><div style={{ fontSize: 11, color: "#64748B", fontWeight: 800 }}>{fact.label}</div><div style={{ marginTop: 3, color: tone.color, fontWeight: 900, overflowWrap: "anywhere" }}>{fact.value}</div></div>;
            })}
          </div>
        </div>
      )}

      {/*  Visible for advanced players */}
    {showAdvancedFilters && (
        <>
      {/* -------- Context (collapsible) -------- */}
      <details
        open={isNarrow}
        style={{
          marginTop: 10,
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          padding: 12,
          background: "#fff",
        }}
      >
      <summary
        style={{
          fontSize: 14,        // smaller
          fontWeight: 600,     // medium, not bold
          opacity: 0.75,       // softer
          cursor: "pointer",
          letterSpacing: 0.2,
        }}
      >
        Context
      </summary>



        <div style={{ marginTop: 10 }}>
          {/* 👇 paste your full Context block here, BUT replace `p` with `gridDetailsRow` */}
          {/* Example: const b2bToday = Boolean(gridDetailsRow["b2b_today"]); etc */}
          <div
                        style={{
                          padding: "8px 10px",
                          fontSize: 11,
                          padding: "4px 8px",
                          borderRadius: 999,
                          borderRadius: 12,
                          background: darkMode ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
                          border: darkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
                          marginTop: 2,
                        }}
                      >
                        {/* existing chips row + risk badges */}
                                              {/* Analytics chips (only if user has access) */}
                      {canUseFeature && (() => {
                        // Only render if NBA is active OR the row actually has NBA context fields
                        const hasNBAKeys =
                          "b2b_today" in gridDetailsRow || "b2b_tomorrow" in gridDetailsRow || "mpg" in gridDetailsRow ||
                          "min_trend" in gridDetailsRow || "ft_rate_trend" in gridDetailsRow || "shot_vol_trend" in gridDetailsRow ||
                          "fouls_trend" in gridDetailsRow || "reb_trend" in gridDetailsRow || "oreb_trend" in gridDetailsRow ||
                          "pts_trend" in gridDetailsRow || "stocks_trend" in gridDetailsRow || "blowout_risk" in gridDetailsRow;

                        if (activeSport !== "NBA" && !hasNBAKeys) return null;

                        // ---- Styles ----
                        const rowStyle = {
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "6px",
                          margin: ".4rem 0",
                        };

                        // ---- Helpers ----
                        const toNum = (v) => {
                          if (v === null || v === undefined) return null;
                          const n = Number(String(v).replace(/[^\d.+-eE]/g, ""));
                          return Number.isFinite(n) ? n : null;
                        };

                        // Numeric (e.g., +1.2), or direction labels ("up"/"down"/"flat")
                        const parseTrend = (v) => {
                          if (v === null || v === undefined) return { kind: "none" };
                          const s = String(v).trim().toLowerCase();
                          const n = toNum(v);
                          if (n !== null) return { kind: "numeric", value: n };
                          if (s === "up") return { kind: "dir", dir: "up" };
                          if (s === "down") return { kind: "dir", dir: "down" };
                          if (s === "flat" || s === "even" || s === "neutral") return { kind: "dir", dir: "flat" };
                          return { kind: "none" };
                        };

                        // ===== Meaning-aware coloring =====
                        const POLARITY = {
                          min_trend:      "up_good",   // more minutes = good
                          shot_vol_trend: "up_good",
                          ft_rate_trend:  "up_good",
                          fouls_trend:    "down_good", // fewer fouls = good
                          reb_trend:      "up_good",
                          oreb_trend:     "up_good",
                          pts_trend:      "up_good",
                          stocks_trend:   "up_good",
                        };
                        const EPS = { default: 0.2, fouls_trend: 0.15 };

                        const toneForStat = (num, statKey) => {
                          const eps = EPS[statKey] ?? EPS.default;
                          const pol = POLARITY[statKey] ?? "neutral";
                          if (!Number.isFinite(num)) return chipBase;
                        
                          if (pol === "up_good")   return num >  eps ? goodChip : (num < -eps ? warnChip : chipBase);
                          if (pol === "down_good") return num < -eps ? goodChip : (num >  eps ? warnChip : chipBase);
                          return Math.abs(num) > eps ? warnChip : chipBase;
                        };
                        

                        // Pretty trend pill w/ semantics-aware color
                        const trendChip = (label, raw, statKey) => {
                          const t = parseTrend(raw);
                          if (t.kind === "none") return null;
                          const eps = EPS[statKey] ?? EPS.default;

                          if (t.kind === "numeric") {
                            const num = t.value;
                            const arrow = num > eps ? "▲" : num < -eps ? "▼" : "•";
                            const signed = num > 0 ? `+${num.toFixed(1)}` : (num < 0 ? `−${Math.abs(num).toFixed(1)}` : "±0.0");
                            return <span style={toneForStat(num, statKey)}>{label} {arrow} {signed}</span>;
                          }

                          // Directional strings: color by meaning even w/o a number
                          if (t.kind === "dir") {
                            const pol = POLARITY[statKey] || "neutral";
                            const arrow = t.dir === "up" ? "▲" : t.dir === "down" ? "▼" : "•";
                            let tone = chipBase;
                            if (pol === "up_good")   tone = t.dir === "up" ? goodChip : (t.dir === "down" ? warnChip : chipBase);
                            else if (pol === "down_good") tone = t.dir === "down" ? goodChip : (t.dir === "up" ? warnChip : chipBase);
                            return <span style={tone}>{label} {arrow}</span>;
                          }
                          return null;
                        };

                        // B2B: No = good (green), Yes = risk (amber)
                        const boolChip = (label, on) => {
                          if (on === undefined || on === null) return null;
                          return <span style={on ? warnChip : goodChip}>{label}{on ? " • Yes" : " • No"}</span>;
                        };

                        const numChip = (label, v, digits = 1) => {
                          const n = toNum(v);
                          if (n === null) return null;
                          return <span style={chipBase}>{label} {n.toFixed(digits)}</span>;
                        };

                        // Blowout: string label OR numeric percent
                        const blowoutPill = (v) => {
                          if (v === null || v === undefined) return null;
                        
                          const raw = String(v).trim();
                          const s = raw.toLowerCase();
                          let tone = chipBase;
                          const label = "Blowout";
                        
                          // 1) Handle text labels first (low / moderate / high / none)
                          if (s.includes("low") || s === "none") {
                            tone = goodChip;
                            return <span style={tone}>{label} low</span>;
                          }
                          if (s.includes("mod")) {
                            tone = warnChip;
                            return <span style={tone}>{label} moderate</span>;
                          }
                          if (s.includes("high")) {
                            tone = riskChip;
                            return <span style={tone}>{label} high</span>;
                          }
                        
                          // 2) Otherwise, treat as a numeric percentage only if > 0
                          const pctNum = toNum(s.endsWith("%") ? s.slice(0, -1) : s);
                          if (pctNum !== null && pctNum > 0) {
                            let toneForPct = chipBase;
                            if (pctNum >= 20) toneForPct = riskChip;
                            else if (pctNum >= 10) toneForPct = warnChip;
                            else toneForPct = goodChip;
                        
                            const capped = Math.max(0, Math.min(100, pctNum));
                            return (
                              <span style={toneForPct}>
                                {label} {capped.toFixed(0)}%
                              </span>
                            );
                          }
                        
                          // 3) For 0 or anything unrecognized, show nothing (no 0% chip)
                          return null;
                        };
                        

                        // ---- Read fields from row p ----
                        const b2bToday     = Boolean(gridDetailsRow["b2b_today"]);
                        const b2bTomorrow  = Boolean(gridDetailsRow["b2b_tomorrow"]);
                        const mpg          = gridDetailsRow["mpg"];
                        const minTrend     = gridDetailsRow["min_trend"];
                        const ftRateTrend  = gridDetailsRow["ft_rate_trend"];
                        const shotVolTrend = gridDetailsRow["shot_vol_trend"];
                        const foulsTrend   = gridDetailsRow["fouls_trend"];
                        const rebTrend     = gridDetailsRow["reb_trend"];
                        const orebTrend    = gridDetailsRow["oreb_trend"];
                        const ptsTrend     = gridDetailsRow["pts_trend"];
                        const stocksTrend  = gridDetailsRow["stocks_trend"];
                        const blowoutRisk  = getBlowoutTierRaw(gridDetailsRow);

                        // NEW: minutes sample info
                        const minutesGamesSample = gridDetailsRow["minutes_games_sample"];
                        const minutesRecentMpg   = gridDetailsRow["minutes_recent_mpg"];
                        const minutesStd         = gridDetailsRow["minutes_std"];
                        const minutesFlag        = gridDetailsRow["minutes_flag"];       // "Tiny Sample" | "Small Sample" | "OK"
                        const lineupMinutesOk    = gridDetailsRow["lineup_minutes_ok"];  // boolean

                        // Show the row if *any* signal exists (numeric OR categorical)
                        const hasAnyNBA =
                          b2bToday || b2bTomorrow ||
                          toNum(mpg) !== null ||
                          [minTrend, ftRateTrend, shotVolTrend, foulsTrend, rebTrend, orebTrend, ptsTrend, stocksTrend]
                            .some(v => parseTrend(v).kind !== "none") ||
                          String(blowoutRisk ?? "").trim() ||
                          String(minutesFlag ?? "").trim() ||
                          String(gridDetailsRow?.["Risk Tier"] ?? gridDetailsRow?.RiskTier ?? gridDetailsRow?.riskTier ?? "").trim() ||
                          String(gridDetailsRow?.["Risk Flags"] ?? gridDetailsRow?.RiskFlags ?? gridDetailsRow?.riskFlags ?? "").trim() ||
                          (toNum(gridDetailsRow?.["Risk Score"] ?? gridDetailsRow?.RiskScore ?? gridDetailsRow?.riskScore) !== null);
                        if (!hasAnyNBA) return <ContextState available={false} darkMode={darkMode} />;

                        // Build risk badges
                        const riskBadges = [];

                        // Minutes trend (existing)
                        if (toNum(minTrend) !== null) {
                          const mt = toNum(minTrend);
                          if (mt <= -3.0) riskBadges.push(<span key="rb-min1" style={riskChip}>Minutes Squeeze</span>);
                          else if (mt <= -1.5) riskBadges.push(<span key="rb-min2" style={warnChip}>Minutes Down</span>);
                        }

                        // Foul trends (existing)
                        if (toNum(foulsTrend) !== null) {
                          const ft = toNum(foulsTrend);
                          if (ft >= 1.2) riskBadges.push(<span key="rb-foul1" style={riskChip}>Foul Risk ↑</span>);
                          else if (ft >= 0.7) riskBadges.push(<span key="rb-foul2" style={warnChip}>Fouls Rising</span>);
                        }

                        // NEW: minutes sample quality
                        if (minutesFlag === "Tiny Sample") {
                          riskBadges.push(
                            <span key="rb-min-sample-tiny" style={riskChip}>
                              Minutes Sample: Tiny{typeof minutesGamesSample === "number" ? ` (${minutesGamesSample} g)` : ""}
                            </span>
                          );
                        } else if (minutesFlag === "Small Sample") {
                          riskBadges.push(
                            <span key="rb-min-sample-small" style={warnChip}>
                              Minutes Sample: Small{typeof minutesGamesSample === "number" ? ` (${minutesGamesSample} g)` : ""}
                            </span>
                          );
                        }

                        // Optional: flag non-lineup-safe minutes
                        if (lineupMinutesOk === false) {
                          riskBadges.push(
                            <span key="rb-lineup-minutes" style={riskChip}>
                              Lineup Risk (minutes)
                            </span>
                          );
                        }

                        // Return a single root (fragment)
                        return (
                          <>
                            <div style={rowStyle} aria-label="Recent trends and context">
                              {/* Schedule / workload */}
                              {boolChip("B2B Today", b2bToday)}
                              {boolChip("B2B Tomorrow", b2bTomorrow)}
                              {numChip("MPG", mpg, 1)}

                              {/* Trends (meaning-aware colors) */}
                              {trendChip("Minutes Δ",   minTrend,     "min_trend")}
                              {trendChip("Shot Vol Δ",  shotVolTrend, "shot_vol_trend")}
                              {trendChip("FT Rate Δ",   ftRateTrend,  "ft_rate_trend")}
                              {trendChip("Fouls Δ",     foulsTrend,   "fouls_trend")}
                              {trendChip("Reb Δ",       rebTrend,     "reb_trend")}
                              {trendChip("OReb Δ",      orebTrend,    "oreb_trend")}
                              {trendChip("Pts Δ",       ptsTrend,     "pts_trend")}
                              {trendChip("Stocks Δ",    stocksTrend,  "stocks_trend")}
                              {blowoutPill(blowoutRisk)}
                            </div>

                            {/* Risk badges row (goes just below chips row) */}
                            {riskBadges.length > 0 && (
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: "4px" }}>
                                {riskBadges}
                              </div>
                            )}
                          </>
                        );

                      })()}
          </div>
        </div>
      </details>
    </> )}
    </div>
  );
})()}

          </div>


{/* -------- Last 10 Games (embedded so you don't have to switch to Card view) -------- */}
{(() => {
  const customerHistoryLimit = Number(gridDetailsRow?.__pwgCustomerHistoryLimit) || 0;
  const l10 = getL10Rows(gridDetailsRow, { max: customerHistoryLimit || 10, range: "L10" });
  const l20 = customerHistoryLimit
    ? []
    : getL10Rows(gridDetailsRow, { max: 20, range: "L20" });

  if ((!Array.isArray(l10) || l10.length === 0) && (!Array.isArray(l20) || l20.length === 0)) {
    return null;
  }

  const propHeader = gridDetailsRow["Prop Type"] || gridDetailsRow.PropType || gridDetailsRow.Prop || "";
  const _isMinutesProp = isMinutesLikePropType(propHeader);
  
  const activeRows =
    detailChartRange === "L20" && Array.isArray(l20) && l20.length > 0
      ? l20
      : l10;
  
  const isBaseball = ["MLB", "KBO"].includes(String(activeSport || "").toUpperCase());
  const detailPlayerType = getPlayerTypeValue(gridDetailsRow);
  const detailOppMeta = getOpportunityMetaForDetail(gridDetailsRow, activeSport);
  
  const showMinutesCol = !isBaseball;
  const showPACol = isBaseball && detailPlayerType === "BATTER";
  const showABCol = isBaseball && detailPlayerType === "BATTER";
  const showOutsCol = isBaseball && detailPlayerType === "PITCHER";

  const numericHeaderStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    textAlign: "center",
    fontWeight: 700,
    width: 110,
    minWidth: 110,
    whiteSpace: "nowrap",
  };

  const numericCellStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    textAlign: "center",
    width: 110,
    minWidth: 110,
    whiteSpace: "nowrap",
  };

  const thStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    fontWeight: 600,
  };

  return (
    <HistoricalGamesDisclosure
      defaultOpen={!isNarrow}
      resetKey={`${gridDetailsRow?.Player || ""}|${gridDetailsRow?.["Prop Type"] || ""}|${gridDetailsRow?.["Prop Value"] ?? ""}`}
      style={{ marginTop: 0, marginBottom: 12 }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontWeight: 950, fontSize: 16 }}>
          {customerHistoryLimit
            ? `Last ${customerHistoryLimit} Games`
            : detailChartRange === "L20" ? "Last 20 Games" : "Last 10 Games"}
        </div>
        <div style={{ color: "#6b7280", fontWeight: 800, fontSize: 12 }}>
        (value column = {
          (() => {
            const oppMeta = getOpportunityMetaForDetail(gridDetailsRow, activeSport);
            const detailPlayerType = getPlayerTypeValue(gridDetailsRow);

            if (String(activeSport || "").toUpperCase() === "NBA") {
              return shortPropHeader(propHeader);
            }

            if (["MLB", "KBO"].includes(String(activeSport || "").toUpperCase())) {
              if (detailPlayerType === "PITCHER") {
                return shortPropHeader(propHeader);
              }
              return shortPropHeader(propHeader);
            }

            return _isMinutesProp ? "MIN" : shortPropHeader(propHeader);
          })()
        })
        </div>
      </div>

      {/* ✅ CHART */}
      {(() => {
        const line = Number(gridDetailsRow["Prop Value"]);
        const propType = gridDetailsRow["Prop Type"] || gridDetailsRow.PropType || gridDetailsRow.Prop || "";
        const isMinutesProp = isMinutesLikePropType(propType);
        const detailPlayerType = getPlayerTypeValue(gridDetailsRow);


        const buildPoints = (rows) =>
        [...rows]
          .map((entry) => {
            const dateA = entry.Date || entry.GameDate || entry.date || "";
            const dateB = entry.DateB || entry.GameDateB || entry.dateB || "";
            const date = dateB ? `${dateA} | ${dateB}` : dateA;
      
            const opportunity =
              ["MLB", "KBO"].includes(String(activeSport || "").toUpperCase())
                ? getMLBOpportunityValue(entry, detailPlayerType)
                : getMinutesValue(entry, activeSport);
      
            const secondaryOpportunity =
              ["MLB", "KBO"].includes(String(activeSport || "").toUpperCase())
                ? getMLBSecondaryOpportunityValue(entry, detailPlayerType)
                : null;
      
            return {
              date,
              matchup: entry.Matchup || "",
              value: isMinutesProp
                ? getMinutesValue(entry, activeSport)
                : getL10Value(entry, propType, activeSport),
              opportunity,
              secondaryOpportunity,
            };
          })
          .filter((p) => Number.isFinite(p.value))
          .reverse();

        const normalL10Points = buildPoints(l10);
        const normalL20Points = buildPoints(l20);

        // combo fallback
        const comboPoints =
          (!normalL10Points.length && isComboPlayerName(gridDetailsRow?.Player))
            ? buildComboPoints({ pool: gridDetailsPool, comboRow: gridDetailsRow })
            : [];

        const l10Points = normalL10Points.length ? normalL10Points : comboPoints;
        const l20Points = normalL20Points.length ? normalL20Points : comboPoints;


        return (
          <Sparkline
            points={l10Points}
            last20Points={l20Points}
            lineValue={line}
            height={120}
            chartRange={customerHistoryLimit ? "L10" : detailChartRange}
            setChartRange={setDetailChartRange}
            showControls={!customerHistoryLimit}
            showBands={detailShowBands}
            setShowBands={setDetailShowBands}
            opportunityLabel={detailOppMeta.label}
          />
        );
      })()}

      {(renderPatternChip(gridDetailsRow) || renderGuruPickChip(gridDetailsRow)) && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {renderPatternChip(gridDetailsRow)}
          {renderGuruPickChip(gridDetailsRow)}
          {SHOW_GURU_PICK_REASON && gridDetailsRow?.Guru_Pick_Reason && (
            <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
              {gridDetailsRow.Guru_Pick_Reason}
            </span>
          )}
        </div>
      )}

      <div style={{ overflowX: "auto", marginTop: 10 }}>
        <table style={{ width: "100%", minWidth: 520, fontSize: "0.85rem", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              <th style={{ padding: "8px 6px", borderBottom: "1px solid #f3f4f6" }}>Date</th>
              <th style={{ padding: "8px 6px", borderBottom: "1px solid #f3f4f6" }}>Matchup</th>
              {showMinutesCol && <th style={numericHeaderStyle}>MIN</th>}
              {showPACol && <th style={numericHeaderStyle}>PA</th>}
              {showABCol && <th style={numericHeaderStyle}>AB</th>}
              {showOutsCol && <th style={numericHeaderStyle}>OUTS</th>}

              {!_isMinutesProp && (
                <th
                  style={numericHeaderStyle}
                  title={propHeader}
                >
                  {activeSport === "MLB" ? mlbHistoricalDisplayLabel(propHeader) : shortPropHeader(propHeader)}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {activeRows.map((entry, idx) => {
              const v = _isMinutesProp
                ? getMinutesValue(entry, activeSport)
                : getL10Value(entry, gridDetailsRow["Prop Type"], activeSport);

              const sty = rowResultStyle(v, gridDetailsRow);

              const dateA =
                entry.Date
                  ? new Date(entry.Date).toLocaleDateString()
                  : (entry.GameDate || entry.DateStr || entry.date || "");

              const dateB =
                entry.DateB
                  ? new Date(entry.DateB).toLocaleDateString()
                  : (entry.GameDateB || entry.DateStrB || entry.dateB || "");

              const dateCell = dateB ? `${dateA} | ${dateB}` : dateA;

              const matchupCell = (() => {
                if (activeSport === "WNBA" && typeof matchupCellWNBA === "function") {
                  return matchupCellWNBA(entry);
                }
                if (entry.Matchup) return entry.Matchup;
                const team = entry.Team || gridDetailsRow.Team || "";
                const opp = entry.Opponent || "";
                if (team && opp) {
                  const ha = String(entry["Home/Away"] || entry.HomeAway || "").toLowerCase();
                  const sym = ha === "away" || ha === "a" || ha === "@" ? "@" : "vs";
                  return `${team} ${sym} ${opp}`.trim();
                }
                return opp || "";
              })();

              const min = getMinutesValue(entry, activeSport);
              const paVal = getMLBOpportunityValue(entry, "BATTER");
              const abVal = getMLBSecondaryOpportunityValue(entry, "BATTER");
              const outsVal = getMLBOpportunityValue(entry, "PITCHER");

              const numericColStyle = {
                padding: "8px 6px",
                borderBottom: "1px solid #f3f4f6",
                textAlign: "center",
                width: 100,
                minWidth: 100,
                whiteSpace: "nowrap",
              };

              return (
                <tr key={idx} style={sty}>
                  <td
                    style={{
                      padding: "8px 6px",
                      borderBottom: "1px solid #f3f4f6",
                      whiteSpace: "pre-line",
                      lineHeight: 1.25,
                    }}
                  >
                    {dateCell}
                  </td>

                  <td
                    style={{
                      padding: "8px 6px",
                      borderBottom: "1px solid #f3f4f6",
                      whiteSpace: "pre-line",
                      lineHeight: 1.25,
                    }}
                  >
                    {matchupCell}
                  </td>

                  {showMinutesCol && (
                    <td style={numericColStyle}>
                      {Number.isFinite(min) ? min.toFixed(1) : ""}
                    </td>
                  )}

                  {showPACol && (
                    <td style={numericColStyle}>
                      {Number.isFinite(paVal) ? paVal : ""}
                    </td>
                  )}

                  {showABCol && (
                    <td style={numericColStyle}>
                      {Number.isFinite(abVal) ? abVal : ""}
                    </td>
                  )}

                  {showOutsCol && (
                    <td style={numericColStyle}>
                      {Number.isFinite(outsVal) ? outsVal : ""}
                    </td>
                  )}

                  {!_isMinutesProp && (
                    <td style={numericColStyle}>
                      {Number.isFinite(v) ? v : "Unavailable"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(() => {
        const line = Number(gridDetailsRow["Prop Value"]);
        let hit = 0, miss = 0, even = 0;

        activeRows.forEach((r) => {
          const v = _isMinutesProp ? getMinutesValue(r, activeSport) : getL10Value(r, gridDetailsRow["Prop Type"], activeSport);
          if (!Number.isFinite(v) || !Number.isFinite(line)) return;
          if (v > line) hit++;
          else if (v < line) miss++;
          else even++;
        });

        const total = hit + miss + even || 1;
        const Hpct = Math.round((hit / total) * 100);
        const Mpct = Math.round((miss / total) * 100);

        return (
          <div style={{ marginTop: 10, display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, fontWeight: 800, color: "#111827" }}>
            <span>✅ Hit: {hit}</span>
            <span>❌ Miss: {miss}</span>
            <span>⚖️ Even: {even}</span>
            <span style={{ color: Hpct >= 70 ? "#166534" : "#374151" }}>🔥 H%: {Hpct}%</span>
            <span style={{ color: Mpct >= 70 ? "#991b1b" : "#374151" }}>🧊 M%: {Mpct}%</span>
          </div>
        );
      })()}
    </HistoricalGamesDisclosure>
  );
})()}


{/* -------- Last 10 Games vs Opponent (embedded so you don't have to switch to Card view) -------- */}
{(() => {
  const l10 = getL10Rows(gridDetailsRow, { vsOpp: true, max: 10 });
  if (!Array.isArray(l10) || l10.length === 0) return null;

  const propHeader = gridDetailsRow["Prop Type"] || gridDetailsRow.PropType || gridDetailsRow.Prop || "";
  const _isMinutesProp = isMinutesLikePropType(propHeader);

  const isBaseball = ["MLB", "KBO"].includes(String(activeSport || "").toUpperCase());
  const detailPlayerType = getPlayerTypeValue(gridDetailsRow);
  const detailOppMeta = getOpportunityMetaForDetail(gridDetailsRow, activeSport);
  
  const showMinutesCol = !isBaseball;
  const showPACol = isBaseball && detailPlayerType === "BATTER";
  const showABCol = isBaseball && detailPlayerType === "BATTER";
  const showOutsCol = isBaseball && detailPlayerType === "PITCHER";

  const numericColStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    textAlign: "center",
    width: 100,
    minWidth: 100,
    whiteSpace: "nowrap",
  };

  const thStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    fontWeight: 600,
  };

  const numericHeaderStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    textAlign: "center",
    fontWeight: 700,
    width: 110,
    minWidth: 110,
    whiteSpace: "nowrap",
  };
  
  const numericCellStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    textAlign: "center",
    width: 110,
    minWidth: 110,
    whiteSpace: "nowrap",
  };

  const leftHeaderStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    textAlign: "left",
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
  
  const leftCellStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    whiteSpace: "pre-line",
    lineHeight: 1.25,
  };
  
  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 14,
        background: "#ffffff",
        marginBottom: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontWeight: 950, fontSize: 16 }}>Last 10 Games vs Opp</div>
        <div style={{ color: "#6b7280", fontWeight: 800, fontSize: 12 }}>
          (value column = {_isMinutesProp ? "MIN" : (activeSport === "MLB" ? mlbHistoricalDisplayLabel(propHeader) : shortPropHeader(propHeader))})
        </div>
      </div>

      {(() => {
        const line = Number(gridDetailsRow["Prop Value"]);
        const propType =
        gridDetailsRow["Prop Type"] || gridDetailsRow.PropType || gridDetailsRow.Prop || "";
        const isMinutesProp = isMinutesLikePropType(propType);

        const buildVsOppPoints = (rows) =>
        [...rows]
          .map((entry) => {
            const dateA = entry.Date || entry.GameDate || entry.date || "";
            const dateB = entry.DateB || entry.GameDateB || entry.dateB || "";
            const date = dateB ? `${dateA} | ${dateB}` : dateA;
      
            const min = getMinutesValue(entry, activeSport);
            const detailPlayerType = getPlayerTypeValue(gridDetailsRow);
            const sport = String(activeSport || "").toUpperCase();
      
            const paVal = getMLBOpportunityValue(entry, "BATTER");
            const abVal = getMLBSecondaryOpportunityValue(entry, "BATTER");
            const outsVal = getMLBOpportunityValue(entry, "PITCHER");
      
            const opportunity =
              ["MLB", "KBO"].includes(sport)
                ? detailPlayerType === "BATTER"
                  ? paVal
                  : detailPlayerType === "PITCHER"
                    ? outsVal
                    : null
                : min;
      
            return {
              date,
              matchup: entry.Matchup || "",
              value: isMinutesProp
                ? getMinutesValue(entry, activeSport)
                : getL10Value(entry, propType, activeSport),
              opportunity,
              minutes: min,
              pa: paVal,
              ab: abVal,
              outs: outsVal,
            };
          })
          .filter((p) => Number.isFinite(p.value))
          .reverse();

        const normalPoints = buildVsOppPoints(l10);

        const comboPoints =
          (!normalPoints.length && isComboPlayerName(gridDetailsRow?.Player))
            ? buildComboPoints({ pool: gridDetailsPool, comboRow: gridDetailsRow })
            : [];

        const points = normalPoints.length ? normalPoints : comboPoints;

        return (
          <Sparkline
            points={points}
            lineValue={line}
            height={120}
            chartRange={"L10"}
            setChartRange={() => {}}
            showBands={detailShowBands}
            setShowBands={setDetailShowBands}
          />
        );
      })()}

      {(renderPatternChip(gridDetailsRow) || renderGuruPickChip(gridDetailsRow)) && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {renderPatternChip(gridDetailsRow)}
          {renderGuruPickChip(gridDetailsRow)}
          {SHOW_GURU_PICK_REASON && gridDetailsRow?.Guru_Pick_Reason && (
            <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
              {gridDetailsRow.Guru_Pick_Reason}
            </span>
          )}
        </div>
      )}



      <div style={{ overflowX: "auto", marginTop: 10 }}>
        <table style={{ width: "100%", minWidth: 520, fontSize: "0.85rem", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={leftHeaderStyle}>Date</th>
              <th style={leftHeaderStyle}>Matchup</th>

              {showMinutesCol && <th style={numericHeaderStyle}>MIN</th>}
              {showPACol && <th style={numericHeaderStyle}>PA</th>}
              {showABCol && <th style={numericHeaderStyle}>AB</th>}
              {showOutsCol && <th style={numericHeaderStyle}>OUTS</th>}

              <th
                style={numericHeaderStyle}
                title={propHeader}
              >
                  {activeSport === "MLB" ? mlbHistoricalDisplayLabel(propHeader) : shortPropHeader(propHeader)}
              </th>
            </tr>
          </thead>
          <tbody>
            {l10.map((entry, idx) => {
              const v = _isMinutesProp ? getMinutesValue(entry, activeSport) : getL10Value(entry, gridDetailsRow["Prop Type"], activeSport);
              const sty = rowResultStyle(v, gridDetailsRow);

              // ---- date cell (supports combos) ----
              const dateA =
                entry.Date
                  ? new Date(entry.Date).toLocaleDateString()
                  : (entry.GameDate || entry.DateStr || entry.date || "");

              const dateB =
                entry.DateB
                  ? new Date(entry.DateB).toLocaleDateString()
                  : (entry.GameDateB || entry.DateStrB || entry.dateB || "");

              const dateCell = dateB ? `${dateA} | ${dateB}` : dateA;


              const matchupCell = (() => {
                if (activeSport === "WNBA" && typeof matchupCellWNBA === "function") {
                  return matchupCellWNBA(entry);
                }
                if (entry.Matchup) return entry.Matchup;
                const team = entry.Team || gridDetailsRow.Team || "";
                const opp  = entry.Opponent || "";
                if (team && opp) {
                  const ha = String(entry["Home/Away"] || entry.HomeAway || "").toLowerCase();
                  const sym = ha === "away" || ha === "a" || ha === "@" ? "@" : "vs";
                  return `${team} ${sym} ${opp}`.trim();
                }
                return opp || "";
              })();

              const min = getMinutesValue(entry, activeSport);

              return (
                <tr key={idx} style={sty}>
                  <td
                    style={{
                      padding: "8px 6px",
                      borderBottom: "1px solid #f3f4f6",
                      whiteSpace: "pre-line",      // 👈 key for "A | B" to break cleanly
                      lineHeight: 1.25,
                    }}
                  >
                    {dateCell}
                  </td>

                  <td
                    style={{
                      padding: "8px 6px",
                      borderBottom: "1px solid #f3f4f6",
                      whiteSpace: "pre-line",      // allows your matchup "A | B" to wrap
                      lineHeight: 1.25,
                    }}
                  >
                    {matchupCell}
                  </td>

                  {showMinutesCol && (
                    <td style={numericCellStyle}>
                      {Number.isFinite(min) ? min.toFixed(1) : ""}
                    </td>
                  )}

                  <MlbHistoryCells
                    entry={entry}
                    metricValue={v}
                    showPA={showPACol}
                    showAB={showABCol}
                    showMetric={!_isMinutesProp}
                    cellStyle={numericCellStyle}
                  />

                  {showOutsCol && (
                    <td style={numericCellStyle}>
                      {Number.isFinite(getOutsValue(entry)) ? getOutsValue(entry) : ""}
                    </td>
                  )}

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(() => {
        const line = Number(gridDetailsRow["Prop Value"]);
        let hit = 0, miss = 0, even = 0;

        l10.forEach((r) => {
          const v = _isMinutesProp ? getMinutesValue(r, activeSport) : getL10Value(r, gridDetailsRow["Prop Type"], activeSport);
          if (!Number.isFinite(v) || !Number.isFinite(line)) return;
          if (v > line) hit++;
          else if (v < line) miss++;
          else even++;
        });

        const total = hit + miss + even || 1;
        const Hpct = Math.round((hit / total) * 100);
        const Mpct = Math.round((miss / total) * 100);

        return (
          <div style={{ marginTop: 10, display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, fontWeight: 800, color: "#111827" }}>
            <span>✅ Hit: {hit}</span>
            <span>❌ Miss: {miss}</span>
            <span>⚖️ Even: {even}</span>
            <span style={{ color: Hpct >= 70 ? "#166534" : "#374151" }}>🔥 H%: {Hpct}%</span>
            <span style={{ color: Mpct >= 70 ? "#991b1b" : "#374151" }}>🧊 M%: {Mpct}%</span>
          </div>
        );
      })()}
    </div>
  );
})()}

            
            {/* Optional longer analysis fields if present */}
            {(() => {
              const longText =
                gridDetailsRow.Commentary ||
                gridDetailsRow.commentary ||
                gridDetailsRow.Analysis ||
                gridDetailsRow.analysis ||
                gridDetailsRow.Notes ||
                gridDetailsRow.notes ||
                "";
              if (!longText) return null;
              return (
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    padding: 14,
                    background: "#ffffff",
                  }}
                >
                  <div style={{ fontWeight: 900, marginBottom: 8 }}>Analysis</div>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.45, color: "#111827" }}>
                    {String(longText)}
                  </div>
                </div>
              );
            })()}
          </div>
          )}
          </>
          )}
          </div>
        </div>
      </div>
    )}

    {/* ===================== CLEAR MY PLAYS CONFIRMATION ===================== */}
    {clearMyPlaysDialogOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="clear-my-plays-title"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 12000,
          background: "rgba(15,23,42,0.58)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
        onClick={() => setClearMyPlaysDialogOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setClearMyPlaysDialogOpen(false);
        }}
      >
        <div
          style={{
            width: "min(460px, 100%)",
            background: darkMode ? "#111827" : "#ffffff",
            color: darkMode ? "#f8fafc" : "#0f172a",
            borderRadius: 20,
            border: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e2e8f0",
            boxShadow: "0 24px 70px rgba(15,23,42,0.32)",
            padding: 22,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div id="clear-my-plays-title" style={{ fontSize: 22, fontWeight: 950, marginBottom: 8 }}>
            Clear My Plays?
          </div>
          <div style={{ color: darkMode ? "#cbd5e1" : "#475569", lineHeight: 1.5, marginBottom: 20 }}>
            Remove all {myGuruList.length} saved {myGuruList.length === 1 ? "play" : "plays"} from your list? This cannot be undone.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              autoFocus
              onClick={() => setClearMyPlaysDialogOpen(false)}
              style={{
                padding: "10px 16px",
                borderRadius: 999,
                border: darkMode ? "1px solid rgba(255,255,255,0.16)" : "1px solid #cbd5e1",
                background: darkMode ? "#1f2937" : "#ffffff",
                color: darkMode ? "#f8fafc" : "#0f172a",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                clearMyGuruList();
                setGuruListFilter("all");
                setMyPlaysSportFilter("ALL");
                setClearMyPlaysDialogOpen(false);
              }}
              style={{
                padding: "10px 16px",
                borderRadius: 999,
                border: "1px solid #dc2626",
                background: "#dc2626",
                color: "#ffffff",
                fontWeight: 950,
                cursor: "pointer",
              }}
            >
              Clear All Plays
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ===================== GRID COMPARE MODAL (MAX 8) ===================== */}
    {gridCompareOpen && Array.isArray(gridCompareRows) && gridCompareRows.length > 0 && (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "rgba(0,0,0,0.55)",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          padding: "16px",
        }}
        onClick={() => {
          setGridCompareOpen(false);
          setGridCompareRows([]);
          setGridCompareNote("");
        }}
      >
        <div
          style={{
            width: "min(1040px, 100%)",
            background: "#ffffff",
            borderRadius: 16,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Compare</div>
              <div style={{ color: "#6b7280", fontWeight: 700, fontSize: 13 }}>
                Ranks your selected lines (max 8) using a blended score: Δ, confidence, and L10 hit%.
              </div>
              {gridCompareNote ? (
                <div style={{ marginTop: 6, color: "#92400e", fontWeight: 800, fontSize: 12 }}>
                  {gridCompareNote}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => {
                setGridCompareOpen(false);
                setGridCompareRows([]);
                setGridCompareNote("");
              }}
              style={{
                borderRadius: 999,
                padding: "8px 12px",
                border: "1px solid #e5e7eb",
                background: "#f9fafb",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              ✕ Close
            </button>
          </div>

          <div style={{ padding: 16, overflowY: "auto" }}>
            {(() => {
              const ordinalChoice = (i) => {
                const n = i + 1;
                const suffix = (n % 100 >= 11 && n % 100 <= 13) ? 'th' : (n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th');
                return `${n}${suffix} choice`;
              };

              const ranked = [...gridCompareRows]
                .map((r) => ({ row: r, score: getCompareScore(r) }))
                .sort((a, b) => b.score - a.score);

              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
                  {ranked.map((item, idx) => {
                    const r = item.row;
                    const rankLabel = ordinalChoice(idx);
                    return (
                      <div
                        key={`${r.Player || r.player || "row"}-${idx}`}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 16,
                          padding: 14,
                          background: idx === 0 ? "#f0fdf4" : "#ffffff",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                          <div style={{ fontWeight: 950, fontSize: 16 }}>
                            {r.Player || r.player || "Player"}
                          </div>
                          <div style={{ fontWeight: 900, color: idx === 0 ? "#166534" : "#6b7280" }}>
                            {rankLabel}
                          </div>
                        </div>

                        <div style={{ marginTop: 4, color: "#6b7280", fontWeight: 800, fontSize: 12 }}>
                          {(r.Team || r.team || "")}
                          {(r.Opponent || r.opp || r["Opp"] || "")
                            ? ` vs ${(r.Opponent || r.opp || r["Opp"])}`
                            : ""}
                          {" • "}
                          {deriveTierFromRow(r)}
                        </div>

                        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          {[
                            { k: "Prop", v: r["Prop Type"] || r.PropType || "" },
                            { k: "Line", v: r["Prop Value"] || r.PropValue || r.Line || "" },
                            { k: "Benchmark", v: fmtNum(getProjValue(r)) },
                            { k: "Δ Edge", v: fmtNum(getDeltaValue(r)) },
                            { k: "Tag", v: r.Tag || r.tag || "" },
                            { k: "Score", v: item.score.toFixed(2) },
                          ].map((x) => (
                            <div key={x.k} style={{ border: "1px solid #f3f4f6", borderRadius: 12, padding: "8px 10px", background: "#fafafa" }}>
                              <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 900 }}>{x.k}</div>
                              <div style={{ fontWeight: 900, color: "#111827" }}>{String(x.v ?? "")}</div>
                            </div>
                          ))}
                        </div>

                        <div style={{ marginTop: 10, color: "#111827", fontWeight: 800, fontSize: 12 }}>
                          {buildPlayerHover(r)}
                        </div>

                        <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                          <button
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "10px 14px",
                              borderRadius: 999,
                              border: "1px solid #dbeafe",
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              fontWeight: 800,
                              cursor: "pointer",
                            }}
                            
                            onClick={(e) => {
                              e.stopPropagation();

                              // close compare modal first
                              const pool = Array.isArray(gridCompareRows) ? gridCompareRows : [];
                              setGridCompareOpen(false);

                              // open details with the correct row + a real pool
                              handleOpenGridDetails(r, pool);
                            }}
                          >
                            🔎 Open details
                          </button>

                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    )}



      {/* Close PW container + page wrapper (fixed) */}
      </div>
    </div>

    { /* End of <div className="pw-container">  */}
     </>
    ); //End of original return
  } // End of PropsDashboard
